import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStaffAuth, ROLE_META } from './StaffAuthContext';
import { consumeDesktopRedirect } from './staffDesktopRedirect';
import VirtualShopOwnerAdmin from './modules/virtualShop/VirtualShopOwnerAdmin';
import { createVirtualShopAdminApi } from './modules/virtualShop/virtualShopAdminApi';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine} from 'recharts';

const API = process.env.REACT_APP_API_URL || 'https://api.visinvoke.com';
const VIRTUAL_SHOP_ADMIN_API = createVirtualShopAdminApi(API);

const T = {
  bg:'#0b1018', surface:'#131a24', border:'rgba(255,255,255,0.08)', borderSoft:'rgba(255,255,255,0.05)',
  text:'#e8edf4', textMid:'#c2ccd8', textSub:'#8b97a8', textFaint:'#5f6b7d',
  accent:'#2D6CFF', accentLight:'rgba(45,108,255,0.14)', accentBorder:'rgba(45,108,255,0.40)',
  green:'#4ADE80', greenBg:'rgba(34,197,94,0.13)', greenBorder:'rgba(34,197,94,0.35)',
  red:'#F87171', redBg:'rgba(239,68,68,0.13)', redBorder:'rgba(239,68,68,0.35)',
  amber:'#FBBF24', amberBg:'rgba(245,158,11,0.13)', amberBorder:'rgba(245,158,11,0.35)',
  purple:'#C084FC', purpleBg:'rgba(168,85,247,0.14)',
  sidebar:'#0e1520', sidebarBorder:'rgba(255,255,255,0.07)',
  sidebarText:'#8899aa', sidebarActive:'#ffffff', sidebarActiveBg:'rgba(255,255,255,0.09)',
  sidebarSection:'#5a6b7f',
  chart1:'#4d8bff', chart2:'#4ADE80', chart3:'#FBBF24', chart4:'#C084FC',
  chart5:'#F87171', chart6:'#2DD4BF', chart7:'#FB923C', chart8:'#60A5FA',
};
const CHART_COLORS = [T.chart1,T.chart2,T.chart3,T.chart4,T.chart5,T.chart6,T.chart7,T.chart8];
const font = "'Inter','Helvetica Neue',Arial,sans-serif";
const mono = "'JetBrains Mono','Fira Code',monospace";
const OPT_DARK = { background:'#131a24', color:'#e8edf4' };

const s = {
  root:{minHeight:'100vh',background:T.bg,fontFamily:font,display:'flex',color:T.text,WebkitFontSmoothing:'antialiased'},
  sidebar:{width:220,minHeight:'100vh',background:T.sidebar,display:'flex',flexDirection:'column',position:'fixed',top:0,left:0,bottom:0,zIndex:100,overflowY:'auto'},
  sidebarHeader:{padding:'20px 16px 16px',borderBottom:`1px solid ${T.sidebarBorder}`},
  logoRow:{display:'flex',alignItems:'center',gap:9,marginBottom:3},
  logoBox:{width:26,height:26,background:T.accent,borderRadius:5,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800,color:'#fff',letterSpacing:'-0.5px'},
  logoText:{fontSize:15,fontWeight:700,color:'#fff',letterSpacing:'0.02em'},
  logoBadge:{fontSize:12,fontWeight:600,color:T.sidebarSection,letterSpacing:'0.1em',textTransform:'uppercase'},
  navGroup:{padding:'10px 0 2px'},
  navLabel:{fontSize:13,fontWeight:700,letterSpacing:'0.08em',color:T.sidebarSection,textTransform:'uppercase',padding:'2px 16px 6px'},
  navItem:(a)=>({display:'flex',alignItems:'center',gap:9,padding:'8px 16px',fontSize:14,fontWeight:a?600:400,color:a?T.sidebarActive:T.sidebarText,background:a?T.sidebarActiveBg:'transparent',borderLeft:`2px solid ${a?T.accent:'transparent'}`,cursor:'pointer',transition:'all 0.1s',userSelect:'none'}),
  sidebarFooter:{marginTop:'auto',padding:'12px 16px',borderTop:`1px solid ${T.sidebarBorder}`},
  backLink:{display:'flex',alignItems:'center',gap:7,fontSize:14,color:T.sidebarSection,cursor:'pointer',background:'none',border:'none',padding:0,fontFamily:font},
  main:{marginLeft:220,flex:1,minWidth:0},
  topbar:{background:T.surface,borderBottom:`1px solid ${T.border}`,padding:'0 28px',height:52,display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:50,boxShadow:'0 1px 3px rgba(0,0,0,0.04)'},
  pageTitle:{fontSize:22,fontWeight:700,color:T.text},
  pageSub:{fontSize:13,color:T.textSub,marginTop:1},
  content:{padding:'20px 28px'},
  grid:(c,gap=12)=>({display:'grid',gridTemplateColumns:`repeat(${c},1fr)`,gap,marginBottom:gap}),
  panel:{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,padding:'16px 18px',boxShadow:'0 1px 3px rgba(0,0,0,0.04)'},
  panelTitle:{fontSize:17,fontWeight:700,color:T.text,marginBottom:2},
  panelSub:{fontSize:13,color:T.textSub,marginBottom:12},
  tableCard:{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,overflow:'hidden',boxShadow:'0 1px 3px rgba(0,0,0,0.04)',marginBottom:12},
  tableHeader:{padding:'12px 16px',borderBottom:`1px solid ${T.borderSoft}`,display:'flex',alignItems:'center',justifyContent:'space-between',background:T.bg},
  tableTitle:{fontSize:15,fontWeight:700,color:T.text,display:'flex',alignItems:'center',gap:7},
  tableBadge:{fontSize:13,fontWeight:600,color:T.textSub,background:T.surface,border:`1px solid ${T.border}`,borderRadius:4,padding:'1px 6px'},
  table:{width:'100%',borderCollapse:'collapse',fontSize:15},
  th:{textAlign:'left',padding:'14px 16px',fontSize:14,fontWeight:600,color:'#F0F4FA',textTransform:'uppercase',letterSpacing:'0.2px',height:48,background:T.bg,borderBottom:`1px solid ${T.border}`,whiteSpace:'nowrap',position:'sticky',top:0,zIndex:1},
  td:{padding:'14px 12px',borderBottom:`1px solid ${T.borderSoft}`,color:'#F5F7FB',fontSize:14,verticalAlign:'middle'},
  tds:{padding:'14px 16px',borderBottom:`1px solid ${T.borderSoft}`,color:'#FFFFFF',fontWeight:500,verticalAlign:'middle',fontSize:15},
  badge:(c)=>({display:'inline-flex',alignItems:'center',justifyContent:'center',gap:5,minHeight:32,boxSizing:'border-box',padding:'0 12px',borderRadius:6,fontSize:13,fontWeight:500,whiteSpace:'nowrap',
    background:c==='green'?T.greenBg:c==='red'?T.redBg:c==='amber'?T.amberBg:c==='blue'?T.accentLight:c==='purple'?T.purpleBg:T.bg,
    color:c==='green'?T.green:c==='red'?T.red:c==='amber'?T.amber:c==='blue'?T.accent:c==='purple'?T.purple:T.textSub,
    border:`1px solid ${c==='green'?T.greenBorder:c==='red'?T.redBorder:c==='amber'?T.amberBorder:c==='blue'?T.accentBorder:T.border}`}),
  filterRow:{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap',alignItems:'center'},
  input:{background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,minHeight:42,padding:'0 14px',color:'#FFFFFF',fontSize:14,outline:'none',fontFamily:font,boxSizing:'border-box'},
  select:{background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,minHeight:42,padding:'0 14px',color:'#FFFFFF',fontSize:14,outline:'none',cursor:'pointer',fontFamily:font,colorScheme:'dark',boxSizing:'border-box'},
  btn:(v)=>({minHeight:40,padding:'0 16px',borderRadius:6,fontSize:14,fontWeight:600,cursor:'pointer',border:v==='primary'?'none':`1px solid ${T.border}`,background:v==='primary'?T.accent:T.surface,color:v==='primary'?'#FFFFFF':'#F0F4FA',fontFamily:font,display:'inline-flex',alignItems:'center',justifyContent:'center',gap:5,boxSizing:'border-box'}),
  pagination:{display:'flex',gap:16,alignItems:'start',padding:'12px 16px',borderTop:`1px solid ${T.borderSoft}`,fontSize:13,color:T.textSub},
  loading:{padding:'40px 0',textAlign:'center',color:T.textSub,fontSize:14,display:'flex',flexDirection:'column',alignItems:'center',gap:8},
  error:{padding:'12px 14px',background:T.redBg,border:`1px solid ${T.redBorder}`,borderRadius:7,color:T.red,fontSize:14,fontWeight:600,marginBottom:12},
  empty:{padding:'28px 0',textAlign:'center',color:T.textFaint,fontSize:14},
  kvRow:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 0',borderBottom:`1px solid ${T.borderSoft}`,fontSize:14},
  kvLabel:{color:T.textSub},kvValue:{color:T.text,fontWeight:700},
  pw:{height:5,background:T.borderSoft,borderRadius:3,overflow:'hidden',marginTop:6},
  pb:(p,c)=>({height:'100%',width:`${Math.min(p,100)}%`,background:c||T.accent,borderRadius:3}),
};

const fmt=(n)=>{if(n===null||n===undefined)return'-';if(typeof n==='number')return n.toLocaleString('en-AU');return n;};
const fmtDate=(iso)=>{if(!iso)return'-';return new Date(iso).toLocaleString('en-AU',{dateStyle:'short',timeStyle:'short'});};
const fmtAgo=(iso)=>{if(!iso)return'-';const d=Math.floor((Date.now()-new Date(iso))/1000);if(d<60)return`${d}s ago`;if(d<3600)return`${Math.floor(d/60)}m ago`;if(d<86400)return`${Math.floor(d/3600)}h ago`;return`${Math.floor(d/86400)}d ago`;};

function SBadge({status}){
  const m={completed:'green',active:'green',success:'green',failed:'red',cancelled:'red',queued:'amber',trialing:'amber',trial:'amber',pending:'amber',processing:'blue'};
  const d={completed:'*',active:'*',success:'*',failed:'x',cancelled:'x',queued:'o',trialing:'o',processing:'o'};
  return<span style={s.badge(m[status]||'grey')}>{d[status]||'.'} {status}</span>;
}

// Circular radial KPI - matches reference image donut ring style
// SVG stroke-based ring KPI -- matches APEX reference exactly
// White card, thick coloured ring, large number centred, label below
function RadialKPI({value,label,color}){
  const borderColor=color==='red'?'#e53e3e':color==='green'?'#38a169':color==='amber'?'#d69e2e':color==='purple'?'#805ad5':'#3182ce';
  const sv=value==null?'-':String(value);
  const isLongText=/[a-zA-Z]/.test(sv.replace(/[kKmMbB]$/,''))&&sv.length>12;
  const valStyle=isLongText
    ?{fontSize:20,fontWeight:500,color:'#FFFFFF',lineHeight:1.25,minWidth:0,maxWidth:'100%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}
    :{fontSize:28,fontWeight:600,color:'#FFFFFF',lineHeight:1.15,letterSpacing:'-0.02em',minWidth:0,overflowWrap:'anywhere'};
  return(
    <div style={{background:T.surface,border:'1px solid '+borderColor,borderLeft:'3px solid '+borderColor,borderRadius:6,padding:'18px',display:'flex',flexDirection:'column',gap:6,flex:1,minWidth:0,boxSizing:'border-box',overflow:'hidden',boxShadow:'0 1px 3px rgba(0,0,0,0.25)'}}>
      <div style={{fontSize:13,fontWeight:500,color:'#C9D3E0',textTransform:'uppercase',letterSpacing:'0.06em',lineHeight:1.35}}>{label}</div>
      <div title={sv} style={valStyle}>{sv}</div>
    </div>
  );
}

// Mini ring for compact inline use
function MiniRing({pct,color,size=56}){
  const strokeColor=color||'#3182ce';
  const r=22;const circ=2*Math.PI*r;
  const safe=Math.min(Math.max(pct||0,0),100);
  const offset=circ*(1-safe/100);
  return(
    <svg width={size} height={size} viewBox="0 0 56 56" style={{flexShrink:0}}>
      <circle cx="28" cy="28" r={r} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="7"/>
      <circle cx="28" cy="28" r={r} fill="none" stroke={strokeColor} strokeWidth="7"
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform="rotate(-90 28 28)"/>
    </svg>
  );
}

function Panel({title,sub,children,height,action,mb=12}){
  return(
    <div style={{...s.panel,marginBottom:mb}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:sub?4:12}}>
        <div>
          <div style={s.panelTitle}>{title}</div>
          {sub&&<div style={s.panelSub}>{sub}</div>}
        </div>
        {action&&<div>{action}</div>}
      </div>
      <div style={height?{height}:{}}>{children}</div>
    </div>
  );
}

function TableCard({title,count,children,action}){
  return(
    <div style={s.tableCard}>
      <div style={s.tableHeader}>
        <div style={s.tableTitle}>{title}{count!==undefined&&<span style={s.tableBadge}>{fmt(count)}</span>}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Loader(){
  return(
    <div style={s.loading}>
      <div style={{width:18,height:18,border:`2px solid ${T.border}`,borderTopColor:T.accent,borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>
      Loading...<style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function Pager({page,pages,total,onPage}){
  return(
    <div style={s.pagination}>
      <button style={s.btn()} onClick={()=>onPage(page-1)} disabled={page<=1}>&lt;- Prev</button>
      <span style={{flex:1,textAlign:'center'}}>Page {page} of {pages} . {fmt(total)} total</span>
      <button style={s.btn()} onClick={()=>onPage(page+1)} disabled={page>=pages}>Next -&gt;</button>
    </div>
  );
}

function DF({value,onChange,options}){
  const opts=options||[7,14,30,60,90];
  return(
    <select style={s.select} value={value} onChange={e=>onChange(Number(e.target.value))}>
      {opts.map(d=><option key={d} value={d}>Last {d} days</option>)}
    </select>
  );
}

const TT=({active,payload,label,prefix,suffix})=>{
  if(!active||!payload?.length)return null;
  return(
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:'8px 12px',fontSize:14,boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}>
      {label&&<div style={{fontWeight:700,color:T.text,marginBottom:4}}>{label}</div>}
      {payload.map((p,i)=>(
        <div key={i} style={{color:p.color||T.text,display:'flex',alignItems:'center',gap:6}}>
          <span style={{width:8,height:8,borderRadius:'50%',background:p.color,display:'inline-block'}}/>
          {p.name}: <strong>{prefix||''}{typeof p.value==='number'?p.value.toLocaleString('en-AU'):p.value}{suffix||''}</strong>
        </div>
      ))}
    </div>
  );
};

const PM={
  tickets:['Support Tickets','Manage and respond to user support requests'],
  actions:['User Actions','Apply credit grants, compensation, and account actions'],
  rewards:['Reward Moderation','Review social submissions and grant referral rewards'],
  overview:['Dashboard','Platform overview - key metrics at a glance'],
  activity:['Live Activity','Most recent generation events'],
  health:['System Health','Job success rates and error analysis'],
  plans:['Plans & Revenue','Subscription performance and MRR'],
  margin:['Cost vs Revenue','Profitability and margin tracking'],
  credits:['Credit Monitor','Credits issued, consumed, and balances'],
  generations:['Generations','Volume trends and usage patterns'],
  generators:['Generator Analytics','Performance by generator and category'],
  ledger:['Usage Ledger','Full filterable generation log'],
  costs:['Cost Analytics','Provider spend tracking'],
  users:['Users','Account management and profiles'],
  jobs:['Job Monitor','Generation pipeline monitoring'],
  staff:['Staff Accounts','Manage staff roles, MFA, and access'],
  sessions:['Staff Sessions','Active sessions and revocation controls'],
  security:['Security Center','Failed logins, MFA events, suspicious activity'],
  audit:['Audit Log','Complete staff action history'],
  dcc_overview:['DCC Admin','Overview of DCC news, publishing and client status'],
  dcc_news:['News & Updates','Create, schedule and manage the DCC News & Updates feed'],
  virtual_shop:['Virtual Shop','Manage sections, fixed twelve-slot pages, products and owner-supplied media'],
  comms_overview:   ['Communications Overview','Email delivery KPIs and operational health'],
  comms_queue:      ['Mail Queue','Operational mail event log and delivery telemetry'],
  comms_delivery:   ['Delivery Monitor','SES delivery events, bounces, and complaints'],
  comms_suppression:['Suppression List','Email suppression management'],
  comms_templates:  ['Templates','Staff email template registry'],
  intel: ['User Intelligence','Unified read-only user intelligence console'],
  support_queue:     ['Support Queue','Open and unresolved support tickets'],
  support_assigned:  ['Assigned Tickets','Tickets assigned to staff members'],
  support_escalated: ['Escalated Tickets','High-priority and escalated tickets'],
  support_resolved:  ['Resolved Tickets','Closed and resolved ticket history'],
  support_analytics: ['Support Analytics','How much support VISINVOKE needs, and which product causes it'],
  products_overview: ['Products','Every VISINVOKE product - paid, free, internal-currency, pre-launch and retired'],
  products_registry: ['Product Registry','The one canonical product identity, and where each came from'],
  platform_overview: ['Platform Overview','Generation KPIs and operational health telemetry'],
  platform_jobs:     ['Jobs Monitor','Operational generation job table'],
  platform_providers:['Provider Telemetry','Per-provider operational metrics'],
  platform_failures: ['Failure Center','Centralized generation and system failures'],
  platform_credits:  ['Credit Telemetry','Credit consumption and risk monitoring'],
  platform_queues:   ['Queue Monitor','Read-only queue depth and wait time visibility'],
  autonomous_overview:  ['Autonomous Ops Overview','Bounded autonomous recovery execution status'],
  autonomous_rules:     ['Recovery Rules','Deterministic bounded recovery rule registry'],
  autonomous_queue:     ['Queue Recovery','Autonomous queue and stuck job recovery'],
  autonomous_provider:  ['Provider Failover','Provider state machine and autonomous failover'],
  autonomous_credits:   ['Credit Protection','Autonomous credit refund and reconciliation'],
  autonomous_security:  ['Security Response','Bounded autonomous security response rules'],
  autonomous_history:   ['Execution History','Bounded execution audit log'],
  auto_overview:    ['Automation Overview','Rule evaluation state and operational severity'],
  auto_rules:       ['Automation Rules','Deterministic rule registry and trigger state'],
  auto_triggers:    ['Trigger Events','Current and historical automation trigger events'],
  auto_incidents:   ['Incident Detection','Active operational incidents from rule evaluation'],
  auto_suggestions: ['Suggestions','Deterministic operational recommendations'],
  auto_history:     ['Automation History','Audit log of automation operational actions'],
  ci_revenue:       ['Revenue Intelligence','Executive revenue performance across all products'],
  ci_subscribers:   ['Subscriber Intelligence','Subscription health by product and tier'],
  ci_conversion:    ['Conversion Intelligence','User journey conversion from registration to payment'],
  ci_acquisition:   ['Acquisition Intelligence','Advertising spend vs paying customer outcomes'],
  ci_retention:     ['Retention Intelligence','Churn, renewal, LTV, and reactivation tracking'],
  ci_ecosystem:     ['Ecosystem Adoption','Multi-product ownership and cross-sell intelligence'],
  ci_health:        ['Product Health','Deterministic health scores per product'],
  ci_target:        ['Owner Target Tracker','Progress toward A$6,000 monthly operating profit'],
  mc_channels:      ['Social Channels','Official VISINVOKE channel metrics and attribution'],
  mc_ci:            ['Channel Intelligence','Unified channel performance and attribution intelligence'],
  mc_calendar:      ['Content Calendar','Planned and published content across all platforms'],
  mc_activity:      ['Marketing Activity','Log and manage every marketing action'],
  mc_campaigns:     ['Campaigns','Group marketing activities into trackable campaigns'],
  mc_impact:        ['Campaign Impact','Measure outcomes after each published activity'],
  mc_traffic:       ['Traffic Sources','Where registrations and paying users come from'],
  mc_community:     ['Community Growth','Community size and engagement across platforms'],
  mc_releases:      ['Release Tracker','Planned and released product updates and launches'],
  mc_email:         ['Email Campaigns','Email marketing campaign tracking and revenue attribution'],
  exec_intel:       ['Intelligence Foundation','Data collection and tracking system status'],
  exec_overview:    ['Executive Overview','Complete business health at a glance'],
  exec_health:      ['Business Health','Weighted operational health score across all systems'],
  exec_signals:     ['Growth Signals','Fastest growing products, channels, and campaigns'],
  exec_forecast:    ['Forecasting','Projected subscribers, revenue, and profit'],
  gi_attribution:   ['Generator Attribution','Generator-level usage and attribution intelligence'],
  gi_performance:   ['Generator Performance','Usage and failure rates by generator'],
  gi_engines:       ['Engine Performance','Usage by engine category and domain'],
  gi_trends:        ['Usage Trends','Daily, weekly, and monthly generation activity'],
  gi_revenue:       ['Generator Revenue','Revenue and cost attribution by generator'],
  si_performance:   ['Slug Performance','Usage and conversion by individual slug'],
  si_families:      ['Slug Families','Usage and revenue by slug family'],
  si_demand:        ['Community Demand','Requested slugs, families, and themes'],
  si_planning:      ['Future Planning','Growth opportunities and underserved demand'],
  mc_release_impact:['Release Impact','Measured outcomes after each product release'],
  mc_voting:        ['Community Voting','Roadmap intelligence from community votes'],
  ci_cohorts:       ['Cohort Intelligence','Retention tracking by signup period'],
  ci_ltv:           ['Product LTV','Lifetime value by product and tier'],
  ci_migration:     ['Cross Product Migration','User journey flows across VISINVOKE products'],
  comms_threads:    ['Communication Threads','All inbound and outbound communication threads'],
  comms_inboxes:    ['Mailbox Inboxes','Direct Gmail mailbox viewer across all mailboxes'],
  gmail_mailboxes:  ['Gmail Connector','Gmail API connection management for all mailboxes'],
  tech_overview:     ['Technicians','Technician management overview'],
  tech_applications: ['Technician Applications','Pending technician applications'],
  tech_submissions:  ['Technician Submissions','Submitted technician documents'],
  tech_accounts:     ['Technician Accounts','Active technician accounts'],
  tech_verification: ['Technician Verification','Technician verification queue'],
  tech_capabilities: ['Technician Capabilities','Technician capability management'],
  tech_audit:        ['Technician Audit','Technician activity audit log'],
};

// -- OVERVIEW -----------------------------------------------------------------
function SecOverview({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{fetch(`${api}/v1/admin/overview`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed to load'));},[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  const dist=data.image_count_dist||{};
  const total=Object.values(dist).reduce((a,b)=>a+b,0)||1;
  const distData=[1,2,4].map(n=>({name:`${n} img`,value:dist[String(n)]||0,pct:Math.round((dist[String(n)]||0)/total*100)}));
  const activeData=[
    {name:'24h',value:data.active_users?.['24h']||0,fill:T.chart1},
    {name:'7d', value:data.active_users?.['7d']||0, fill:T.chart2},
    {name:'30d',value:data.active_users?.['30d']||0,fill:T.chart3},
  ];
  const u24=data.active_users?.['24h']||0;
  const u7=data.active_users?.['7d']||0;
  const u30=data.active_users?.['30d']||0;
  const totalU=data.total_users||1;
  return(
    <>
      {/* KPI ring strip */}
      <div style={{background:'rgba(255,255,255,0.03)',borderRadius:10,padding:'20px 24px',marginBottom:12,border:'1px solid rgba(255,255,255,0.07)'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:16,alignItems:'start'}}>
          <RadialKPI value={fmt(data.total_users)} label="Total Users" color="blue"/>
          <RadialKPI value={fmt(u24)} label="Active 24h" color="green"/>
          <RadialKPI value={fmt(u7)} label="Active 7d" color="green"/>
          <RadialKPI value={fmt(data.total_generations)} label="Generations" color="blue"/>
          <RadialKPI value={fmt(data.total_images)} label="Images" color="purple"/>
          <RadialKPI value={`$${data.total_cost_usd}`} label="Cost USD" color={parseFloat(data.total_cost_usd)>100?'amber':'green'}/>
        </div>
      </div>

      {/* Main charts row */}
      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:12,marginBottom:12}}>
        <Panel title="Active Users by Period" sub="Unique users who generated" height={210}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={activeData} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="name" tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip content={<TT/>}/>
              <Bar dataKey="value" name="Users" radius={[4,4,0,0]}>
                {activeData.map((e,i)=><Cell key={i} fill={e.fill}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Image Count Split" sub="Per generation" height={210}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={distData} cx="50%" cy="50%" innerRadius={52} outerRadius={78} dataKey="value" paddingAngle={4}>
                {distData.map((e,i)=><Cell key={i} fill={CHART_COLORS[i]}/>)}
              </Pie>
              <Tooltip formatter={(v,n,p)=>[`${v} (${p.payload.pct}%)`,p.payload.name]}/>
              <Legend iconSize={10} wrapperStyle={{fontSize:14,color:T.textSub}}/>
            </PieChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Platform Reach" sub="User activity rings" height={210}>
          <div style={{display:'flex',flexDirection:'column',gap:10,paddingTop:4}}>
            {[
              {label:'Active 24h',val:u24,max:totalU,color:T.chart2},
              {label:'Active 7d',val:u7,max:totalU,color:T.chart1},
              {label:'Active 30d',val:u30,max:totalU,color:T.chart3},
            ].map(({label,val,max,color})=>{
              const pct=max>0?Math.round(val/max*100):0;
              return(
                <div key={label} style={{display:'flex',alignItems:'center',gap:10}}>
                  <MiniRing pct={pct} color={color} size={48} ring={7}/>
                  <div style={{flex:1}}>
                    <div style={{fontSize:14,fontWeight:700,color:T.text}}>{fmt(val)}</div>
                    <div style={{fontSize:13,color:T.textSub}}>{label} . {pct}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      {/* Secondary stats row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:12}}>
        {[
          {label:'Credits Consumed',val:fmt(data.total_credits_used),sub:'All-time',color:T.chart4},
          {label:'Avg Images / Gen',val:data.avg_images_per_gen,sub:'Higher = more cost',color:T.chart3},
          {label:'Total Images',val:fmt(data.total_images),sub:'All-time generated',color:T.chart1},
          {label:'Est. Cost (USD)',val:`$${data.total_cost_usd}`,sub:'fal.ai cumulative',color:parseFloat(data.total_cost_usd)>100?T.chart5:T.chart2},
        ].map(({label,val,sub,color})=>(
          <div key={label} style={{...s.panel,padding:'14px 16px',borderTop:`3px solid ${color}`}}>
            <div style={{fontSize:13,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>{label}</div>
            <div title={val==null?'-':String(val)} style={{fontSize:28,fontWeight:600,color:'#FFFFFF',lineHeight:1.15,letterSpacing:'-0.02em',minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{val??'-'}</div>
            <div style={{fontSize:13,color:T.textSub,marginTop:6}}>{sub}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// -- HEALTH --------------------------------------------------------------------
function SecHealth({api}){
  const[data,setData]=useState(null);const[days,setDays]=useState(7);const[err,setErr]=useState(null);
  useEffect(()=>{setData(null);fetch(`${api}/v1/admin/health/jobs?days=${days}`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));},[api,days]);
  if(err)return<div style={s.error}>! {err}</div>;
  return(
    <>
      <div style={s.filterRow}>
        <select style={s.select} value={days} onChange={e=>setDays(Number(e.target.value))}>
          {[1,3,7,14,30].map(d=><option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>
      {!data?<Loader/>:(
        <>
          {/* Radial health KPI strip */}
          <div style={{background:'rgba(255,255,255,0.03)',borderRadius:10,padding:'20px 24px',marginBottom:12,border:'1px solid rgba(255,255,255,0.07)'}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,alignItems:'start'}}>
              <RadialKPI value={fmt(data.total_jobs)} label="Total Jobs" color="blue"/>
              <RadialKPI
                value={`${data.error_rate_pct}%`}
                label="Error Rate"
                color={data.error_rate_pct>5?'red':data.error_rate_pct>1?'amber':'green'}
              />
              <RadialKPI
                value={`${fmt(data.avg_latency_ms)}ms`}
                label="Avg Latency"
                color={data.avg_latency_ms>15000?'red':data.avg_latency_ms>8000?'amber':'green'}
              />
              <RadialKPI
                value={fmt(data.reconciliation_pending)}
                label="Reconciliation"
                color={data.reconciliation_pending>0?'red':'green'}
              />
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
            <Panel title="Job Status Breakdown" height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={Object.entries(data.by_status||{}).map(([k,v])=>({name:k,value:v}))} cx="50%" cy="50%" innerRadius={58} outerRadius={85} dataKey="value" paddingAngle={4}>
                    {Object.keys(data.by_status||{}).map((k,i)=><Cell key={i} fill={k==='completed'?T.chart2:k==='failed'?T.chart5:k==='processing'?T.chart1:T.chart3}/>)}
                  </Pie>
                  <Tooltip formatter={(v)=>[fmt(v),'Jobs']}/>
                  <Legend iconSize={10} wrapperStyle={{fontSize:14,color:T.textSub}}/>
                </PieChart>
              </ResponsiveContainer>
            </Panel>
            <TableCard title="Error Codes">
              {data.error_codes.length===0?(
                <div style={{...s.empty,padding:'40px 0'}}>
                  <div style={{fontSize:22,marginBottom:6}}>v</div>
                  <div style={{color:T.green,fontWeight:700}}>No errors in this period</div>
                </div>
              ):(
                <table style={s.table}>
                  <thead><tr><th style={s.th}>Error Code</th><th style={s.th}>Count</th></tr></thead>
                  <tbody>
                    {data.error_codes.map(r=>(
                      <tr key={r.code}>
                        <td style={s.td}><code style={{fontFamily:mono,fontSize:14,color:T.red}}>{r.code}</code></td>
                        <td style={s.tds}>{fmt(r.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </TableCard>
          </div>
        </>
      )}
    </>
  );
}

// -- PLANS ---------------------------------------------------------------------



// ============================================================================
// PHASE A — EXECUTIVE COMMAND CENTRE + GENERATOR INTELLIGENCE
// ============================================================================


// ============================================================================
// PHASE C — INTELLIGENCE CONSUMPTION COMPONENTS
// All pages read from Phase B tables via dedicated endpoints.
// Empty/collecting/unavailable states handled throughout.
// ============================================================================

// Shared: "Collecting Data" state for Phase C pages with empty tables
function SecCollecting({title, dep, status}){
  const cfg = status==='collecting'
    ? {icon:'📊', color:T.accent, bg:T.accentLight, border:T.accentBorder, label:'Collecting Data', msg:'Data is being captured. Metrics will appear as records accumulate.'}
    : status==='empty'
    ? {icon:'⬜', color:T.amber, bg:T.amberBg, border:T.amberBorder, label:'No Data Yet', msg:`No records collected yet. ${dep||'Use the Intelligence Foundation to start collecting data.'}`}
    : {icon:'⚠', color:T.textSub, bg:T.bg, border:T.border, label:'Setup Required', msg:dep||'Configure the data source in Intelligence Foundation.'};
  return(
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:300,padding:48,textAlign:'center'}}>
      <div style={{fontSize:32,marginBottom:14}}>{cfg.icon}</div>
      <div style={{fontSize:15,fontWeight:700,color:T.textMid,marginBottom:8}}>{title}</div>
      <div style={{display:'inline-block',padding:'3px 12px',borderRadius:10,fontSize:12,fontWeight:700,background:cfg.bg,color:cfg.color,border:`1px solid ${cfg.border}`,marginBottom:12}}>{cfg.label}</div>
      <div style={{fontSize:13,color:T.textSub,maxWidth:420,lineHeight:1.6}}>{cfg.msg}</div>
    </div>
  );
}

// ── COHORT INTELLIGENCE ───────────────────────────────────────────────────────
function SecCICohorts({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/commercial-intelligence/cohorts`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const cohorts=data.cohorts||[];
  const OFFSETS=[1,2,3,6,12];
  const hasRetention=data.has_retention_data;

  // Heatmap colour for a retention pct
  function retColor(pct){
    if(pct==null) return T.borderSoft;
    if(pct>=80) return '#276749';
    if(pct>=60) return '#38a169';
    if(pct>=40) return '#d69e2e';
    if(pct>=20) return '#c05621';
    return '#c53030';
  }

  return(
    <>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(data.total_tracked)} label="Users With Cohort Data" color="blue"/>
        <RadialKPI value={fmt(data.untracked_users)} label="Untracked Users" color={data.untracked_users>0?'amber':'green'}/>
        <RadialKPI value={fmt(cohorts.length)} label="Cohorts" color="blue"/>
      </div>

      {data.untracked_users>0&&(
        <div style={{marginBottom:12}}>
          <CIRec text={`${fmt(data.untracked_users)} users have no cohort record — run Backfill Cohorts in Intelligence Foundation.`}/>
        </div>
      )}

      {cohorts.length===0&&<SecCollecting title="Cohort Intelligence" status={data.collection_status} dep="Run 'Backfill Cohorts' in Executive → Intel Foundation to populate cohort records."/>}

      {cohorts.length>0&&(
        <>
          {/* Retention heatmap */}
          {hasRetention&&(
            <div style={{...s.panel,padding:'16px 18px',marginBottom:12,overflowX:'auto'}}>
              <div style={{fontSize:13,fontWeight:700,color:T.textMid,marginBottom:10}}>Retention Heatmap</div>
              <table style={{borderCollapse:'collapse',fontSize:11,minWidth:400}}>
                <thead>
                  <tr>
                    <th style={{padding:'4px 10px',textAlign:'left',color:T.textSub,borderBottom:`1px solid ${T.border}`}}>Cohort</th>
                    <th style={{padding:'4px 8px',textAlign:'center',color:T.textSub,borderBottom:`1px solid ${T.border}`}}>Size</th>
                    {OFFSETS.map(o=><th key={o} style={{padding:'4px 8px',textAlign:'center',color:T.textSub,borderBottom:`1px solid ${T.border}`}}>M{o}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {cohorts.slice(0,12).map(c=>(
                    <tr key={c.cohort_label}>
                      <td style={{padding:'3px 10px',fontWeight:600,color:T.text}}>{c.cohort_label}</td>
                      <td style={{padding:'3px 8px',textAlign:'center',color:T.textSub}}>{fmt(c.cohort_size)}</td>
                      {OFFSETS.map(o=>{
                        const r=c.retention[o];
                        const pct=r?.pct;
                        return(
                          <td key={o} style={{padding:'3px 5px',textAlign:'center'}}>
                            {pct!=null?<span style={{display:'inline-block',padding:'2px 8px',borderRadius:4,background:retColor(pct),color:'#fff',fontSize:11,fontWeight:700}}>{Math.round(pct)}%</span>:<span style={{color:T.textFaint,fontSize:10}}>—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{fontSize:11,color:T.textSub,marginTop:8}}>M1=Month 1 retention, M2=Month 2, etc. Colours: green ≥60%, amber 40-59%, red &lt;40%.</div>
            </div>
          )}

          {!hasRetention&&(
            <div style={{...s.panel,padding:'14px 16px',marginBottom:12,background:T.amberBg,border:`1px solid ${T.amberBorder}`}}>
              <div style={{fontSize:12,color:T.amber,fontWeight:700}}>Retention heatmap requires cohort_retention records.</div>
              <div style={{fontSize:12,color:T.textMid,marginTop:4}}>Populate by running cohort retention analysis jobs against the user_cohorts table.</div>
            </div>
          )}

          <TableCard title="Cohort Summary" count={cohorts.length}>
            <table style={s.table}>
              <thead><tr>{['Cohort','Reg Month','Size','With Product'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {cohorts.map((c,i)=>(
                  <tr key={c.cohort_label||i}>
                    <td style={s.tds}><span style={{fontWeight:700}}>{c.cohort_label}</span></td>
                    <td style={s.td}>{c.reg_month||'—'}</td>
                    <td style={{...s.td,fontWeight:700}}>{fmt(c.cohort_size)}</td>
                    <td style={{...s.td,color:c.with_product>0?T.green:T.textSub}}>{fmt(c.with_product)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        </>
      )}
    </>
  );
}

// ── PRODUCT LTV ───────────────────────────────────────────────────────────────
function SecCIProductLTV({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/commercial-intelligence/product-ltv`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const products=data.products||[];
  if(!products.length) return<SecCollecting title="Product LTV" status={data.collection_status} dep="Populate user_product_ltv via payment_ledger sync or manual attribution."/>;

  return(
    <>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(data.total_ltv_records)} label="LTV Records" color="blue"/>
        <RadialKPI value={data.best_product||'—'} label="Highest LTV Product" color="green"/>
        <RadialKPI value={data.worst_product||'—'} label="Lowest LTV Product" color="red"/>
        <RadialKPI value={`A$${fmt(products[0]?.avg_ltv||0)}`} label="Best Avg LTV" color="green"/>
      </div>

      {products.length>0&&(
        <Panel title="LTV by Product" sub="Average LTV per user" height={220} mb={12}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={products} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="product_id" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip formatter={v=>[`A$${fmt(v)}`,'Avg LTV']}/>
              <Bar dataKey="avg_ltv" name="Avg LTV" radius={[3,3,0,0]}>{products.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%8]}/>)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <TableCard title="Product LTV Breakdown" count={products.length}>
        <table style={s.table}>
          <thead><tr>{['Product','Users','Total Revenue','Avg LTV','Median LTV','Avg Sub Months','Refund Rate','Active'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {products.map((p,i)=>(
              <tr key={p.product_id||i}>
                <td style={s.tds}><span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:CHART_COLORS[i%8],marginRight:8}}></span>{p.product_id||'—'}</td>
                <td style={s.td}>{fmt(p.user_count)}</td>
                <td style={{...s.td,fontWeight:700,color:T.green}}>A${fmt(p.total_revenue)}</td>
                <td style={{...s.td,fontWeight:700}}>A${fmt(p.avg_ltv)}</td>
                <td style={s.td}>{p.median_ltv!=null?`A$${fmt(p.median_ltv)}`:'—'}</td>
                <td style={s.td}>{p.avg_sub_months!=null?`${p.avg_sub_months}mo`:'—'}</td>
                <td style={{...s.td,color:(p.refund_rate_pct||0)>5?T.red:T.green}}>{p.refund_rate_pct||0}%</td>
                <td style={s.td}>{fmt(p.active_users)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ── CROSS PRODUCT MIGRATION ───────────────────────────────────────────────────
function SecCIMigration({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/commercial-intelligence/cross-product-migration`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const paths=data.top_migration_paths||[];
  const depth=data.ownership_depth||[];
  if(!paths.length&&!depth.length) return<SecCollecting title="Cross Product Migration" status={data.collection_status} dep="Log product access events via product_journey_events table."/>;

  return(
    <>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(data.total_journey_events)} label="Journey Events" color="blue"/>
        <RadialKPI value={fmt(data.total_tracked_users)} label="Users Tracked" color="blue"/>
        <RadialKPI value={fmt(paths.length)} label="Migration Paths" color="green"/>
      </div>

      {depth.length>0&&(
        <Panel title="Product Ownership Depth" sub="Users by number of products owned" height={200} mb={12}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={depth} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="products_owned" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false} label={{value:'Products Owned',position:'insideBottom',offset:-2,fontSize:10,fill:T.textSub}}/>
              <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip/>
              <Bar dataKey="users" name="Users" fill={T.chart1} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <TableCard title="Top Migration Paths" count={paths.length}>
        <table style={s.table}>
          <thead><tr>{['Journey','Event Type','Count','Avg Revenue at Event'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {paths.map((p,i)=>(
              <tr key={i}>
                <td style={s.tds}><span style={{fontWeight:700,color:T.accent}}>{p.path}</span></td>
                <td style={s.td}><span style={{fontSize:11,fontWeight:700,color:T.textSub}}>{p.event_type}</span></td>
                <td style={{...s.td,fontWeight:700}}>{fmt(p.event_count)}</td>
                <td style={s.td}>{p.avg_revenue!=null?`A$${fmt(p.avg_revenue)}`:'—'}</td>
              </tr>
            ))}
            {paths.length===0&&<tr><td colSpan={4} style={s.empty}>No migration paths yet — product journey events not captured</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ── SLUG PERFORMANCE ──────────────────────────────────────────────────────────
function SecSIPerformance({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);const[days,setDays]=useState(30);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load=useCallback(()=>{setData(null);fetch(`${api}/v1/staff/slug-intelligence/performance?days=${days}`,{credentials:'include'}).then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));},[api,days]);
  useEffect(()=>{load();},[load]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const top=data.top_slugs||[];
  const least=data.least_used||[];

  if(!top.length) return<SecCollecting title="Slug Performance" status={data.collection_status} dep="Wire generation events to log slug used — insert into slug_usage_log on each generation."/>;

  return(
    <>
      <div style={s.filterRow}><DF value={days} onChange={setDays}/></div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(data.total_slugs)} label="Unique Slugs Used" color="blue"/>
        <RadialKPI value={fmt(data.total_uses)} label="Total Uses" color="green"/>
        <RadialKPI value={top[0]?.slug?.split('-').slice(0,3).join('-')||'—'} label="Most Used Slug" color="blue"/>
      </div>

      {top.length>0&&(
        <Panel title="Top 15 Slugs by Uses" sub={`Last ${days} days`} height={280} mb={12}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top.slice(0,15).map(g=>({name:g.slug.split('-').slice(0,3).join('-'),uses:g.uses}))} layout="vertical" margin={{top:0,right:16,bottom:0,left:80}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} horizontal={false}/>
              <XAxis type="number" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis type="category" dataKey="name" tick={{fontSize:10,fill:T.textSub}} axisLine={false} tickLine={false} width={90}/>
              <Tooltip/>
              <Bar dataKey="uses" name="Uses" fill={T.chart1} radius={[0,3,3,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <TableCard title="Slug Performance" count={top.length}>
        <table style={s.table}>
          <thead><tr>{['Slug','Family','Domain','Category','Uses','Credits','Fail Rate'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {top.map((g,i)=>(
              <tr key={g.slug||i}>
                <td style={s.tds}><code style={{fontSize:11,color:T.accent,fontFamily:mono}}>{g.slug}</code></td>
                <td style={s.td}>{g.family_id||'—'}</td>
                <td style={s.td}>{g.domain||'—'}</td>
                <td style={s.td}>{g.category||'—'}</td>
                <td style={{...s.td,fontWeight:700}}>{fmt(g.uses)}</td>
                <td style={s.td}>{fmt(g.credits||0)}</td>
                <td style={{...s.td,color:(g.fail_rate||0)>=20?T.red:(g.fail_rate||0)>=10?T.amber:T.green,fontWeight:700}}>{g.fail_rate!=null?`${g.fail_rate}%`:'—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ── SLUG FAMILIES ─────────────────────────────────────────────────────────────
function SecSIFamilies({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);const[days,setDays]=useState(30);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load=useCallback(()=>{setData(null);fetch(`${api}/v1/staff/slug-intelligence/families?days=${days}`,{credentials:'include'}).then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));},[api,days]);
  useEffect(()=>{load();},[load]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const families=data.families||[];
  if(!families.length) return<SecCollecting title="Slug Families" status={data.collection_status} dep="Add slug families in Executive → Intel Foundation → Slug Families, then wire slug_usage_log.family_id."/>;

  const totalUses=families.reduce((s,f)=>s+f.uses,0);
  return(
    <>
      <div style={s.filterRow}><DF value={days} onChange={setDays}/></div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(data.family_count)} label="Slug Families" color="blue"/>
        <RadialKPI value={fmt(totalUses)} label="Total Uses" color="green"/>
        <RadialKPI value={families[0]?.family_name||'—'} label="Top Family" color="blue"/>
      </div>

      {families.length>0&&(
        <Panel title="Family Usage" sub={`Last ${days} days`} height={220} mb={12}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={families.slice(0,10)} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="family_name" tick={{fontSize:10,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip/>
              <Bar dataKey="uses" name="Uses" radius={[3,3,0,0]}>{families.slice(0,10).map((_,i)=><Cell key={i} fill={CHART_COLORS[i%8]}/>)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <TableCard title="Slug Family Performance" count={families.length}>
        <table style={s.table}>
          <thead><tr>{['Family','Domain','Engine','Category','Uses','Credits','Share'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {families.map((f,i)=>{
              const share=totalUses>0?Math.round(f.uses/totalUses*100):0;
              return(
                <tr key={f.family_id||i}>
                  <td style={s.tds}><span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:CHART_COLORS[i%8],marginRight:8}}></span>{f.family_name}</td>
                  <td style={s.td}>{f.domain||'—'}</td>
                  <td style={s.td}>{f.engine||'—'}</td>
                  <td style={s.td}>{f.category||'—'}</td>
                  <td style={{...s.td,fontWeight:700}}>{fmt(f.uses)}</td>
                  <td style={s.td}>{fmt(f.credits||0)}</td>
                  <td style={s.td}><div style={{display:'flex',alignItems:'center',gap:6}}><div style={{...s.pw,flex:1,marginTop:0,height:4}}><div style={s.pb(share,CHART_COLORS[i%8])}/></div><span style={{fontSize:11,color:T.textSub}}>{share}%</span></div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ── COMMUNITY DEMAND ──────────────────────────────────────────────────────────
function SecSIDemand({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/slug-intelligence/community-demand`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const cats=data.categories||[];
  if(!cats.length) return<SecCollecting title="Community Demand" status={data.collection_status} dep="Create vote categories in Intel Foundation → Community Voting, then capture votes."/>;

  return(
    <>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(data.total_votes)} label="Total Votes" color="blue"/>
        <RadialKPI value={fmt((data.pending||[]).length)} label="Pending Requests" color="amber"/>
        <RadialKPI value={fmt((data.delivered||[]).length)} label="Delivered" color="green"/>
      </div>

      <TableCard title="Community Demand Rankings" count={cats.length}>
        <table style={s.table}>
          <thead><tr>{['Request','Type','Votes','30D Votes','Status','Delivered'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {cats.map((c,i)=>(
              <tr key={c.category_id||i}>
                <td style={s.tds}><span style={{fontWeight:600}}>{c.category_name}</span></td>
                <td style={s.td}><span style={{fontSize:11,fontWeight:700,color:T.accent}}>{c.category_type}</span></td>
                <td style={{...s.td,fontWeight:700}}>{fmt(c.vote_count)}</td>
                <td style={{...s.td,color:c.recent_votes_30d>0?T.green:T.textSub}}>{fmt(c.recent_votes_30d)}</td>
                <td style={s.td}><MCStatusPill status={c.status}/></td>
                <td style={{...s.td,color:c.is_delivered?T.green:T.textSub,fontWeight:c.is_delivered?700:400}}>{c.is_delivered?'✓ Delivered':'Pending'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ── FUTURE PLANNING ───────────────────────────────────────────────────────────
function SecSIPlanning({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/slug-intelligence/planning`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const opps=data.opportunities||[];
  const growing=data.fastest_growing||[];
  if(!opps.length&&!growing.length) return<SecCollecting title="Future Planning" status={data.collection_status} dep="Requires slug families and community demand data. Add families and capture votes first."/>;

  return(
    <>
      {opps.length>0&&(
        <TableCard title="Top Opportunities (High Demand / Low Supply)" count={opps.length}>
          <table style={s.table}>
            <thead><tr>{['Family','Domain','Slug Count','Community Demand','Usage 30D','Opportunity Score'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {opps.map((o,i)=>(
              <tr key={o.family_id||i}>
                <td style={s.tds}>{o.family_name||o.family_id}</td>
                <td style={s.td}>{o.domain||'—'}</td>
                <td style={s.td}>{fmt(o.slug_count)}</td>
                <td style={{...s.td,fontWeight:700,color:o.community_demand>0?T.accent:T.textSub}}>{fmt(o.community_demand)}</td>
                <td style={s.td}>{fmt(o.usage_30d)}</td>
                <td style={{...s.td,fontWeight:700,color:o.opportunity_score>50?T.green:T.amber}}>{fmt(o.opportunity_score)}</td>
              </tr>
            ))}
          </tbody>
          </table>
        </TableCard>
      )}

      {growing.length>0&&(
        <TableCard title="Fastest Growing Slugs">
          <table style={s.table}>
            <thead><tr>{['Slug','Family','Domain','Recent Uses','Prior Uses','Growth %'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {growing.map((g,i)=>(
              <tr key={g.slug||i}>
                <td style={s.tds}><code style={{fontSize:11,fontFamily:mono,color:T.accent}}>{g.slug}</code></td>
                <td style={s.td}>{g.family_id||'—'}</td>
                <td style={s.td}>{g.domain||'—'}</td>
                <td style={{...s.td,fontWeight:700,color:T.green}}>{fmt(g.recent_uses)}</td>
                <td style={s.td}>{fmt(g.prior_uses)}</td>
                <td style={{...s.td,fontWeight:700,color:g.growth_pct!=null?(g.growth_pct>0?T.green:T.red):T.textSub}}>{g.growth_pct!=null?`${g.growth_pct>0?'+':''}${g.growth_pct}%`:'New'}</td>
              </tr>
            ))}
          </tbody>
          </table>
        </TableCard>
      )}
    </>
  );
}

// ── COMMUNITY VOTING INTELLIGENCE ─────────────────────────────────────────────
function SecMCVoting({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/marketing-community/voting-intelligence`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const cats=data.top_categories||[];
  const trend=data.vote_trend||[];
  if(!cats.length) return<SecCollecting title="Community Voting" status={data.collection_status} dep="Create vote categories in Intel Foundation → Community Voting, then log votes via the API or future customer-facing vote UI."/>;

  return(
    <>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(data.total_votes)} label="Total Votes" color="blue"/>
        <RadialKPI value={fmt(data.delivered_count)} label="Delivered" color="green"/>
        <RadialKPI value={fmt(data.pending_count)} label="Pending" color="amber"/>
        <RadialKPI value={fmt(cats.length)} label="Categories" color="blue"/>
      </div>

      {trend.length>0&&(
        <Panel title="Vote Trend" sub="Last 30 days" height={180} mb={12}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="date" tick={{fontSize:10,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip/>
              <Line type="monotone" dataKey="votes" stroke={T.chart1} strokeWidth={2} dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:12}}>
        {(data.type_summary||[]).map((t,i)=>(
          <div key={t.category_type||i} style={{...s.panel,padding:'12px 14px',borderLeft:`3px solid ${CHART_COLORS[i%8]}`}}>
            <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',marginBottom:6}}>{t.category_type}</div>
            <div style={{fontSize:22,fontWeight:800,color:T.text}}>{fmt(t.total_votes)}</div>
            <div style={{fontSize:11,color:T.textSub}}>{fmt(t.categories)} categories · {fmt(t.delivered)} delivered</div>
          </div>
        ))}
      </div>

      <TableCard title="Vote Rankings" count={cats.length}>
        <table style={s.table}>
          <thead><tr>{['Request','Type','Votes','Status','Delivered'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {cats.map((c,i)=>(
              <tr key={c.category_id||i}>
                <td style={s.tds}>{c.category_name}</td>
                <td style={s.td}><span style={{fontSize:11,fontWeight:700,color:T.accent}}>{c.category_type}</span></td>
                <td style={{...s.td,fontWeight:700}}>{fmt(c.vote_count)}</td>
                <td style={s.td}><MCStatusPill status={c.status}/></td>
                <td style={{...s.td,color:c.is_delivered?T.green:T.textSub,fontWeight:c.is_delivered?700:400}}>{c.is_delivered?'✓ Delivered':'Pending'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ── GENERATOR ATTRIBUTION ─────────────────────────────────────────────────────
function SecGIAttribution({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);const[days,setDays]=useState(30);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load=useCallback(()=>{setData(null);fetch(`${api}/v1/staff/generator-intelligence/attribution?days=${days}`,{credentials:'include'}).then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));},[api,days]);
  useEffect(()=>{load();},[load]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const bySlug=data.by_generator||[];
  const byDomain=data.by_domain||[];
  if(!bySlug.length) return<SecCollecting title="Generator Attribution" status={data.collection_status} dep="Wire generation events to insert into generator_attribution table on each completed job."/>;

  return(
    <>
      <div style={s.filterRow}><DF value={days} onChange={setDays}/></div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(data.total_uses)} label="Total Uses" color="blue"/>
        <RadialKPI value={fmt(data.total_users)} label="Unique Users" color="green"/>
        <RadialKPI value={fmt(data.first_uses_30d)} label="First Uses (New Discovery)" color="blue"/>
        <RadialKPI value={bySlug[0]?.generator_slug?.split('-').slice(0,2).join('-')||'—'} label="Most Used Generator" color="blue"/>
      </div>

      {byDomain.length>0&&(
        <Panel title="Attribution by Domain" sub={`Last ${days} days`} height={200} mb={12}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byDomain} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="domain" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis yAxisId="l" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis yAxisId="r" orientation="right" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip content={<TT/>}/>
              <Legend iconSize={9} wrapperStyle={{fontSize:12,color:T.textSub}}/>
              <Bar yAxisId="l" dataKey="uses" name="Uses" fill={T.chart1} radius={[3,3,0,0]}/>
              <Bar yAxisId="r" dataKey="unique_users" name="Users" fill={T.chart2} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <TableCard title="Generator Attribution Detail" count={bySlug.length}>
        <table style={s.table}>
          <thead><tr>{['Generator','Engine','Domain','Uses','Credits','Fail Rate','First Uses','Trial Uses'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {bySlug.map((g,i)=>(
              <tr key={g.generator_slug||i}>
                <td style={s.tds}><code style={{fontSize:11,color:T.accent,fontFamily:mono}}>{g.generator_slug}</code></td>
                <td style={s.td}>{g.engine||'—'}</td>
                <td style={s.td}>{g.domain||'—'}</td>
                <td style={{...s.td,fontWeight:700}}>{fmt(g.uses)}</td>
                <td style={s.td}>{fmt(g.credits)}</td>
                <td style={{...s.td,color:(g.fail_rate||0)>=20?T.red:(g.fail_rate||0)>=10?T.amber:T.green,fontWeight:700}}>{g.fail_rate}%</td>
                <td style={{...s.td,color:g.first_uses>0?T.green:T.textSub}}>{fmt(g.first_uses)}</td>
                <td style={{...s.td,color:g.trial_uses>0?T.accent:T.textSub}}>{fmt(g.trial_uses)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ── Shared: Phase B/C unavailable state ──────────────────────────────────────
function SecSIUnavailable({title, dep}){
  return(
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:340,padding:48,textAlign:'center'}}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.amberBorder} strokeWidth="1.5" style={{marginBottom:16}}>
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <div style={{fontSize:16,fontWeight:700,color:T.textMid,marginBottom:8}}>{title}</div>
      <div style={{fontSize:13,color:T.textSub,marginBottom:16,maxWidth:400,lineHeight:1.6}}>
        This page requires a data source that has not yet been set up.
      </div>
      <div style={{background:T.amberBg,border:`1px solid ${T.amberBorder}`,borderRadius:8,padding:'10px 16px',fontSize:12,color:T.amber,fontWeight:600,maxWidth:460}}>
        Required: {dep}
      </div>
    </div>
  );
}

// ── Business health deterministic score engine ────────────────────────────────
function _bizHealthScore(data){
  // Returns {overall, components, status, label}
  const scores = {};
  if(data.revenue_growth_pct != null) scores.revenue_growth = Math.min(Math.max(50 + data.revenue_growth_pct, 0), 100);
  if(data.subscriber_growth_pct != null) scores.subscriber_growth = Math.min(Math.max(50 + data.subscriber_growth_pct, 0), 100);
  if(data.churn_rate_pct != null) scores.retention = Math.min(Math.max(100 - data.churn_rate_pct * 5, 0), 100);
  if(data.trial_to_paid_pct != null) scores.conversion = Math.min(data.trial_to_paid_pct * 4, 100);
  if(data.margin_pct != null) scores.margin = Math.min(Math.max(data.margin_pct, 0), 100);
  if(data.job_success_rate != null) scores.platform = data.job_success_rate;
  const vals = Object.values(scores).filter(v => v != null);
  const overall = vals.length ? Math.round(vals.reduce((a,b)=>a+b,0)/vals.length) : null;
  const label = overall==null?'Insufficient Data':overall>=85?'Excellent':overall>=70?'Strong':overall>=50?'Developing':overall>=30?'At Risk':'Critical';
  const statusColor = overall==null?T.textSub:overall>=85?T.green:overall>=70?T.accent:overall>=50?T.amber:overall>=30?'#c05621':T.red;
  return {overall, components:scores, label, statusColor, componentCount:vals.length};
}

// ── EXECUTIVE OVERVIEW ────────────────────────────────────────────────────────
function SecExecOverview({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    Promise.all([
      fetch(`${api}/v1/admin/overview`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/commercial-intelligence/revenue`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/commercial-intelligence/subscribers`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/commercial-intelligence/owner-target`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/marketing-community/channels`,{credentials:'include'}).then(r=>r.json()).catch(()=>({channels:[]})),
      fetch(`${api}/v1/staff/marketing-community/campaigns`,{credentials:'include'}).then(r=>r.json()).catch(()=>({campaigns:[]})),
      fetch(`${api}/v1/admin/analytics/generators?days=30&limit=5`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
    ]).then(([ov,rev,subs,target,channels,campaigns,gens])=>{
      setData({ov,rev,subs,target,channels:channels.channels||[],campaigns:campaigns.campaigns||[],gens});
    }).catch(()=>setErr('Failed to load executive overview'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const {ov,rev,subs,target,channels,campaigns,gens} = data;
  const health = _bizHealthScore({
    revenue_growth_pct: null,
    subscriber_growth_pct: subs.net_growth!=null&&subs.active_subscribers>0?subs.net_growth/subs.active_subscribers*100:null,
    churn_rate_pct: null,
    trial_to_paid_pct: null,
    margin_pct: target.monthly_profit_aud&&target.revenue_30d_aud>0?target.monthly_profit_aud/target.revenue_30d_aud*100:null,
    job_success_rate: ov.success_rate||null,
  });

  const bestChan = [...channels].sort((a,b)=>(b.revenue_30d_aud||0)-(a.revenue_30d_aud||0))[0];
  const bestCampaign = [...campaigns].filter(c=>c.status==='completed'||c.status==='active').sort((a,b)=>(b.spent_aud||0)-(a.spent_aud||0))[0];
  const topGen = (gens.top_generators||[])[0];

  const TARGET = 6000;
  const profit = target.monthly_profit_aud||0;
  const progressPct = Math.min(Math.round(profit/TARGET*100),200);
  const progressColor = progressPct>=100?T.green:progressPct>=85?T.amber:T.red;

  return(
    <>
      {/* Row 1: Revenue */}
      <div style={{background:T.bg,borderRadius:10,padding:'16px 20px',marginBottom:12,border:`1px solid ${T.border}`}}>
        <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:10}}>Revenue</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:12}}>
          <RadialKPI value={`A$${fmt(rev.revenue_30d_aud)}`} label="Revenue 30D" color="green"/>
          <RadialKPI value={`A$${fmt(rev.annual_run_rate)}`} label="Annual Run Rate" color="blue"/>
          <RadialKPI value={`A$${fmt(rev.subscription_rev)}`} label="Subscription Rev" color="blue"/>
          <RadialKPI value={`A$${fmt(rev.oneoff_rev)}`} label="One-Off Rev" color="blue"/>
          <RadialKPI value={`A$${fmt(rev.topup_rev)}`} label="Topup Rev" color="blue"/>
          <RadialKPI value={`A$${fmt(rev.refunds_30d)}`} label="Refunds 30D" color={(rev.refunds_30d||0)>0?'red':'green'}/>
        </div>
      </div>

      {/* Row 2: Subscribers + Platform */}
      <div style={{background:T.bg,borderRadius:10,padding:'16px 20px',marginBottom:12,border:`1px solid ${T.border}`}}>
        <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:10}}>Subscribers & Platform</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:12}}>
          <RadialKPI value={fmt(subs.active_subscribers)} label="Active Subscribers" color="green"/>
          <RadialKPI value={fmt(subs.new_30d)} label="New 30D" color="green"/>
          <RadialKPI value={fmt(subs.cancelled_30d)} label="Cancelled 30D" color={(subs.cancelled_30d||0)>0?'red':'green'}/>
          <RadialKPI value={fmt(subs.trial_users)} label="Trial Users" color="blue"/>
          <RadialKPI value={fmt(ov.total_generations)} label="Total Generations" color="blue"/>
          <RadialKPI value={`${ov.success_rate||0}%`} label="Job Success Rate" color={(ov.success_rate||0)>=95?'green':(ov.success_rate||0)>=85?'amber':'red'}/>
        </div>
      </div>

      {/* Row 3: Best performers */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
        <div style={{...s.panel,padding:'14px 16px',borderLeft:`4px solid ${T.chart2}`}}>
          <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>Top Channel</div>
          <div style={{fontSize:17,fontWeight:700,color:T.text,marginBottom:2}}>{bestChan?.channel_name||bestChan?.platform||'—'}</div>
          <div style={{fontSize:12,color:T.textSub}}>{bestChan?.revenue_30d_aud>0?`A$${fmt(bestChan.revenue_30d_aud)} revenue 30D`:'Add channels in Social Channels'}</div>
        </div>
        <div style={{...s.panel,padding:'14px 16px',borderLeft:`4px solid ${T.chart3}`}}>
          <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>Top Campaign</div>
          <div style={{fontSize:17,fontWeight:700,color:T.text,marginBottom:2}}>{bestCampaign?.campaign_name||'—'}</div>
          <div style={{fontSize:12,color:T.textSub}}>{bestCampaign?`Status: ${bestCampaign.status}`:'No campaigns yet'}</div>
        </div>
        <div style={{...s.panel,padding:'14px 16px',borderLeft:`4px solid ${T.chart4}`}}>
          <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>Top Generator</div>
          <div style={{fontSize:17,fontWeight:700,color:T.text,marginBottom:2}}>{topGen?.name||topGen?.slug||'—'}</div>
          <div style={{fontSize:12,color:T.textSub}}>{topGen?`${fmt(topGen.uses)} uses 30D`:''}</div>
        </div>
      </div>

      {/* Owner target progress */}
      <div style={{...s.panel,padding:'16px 20px',marginBottom:12,borderLeft:`4px solid ${progressColor}`}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:2}}>Owner Target — A$6,000/month operating profit</div>
            <div style={{fontSize:13,color:T.textSub}}>A${fmt(profit)} current · A${fmt(Math.max(6000-profit,0))} remaining</div>
          </div>
          <div style={{fontSize:28,fontWeight:800,color:progressColor}}>{progressPct}%</div>
        </div>
        <div style={{height:8,background:T.borderSoft,borderRadius:4,overflow:'hidden'}}>
          <div style={{height:'100%',width:`${Math.min(progressPct,100)}%`,background:progressColor,borderRadius:4,transition:'width 0.4s'}}/>
        </div>
      </div>

      {/* Business Health summary */}
      <div style={{...s.panel,padding:'16px 20px',borderLeft:`4px solid ${health.statusColor}`}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:700,color:T.textMid}}>Business Health</div>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            {health.overall!=null&&<div style={{fontSize:24,fontWeight:800,color:health.statusColor}}>{health.overall}</div>}
            <span style={{padding:'3px 10px',borderRadius:10,fontSize:12,fontWeight:700,background:health.statusColor+'20',color:health.statusColor,border:`1px solid ${health.statusColor}40`}}>{health.label}</span>
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
          {Object.entries(health.components).map(([k,v])=>(
            <div key={k} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 10px',background:T.bg,borderRadius:6,border:`1px solid ${T.border}`}}>
              <span style={{fontSize:12,color:T.textSub,textTransform:'capitalize'}}>{k.replace(/_/g,' ')}</span>
              <span style={{fontSize:12,fontWeight:700,color:v>=70?T.green:v>=50?T.amber:T.red}}>{Math.round(v)}</span>
            </div>
          ))}
          {health.componentCount===0&&<div style={{gridColumn:'1/-1',fontSize:12,color:T.textSub}}>More data required to calculate health score — populate payment_ledger and subscription tables.</div>}
        </div>
      </div>
    </>
  );
}

// ── BUSINESS HEALTH ───────────────────────────────────────────────────────────
function SecExecHealth({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    Promise.all([
      fetch(`${api}/v1/staff/commercial-intelligence/revenue`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/commercial-intelligence/subscribers`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/commercial-intelligence/retention`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/commercial-intelligence/conversions`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/commercial-intelligence/owner-target`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/platform/overview`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
    ]).then(([rev,subs,ret,conv,target,plat])=>setData({rev,subs,ret,conv,target,plat})).catch(()=>setErr('Failed'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const {subs,ret,conv,target,plat} = data;
  const marginPct = target.revenue_30d_aud>0?(target.monthly_profit_aud||0)/target.revenue_30d_aud*100:null;
  const health = _bizHealthScore({
    subscriber_growth_pct: subs.net_growth!=null&&subs.active_subscribers>0?subs.net_growth/subs.active_subscribers*100:null,
    churn_rate_pct: ret.churn_rate_pct||null,
    trial_to_paid_pct: conv.trial_to_paid_pct||null,
    margin_pct: marginPct,
    job_success_rate: plat.success_rate||null,
  });

  const COMPONENT_LABELS = {
    revenue_growth:'Revenue Growth', subscriber_growth:'Subscriber Growth', retention:'Retention',
    conversion:'Conversion', margin:'Profit Margin', platform:'Platform Health', community_growth:'Community Growth',
  };
  const COMPONENT_DESC = {
    revenue_growth:'Month-over-month revenue change', subscriber_growth:'Net subscriber growth vs active base',
    retention:'Inverse of churn rate', conversion:'Trial-to-paid rate × 4', margin:'Operating profit as % of revenue',
    platform:'Generation job success rate', community_growth:'Channel follower growth rate',
  };

  return(
    <>
      <div style={{...s.panel,padding:'20px 24px',marginBottom:16,borderLeft:`4px solid ${health.statusColor}`}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
          <div style={{fontSize:15,fontWeight:700,color:T.textMid}}>Overall Business Health</div>
          <span style={{padding:'3px 12px',borderRadius:10,fontSize:13,fontWeight:700,background:health.statusColor+'20',color:health.statusColor,border:`1px solid ${health.statusColor}40`}}>{health.label}</span>
        </div>
        <div style={{fontSize:36,fontWeight:800,color:health.statusColor,marginBottom:4}}>{health.overall!=null?health.overall:'—'}</div>
        <div style={{fontSize:12,color:T.textSub}}>Weighted average of {health.componentCount} available component{health.componentCount!==1?'s':''} out of 7. Missing components are excluded from the average.</div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
        {['revenue_growth','subscriber_growth','retention','conversion','margin','platform','community_growth'].map(k=>{
          const v = health.components[k];
          const color = v==null?T.textFaint:v>=85?T.green:v>=70?T.accent:v>=50?T.amber:T.red;
          const label = v==null?'No Data':v>=85?'Excellent':v>=70?'Strong':v>=50?'Developing':v>=30?'At Risk':'Critical';
          return(
            <div key={k} style={{...s.panel,padding:'14px 16px',borderLeft:`3px solid ${color}`}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                <div style={{fontSize:13,fontWeight:700,color:T.textMid}}>{COMPONENT_LABELS[k]}</div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  {v!=null&&<span style={{fontSize:16,fontWeight:800,color}}>{Math.round(v)}</span>}
                  <span style={{fontSize:11,fontWeight:700,color,background:color+'18',border:`1px solid ${color}30`,padding:'1px 7px',borderRadius:8}}>{label}</span>
                </div>
              </div>
              <div style={{fontSize:11,color:T.textSub}}>{COMPONENT_DESC[k]}</div>
              {v==null&&<div style={{fontSize:11,color:T.amberBorder,marginTop:4}}>Unavailable — requires additional backend data</div>}
              {v!=null&&(
                <div style={{height:5,background:T.borderSoft,borderRadius:3,marginTop:8,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${v}%`,background:color,borderRadius:3}}/>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{...s.panel,padding:'14px 16px',background:T.amberBg,border:`1px solid ${T.amberBorder}`}}>
        <div style={{fontSize:12,fontWeight:700,color:T.amber,marginBottom:4}}>Score Methodology</div>
        <div style={{fontSize:12,color:T.textMid,lineHeight:1.6}}>
          All scores are deterministic. Revenue Growth: 50 + monthly % change. Subscriber Growth: 50 + net growth rate. Retention: 100 − (churn × 5). Conversion: trial-to-paid % × 4. Margin: profit as % of revenue. Platform: job success rate. Community: N/A pending API connection. Missing components are excluded from the overall average — a score of 70 from 4 components is more reliable than 70 from 7.
        </div>
      </div>
    </>
  );
}

// ── GROWTH SIGNALS ────────────────────────────────────────────────────────────
function SecExecSignals({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    Promise.all([
      fetch(`${api}/v1/admin/analytics/generators?days=30&limit=20`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/commercial-intelligence/subscribers`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/marketing-community/channels`,{credentials:'include'}).then(r=>r.json()).catch(()=>({channels:[]})),
      fetch(`${api}/v1/staff/marketing-community/campaigns`,{credentials:'include'}).then(r=>r.json()).catch(()=>({campaigns:[]})),
    ]).then(([gens,subs,chans,camps])=>setData({gens,subs,channels:chans.channels||[],campaigns:camps.campaigns||[]})).catch(()=>setErr('Failed'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const {gens,subs,channels,campaigns} = data;
  const topGens=(gens.top_generators||[]).slice(0,5);
  const leastGens=(gens.least_used||[]).slice(0,3);
  const byCategory=(gens.by_category||[]).sort((a,b)=>b.uses-a.uses);

  // Signal classification: green = growing, amber = stable, red = declining
  function SignalRow({label,value,signal,note}){
    const color=signal==='green'?T.green:signal==='red'?T.red:T.amber;
    return(
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',borderBottom:`1px solid ${T.borderSoft}`}}>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:T.text}}>{label}</div>
          {note&&<div style={{fontSize:11,color:T.textSub}}>{note}</div>}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:13,fontWeight:700,color:T.textMid}}>{value}</span>
          <span style={{fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:10,background:color+'18',color,border:`1px solid ${color}30`}}>
            {signal==='green'?'↑ Growing':signal==='red'?'↓ Declining':'→ Stable'}
          </span>
        </div>
      </div>
    );
  }

  const netGrowth=subs.net_growth||0;
  const subSignal=netGrowth>5?'green':netGrowth<-2?'red':'amber';

  const activeChans=channels.filter(c=>c.registrations_30d>0);
  const bestChan=[...channels].sort((a,b)=>(b.registrations_30d||0)-(a.registrations_30d||0))[0];

  const activeCamps=campaigns.filter(c=>c.status==='active');

  return(
    <>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
        {/* Subscriber signals */}
        <div style={s.panel}>
          <div style={{padding:'12px 16px',borderBottom:`1px solid ${T.border}`,fontSize:13,fontWeight:700,color:T.textMid}}>Subscriber Signals</div>
          <SignalRow label="Net subscriber growth 30D" value={`${netGrowth>=0?'+':''}${fmt(netGrowth)}`} signal={subSignal} note={`${fmt(subs.active_subscribers)} active, ${fmt(subs.new_30d)} new, ${fmt(subs.cancelled_30d)} cancelled`}/>
          <SignalRow label="Trial users" value={fmt(subs.trial_users)} signal={subs.trial_users>0?'green':'amber'} note="Active trials in system"/>
          <SignalRow label="Reactivations 30D" value={fmt(subs.reactivations_30d)} signal={(subs.reactivations_30d||0)>0?'green':'amber'} note="Previously cancelled returning"/>
        </div>

        {/* Generator signals */}
        <div style={s.panel}>
          <div style={{padding:'12px 16px',borderBottom:`1px solid ${T.border}`,fontSize:13,fontWeight:700,color:T.textMid}}>Generator Signals</div>
          {topGens.length===0&&<div style={{padding:'16px',fontSize:12,color:T.textSub}}>No generator data yet</div>}
          {topGens.slice(0,3).map(g=>(
            <SignalRow key={g.slug} label={g.name||g.slug} value={`${fmt(g.uses)} uses`} signal={g.uses>10?'green':'amber'} note={`${fmt(g.fail_rate||0)}% fail rate`}/>
          ))}
          {leastGens.filter(g=>g.uses===0).length>0&&(
            <SignalRow label={`${leastGens.filter(g=>g.uses===0).length} generators unused`} value="0 uses" signal="red" note="Consider reviewing or retiring"/>
          )}
        </div>

        {/* Channel signals */}
        <div style={s.panel}>
          <div style={{padding:'12px 16px',borderBottom:`1px solid ${T.border}`,fontSize:13,fontWeight:700,color:T.textMid}}>Channel Signals</div>
          {channels.length===0&&<div style={{padding:'16px',fontSize:12,color:T.textSub}}>No channels configured — add in Social Channels</div>}
          {bestChan&&<SignalRow label={`Top: ${bestChan.channel_name||bestChan.platform}`} value={`${fmt(bestChan.registrations_30d||0)} registrations`} signal={(bestChan.registrations_30d||0)>0?'green':'amber'} note="Highest registrations 30D"/>}
          <SignalRow label="Channels with attribution" value={fmt(activeChans.length)} signal={activeChans.length>0?'green':'amber'} note={`of ${channels.length} total channels`}/>
          {channels.length===0&&<SignalRow label="UTM tracking" value="Not configured" signal="red" note="Required for channel attribution"/>}
        </div>

        {/* Campaign signals */}
        <div style={s.panel}>
          <div style={{padding:'12px 16px',borderBottom:`1px solid ${T.border}`,fontSize:13,fontWeight:700,color:T.textMid}}>Campaign Signals</div>
          {campaigns.length===0&&<div style={{padding:'16px',fontSize:12,color:T.textSub}}>No campaigns logged yet — create campaigns in Campaigns</div>}
          <SignalRow label="Active campaigns" value={fmt(activeCamps.length)} signal={activeCamps.length>0?'green':'amber'}/>
          {campaigns.length>0&&<SignalRow label="Total campaigns" value={fmt(campaigns.length)} signal="amber"/>}
          {byCategory.length>0&&<SignalRow label={`Top category: ${byCategory[0]?.category||'Unknown'}`} value={`${fmt(byCategory[0]?.uses)} uses`} signal="green"/>}
        </div>
      </div>

      {/* Category growth breakdown */}
      {byCategory.length>0&&(
        <Panel title="Generation Volume by Category" sub="Last 30 days" height={220}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byCategory.slice(0,8)} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="category" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip content={<TT/>}/>
              <Bar dataKey="uses" name="Uses" fill={T.chart1} radius={[3,3,0,0]}>{byCategory.slice(0,8).map((_,i)=><Cell key={i} fill={CHART_COLORS[i%8]}/>)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}
    </>
  );
}

// ── FORECASTING (Phase C — requires historical snapshots) ─────────────────────
function SecExecForecast({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    Promise.all([
      fetch(`${api}/v1/staff/commercial-intelligence/subscribers`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/commercial-intelligence/revenue`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/staff/commercial-intelligence/owner-target`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
    ]).then(([subs,rev,target])=>setData({subs,rev,target})).catch(()=>setErr('Failed'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const {subs,rev,target} = data;
  // Simple linear projection from current 30D snapshot
  // Confidence is LOW because we have only one data point
  const netGrowthPerMonth=subs.net_growth||0;
  const currentSubs=subs.active_subscribers||0;
  const rev30=rev.revenue_30d_aud||0;
  const profit30=target.monthly_profit_aud||0;
  const arpu=currentSubs>0?rev30/currentSubs:0;

  function project(months){
    const projSubs=Math.max(Math.round(currentSubs+netGrowthPerMonth*months),0);
    const projRev=Math.round(projSubs*arpu);
    const projProfit=rev30>0?Math.round(projRev*(profit30/rev30)):0;
    return{subs:projSubs,rev:projRev,profit:projProfit};
  }

  const periods=[
    {label:'30 Days',months:1},
    {label:'90 Days',months:3},
    {label:'180 Days',months:6},
    {label:'365 Days',months:12},
  ];

  const hasHistory=currentSubs>0&&rev30>0;

  return(
    <>
      <div style={{...s.panel,padding:'14px 16px',marginBottom:16,background:T.amberBg,border:`1px solid ${T.amberBorder}`}}>
        <div style={{fontSize:12,fontWeight:700,color:T.amber,marginBottom:4}}>Confidence: {hasHistory?'LOW':'Insufficient Data'}</div>
        <div style={{fontSize:12,color:T.textMid,lineHeight:1.6}}>
          {hasHistory
            ? 'Projections use a single 30-day data point for linear extrapolation. Confidence will increase as historical snapshots accumulate. Projections assume constant growth rate and ARPU.'
            : 'Insufficient data to generate projections. Requires active subscribers and revenue data in payment_ledger.'
          }
        </div>
      </div>

      {!hasHistory&&<SecSIUnavailable title="Revenue Forecasting" dep="payment_ledger table with subscription revenue records"/>}

      {hasHistory&&(
        <>
          <div style={{...s.panel,padding:'12px 16px',marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:600,color:T.textSub,marginBottom:8}}>Assumptions</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
              {[
                {label:'Current Subscribers',val:fmt(currentSubs)},
                {label:'Monthly Net Growth',val:`+${fmt(netGrowthPerMonth)}`},
                {label:'ARPU',val:`A$${fmt(Math.round(arpu*100)/100)}`},
                {label:'Revenue 30D',val:`A$${fmt(rev30)}`},
                {label:'Profit 30D',val:`A$${fmt(profit30)}`},
                {label:'Growth Rate',val:currentSubs>0?`${(netGrowthPerMonth/currentSubs*100).toFixed(1)}%/mo`:'N/A'},
              ].map(({label,val})=>(
                <div key={label} style={{padding:'6px 10px',background:T.bg,borderRadius:6,border:`1px solid ${T.border}`}}>
                  <div style={{fontSize:11,color:T.textSub}}>{label}</div>
                  <div style={{fontSize:14,fontWeight:700,color:T.text}}>{val}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:12}}>
            {periods.map(({label,months})=>{
              const p=project(months);
              return(
                <div key={label} style={{...s.panel,padding:'16px'}}>
                  <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:8}}>{label}</div>
                  <div style={{marginBottom:6}}>
                    <div style={{fontSize:11,color:T.textSub}}>Subscribers</div>
                    <div style={{fontSize:18,fontWeight:700,color:T.accent}}>{fmt(p.subs)}</div>
                  </div>
                  <div style={{marginBottom:6}}>
                    <div style={{fontSize:11,color:T.textSub}}>Revenue/month</div>
                    <div style={{fontSize:18,fontWeight:700,color:T.green}}>A${fmt(p.rev)}</div>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:T.textSub}}>Profit/month</div>
                    <div style={{fontSize:18,fontWeight:700,color:p.profit>=6000?T.green:p.profit>=3000?T.amber:T.red}}>A${fmt(p.profit)}</div>
                  </div>
                  <div style={{marginTop:8,fontSize:10,color:T.amberBorder,fontWeight:700}}>LOW CONFIDENCE</div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

// ============================================================================
// GENERATOR INTELLIGENCE — 4 pages backed by /v1/admin/analytics/generators
// ============================================================================

function SecGIPerformance({api}){
  const[data,setData]=useState(null);const[days,setDays]=useState(30);const[err,setErr]=useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load=useCallback(()=>{setData(null);fetch(`${api}/v1/admin/analytics/generators?days=${days}&limit=50`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));},[api,days]);
  useEffect(()=>{load();},[load]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  const top=(data.top_generators||[]);
  const least=(data.least_used||[]);
  const totalUses=top.reduce((s,g)=>s+(g.uses||0),0);

  return(
    <>
      <div style={s.filterRow}><DF value={days} onChange={setDays}/></div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(top.length)} label="Generators Tracked" color="blue"/>
        <RadialKPI value={fmt(totalUses)} label="Total Uses" color="green"/>
        <RadialKPI value={fmt(top[0]?.slug||'—')} label="Most Used" color="blue"/>
        <RadialKPI value={fmt(least.filter(g=>g.uses===0).length)} label="Unused Generators" color={least.filter(g=>g.uses===0).length>0?'red':'green'}/>
      </div>

      {top.length>0&&(
        <Panel title="Top 20 Generators by Uses" sub={`Last ${days} days`} height={Math.max(300,Math.min(340,290+Math.min(top.length,20)*3))} mb={8}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top.slice(0,20).map(g=>({name:(g.slug||'').split('-').slice(0,3).join('-'),uses:g.uses,fail_rate:g.fail_rate||0}))} layout="vertical" margin={{top:0,right:16,bottom:0,left:80}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} horizontal={false}/>
              <XAxis type="number" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis type="category" dataKey="name" tick={{fontSize:10,fill:T.textSub}} axisLine={false} tickLine={false} width={90}/>
              <Tooltip content={<TT/>}/>
              <Bar dataKey="uses" name="Uses" fill={T.chart1} radius={[0,3,3,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <TableCard title="Generator Performance" count={top.length}>
        <table style={s.table}>
          <thead><tr>{['Generator','Uses','Images','Fail Rate','Status'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {top.map((g,i)=>{
              const fr=g.fail_rate||0;
              const status=fr>=30?'red':fr>=15?'amber':'green';
              return(
                <tr key={g.slug||i}>
                  <td style={s.tds}><div style={{fontWeight:600,fontSize:13}}>{g.name||g.slug}</div><code style={{fontSize:10,color:T.textFaint,fontFamily:mono}}>{g.slug}</code></td>
                  <td style={{...s.td,fontWeight:700}}>{fmt(g.uses)}</td>
                  <td style={s.td}>{fmt(g.images||0)}</td>
                  <td style={{...s.td,color:fr>=20?T.red:fr>=10?T.amber:T.green,fontWeight:700}}>{fr}%</td>
                  <td style={s.td}><span style={{...s.badge(status)}}>{status==='green'?'Healthy':status==='amber'?'Watch':'High Fail'}</span></td>
                </tr>
              );
            })}
            {top.length===0&&<tr><td colSpan={5} style={s.empty}>No generator data — run generations to populate</td></tr>}
          </tbody>
        </table>
      </TableCard>

      {least.length>0&&(
        <TableCard title="Least Used / Unused" count={least.length}>
          <table style={s.table}>
            <thead><tr>{['Generator','Uses','Status'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {least.map((g,i)=>(
                <tr key={g.slug||i}>
                  <td style={s.tds}>{g.name||g.slug}<br/><code style={{fontSize:10,color:T.textFaint,fontFamily:mono}}>{g.slug}</code></td>
                  <td style={s.td}><span style={s.badge(g.uses===0?'red':'amber')}>{fmt(g.uses)}</span></td>
                  <td style={s.td}>{g.uses===0?'Unused':'Underused'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      )}
    </>
  );
}

function SecGIEngines({api}){
  const[data,setData]=useState(null);const[days,setDays]=useState(30);const[err,setErr]=useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load=useCallback(()=>{setData(null);fetch(`${api}/v1/admin/analytics/generators?days=${days}&limit=100`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));},[api,days]);
  useEffect(()=>{load();},[load]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const byCategory=(data.by_category||[]).sort((a,b)=>b.uses-a.uses);
  const totalUses=byCategory.reduce((s,c)=>s+(c.uses||0),0);

  // Engine domains from existing SecGenerators data
  const DOMAINS=['dnd','professional','battlemaps'];
  const domainData=DOMAINS.map(d=>({
    domain:d.toUpperCase(),
    uses:(data.by_category||[]).filter(c=>(c.category||'').toLowerCase().includes(d)||
      (d==='dnd'&&['character','creature','environment','magic','object','faction','cinematic'].some(k=>(c.category||'').toLowerCase().includes(k))))
      .reduce((s,c)=>s+(c.uses||0),0),
  }));

  return(
    <>
      <div style={s.filterRow}><DF value={days} onChange={setDays}/></div>

      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,marginBottom:12}}>
        <Panel title="Usage by Category" sub={`Last ${days} days`} height={280}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byCategory.slice(0,10)} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="category" tick={{fontSize:10,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip content={<TT/>}/>
              <Bar dataKey="uses" name="Uses" radius={[3,3,0,0]}>{byCategory.slice(0,10).map((_,i)=><Cell key={i} fill={CHART_COLORS[i%8]}/>)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Domain Split" sub="By engine domain" height={280}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={domainData.map((d,i)=>({...d,fill:CHART_COLORS[i%8]}))} cx="50%" cy="50%" innerRadius={52} outerRadius={80} dataKey="uses" nameKey="domain" paddingAngle={3}>
                {domainData.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%8]}/>)}
              </Pie>
              <Tooltip formatter={(v)=>[fmt(v),'Uses']}/>
              <Legend iconSize={9} wrapperStyle={{fontSize:12,color:T.textSub}}/>
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <TableCard title="Category Breakdown" count={byCategory.length}>
        <table style={s.table}>
          <thead><tr>{['Category','Uses','Share'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {byCategory.map((c,i)=>{
              const share=totalUses>0?Math.round((c.uses||0)/totalUses*100):0;
              return(
                <tr key={c.category||i}>
                  <td style={s.tds}><span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:CHART_COLORS[i%8],marginRight:8}}></span>{c.category||'Unknown'}</td>
                  <td style={{...s.td,fontWeight:700}}>{fmt(c.uses)}</td>
                  <td style={s.td}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div style={{...s.pw,flex:1,marginTop:0,height:5}}><div style={s.pb(share,CHART_COLORS[i%8])}/></div>
                      <span style={{fontSize:12,color:T.textSub,minWidth:30}}>{share}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {byCategory.length===0&&<tr><td colSpan={3} style={s.empty}>No category data yet</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

function SecGITrends({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    Promise.all([
      fetch(`${api}/v1/admin/analytics/generators?days=7&limit=10`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/admin/analytics/generators?days=30&limit=10`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
      fetch(`${api}/v1/admin/analytics/generators?days=90&limit=10`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
    ]).then(([d7,d30,d90])=>setData({d7,d30,d90})).catch(()=>setErr('Failed'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const {d7,d30,d90} = data;
  const cat7=(d7.by_category||[]).sort((a,b)=>b.uses-a.uses);
  const cat30=(d30.by_category||[]).sort((a,b)=>b.uses-a.uses);

  // Compare 7D vs 30D normalised (7D * 4.3 ≈ monthly)
  const trendData=cat30.slice(0,8).map(c=>{
    const match7=cat7.find(x=>x.category===c.category);
    const proj7=(match7?.uses||0)*4.3;
    return{name:c.category||'?',monthly:c.uses,weekly_proj:Math.round(proj7)};
  });

  return(
    <>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
        <div style={{...s.panel,padding:'14px 16px',borderLeft:`4px solid ${T.chart1}`}}>
          <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>Total Uses 7D</div>
          <div style={{fontSize:28,fontWeight:800,color:T.text}}>{fmt((d7.top_generators||[]).reduce((s,g)=>s+(g.uses||0),0))}</div>
        </div>
        <div style={{...s.panel,padding:'14px 16px',borderLeft:`4px solid ${T.chart2}`}}>
          <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>Total Uses 30D</div>
          <div style={{fontSize:28,fontWeight:800,color:T.text}}>{fmt((d30.top_generators||[]).reduce((s,g)=>s+(g.uses||0),0))}</div>
        </div>
        <div style={{...s.panel,padding:'14px 16px',borderLeft:`4px solid ${T.chart3}`}}>
          <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>Total Uses 90D</div>
          <div style={{fontSize:28,fontWeight:800,color:T.text}}>{fmt((d90.top_generators||[]).reduce((s,g)=>s+(g.uses||0),0))}</div>
        </div>
      </div>

      {trendData.length>0&&(
        <Panel title="7-Day vs 30-Day Usage by Category" sub="Weekly pace (×4.3) vs actual monthly" height={260} mb={12}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trendData} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="name" tick={{fontSize:10,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip content={<TT/>}/>
              <Legend iconSize={9} wrapperStyle={{fontSize:12,color:T.textSub}}/>
              <Bar dataKey="monthly" name="30D Actual" fill={T.chart1} radius={[3,3,0,0]}/>
              <Bar dataKey="weekly_proj" name="7D Pace (×4.3)" fill={T.chart2} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <div style={{...s.panel,padding:'12px 16px',background:T.amberBg,border:`1px solid ${T.amberBorder}`}}>
        <div style={{fontSize:12,color:T.textMid}}>Daily and weekly trend charts require time-series data from the generator analytics backend. Currently showing period comparisons. Connect a time-series endpoint to enable full trend graphs.</div>
      </div>
    </>
  );
}

function SecGIRevenue({api}){
  const[data,setData]=useState(null);const[days,setDays]=useState(30);const[err,setErr]=useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load=useCallback(()=>{setData(null);fetch(`${api}/v1/admin/analytics/generators?days=${days}&limit=50`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));},[api,days]);
  useEffect(()=>{load();},[load]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const top=(data.top_generators||[]);
  const byCat=(data.by_category||[]);

  // Cost data: generators consume credits, credits have cost from ImageGenerationCost
  // The generators endpoint returns uses + images; cost per image derivable if cost data exists
  const hasCostData=top.some(g=>g.cost_usd!=null);

  return(
    <>
      <div style={s.filterRow}><DF value={days} onChange={setDays}/></div>

      {!hasCostData&&(
        <div style={{marginBottom:12}}>
          <CIUnavailable label="Revenue attribution by generator requires payment_ledger.payment_type_detail or per-generator cost tracking. Showing usage data only — cost/revenue join not yet implemented."/>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(top.length)} label="Generators" color="blue"/>
        <RadialKPI value={fmt(top.reduce((s,g)=>s+(g.uses||0),0))} label="Total Uses" color="green"/>
        <RadialKPI value={fmt(top.reduce((s,g)=>s+(g.images||0),0))} label="Total Images" color="blue"/>
      </div>

      <Panel title="Usage by Generator (Revenue Proxy)" sub={`Top generators by uses — last ${days} days. Revenue attribution requires payment_ledger join.`} height={300} mb={12}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={top.slice(0,15).map(g=>({name:(g.slug||'').split('-').slice(0,2).join('-'),uses:g.uses,images:g.images||0}))} layout="vertical" margin={{top:0,right:16,bottom:0,left:80}}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} horizontal={false}/>
            <XAxis type="number" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
            <YAxis type="category" dataKey="name" tick={{fontSize:10,fill:T.textSub}} axisLine={false} tickLine={false} width={90}/>
            <Tooltip content={<TT/>}/>
            <Legend iconSize={9} wrapperStyle={{fontSize:12,color:T.textSub}}/>
            <Bar dataKey="uses" name="Uses" fill={T.chart1} radius={[0,3,3,0]}/>
            <Bar dataKey="images" name="Images" fill={T.chart2} radius={[0,3,3,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      <TableCard title="Category Revenue Proxy" count={byCat.length}>
        <table style={s.table}>
          <thead><tr>{['Category','Uses','Images','Revenue Attribution'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {byCat.sort((a,b)=>b.uses-a.uses).map((c,i)=>(
              <tr key={c.category||i}>
                <td style={s.tds}>{c.category||'Unknown'}</td>
                <td style={{...s.td,fontWeight:700}}>{fmt(c.uses)}</td>
                <td style={s.td}>{fmt(c.images||0)}</td>
                <td style={{...s.td,fontSize:11,color:T.textFaint}}>Requires payment_ledger join</td>
              </tr>
            ))}
            {byCat.length===0&&<tr><td colSpan={4} style={s.empty}>No data yet</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ── RELEASE IMPACT (uses marketing_activities + marketing_releases + campaign data) ─
function SecMCReleaseImpact({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);const[window,setWindow]=useState(7);
  useEffect(()=>{
    Promise.all([
      fetch(`${api}/v1/staff/marketing-community/releases?days=180`,{credentials:'include'}).then(r=>r.json()).catch(()=>({releases:[]})),
      fetch(`${api}/v1/staff/marketing-community/campaign-impact?window=${window}`,{credentials:'include'}).then(r=>r.json()).catch(()=>({impacts:[]})),
      fetch(`${api}/v1/staff/commercial-intelligence/subscribers`,{credentials:'include'}).then(r=>r.json()).catch(()=>({})),
    ]).then(([rel,imp,subs])=>setData({releases:rel.releases||[],impacts:imp.impacts||[],subs})).catch(()=>setErr('Failed'));
  },[api,window]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const {releases,impacts} = data;
  const released=releases.filter(r=>r.status==='released'||r.status==='Released');
  const planned=releases.filter(r=>['planned','in progress','Planned','In Progress'].includes(r.status));
  const highImpact=impacts.filter(i=>i.registrations>0||i.revenue_aud>0).sort((a,b)=>(b.revenue_aud||0)-(a.revenue_aud||0));

  return(
    <>
      <div style={{...s.filterRow,marginBottom:12,alignItems:'center'}}>
        <span style={{fontSize:13,color:T.textSub,fontWeight:600}}>Impact Window:</span>
        {[1,2,7,14,30].map(d=>(
          <button key={d} style={{...s.btn(),background:window===d?T.accent:'',color:window===d?'#fff':T.textSub,borderColor:window===d?T.accent:T.border}} onClick={()=>setWindow(d)}>{d}D</button>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(releases.length)} label="Total Releases" color="blue"/>
        <RadialKPI value={fmt(released.length)} label="Released" color="green"/>
        <RadialKPI value={fmt(planned.length)} label="Planned / In Progress" color="blue"/>
        <RadialKPI value={fmt(highImpact.length)} label={`With Impact Data (${window}D)`} color={highImpact.length>0?'green':'amber'}/>
      </div>

      {highImpact.length>0&&(
        <TableCard title={`Release Impact — ${window}-day window after publication`} count={highImpact.length}>
          <table style={s.table}>
            <thead><tr>{['Activity/Release','Platform','Product','Published','Registrations','Revenue','ROAS','Attribution'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {highImpact.map((imp,i)=>(
                <tr key={imp.activity_id||i}>
                  <td style={s.tds}>{imp.title}</td>
                  <td style={s.td}><MCPlatformTag platform={imp.platform}/></td>
                  <td style={s.td}>{imp.product||'—'}</td>
                  <td style={{...s.td,fontSize:12,whiteSpace:'nowrap'}}>{imp.published_date?new Date(imp.published_date).toLocaleDateString('en-AU'):'—'}</td>
                  <td style={{...s.td,fontWeight:700,color:imp.registrations>0?T.green:T.textSub}}>{imp.registrations!=null?fmt(imp.registrations):'—'}</td>
                  <td style={{...s.td,fontWeight:700,color:imp.revenue_aud>0?T.green:T.textSub}}>{imp.revenue_aud>0?`A$${fmt(imp.revenue_aud)}`:'—'}</td>
                  <td style={{...s.td,fontWeight:700,color:imp.roas!=null?(imp.roas<1?T.red:imp.roas>=3?T.green:T.amber):T.textSub}}>{imp.roas!=null?`${imp.roas.toFixed(2)}x`:'—'}</td>
                  <td style={{...s.td,fontSize:11,fontWeight:700,color:imp.attribution_type==='Direct Attribution'?T.green:imp.attribution_type==='Possible Influence'?T.amber:T.textSub}}>{imp.attribution_type||'Unknown'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      )}

      <TableCard title="Release Log" count={releases.length}>
        <table style={s.table}>
          <thead><tr>{['Release','Type','Product','Status','Scheduled','Released','Expected Impact','Actual Impact'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {releases.map((r,i)=>(
              <tr key={r.id||i}>
                <td style={s.tds}>{r.release_name}</td>
                <td style={s.td}><span style={{fontSize:11,fontWeight:700,color:T.accent}}>{r.release_type}</span></td>
                <td style={s.td}>{r.related_product||'—'}</td>
                <td style={s.td}><MCStatusPill status={r.status}/></td>
                <td style={{...s.td,fontSize:12,whiteSpace:'nowrap'}}>{r.scheduled_date?new Date(r.scheduled_date).toLocaleDateString('en-AU'):'—'}</td>
                <td style={{...s.td,fontSize:12,whiteSpace:'nowrap',color:r.actual_release_date?T.green:T.textSub}}>{r.actual_release_date?new Date(r.actual_release_date).toLocaleDateString('en-AU'):'—'}</td>
                <td style={{...s.td,fontSize:12,color:T.textMid,maxWidth:160}}>{r.expected_impact||'—'}</td>
                <td style={{...s.td,fontSize:12,color:r.actual_impact?T.green:T.textFaint,maxWidth:160}}>{r.actual_impact||'—'}</td>
              </tr>
            ))}
            {releases.length===0&&<tr><td colSpan={8} style={s.empty}>No releases logged — add in Release Tracker</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ============================================================================
// MARKETING & COMMUNITY — 9 pages
// Permission: marketing_community.view / .manage / .financials
// ============================================================================

// Shared helpers for MC pages
function MCStatusPill({status}){
  const cfg={
    draft:      {color:T.textSub,     bg:T.bg,       border:T.border},
    scheduled:  {color:T.accent,      bg:T.accentLight, border:T.accentBorder},
    published:  {color:T.green,       bg:T.greenBg,  border:T.greenBorder},
    active:     {color:T.green,       bg:T.greenBg,  border:T.greenBorder},
    completed:  {color:T.green,       bg:T.greenBg,  border:T.greenBorder},
    cancelled:  {color:T.red,         bg:T.redBg,    border:T.redBorder},
    paused:     {color:T.amber,       bg:T.amberBg,  border:T.amberBorder},
    planning:   {color:T.accent,      bg:T.accentLight, border:T.accentBorder},
    released:   {color:T.green,       bg:T.greenBg,  border:T.greenBorder},
    delayed:    {color:T.amber,       bg:T.amberBg,  border:T.amberBorder},
    'in progress':{color:T.accent,   bg:T.accentLight, border:T.accentBorder},
  }[status?.toLowerCase()] || {color:T.textSub, bg:T.bg, border:T.border};
  return <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',minHeight:32,boxSizing:'border-box',padding:'0 12px',borderRadius:6,fontSize:13,fontWeight:500,whiteSpace:'nowrap',
    background:cfg.bg,color:cfg.color,border:`1px solid ${cfg.border}`}}>{status?.toUpperCase()}</span>;
}

function MCPlatformTag({platform}){
  const COLORS={
    tiktok:'#010101', youtube:'#ff0000', facebook:'#1877f2',
    instagram:'#e4405f', reddit:'#ff4500', discord:'#5865f2',
    twitter:'#1da1f2', x:'#000000', linkedin:'#0a66c2',
    email:'#276749', blog:'#b7791f', google:'#4285f4', unknown:'#718096',
  };
  const c=COLORS[(platform||'').toLowerCase()]||'#718096';
  return <span style={{display:'inline-block',padding:'2px 9px',borderRadius:10,fontSize:11,fontWeight:700,
    background:`${c}18`,color:c,border:`1px solid ${c}30`}}>{platform||'—'}</span>;
}

function MCFormModal({title, fields, onSubmit, onClose, submitting, err}){
  const [vals, setVals] = useState({});
  const set = (k,v) => setVals(p=>({...p,[k]:v}));
  return(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:7800,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:T.surface,borderRadius:10,boxShadow:'0 8px 40px rgba(0,0,0,0.18)',width:520,maxHeight:'80vh',overflowY:'auto',padding:'24px 28px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
          <div style={{fontSize:17,fontWeight:700,color:T.text}}>{title}</div>
          <button style={{background:'none',border:'none',color:T.textFaint,cursor:'pointer',fontSize:20}} onClick={onClose}>×</button>
        </div>
        {err&&<div style={s.error}>! {err}</div>}
        {fields.map(f=>(
          <div key={f.key} style={{marginBottom:12}}>
            <label style={{display:'block',fontSize:12,fontWeight:600,color:T.textSub,marginBottom:4}}>{f.label}{f.required&&<span style={{color:T.red}}> *</span>}</label>
            {f.type==='select'?(
              <select style={{...s.input,width:'100%'}} value={vals[f.key]||''} onChange={e=>set(f.key,e.target.value)}>
                <option value="">— select —</option>
                {f.options.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
              </select>
            ):f.type==='textarea'?(
              <textarea style={{...s.input,width:'100%',minHeight:80,resize:'vertical'}} value={vals[f.key]||''} onChange={e=>set(f.key,e.target.value)} placeholder={f.placeholder}/>
            ):(
              <input style={{...s.input,width:'100%'}} type={f.type||'text'} value={vals[f.key]||''} onChange={e=>set(f.key,e.target.value)} placeholder={f.placeholder}/>
            )}
          </div>
        ))}
        <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginTop:20}}>
          <button style={s.btn()} onClick={onClose}>Cancel</button>
          <button style={{...s.btn(),background:T.accent,color:'#fff',border:'none'}} disabled={submitting} onClick={()=>onSubmit(vals)}>{submitting?'Saving...':'Save'}</button>
        </div>
      </div>
    </div>
  );
}



// ============================================================================
// INTELLIGENCE FOUNDATION STATUS PAGE
// Shows data collection system status — no fake metrics, no fake charts
// ============================================================================

function SecIntelFoundation({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[quality,setQuality]=useState(null);
  const[snapping,setSnapping]=useState(false);const[snapMsg,setSnapMsg]=useState('');
  const[backfilling,setBackfilling]=useState(false);const[backMsg,setBackMsg]=useState('');

  const load=useCallback(()=>{
    Promise.all([
      fetch(`${api}/v1/staff/intelligence-foundation/status`,{credentials:'include'}).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/v1/staff/intelligence-foundation/data-quality`,{credentials:'include'}).then(r=>r.json()).catch(()=>null),
    ]).then(([status,dq])=>{setData(status);setQuality(dq);}).catch(()=>setErr('Failed to load'));
  },[api]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[]);

  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const systems=data.systems||[];
  const summary=data.summary||{};

  const STATUS_CONFIG={
    collecting:{color:T.green,bg:T.greenBg,border:T.greenBorder,label:'Collecting'},
    ready:     {color:T.accent,bg:T.accentLight,border:T.accentBorder,label:'Ready'},
    missing:   {color:T.red,bg:T.redBg,border:T.redBorder,label:'Not Created'},
  };

  const handleSnapshot=()=>{
    setSnapping(true);setSnapMsg('');
    fetch(`${api}/v1/staff/intelligence-foundation/snapshot`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({period:'daily'})})
      .then(r=>r.json()).then(d=>setSnapMsg(d.ok?`Snapshot captured for ${d.snapshot_date}`:`Error: ${d.error}`)).catch(()=>setSnapMsg('Network error')).finally(()=>setSnapping(false));
  };

  const handleBackfill=()=>{
    if(!window.confirm('Backfill cohort records from all existing users? This may take a few seconds.'))return;
    setBackfilling(true);setBackMsg('');
    fetch(`${api}/v1/staff/intelligence-foundation/cohorts/backfill`,{method:'POST',credentials:'include'})
      .then(r=>r.json()).then(d=>setBackMsg(d.ok?`Backfill complete: ${d.inserted} inserted, ${d.skipped} skipped`:`Error: ${d.error}`)).catch(()=>setBackMsg('Network error')).finally(()=>{setBackfilling(false);load();});
  };

  const healthPct=quality?.health_pct||0;
  const healthColor=healthPct>=80?T.green:healthPct>=50?T.amber:T.red;

  return(
    <>
      {/* Summary header */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(summary.total)} label="Total Systems" color="blue"/>
        <RadialKPI value={fmt(summary.collecting)} label="Collecting Data" color="green"/>
        <RadialKPI value={fmt(summary.configured)} label="Configured" color="blue"/>
        <RadialKPI value={fmt(summary.missing)} label="Not Created" color={summary.missing>0?'red':'green'}/>
      </div>

      {/* Data quality score */}
      {quality&&(
        <div style={{...s.panel,padding:'14px 18px',marginBottom:12,borderLeft:`4px solid ${healthColor}`}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
            <div style={{fontSize:13,fontWeight:700,color:T.textMid}}>Data Quality Score</div>
            <div style={{fontSize:22,fontWeight:800,color:healthColor}}>{healthPct}%</div>
          </div>
          <div style={{height:6,background:T.borderSoft,borderRadius:3,overflow:'hidden',marginBottom:10}}>
            <div style={{height:'100%',width:`${healthPct}%`,background:healthColor,borderRadius:3}}/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:6}}>
            {(quality.checks||[]).map((c,i)=>(
              <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'4px 8px',background:T.bg,borderRadius:5,border:`1px solid ${T.border}`}}>
                <span style={{fontSize:11,color:T.textSub}}>{c.label}</span>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  {c.value!=null&&<span style={{fontSize:11,fontWeight:700,color:T.textMid}}>{fmt(c.value)}</span>}
                  <span style={{fontSize:10,fontWeight:700,color:c.ok?T.green:c.severity==='missing'?T.textFaint:T.red}}>{c.ok?'✓':c.severity==='missing'?'—':'!'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div style={{...s.filterRow,marginBottom:12,flexWrap:'wrap',gap:8}}>
        <button style={{...s.btn(),background:T.accent,color:'#fff',border:'none'}} disabled={snapping} onClick={handleSnapshot}>
          {snapping?'Capturing...':'📸 Capture Daily Snapshot'}
        </button>
        <button style={{...s.btn(),color:T.accent,borderColor:T.accentBorder}} disabled={backfilling} onClick={handleBackfill}>
          {backfilling?'Backfilling...':'🔄 Backfill Cohorts from Existing Users'}
        </button>
        <button style={{...s.btn()}} onClick={load}>↻ Refresh</button>
        {snapMsg&&<span style={{fontSize:12,color:T.textMid,alignSelf:'center'}}>{snapMsg}</span>}
        {backMsg&&<span style={{fontSize:12,color:T.textMid,alignSelf:'center'}}>{backMsg}</span>}
      </div>

      {/* System status table */}
      <TableCard title="Intelligence Systems" count={systems.length}>
        <table style={s.table}>
          <thead><tr>{['System','Status','Records','Powers'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {systems.map((sys,i)=>{
              const cfg=STATUS_CONFIG[sys.status]||STATUS_CONFIG.missing;
              return(
                <tr key={sys.table||i}>
                  <td style={s.tds}>
                    <div style={{fontWeight:600,color:T.text}}>{sys.label}</div>
                    <code style={{fontSize:10,color:T.textFaint,fontFamily:mono}}>{sys.table}</code>
                  </td>
                  <td style={s.td}>
                    <span style={{display:'inline-block',padding:'2px 9px',borderRadius:10,fontSize:11,fontWeight:700,background:cfg.bg,color:cfg.color,border:`1px solid ${cfg.border}`}}>
                      {cfg.label}
                    </span>
                  </td>
                  <td style={s.td}>{sys.record_count!=null?fmt(sys.record_count):<span style={{color:T.textFaint,fontSize:11}}>—</span>}</td>
                  <td style={{...s.td,fontSize:11,color:T.textSub,maxWidth:240}}>{sys.powers}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>

      {/* Setup guidance */}
      {summary.missing>0&&(
        <div style={{...s.panel,padding:'14px 16px',marginTop:12,background:T.amberBg,border:`1px solid ${T.amberBorder}`}}>
          <div style={{fontSize:12,fontWeight:700,color:T.amber,marginBottom:6}}>Setup Required</div>
          <div style={{fontSize:12,color:T.textMid,lineHeight:1.7}}>
            {summary.missing} systems show as "Not Created". This is normal on first deploy — tables are created automatically when first accessed. Click <strong>Refresh</strong> to re-check after the first API call.
            For data to populate: capture snapshots daily using the button above, backfill cohorts once from existing users, and log marketing spend and operational costs under their respective sections.
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// CHANNEL INTELLIGENCE — Unified channel performance attribution
// Permission: channel_intelligence.view / .financials
// Aggregates: marketing_channels, user_attribution, payment_ledger,
//             marketing_activities, marketing_releases, user_subscriptions
// ============================================================================

const CI_CHANNELS = ['TikTok','YouTube','Reddit','Discord','LinkedIn','Instagram','X','Email','Organic','Direct','Unknown'];

// Deterministic channel health score from component scores
function ciChannelScore(scores){
  const vals = Object.values(scores).filter(v => v != null && !isNaN(v));
  return vals.length ? Math.round(vals.reduce((a,b)=>a+b,0)/vals.length) : null;
}

function CIChannelHealthBadge({score}){
  if(score==null) return <span style={{fontSize:11,color:T.textFaint}}>—</span>;
  const s=Math.round(score);
  const cfg=s>=85?{label:'Excellent',color:'#276749',bg:'#f0fff4',border:'#9ae6b4'}:
            s>=70?{label:'Strong',  color:'#2b6cb0',bg:'#ebf4ff',border:'#bee3f8'}:
            s>=50?{label:'Stable',  color:'#b7791f',bg:'#fffaf0',border:'#fbd38d'}:
            s>=30?{label:'Declining',color:'#c05621',bg:'#fffaf0',border:'#fbd38d'}:
                   {label:'Critical',color:'#c53030',bg:'#fff5f5',border:'#feb2b2'};
  return <span style={{display:'inline-block',padding:'2px 8px',borderRadius:10,fontSize:11,fontWeight:700,
    background:cfg.bg,color:cfg.color,border:`1px solid ${cfg.border}`}}>{cfg.label}</span>;
}

function SecMCChannelIntel({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[tab,setTab]=useState('overview');const[impactWindow,setImpactWindow]=useState(7);

  useEffect(()=>{
    // Aggregate data from existing MC endpoints
    Promise.all([
      fetch(`${api}/v1/staff/marketing-community/channels`,{credentials:'include'}).then(r=>r.json()).catch(()=>({channels:[]})),
      fetch(`${api}/v1/staff/marketing-community/traffic-sources?days=30`,{credentials:'include'}).then(r=>r.json()).catch(()=>({sources:[]})),
      fetch(`${api}/v1/staff/marketing-community/campaign-impact?window=7`,{credentials:'include'}).then(r=>r.json()).catch(()=>({impacts:[]})),
      fetch(`${api}/v1/staff/marketing-community/releases?days=90`,{credentials:'include'}).then(r=>r.json()).catch(()=>({releases:[]})),
      fetch(`${api}/v1/staff/channel-intelligence/summary`,{credentials:'include'}).then(r=>r.json()).catch(()=>null),
    ]).then(([chData,trData,impData,relData,ciSummary])=>{
      setData({channels:chData.channels||[], sources:trData.sources||[], impacts:impData.impacts||[], releases:relData.releases||[]});
    }).catch(()=>setErr('Failed to load channel intelligence data'));
  },[api]);

  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const channels=data.channels||[];
  const sources=data.sources||[];
  const impacts=data.impacts||[];
  const releases=data.releases||[];
  // Merge channel data with traffic source data
  const merged = CI_CHANNELS.map(ch=>{
    const chanRow = channels.find(c=>(c.platform||'').toLowerCase()===ch.toLowerCase()||
                                     (c.channel_name||'').toLowerCase().includes(ch.toLowerCase()));
    const srcRow  = sources.find(s=>(s.source||'').toLowerCase()===ch.toLowerCase());
    return {
      name:ch,
      followers:        chanRow?.followers_count||0,
      posts_30d:        chanRow?.posts_30d||0,
      reach_30d:        chanRow?.reach_30d||0,
      engagement_30d:   chanRow?.engagement_30d||0,
      clicks_30d:       chanRow?.clicks_30d||0,
      registrations_30d:chanRow?.registrations_30d||(srcRow?.registrations||0),
      paying_users:     chanRow?.paying_users_30d||(srcRow?.paying_users||0),
      revenue_30d:      chanRow?.revenue_30d_aud||(srcRow?.revenue_aud||0),
      api_connected:    chanRow?.api_connected||false,
      conversion_pct:   srcRow?.conversion_pct||null,
      revenue_share:    srcRow?.revenue_share_pct||null,
      has_data:         !!(chanRow||srcRow),
    };
  }).filter(c=>c.has_data||c.followers>0);

  const totalFollowers=merged.reduce((s,c)=>s+c.followers,0);
  const totalReg=merged.reduce((s,c)=>s+c.registrations_30d,0);
  const totalPaying=merged.reduce((s,c)=>s+c.paying_users,0);
  const totalRev=merged.reduce((s,c)=>s+c.revenue_30d,0);

  // Compute per-channel health scores deterministically
  const avgReg=totalReg/Math.max(merged.length,1);
  const avgRev=totalRev/Math.max(merged.length,1);
  const withScores=merged.map(c=>{
    const scores={};
    if(c.followers>0) scores.growth=Math.min(c.followers>1000?80:c.followers>100?60:40,100);
    if(c.engagement_30d>0&&c.followers>0) scores.engagement=Math.min(Math.round(c.engagement_30d/c.followers*1000),100);
    if(c.clicks_30d>0) scores.traffic=Math.min(Math.round(c.clicks_30d/Math.max(c.followers,1)*200),100);
    if(c.registrations_30d>0) scores.conversion=Math.min(Math.round(c.registrations_30d/avgReg*50),100);
    if(c.revenue_30d>0) scores.revenue=Math.min(Math.round(c.revenue_30d/Math.max(avgRev,1)*50),100);
    const overall=ciChannelScore(scores);
    const revPerSub=c.followers>0?round2(c.revenue_30d/c.followers):null;
    return {...c,scores,overall,revPerSub};
  });

  // Rankings
  const byRev=[...withScores].sort((a,b)=>b.revenue_30d-a.revenue_30d);
  const byConv=[...withScores].sort((a,b)=>(b.conversion_pct||0)-(a.conversion_pct||0));
  const byGrowth=[...withScores].sort((a,b)=>b.followers-a.followers);
  const byHealth=[...withScores].sort((a,b)=>(b.overall||0)-(a.overall||0));

  function round2(n){return n?Math.round(n*100)/100:null;}

  const TABS=[
    {id:'overview',   label:'Overview'},
    {id:'performance',label:'Channel Performance'},
    {id:'attribution',label:'Attribution'},
    {id:'releases',   label:'Release Correlation'},
    {id:'audience',   label:'Audience Intelligence'},
    {id:'comparison', label:'Comparison'},
    {id:'health',     label:'Channel Health'},
  ];

  return(
    <>
      {/* Tab bar — matches existing dashboard tab pattern */}
      <div style={{display:'flex',gap:0,marginBottom:16,borderBottom:`1px solid ${T.border}`}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            padding:'7px 16px',fontSize:13,fontWeight:tab===t.id?700:500,cursor:'pointer',
            border:'none',background:'none',
            borderBottom:`2px solid ${tab===t.id?T.accent:'transparent'}`,
            color:tab===t.id?T.accent:T.textSub,transition:'all 0.12s',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab==='overview'&&(
        <>
          <div style={{background:T.bg,borderRadius:10,padding:'16px 20px',marginBottom:12,border:`1px solid ${T.border}`}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:12,alignItems:'start'}}>
              <RadialKPI value={fmt(totalFollowers)}  label="Total Followers"   color="blue"/>
              <RadialKPI value={fmt(totalReg)}        label="Registrations 30D" color="green"/>
              <RadialKPI value={fmt(totalPaying)}     label="Paying Users 30D"  color="green"/>
              <RadialKPI value={`A$${fmt(totalRev)}`} label="Revenue 30D"       color="green"/>
              <RadialKPI value={byRev[0]?.name||'—'}  label="Top Revenue Ch."   color="blue"/>
              <RadialKPI value={byConv[0]?.name||'—'} label="Top Conversion Ch." color="blue"/>
            </div>
          </div>

          {merged.length===0&&<CIUnavailable label="No channel data yet — add channels in Social Channels and ensure UTM tracking is configured"/>}

          {merged.length>0&&(
            <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,marginBottom:12}}>
              <Panel title="Revenue by Channel" sub="Last 30 days" height={220}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byRev.slice(0,8)} margin={{top:4,right:8,bottom:0,left:0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
                    <XAxis dataKey="name" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
                    <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
                    <Tooltip formatter={v=>[`A$${fmt(v)}`,'Revenue']}/>
                    <Bar dataKey="revenue_30d" name="Revenue" radius={[3,3,0,0]}>{byRev.slice(0,8).map((_,i)=><Cell key={i} fill={CHART_COLORS[i%8]}/>)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title="Revenue Share" sub="By channel" height={220}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={byRev.slice(0,6).map((c,i)=>({name:c.name,value:c.revenue_30d||0,fill:CHART_COLORS[i%8]}))} cx="50%" cy="50%" innerRadius={48} outerRadius={78} dataKey="value" paddingAngle={3}>
                      {byRev.slice(0,6).map((_,i)=><Cell key={i} fill={CHART_COLORS[i%8]}/>)}
                    </Pie>
                    <Tooltip formatter={v=>[`A$${fmt(v)}`,'Revenue']}/>
                    <Legend iconSize={9} wrapperStyle={{fontSize:12,color:T.textSub}}/>
                  </PieChart>
                </ResponsiveContainer>
              </Panel>
            </div>
          )}

          {/* Top channel rankings */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
            {[
              {label:'Highest Revenue',ch:byRev[0],val:`A$${fmt(byRev[0]?.revenue_30d||0)}`},
              {label:'Highest Conversion',ch:byConv[0],val:byConv[0]?.conversion_pct!=null?`${byConv[0].conversion_pct}%`:'—'},
              {label:'Highest Growth',ch:byGrowth[0],val:fmt(byGrowth[0]?.followers||0)+' followers'},
            ].map(({label,ch,val})=>(
              <div key={label} style={{...s.panel,padding:'14px 16px',borderLeft:`4px solid ${T.chart1}`}}>
                <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>{label}</div>
                <div style={{fontSize:18,fontWeight:700,color:T.text,marginBottom:2}}>{ch?.name||'—'}</div>
                <div style={{fontSize:13,color:T.textSub}}>{val}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── CHANNEL PERFORMANCE ── */}
      {tab==='performance'&&(
        <>
          {merged.length===0&&<CIUnavailable label="No channel performance data — add channels in Social Channels and configure UTM tracking"/>}
          <TableCard title="Channel Performance" count={withScores.length}>
            <table style={s.table}>
              <thead><tr>{['Channel','API','Followers','Posts 30D','Engagement','Clicks','Registrations','Paying Users','Revenue','Rev/Subscriber','Conversion %','Health'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {withScores.map((c,i)=>(
                  <tr key={c.name}>
                    <td style={s.tds}><MCPlatformTag platform={c.name}/></td>
                    <td style={s.td}><span style={{fontSize:10,fontWeight:700,color:c.api_connected?T.green:T.textFaint}}>{c.api_connected?'API':'Manual'}</span></td>
                    <td style={{...s.td,fontWeight:700}}>{fmt(c.followers)||'—'}</td>
                    <td style={s.td}>{c.posts_30d||'—'}</td>
                    <td style={s.td}>{c.engagement_30d>0?fmt(c.engagement_30d):<span style={{color:T.textFaint,fontSize:11}}>—</span>}</td>
                    <td style={s.td}>{c.clicks_30d>0?fmt(c.clicks_30d):<span style={{color:T.textFaint,fontSize:11}}>—</span>}</td>
                    <td style={{...s.td,color:c.registrations_30d>0?T.green:T.textSub,fontWeight:c.registrations_30d>0?700:400}}>{fmt(c.registrations_30d)||'—'}</td>
                    <td style={{...s.td,color:c.paying_users>0?T.green:T.textSub,fontWeight:c.paying_users>0?700:400}}>{fmt(c.paying_users)||'—'}</td>
                    <td style={{...s.td,fontWeight:700,color:c.revenue_30d>0?T.green:T.textSub}}>{c.revenue_30d>0?`A$${fmt(c.revenue_30d)}`:'—'}</td>
                    <td style={s.td}>{c.revPerSub!=null?`A$${fmt(c.revPerSub)}`:<span style={{color:T.textFaint,fontSize:11}}>—</span>}</td>
                    <td style={s.td}>{c.conversion_pct!=null?`${c.conversion_pct}%`:<span style={{color:T.textFaint,fontSize:11}}>—</span>}</td>
                    <td style={s.td}><CIChannelHealthBadge score={c.overall}/></td>
                  </tr>
                ))}
                {withScores.length===0&&<tr><td colSpan={12} style={s.empty}>No channel data — add channels in Social Channels</td></tr>}
              </tbody>
            </table>
          </TableCard>
        </>
      )}

      {/* ── ATTRIBUTION ── */}
      {tab==='attribution'&&(
        <>
          <CIUnavailable label="First/last touch attribution requires user_attribution table — registrations by source shown from UTM data"/>
          <TableCard title="Channel Attribution" count={sources.length}>
            <table style={s.table}>
              <thead><tr>{['Channel','First Touch Users','Registrations','Paying Users','Revenue','Conversion %','Revenue Share','Attribution Type'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {sources.map((src,i)=>(
                  <tr key={src.source||i}>
                    <td style={s.tds}><MCPlatformTag platform={src.source}/></td>
                    <td style={s.td}>{fmt(src.first_touch_users||src.registrations||0)}</td>
                    <td style={s.td}>{fmt(src.registrations||0)}</td>
                    <td style={{...s.td,fontWeight:700,color:(src.paying_users||0)>0?T.green:T.textSub}}>{fmt(src.paying_users||0)}</td>
                    <td style={{...s.td,fontWeight:700,color:(src.revenue_aud||0)>0?T.green:T.textSub}}>{src.revenue_aud>0?`A$${fmt(src.revenue_aud)}`:'—'}</td>
                    <td style={s.td}>{src.conversion_pct!=null?`${src.conversion_pct}%`:'—'}</td>
                    <td style={s.td}>
                      <div style={{display:'flex',alignItems:'center',gap:7}}>
                        <div style={{...s.pw,flex:1,marginTop:0,height:4}}><div style={s.pb(src.revenue_share_pct||0,CHART_COLORS[i%8])}/></div>
                        <span style={{fontSize:11,color:T.textSub,minWidth:28}}>{src.revenue_share_pct||0}%</span>
                      </div>
                    </td>
                    <td style={s.td}><span style={{fontSize:11,fontWeight:700,color:src.registrations>0?T.green:T.amber}}>{src.registrations>0?'Direct Attribution':'Possible Influence'}</span></td>
                  </tr>
                ))}
                {sources.length===0&&<tr><td colSpan={8} style={s.empty}>No attribution data — UTM tracking must be configured in Marketing Activity</td></tr>}
              </tbody>
            </table>
          </TableCard>
        </>
      )}

      {/* ── RELEASE CORRELATION ── */}
      {tab==='releases'&&(
        <>
          <div style={{...s.filterRow,marginBottom:12,alignItems:'center'}}>
            <span style={{fontSize:13,color:T.textSub,fontWeight:600}}>Impact Window:</span>
            {[1,2,7,14,30].map(d=>(
              <button key={d} style={{...s.btn(),background:impactWindow===d?T.accent:'',color:impactWindow===d?'#fff':T.textSub,borderColor:impactWindow===d?T.accent:T.border}} onClick={()=>setImpactWindow(d)}>{d}D</button>
            ))}
          </div>

          <CIUnavailable label="Visitor analytics unavailable — registration and Stripe outcomes shown"/>

          <TableCard title="Release Impact by Channel" count={impacts.length}>
            <table style={s.table}>
              <thead><tr>{['Activity/Release','Platform','Product','Published','Spend','Registrations','Revenue','ROAS','Attribution'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {impacts.map((imp,i)=>(
                  <tr key={imp.activity_id||i}>
                    <td style={s.tds}>{imp.title}</td>
                    <td style={s.td}><MCPlatformTag platform={imp.platform}/></td>
                    <td style={s.td}>{imp.product||'—'}</td>
                    <td style={{...s.td,fontSize:12,whiteSpace:'nowrap'}}>{imp.published_date?new Date(imp.published_date).toLocaleDateString('en-AU'):'—'}</td>
                    <td style={{...s.td,color:imp.spend_aud>0?T.amber:T.textSub,fontWeight:imp.spend_aud>0?700:400}}>{imp.spend_aud>0?`A$${fmt(imp.spend_aud)}`:'—'}</td>
                    <td style={{...s.td,color:imp.registrations>0?T.green:T.textSub,fontWeight:imp.registrations>0?700:400}}>{imp.registrations!=null?fmt(imp.registrations):'—'}</td>
                    <td style={{...s.td,color:imp.revenue_aud>0?T.green:T.textSub,fontWeight:imp.revenue_aud>0?700:400}}>{imp.revenue_aud>0?`A$${fmt(imp.revenue_aud)}`:'—'}</td>
                    <td style={{...s.td,color:imp.roas!=null?(imp.roas<1?T.red:imp.roas>=3?T.green:T.amber):T.textSub,fontWeight:700}}>{imp.roas!=null?`${imp.roas.toFixed(2)}x`:'—'}</td>
                    <td style={{...s.td,fontSize:11,fontWeight:700,color:imp.attribution_type==='Direct Attribution'?T.green:imp.attribution_type==='Possible Influence'?T.amber:T.textSub}}>{imp.attribution_type||'Unknown'}</td>
                  </tr>
                ))}
                {impacts.length===0&&<tr><td colSpan={9} style={s.empty}>No published activities — publish activities in Marketing Activity to see release correlation</td></tr>}
              </tbody>
            </table>
          </TableCard>

          {/* Releases list */}
          <TableCard title="Planned & Recent Releases" count={releases.length}>
            <table style={s.table}>
              <thead><tr>{['Release','Type','Product','Status','Scheduled','Expected Impact'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {releases.map((r,i)=>(
                  <tr key={r.id||i}>
                    <td style={s.tds}>{r.release_name}</td>
                    <td style={s.td}><span style={{fontSize:11,fontWeight:700,color:T.accent}}>{r.release_type}</span></td>
                    <td style={s.td}>{r.related_product||'—'}</td>
                    <td style={s.td}><MCStatusPill status={r.status}/></td>
                    <td style={{...s.td,fontSize:12,whiteSpace:'nowrap'}}>{r.scheduled_date?new Date(r.scheduled_date).toLocaleDateString('en-AU'):'—'}</td>
                    <td style={{...s.td,fontSize:12,color:T.textMid,maxWidth:200}}>{r.expected_impact||'—'}</td>
                  </tr>
                ))}
                {releases.length===0&&<tr><td colSpan={6} style={s.empty}>No releases logged — add releases in Release Tracker</td></tr>}
              </tbody>
            </table>
          </TableCard>
        </>
      )}

      {/* ── AUDIENCE INTELLIGENCE ── */}
      {tab==='audience'&&(
        <>
          <CIUnavailable label="LTV, retention, and refund data require payment_ledger and user_subscriptions tables — conversion data shown from existing attribution"/>
          <TableCard title="Audience Quality by Channel" count={withScores.length}>
            <table style={s.table}>
              <thead><tr>{['Channel','Registrations','Paying Users','Conversion %','Revenue','Revenue/User','Audience Score','Notes'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {withScores.map((c,i)=>{
                  const revPerUser=c.paying_users>0?Math.round(c.revenue_30d/c.paying_users*100)/100:null;
                  // Audience quality: composite of conversion and revenue density
                  const convScore=c.conversion_pct!=null?Math.min(Math.round(c.conversion_pct*5),100):null;
                  const revScore=revPerUser!=null?Math.min(Math.round(revPerUser*2),100):null;
                  const qualityScores={};
                  if(convScore!=null) qualityScores.conversion=convScore;
                  if(revScore!=null) qualityScores.revenue=revScore;
                  const audienceScore=ciChannelScore(qualityScores);
                  return(
                    <tr key={c.name}>
                      <td style={s.tds}><MCPlatformTag platform={c.name}/></td>
                      <td style={s.td}>{fmt(c.registrations_30d)||'—'}</td>
                      <td style={{...s.td,fontWeight:700,color:c.paying_users>0?T.green:T.textSub}}>{fmt(c.paying_users)||'—'}</td>
                      <td style={s.td}>{c.conversion_pct!=null?`${c.conversion_pct}%`:<span style={{color:T.textFaint,fontSize:11}}>—</span>}</td>
                      <td style={{...s.td,fontWeight:700,color:c.revenue_30d>0?T.green:T.textSub}}>{c.revenue_30d>0?`A$${fmt(c.revenue_30d)}`:'—'}</td>
                      <td style={s.td}>{revPerUser!=null?`A$${fmt(revPerUser)}`:<span style={{color:T.textFaint,fontSize:11}}>—</span>}</td>
                      <td style={s.td}><CIChannelHealthBadge score={audienceScore}/></td>
                      <td style={{...s.td,fontSize:11,color:T.textSub}}>{!c.api_connected?'Manual data':c.registrations_30d===0?'No registrations yet':'From UTM attribution'}</td>
                    </tr>
                  );
                })}
                {withScores.length===0&&<tr><td colSpan={8} style={s.empty}>No channel data — add channels in Social Channels and configure UTM tracking</td></tr>}
              </tbody>
            </table>
          </TableCard>
        </>
      )}

      {/* ── COMPARISON ── */}
      {tab==='comparison'&&(
        <>
          {merged.length===0&&<CIUnavailable label="No channel data to compare"/>}
          {merged.length>1&&(
            <Panel title="Channel Comparison" sub="Revenue and registrations" height={240} mb={12}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={withScores} margin={{top:4,right:8,bottom:0,left:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
                  <XAxis dataKey="name" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
                  <YAxis yAxisId="l" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
                  <YAxis yAxisId="r" orientation="right" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<TT/>}/>
                  <Legend iconSize={9} wrapperStyle={{fontSize:12,color:T.textSub}}/>
                  <Bar yAxisId="l" dataKey="revenue_30d" name="Revenue 30D" fill={T.chart2} radius={[3,3,0,0]}/>
                  <Bar yAxisId="r" dataKey="registrations_30d" name="Registrations" fill={T.chart1} radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          )}

          {/* Ranking cards */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:12}}>
            {[
              {label:'Highest Revenue',items:byRev.slice(0,3),key:'revenue_30d',fmt:v=>`A$${fmt(v)}`},
              {label:'Highest Conversion',items:byConv.slice(0,3),key:'conversion_pct',fmt:v=>v!=null?`${v}%`:'—'},
              {label:'Highest Growth',items:byGrowth.slice(0,3),key:'followers',fmt:v=>fmt(v)+' followers'},
            ].map(({label,items,key,fmt:fmtFn})=>(
              <div key={label} style={{...s.panel,padding:'14px 16px'}}>
                <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:10}}>{label}</div>
                {items.map((c,i)=>(
                  <div key={c.name} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'4px 0',borderBottom:i<items.length-1?`1px solid ${T.borderSoft}`:'none'}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontSize:12,fontWeight:700,color:T.textFaint,minWidth:16}}>#{i+1}</span>
                      <MCPlatformTag platform={c.name}/>
                    </div>
                    <span style={{fontSize:12,fontWeight:700,color:T.text}}>{fmtFn(c[key])}</span>
                  </div>
                ))}
                {items.length===0&&<div style={{fontSize:12,color:T.textFaint}}>No data</div>}
              </div>
            ))}
          </div>

          <TableCard title="Full Channel Comparison">
            <table style={s.table}>
              <thead><tr>{['Rank','Channel','Revenue','Registrations','Paying Users','Conversion %','Followers','Health'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {byHealth.map((c,i)=>(
                  <tr key={c.name}>
                    <td style={{...s.td,fontWeight:700,color:T.textSub,fontSize:13}}>#{i+1}</td>
                    <td style={s.tds}><MCPlatformTag platform={c.name}/></td>
                    <td style={{...s.td,fontWeight:700,color:c.revenue_30d>0?T.green:T.textSub}}>{c.revenue_30d>0?`A$${fmt(c.revenue_30d)}`:'—'}</td>
                    <td style={s.td}>{fmt(c.registrations_30d)||'—'}</td>
                    <td style={{...s.td,fontWeight:700,color:c.paying_users>0?T.green:T.textSub}}>{fmt(c.paying_users)||'—'}</td>
                    <td style={s.td}>{c.conversion_pct!=null?`${c.conversion_pct}%`:'—'}</td>
                    <td style={s.td}>{fmt(c.followers)||'—'}</td>
                    <td style={s.td}><CIChannelHealthBadge score={c.overall}/></td>
                  </tr>
                ))}
                {byHealth.length===0&&<tr><td colSpan={8} style={s.empty}>No channel data — add channels in Social Channels</td></tr>}
              </tbody>
            </table>
          </TableCard>
        </>
      )}

      {/* ── CHANNEL HEALTH ── */}
      {tab==='health'&&(
        <>
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:12}}>
            {[
              {label:'Excellent',count:withScores.filter(c=>c.overall>=85).length,color:T.green,bg:T.greenBg},
              {label:'Strong',count:withScores.filter(c=>c.overall>=70&&c.overall<85).length,color:T.accent,bg:T.accentLight},
              {label:'Stable',count:withScores.filter(c=>c.overall>=50&&c.overall<70).length,color:T.amber,bg:T.amberBg},
              {label:'Declining',count:withScores.filter(c=>c.overall!=null&&c.overall>=30&&c.overall<50).length,color:'#c05621',bg:T.amberBg},
              {label:'Critical',count:withScores.filter(c=>c.overall!=null&&c.overall<30).length,color:T.red,bg:T.redBg},
            ].map(x=>(
              <div key={x.label} style={{background:x.bg,border:`1px solid ${x.color}20`,borderRadius:8,padding:'12px 14px',borderLeft:`3px solid ${x.color}`,textAlign:'center'}}>
                <div style={{fontSize:11,fontWeight:700,color:x.color,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:4}}>{x.label}</div>
                <div style={{fontSize:28,fontWeight:800,color:x.color}}>{x.count}</div>
              </div>
            ))}
          </div>

          <TableCard title="Channel Health Scores" count={withScores.length}>
            <table style={s.table}>
              <thead><tr>{['Channel','Overall','Growth','Engagement','Traffic','Conversion','Revenue','Retention','LTV','Status'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {withScores.sort((a,b)=>(b.overall||0)-(a.overall||0)).map((c,i)=>(
                  <tr key={c.name}>
                    <td style={s.tds}><MCPlatformTag platform={c.name}/></td>
                    <td style={{...s.td,fontWeight:800,fontSize:15,color:c.overall>=70?T.green:c.overall>=50?T.amber:T.red}}>{c.overall!=null?c.overall:'—'}</td>
                    {['growth','engagement','traffic','conversion','revenue'].map(k=>(
                      <td key={k} style={{...s.td,color:c.scores[k]>=70?T.green:c.scores[k]>=50?T.amber:c.scores[k]!=null?T.red:T.textFaint,fontWeight:c.scores[k]!=null?700:400}}>
                        {c.scores[k]!=null?c.scores[k]:<span style={{fontSize:11}}>—</span>}
                      </td>
                    ))}
                    <td style={{...s.td,fontSize:11,color:T.textFaint}}>—</td>
                    <td style={{...s.td,fontSize:11,color:T.textFaint}}>—</td>
                    <td style={s.td}><CIChannelHealthBadge score={c.overall}/></td>
                  </tr>
                ))}
                {withScores.length===0&&<tr><td colSpan={10} style={s.empty}>No channel data — add channels in Social Channels</td></tr>}
              </tbody>
            </table>
          </TableCard>
        </>
      )}
    </>
  );
}

// ─── PAGE 1: SOCIAL CHANNELS ────────────────────────────────────────────────
function SecMCChannels({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[showAdd,setShowAdd]=useState(false);const[saving,setSaving]=useState(false);const[saveErr,setSaveErr]=useState('');
  const load=()=>{
    fetch(`${api}/v1/staff/marketing-community/channels`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const channels=data.channels||[];
  const totalFollowers=channels.reduce((s,c)=>s+(c.followers_count||0),0);

  const CHANNEL_FIELDS=[
    {key:'channel_name',label:'Channel Name',required:true,placeholder:'VISINVOKE TikTok'},
    {key:'platform',label:'Platform',required:true,type:'select',options:['TikTok','YouTube','Facebook','Instagram','Reddit','Discord','Twitter/X','LinkedIn','Email','Blog']},
    {key:'handle',label:'Handle / Username',placeholder:'@visinvoke'},
    {key:'url',label:'URL',placeholder:'https://tiktok.com/@visinvoke'},
    {key:'status',label:'Status',type:'select',options:['active','inactive','paused']},
  ];

  const handleAdd=(vals)=>{
    setSaving(true);setSaveErr('');
    fetch(`${api}/v1/staff/marketing-community/channels`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify(vals)})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok){setShowAdd(false);load();}else setSaveErr(d.error||'Failed');}).catch(()=>setSaveErr('Network error')).finally(()=>setSaving(false));
  };

  return(
    <>
      {showAdd&&<MCFormModal title="Add Social Channel" fields={CHANNEL_FIELDS} onSubmit={handleAdd} onClose={()=>setShowAdd(false)} submitting={saving} err={saveErr}/>}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(channels.length)} label="Active Channels" color="blue"/>
        <RadialKPI value={fmt(totalFollowers)} label="Total Followers" color="green"/>
        <RadialKPI value={fmt(channels.reduce((s,c)=>s+(c.registrations_30d||0),0))} label="Registrations 30D" color="blue"/>
        <RadialKPI value={`A$${fmt(channels.reduce((s,c)=>s+(c.revenue_30d_aud||0),0))}`} label="Revenue 30D" color="green"/>
      </div>

      <TableCard title="Social Channels" count={channels.length} action={<button style={{...s.btn(),color:T.accent,borderColor:T.accentBorder}} onClick={()=>setShowAdd(true)}>+ Add Channel</button>}>
        <table style={s.table}>
          <thead><tr>{['Channel','Platform','Status','API','Followers','Posts 30D','Reach 30D','Engagement 30D','Registrations 30D','Revenue 30D','Last Updated'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {channels.map((c,i)=>(
              <tr key={c.id||i}>
                <td style={s.tds}><div style={{fontWeight:600,color:T.text}}>{c.channel_name}</div><div style={{fontSize:11,color:T.textSub}}>{c.handle||c.url||''}</div></td>
                <td style={s.td}><MCPlatformTag platform={c.platform}/></td>
                <td style={s.td}><MCStatusPill status={c.status}/></td>
                <td style={s.td}><span style={{fontSize:11,fontWeight:700,color:c.api_connected?T.green:T.textSub}}>{c.api_connected?'Connected':'Manual'}</span></td>
                <td style={{...s.td,fontWeight:700}}>{fmt(c.followers_count||0)}</td>
                <td style={s.td}>{fmt(c.posts_30d||0)}</td>
                <td style={s.td}>{fmt(c.reach_30d||0)}</td>
                <td style={s.td}>{fmt(c.engagement_30d||0)}</td>
                <td style={{...s.td,color:c.registrations_30d>0?T.green:T.textSub,fontWeight:c.registrations_30d>0?700:400}}>{fmt(c.registrations_30d||0)}</td>
                <td style={{...s.td,color:c.revenue_30d_aud>0?T.green:T.textSub,fontWeight:c.revenue_30d_aud>0?700:400}}>{c.revenue_30d_aud>0?`A$${fmt(c.revenue_30d_aud)}`:'—'}</td>
                <td style={{...s.td,fontSize:11,color:T.textSub}}>{c.last_updated_at?fmtAgo(c.last_updated_at):'Never'}</td>
              </tr>
            ))}
            {channels.length===0&&<tr><td colSpan={11} style={s.empty}>No channels yet — add your first channel above</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 2: CONTENT CALENDAR ────────────────────────────────────────────────
function SecMCCalendar({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[view,setView]=useState('table');
  useEffect(()=>{
    fetch(`${api}/v1/staff/marketing-community/content-calendar`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  const items=data.items||[];

  return(
    <>
      <div style={{display:'flex',gap:8,marginBottom:12}}>
        {['table','timeline'].map(v=>(
          <button key={v} style={{...s.btn(),background:view===v?T.accent:'',color:view===v?'#fff':T.textSub,borderColor:view===v?T.accent:T.border}} onClick={()=>setView(v)}>{v.charAt(0).toUpperCase()+v.slice(1)}</button>
        ))}
      </div>

      <TableCard title="Content Calendar" count={items.length}>
        <table style={s.table}>
          <thead><tr>{['Date','Type','Platform','Title','Product','Campaign','Status','Spend','URL'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {items.map((item,i)=>{
              const date=item.published_date||item.scheduled_date;
              return(
                <tr key={item.id||i}>
                  <td style={{...s.td,whiteSpace:'nowrap',fontSize:12}}>{date?new Date(date).toLocaleDateString('en-AU'):'—'}</td>
                  <td style={s.td}><span style={{fontSize:11,fontWeight:700,color:T.accent}}>{item.activity_type}</span></td>
                  <td style={s.td}><MCPlatformTag platform={item.platform}/></td>
                  <td style={s.tds}>{item.title}</td>
                  <td style={s.td}>{item.related_product||'—'}</td>
                  <td style={s.td}>{item.utm_campaign||'—'}</td>
                  <td style={s.td}><MCStatusPill status={item.status}/></td>
                  <td style={s.td}>{item.spend_aud>0?`A$${fmt(item.spend_aud)}`:'—'}</td>
                  <td style={s.td}>{item.url?<a href={item.url} target="_blank" rel="noopener noreferrer" style={{color:T.accent,fontSize:11}}>Link</a>:'—'}</td>
                </tr>
              );
            })}
            {items.length===0&&<tr><td colSpan={9} style={s.empty}>No content in calendar window — add activities in Marketing Activity</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 3: MARKETING ACTIVITY ──────────────────────────────────────────────
function SecMCActivity({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[showAdd,setShowAdd]=useState(false);const[saving,setSaving]=useState(false);const[saveErr,setSaveErr]=useState('');
  const[platform,setPlatform]=useState('');const[status,setStatus]=useState('');

  const load=()=>{
    const params=new URLSearchParams({days:90});
    if(platform)params.set('platform',platform);
    if(status)params.set('status',status);
    fetch(`${api}/v1/staff/marketing-community/activity?${params}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[api,platform,status]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  const acts=data.activities||[];

  const ACT_TYPES=['TikTok Post','YouTube Video','YouTube Short','Facebook Post','Facebook Ad','Google Ad','Reddit Post','Discord Announcement','Instagram Post','Email Campaign','Blog Post','Product Launch','Slug Drop','Battlemap Pack Release','Text RPG Update','Combat Pro Update','Social App Release','Community Vote','Pricing Change','Promotion','Manual Note'];
  const PLATFORMS=['TikTok','YouTube','Facebook','Instagram','Reddit','Discord','Google','Email','Blog','Other'];

  const FIELDS=[
    {key:'title',label:'Title',required:true,placeholder:'Launch Week TikTok #1'},
    {key:'activity_type',label:'Activity Type',required:true,type:'select',options:ACT_TYPES},
    {key:'platform',label:'Platform',type:'select',options:PLATFORMS},
    {key:'status',label:'Status',type:'select',options:['draft','scheduled','published','cancelled','completed']},
    {key:'scheduled_date',label:'Scheduled Date',type:'datetime-local'},
    {key:'published_date',label:'Published Date',type:'datetime-local'},
    {key:'related_product',label:'Related Product',placeholder:'combat_pro_t1'},
    {key:'url',label:'URL',placeholder:'https://...'},
    {key:'spend_aud',label:'Spend (AUD)',type:'number',placeholder:'0.00'},
    {key:'utm_source',label:'UTM Source',placeholder:'tiktok'},
    {key:'utm_medium',label:'UTM Medium',placeholder:'social'},
    {key:'utm_campaign',label:'UTM Campaign',placeholder:'launch-week'},
    {key:'utm_content',label:'UTM Content',placeholder:''},
    {key:'notes',label:'Notes',type:'textarea'},
  ];

  const handleAdd=(vals)=>{
    setSaving(true);setSaveErr('');
    fetch(`${api}/v1/staff/marketing-community/activity`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify(vals)})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok){setShowAdd(false);load();}else setSaveErr(d.error||'Failed');}).catch(()=>setSaveErr('Network error')).finally(()=>setSaving(false));
  };

  return(
    <>
      {showAdd&&<MCFormModal title="Log Marketing Activity" fields={FIELDS} onSubmit={handleAdd} onClose={()=>setShowAdd(false)} submitting={saving} err={saveErr}/>}

      <div style={{...s.filterRow,marginBottom:12}}>
        <select style={s.select} value={platform} onChange={e=>setPlatform(e.target.value)}>
          <option value="">All Platforms</option>
          {PLATFORMS.map(p=><option key={p} value={p}>{p}</option>)}
        </select>
        <select style={s.select} value={status} onChange={e=>setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {['draft','scheduled','published','cancelled','completed'].map(st=><option key={st} value={st}>{st}</option>)}
        </select>
        <button style={{...s.btn(),background:T.accent,color:'#fff',border:'none'}} onClick={()=>setShowAdd(true)}>+ Log Activity</button>
      </div>

      <TableCard title="Marketing Activity" count={acts.length}>
        <table style={s.table}>
          <thead><tr>{['Date','Type','Platform','Title','Product','Campaign','Status','Spend','Created By'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {acts.map((a,i)=>{
              const date=a.published_date||a.scheduled_date||a.created_at;
              return(
                <tr key={a.id||i}>
                  <td style={{...s.td,whiteSpace:'nowrap',fontSize:12}}>{date?new Date(date).toLocaleDateString('en-AU'):'—'}</td>
                  <td style={s.td}><span style={{fontSize:11,fontWeight:700,color:T.accent}}>{a.activity_type}</span></td>
                  <td style={s.td}><MCPlatformTag platform={a.platform}/></td>
                  <td style={s.tds}><div style={{fontWeight:600}}>{a.title}</div>{a.url&&<div style={{fontSize:11,color:T.textSub}}>{a.url.slice(0,40)}…</div>}</td>
                  <td style={s.td}>{a.related_product||'—'}</td>
                  <td style={s.td}>{a.utm_campaign||'—'}</td>
                  <td style={s.td}><MCStatusPill status={a.status}/></td>
                  <td style={{...s.td,color:a.spend_aud>0?T.amber:T.textSub,fontWeight:a.spend_aud>0?700:400}}>{a.spend_aud>0?`A$${fmt(a.spend_aud)}`:'—'}</td>
                  <td style={{...s.td,fontSize:11,color:T.textSub}}>{a.created_by||'—'}</td>
                </tr>
              );
            })}
            {acts.length===0&&<tr><td colSpan={9} style={s.empty}>No activity logged — click Log Activity to start tracking</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 4: CAMPAIGNS ───────────────────────────────────────────────────────
function SecMCCampaigns({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[showAdd,setShowAdd]=useState(false);const[saving,setSaving]=useState(false);const[saveErr,setSaveErr]=useState('');
  const load=()=>{
    fetch(`${api}/v1/staff/marketing-community/campaigns`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  const campaigns=data.campaigns||[];

  const FIELDS=[
    {key:'campaign_name',label:'Campaign Name',required:true,placeholder:'Launch Week'},
    {key:'campaign_type',label:'Campaign Type',type:'select',options:['Product Launch','Organic Push','Paid Ads','Community','Email','Announcement','Other']},
    {key:'start_date',label:'Start Date',type:'date'},
    {key:'end_date',label:'End Date',type:'date'},
    {key:'status',label:'Status',type:'select',options:['planning','active','paused','completed','cancelled']},
    {key:'budget_aud',label:'Budget (AUD)',type:'number',placeholder:'0.00'},
    {key:'related_products',label:'Related Products',placeholder:'combat_pro_t1, core'},
    {key:'primary_goal',label:'Primary Goal',placeholder:'100 new subscribers'},
    {key:'utm_campaign',label:'UTM Campaign Slug',placeholder:'launch-week'},
    {key:'notes',label:'Notes',type:'textarea'},
  ];

  const handleAdd=(vals)=>{
    setSaving(true);setSaveErr('');
    fetch(`${api}/v1/staff/marketing-community/campaigns`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify(vals)})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok){setShowAdd(false);load();}else setSaveErr(d.error||'Failed');}).catch(()=>setSaveErr('Network error')).finally(()=>setSaving(false));
  };

  return(
    <>
      {showAdd&&<MCFormModal title="Create Campaign" fields={FIELDS} onSubmit={handleAdd} onClose={()=>setShowAdd(false)} submitting={saving} err={saveErr}/>}

      <TableCard title="Campaigns" count={campaigns.length} action={<button style={{...s.btn(),color:T.accent,borderColor:T.accentBorder}} onClick={()=>setShowAdd(true)}>+ New Campaign</button>}>
        <table style={s.table}>
          <thead><tr>{['Campaign','Type','Status','Start','End','Budget','Spent','Goal','UTM Campaign'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {campaigns.map((c,i)=>{
              const spentPct=c.budget_aud>0?Math.round((c.spent_aud||0)/c.budget_aud*100):null;
              return(
                <tr key={c.id||i}>
                  <td style={s.tds}><div style={{fontWeight:600,color:T.text}}>{c.campaign_name}</div>{c.related_products&&<div style={{fontSize:11,color:T.textSub}}>{c.related_products}</div>}</td>
                  <td style={s.td}><span style={{fontSize:11,fontWeight:700,color:T.accent}}>{c.campaign_type||'—'}</span></td>
                  <td style={s.td}><MCStatusPill status={c.status}/></td>
                  <td style={{...s.td,fontSize:12,whiteSpace:'nowrap'}}>{c.start_date?new Date(c.start_date).toLocaleDateString('en-AU'):'—'}</td>
                  <td style={{...s.td,fontSize:12,whiteSpace:'nowrap'}}>{c.end_date?new Date(c.end_date).toLocaleDateString('en-AU'):'—'}</td>
                  <td style={s.td}>{c.budget_aud?`A$${fmt(c.budget_aud)}`:'—'}</td>
                  <td style={s.td}>
                    {c.budget_aud?(
                      <div>
                        <div style={{fontSize:12,fontWeight:700,color:spentPct>100?T.red:T.text}}>A${fmt(c.spent_aud||0)}</div>
                        <div style={{...s.pw,height:4,marginTop:3}}><div style={s.pb(spentPct||0,spentPct>100?T.red:T.chart2)}/></div>
                        <div style={{fontSize:10,color:T.textSub,marginTop:2}}>{spentPct||0}%</div>
                      </div>
                    ):'—'}
                  </td>
                  <td style={{...s.td,fontSize:12,color:T.textMid}}>{c.primary_goal||'—'}</td>
                  <td style={{...s.td,fontSize:11,fontFamily:mono,color:T.textSub}}>{c.utm_campaign||'—'}</td>
                </tr>
              );
            })}
            {campaigns.length===0&&<tr><td colSpan={9} style={s.empty}>No campaigns yet — click New Campaign to create your first</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 5: CAMPAIGN IMPACT ─────────────────────────────────────────────────
function SecMCImpact({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[window,setWindow]=useState(7);
  useEffect(()=>{
    fetch(`${api}/v1/staff/marketing-community/campaign-impact?window=${window}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  },[api,window]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  const impacts=data.impacts||[];

  const ATTR_COLORS={'Direct Attribution':T.green,'Possible Influence':T.amber,'Unknown':T.textSub};

  return(
    <>
      <div style={{...s.filterRow,marginBottom:12,alignItems:'center'}}>
        <span style={{fontSize:13,color:T.textSub,fontWeight:600}}>Impact Window:</span>
        {[1,2,7,14,30].map(d=>(
          <button key={d} style={{...s.btn(),background:window===d?T.accent:'',color:window===d?'#fff':T.textSub,borderColor:window===d?T.accent:T.border}} onClick={()=>setWindow(d)}>{d}D</button>
        ))}
      </div>

      <CIUnavailable label="Visitor data unavailable — visitor metrics require external analytics integration"/>

      <TableCard title={`Campaign Impact (${window}-day window)`} count={impacts.length}>
        <table style={s.table}>
          <thead><tr>{['Activity/Campaign','Platform','Product','Published','Spend','Visitors','Registrations','Revenue','CAC','ROAS','Attribution'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {impacts.map((imp,i)=>(
              <tr key={imp.activity_id||i}>
                <td style={s.tds}>{imp.title}</td>
                <td style={s.td}><MCPlatformTag platform={imp.platform}/></td>
                <td style={s.td}>{imp.product||'—'}</td>
                <td style={{...s.td,fontSize:12,whiteSpace:'nowrap'}}>{imp.published_date?new Date(imp.published_date).toLocaleDateString('en-AU'):'—'}</td>
                <td style={{...s.td,color:imp.spend_aud>0?T.amber:T.textSub,fontWeight:imp.spend_aud>0?700:400}}>{imp.spend_aud>0?`A$${fmt(imp.spend_aud)}`:'—'}</td>
                <td style={s.td}><span style={{color:T.textFaint,fontSize:11}}>Unavail.</span></td>
                <td style={{...s.td,color:imp.registrations>0?T.green:T.textSub,fontWeight:imp.registrations>0?700:400}}>{imp.registrations!=null?fmt(imp.registrations):'—'}</td>
                <td style={{...s.td,color:imp.revenue_aud>0?T.green:T.textSub,fontWeight:imp.revenue_aud>0?700:400}}>{imp.revenue_aud>0?`A$${fmt(imp.revenue_aud)}`:'—'}</td>
                <td style={s.td}>{imp.cac!=null?`A$${fmt(imp.cac)}`:'—'}</td>
                <td style={{...s.td,color:imp.roas!=null?(imp.roas<1?T.red:imp.roas>=3?T.green:T.amber):T.textSub,fontWeight:700}}>{imp.roas!=null?`${imp.roas.toFixed(2)}x`:'—'}</td>
                <td style={{...s.td,fontSize:11,fontWeight:700,color:ATTR_COLORS[imp.attribution_type]||T.textSub}}>{imp.attribution_type||'Unknown'}</td>
              </tr>
            ))}
            {impacts.length===0&&<tr><td colSpan={11} style={s.empty}>No published activities yet — publish activities in Marketing Activity to track impact</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 6: TRAFFIC SOURCES ─────────────────────────────────────────────────
function SecMCTraffic({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);const[days,setDays]=useState(30);
  useEffect(()=>{
    fetch(`${api}/v1/staff/marketing-community/traffic-sources?days=${days}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  },[api,days]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  const sources=data.sources||[];
  return(
    <>
      <div style={{...s.filterRow,marginBottom:12}}>
        {[7,30,90].map(d=>(
          <button key={d} style={{...s.btn(),background:days===d?T.accent:'',color:days===d?'#fff':T.textSub,borderColor:days===d?T.accent:T.border}} onClick={()=>setDays(d)}>{d}D</button>
        ))}
      </div>

      <CIUnavailable label="Visitor data requires external analytics — registration-based attribution shown"/>

      {sources.length>0&&(
        <Panel title="Revenue by Source" sub={`Last ${days} days`} height={200} mb={12}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sources} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="source" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip formatter={v=>[`A$${fmt(v)}`,'Revenue']}/>
              <Bar dataKey="revenue_aud" name="Revenue" fill={T.chart1} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <TableCard title="Traffic Sources" count={sources.length}>
        <table style={s.table}>
          <thead><tr>{['Source','Visitors','Registrations','Trials','Paying Users','Revenue','Conversion %','Revenue Share'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {sources.map((src,i)=>(
              <tr key={src.source||i}>
                <td style={s.tds}><MCPlatformTag platform={src.source}/></td>
                <td style={s.td}><span style={{color:T.textFaint,fontSize:11}}>Unavail.</span></td>
                <td style={{...s.td,fontWeight:700,color:T.accent}}>{fmt(src.registrations||0)}</td>
                <td style={s.td}>{src.trials!=null?fmt(src.trials):'—'}</td>
                <td style={{...s.td,fontWeight:700,color:(src.paying_users||0)>0?T.green:T.textSub}}>{fmt(src.paying_users||0)}</td>
                <td style={{...s.td,fontWeight:700,color:(src.revenue_aud||0)>0?T.green:T.textSub}}>{src.revenue_aud>0?`A$${fmt(src.revenue_aud)}`:'—'}</td>
                <td style={s.td}>{src.conversion_pct!=null?`${src.conversion_pct}%`:'—'}</td>
                <td style={s.td}>
                  <div style={{display:'flex',alignItems:'center',gap:7}}>
                    <div style={{...s.pw,flex:1,marginTop:0,height:4}}><div style={s.pb(src.revenue_share_pct||0,CHART_COLORS[i%8])}/></div>
                    <span style={{fontSize:11,color:T.textSub,minWidth:28}}>{src.revenue_share_pct||0}%</span>
                  </div>
                </td>
              </tr>
            ))}
            {sources.length===0&&<tr><td colSpan={8} style={s.empty}>No traffic source data yet — UTM tracking must be set up to populate this page</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 7: COMMUNITY GROWTH ────────────────────────────────────────────────
function SecMCCommunity({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/marketing-community/community-growth`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  const channels=data.channels||[];
  const totalMembers=channels.reduce((s,c)=>s+(c.followers_count||0),0);
  const totalEng=channels.reduce((s,c)=>s+(c.engagement_30d||0),0);

  return(
    <>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(totalMembers)} label="Total Community Size" color="green"/>
        <RadialKPI value={fmt(totalEng)} label="Engagement 30D" color="blue"/>
        <RadialKPI value={fmt(channels.length)} label="Active Channels" color="blue"/>
      </div>

      {channels.length>0&&(
        <Panel title="Community Size by Channel" sub="Followers / Members" height={200} mb={12}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={channels} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="channel_name" tick={{fontSize:10,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip/>
              <Bar dataKey="followers_count" name="Followers" radius={[3,3,0,0]}>{channels.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%8]}/>)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <TableCard title="Community Channels" count={channels.length}>
        <table style={s.table}>
          <thead><tr>{['Channel','Platform','Followers','Reach 30D','Engagement 30D','API Status','Last Updated'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {channels.map((c,i)=>(
              <tr key={i}>
                <td style={s.tds}>{c.channel_name}</td>
                <td style={s.td}><MCPlatformTag platform={c.platform}/></td>
                <td style={{...s.td,fontWeight:700,fontSize:15}}>{fmt(c.followers_count||0)}</td>
                <td style={s.td}>{fmt(c.reach_30d||0)}</td>
                <td style={s.td}>{fmt(c.engagement_30d||0)}</td>
                <td style={s.td}><span style={{fontSize:11,fontWeight:700,color:c.api_connected?T.green:T.textSub}}>{c.api_connected?'API Connected':'Manual'}</span></td>
                <td style={{...s.td,fontSize:11,color:T.textSub}}>{c.last_updated_at?fmtAgo(c.last_updated_at):'Never'}</td>
              </tr>
            ))}
            {channels.length===0&&<tr><td colSpan={7} style={s.empty}>No community channels configured — add channels in Social Channels</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 8: RELEASE TRACKER ─────────────────────────────────────────────────
function SecMCReleases({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[showAdd,setShowAdd]=useState(false);const[saving,setSaving]=useState(false);const[saveErr,setSaveErr]=useState('');
  const[status,setStatus]=useState('');

  const load=()=>{
    const params=new URLSearchParams({days:180});
    if(status)params.set('status',status);
    fetch(`${api}/v1/staff/marketing-community/releases?${params}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[api,status]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  const releases=data.releases||[];

  const RELEASE_TYPES=['Product Launch','Product Update','Generator Release','Slug Drop','Battlemap Pack Release','Combat Pro Update','Text RPG Update','Social App Launch','Community Vote','Pricing Update','Website Update','Other'];
  const FIELDS=[
    {key:'release_name',label:'Release Name',required:true,placeholder:'Combat Pro v2.0'},
    {key:'release_type',label:'Release Type',required:true,type:'select',options:RELEASE_TYPES},
    {key:'related_product',label:'Related Product',placeholder:'combat_pro_t2'},
    {key:'scheduled_date',label:'Scheduled Date',type:'datetime-local'},
    {key:'status',label:'Status',type:'select',options:['planned','in progress','released','delayed','cancelled']},
    {key:'description',label:'Description',type:'textarea'},
    {key:'expected_impact',label:'Expected Impact',type:'textarea'},
  ];

  const handleAdd=(vals)=>{
    setSaving(true);setSaveErr('');
    fetch(`${api}/v1/staff/marketing-community/releases`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify(vals)})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok){setShowAdd(false);load();}else setSaveErr(d.error||'Failed');}).catch(()=>setSaveErr('Network error')).finally(()=>setSaving(false));
  };

  const upcoming=releases.filter(r=>['planned','in progress'].includes((r.status||'').toLowerCase()));
  const recent=releases.filter(r=>r.status?.toLowerCase()==='released');

  return(
    <>
      {showAdd&&<MCFormModal title="Log Release" fields={FIELDS} onSubmit={handleAdd} onClose={()=>setShowAdd(false)} submitting={saving} err={saveErr}/>}

      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(upcoming.length)} label="Upcoming Releases" color="blue"/>
        <RadialKPI value={fmt(recent.length)} label="Released (180D)" color="green"/>
        <RadialKPI value={fmt(releases.filter(r=>r.status?.toLowerCase()==='delayed').length)} label="Delayed" color={(releases.filter(r=>r.status?.toLowerCase()==='delayed').length)>0?'red':'green'}/>
      </div>

      <div style={{...s.filterRow,marginBottom:12}}>
        <select style={s.select} value={status} onChange={e=>setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {['planned','in progress','released','delayed','cancelled'].map(st=><option key={st} value={st}>{st}</option>)}
        </select>
        <button style={{...s.btn(),background:T.accent,color:'#fff',border:'none'}} onClick={()=>setShowAdd(true)}>+ Log Release</button>
      </div>

      <TableCard title="Releases" count={releases.length}>
        <table style={s.table}>
          <thead><tr>{['Release','Type','Product','Status','Scheduled','Released','Description'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {releases.map((r,i)=>(
              <tr key={r.id||i}>
                <td style={s.tds}><div style={{fontWeight:600}}>{r.release_name}</div></td>
                <td style={s.td}><span style={{fontSize:11,fontWeight:700,color:T.accent}}>{r.release_type}</span></td>
                <td style={s.td}>{r.related_product||'—'}</td>
                <td style={s.td}><MCStatusPill status={r.status}/></td>
                <td style={{...s.td,fontSize:12,whiteSpace:'nowrap'}}>{r.scheduled_date?new Date(r.scheduled_date).toLocaleDateString('en-AU'):'—'}</td>
                <td style={{...s.td,fontSize:12,whiteSpace:'nowrap',color:r.actual_release_date?T.green:T.textSub}}>{r.actual_release_date?new Date(r.actual_release_date).toLocaleDateString('en-AU'):'—'}</td>
                <td style={{...s.td,fontSize:12,color:T.textMid,maxWidth:200}}>{r.description||'—'}</td>
              </tr>
            ))}
            {releases.length===0&&<tr><td colSpan={7} style={s.empty}>No releases logged — click Log Release to start tracking</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 9: EMAIL CAMPAIGNS ─────────────────────────────────────────────────
function SecMCEmail({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[showAdd,setShowAdd]=useState(false);const[saving,setSaving]=useState(false);const[saveErr,setSaveErr]=useState('');
  const load=()=>{
    fetch(`${api}/v1/staff/marketing-community/email-campaigns`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');}).catch(()=>setErr('Network error'));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  const campaigns=data.campaigns||[];

  const FIELDS=[
    {key:'campaign_name',label:'Campaign Name',required:true,placeholder:'Launch Week Announcement'},
    {key:'audience',label:'Audience',placeholder:'All subscribers'},
    {key:'subject',label:'Subject Line',placeholder:'Introducing VISINVOKE Combat Pro'},
    {key:'sent_date',label:'Sent Date',type:'datetime-local'},
    {key:'status',label:'Status',type:'select',options:['draft','scheduled','sent','cancelled']},
    {key:'recipients_count',label:'Recipients',type:'number',placeholder:'0'},
    {key:'related_product',label:'Related Product',placeholder:'combat_pro_t1'},
  ];

  const handleAdd=(vals)=>{
    setSaving(true);setSaveErr('');
    fetch(`${api}/v1/staff/marketing-community/email-campaigns`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify(vals)})
      .then(r=>r.json().then(d=>({ok:r.ok,d}))).then(({ok,d})=>{if(ok){setShowAdd(false);load();}else setSaveErr(d.error||'Failed');}).catch(()=>setSaveErr('Network error')).finally(()=>setSaving(false));
  };

  const totalSent=campaigns.reduce((s,c)=>s+(c.recipients_count||0),0);
  const totalRev=campaigns.reduce((s,c)=>s+(parseFloat(c.revenue_aud)||0),0);

  return(
    <>
      {showAdd&&<MCFormModal title="Log Email Campaign" fields={FIELDS} onSubmit={handleAdd} onClose={()=>setShowAdd(false)} submitting={saving} err={saveErr}/>}

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:12}}>
        <RadialKPI value={fmt(campaigns.length)} label="Total Campaigns" color="blue"/>
        <RadialKPI value={fmt(totalSent)} label="Total Recipients" color="blue"/>
        <RadialKPI value={fmt(campaigns.filter(c=>c.opens_count>0).length)} label="With Open Data" color="green"/>
        <RadialKPI value={`A$${fmt(totalRev)}`} label="Revenue Attributed" color="green"/>
      </div>

      <TableCard title="Email Campaigns" count={campaigns.length} action={<button style={{...s.btn(),color:T.accent,borderColor:T.accentBorder}} onClick={()=>setShowAdd(true)}>+ Log Campaign</button>}>
        <table style={s.table}>
          <thead><tr>{['Campaign','Audience','Status','Sent','Recipients','Opens','Clicks','Registrations','Revenue','Unsubscribes'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {campaigns.map((c,i)=>{
              const openRate=c.recipients_count>0?Math.round((c.opens_count||0)/c.recipients_count*100):null;
              const clickRate=c.recipients_count>0?Math.round((c.clicks_count||0)/c.recipients_count*100):null;
              const hasMetrics=c.opens_count>0||c.clicks_count>0;
              return(
                <tr key={c.id||i}>
                  <td style={s.tds}><div style={{fontWeight:600}}>{c.campaign_name}</div>{c.subject&&<div style={{fontSize:11,color:T.textSub}}>{c.subject}</div>}</td>
                  <td style={s.td}>{c.audience||'—'}</td>
                  <td style={s.td}><MCStatusPill status={c.status}/></td>
                  <td style={{...s.td,fontSize:12,whiteSpace:'nowrap'}}>{c.sent_date?new Date(c.sent_date).toLocaleDateString('en-AU'):'—'}</td>
                  <td style={s.td}>{fmt(c.recipients_count||0)}</td>
                  <td style={s.td}>{hasMetrics?<><span style={{fontWeight:700}}>{fmt(c.opens_count||0)}</span>{openRate!=null&&<span style={{fontSize:11,color:T.textSub}}> ({openRate}%)</span>}</>:<span style={{color:T.textFaint,fontSize:11}}>Unavail.</span>}</td>
                  <td style={s.td}>{hasMetrics?<><span style={{fontWeight:700}}>{fmt(c.clicks_count||0)}</span>{clickRate!=null&&<span style={{fontSize:11,color:T.textSub}}> ({clickRate}%)</span>}</>:<span style={{color:T.textFaint,fontSize:11}}>Unavail.</span>}</td>
                  <td style={{...s.td,color:(c.registrations_count||0)>0?T.green:T.textSub,fontWeight:(c.registrations_count||0)>0?700:400}}>{fmt(c.registrations_count||0)}</td>
                  <td style={{...s.td,color:(c.revenue_aud||0)>0?T.green:T.textSub,fontWeight:(c.revenue_aud||0)>0?700:400}}>{c.revenue_aud>0?`A$${fmt(c.revenue_aud)}`:'—'}</td>
                  <td style={{...s.td,color:(c.unsubscribes_count||0)>0?T.amber:T.textSub}}>{fmt(c.unsubscribes_count||0)}</td>
                </tr>
              );
            })}
            {campaigns.length===0&&<tr><td colSpan={10} style={s.empty}>No email campaigns logged — click Log Campaign to start tracking</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ============================================================================
// COMMERCIAL INTELLIGENCE — 8 pages
// Permission: commercial_intelligence.view / .financials / .executive
// ============================================================================

// Shared unavailable state component for CI
function CIUnavailable({label}){
  return(
    <div style={{display:'flex',alignItems:'center',gap:6,padding:'7px 10px',background:T.amberBg,border:`1px solid ${T.amberBorder}`,borderRadius:5,fontSize:12,color:T.amber,fontWeight:600,marginBottom:8}}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      {label} — data source not yet available
    </div>
  );
}

// Small pill status badge for health scores
function HealthBadge({score}){
  const s2=Math.round(score);
  const cfg=s2>=85?{label:'Excellent',color:'#276749',bg:'#f0fff4',border:'#9ae6b4'}:
            s2>=70?{label:'Strong',  color:'#2b6cb0',bg:'#ebf4ff',border:'#bee3f8'}:
            s2>=50?{label:'Stable',  color:'#b7791f',bg:'#fffaf0',border:'#fbd38d'}:
            s2>=30?{label:'Declining',color:'#c05621',bg:'#fffaf0',border:'#fbd38d'}:
                   {label:'Critical',color:'#c53030',bg:'#fff5f5',border:'#feb2b2'};
  return <span style={{display:'inline-block',padding:'2px 8px',borderRadius:10,fontSize:11,fontWeight:700,background:cfg.bg,color:cfg.color,border:`1px solid ${cfg.border}`}}>{cfg.label}</span>;
}

// Reusable CI rec box
function CIRec({text}){
  return(
    <div style={{display:'flex',gap:7,padding:'5px 8px',marginBottom:4,background:'rgba(197,48,48,0.04)',border:'1px solid rgba(197,48,48,0.12)',borderLeft:'2px solid #c53030',borderRadius:4}}>
      <span style={{color:'#fc8181',flexShrink:0,fontSize:11,fontWeight:800,marginTop:1}}>!</span>
      <span style={{fontSize:12,color:T.textMid,lineHeight:1.5}}>{text}</span>
    </div>
  );
}

// CI KPI strip helper (same style as RadialKPI strip in SecPlans)
function CIKpiStrip({kpis}){
  const cols=Math.min(kpis.length,6);
  return(
    <div style={{background:T.bg,borderRadius:10,padding:'16px 20px',marginBottom:12,border:`1px solid ${T.border}`}}>
      <div style={{display:'grid',gridTemplateColumns:`repeat(${cols},1fr)`,gap:12,alignItems:'start'}}>
        {kpis.map((k,i)=><RadialKPI key={i} value={k.value} label={k.label} color={k.color||'blue'}/>)}
      </div>
    </div>
  );
}

// ─── PAGE 1: REVENUE INTELLIGENCE ──────────────────────────────────────────
function SecCIRevenue({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/commercial-intelligence/revenue`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed to load');})
      .catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const d=data;
  const recRules=[];
  if((d.refund_rate_pct||0)>5) recRules.push('Refund rate exceeds 5% — review Product Health for affected products.');
  if((d.arpu_aud||0)<(d.arpu_30d_prior||d.arpu_aud||0)*0.9) recRules.push('ARPU has dropped more than 10% — users may be downgrading. Check Subscriber Intelligence.');

  const products=(d.products||[]);
  const totalRev=products.reduce((s,p)=>s+(p.revenue_30d||0),0);

  return(
    <>
      <CIKpiStrip kpis={[
        {value:`A$${fmt(d.revenue_30d_aud)}`,  label:'Revenue 30D',          color:'green'},
        {value:`A$${fmt(d.revenue_90d_aud)}`,  label:'Revenue 90D',          color:'blue'},
        {value:`A$${fmt(d.annual_run_rate)}`,  label:'Annual Run Rate',       color:'blue'},
        {value:`A$${fmt(d.subscription_rev)}`, label:'Subscription Revenue',  color:'green'},
        {value:`A$${fmt(d.oneoff_rev)}`,       label:'Pack / One-Off Revenue',color:'blue'},
        {value:`A$${fmt(d.topup_rev)}`,        label:'Credit Top-Up Revenue', color:'blue'},
      ]}/>
      <CIKpiStrip kpis={[
        {value:`A$${fmt(d.refunds_30d)}`,      label:'Refunds 30D',           color:d.refunds_30d>0?'red':'green'},
        {value:`A$${fmt(d.arpu_aud)}`,         label:'ARPU',                  color:'blue'},
        {value:`A$${fmt(d.arppu_aud)}`,        label:'ARPPU',                 color:'blue'},
        {value:`${fmt(d.refund_rate_pct||0)}%`,label:'Refund Rate',           color:(d.refund_rate_pct||0)>5?'red':'green'},
      ]}/>

      {recRules.length>0&&<div style={{marginBottom:12}}>{recRules.map((r,i)=><CIRec key={i} text={r}/>)}</div>}

      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,marginBottom:12}}>
        <Panel title="Revenue by Product" sub="Last 30 days" height={products.length>1?220:170}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={products} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="name" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip formatter={v=>[`A$${fmt(v)}`,'Revenue']}/>
              <Bar dataKey="revenue_30d" name="Revenue 30D" fill={T.chart1} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Revenue Share" sub="By product" height={products.length>1?220:170}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={products.map((p,i)=>({name:p.name,value:p.revenue_30d||0,fill:CHART_COLORS[i%8]}))} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
                {products.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%8]}/>)}
              </Pie>
              <Tooltip formatter={v=>[`A$${fmt(v)}`,'Revenue']}/>
              <Legend iconSize={9} wrapperStyle={{fontSize:12,color:T.textSub}}/>
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <TableCard title="Revenue by Product" count={products.length}>
        <table style={s.table}>
          <thead><tr>{['Product','Revenue 30D','Revenue 90D','Active Paying Users','ARPU','ARPPU','Refunds','Revenue Share','Trend'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {products.map((p,i)=>{
              const share=totalRev>0?Math.round((p.revenue_30d||0)/totalRev*100):0;
              const trend=p.revenue_30d>p.revenue_30d_prior?'up':p.revenue_30d<p.revenue_30d_prior?'down':'flat';
              const trendColor=trend==='up'?T.green:trend==='down'?T.red:T.textSub;
              return(
                <tr key={p.id||i}>
                  <td style={s.tds}><span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:CHART_COLORS[i%8],marginRight:7}}></span>{p.name}</td>
                  <td style={s.td}>A${fmt(p.revenue_30d)}</td>
                  <td style={s.td}>A${fmt(p.revenue_90d)}</td>
                  <td style={s.td}>{fmt(p.paying_users)}</td>
                  <td style={s.td}>{p.arpu!=null?`A$${fmt(p.arpu)}`:'—'}</td>
                  <td style={s.td}>{p.arppu!=null?`A$${fmt(p.arppu)}`:'—'}</td>
                  <td style={{...s.td,color:p.refunds>0?T.red:T.textSub}}>A${fmt(p.refunds||0)}</td>
                  <td style={s.td}>
                    <div style={{display:'flex',alignItems:'center',gap:7}}>
                      <div style={{...s.pw,flex:1,marginTop:0,height:5}}><div style={s.pb(share,CHART_COLORS[i%8])}/></div>
                      <span style={{fontSize:12,color:T.textSub,minWidth:24}}>{share}%</span>
                    </div>
                  </td>
                  <td style={{...s.td,color:trendColor,fontWeight:700}}>{trend==='up'?'↑':trend==='down'?'↓':'—'}</td>
                </tr>
              );
            })}
            {products.length===0&&<tr><td colSpan={9} style={s.empty}>No product revenue data yet</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 2: SUBSCRIBER INTELLIGENCE ────────────────────────────────────────
function SecCISubscribers({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/commercial-intelligence/subscribers`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed to load');})
      .catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const d=data;
  const products=d.products||[];
  const recRules=[];
  if((d.cancelled_30d||0)>(d.new_30d||0)) recRules.push('Cancellations exceed new subscribers — flag retention risk. Review Retention Intelligence.');
  if((d.expired_trials||0)>(d.trial_users||0)*0.7) recRules.push('More than 70% of trials are expiring without payment — review Conversion Intelligence.');

  return(
    <>
      <CIKpiStrip kpis={[
        {value:fmt(d.active_subscribers),  label:'Active Subscribers', color:'green'},
        {value:fmt(d.new_30d),             label:'New 30D',            color:'green'},
        {value:fmt(d.cancelled_30d),       label:'Cancelled 30D',      color:(d.cancelled_30d||0)>0?'red':'green'},
        {value:fmt(d.trial_users),         label:'Trial Users',         color:'blue'},
        {value:fmt(d.expired_trials),      label:'Expired Trials',      color:(d.expired_trials||0)>0?'amber':'green'},
        {value:fmt(d.upgrades_30d),        label:'Upgrades 30D',        color:'green'},
      ]}/>
      <CIKpiStrip kpis={[
        {value:fmt(d.downgrades_30d),      label:'Downgrades 30D',      color:(d.downgrades_30d||0)>0?'amber':'green'},
        {value:fmt(d.reactivations_30d),   label:'Reactivations 30D',   color:'blue'},
        {value:`${d.net_growth>=0?'+':''}${fmt(d.net_growth)}`, label:'Net Growth 30D', color:d.net_growth>=0?'green':'red'},
      ]}/>

      {recRules.length>0&&<div style={{marginBottom:12}}>{recRules.map((r,i)=><CIRec key={i} text={r}/>)}</div>}

      <Panel title="Subscriber Trend" sub="New vs Cancelled" height={220} mb={12}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d.trend||[]} margin={{top:4,right:8,bottom:0,left:0}}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
            <XAxis dataKey="period" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
            <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
            <Tooltip content={<TT/>}/>
            <Legend iconSize={9} wrapperStyle={{fontSize:12,color:T.textSub}}/>
            <Bar dataKey="new" name="New" fill={T.chart2} radius={[3,3,0,0]}/>
            <Bar dataKey="cancelled" name="Cancelled" fill={T.chart5} radius={[3,3,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      <TableCard title="Subscribers by Product" count={products.length}>
        <table style={s.table}>
          <thead><tr>{['Product','Tier','Active','New 30D','Cancelled 30D','Trial Users','Expired Trials','Upgrades','Downgrades','Reactivations','Net Growth'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {products.map((p,i)=>(
              <tr key={p.id||i}>
                <td style={s.tds}>{p.name}</td>
                <td style={s.td}>{p.tier||'—'}</td>
                <td style={{...s.td,fontWeight:700,color:T.accent}}>{fmt(p.active)}</td>
                <td style={{...s.td,color:T.green}}>{fmt(p.new_30d)}</td>
                <td style={{...s.td,color:(p.cancelled_30d||0)>0?T.red:T.textSub}}>{fmt(p.cancelled_30d)}</td>
                <td style={s.td}>{fmt(p.trial_users)}</td>
                <td style={{...s.td,color:(p.expired_trials||0)>0?T.amber:T.textSub}}>{fmt(p.expired_trials)}</td>
                <td style={{...s.td,color:T.green}}>{fmt(p.upgrades)}</td>
                <td style={{...s.td,color:(p.downgrades||0)>0?T.amber:T.textSub}}>{fmt(p.downgrades)}</td>
                <td style={s.td}>{fmt(p.reactivations)}</td>
                <td style={{...s.td,fontWeight:700,color:(p.net_growth||0)>=0?T.green:T.red}}>{p.net_growth>=0?'+':''}{fmt(p.net_growth)}</td>
              </tr>
            ))}
            {products.length===0&&<tr><td colSpan={11} style={s.empty}>No subscription data yet</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 3: CONVERSION INTELLIGENCE ────────────────────────────────────────
function SecCIConversion({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/commercial-intelligence/conversions`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed to load');})
      .catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const d=data;
  const hasVisitors=d.visitor_tracking_available===true;
  const recRules=[];
  if((d.trial_to_paid_pct||0)<15) recRules.push('Trial-to-paid conversion below 15% — review onboarding friction and trial length.');
  if((d.trial_to_paid_pct||0)<(d.trial_to_paid_prior||d.trial_to_paid_pct||0)*0.8) recRules.push('Trial-to-paid conversion dropped more than 20% — flag conversion risk.');

  const funnelData=[
    {stage:'Registrations',value:d.registrations_30d||0,color:T.chart1},
    {stage:'Trials',value:d.trials_30d||0,color:T.chart2},
    {stage:'New Paying',value:d.new_paying_30d||0,color:T.chart3},
    {stage:'Renewals',value:d.renewals_30d||0,color:T.chart4},
  ];

  return(
    <>
      {!hasVisitors&&<CIUnavailable label="Visitor analytics not configured — visitor-based conversion rates unavailable"/>}
      <CIKpiStrip kpis={[
        {value:hasVisitors?fmt(d.visitors_30d):'—',  label:'Visitors 30D',            color:'blue'},
        {value:fmt(d.registrations_30d),              label:'Registrations 30D',        color:'blue'},
        {value:fmt(d.trials_30d),                     label:'Trials 30D',               color:'blue'},
        {value:fmt(d.new_paying_30d),                 label:'New Paying Users 30D',     color:'green'},
        {value:fmt(d.renewals_30d),                   label:'Renewals 30D',             color:'green'},
        {value:`${fmt(d.trial_to_paid_pct||0)}%`,    label:'Trial-To-Paid %',          color:(d.trial_to_paid_pct||0)>=15?'green':'red'},
      ]}/>
      <CIKpiStrip kpis={[
        {value:`${fmt(d.reg_to_paid_pct||0)}%`,       label:'Registration-To-Paid %',   color:'blue'},
        {value:hasVisitors?`${fmt(d.visitor_to_paid_pct||0)}%`:'—', label:'Visitor-To-Paid %', color:'blue'},
      ]}/>

      {recRules.length>0&&<div style={{marginBottom:12}}>{recRules.map((r,i)=><CIRec key={i} text={r}/>)}</div>}

      <Panel title="Conversion Funnel" sub="30-day window" height={220} mb={12}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={funnelData} margin={{top:4,right:8,bottom:0,left:0}} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} horizontal={false}/>
            <XAxis type="number" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
            <YAxis type="category" dataKey="stage" tick={{fontSize:12,fill:T.textSub}} axisLine={false} tickLine={false} width={90}/>
            <Tooltip/>
            <Bar dataKey="value" radius={[0,3,3,0]}>{funnelData.map((e,i)=><Cell key={i} fill={e.color}/>)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      <TableCard title="Conversion by Product">
        <table style={s.table}>
          <thead><tr>{['Product','Registrations','Trials','New Paying','Renewals','Reg-To-Trial%','Trial-To-Paid%','Paid-To-Renewal%'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {(d.products||[]).map((p,i)=>(
              <tr key={p.id||i}>
                <td style={s.tds}>{p.name}</td>
                <td style={s.td}>{fmt(p.registrations)}</td>
                <td style={s.td}>{fmt(p.trials)}</td>
                <td style={s.td}>{fmt(p.new_paying)}</td>
                <td style={s.td}>{fmt(p.renewals)}</td>
                <td style={s.td}>{p.reg_to_trial_pct!=null?`${fmt(p.reg_to_trial_pct)}%`:'—'}</td>
                <td style={{...s.td,color:(p.trial_to_paid_pct||0)<15?T.red:T.green,fontWeight:700}}>{p.trial_to_paid_pct!=null?`${fmt(p.trial_to_paid_pct)}%`:'—'}</td>
                <td style={s.td}>{p.paid_to_renewal_pct!=null?`${fmt(p.paid_to_renewal_pct)}%`:'—'}</td>
              </tr>
            ))}
            {!(d.products||[]).length&&<tr><td colSpan={8} style={s.empty}>No conversion data yet</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 4: ACQUISITION INTELLIGENCE ───────────────────────────────────────
function SecCIAcquisition({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/commercial-intelligence/acquisition`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed to load');})
      .catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const d=data;
  const hasAdSpend=d.ad_spend_available===true;
  const sources=d.sources||[];
  const recRules=[];
  if((d.cac_30d||0)>(d.cac_prior||0)*1.25&&d.cac_prior) recRules.push('CAC has increased more than 25% — flag acquisition efficiency risk. Review source breakdown.');
  if(sources.some(s=>s.roas!=null&&s.roas<1.0)) recRules.push('One or more sources have ROAS below 1.0 — ad spend is losing money on those sources.');

  return(
    <>
      {!hasAdSpend&&<CIUnavailable label="Advertising spend records not configured — CAC and ROAS unavailable"/>}
      <CIKpiStrip kpis={[
        {value:hasAdSpend?`A$${fmt(d.ad_spend_30d)}`:'—',   label:'Ad Spend 30D',           color:'amber'},
        {value:hasAdSpend?fmt(d.new_paying_from_ads):'—',    label:'New Paying From Ads 30D', color:'blue'},
        {value:hasAdSpend?`A$${fmt(d.cac_30d)}`:'—',        label:'CAC 30D',                 color:((d.cac_30d||0)>(d.ltv||0)/3)?'red':'green'},
        {value:hasAdSpend?`A$${fmt(d.cost_per_reg)}`:'—',   label:'Cost Per Registration',   color:'blue'},
        {value:hasAdSpend?`A$${fmt(d.cost_per_trial)}`:'—', label:'Cost Per Trial',           color:'blue'},
        {value:hasAdSpend?`A$${fmt(d.cost_per_paying)}`:'—',label:'Cost Per Paying User',     color:'blue'},
      ]}/>
      <CIKpiStrip kpis={[
        {value:d.best_source||'—',   label:'Best Source',  color:'green'},
        {value:d.worst_source||'—',  label:'Worst Source', color:'red'},
      ]}/>

      {recRules.length>0&&<div style={{marginBottom:12}}>{recRules.map((r,i)=><CIRec key={i} text={r}/>)}</div>}

      <TableCard title="Source Performance">
        <table style={s.table}>
          <thead><tr>{['Source','Spend','Registrations','Trials','New Paying','Revenue 30D','CAC','Cost/Reg','Cost/Trial','ROAS'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {sources.map((src,i)=>(
              <tr key={src.source||i}>
                <td style={s.tds}><span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:CHART_COLORS[i%8],marginRight:7}}></span>{src.source}</td>
                <td style={s.td}>{src.spend!=null?`A$${fmt(src.spend)}`:'—'}</td>
                <td style={s.td}>{fmt(src.registrations)}</td>
                <td style={s.td}>{fmt(src.trials)}</td>
                <td style={s.td}>{fmt(src.new_paying)}</td>
                <td style={s.td}>{src.revenue_30d!=null?`A$${fmt(src.revenue_30d)}`:'—'}</td>
                <td style={s.td}>{src.cac!=null?`A$${fmt(src.cac)}`:'—'}</td>
                <td style={s.td}>{src.cost_per_reg!=null?`A$${fmt(src.cost_per_reg)}`:'—'}</td>
                <td style={s.td}>{src.cost_per_trial!=null?`A$${fmt(src.cost_per_trial)}`:'—'}</td>
                <td style={{...s.td,color:src.roas!=null?(src.roas<1.0?T.red:src.roas>=3.0?T.green:T.amber):T.textSub,fontWeight:700}}>
                  {src.roas!=null?`${src.roas.toFixed(2)}x`:'—'}
                </td>
              </tr>
            ))}
            {sources.length===0&&<tr><td colSpan={10} style={s.empty}>{hasAdSpend?'No source data yet':'Ad spend tracking not configured'}</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 5: RETENTION INTELLIGENCE ─────────────────────────────────────────
function SecCIRetention({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/commercial-intelligence/retention`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed to load');})
      .catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const d=data;
  const products=d.products||[];
  const ltvCac=d.ltv&&d.cac_30d?+(d.ltv/d.cac_30d).toFixed(2):null;
  const recRules=[];
  if((d.churn_rate_pct||0)>(d.churn_rate_prior||0)*1.15&&d.churn_rate_prior) recRules.push('Churn rate has increased more than 15% — flag retention risk. Review cancellation reasons and support tickets.');
  if(ltvCac&&ltvCac<3.0) recRules.push(`LTV:CAC ratio is ${ltvCac} — below 3.0 is a business model risk. Review acquisition costs.`);

  return(
    <>
      <CIKpiStrip kpis={[
        {value:`${fmt(d.churn_rate_pct||0)}%`,  label:'Churn Rate 30D',      color:(d.churn_rate_pct||0)>10?'red':(d.churn_rate_pct||0)>5?'amber':'green'},
        {value:`${fmt(d.renewal_rate_pct||0)}%`,label:'Renewal Rate 30D',     color:(d.renewal_rate_pct||0)>=85?'green':(d.renewal_rate_pct||0)>=70?'amber':'red'},
        {value:d.avg_sub_length_months!=null?`${fmt(d.avg_sub_length_months)}mo`:'—', label:'Avg Sub Length', color:'blue'},
        {value:`${fmt(d.reactivation_rate_pct||0)}%`, label:'Reactivation Rate', color:'blue'},
        {value:d.ltv!=null?`A$${fmt(d.ltv)}`:'—', label:'LTV', color:'blue'},
        {value:ltvCac!=null?`${ltvCac}:1`:'—', label:'LTV:CAC', color:ltvCac!=null?(ltvCac>=3?'green':ltvCac>=2?'amber':'red'):'blue'},
      ]}/>
      <CIKpiStrip kpis={[
        {value:`A$${fmt(d.cancelled_revenue_30d)}`, label:'Cancelled Revenue 30D', color:(d.cancelled_revenue_30d||0)>0?'red':'green'},
        {value:d.saved_revenue_available?`A$${fmt(d.saved_revenue_30d)}`:'Unavailable', label:'Saved Revenue 30D', color:'green'},
      ]}/>

      {recRules.length>0&&<div style={{marginBottom:12}}>{recRules.map((r,i)=><CIRec key={i} text={r}/>)}</div>}

      <TableCard title="Retention by Product">
        <table style={s.table}>
          <thead><tr>{['Product','Active','Cancelled 30D','Churn Rate','Renewal Rate','Avg Sub Length','LTV','Cancelled Rev','Reactivations','Saved Rev'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {products.map((p,i)=>(
              <tr key={p.id||i}>
                <td style={s.tds}>{p.name}</td>
                <td style={s.td}>{fmt(p.active)}</td>
                <td style={{...s.td,color:(p.cancelled_30d||0)>0?T.red:T.textSub}}>{fmt(p.cancelled_30d)}</td>
                <td style={{...s.td,color:(p.churn_rate_pct||0)>10?T.red:(p.churn_rate_pct||0)>5?T.amber:T.green,fontWeight:700}}>{p.churn_rate_pct!=null?`${fmt(p.churn_rate_pct)}%`:'—'}</td>
                <td style={{...s.td,color:(p.renewal_rate_pct||0)>=85?T.green:T.amber,fontWeight:700}}>{p.renewal_rate_pct!=null?`${fmt(p.renewal_rate_pct)}%`:'—'}</td>
                <td style={s.td}>{p.avg_sub_length_months!=null?`${fmt(p.avg_sub_length_months)}mo`:'—'}</td>
                <td style={s.td}>{p.ltv!=null?`A$${fmt(p.ltv)}`:'—'}</td>
                <td style={{...s.td,color:(p.cancelled_revenue_30d||0)>0?T.red:T.textSub}}>{p.cancelled_revenue_30d!=null?`A$${fmt(p.cancelled_revenue_30d)}`:'—'}</td>
                <td style={s.td}>{fmt(p.reactivations)}</td>
                <td style={s.td}>{p.saved_revenue!=null?`A$${fmt(p.saved_revenue)}`:'—'}</td>
              </tr>
            ))}
            {products.length===0&&<tr><td colSpan={10} style={s.empty}>No retention data yet</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 6: ECOSYSTEM ADOPTION ──────────────────────────────────────────────
function SecCIEcosystem({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/commercial-intelligence/ecosystem`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed to load');})
      .catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const d=data;
  const depths=d.ownership_depth||[];
  const combos=d.combinations||[];
  const recRules=[];
  if((d.cross_product_adoption_pct||0)>20) recRules.push('Cross-product adoption above 20% — flag ecosystem growth opportunity. Monitor multi-product revenue share.');

  const depthChart=depths.map(x=>({name:`${x.products_owned} product${x.products_owned!==1?'s':''}`,users:x.users,rev:x.revenue_30d}));

  return(
    <>
      <CIKpiStrip kpis={[
        {value:fmt(d.users_1_product),    label:'Users With 1 Product',    color:'blue'},
        {value:fmt(d.users_2_products),   label:'Users With 2 Products',   color:'blue'},
        {value:fmt(d.users_3_products),   label:'Users With 3 Products',   color:'green'},
        {value:fmt(d.users_4_products),   label:'Users With 4 Products',   color:'green'},
        {value:fmt(d.users_5plus),        label:'Users With 5+ Products',  color:'green'},
        {value:`${fmt(d.cross_product_adoption_pct||0)}%`, label:'Cross-Product Adoption', color:(d.cross_product_adoption_pct||0)>=20?'green':'blue'},
      ]}/>
      <CIKpiStrip kpis={[
        {value:d.avg_products_per_user!=null?fmt(d.avg_products_per_user):'—', label:'Avg Products / Paying User', color:'blue'},
        {value:`${fmt(d.multi_product_revenue_share||0)}%`, label:'Multi-Product Revenue Share', color:'green'},
      ]}/>

      {recRules.length>0&&<div style={{marginBottom:12}}>{recRules.map((r,i)=><CIRec key={i} text={r}/>)}</div>}

      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,marginBottom:12}}>
        <Panel title="Ownership Depth" sub="Users and revenue by products owned" height={220}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={depthChart} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="name" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis yAxisId="l" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis yAxisId="r" orientation="right" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip content={<TT/>}/>
              <Legend iconSize={9} wrapperStyle={{fontSize:12,color:T.textSub}}/>
              <Bar yAxisId="l" dataKey="users" name="Users" fill={T.chart1} radius={[3,3,0,0]}/>
              <Bar yAxisId="r" dataKey="rev" name="Revenue 30D" fill={T.chart2} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Multi-Product Revenue" sub="Revenue share by ownership depth" height={220}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={depths.map((x,i)=>({name:`${x.products_owned}p`,value:x.revenue_30d||0,fill:CHART_COLORS[i%8]}))} cx="50%" cy="50%" innerRadius={48} outerRadius={78} dataKey="value" paddingAngle={3}>
                {depths.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%8]}/>)}
              </Pie>
              <Tooltip formatter={v=>[`A$${fmt(v)}`,'Revenue']}/>
              <Legend iconSize={9} wrapperStyle={{fontSize:12,color:T.textSub}}/>
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <TableCard title="Ownership Depth" count={depths.length}>
        <table style={s.table}>
          <thead><tr>{['Products Owned','Users','% of Paying Users','Revenue 30D','Revenue Share','Growth 30D'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {depths.map((x,i)=>{
              const share=d.total_revenue_30d>0?Math.round((x.revenue_30d||0)/d.total_revenue_30d*100):0;
              return(
                <tr key={i}>
                  <td style={s.tds}><span style={{display:'inline-block',padding:'2px 8px',borderRadius:10,fontSize:11,fontWeight:700,background:x.products_owned>=3?T.greenBg:x.products_owned>=2?T.accentLight:T.bg,color:x.products_owned>=3?T.green:x.products_owned>=2?T.accent:T.textSub,border:`1px solid ${x.products_owned>=3?T.greenBorder:x.products_owned>=2?T.accentBorder:T.border}`}}>{x.products_owned} product{x.products_owned!==1?'s':''}</span></td>
                  <td style={s.td}>{fmt(x.users)}</td>
                  <td style={s.td}>{x.pct_of_paying!=null?`${fmt(x.pct_of_paying)}%`:'—'}</td>
                  <td style={s.td}>{x.revenue_30d!=null?`A$${fmt(x.revenue_30d)}`:'—'}</td>
                  <td style={s.td}>{share}%</td>
                  <td style={{...s.td,color:(x.growth_30d||0)>=0?T.green:T.red,fontWeight:700}}>{x.growth_30d!=null?`${x.growth_30d>=0?'+':''}${fmt(x.growth_30d)}`:'—'}</td>
                </tr>
              );
            })}
            {depths.length===0&&<tr><td colSpan={6} style={s.empty}>No ecosystem data yet</td></tr>}
          </tbody>
        </table>
      </TableCard>

      <TableCard title="Top Product Combinations" count={combos.length}>
        <table style={s.table}>
          <thead><tr>{['Product Combination','Users','Revenue 30D','Avg Revenue / User','Growth 30D'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {combos.map((c,i)=>(
              <tr key={i}>
                <td style={s.tds}>{c.combination}</td>
                <td style={s.td}>{fmt(c.users)}</td>
                <td style={s.td}>{c.revenue_30d!=null?`A$${fmt(c.revenue_30d)}`:'—'}</td>
                <td style={s.td}>{c.avg_revenue_per_user!=null?`A$${fmt(c.avg_revenue_per_user)}`:'—'}</td>
                <td style={{...s.td,color:(c.growth_30d||0)>=0?T.green:T.red,fontWeight:700}}>{c.growth_30d!=null?`${c.growth_30d>=0?'+':''}${fmt(c.growth_30d)}`:'—'}</td>
              </tr>
            ))}
            {combos.length===0&&<tr><td colSpan={5} style={s.empty}>No combination data yet</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 7: PRODUCT HEALTH ──────────────────────────────────────────────────
function SecCIHealth({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/commercial-intelligence/product-health`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed to load');})
      .catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const products=data.products||[];
  const critical=products.filter(p=>p.overall_score<30);
  const declining=products.filter(p=>p.overall_score>=30&&p.overall_score<50);

  const RECOMMENDED_ACTIONS={
    churn:          'Review retention and cancellation reasons.',
    conversion:     'Review onboarding and trial flow.',
    support_load:   'Review UX friction and documentation.',
    refund_rate:    'Review product expectation mismatch.',
    usage:          'Review activation and engagement.',
    revenue_growth: 'Review pricing and marketing spend.',
    subscriber_growth:'Review acquisition and trial strategy.',
  };

  const ScoreCell=({val,invert})=>{
    if(val==null)return<td style={s.td}>—</td>;
    const color=invert?(val>=70?T.red:val>=50?T.amber:T.green):(val>=70?T.green:val>=50?T.amber:T.red);
    return<td style={{...s.td,fontWeight:700,color}}>{Math.round(val)}</td>;
  };

  return(
    <>
      {critical.length>0&&<div style={{marginBottom:8}}><CIRec text={`${critical.length} product(s) are CRITICAL — immediate investigation required.`}/></div>}
      {declining.length>0&&<div style={{marginBottom:12}}><CIRec text={`${declining.length} product(s) are DECLINING — review primary risk components.`}/></div>}

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:12}}>
        {[
          {label:'Excellent',count:products.filter(p=>p.overall_score>=85).length,color:T.green,bg:T.greenBg},
          {label:'Strong/Stable',count:products.filter(p=>p.overall_score>=50&&p.overall_score<85).length,color:T.accent,bg:T.accentLight},
          {label:'Declining',count:declining.length,color:T.amber,bg:T.amberBg},
          {label:'Critical',count:critical.length,color:T.red,bg:T.redBg},
        ].map(x=>(
          <div key={x.label} style={{background:x.bg,border:`1px solid ${x.color}20`,borderRadius:8,padding:'12px 14px',borderLeft:`3px solid ${x.color}`}}>
            <div style={{fontSize:11,fontWeight:700,color:x.color,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:4}}>{x.label}</div>
            <div style={{fontSize:28,fontWeight:800,color:x.color}}>{x.count}</div>
          </div>
        ))}
      </div>

      <TableCard title="Product Health Scores" count={products.length}>
        <table style={s.table}>
          <thead><tr>{['Product','Overall','Status','Rev Growth','Sub Growth','Conversion','Churn','Support Load','Refund Rate','Usage','Primary Risk','Recommended Action'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {products.map((p,i)=>(
              <tr key={p.id||i}>
                <td style={s.tds}>{p.name}</td>
                <td style={{...s.td,fontWeight:800,fontSize:15,color:p.overall_score>=70?T.green:p.overall_score>=50?T.amber:T.red}}>{Math.round(p.overall_score)}</td>
                <td style={s.td}><HealthBadge score={p.overall_score}/></td>
                <ScoreCell val={p.revenue_growth_score}/>
                <ScoreCell val={p.subscriber_growth_score}/>
                <ScoreCell val={p.conversion_score}/>
                <ScoreCell val={p.retention_score} invert/>
                <ScoreCell val={p.support_load_score} invert/>
                <ScoreCell val={p.refund_score} invert/>
                <ScoreCell val={p.usage_score}/>
                <td style={{...s.td,color:T.amber,fontWeight:600,fontSize:12}}>{p.primary_risk||'—'}</td>
                <td style={{...s.td,fontSize:12,color:T.textMid,maxWidth:180}}>{p.primary_risk?RECOMMENDED_ACTIONS[p.primary_risk]||'Review this product.':'—'}</td>
              </tr>
            ))}
            {products.length===0&&<tr><td colSpan={12} style={s.empty}>No product health data yet</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// ─── PAGE 8: OWNER TARGET TRACKER ────────────────────────────────────────────
function SecCITarget({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/commercial-intelligence/owner-target`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed to load');})
      .catch(()=>setErr('Network error'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const d=data;
  const TARGET=6000;
  const profit=d.monthly_profit_aud||0;
  const pct=Math.min(Math.round(profit/TARGET*100),200);
  const gap=Math.max(TARGET-profit,0);
  const statusColor=pct>=100?T.green:pct>=85?T.amber:T.red;
  const statusLabel=pct>=100?'TARGET MET':pct>=85?'AMBER — CLOSE':'RED — TARGET GAP';

  const recRules=[];
  if(pct<85) recRules.push(`Operating profit is ${pct}% of target — A$${fmt(gap)} gap remaining. Review cost breakdown and revenue growth levers.`);
  if((d.infrastructure_costs_30d||0)>(d.revenue_30d_aud||0)*0.3) recRules.push('Infrastructure costs exceed 30% of revenue — review Platform Providers for optimisation.');

  const costRows=[
    {category:'Revenue',amount:d.revenue_30d_aud||0,share:100,trend:d.revenue_trend,notes:'Gross revenue last 30 days',positive:true},
    {category:'Infrastructure Costs',amount:-(d.infrastructure_costs_30d||0),share:d.revenue_30d_aud>0?Math.round((d.infrastructure_costs_30d||0)/d.revenue_30d_aud*100):0,trend:d.infra_trend,notes:'AWS, EB, RDS, CloudFront, S3'},
    {category:'Advertising Spend',amount:-(d.ad_spend_30d||0),share:d.revenue_30d_aud>0?Math.round((d.ad_spend_30d||0)/d.revenue_30d_aud*100):0,trend:d.ads_trend,notes:d.ad_spend_available?'Google, Facebook, YouTube, TikTok':'Not yet tracked'},
    {category:'Payment Processing Fees',amount:-(d.payment_fees_30d||0),share:d.revenue_30d_aud>0?Math.round((d.payment_fees_30d||0)/d.revenue_30d_aud*100):0,trend:null,notes:'Stripe processing fees (~2.9% + 30c)'},
    {category:'Platform Operating Costs',amount:-(d.platform_costs_30d||0),share:d.revenue_30d_aud>0?Math.round((d.platform_costs_30d||0)/d.revenue_30d_aud*100):0,trend:null,notes:'fal.ai, Resend, other SaaS'},
    {category:'Operating Profit',amount:profit,share:d.revenue_30d_aud>0?Math.round(profit/d.revenue_30d_aud*100):0,trend:d.profit_trend,notes:'Revenue minus all costs above',positive:true},
  ];

  return(
    <>
      {/* Target status banner */}
      <div style={{background:pct>=100?T.greenBg:pct>=85?T.amberBg:T.redBg,border:`1px solid ${statusColor}30`,borderLeft:`4px solid ${statusColor}`,borderRadius:8,padding:'14px 18px',marginBottom:12,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <div style={{fontSize:11,fontWeight:800,color:statusColor,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:4}}>{statusLabel}</div>
          <div style={{fontSize:13,color:T.textMid}}>Target: <strong>A$6,000 monthly operating profit (VISINVOKE only)</strong></div>
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:32,fontWeight:800,color:statusColor}}>A${fmt(profit)}</div>
          <div style={{fontSize:12,color:T.textSub}}>Current monthly profit</div>
        </div>
      </div>

      {recRules.length>0&&<div style={{marginBottom:12}}>{recRules.map((r,i)=><CIRec key={i} text={r}/>)}</div>}

      {/* Progress bar */}
      <div style={{...s.panel,padding:'16px 18px',marginBottom:12}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:8}}>
          <div style={{fontSize:13,fontWeight:700,color:T.textMid}}>Progress to Target</div>
          <div style={{fontSize:18,fontWeight:800,color:statusColor}}>{pct}%</div>
        </div>
        <div style={{height:12,background:T.borderSoft,borderRadius:6,overflow:'hidden'}}>
          <div style={{height:'100%',width:`${Math.min(pct,100)}%`,background:statusColor,borderRadius:6,transition:'width 0.4s'}}/>
        </div>
        <div style={{display:'flex',justifyContent:'space-between',marginTop:6}}>
          <div style={{fontSize:11,color:T.textSub}}>A$0</div>
          <div style={{fontSize:11,color:T.textSub}}>A$6,000 target</div>
        </div>
        {gap>0&&<div style={{marginTop:8,fontSize:13,color:T.amber,fontWeight:600}}>Remaining gap: A${fmt(gap)}</div>}
      </div>

      <CIKpiStrip kpis={[
        {value:`A$${fmt(profit)}`,              label:'Monthly Operating Profit', color:pct>=100?'green':pct>=85?'amber':'red'},
        {value:`A$${fmt(profit*12)}`,           label:'Annual Run Rate',          color:'blue'},
        {value:`A$${fmt(d.revenue_30d_aud)}`,   label:'Revenue 30D',              color:'green'},
        {value:`A$${fmt((d.infrastructure_costs_30d||0)+(d.ad_spend_30d||0)+(d.payment_fees_30d||0)+(d.platform_costs_30d||0))}`, label:'Total Costs 30D', color:'red'},
        {value:d.projected_target_date||'—',    label:'Projected Target Date',    color:'blue'},
      ]}/>

      <Panel title="Profit Trend" sub="Last 12 months" height={200} mb={12}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={d.profit_trend_chart||[]} margin={{top:4,right:8,bottom:0,left:0}}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
            <XAxis dataKey="month" tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
            <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false}/>
            <Tooltip formatter={v=>[`A$${fmt(v)}`]}/>
            <ReferenceLine y={6000} stroke={T.green} strokeDasharray="4 4" label={{value:'Target',fontSize:10,fill:T.green}}/>
            <Line type="monotone" dataKey="profit" stroke={T.chart1} strokeWidth={2} dot={false} name="Profit"/>
            <Line type="monotone" dataKey="revenue" stroke={T.chart2} strokeWidth={1.5} dot={false} name="Revenue" strokeDasharray="4 4"/>
          </LineChart>
        </ResponsiveContainer>
      </Panel>

      <TableCard title="Cost Breakdown">
        <table style={s.table}>
          <thead><tr>{['Category','Amount 30D','Share of Revenue','Trend','Notes'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {costRows.map((row,i)=>{
              const isProfit=row.category==='Operating Profit';
              const isRev=row.category==='Revenue';
              const amtColor=isRev?T.green:isProfit?(row.amount>=0?T.green:T.red):(row.amount<0?T.red:T.textMid);
              const trendColor=row.trend==='up'?(isRev||isProfit?T.green:T.red):row.trend==='down'?(isRev||isProfit?T.red:T.green):T.textSub;
              return(
                <tr key={i} style={{background:isProfit?T.greenBg:isRev?T.accentLight:'transparent'}}>
                  <td style={{...s.tds,fontWeight:isProfit||isRev?700:400}}>{row.category}</td>
                  <td style={{...s.td,fontWeight:700,color:amtColor}}>A${fmt(Math.abs(row.amount))}{row.amount<0?' (cost)':''}</td>
                  <td style={s.td}>{row.share}%</td>
                  <td style={{...s.td,color:trendColor,fontWeight:700}}>{row.trend==='up'?'↑':row.trend==='down'?'↓':'—'}</td>
                  <td style={{...s.td,fontSize:12,color:T.textSub}}>{row.notes}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

function SecPlans({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{fetch(`${api}/v1/admin/analytics/plans`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed to load'));},[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  const planBar=(data.plans||[]).map(p=>({name:p.name,subscribers:p.count,revenue:p.revenue}));
  const revPie=(data.plans||[]).map((p,i)=>({name:p.name,value:p.revenue,fill:CHART_COLORS[i%8]}));
  return(
    <>
      {/* Radial revenue KPI strip */}
      <div style={{background:'rgba(255,255,255,0.03)',borderRadius:10,padding:'20px 24px',marginBottom:12,border:'1px solid rgba(255,255,255,0.07)'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:16,alignItems:'start'}}>
          <RadialKPI value={`A$${fmt(data.mrr_aud)}`} label="MRR (AUD)" color="green"/>
          <RadialKPI value={`A$${fmt(data.arpu_aud)}`} label="ARPU" color="blue"/>
          <RadialKPI value={fmt(data.total_active_subs)} label="Active Subs" color="blue"/>
          <RadialKPI value={fmt(data.total_churned)} label="Churned" color={data.total_churned>0?'red':'green'}/>
          <RadialKPI value={fmt(data.upgrades_30d)} label="Upgrades 30d" color="green"/>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,marginBottom:12}}>
        <Panel title="Subscribers & Revenue by Plan" sub="Active and trialing" height={240}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={planBar} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="name" tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis yAxisId="left" tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis yAxisId="right" orientation="right" tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip content={<TT/>}/>
              <Legend iconSize={10} wrapperStyle={{fontSize:14,color:T.textSub}}/>
              <Bar yAxisId="left" dataKey="subscribers" name="Subscribers" fill={T.chart1} radius={[3,3,0,0]}/>
              <Bar yAxisId="right" dataKey="revenue" name="Revenue (AUD)" fill={T.chart2} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Revenue Share" sub="By plan" height={240}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={revPie} cx="50%" cy="50%" innerRadius={58} outerRadius={85} dataKey="value" paddingAngle={3}>
                {revPie.map((e,i)=><Cell key={i} fill={e.fill}/>)}
              </Pie>
              <Tooltip formatter={(v)=>[`A$${fmt(v)}`,'Revenue']}/>
              <Legend iconSize={10} wrapperStyle={{fontSize:14,color:T.textSub}}/>
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Revenue source mini strip */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
        {[
          {label:'Subscription Revenue',val:`A$${fmt(data.mrr_aud)}`,color:T.chart1},
          {label:'Topup Revenue',val:`A$${fmt(data.topup_revenue_aud)}`,color:T.chart2},
          {label:'Hobby Pack Revenue',val:`A$${fmt(data.hobby_revenue_aud)}`,color:T.chart3},
        ].map(({label,val,color})=>(
          <div key={label} style={{...s.panel,padding:'14px 16px',borderLeft:`4px solid ${color}`}}>
            <div style={{fontSize:13,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:7}}>{label}</div>
            <div style={{fontSize:34,fontWeight:800,color:T.text}}>{val}</div>
          </div>
        ))}
      </div>

      <TableCard title="Plan Breakdown" count={data.total_active_subs}>
        <table style={s.table}>
          <thead><tr>{['Plan','Slug','Price/mo AUD','Subscribers','Revenue','Revenue Share'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {(data.plans||[]).map((p,i)=>{
              const share=data.mrr_aud>0?Math.round(p.revenue/data.mrr_aud*100):0;
              return(
                <tr key={p.slug}>
                  <td style={s.tds}><span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:CHART_COLORS[i%8],marginRight:7}}></span>{p.name}</td>
                  <td style={s.td}><code style={{fontFamily:mono,fontSize:13,color:T.textSub}}>{p.slug}</code></td>
                  <td style={s.td}>A${p.price}</td>
                  <td style={s.td}>{fmt(p.count)}</td>
                  <td style={s.tds}>A${fmt(p.revenue)}</td>
                  <td style={s.td}>
                    <div style={{display:'flex',alignItems:'center',gap:7}}>
                      <div style={{...s.pw,flex:1,marginTop:0,height:6}}><div style={s.pb(share,CHART_COLORS[i%8])}/></div>
                      <span style={{fontSize:13,color:T.textSub,minWidth:28}}>{share}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {(data.plans||[]).length===0&&<tr><td colSpan={6} style={s.empty}>No active subscriptions yet</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );
}

// -- MARGIN --------------------------------------------------------------------
function SecMargin({api}){
  const[data,setData]=useState(null);const[days,setDays]=useState(30);const[err,setErr]=useState(null);
  useEffect(()=>{setData(null);fetch(`${api}/v1/admin/analytics/margin?days=${days}`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));},[api,days]);
  if(err)return<div style={s.error}>! {err}</div>;
  return(
    <>
      <div style={s.filterRow}><DF value={days} onChange={setDays}/></div>
      {!data?<Loader/>:(
        <>
          {/* Radial margin KPI strip */}
          <div style={{background:'rgba(255,255,255,0.03)',borderRadius:10,padding:'20px 24px',marginBottom:12,border:'1px solid rgba(255,255,255,0.07)'}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,alignItems:'start'}}>
              <RadialKPI value={`A$${fmt(data.revenue_aud?.total)}`} label={`Revenue ${days}d`} color="green"/>
              <RadialKPI value={`A$${fmt(data.cost_aud_approx)}`} label="Provider Cost" color="amber"/>
              <RadialKPI value={`${data.margin_pct}%`} label="Gross Margin" color={data.margin_pct>=80?'green':data.margin_pct>=60?'amber':'red'}/>
              <RadialKPI value={data.profitable?'Yes':'No'} label="Profitable" color={data.profitable?'green':'red'}/>
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
            <Panel title="Revenue vs Cost vs Profit" sub={`Last ${days} days (AUD)`} height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  {name:'Revenue',value:data.revenue_aud?.total||0},
                  {name:'Cost',value:data.cost_aud_approx||0},
                  {name:'Profit',value:(data.revenue_aud?.total||0)-(data.cost_aud_approx||0)},
                ]} margin={{top:4,right:8,bottom:0,left:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
                  <XAxis dataKey="name" tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
                  <Tooltip formatter={(v)=>[`A$${fmt(v)}`]}/>
                  <Bar dataKey="value" name="AUD" radius={[4,4,0,0]}>
                    {[T.chart2,T.chart5,T.chart1].map((c,i)=><Cell key={i} fill={c}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="Revenue Sources" sub="By type" height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={[
                    {name:'Subscriptions',value:data.revenue_aud?.subscriptions||0},
                    {name:'Top-ups',value:data.revenue_aud?.topups||0},
                    {name:'Hobby Packs',value:data.revenue_aud?.hobby_packs||0},
                  ]} cx="50%" cy="50%" innerRadius={58} outerRadius={82} dataKey="value" paddingAngle={4}>
                    {[T.chart1,T.chart2,T.chart3].map((c,i)=><Cell key={i} fill={c}/>)}
                  </Pie>
                  <Tooltip formatter={(v)=>[`A$${fmt(v)}`]}/>
                  <Legend iconSize={10} wrapperStyle={{fontSize:14,color:T.textSub}}/>
                </PieChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <div style={{...s.panel,marginBottom:12,padding:'12px 16px',background:data.margin_pct>=80?T.greenBg:T.amberBg,border:`1px solid ${data.margin_pct>=80?T.greenBorder:T.amberBorder}`}}>
            <div style={{fontSize:13,fontWeight:700,color:data.margin_pct>=80?T.green:T.amber,textAlign:'center'}}>
              {data.margin_pct>=85?'v Excellent margin - on target':data.margin_pct>=70?'! Margin below 85% target':'x Margin critically low'}
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            {[
              ['Subscriptions',`A$${fmt(data.revenue_aud?.subscriptions)}`],
              ['Credit Top-ups',`A$${fmt(data.revenue_aud?.topups)}`],
              ['Hobby Packs',`A$${fmt(data.revenue_aud?.hobby_packs)}`],
              ['Total Revenue',`A$${fmt(data.revenue_aud?.total)}`],
              ['Provider Cost (USD)',`$${data.cost_usd}`],
              ['Provider Cost (AUD ~)',`A$${data.cost_aud_approx}`],
              ['Gross Profit',`A$${fmt((data.revenue_aud?.total||0)-(data.cost_aud_approx||0))}`],
              ['Gross Margin',`${data.margin_pct}%`],
            ].map(([k,v])=>(
              <div key={k} style={{...s.panel,padding:'10px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{fontSize:14,color:T.textSub}}>{k}</span>
                <span style={{fontSize:13,fontWeight:700,color:T.text}}>{v}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// -- CREDITS -------------------------------------------------------------------
function SecCredits({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{fetch(`${api}/v1/admin/credits/summary`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));},[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  const totalIssued=(data.issued?.subscription||0)+(data.issued?.topup||0)+(data.issued?.hobby||0);
  const utilRate=totalIssued>0?Math.round(data.total_consumed/totalIssued*100):0;
  const issuedPie=[{name:'Subscription',value:data.issued?.subscription||0},{name:'Topup',value:data.issued?.topup||0},{name:'Hobby',value:data.issued?.hobby||0}];
  return(
    <>
      {/* Radial credit KPI strip */}
      <div style={{background:'rgba(255,255,255,0.03)',borderRadius:10,padding:'20px 24px',marginBottom:12,border:'1px solid rgba(255,255,255,0.07)'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:16,alignItems:'start'}}>
          <RadialKPI value={fmt(totalIssued)} label="Total Issued" color="blue"/>
          <RadialKPI value={fmt(data.total_consumed)} label="Consumed" color="blue"/>
          <RadialKPI value={`${utilRate}%`} label="Utilisation" color={utilRate>80?'green':'amber'}/>
          <RadialKPI value={fmt(data.current_balances?.subscription_credits)} label="Sub Balances" color="purple"/>
          <RadialKPI value={fmt(data.current_balances?.topup_credits)} label="Topup Balances" color="amber"/>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
        <Panel title="Credits Issued by Source" height={230}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={issuedPie} cx="50%" cy="50%" innerRadius={58} outerRadius={85} dataKey="value" paddingAngle={4}>
                {issuedPie.map((_,i)=><Cell key={i} fill={CHART_COLORS[i]}/>)}
              </Pie>
              <Tooltip formatter={(v)=>[fmt(v),'Credits']}/>
              <Legend iconSize={10} wrapperStyle={{fontSize:14,color:T.textSub}}/>
            </PieChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Credits Consumed by Source" height={230}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={(data.by_source||[]).map(r=>({name:r.source||'unknown',credits:r.credits||0,uses:r.uses||0}))} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="name" tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip content={<TT/>}/>
              <Bar dataKey="credits" name="Credits Used" fill={T.chart1} radius={[3,3,0,0]}/>
              <Bar dataKey="uses" name="Generations" fill={T.chart3} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Utilisation bar */}
      <div style={{...s.panel,padding:'14px 18px'}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
          <span style={{fontSize:15,fontWeight:700,color:T.text}}>Credit Utilisation Rate</span>
          <span style={{fontSize:13,fontWeight:800,color:utilRate>80?T.green:T.amber}}>{utilRate}%</span>
        </div>
        <div style={{height:10,background:T.borderSoft,borderRadius:6,overflow:'hidden'}}>
          <div style={{height:'100%',width:`${utilRate}%`,background:utilRate>80?T.chart2:T.chart3,borderRadius:6,transition:'width 0.5s'}}/>
        </div>
        <div style={{display:'flex',justifyContent:'space-between',marginTop:6,fontSize:13,color:T.textSub}}>
          <span>0 credits</span><span>{fmt(totalIssued)} issued</span>
        </div>
      </div>
    </>
  );
}

// -- GENERATIONS ---------------------------------------------------------------
function SecGenerations({api}){
  const[data,setData]=useState(null);const[days,setDays]=useState(30);const[err,setErr]=useState(null);
  useEffect(()=>{setData(null);fetch(`${api}/v1/admin/analytics/generations?days=${days}`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));},[api,days]);
  if(err)return<div style={s.error}>! {err}</div>;
  return(
    <>
      <div style={s.filterRow}><DF value={days} onChange={setDays}/></div>
      {!data?<Loader/>:(
        <>
          {/* Radial generation KPI strip */}
          <div style={{background:'rgba(255,255,255,0.03)',borderRadius:10,padding:'20px 24px',marginBottom:12,border:'1px solid rgba(255,255,255,0.07)'}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,alignItems:'start'}}>
              <RadialKPI value={fmt(data.total_generations)} label={`Generations ${days}d`} color="blue"/>
              <RadialKPI value={fmt(data.total_images)} label={`Images ${days}d`} color="purple"/>
              <RadialKPI value={data.avg_images_per_gen} label="Avg per Gen" color="amber"/>
            </div>
          </div>

          <Panel title="Daily Generation Volume" sub={`Generations and images over last ${days} days`} height={(data.daily||[]).length>3?260:210} mb={8}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[...(data.daily||[])].reverse()} margin={{top:4,right:16,bottom:0,left:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
                <XAxis dataKey="day" tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false} interval="preserveStartEnd"/>
                <YAxis tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
                <Tooltip content={<TT/>}/>
                <Legend iconSize={10} wrapperStyle={{fontSize:14,color:T.textSub}}/>
                <Line type="monotone" dataKey="gens" name="Generations" stroke={T.chart1} strokeWidth={2} dot={false} activeDot={{r:4}}/>
                <Line type="monotone" dataKey="images" name="Images" stroke={T.chart2} strokeWidth={2} dot={false} activeDot={{r:4}}/>
              </LineChart>
            </ResponsiveContainer>
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
            <Panel title="Image Count Distribution" sub="Sessions by image count" height={200}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={Object.entries(data.image_count_dist||{}).map(([k,v])=>({name:`${k} image${k>1?'s':''}`,value:v}))} cx="50%" cy="50%" innerRadius={52} outerRadius={78} dataKey="value" paddingAngle={4}>
                    {Object.keys(data.image_count_dist||{}).map((_,i)=><Cell key={i} fill={CHART_COLORS[i]}/>)}
                  </Pie>
                  <Tooltip formatter={(v)=>[fmt(v),'Generations']}/>
                  <Legend iconSize={10} wrapperStyle={{fontSize:14,color:T.textSub}}/>
                </PieChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Top Heavy Users" height={200}>
              {(data.heavy_users||[]).slice(0,5).map((r,i)=>(
                <div key={r.user_id} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 0',borderBottom:`1px solid ${T.borderSoft}`}}>
                  <span style={{fontSize:13,color:T.textFaint,minWidth:18}}>#{i+1}</span>
                  <code style={{fontFamily:mono,fontSize:11,color:T.textSub,flex:1}}>{r.user_id?.slice(0,16)}...</code>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:14,fontWeight:700,color:T.text}}>{fmt(r.gens)} gen</div>
                    <div style={{fontSize:13,color:T.textSub}}>{fmt(r.images)} img</div>
                  </div>
                </div>
              ))}
              {(data.heavy_users||[]).length===0&&<div style={s.empty}>No data yet</div>}
            </Panel>
          </div>
        </>
      )}
    </>
  );
}

// -- GENERATORS ----------------------------------------------------------------
function SecGenerators({api}){
  const[data,setData]=useState(null);const[days,setDays]=useState(30);const[err,setErr]=useState(null);
  useEffect(()=>{setData(null);fetch(`${api}/v1/admin/analytics/generators?days=${days}&limit=20`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));},[api,days]);
  if(err)return<div style={s.error}>! {err}</div>;
  return(
    <>
      <div style={s.filterRow}><DF value={days} onChange={setDays}/></div>
      {!data?<Loader/>:(
        <>
          <Panel title="Top Generators by Uses" sub={`Last ${days} days`} height={280} mb={12}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(data.top_generators||[]).slice(0,10).map(r=>({name:r.slug?.split('-').slice(0,2).join('-')||r.slug,uses:r.uses,images:r.images||0}))} layout="vertical" margin={{top:0,right:16,bottom:0,left:60}}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} horizontal={false}/>
                <XAxis type="number" tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
                <YAxis type="category" dataKey="name" tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false} width={80}/>
                <Tooltip content={<TT/>}/>
                <Legend iconSize={10} wrapperStyle={{fontSize:14,color:T.textSub}}/>
                <Bar dataKey="uses" name="Uses" fill={T.chart1} radius={[0,3,3,0]}/>
                <Bar dataKey="images" name="Images" fill={T.chart2} radius={[0,3,3,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
            <Panel title="Usage by Category" sub={`Last ${days} days`} height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={(data.by_category||[]).map(r=>({name:r.category||'Unknown',value:r.uses}))} cx="50%" cy="50%" innerRadius={52} outerRadius={78} dataKey="value" paddingAngle={3}>
                    {(data.by_category||[]).map((_,i)=><Cell key={i} fill={CHART_COLORS[i%8]}/>)}
                  </Pie>
                  <Tooltip formatter={(v)=>[fmt(v),'Uses']}/>
                  <Legend iconSize={10} wrapperStyle={{fontSize:14,color:T.textSub}}/>
                </PieChart>
              </ResponsiveContainer>
            </Panel>
            <TableCard title="Least Used" count={data.least_used?.length}>
              <table style={s.table}>
                <thead><tr><th style={s.th}>Generator</th><th style={s.th}>Uses</th></tr></thead>
                <tbody>
                  {(data.least_used||[]).map(r=>(
                    <tr key={r.slug}>
                      <td style={s.td}><div style={{fontSize:14,color:T.text}}>{r.name||r.slug}</div><code style={{fontFamily:mono,fontSize:11,color:T.textFaint}}>{r.slug}</code></td>
                      <td style={s.td}><span style={s.badge(r.uses===0?'red':'amber')}>{fmt(r.uses)}</span></td>
                    </tr>
                  ))}
                  {(data.least_used||[]).length===0&&<tr><td colSpan={2} style={s.empty}>No data yet</td></tr>}
                </tbody>
              </table>
            </TableCard>
          </div>
        </>
      )}
    </>
  );
}

// -- LEDGER --------------------------------------------------------------------
function SecLedger({api}){
  const[data,setData]=useState(null);const[page,setPage]=useState(1);
  const[userId,setUserId]=useState('');const[slug,setSlug]=useState('');
  const[source,setSource]=useState('');const[status,setStatus]=useState('');
  const[dateFrom,setDateFrom]=useState('');const[dateTo,setDateTo]=useState('');
  const[err,setErr]=useState(null);
  const load=useCallback(()=>{
    setData(null);const p=new URLSearchParams({page,per_page:50});
    if(userId)p.set('user_id',userId);if(slug)p.set('generator_slug',slug);
    if(source)p.set('credit_source',source);if(status)p.set('job_status',status);
    if(dateFrom)p.set('date_from',dateFrom);if(dateTo)p.set('date_to',dateTo);
    fetch(`${api}/v1/admin/usage/ledger?${p}`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));
  },[api,page,userId,slug,source,status,dateFrom,dateTo]);
  useEffect(()=>{load();},[load]);
  return(
    <>
      <div style={s.filterRow}>
        <input style={{...s.input,width:140}} placeholder="User ID" value={userId} onChange={e=>{setUserId(e.target.value);setPage(1);}}/>
        <input style={{...s.input,width:160}} placeholder="Generator slug" value={slug} onChange={e=>{setSlug(e.target.value);setPage(1);}}/>
        <select style={s.select} value={source} onChange={e=>{setSource(e.target.value);setPage(1);}}>
          <option value="">All sources</option>{['subscription','topup','trial','hobby'].map(v=><option key={v} value={v}>{v}</option>)}
        </select>
        <select style={s.select} value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
          <option value="">All statuses</option>{['success','failed','partial'].map(v=><option key={v} value={v}>{v}</option>)}
        </select>
        <input style={{...s.input,width:130}} type="date" value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setPage(1);}}/>
        <span style={{color:T.textFaint,fontSize:14}}>to</span>
        <input style={{...s.input,width:130}} type="date" value={dateTo} onChange={e=>{setDateTo(e.target.value);setPage(1);}}/>
        <button style={s.btn('primary')} onClick={()=>{setPage(1);load();}}>Apply</button>
      </div>
      {err&&<div style={s.error}>! {err}</div>}
      {!data&&<Loader/>}
      {data&&(
        <TableCard title="Usage Ledger" count={data.total}>
          <table style={s.table}>
            <thead><tr>{['Date','User','Generator','Images','Credits','Source','Status','Seed'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.logs.map(u=>(
                <tr key={u.id}>
                  <td style={{...s.td,fontSize:14,whiteSpace:'nowrap'}}>{fmtDate(u.created_at)}</td>
                  <td style={s.td}><code style={{fontFamily:mono,fontSize:11,color:T.textSub}}>{u.user_id?.slice(0,13)}...</code></td>
                  <td style={s.td}><span style={{fontSize:14}}>{u.generator_slug||'-'}</span></td>
                  <td style={s.tds}>{u.image_count}</td>
                  <td style={s.td}>{u.credits_used}</td>
                  <td style={s.td}><span style={s.badge('blue')}>{u.credit_source||'-'}</span></td>
                  <td style={s.td}><SBadge status={u.job_status||'unknown'}/></td>
                  <td style={s.td}><code style={{fontFamily:mono,fontSize:11,color:T.textFaint}}>{u.seed_base||'-'}</code></td>
                </tr>
              ))}
              {data.logs.length===0&&<tr><td colSpan={8} style={s.empty}>No records match your filters</td></tr>}
            </tbody>
          </table>
          <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage}/>
        </TableCard>
      )}
    </>
  );
}

// -- COMMERCE (unified sales / revenue / credits / reconciliation) ----------------------------
// Owner view of models.PaymentLedger, the platform-wide record of real-money events. These four
// sections are the Admin surface for /v1/admin/commerce/*. Two rules hold throughout:
//   * cash revenue and Virtual Shop CREDIT SPEND are never mixed -- selling a credit pack is the
//     sale, spending those credits later is not a second sale;
//   * nothing is fabricated -- an unavailable source renders an explicit unavailable state, and an
//     empty period renders an honest zero.
const COMMERCE_PERIODS = [
  { key: 'today',   label: 'Today'        },
  { key: 'week',    label: 'Last 7 days'  },
  { key: 'month',   label: 'Last 30 days' },
  { key: 'quarter', label: 'Last 90 days' },
  { key: 'year',    label: 'Last year'    },
  { key: 'all',     label: 'All time'     },
];

function PeriodPicker({ value, onChange }) {
  return (
    <select style={s.select} value={value} onChange={e => onChange(e.target.value)}>
      {COMMERCE_PERIODS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
    </select>
  );
}

// AUD is the platform reporting currency. Native-currency amounts are shown alongside it wherever
// a sale was not made in AUD, so a converted figure is never mistaken for the amount charged.
const aud = (n) => (n === null || n === undefined)
  ? '-'
  : 'A$' + Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const minorToMajor = (n, ccy) => (n === null || n === undefined)
  ? '-'
  : (ccy || '').toUpperCase() + ' ' + (Number(n) / 100).toLocaleString('en-AU',
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const productLabel = (key) => (key || 'unclassified').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// -- Commercial Summary ------------------------------------------------------------------------
function SecCommerceSummary({ api }) {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setData(null); setErr(null);
    fetch(`${api}/v1/admin/commerce/summary?period=${period}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch(() => setErr('Could not load the commercial summary.'));
  }, [api, period]);

  const cash = data?.cash_revenue;
  return (
    <>
      <div style={s.filterRow}>
        <PeriodPicker value={period} onChange={setPeriod} />
        {data && <span style={{ color: T.textFaint, fontSize: 13 }}>
          Business day: {data.timezone}
        </span>}
      </div>
      {err && <div style={s.error}>! {err}</div>}
      {!err && !data && <Loader />}
      {data && (
        <>
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: '20px 24px', marginBottom: 12, border: '1px solid rgba(255,255,255,0.07)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 16, alignItems: 'start' }}>
              <RadialKPI value={aud(cash.gross_aud)} label="Gross Revenue" color="blue" />
              <RadialKPI value={aud(cash.refunds_aud)} label="Refunds" color={cash.refunds_aud > 0 ? 'amber' : 'blue'} />
              <RadialKPI value={aud(cash.net_aud)} label="Net Revenue" color="green" />
              <RadialKPI value={fmt(cash.transactions)} label="Transactions" color="purple" />
              <RadialKPI value={aud(cash.average_transaction_aud)} label="Average Sale" color="blue" />
            </div>
            <div style={{ marginTop: 14, color: T.textFaint, fontSize: 13 }}>
              {fmt(cash.paying_customers)} paying customer{cash.paying_customers === 1 ? '' : 's'} in this period.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
            <TableCard title="Revenue by Product" count={data.by_product.length}>
              <table style={s.table}>
                <thead><tr>{['Product', 'Revenue (AUD)', 'Transactions', 'Buyers'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {data.by_product.map(p => (
                    <tr key={p.product}>
                      <td style={s.td}>{productLabel(p.product)}</td>
                      <td style={s.td}>{aud(p.revenue_aud)}</td>
                      <td style={s.tds}>{fmt(p.transactions)}</td>
                      <td style={s.tds}>{fmt(p.buyers)}</td>
                    </tr>
                  ))}
                  {data.by_product.length === 0 && (
                    <tr><td colSpan={4} style={s.empty}>No sales recorded in this period</td></tr>
                  )}
                </tbody>
              </table>
            </TableCard>

            <div>
              <Panel title="By Commercial Type" height={200}>
                <div style={{ padding: '4px 2px' }}>
                  {Object.keys(data.by_payment_type || {}).length === 0
                    ? <div style={s.empty}>No sales in this period</div>
                    : Object.entries(data.by_payment_type).map(([k, v]) => (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${T.borderSoft}` }}>
                          <span style={{ color: T.textSub, fontSize: 14 }}>{productLabel(k)}</span>
                          <strong style={{ fontSize: 14 }}>{aud(v)}</strong>
                        </div>
                      ))}
                </div>
              </Panel>
              <Panel title="Charged Currencies" height={170}>
                <div style={{ padding: '4px 2px' }}>
                  {(data.by_currency || []).length === 0
                    ? <div style={s.empty}>No sales in this period</div>
                    : data.by_currency.map(c => (
                        <div key={c.currency} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${T.borderSoft}` }}>
                          <span style={{ color: T.textSub, fontSize: 14 }}>
                            {c.currency} <span style={{ color: T.textFaint }}>({fmt(c.transactions)})</span>
                          </span>
                          <strong style={{ fontSize: 14 }}>{minorToMajor(c.amount_minor, c.currency)}</strong>
                        </div>
                      ))}
                  <div style={{ color: T.textFaint, fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
                    Amounts as charged. AUD totals above convert non-AUD sales at the platform rate.
                  </div>
                </div>
              </Panel>
            </div>
          </div>
          <div style={{ color: T.textFaint, fontSize: 12, lineHeight: 1.6 }}>{data.note}</div>
        </>
      )}
    </>
  );
}

// -- Account drill-through ---------------------------------------------------------------------
// Opened from a transaction row so the owner can go from "what sold" straight to "who bought it and
// what do they own". Read-only; the endpoint deliberately returns no secrets.
function CommerceAccountPanel({ api, userId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setData(null); setErr(null);
    fetch(`${api}/v1/admin/commerce/account/${encodeURIComponent(userId)}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch(() => setErr('Could not load this account.'));
  }, [api, userId]);

  const flags = data?.entitlements?.product_flags || {};
  const owned = Object.entries(flags).filter(([, v]) =>
    v === true || (v && typeof v === 'object' && v.unlocked));

  return (
    <div style={{ marginBottom: 12, border: `1px solid ${T.borderSoft}`, borderRadius: 10, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <strong style={{ fontSize: 15 }}>Account detail</strong>
        <button style={s.btn()} onClick={onClose}>Close</button>
      </div>
      {err && <div style={s.error}>! {err}</div>}
      {!err && !data && <Loader />}
      {data && (
        <>
          {/* Products / Access / Usage. Free products included, so the owner can answer
              "do they have File Converter and have they used it" without another screen. */}
          <AccountProductsPanel api={api} userId={userId}/>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 12 }}>
            <RadialKPI value={aud(data.commercial.lifetime_gross_aud)} label="Lifetime Gross" color="blue" />
            <RadialKPI value={aud(data.commercial.lifetime_refunds_aud)} label="Refunded" color="amber" />
            <RadialKPI value={fmt(data.commercial.transaction_count)} label="Transactions" color="purple" />
            <RadialKPI value={fmt(data.commercial.not_granted_count)}
                       label="Paid, Not Granted"
                       color={data.commercial.not_granted_count > 0 ? 'red' : 'green'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Panel title="Identity" height={210}>
              <div style={{ padding: '4px 2px', fontSize: 14, lineHeight: 1.9 }}>
                <div>{data.identity.email}</div>
                <div style={{ color: T.textFaint }}>Joined {fmtDate(data.identity.created_at)}</div>
                <div style={{ color: T.textFaint }}>Status {data.identity.account_status || '-'}</div>
                <div style={{ color: T.textFaint }}>Stripe {data.identity.stripe_customer_id || '-'}</div>
                <div style={{ color: T.textFaint }}>
                  Spendable credits {fmt(data.entitlements?.spendable_credits?.topup_credits)}
                </div>
              </div>
            </Panel>
            <Panel title="Product Access" height={210}>
              <div style={{ padding: '4px 2px' }}>
                {owned.length === 0
                  ? <div style={s.empty}>No product unlocks on this account</div>
                  : owned.map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
                        <span style={{ fontSize: 14 }}>{productLabel(k)}</span>
                        <span style={s.badge('green')}>
                          {typeof v === 'object' && v.status ? v.status : 'owned'}
                        </span>
                      </div>
                    ))}
                {Array.isArray(data.entitlements?.scc) && data.entitlements.scc.length > 0 && (
                  <div style={{ marginTop: 8, color: T.textFaint, fontSize: 13 }}>
                    SCC business: {data.entitlements.scc.map(t => t.tenant_name || t.tenant_id).join(', ')}
                  </div>
                )}
              </div>
            </Panel>
          </div>

          {Array.isArray(data.reconciliation) && data.reconciliation.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <TableCard title="Unresolved for this account" count={data.reconciliation.length}>
                <table style={s.table}>
                  <thead><tr>{['Date', 'Type', 'Product', 'Reason'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {data.reconciliation.map(r => (
                      <tr key={r.id}>
                        <td style={{ ...s.td, fontSize: 14 }}>{fmtDate(r.created_at)}</td>
                        <td style={s.td}><span style={s.badge('amber')}>{productLabel(r.reconciliation_type)}</span></td>
                        <td style={s.td}>{productLabel(r.purchase_type)}</td>
                        <td style={{ ...s.td, fontSize: 13, color: T.textSub }}>{(r.failure_reason || '').slice(0, 90)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableCard>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// -- Sales / Transactions ----------------------------------------------------------------------
function SecCommerceTransactions({ api }) {
  const [openAccount, setOpenAccount] = useState(null);
  const [period, setPeriod] = useState('month');
  const [page, setPage] = useState(1);
  const [product, setProduct] = useState('');
  const [ptype, setPtype] = useState('');
  const [userId, setUserId] = useState('');
  const [refunds, setRefunds] = useState('');
  const [entitlement, setEntitlement] = useState('');
  const [stripeRef, setStripeRef] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    setData(null); setErr(null);
    const p = new URLSearchParams({ period, page, per_page: 50 });
    if (product) p.set('product', product);
    if (ptype) p.set('payment_type', ptype);
    if (userId) p.set('user_id', userId);
    if (refunds) p.set('refunds', refunds);
    if (entitlement) p.set('entitlement', entitlement);
    if (stripeRef) p.set('stripe_ref', stripeRef.trim());
    fetch(`${api}/v1/admin/commerce/transactions?${p}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch(() => setErr('Could not load transactions.'));
  }, [api, period, page, product, ptype, userId, refunds, entitlement, stripeRef]);
  useEffect(() => { load(); }, [load]);

  const reset = (fn) => (e) => { fn(e.target.value); setPage(1); };
  return (
    <>
      <div style={s.filterRow}>
        <PeriodPicker value={period} onChange={v => { setPeriod(v); setPage(1); }} />
        <input style={{ ...s.input, width: 190 }} placeholder="Product key" value={product} onChange={reset(setProduct)} />
        <select style={s.select} value={ptype} onChange={reset(setPtype)}>
          <option value="">All types</option>
          {['subscription', 'subscription_renewal', 'one_off', 'credit_topup'].map(v => <option key={v} value={v}>{productLabel(v)}</option>)}
        </select>
        <input style={{ ...s.input, width: 150 }} placeholder="User ID" value={userId} onChange={reset(setUserId)} />
        <select style={s.select} value={refunds} onChange={reset(setRefunds)}>
          <option value="">Sales and refunds</option>
          <option value="exclude">Sales only</option>
          <option value="only">Refunds only</option>
        </select>
        <select style={s.select} value={entitlement} onChange={reset(setEntitlement)}>
          <option value="">Any grant status</option>
          <option value="missing">Paid, not granted</option>
        </select>
        <input style={{ ...s.input, width: 210 }} placeholder="Stripe session / PI / event id" value={stripeRef} onChange={reset(setStripeRef)} />
        <button style={s.btn('primary')} onClick={() => { setPage(1); load(); }}>Apply</button>
      </div>
      {openAccount && (
        <CommerceAccountPanel api={api} userId={openAccount} onClose={() => setOpenAccount(null)} />
      )}
      {err && <div style={s.error}>! {err}</div>}
      {!err && !data && <Loader />}
      {data && (
        <TableCard title="Transactions" count={data.total}>
          <table style={s.table}>
            <thead><tr>{['Date', 'Account', 'Product', 'Type', 'Amount', 'Charged', 'Granted', 'Stripe'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.transactions.map(t => (
                <tr key={t.id}>
                  <td style={{ ...s.td, fontSize: 14, whiteSpace: 'nowrap' }}>{fmtDate(t.created_at)}</td>
                  <td style={s.td}>
                    {t.user_email
                      ? <button style={{ ...s.btn(), fontSize: 13, padding: '4px 10px' }}
                                onClick={() => setOpenAccount(t.user_id)}>
                          {t.user_email}
                        </button>
                      : <span style={{ ...s.badge('amber') }}>no account</span>}
                  </td>
                  <td style={s.td}><span style={{ fontSize: 14 }}>{productLabel(t.payment_type_detail)}</span></td>
                  <td style={s.td}><span style={s.badge('blue')}>{productLabel(t.payment_type)}</span></td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                    {t.is_refund
                      ? <span style={{ color: T.amber }}>{aud(t.amount_aud)}</span>
                      : aud(t.amount_aud)}
                  </td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap', color: T.textSub, fontSize: 13 }}>
                    {minorToMajor(t.amount_minor, t.currency)}
                    {t.fx_rate_used ? <span style={{ color: T.textFaint }}> @{t.fx_rate_used}</span> : null}
                  </td>
                  <td style={s.td}>
                    {t.is_refund
                      ? <span style={s.badge('amber')}>refund</span>
                      : t.entitlement_granted
                        ? <span style={s.badge('green')}>granted</span>
                        : <span style={s.badge('red')}>NOT GRANTED</span>}
                  </td>
                  <td style={s.td}>
                    <code style={{ fontFamily: mono, fontSize: 11, color: T.textFaint }}>
                      {(t.stripe_session_id || t.stripe_payment_intent_id || t.stripe_event_id || '-')?.slice(0, 20)}
                    </code>
                  </td>
                </tr>
              ))}
              {data.transactions.length === 0 && (
                <tr><td colSpan={8} style={s.empty}>No transactions match your filters</td></tr>
              )}
            </tbody>
          </table>
          <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} />
        </TableCard>
      )}
    </>
  );
}

// -- Shop Credit Activity ----------------------------------------------------------------------
function SecCommerceCredits({ api }) {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setData(null); setErr(null);
    fetch(`${api}/v1/admin/commerce/credit-activity?period=${period}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch(() => setErr('Could not load Shop Credit activity.'));
  }, [api, period]);

  return (
    <>
      <div style={s.filterRow}><PeriodPicker value={period} onChange={setPeriod} /></div>
      {err && <div style={s.error}>! {err}</div>}
      {!err && !data && <Loader />}
      {data && data.available === false && (
        // Honest unavailable state -- never a zero that reads as a real total.
        <div style={s.error}>Shop Credit activity is unavailable: {data.reason}</div>
      )}
      {data && data.available !== false && (
        <>
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: '20px 24px', marginBottom: 12, border: '1px solid rgba(255,255,255,0.07)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, alignItems: 'start' }}>
              <RadialKPI value={aud(data.shop_credit_pack_revenue_aud)} label="Pack Revenue (cash)" color="green" />
              <RadialKPI value={fmt(data.credits_granted)} label="Credits Granted" color="blue" />
              <RadialKPI value={fmt(data.credits_spent)} label="Credits Spent" color="purple" />
              <RadialKPI value={fmt(data.ledger_events)} label="Ledger Events" color="blue" />
            </div>
          </div>
          <Panel title="How to read this" height={130}>
            <div style={{ padding: '6px 2px', color: T.textSub, fontSize: 14, lineHeight: 1.7 }}>
              <div><strong>Pack Revenue</strong> is real money received for Shop Credit packs and is the only
                figure here that appears in revenue totals.</div>
              <div style={{ marginTop: 6 }}><strong>Credits Granted / Spent</strong> are movements of internal
                currency. Spending credits is <em>not</em> a second sale and is never added to revenue.</div>
              <div style={{ marginTop: 6, color: T.textFaint, fontSize: 13 }}>{data.note}</div>
            </div>
          </Panel>
        </>
      )}
    </>
  );
}

// -- Reconciliation ----------------------------------------------------------------------------
const RECON_LABELS = {
  payment_without_entitlement: 'Paid, entitlement not granted',
  payment_without_account: 'Payment with no resolvable account',
  grant_failures: 'Webhook fulfilment failures',
  active_subscription_without_payment: 'Active subscription, no recorded payment',
};

function SecCommerceReconciliation({ api }) {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setData(null); setErr(null);
    fetch(`${api}/v1/admin/commerce/reconciliation?period=${period}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch(() => setErr('Could not load reconciliation.'));
  }, [api, period]);

  return (
    <>
      <div style={s.filterRow}><PeriodPicker value={period} onChange={setPeriod} /></div>
      {err && <div style={s.error}>! {err}</div>}
      {!err && !data && <Loader />}
      {data && (
        <>
          <div style={{
            background: data.clean ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)',
            border: `1px solid ${data.clean ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}`,
            borderRadius: 10, padding: '16px 20px', marginBottom: 12,
          }}>
            <strong style={{ fontSize: 15 }}>
              {data.clean
                ? 'No commercial mismatches detected in this period.'
                : `${fmt(data.total_findings)} unresolved mismatch${data.total_findings === 1 ? '' : 'es'}`}
            </strong>
            <div style={{ color: T.textFaint, fontSize: 13, marginTop: 6 }}>{data.note}</div>
          </div>

          {Object.entries(data.findings).map(([key, f]) => (
            <TableCard key={key} title={RECON_LABELS[key] || productLabel(key)} count={f.count}>
              <div style={{ padding: '10px 16px', color: T.textFaint, fontSize: 13 }}>{f.description}</div>
              {f.count === 0
                ? <div style={s.empty}>None</div>
                : (
                  <table style={s.table}>
                    <thead><tr>{['Date', 'Account', 'Product', 'Amount', 'Reference'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {f.items.map((it, i) => (
                        <tr key={it.id || i}>
                          <td style={{ ...s.td, fontSize: 14, whiteSpace: 'nowrap' }}>{fmtDate(it.created_at)}</td>
                          <td style={s.td}>
                            <code style={{ fontFamily: mono, fontSize: 11, color: T.textSub }}>
                              {(it.user_id || 'none')?.slice(0, 16)}
                            </code>
                          </td>
                          <td style={s.td}>{productLabel(it.payment_type_detail || it.purchase_type)}</td>
                          <td style={s.td}>{it.amount_aud !== undefined ? aud(it.amount_aud) : '-'}</td>
                          <td style={s.td}>
                            <code style={{ fontFamily: mono, fontSize: 11, color: T.textFaint }}>
                              {(it.stripe_session_id || it.stripe_subscription_id || it.failure_reason || '-')?.slice(0, 40)}
                            </code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </TableCard>
          ))}
        </>
      )}
    </>
  );
}

// -- COSTS ---------------------------------------------------------------------
function SecCosts({api}){
  const[data,setData]=useState(null);const[days,setDays]=useState(30);const[err,setErr]=useState(null);
  useEffect(()=>{setData(null);fetch(`${api}/v1/admin/analytics/costs?days=${days}`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));},[api,days]);
  if(err)return<div style={s.error}>! {err}</div>;
  return(
    <>
      <div style={s.filterRow}><DF value={days} onChange={setDays}/></div>
      {!data?<Loader/>:(
        <>
          <div style={{background:'rgba(255,255,255,0.03)',borderRadius:10,padding:'20px 24px',marginBottom:12,border:'1px solid rgba(255,255,255,0.07)'}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,alignItems:'start'}}>
              <RadialKPI value={`$${data.total_cost_usd}`} label="All-time Cost USD" color="amber"/>
              <RadialKPI value={`$${data.period_cost_usd}`} label={`Cost ${days}d USD`} color="amber"/>
              <RadialKPI value={`$${data.cost_per_image_usd}`} label="Cost per Image" color="blue"/>
            </div>
          </div>
          <Panel title="Daily Provider Cost" sub={`USD spend per day over last ${days} days`} height={240} mb={12}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[...(data.daily||[])].reverse()} margin={{top:4,right:16,bottom:0,left:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
                <XAxis dataKey="day" tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false} interval="preserveStartEnd"/>
                <YAxis tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
                <Tooltip formatter={(v)=>[`$${v}`,'Cost USD']}/>
                <Bar dataKey="cost" name="Cost (USD)" fill={T.chart3} radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          <TableCard title="Top Cost Users" count={data.top_cost_users?.length}>
            <table style={s.table}>
              <thead><tr><th style={s.th}>#</th><th style={s.th}>User ID</th><th style={s.th}>Cost (USD)</th><th style={s.th}>Jobs</th></tr></thead>
              <tbody>
                {(data.top_cost_users||[]).map((r,i)=>(
                  <tr key={r.user_id}>
                    <td style={{...s.td,color:T.textFaint,fontSize:14}}>#{i+1}</td>
                    <td style={s.td}><code style={{fontFamily:mono,fontSize:13,color:T.textSub}}>{r.user_id?.slice(0,16)}...</code></td>
                    <td style={s.tds}>${r.cost}</td>
                    <td style={s.td}>{fmt(r.jobs)}</td>
                  </tr>
                ))}
                {(data.top_cost_users||[]).length===0&&<tr><td colSpan={4} style={s.empty}>No cost records yet</td></tr>}
              </tbody>
            </table>
          </TableCard>
        </>
      )}
    </>
  );
}

// -- ACTIVITY ------------------------------------------------------------------
function SecActivity({api}){
  const[data,setData]=useState(null);const[limit,setLimit]=useState(50);const[err,setErr]=useState(null);
  const load=useCallback(()=>{setData(null);fetch(`${api}/v1/admin/activity/feed?limit=${limit}`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));},[api,limit]);
  useEffect(()=>{load();},[load]);
  return(
    <>
      <div style={s.filterRow}>
        <select style={s.select} value={limit} onChange={e=>setLimit(Number(e.target.value))}>
          {[25,50,100,200].map(n=><option key={n} value={n}>Last {n} events</option>)}
        </select>
        <button style={s.btn('primary')} onClick={load}>Refresh</button>
      </div>
      {err&&<div style={s.error}>! {err}</div>}
      {!data&&<Loader/>}
      {data&&(
        <TableCard title="Generation Events" count={data.count}>
          <table style={s.table}>
            <thead><tr>{['Time','User','Generator','Images','Credits','Source','Status'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.events.map((e,i)=>(
                <tr key={i}>
                  <td style={{...s.td,whiteSpace:'nowrap'}}>
                    <div style={{fontSize:14}}>{fmtAgo(e.at)}</div>
                    <div style={{fontSize:13,color:T.textFaint}}>{fmtDate(e.at)}</div>
                  </td>
                  <td style={s.td}><code style={{fontFamily:mono,fontSize:11,color:T.textSub}}>{e.user_id?.slice(0,13)}...</code></td>
                  <td style={s.td}><span style={{fontSize:14}}>{e.generator_slug||'-'}</span></td>
                  <td style={s.tds}>{e.image_count}</td>
                  <td style={s.td}>{e.credits_used}</td>
                  <td style={s.td}><span style={s.badge('blue')}>{e.credit_source||'-'}</span></td>
                  <td style={s.td}><SBadge status={e.status||'unknown'}/></td>
                </tr>
              ))}
              {data.events.length===0&&<tr><td colSpan={7} style={s.empty}>No events yet</td></tr>}
            </tbody>
          </table>
        </TableCard>
      )}
    </>
  );
}

// -- USER PROFILE --------------------------------------------------------------
function UserProfile({api,userId,onBack,defaultTab}){
  const{hasPermission}=useStaffAuth();
  const[tab,setTab]=useState(defaultTab||'overview');

  // Overview data (existing endpoint)
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/admin/users/${userId}/intelligence`,{credentials:'include'})
      .then(r=>r.json()).then(setData).catch(()=>setErr('Failed to load profile'));
  },[api,userId]);

  // Intelligence data (new endpoint) -- lazy loaded when tab activated
  const[intel,setIntel]=useState(null);const[intelErr,setIntelErr]=useState(null);
  const[intelLoading,setIntelLoading]=useState(false);
  const loadIntel=useCallback(()=>{
    if(intelLoading)return;
    setIntelLoading(true);setIntelErr(null);
    fetch(`${api}/v1/staff/intelligence/${userId}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setIntel(d);else setIntelErr(d.error||'Failed');})
      .catch(()=>setIntelErr('Network error'))
      .finally(()=>setIntelLoading(false));
  },[api,userId,intelLoading]);

  // Mail history data -- lazy loaded when tab activated
  const[mail,setMail]=useState(null);const[mailErr,setMailErr]=useState(null);
  const[mailLoading,setMailLoading]=useState(false);
  const loadMail=useCallback(()=>{
    if(mailLoading)return;
    setMailLoading(true);setMailErr(null);
    fetch(`${api}/v1/staff/comms/user-mail-history/${userId}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setMail(d);else setMailErr(d.error||'Failed');})
      .catch(()=>setMailErr('Network error'))
      .finally(()=>setMailLoading(false));
  },[api,userId,mailLoading]);

  // Load data when tab activates
  useEffect(()=>{
    if((tab==='intelligence'||tab==='security')&&!intel&&!intelLoading)loadIntel();
  },[tab,intel,intelLoading,loadIntel]);
  useEffect(()=>{
    if(tab==='communications'&&!mail&&!mailLoading)loadMail();
  },[tab,mail,mailLoading,loadMail]);

  const canIntel=hasPermission('intelligence.view');
  const canComms=hasPermission('comms.view');

  const TABS=[
    {id:'overview',     label:'Overview',       always:true},
    {id:'intelligence', label:'Intelligence',   perm:canIntel},
    {id:'communications',label:'Communications',perm:canComms},
    {id:'security',     label:'Security',       perm:canIntel},
  ].filter(t=>t.always||t.perm);

  // Ensure active tab is valid after permission filter
  const validTab=TABS.find(t=>t.id===tab)?tab:TABS[0].id;

  const tabStyle=(id)=>({
    padding:'8px 16px',borderRadius:'6px 6px 0 0',fontSize:14,fontWeight:600,
    cursor:'pointer',border:'none',background:validTab===id?T.surface:'transparent',
    color:validTab===id?T.text:T.textSub,
    borderBottom:validTab===id?`2px solid ${T.accent}`:'2px solid transparent',
    transition:'all 0.1s',
  });

  if(err)return<><button style={s.btn()} onClick={onBack}>&lt;- Back</button><div style={{marginTop:12,...s.error}}>! {err}</div></>;
  if(!data)return<Loader/>;

  const u=data.user;const usage=data.usage;
  const icData=Object.entries(usage.image_count_dist||{}).map(([k,v])=>({name:`${k} img`,value:v}));
  const actData=[...data.daily_30d].reverse().map(r=>({day:r.day.slice(5),gens:r.gens}));

  // -- Tab: Overview (existing content verbatim)
  const renderOverview=()=>(
    <>
      <div style={{...s.panel,marginBottom:12,padding:'16px 20px'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,alignItems:'start'}}>
          <RadialKPI value={fmt(usage.total_generations)} label="Generations" color="blue"/>
          <RadialKPI value={fmt(usage.total_images)} label="Images" color="purple"/>
          <RadialKPI value={fmt(usage.total_credits)} label="Credits Used" color="amber"/>
          <RadialKPI value={fmt(u.credits)} label="Current Credits" color="green"/>
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,marginBottom:12}}>
        <Panel title="30-Day Activity" sub="Generations per day" height={200}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={actData} margin={{top:4,right:8,bottom:0,left:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
              <XAxis dataKey="day" tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false} interval={6}/>
              <YAxis tick={{fontSize:13,fill:T.textSub}} axisLine={false} tickLine={false}/>
              <Tooltip content={<TT/>}/>
              <Bar dataKey="gens" name="Generations" fill={T.chart1} radius={[2,2,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Image Count Behaviour" height={200}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={icData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={4}>
                {icData.map((_,i)=><Cell key={i} fill={CHART_COLORS[i]}/>)}
              </Pie>
              <Tooltip formatter={(v)=>[fmt(v),'Generations']}/>
              <Legend iconSize={10} wrapperStyle={{fontSize:14,color:T.textSub}}/>
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
        <TableCard title="Account Details">
          <div style={{padding:'4px 0'}}>
            {[['Email',u.email],['Name',`${u.first_name||''} ${u.last_name||''}`.trim()||'-'],['Company',u.company||'-'],['Country',u.country||'-'],['Tier',u.tier],['Credits',fmt(u.credits)],['Topup Credits',fmt(u.topup_credits)]].map(([k,v])=>(
              <div key={k} style={{...s.kvRow,padding:'8px 16px'}}><span style={s.kvLabel}>{k}</span><span style={s.kvValue}>{v}</span></div>
            ))}
          </div>
        </TableCard>
        <TableCard title="Subscription">
          {data.subscription?(
            <div style={{padding:'4px 0'}}>
              {[['Plan',data.subscription.plan?.name],['Status',data.subscription.status],['Period End',fmtDate(data.subscription.current_period_end)],['Used This Period',fmt(data.subscription.specs_used_this_period)]].map(([k,v])=>(
                <div key={k} style={{...s.kvRow,padding:'8px 16px'}}><span style={s.kvLabel}>{k}</span><span style={s.kvValue}>{v}</span></div>
              ))}
            </div>
          ):(
            <div style={{...s.empty,padding:'32px 0'}}>No active subscription</div>
          )}
        </TableCard>
      </div>
      <TableCard title="Top Generators">
        <table style={s.table}>
          <thead><tr><th style={s.th}>#</th><th style={s.th}>Generator</th><th style={s.th}>Uses</th></tr></thead>
          <tbody>
            {data.top_generators.map((r,i)=>(
              <tr key={r.slug}>
                <td style={{...s.td,color:T.textFaint,width:28}}>#{i+1}</td>
                <td style={s.td}><div style={{fontSize:15,fontWeight:600,color:T.text}}>{r.name||r.slug}</div><code style={{fontFamily:mono,fontSize:11,color:T.textFaint}}>{r.slug}</code></td>
                <td style={s.tds}>{fmt(r.uses)}</td>
              </tr>
            ))}
            {data.top_generators.length===0&&<tr><td colSpan={3} style={s.empty}>No generators used yet</td></tr>}
          </tbody>
        </table>
      </TableCard>
    </>
  );

  // -- Tab: Intelligence (compact timeline using shared IntelEventRow)
  const renderIntelligence=()=>{
    if(intelLoading)return<Loader/>;
    if(intelErr)return<div style={s.error}>! {intelErr}</div>;
    if(!intel)return<Loader/>;
    const timeline=intel.timeline||[];
    const critCount=timeline.filter(e=>e.severity==='critical').length;
    const highCount=timeline.filter(e=>e.severity==='high').length;
    return(
      <>
        {/* KPI summary row */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:10,marginBottom:12}}>
          {[
            {label:'Total Events',    val:intel.timeline_count,          color:T.chart1},
            {label:'Critical',        val:critCount,                      color:critCount>0?T.red:T.chart2},
            {label:'High',            val:highCount,                      color:highCount>0?'#c05621':T.chart2},
            {label:'Failed Jobs',     val:intel.failed_jobs,              color:intel.failed_jobs>0?T.red:T.chart2},
            {label:'Mail Failures',   val:intel.mail_failed,              color:intel.mail_failed>0?T.red:T.chart2},
            {label:'Suppressed',      val:intel.suppressed?'YES':'No',    color:intel.suppressed?T.red:T.green},
          ].map(({label,val,color})=>(
            <div key={label} style={{...s.panel,padding:'10px 12px',borderTop:`3px solid ${color}`}}>
              <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>{label}</div>
              <div style={{fontSize:22,fontWeight:800,color:T.text,lineHeight:1}}>{fmt(val)}</div>
            </div>
          ))}
        </div>
        {/* Severity alert */}
        {(critCount>0||highCount>0)&&(
          <div style={{display:'flex',gap:8,marginBottom:10}}>
            {critCount>0&&<div style={{padding:'6px 12px',borderRadius:5,background:T.redBg,border:`1px solid ${T.redBorder}`,fontSize:13,fontWeight:700,color:T.red}}>{critCount} CRITICAL</div>}
            {highCount>0&&<div style={{padding:'6px 12px',borderRadius:5,background:'#fffaf0',border:'1px solid #fbd38d',fontSize:13,fontWeight:700,color:'#c05621'}}>{highCount} HIGH</div>}
          </div>
        )}
        {/* Timeline */}
        <div style={{display:'flex',flexDirection:'column',gap:3}}>
          {timeline.length===0?<div style={{...s.panel,...s.empty}}>No intelligence events found.</div>
            :timeline.map((ev,i)=><IntelEventRow key={`intel-${ev.type}-${ev.ts}-${i}`} ev={ev}/>)}
        </div>
      </>
    );
  };

  // -- Tab: Communications (mail history endpoint)
  const renderCommunications=()=>{
    if(mailLoading)return<Loader/>;
    if(mailErr)return<div style={s.error}>! {mailErr}</div>;
    if(!mail)return<Loader/>;
    const auditEvents=mail.audit_events||[];
    const tokenEvents=mail.token_events||[];
    const deliveryEvents=mail.delivery_events||[];
    return(
      <>
        {/* Token events */}
        <TableCard title="Auth Token Emails" count={tokenEvents.length}>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Type</th><th style={s.th}>State</th><th style={s.th}>Created By</th><th style={s.th}>Expires</th><th style={s.th}>Sent</th></tr></thead>
            <tbody>
              {tokenEvents.length===0?<tr><td colSpan={5} style={{...s.td,...s.empty}}>No token emails.</td></tr>
                :tokenEvents.map((t,i)=>(
                <tr key={i}>
                  <td style={s.td}><span style={s.badge('blue')}>{(t.token_type||'').replace(/_/g,' ')}</span></td>
                  <td style={s.td}><span style={s.badge(t.is_used?'green':t.state==='Expired'?'red':'amber')}>{t.is_used?'Redeemed':t.detail||'-'}</span></td>
                  <td style={s.td}><span style={{fontSize:13,fontFamily:'monospace',color:T.textSub}}>{t.created_by||'system'}</span></td>
                  <td style={{...s.td,fontSize:13,color:T.textSub}}>{fmtDate(t.expires_at)}</td>
                  <td style={{...s.td,fontSize:13,color:T.textSub}}>{fmtDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
        {/* Mail audit events */}
        <TableCard title="Mail Audit Events" count={auditEvents.length}>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Status</th><th style={s.th}>Template</th><th style={s.th}>Error</th><th style={s.th}>Sent At</th></tr></thead>
            <tbody>
              {auditEvents.length===0?<tr><td colSpan={4} style={{...s.td,...s.empty}}>No mail audit events.</td></tr>
                :auditEvents.map((e,i)=>{
                const status=(e.action||'').replace('MAIL:','');
                return(
                  <tr key={i}>
                    <td style={s.td}><MailStatusPill status={status}/></td>
                    <td style={s.td}><code style={{fontFamily:'monospace',fontSize:13,color:T.accent}}>{e.template||'-'}</code></td>
                    <td style={{...s.td,fontSize:13,color:T.red}}>{e.error||'-'}</td>
                    <td style={{...s.td,fontSize:13,color:T.textSub}}>{fmtDate(e.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableCard>
        {/* SES delivery events */}
        <TableCard title="SES Delivery Events" count={deliveryEvents.length}>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Event Type</th><th style={s.th}>SES Message ID</th><th style={s.th}>Provider</th><th style={s.th}>Time</th></tr></thead>
            <tbody>
              {deliveryEvents.length===0?<tr><td colSpan={4} style={{...s.td,...s.empty}}>No SES delivery events.</td></tr>
                :deliveryEvents.map((e,i)=>(
                <tr key={i}>
                  <td style={s.td}>
                    <span style={{display:'inline-flex',padding:'3px 9px',borderRadius:4,fontSize:13,fontWeight:700,
                      background:e.event_type==='Bounce'||e.event_type==='bounce'?T.redBg:e.event_type==='Complaint'||e.event_type==='complaint'?'#faf5ff':T.greenBg,
                      color:e.event_type==='Bounce'||e.event_type==='bounce'?T.red:e.event_type==='Complaint'||e.event_type==='complaint'?T.purple:T.green,
                    }}>{e.event_type}</span>
                  </td>
                  <td style={s.td}><span style={{fontFamily:'monospace',fontSize:11,color:T.textSub}}>{e.ses_message_id?e.ses_message_id.slice(0,28)+'...':'-'}</span></td>
                  <td style={s.td}><span style={{fontSize:13}}>{e.provider||'-'}</span></td>
                  <td style={{...s.td,fontSize:13,color:T.textSub}}>{fmtDate(e.event_timestamp||e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      </>
    );
  };

  // -- Tab: Security (intel filtered to security event types)
  const renderSecurity=()=>{
    if(intelLoading)return<Loader/>;
    if(intelErr)return<div style={s.error}>! {intelErr}</div>;
    if(!intel)return<Loader/>;
    const SEC_TYPES=new Set(['failed_login','staff_session','suppression']);
    const SEC_TOKEN_TYPES=new Set(['password_reset','mfa_recovery']);
    const secEvents=(intel.timeline||[]).filter(ev=>{
      if(SEC_TYPES.has(ev.type))return true;
      if(ev.type==='token_event'&&SEC_TOKEN_TYPES.has(ev.meta&&ev.meta.token_type))return true;
      if(ev.type==='suppression')return true;
      return false;
    });
    return(
      <>
        {intel.suppressed&&(
          <div style={{padding:'10px 14px',background:T.redBg,border:`1px solid ${T.redBorder}`,borderRadius:7,fontSize:14,fontWeight:700,color:T.red,marginBottom:12}}>
            Email suppressed: {intel.suppression_type||'unknown reason'}
          </div>
        )}
        <div style={{display:'flex',flexDirection:'column',gap:3}}>
          {secEvents.length===0?<div style={{...s.panel,...s.empty}}>No security events for this user.</div>
            :secEvents.map((ev,i)=><IntelEventRow key={`sec-${ev.type}-${ev.ts}-${i}`} ev={ev}/>)}
        </div>
      </>
    );
  };

  return(
    <>
      <div style={{marginBottom:12}}><button style={s.btn()} onClick={onBack}>&lt;- Back to Users</button></div>

      {/* Identity header */}
      <div style={{...s.panel,marginBottom:0,display:'flex',alignItems:'center',gap:16,borderRadius:'8px 8px 0 0',borderBottom:'none'}}>
        <div style={{width:46,height:46,borderRadius:'50%',background:T.accentLight,border:`1px solid ${T.accentBorder}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,fontWeight:800,color:T.accent}}>
          {(u.first_name?.[0]||u.email?.[0]||'?').toUpperCase()}
        </div>
        <div>
          <div style={{fontSize:17,fontWeight:700,color:T.text}}>{`${u.first_name||''} ${u.last_name||''}`.trim()||'Unknown'}</div>
          <div style={{fontSize:14,color:T.textSub}}>{u.email}</div>
          <div style={{marginTop:4,display:'flex',gap:5}}>
            <SBadge status={u.tier}/>{u.is_admin&&<span style={s.badge('purple')}>Admin</span>}{u.email_verified&&<span style={s.badge('green')}>Verified</span>}
          </div>
        </div>
        <div style={{marginLeft:'auto',textAlign:'right'}}>
          <div style={{fontSize:14,color:T.textSub}}>Member since</div>
          <div style={{fontSize:14,fontWeight:700,color:T.text}}>{fmtDate(u.created_at)}</div>
          <div style={{fontSize:14,color:T.textSub,marginTop:3}}>Last active</div>
          <div style={{fontSize:14,fontWeight:700,color:T.text}}>{fmtAgo(usage.last_active)}</div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{display:'flex',gap:2,borderBottom:`1px solid ${T.border}`,marginBottom:14,background:T.surface,padding:'0 16px',borderRadius:'0 0 0 0'}}>
        {TABS.map(t=>(
          <button key={t.id} style={tabStyle(t.id)} onClick={()=>setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* Tab content */}
      {validTab==='overview'      && renderOverview()}
      {validTab==='intelligence'  && renderIntelligence()}
      {validTab==='communications'&& renderCommunications()}
      {validTab==='security'      && renderSecurity()}
    </>
  );
}

// -- USERS ---------------------------------------------------------------------
function SecUsers({api,onAction,onIntel}){
  const{hasPermission}=useStaffAuth();
  const[data,setData]=useState(null);const[page,setPage]=useState(1);
  const[search,setSearch]=useState('');const[tier,setTier]=useState('');
  const[selected,setSelected]=useState(null);const[selectedTab,setSelectedTab]=useState('overview');
  const[err,setErr]=useState(null);
  const load=useCallback(()=>{
    setData(null);const p=new URLSearchParams({page,per_page:50});
    if(search)p.set('search',search);if(tier)p.set('tier',tier);
    fetch(`${api}/v1/admin/users?${p}`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));
  },[api,page,search,tier]);
  useEffect(()=>{load();},[load]);
  if(selected)return<UserProfile api={api} userId={selected} defaultTab={selectedTab} onBack={()=>{setSelected(null);setSelectedTab('overview');}}/>;
  const openProfile=(uid,tab)=>{setSelectedTab(tab||'overview');setSelected(uid);};
  const canIntel=hasPermission('intelligence.view');
  return(
    <>
      <div style={s.filterRow}>
        <input style={{...s.input,width:200}} placeholder="Search email or name..." value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}/>
        <select style={s.select} value={tier} onChange={e=>{setTier(e.target.value);setPage(1);}}>
          <option value="">All tiers</option>{['trial','active','cancelled'].map(v=><option key={v} value={v}>{v}</option>)}
        </select>
        <button style={s.btn('primary')} onClick={()=>{setPage(1);load();}}>Search</button>
      </div>
      {err&&<div style={s.error}>! {err}</div>}
      {!data&&<Loader/>}
      {data&&(
        <TableCard title="Users" count={data.total}>
          <table style={s.table}>
            <thead><tr>{['User','Tier','Credits','Topup','Country','Joined','',''].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.users.map(u=>(
                <tr key={u.id} style={{cursor:'pointer'}} onClick={()=>openProfile(u.id,'overview')} onMouseEnter={e=>e.currentTarget.style.background=T.borderSoft} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={s.td}><div style={{fontWeight:600,fontSize:15,color:T.text}}>{u.email}</div><div style={{fontSize:14,color:T.textSub}}>{`${u.first_name||''} ${u.last_name||''}`.trim()||'-'}</div></td>
                  <td style={s.td}><SBadge status={u.tier}/></td>
                  <td style={s.tds}>{fmt(u.credits)}</td>
                  <td style={s.td}>{fmt(u.topup_credits)}</td>
                  <td style={s.td}>{u.country||'-'}</td>
                  <td style={{...s.td,fontSize:14}}>{fmtDate(u.created_at)}</td>
                  <td style={s.td}>
                    <div style={{display:'flex',gap:4,alignItems:'center'}}>
                      <EntityChip type="user" id={u.id} label={u.email.split('@')[0]}/>
                      <button style={{...s.btn(),fontSize:13,padding:'4px 9px'}} onClick={e=>{e.stopPropagation();openProfile(u.id,'overview');}}>View</button>
                      {canIntel&&<button style={{...s.btn(),fontSize:13,padding:'4px 9px',color:T.accent,borderColor:T.accentBorder}} onClick={e=>{e.stopPropagation();onIntel&&onIntel(u.id);}} title="Open User Intelligence">Intel</button>}
                    </div>
                  </td>
                  <td style={s.td}><button style={{...s.btn('primary'),fontSize:13,padding:'4px 9px',background:'#553c9a'}} onClick={e=>{e.stopPropagation();onAction&&onAction(u.id);}}>Action</button></td>
                </tr>
              ))}
              {data.users.length===0&&<tr><td colSpan={8} style={s.empty}>No users found</td></tr>}
            </tbody>
          </table>
          <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage}/>
        </TableCard>
      )}
    </>
  );
}

// -- JOBS ----------------------------------------------------------------------
function SecJobs({api}){
  const[data,setData]=useState(null);const[page,setPage]=useState(1);
  const[status,setStatus]=useState('');const[days,setDays]=useState(7);
  const[selected,setSelected]=useState(null);const[jobDetail,setJobDetail]=useState(null);
  const[err,setErr]=useState(null);
  useEffect(()=>{if(!selected)return;setJobDetail(null);fetch(`${api}/v1/admin/jobs/${selected}`,{credentials:'include'}).then(r=>r.json()).then(setJobDetail).catch(()=>{});},[api,selected]);
  const load=useCallback(()=>{
    setData(null);const p=new URLSearchParams({page,per_page:50});
    if(status)p.set('status',status);if(days)p.set('days',days);
    fetch(`${api}/v1/admin/jobs?${p}`,{credentials:'include'}).then(r=>r.json()).then(setData).catch(()=>setErr('Failed'));
  },[api,page,status,days]);
  useEffect(()=>{load();},[load]);
  return(
    <>
      {selected&&jobDetail&&(
        <TableCard title="Job Detail" action={<button style={s.btn()} onClick={()=>{setSelected(null);setJobDetail(null);}}>× Close</button>}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',padding:'4px 0'}}>
            {[['Job ID',jobDetail.job_id],['User',jobDetail.user_id],['Status',jobDetail.status],['Generator',jobDetail.generator_slug],['Domain',jobDetail.domain],['Images',jobDetail.image_count],['Provider',jobDetail.provider],['Model',jobDetail.model],['Attempts',jobDetail.attempts],['Error Code',jobDetail.error_code],['Latency (ms)',jobDetail.provider_latency_ms],['Created',fmtDate(jobDetail.created_at)],['Started',fmtDate(jobDetail.started_at)],['Completed',fmtDate(jobDetail.completed_at)]].map(([k,v])=>(
              <div key={k} style={{...s.kvRow,padding:'8px 16px'}}><span style={s.kvLabel}>{k}</span><span style={s.kvValue}>{v||'-'}</span></div>
            ))}
          </div>
        </TableCard>
      )}
      <div style={s.filterRow}>
        <select style={s.select} value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
          <option value="">All statuses</option>{['queued','processing','completed','failed'].map(v=><option key={v} value={v}>{v}</option>)}
        </select>
        <select style={s.select} value={days} onChange={e=>{setDays(Number(e.target.value));setPage(1);}}>
          {[1,3,7,14,30].map(d=><option key={d} value={d}>Last {d} days</option>)}
        </select>
        <button style={s.btn('primary')} onClick={()=>{setPage(1);load();}}>Apply</button>
      </div>
      {err&&<div style={s.error}>! {err}</div>}
      {!data&&<Loader/>}
      {data&&(
        <TableCard title="Jobs" count={data.total}>
          <table style={s.table}>
            <thead><tr>{['Created','User','Generator','Domain','Images','Status','Latency',''].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.jobs.map(j=>(
                <tr key={j.job_id}>
                  <td style={{...s.td,whiteSpace:'nowrap'}}><div style={{fontSize:14}}>{fmtAgo(j.created_at)}</div><div style={{fontSize:13,color:T.textFaint}}>{fmtDate(j.created_at)}</div></td>
                  <td style={s.td}><code style={{fontFamily:mono,fontSize:11,color:T.textSub}}>{j.user_id?.slice(0,12)}...</code></td>
                  <td style={s.td}><span style={{fontSize:14}}>{j.generator_slug}</span></td>
                  <td style={s.td}><span style={s.badge('blue')}>{j.domain}</span></td>
                  <td style={s.tds}>{j.image_count}</td>
                  <td style={s.td}><SBadge status={j.status}/></td>
                  <td style={s.td}>{j.provider_latency_ms?`${j.provider_latency_ms}ms`:'-'}</td>
                  <td style={s.td}><button style={{...s.btn(),fontSize:14,padding:'4px 9px'}} onClick={()=>setSelected(j.job_id)}>View</button></td>
                </tr>
              ))}
              {data.jobs.length===0&&<tr><td colSpan={8} style={s.empty}>No jobs in this period</td></tr>}
            </tbody>
          </table>
          <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage}/>
        </TableCard>
      )}
    </>
  );
}

// -- TICKETS -------------------------------------------------------------------

// -- ACTIONS -------------------------------------------------------------------
const CREDIT_ACTIONS=new Set(['GRANT_CREDITS','COMPENSATION','LOYALTY_BONUS','COURTESY_CREDIT']);
const ACTION_COLORS={
  GRANT_CREDITS:'green',COMPENSATION:'green',LOYALTY_BONUS:'green',COURTESY_CREDIT:'green',
  REMOVE_RESTRICTION:'blue',RESTORE_ACCOUNT:'blue',CLEAR_WARNING:'blue',
  PAUSE:'amber',RESTRICT:'amber',REQUIRE_CONTACT:'amber',WARN:'amber',FLAG:'amber',
  SOFT_BAN:'red',FULL_BAN:'red',RESET_CREDITS:'red',
  ACTIVATE_TECHNICIAN:'green',
};

function SecActions({api,prefillUserId,prefillTechnician}){
  const[meta,setMeta]=useState(null);
  const[userId,setUserId]=useState(prefillUserId||'');
  const[action,setAction]=useState('');
  const[reason,setReason]=useState('');
  const[credits,setCredits]=useState('');
  const[note,setNote]=useState('');
  const[techType,setTechType]=useState('');
  const[techBusiness,setTechBusiness]=useState('');
  const[techTrading,setTechTrading]=useState('');
  const[techCountry,setTechCountry]=useState('');
  const[techVerification,setTechVerification]=useState('unverified');
  const[submitting,setSubmitting]=useState(false);
  const[result,setResult]=useState(null);
  const[resultErr,setResultErr]=useState(null);
  const[log,setLog]=useState(null);
  const[logAction,setLogAction]=useState('');
  const[logPage,setLogPage]=useState(1);
  const[logErr,setLogErr]=useState(null);

  useEffect(()=>{fetch(`${api}/v1/admin/actions/meta`,{credentials:'include'}).then(r=>r.json()).then(setMeta).catch(()=>{});},[api]);
  useEffect(()=>{if(prefillUserId)setUserId(prefillUserId);},[prefillUserId]);
  // One-shot activation prefill from a technician submission. Keyed on the
  // submission id via a ref so it applies exactly once per submission and
  // never re-applies (or overwrites staff edits) when other state changes.
  const _appliedTechRef=useRef(null);
  useEffect(()=>{
    if(!prefillTechnician||!prefillTechnician.id)return;
    if(_appliedTechRef.current===prefillTechnician.id)return;
    _appliedTechRef.current=prefillTechnician.id;
    setAction('ACTIVATE_TECHNICIAN');
    if(prefillTechnician.user_id)setUserId(prefillTechnician.user_id);
    setTechType(prefillTechnician.technician_type||'');
    setTechBusiness(prefillTechnician.business_name||'');
    setTechTrading(prefillTechnician.trading_name||'');
    setTechCountry(prefillTechnician.country||'');
    if(prefillTechnician.verification_status)setTechVerification(prefillTechnician.verification_status);
    if(prefillTechnician.note)setNote(prefillTechnician.note);
  },[prefillTechnician]);

  const loadLog=useCallback(()=>{
    setLog(null);
    const p=new URLSearchParams({page:logPage,per_page:25});
    if(logAction)p.set('action',logAction);
    fetch(`${api}/v1/admin/audit-log?${p}`,{credentials:'include'}).then(r=>r.json()).then(setLog).catch(()=>setLogErr('Failed to load audit log'));
  },[api,logPage,logAction]);

  useEffect(()=>{loadLog();},[loadLog]);

  const handleSubmit=()=>{
    if(!userId.trim()||!action||!reason){setResultErr('User ID, action, and reason are required.');return;}
    if(CREDIT_ACTIONS.has(action)&&!credits){setResultErr('Credit amount is required for this action.');return;}
    setSubmitting(true);setResult(null);setResultErr(null);
    const body={action,reason,private_note:note.trim()||undefined};
    if(CREDIT_ACTIONS.has(action))body.value=Number(credits);
    if(action==='ACTIVATE_TECHNICIAN'){
      const tech={};
      if(techType.trim())tech.technician_type=techType.trim();
      if(techBusiness.trim())tech.business_name=techBusiness.trim();
      if(techTrading.trim())tech.trading_name=techTrading.trim();
      if(techCountry.trim())tech.country=techCountry.trim();
      if(techVerification)tech.verification_status=techVerification;
      // Only present when this activation was driven by a public submission;
      // the backend then atomically marks that submission activated. Absent
      // for normal manual activation, leaving existing behaviour unchanged.
      if(prefillTechnician&&prefillTechnician.id&&_appliedTechRef.current===prefillTechnician.id){
        tech.technician_submission_id=prefillTechnician.id;
      }
      body.technician=tech;
    }
    fetch(`${api}/v1/admin/users/${userId.trim()}/action`,{
      method:'POST',credentials:'include',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify(body),
    }).then(async r=>{
      const d=await r.json();
      if(!r.ok)setResultErr(d.error||(d.detail?`${d.error}: ${d.detail}`:'Action failed'));
      else{setResult(d);setUserId('');setAction('');setReason('');setCredits('');setNote('');setTechType('');setTechBusiness('');setTechTrading('');setTechCountry('');setTechVerification('unverified');_appliedTechRef.current=null;loadLog();}
    }).catch(()=>setResultErr('Network error')).finally(()=>setSubmitting(false));
  };

  const allActions=meta?[...meta.valid_actions].sort():[];
  const allReasons=meta?[...meta.preset_reasons].sort():[];
  const creditPresets=meta?[...meta.credit_grant_presets].sort((a,b)=>a-b):[];
  const needsCredits=CREDIT_ACTIONS.has(action);

  return(
    <>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
        <div style={{...s.panel}}>
          <div style={{...s.panelTitle,marginBottom:4}}>Apply Action to User</div>
          <div style={{...s.panelSub}}>Grant credits, apply restrictions, or modify account status</div>
          {result&&(<div style={{padding:'10px 14px',background:T.greenBg,border:`1px solid ${T.greenBorder}`,borderRadius:7,color:T.green,fontSize:14,fontWeight:600,marginBottom:12}}>v Action applied - {result.action||action} on {result.user_id||userId}</div>)}
          {resultErr&&(<div style={{...s.error,marginBottom:12}}>! {resultErr}</div>)}
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <div><div style={{fontSize:14,fontWeight:600,color:T.textSub,marginBottom:4}}>User ID *</div><input style={{...s.input,width:'100%',boxSizing:'border-box'}} placeholder="User UUID" value={userId} onChange={e=>setUserId(e.target.value)}/></div>
            <div><div style={{fontSize:14,fontWeight:600,color:T.textSub,marginBottom:4}}>Action *</div>
              <select style={{...s.select,width:'100%',boxSizing:'border-box'}} value={action} onChange={e=>{setAction(e.target.value);setCredits('');}}>
                <option value="">Select action...</option>
                <optgroup label="Positive">{['GRANT_CREDITS','COMPENSATION','LOYALTY_BONUS','COURTESY_CREDIT','REMOVE_RESTRICTION','RESTORE_ACCOUNT','CLEAR_WARNING'].map(a=>(<option key={a} value={a}>{a}</option>))}</optgroup>
                <optgroup label="Enforcement">{['WARN','FLAG','PAUSE','RESTRICT','REQUIRE_CONTACT','SOFT_BAN','FULL_BAN'].map(a=>(<option key={a} value={a}>{a}</option>))}</optgroup>
                <optgroup label="Account">{['RESET_CREDITS'].map(a=>(<option key={a} value={a}>{a}</option>))}</optgroup>
                <optgroup label="Technician">{['ACTIVATE_TECHNICIAN'].map(a=>(<option key={a} value={a}>{a}</option>))}</optgroup>
              </select>
            </div>
            {action&&(<div style={{padding:'8px 11px',borderRadius:6,background:ACTION_COLORS[action]==='green'?T.greenBg:ACTION_COLORS[action]==='red'?T.redBg:ACTION_COLORS[action]==='blue'?T.accentLight:T.amberBg,border:`1px solid ${ACTION_COLORS[action]==='green'?T.greenBorder:ACTION_COLORS[action]==='red'?T.redBorder:ACTION_COLORS[action]==='blue'?T.accentBorder:T.amberBorder}`,fontSize:14,color:ACTION_COLORS[action]==='green'?T.green:ACTION_COLORS[action]==='red'?T.red:ACTION_COLORS[action]==='blue'?T.accent:T.amber,fontWeight:600}}>
              {ACTION_COLORS[action]==='green'?'v Positive action':ACTION_COLORS[action]==='red'?'! Destructive action':ACTION_COLORS[action]==='blue'?'Reversal action':'! Enforcement action'}
            </div>)}
            {needsCredits&&(<div><div style={{fontSize:14,fontWeight:600,color:T.textSub,marginBottom:4}}>Credit Amount *</div><div style={{display:'flex',gap:6,flexWrap:'wrap'}}>{creditPresets.map(n=>(<button key={n} style={{...s.btn(credits===String(n)?'primary':''),padding:'7px 14px',fontSize:14}} onClick={()=>setCredits(String(n))}>{n}</button>))}</div></div>)}
            {action==='ACTIVATE_TECHNICIAN'&&(
              <div style={{display:'flex',flexDirection:'column',gap:10,padding:'12px',borderRadius:8,background:T.accentLight,border:`1px solid ${T.accentBorder}`}}>
                <div style={{fontSize:13,fontWeight:700,color:T.accent,textTransform:'uppercase',letterSpacing:'0.06em'}}>Technician Activation</div>
                <div style={{fontSize:13,color:T.textSub}}>Creates or activates the technician account linked to this user. Identity is taken from the existing user; the fields below are optional technician profile details. Default technician capabilities are provisioned automatically on activation.</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  <div><div style={{fontSize:13,fontWeight:600,color:T.textSub,marginBottom:4}}>Technician Type</div><input style={{...s.input,width:'100%',boxSizing:'border-box'}} placeholder="e.g. PC repair, MSP, field tech" value={techType} onChange={e=>setTechType(e.target.value)}/></div>
                  <div><div style={{fontSize:13,fontWeight:600,color:T.textSub,marginBottom:4}}>Country</div><input style={{...s.input,width:'100%',boxSizing:'border-box'}} placeholder="Country" value={techCountry} onChange={e=>setTechCountry(e.target.value)}/></div>
                  <div><div style={{fontSize:13,fontWeight:600,color:T.textSub,marginBottom:4}}>Business Name</div><input style={{...s.input,width:'100%',boxSizing:'border-box'}} placeholder="Business name" value={techBusiness} onChange={e=>setTechBusiness(e.target.value)}/></div>
                  <div><div style={{fontSize:13,fontWeight:600,color:T.textSub,marginBottom:4}}>Trading Name</div><input style={{...s.input,width:'100%',boxSizing:'border-box'}} placeholder="Trading as" value={techTrading} onChange={e=>setTechTrading(e.target.value)}/></div>
                </div>
                <div><div style={{fontSize:13,fontWeight:600,color:T.textSub,marginBottom:4}}>Verification Status</div><select style={{...s.select,width:'100%',boxSizing:'border-box'}} value={techVerification} onChange={e=>setTechVerification(e.target.value)}>{['unverified','in_review','verified','failed'].map(v=><option key={v} value={v}>{v.replace(/_/g,' ')}</option>)}</select></div>
              </div>
            )}
            <div><div style={{fontSize:14,fontWeight:600,color:T.textSub,marginBottom:4}}>Reason *</div><select style={{...s.select,width:'100%',boxSizing:'border-box'}} value={reason} onChange={e=>setReason(e.target.value)}><option value="">Select reason...</option>{allReasons.map(r=><option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}</select></div>
            <div><div style={{fontSize:14,fontWeight:600,color:T.textSub,marginBottom:4}}>Note (optional)</div><textarea style={{...s.input,width:'100%',boxSizing:'border-box',minHeight:56,resize:'vertical',fontFamily:font}} placeholder="Internal note..." value={note} onChange={e=>setNote(e.target.value)}/></div>
            <button style={{...s.btn('primary'),justifyContent:'center',padding:'8px 0'}} onClick={handleSubmit} disabled={submitting||!userId.trim()||!action||!reason||(needsCredits&&!credits)}>{submitting?'Applying...':'Apply Action'}</button>
          </div>
        </div>
        <div style={{...s.panel}}>
          <div style={{...s.panelTitle,marginBottom:12}}>Action Reference</div>
          {[{group:'Positive',color:'green',items:[{a:'GRANT_CREDITS',d:'Add credits to balance'},{a:'COMPENSATION',d:'Credit grant for service issues'},{a:'LOYALTY_BONUS',d:'Reward for long-term users'},{a:'COURTESY_CREDIT',d:'Goodwill credit grant'},{a:'REMOVE_RESTRICTION',d:'Lift an active restriction'},{a:'RESTORE_ACCOUNT',d:'Restore banned account'},{a:'CLEAR_WARNING',d:'Remove warning flag'}]},{group:'Enforcement',color:'amber',items:[{a:'WARN',d:'Issue a warning'},{a:'FLAG',d:'Flag for review'},{a:'PAUSE',d:'Temporarily pause account'},{a:'RESTRICT',d:'Restrict generation access'},{a:'REQUIRE_CONTACT',d:'Require support contact'},{a:'SOFT_BAN',d:'Prevent login, keep data'},{a:'FULL_BAN',d:'Full account ban'}]},{group:'Account',color:'red',items:[{a:'RESET_CREDITS',d:'Zero out credit balance'}]}].map(g=>(
            <div key={g.group} style={{marginBottom:10}}>
              <div style={{fontSize:13,fontWeight:700,color:g.color==='green'?T.green:g.color==='red'?T.red:T.amber,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:4}}>{g.group}</div>
              {g.items.map(({a,d})=>(<div key={a} style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',padding:'3px 0',borderBottom:`1px solid ${T.borderSoft}`,gap:8}}><code style={{fontFamily:mono,fontSize:13,color:T.text,whiteSpace:'nowrap'}}>{a}</code><span style={{fontSize:14,color:T.textSub,textAlign:'right'}}>{d}</span></div>))}
            </div>
          ))}
        </div>
      </div>
      <div style={s.filterRow}>
        <select style={s.select} value={logAction} onChange={e=>{setLogAction(e.target.value);setLogPage(1);}}><option value="">All actions</option>{allActions.map(a=><option key={a} value={a}>{a}</option>)}</select>
        <button style={s.btn('primary')} onClick={()=>{setLogPage(1);loadLog();}}>Refresh</button>
      </div>
      {logErr&&<div style={s.error}>! {logErr}</div>}
      {!log&&<Loader/>}
      {log&&(
        <TableCard title="Action Audit Log" count={log.total}>
          <table style={s.table}>
            <thead><tr>{['Date','Admin','User','Action','Reason','Value','Note'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {(log.logs||[]).map((entry,i)=>(
                <tr key={i}>
                  <td style={{...s.td,fontSize:14,whiteSpace:'nowrap'}}>{fmtDate(entry.created_at)}</td>
                  <td style={s.td}><code style={{fontFamily:mono,fontSize:11,color:T.textSub}}>{entry.admin_user_id?.slice(0,12)}...</code></td>
                  <td style={s.td}><code style={{fontFamily:mono,fontSize:11,color:T.textSub}}>{entry.target_user_id?.slice(0,12)}...</code></td>
                  <td style={s.td}><span style={s.badge(ACTION_COLORS[entry.action]||'grey')}>{entry.action}</span></td>
                  <td style={s.td}><span style={{fontSize:14,color:T.textSub}}>{entry.reason?.replace(/_/g,' ')||'-'}</span></td>
                  <td style={s.tds}>{entry.value!=null?entry.value:'-'}</td>
                  <td style={{...s.td,fontSize:14,color:T.textSub,maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{entry.note||'-'}</td>
                </tr>
              ))}
              {(log.logs||[]).length===0&&<tr><td colSpan={7} style={s.empty}>No actions recorded yet</td></tr>}
            </tbody>
          </table>
          <Pager page={log.page} pages={log.pages} total={log.total} onPage={setLogPage}/>
        </TableCard>
      )}
    </>
  );
}

// -- REWARDS -------------------------------------------------------------------
const REWARD_PRESETS=[5,10,20,25,50,100];

function SecRewards({api}){
  const[tab,setTab]=useState('submissions');
  const[subs,setSubs]=useState(null);const[subStatus,setSubStatus]=useState('PENDING');
  const[subPage,setSubPage]=useState(1);const[subErr,setSubErr]=useState(null);
  const[reviewing,setReviewing]=useState(null);
  const[reviewCredits,setReviewCredits]=useState('');const[reviewLoading,setReviewLoading]=useState(false);
  const[reviewErr,setReviewErr]=useState(null);
  const[refs,setRefs]=useState(null);const[refStatus,setRefStatus]=useState('QUALIFIED');
  const[refPage,setRefPage]=useState(1);const[refErr,setRefErr]=useState(null);
  const[granting,setGranting]=useState(null);
  const[grantCredits,setGrantCredits]=useState('');const[grantLoading,setGrantLoading]=useState(false);
  const[grantErr,setGrantErr]=useState(null);

  const loadSubs=useCallback(()=>{setSubs(null);const p=new URLSearchParams({page:subPage,per_page:50});if(subStatus)p.set('status',subStatus);fetch(`${api}/v1/admin/rewards/submissions?${p}`,{credentials:'include'}).then(r=>r.json()).then(setSubs).catch(()=>setSubErr('Failed to load submissions'));},[api,subPage,subStatus]);
  const loadRefs=useCallback(()=>{setRefs(null);const p=new URLSearchParams({page:refPage,per_page:50});if(refStatus)p.set('status',refStatus);fetch(`${api}/v1/admin/rewards/referrals?${p}`,{credentials:'include'}).then(r=>r.json()).then(setRefs).catch(()=>setRefErr('Failed to load referrals'));},[api,refPage,refStatus]);

  useEffect(()=>{if(tab==='submissions')loadSubs();},[loadSubs,tab]);
  useEffect(()=>{if(tab==='referrals')loadRefs();},[loadRefs,tab]);

  const handleReview=(status)=>{
    if(!reviewing)return;
    if(status==='APPROVED'&&!reviewCredits){setReviewErr('Select a credit amount to approve.');return;}
    setReviewLoading(true);setReviewErr(null);
    const body={status};
    if(status==='APPROVED'){body.reward_type='credit_grant';body.reward_value=Number(reviewCredits);}
    fetch(`${api}/v1/admin/rewards/submissions/${reviewing.id}/review`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(async r=>{const d=await r.json();if(!r.ok)setReviewErr(d.error||'Review failed');else{setReviewing(null);setReviewCredits('');loadSubs();}}).catch(()=>setReviewErr('Network error')).finally(()=>setReviewLoading(false));
  };

  const handleGrant=()=>{
    if(!granting||!grantCredits){setGrantErr('Select a credit amount.');return;}
    setGrantLoading(true);setGrantErr(null);
    fetch(`${api}/v1/admin/rewards/referrals/${granting.id}/grant`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({credits:Number(grantCredits)})}).then(async r=>{const d=await r.json();if(!r.ok)setGrantErr(d.error||'Grant failed');else{setGranting(null);setGrantCredits('');loadRefs();}}).catch(()=>setGrantErr('Network error')).finally(()=>setGrantLoading(false));
  };

  const tabStyle=(active)=>({padding:'9px 18px',fontSize:14,fontWeight:active?700:500,cursor:'pointer',color:active?T.accent:T.textSub,background:'none',border:'none',borderBottom:`2px solid ${active?T.accent:'transparent'}`,fontFamily:font});
  const statusColor2=(st)=>st==='APPROVED'?'green':st==='REJECTED'?'red':'amber';

  return(
    <>
      <div style={{display:'flex',gap:0,borderBottom:`1px solid ${T.border}`,marginBottom:12}}>
        <button style={tabStyle(tab==='submissions')} onClick={()=>setTab('submissions')}>Social Submissions</button>
        <button style={tabStyle(tab==='referrals')} onClick={()=>setTab('referrals')}>Referral Events</button>
      </div>
      {tab==='submissions'&&(
        <>
          {reviewing&&(
            <TableCard title={`Review Submission #${reviewing.id}`} action={<button style={s.btn()} onClick={()=>{setReviewing(null);setReviewErr(null);setReviewCredits('');}}>× Close</button>}>
              <div style={{padding:'14px 16px'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 24px',marginBottom:12}}>
                  {[['User',reviewing.user_email],['Platform',reviewing.platform],['Status',reviewing.status],['Submitted',fmtDate(reviewing.created_at)]].map(([k,v])=>(<div key={k} style={s.kvRow}><span style={s.kvLabel}>{k}</span><span style={s.kvValue}>{v||'-'}</span></div>))}
                </div>
                <div style={{marginBottom:12}}><div style={{fontSize:14,fontWeight:600,color:T.textSub,marginBottom:4}}>Post URL</div><a href={reviewing.url} target="_blank" rel="noopener noreferrer" style={{fontSize:14,color:T.accent,wordBreak:'break-all'}}>{reviewing.url}</a></div>
                {reviewErr&&<div style={{...s.error,marginBottom:10}}>! {reviewErr}</div>}
                <div style={{marginBottom:12}}><div style={{fontSize:14,fontWeight:600,color:T.textSub,marginBottom:6}}>Credits to Grant</div><div style={{display:'flex',gap:6,flexWrap:'wrap'}}>{REWARD_PRESETS.map(n=>(<button key={n} style={{...s.btn(reviewCredits===String(n)?'primary':''),padding:'7px 14px',fontSize:14}} onClick={()=>setReviewCredits(String(n))}>{n}</button>))}</div></div>
                <div style={{display:'flex',gap:8}}>
                  <button style={{...s.btn('primary'),background:T.green}} onClick={()=>handleReview('APPROVED')} disabled={reviewLoading||!reviewCredits}>{reviewLoading?'Processing...':'v Approve & Grant'}</button>
                  <button style={{...s.btn(),color:T.red,borderColor:T.redBorder}} onClick={()=>handleReview('REJECTED')} disabled={reviewLoading}>{reviewLoading?'Processing...':'x Reject'}</button>
                </div>
              </div>
            </TableCard>
          )}
          <div style={s.filterRow}>
            <select style={s.select} value={subStatus} onChange={e=>{setSubStatus(e.target.value);setSubPage(1);}}><option value="">All statuses</option>{['PENDING','APPROVED','REJECTED'].map(v=><option key={v} value={v}>{v}</option>)}</select>
            <button style={s.btn('primary')} onClick={()=>{setSubPage(1);loadSubs();}}>Refresh</button>
          </div>
          {subErr&&<div style={s.error}>! {subErr}</div>}
          {!subs&&<Loader/>}
          {subs&&(
            <TableCard title="Social Submissions" count={subs.total}>
              <table style={s.table}>
                <thead><tr>{['Date','User','Platform','URL','Status','Reward',''].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {(subs.submissions||[]).map(sub=>(
                    <tr key={sub.id}>
                      <td style={{...s.td,fontSize:14,whiteSpace:'nowrap'}}>{fmtDate(sub.created_at)}</td>
                      <td style={s.td}><div style={{fontSize:14,fontWeight:600}}>{sub.user_email||'-'}</div><div style={{fontSize:13,color:T.textFaint}}>{sub.user_name||''}</div></td>
                      <td style={s.td}><span style={s.badge('blue')}>{sub.platform}</span></td>
                      <td style={{...s.td,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}><a href={sub.url} target="_blank" rel="noopener noreferrer" style={{color:T.accent,fontSize:14}}>{sub.url}</a></td>
                      <td style={s.td}><span style={s.badge(statusColor2(sub.status))}>{sub.status}</span></td>
                      <td style={s.td}>{sub.reward_value?<span style={s.badge('green')}>{sub.reward_value} cr</span>:'-'}</td>
                      <td style={s.td}>{sub.status==='PENDING'&&(<button style={{...s.btn(),fontSize:14,padding:'4px 9px'}} onClick={()=>{setReviewing(sub);setReviewErr(null);setReviewCredits('');}}>Review</button>)}</td>
                    </tr>
                  ))}
                  {(subs.submissions||[]).length===0&&<tr><td colSpan={7} style={s.empty}>No submissions found</td></tr>}
                </tbody>
              </table>
              <Pager page={subs.page} pages={subs.pages} total={subs.total} onPage={setSubPage}/>
            </TableCard>
          )}
        </>
      )}
      {tab==='referrals'&&(
        <>
          {granting&&(
            <TableCard title={`Grant Referral Reward - Event #${granting.id}`} action={<button style={s.btn()} onClick={()=>{setGranting(null);setGrantErr(null);setGrantCredits('');}}>× Close</button>}>
              <div style={{padding:'14px 16px'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 24px',marginBottom:12}}>
                  {[['Referrer',granting.referrer_email],['Referred',granting.referred_email],['Status',granting.status],['Qualifying Event',granting.qualifying_event||'-'],['Created',fmtDate(granting.created_at)]].map(([k,v])=>(<div key={k} style={s.kvRow}><span style={s.kvLabel}>{k}</span><span style={s.kvValue}>{v||'-'}</span></div>))}
                </div>
                {grantErr&&<div style={{...s.error,marginBottom:10}}>! {grantErr}</div>}
                <div style={{marginBottom:12}}><div style={{fontSize:14,fontWeight:600,color:T.textSub,marginBottom:6}}>Credits to Grant to Referrer</div><div style={{display:'flex',gap:6,flexWrap:'wrap'}}>{REWARD_PRESETS.map(n=>(<button key={n} style={{...s.btn(grantCredits===String(n)?'primary':''),padding:'7px 14px',fontSize:14}} onClick={()=>setGrantCredits(String(n))}>{n}</button>))}</div></div>
                <button style={{...s.btn('primary'),background:T.green}} onClick={handleGrant} disabled={grantLoading||!grantCredits}>{grantLoading?'Granting...':'v Grant Reward'}</button>
              </div>
            </TableCard>
          )}
          <div style={s.filterRow}>
            <select style={s.select} value={refStatus} onChange={e=>{setRefStatus(e.target.value);setRefPage(1);}}><option value="">All statuses</option>{['PENDING','QUALIFIED'].map(v=><option key={v} value={v}>{v}</option>)}</select>
            <button style={s.btn('primary')} onClick={()=>{setRefPage(1);loadRefs();}}>Refresh</button>
          </div>
          {refErr&&<div style={s.error}>! {refErr}</div>}
          {!refs&&<Loader/>}
          {refs&&(
            <TableCard title="Referral Events" count={refs.total}>
              <table style={s.table}>
                <thead><tr>{['Date','Referrer','Referred','Status','Qualifying Event','Reward',''].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {(refs.referrals||[]).map(ev=>(
                    <tr key={ev.id}>
                      <td style={{...s.td,fontSize:14,whiteSpace:'nowrap'}}>{fmtDate(ev.created_at)}</td>
                      <td style={s.td}><span style={{fontSize:14}}>{ev.referrer_email||'-'}</span></td>
                      <td style={s.td}><span style={{fontSize:14}}>{ev.referred_email||'-'}</span></td>
                      <td style={s.td}><span style={s.badge(ev.status==='QUALIFIED'?'green':'amber')}>{ev.status}</span></td>
                      <td style={{...s.td,fontSize:14,color:T.textSub}}>{ev.qualifying_event||'-'}</td>
                      <td style={s.td}>{ev.reward_granted?<span style={s.badge('green')}>v Granted</span>:'-'}</td>
                      <td style={s.td}>{ev.status==='QUALIFIED'&&!ev.reward_granted&&(<button style={{...s.btn(),fontSize:14,padding:'4px 9px'}} onClick={()=>{setGranting(ev);setGrantErr(null);setGrantCredits('');}}>Grant</button>)}</td>
                    </tr>
                  ))}
                  {(refs.referrals||[]).length===0&&<tr><td colSpan={7} style={s.empty}>No referral events found</td></tr>}
                </tbody>
              </table>
              <Pager page={refs.page} pages={refs.pages} total={refs.total} onPage={setRefPage}/>
            </TableCard>
          )}
        </>
      )}
    </>
  );
}


// -- STAFF ACCOUNTS -----------------------------------------------------------
// ============================================================================
// STAFF DETAIL PANEL — right-side slide-over
// ============================================================================
function StaffDetailPanel({ api, memberId, onClose }) {
  const [data,    setData]    = useState(null);
  const [audit,   setAudit]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState(null);
  const [tab,     setTab]     = useState('profile'); // profile | sessions | audit | permissions

  useEffect(() => {
    if (!memberId) return;
    setLoading(true); setErr(null); setData(null); setAudit(null);
    Promise.all([
      fetch(`${api}/v1/staff/members/${memberId}`, { credentials:'include' }).then(r => r.json()),
      fetch(`${api}/v1/staff/members/${memberId}/audit?per_page=30`, { credentials:'include' }).then(r => r.json()),
    ]).then(([member, auditData]) => {
      setData(member);
      setAudit(auditData);
    }).catch(() => setErr('Failed to load staff detail'))
    .finally(() => setLoading(false));
  }, [api, memberId]);

  const dm = {
    overlay: { position:'fixed', inset:0, zIndex:8500, background:'rgba(0,0,0,0.4)', display:'flex', justifyContent:'flex-end' },
    panel:   { width:480, maxWidth:'95vw', background:'#12161F', borderLeft:'1px solid rgba(255,255,255,0.09)', display:'flex', flexDirection:'column', height:'100vh', overflowY:'auto' },
    header:  { padding:'20px 24px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', alignItems:'center', gap:12 },
    title:   { fontFamily:"'Syne',sans-serif", fontSize:17, fontWeight:700, color:'#fff', flex:1 },
    tab:     (active) => ({ padding:'8px 16px', fontSize:13, fontWeight:active?700:500, cursor:'pointer', border:'none', background:'none', borderBottom:`2px solid ${active?'#2D6CFF':'transparent'}`, color:active?'#2D6CFF':'rgba(255,255,255,0.6)', transition:'all 0.12s', fontFamily:"'DM Sans',sans-serif" }),
    section: { padding:'16px 24px', borderBottom:'1px solid rgba(255,255,255,0.05)' },
    label:   { fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.6)', letterSpacing:'0.09em', textTransform:'uppercase', marginBottom:8 },
    kv:      { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:13 },
    kvLabel: { color:'rgba(255,255,255,0.65)' },
    kvVal:   { color:'#fff', fontWeight:600, textAlign:'right', maxWidth:260, wordBreak:'break-all' },
  };

  const statusColor = (st) => ({ ACTIVE:'#4ADE80', SUSPENDED:'#EF4444', DISABLED:'#6B7280', MFA_REQUIRED:'#F59E0B', INVITED:'#60A5FA' }[st] || '#9CA3AF');

  const TABS = [
    { id:'profile',     label:'Profile' },
    { id:'sessions',    label:'Sessions' },
    { id:'audit',       label:'Audit' },
    { id:'permissions', label:'Permissions' },
  ];

  return (
    <div style={dm.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={dm.panel}>
        {/* Header */}
        <div style={dm.header}>
          <div style={dm.title}>Staff Detail</div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.65)', cursor:'pointer', fontSize:22, lineHeight:1, padding:0 }}>×</button>
        </div>

        {/* Tab bar */}
        <div style={{ display:'flex', borderBottom:'1px solid rgba(255,255,255,0.07)', paddingLeft:8 }}>
          {TABS.map(t => <button key={t.id} style={dm.tab(tab===t.id)} onClick={() => setTab(t.id)}>{t.label}</button>)}
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {loading && <div style={{ padding:'40px 24px', textAlign:'center', color:'rgba(255,255,255,0.6)', fontSize:14 }}>Loading...</div>}
          {err && <div style={{ padding:'16px 24px', color:'#FCA5A5', fontSize:14 }}>{err}</div>}

          {/* PROFILE TAB */}
          {!loading && data && tab === 'profile' && (
            <>
              <div style={dm.section}>
                {/* Avatar + name block */}
                <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16 }}>
                  <div style={{ width:48, height:48, borderRadius:12, background:'rgba(45,108,255,0.15)', border:'1px solid rgba(45,108,255,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:700, color:'#2D6CFF', flexShrink:0 }}>
                    {(data.name||data.email||'?').split(' ').map(p=>p[0]).join('').slice(0,2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize:16, fontWeight:700, color:'#fff' }}>{data.name || <span style={{ color:'rgba(255,255,255,0.6)', fontStyle:'italic' }}>No name</span>}</div>
                    <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', marginTop:2 }}>{data.email}</div>
                  </div>
                </div>

                {/* Status + MFA pills */}
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                  <span style={{ padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700, background:statusColor(data.staff_status)+'20', color:statusColor(data.staff_status), border:`1px solid ${statusColor(data.staff_status)}40` }}>
                    {data.staff_status||'UNKNOWN'}
                  </span>
                  <span style={{ padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700, background:data.mfa_enabled?'rgba(74,222,128,0.12)':'rgba(239,68,68,0.12)', color:data.mfa_enabled?'#4ADE80':'#EF4444', border:`1px solid ${data.mfa_enabled?'rgba(74,222,128,0.25)':'rgba(239,68,68,0.25)'}` }}>
                    MFA {data.mfa_enabled?'Enabled':'Disabled'}
                  </span>
                </div>

                {/* Roles */}
                <div style={dm.label}>Roles</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:4 }}>
                  {(data.roles||[]).map(r => {
                    const rm = ROLE_META[r] || { color:T.textSub, label:r };
                    return <span key={r} style={{ padding:'4px 12px', borderRadius:6, fontSize:13, fontWeight:700, background:rm.color+'18', color:rm.color, border:`1px solid ${rm.color}30` }}>{rm.label||r}</span>;
                  })}
                  {(!data.roles||data.roles.length===0) && <span style={{ fontSize:13, color:'rgba(255,255,255,0.6)' }}>No roles assigned</span>}
                </div>
              </div>

              <div style={dm.section}>
                <div style={dm.label}>Account Info</div>
                {[
                  { label:'User ID',       val:<code style={{ fontFamily:'monospace', fontSize:11 }}>{data.user_id}</code> },
                  { label:'Last Login',    val:data.last_login ? fmtDate(data.last_login) : 'Never' },
                  { label:'Last IP',       val:data.last_ip || '-' },
                  { label:'Created',       val:data.created_at ? fmtDate(data.created_at) : '-' },
                  { label:'Active Sessions', val:<span style={{ fontWeight:700, color:(data.active_sessions||[]).length>0?'#60A5FA':'rgba(255,255,255,0.3)' }}>{(data.active_sessions||[]).length}</span> },
                  { label:'MFA Force Reset', val:data.mfa_force_reset?<span style={{ color:'#F59E0B' }}>Required</span>:'No' },
                ].map(({ label, val }) => (
                  <div key={label} style={dm.kv}>
                    <span style={dm.kvLabel}>{label}</span>
                    <span style={dm.kvVal}>{val}</span>
                  </div>
                ))}
              </div>

              {data.notes && (
                <div style={dm.section}>
                  <div style={dm.label}>Notes</div>
                  <div style={{ fontSize:13, color:'rgba(255,255,255,0.5)', lineHeight:1.6, background:'rgba(255,255,255,0.03)', borderRadius:6, padding:'10px 12px', border:'1px solid rgba(255,255,255,0.07)' }}>{data.notes}</div>
                </div>
              )}
            </>
          )}

          {/* SESSIONS TAB */}
          {!loading && data && tab === 'sessions' && (
            <div style={dm.section}>
              <div style={dm.label}>Active Sessions ({(data.active_sessions||[]).length})</div>
              {(data.active_sessions||[]).length === 0 ? (
                <div style={{ fontSize:13, color:'rgba(255,255,255,0.6)', padding:'12px 0' }}>No active sessions.</div>
              ) : (data.active_sessions||[]).map((sess, i) => {
                const rm = ROLE_META[sess.active_role] || { color:T.textSub, label:sess.active_role||'-' };
                return (
                  <div key={sess.id||i} style={{ padding:'10px 12px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:7, marginBottom:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
                      <span style={{ width:7, height:7, borderRadius:'50%', background:'#4ADE80', display:'inline-block', flexShrink:0 }}/>
                      <span style={{ fontSize:13, fontWeight:700, color:rm.color }}>{rm.label}</span>
                      <span style={{ fontSize:11, color:'rgba(255,255,255,0.6)', marginLeft:'auto' }}>Active</span>
                    </div>
                    {[
                      { label:'Browser',   val:`${sess.browser||'?'} / ${sess.device||'?'}` },
                      { label:'IP',        val:sess.ip_address||'-' },
                      { label:'Login',     val:fmtDate(sess.login_at) },
                      { label:'Last Seen', val:fmtAgo(sess.last_seen_at) },
                    ].map(({ label, val }) => (
                      <div key={label} style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'rgba(255,255,255,0.65)', marginTop:3 }}>
                        <span>{label}</span><span style={{ color:'rgba(255,255,255,0.6)' }}>{val}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* AUDIT TAB */}
          {!loading && audit && tab === 'audit' && (
            <div style={dm.section}>
              <div style={dm.label}>Recent Audit Activity</div>
              {(audit.logs||[]).length === 0 ? (
                <div style={{ fontSize:13, color:'rgba(255,255,255,0.6)', padding:'12px 0' }}>No audit entries.</div>
              ) : (audit.logs||[]).map(l => {
                const riskColor = { CRITICAL:'#EF4444', HIGH:'#F59E0B', MEDIUM:'#60A5FA', LOW:'rgba(255,255,255,0.3)' }[l.risk_level] || 'rgba(255,255,255,0.3)';
                return (
                  <div key={l.id} style={{ padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', display:'flex', flexDirection:'column', gap:2 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ width:5, height:5, borderRadius:'50%', background:riskColor, flexShrink:0, marginTop:1 }}/>
                      <span style={{ fontSize:12, fontFamily:'monospace', color:'rgba(255,255,255,0.7)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.action}</span>
                      <span style={{ fontSize:11, color:'rgba(255,255,255,0.5)', whiteSpace:'nowrap', flexShrink:0 }}>{fmtAgo(l.created_at)}</span>
                    </div>
                    {l.target_type && (
                      <div style={{ fontSize:11, color:'rgba(255,255,255,0.6)', paddingLeft:13 }}>
                        {l.target_type}{l.target_id ? `: ${l.target_id.slice(0,16)}` : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* PERMISSIONS TAB */}
          {!loading && data && tab === 'permissions' && (
            <div style={dm.section}>
              <div style={dm.label}>Role-Derived Permissions</div>
              {(data.roles||[]).length === 0 && (
                <div style={{ fontSize:13, color:'rgba(255,255,255,0.6)' }}>No roles assigned — no permissions active.</div>
              )}
              {(data.roles||[]).map(role => {
                const rm = ROLE_META[role] || { color:T.textSub, label:role };
                const perms = ((data.permissions_by_role&&data.permissions_by_role[role])||[]);
                return (
                  <div key={role} style={{ marginBottom:16 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:rm.color }}>{rm.label||role}</div>
                      <span style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.6)', background:'rgba(255,255,255,0.05)', borderRadius:10, padding:'1px 7px' }}>
                        {perms.length} permissions
                      </span>
                    </div>
                    {perms.length > 0 ? (
                      <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                        {perms.map(p => (
                          <span key={p} style={{ fontSize:11, padding:'2px 7px', borderRadius:4, background:'rgba(255,255,255,0.04)', color:'rgba(255,255,255,0.5)', border:'1px solid rgba(255,255,255,0.08)', fontFamily:'monospace' }}>{p}</span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ fontSize:12, color:'rgba(255,255,255,0.5)', fontStyle:'italic' }}>Permissions not available for this role</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// STAFF TABLE
// ============================================================================
function SecStaff({api}){
  const[staff,setStaff]=useState(null);const[err,setErr]=useState(null);
  const[search,setSearch]=useState('');const[page,setPage]=useState(1);
  const[statusFilter,setStatusFilter]=useState('');const[roleFilter,setRoleFilter]=useState('');
  const[actionMsg,setActionMsg]=useState('');const[actionErr,setActionErr]=useState('');
  const[showCreate,setShowCreate]=useState(false);
  const[showInvite,setShowInvite]=useState(false);
  const[confirmModal,setConfirmModal]=useState(null); // {title,body,action,risk}
  const[invites,setInvites]=useState(null);
  const[showInviteTab,setShowInviteTab]=useState(false);
  const[detailMember,setDetailMember]=useState(null);

  // Create form state
  const[cf,setCf]=useState({first_name:'',last_name:'',email:'',roles:[],notes:'',status:'MFA_REQUIRED'});
  const[creating,setCreating]=useState(false);const[createErr,setCreateErr]=useState('');
  const[createResult,setCreateResult]=useState(null);

  // Invite form state
  const[inv,setInv]=useState({email:'',first_name:'',last_name:'',roles:[],notes:''});
  const[inviting,setInviting]=useState(false);const[invErr,setInvErr]=useState('');
  const[invResult,setInvResult]=useState(null);

  const load=useCallback(()=>{
    const q=new URLSearchParams({page,per_page:50});
    if(search)q.set('search',search);
    if(statusFilter)q.set('status',statusFilter);
    if(roleFilter)q.set('role',roleFilter);
    fetch(`${api}/v1/staff/members?${q}`,{credentials:'include'})
      .then(r=>r.json()).then(d=>{setStaff(d);setErr(null);}).catch(()=>setErr('Failed to load staff'));
  },[api,page,search,statusFilter,roleFilter]);

  useEffect(()=>{load();},[load]);

  const doPost=(url,body,successMsg,errorMsg)=>{
    return fetch(`${api}${url}`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)})
      .then(r=>r.json().then(d=>({ok:r.ok,data:d})))
      .then(({ok,data})=>{
        if(ok){setActionMsg(successMsg);setActionErr('');load();}
        else{setActionErr(data.error||errorMsg);}
      }).catch(()=>setActionErr(errorMsg));
  };

  const openConfirm=(title,body,action,risk='high')=>{
    setConfirmModal({title,body,action,risk});
  };

  const handleConfirm=async()=>{
    if(!confirmModal)return;
    const reasonEl=document.getElementById('confirm-reason');
    const reason=reasonEl?reasonEl.value.trim():'';
    if(!reason){reasonEl&&(reasonEl.style.borderColor='#ef4444');return;}
    await confirmModal.action(reason);
    setConfirmModal(null);
  };

  const handleSuspend=(m)=>openConfirm(
    `Suspend ${m.name||m.email}`,
    `This will immediately revoke all active sessions for ${m.name||m.email} and prevent login until restored.`,
    (reason)=>doPost(`/v1/staff/members/${m.user_id}/suspend`,{reason},'Staff suspended.',`Failed to suspend ${m.email}.`),
    'critical'
  );

  const handleDisable=(m)=>openConfirm(
    `Disable ${m.name||m.email}`,
    `This will permanently disable the account. The staff member will not be able to log in. This action can be reversed by an OWNER.`,
    (reason)=>doPost(`/v1/staff/members/${m.user_id}/disable`,{reason},'Account disabled.',`Failed to disable ${m.email}.`),
    'critical'
  );

  const handleMFAReset=(m)=>openConfirm(
    `Reset MFA for ${m.name||m.email}`,
    `This will clear the current MFA setup and force the staff member to re-enroll on next login. All active sessions will be revoked.`,
    (reason)=>doPost(`/v1/staff/members/${m.user_id}/mfa-reset`,{reason},'MFA reset. Staff must re-enroll on next login.',`Failed to reset MFA.`),
    'high'
  );

  const handleForceLogout=(m)=>openConfirm(
    `Force Logout ${m.name||m.email}`,
    `This will immediately revoke all ${m.active_sessions} active session(s) for this staff member.`,
    (reason)=>doPost(`/v1/staff/members/${m.user_id}/force-logout`,{reason},'All sessions revoked.',`Failed to revoke sessions.`),
    'high'
  );

  const handleRestore=(m)=>{
    doPost(`/v1/staff/members/${m.user_id}`,{status:'ACTIVE'},'Account restored.',`Failed to restore.`);
  };

  const loadInvites=useCallback(()=>{
    fetch(`${api}/v1/staff/invites`,{credentials:'include'})
      .then(r=>r.json()).then(d=>setInvites(d.invites||[])).catch(()=>setInvites([]));
  },[api]);

  useEffect(()=>{if(showInviteTab)loadInvites();},[showInviteTab,loadInvites]);

  const toggleInvRole=(role)=>{
    setInv(prev=>({...prev,roles:prev.roles.includes(role)?prev.roles.filter(r=>r!==role):[...prev.roles,role]}));
  };

  const handleInvite=async(e)=>{
    e.preventDefault();
    if(!inv.email){setInvErr('Email is required.');return;}
    if(!inv.roles.length){setInvErr('At least one role is required.');return;}
    setInviting(true);setInvErr('');
    try{
      const r=await fetch(`${api}/v1/staff/invites`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify(inv)});
      const d=await r.json();
      if(r.ok){
        setInvResult(d);
        setInv({email:'',first_name:'',last_name:'',roles:[],notes:''});
        loadInvites();
      } else {
        setInvErr(d.error||'Failed to send invitation.');
      }
    }catch{setInvErr('Network error.');}
    finally{setInviting(false);}
  };

  const handleCreate=async(e)=>{
    e.preventDefault();
    if(!cf.email){setCreateErr('Email is required.');return;}
    if(!cf.roles.length){setCreateErr('At least one role is required.');return;}
    setCreating(true);setCreateErr('');
    try{
      const r=await fetch(`${api}/v1/staff/members`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify(cf)});
      const d=await r.json();
      if(r.ok){
        setCreateResult(d);
        setCf({first_name:'',last_name:'',email:'',roles:[],notes:'',status:'MFA_REQUIRED'});
        load();
      } else {
        setCreateErr(d.error||'Failed to create staff account.');
      }
    }catch{setCreateErr('Network error.');}
    finally{setCreating(false);}
  };

  const toggleRole=(role)=>{
    setCf(prev=>({...prev,roles:prev.roles.includes(role)?prev.roles.filter(r=>r!==role):[...prev.roles,role]}));
  };

  const ALL_ROLES=['OWNER','CO_OWNER','OPERATIONS_ADMIN','SUPPORT','MODERATION','CONTENT_MANAGER','GENERATOR_QA','FINANCE','ANALYTICS_VIEWER','COMMUNITY_MANAGER','DELIVERABILITY_ADMIN','TECH_OPERATOR','SECURITY_AUDITOR','DEVELOPER_LIMITED','READ_ONLY_ADMIN'];

  const statusBadge=(status)=>{
    const map={ACTIVE:'green',MFA_REQUIRED:'amber',INVITED:'blue',SUSPENDED:'red',DISABLED:'grey',PENDING:'amber',LOCKED:'red'};
    return s.badge(map[status]||'grey');
  };

  // Dark modal styles
  const modal={
    overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9000,display:'flex',alignItems:'center',justifyContent:'center',padding:20},
    box:{background:'#12161F',border:'1px solid rgba(255,255,255,0.1)',borderRadius:16,padding:32,width:'100%',maxWidth:560,maxHeight:'90vh',overflowY:'auto'},
    title:{fontFamily:"'Syne',sans-serif",fontSize:20,fontWeight:700,color:'#fff',marginBottom:10},
    body:{fontSize:15,color:'rgba(255,255,255,0.5)',lineHeight:1.65,marginBottom:24},
    label:{display:'block',fontSize:13,fontWeight:600,color:'rgba(255,255,255,0.65)',letterSpacing:'0.07em',textTransform:'uppercase',marginBottom:7,marginTop:16},
    input:{width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,padding:'12px 16px',fontSize:15,color:'#fff',fontFamily:"'DM Sans',sans-serif",outline:'none'},
    textarea:{width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,padding:'12px 16px',fontSize:15,color:'#fff',fontFamily:"'DM Sans',sans-serif",outline:'none',resize:'vertical',minHeight:80},
    btnRow:{display:'flex',gap:10,marginTop:24,justifyContent:'flex-end'},
    btnPrimary:{padding:'11px 22px',background:'linear-gradient(135deg,#2D6CFF,#1D4ED8)',border:'none',borderRadius:8,fontSize:14,fontWeight:600,color:'#FFFFFF',cursor:'pointer',fontFamily:"'Syne',sans-serif"},
    btnDanger:{padding:'11px 22px',background:'rgba(239,68,68,0.15)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:8,fontSize:14,fontWeight:600,color:'#FCA5A5',cursor:'pointer'},
    btnGhost:{padding:'11px 22px',background:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,fontSize:14,color:'rgba(255,255,255,0.5)',cursor:'pointer'},
    err:{fontSize:14,color:'#FCA5A5',marginTop:8,padding:'10px 14px',background:'rgba(239,68,68,0.1)',borderRadius:6,border:'1px solid rgba(239,68,68,0.2)'},
    success:{fontSize:14,color:'#4ADE80',marginTop:8,padding:'10px 14px',background:'rgba(74,222,128,0.08)',borderRadius:6,border:'1px solid rgba(74,222,128,0.2)'},
  };

  return(
    <>
      {detailMember&&<StaffDetailPanel api={api} memberId={detailMember} onClose={()=>setDetailMember(null)}/>}
      {/* Confirm Modal */}
      {confirmModal&&(
        <div style={modal.overlay} onClick={e=>{if(e.target===e.currentTarget)setConfirmModal(null);}}>
          <div style={{...modal.box,maxWidth:460}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
              <div style={{width:32,height:32,borderRadius:8,background:confirmModal.risk==='critical'?'rgba(239,68,68,0.15)':'rgba(245,158,11,0.15)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={confirmModal.risk==='critical'?'#EF4444':'#F59E0B'} strokeWidth="2"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
              </div>
              <div style={modal.title}>{confirmModal.title}</div>
            </div>
            <div style={modal.body}>{confirmModal.body}</div>
            <div style={modal.label}>Reason <span style={{color:'#EF4444'}}>*</span></div>
            <textarea id="confirm-reason" style={modal.textarea} placeholder="Enter reason for this action (required for audit log)..." rows={3}/>
            <div style={{fontSize:14,color:'rgba(255,255,255,0.6)',marginTop:6}}>This action will be permanently logged in the staff audit trail.</div>
            <div style={modal.btnRow}>
              <button style={modal.btnGhost} onClick={()=>setConfirmModal(null)}>Cancel</button>
              <button style={confirmModal.risk==='critical'?modal.btnDanger:modal.btnPrimary} onClick={handleConfirm}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Staff Modal */}
      {showInvite&&(
        <div style={modal.overlay} onClick={e=>{if(e.target===e.currentTarget&&!inviting){setShowInvite(false);setInvResult(null);setInvErr('');}}}>
          <div style={modal.box}>
            {invResult?(
              <>
                <div style={{...modal.title,color:'#4ADE80'}}>Invitation Sent</div>
                <div style={{...modal.body,marginBottom:16}}>An invitation email has been sent to <strong style={{color:'#fff'}}>{invResult.email}</strong>. The link expires in {invResult.expires_hours||48} hours.</div>
                <div style={{...modal.success,marginBottom:16}}>The staff member must click the invite link and set up MFA before accessing the platform.</div>
                <div style={modal.btnRow}>
                  <button style={modal.btnPrimary} onClick={()=>{setShowInvite(false);setInvResult(null);}}>Done</button>
                </div>
              </>
            ):(
              <form onSubmit={handleInvite}>
                <div style={modal.title}>Invite Staff Member</div>
                <div style={modal.body}>Send a secure invitation email. The recipient must set a password and complete MFA setup before gaining access.</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  <div>
                    <div style={modal.label}>First Name</div>
                    <input style={modal.input} value={inv.first_name} onChange={e=>setInv(p=>({...p,first_name:e.target.value}))} placeholder="First name"/>
                  </div>
                  <div>
                    <div style={modal.label}>Last Name</div>
                    <input style={modal.input} value={inv.last_name} onChange={e=>setInv(p=>({...p,last_name:e.target.value}))} placeholder="Last name"/>
                  </div>
                </div>
                <div style={modal.label}>Email Address <span style={{color:'#EF4444'}}>*</span></div>
                <input style={modal.input} type="email" value={inv.email} onChange={e=>setInv(p=>({...p,email:e.target.value}))} placeholder="staff@example.com" required/>
                <div style={modal.label}>Assign Roles <span style={{color:'#EF4444'}}>*</span></div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:4}}>
                  {ALL_ROLES.map(role=>{
                    const rm=ROLE_META[role]||{color:T.textSub,label:role};
                    const sel=inv.roles.includes(role);
                    return(
                      <button type="button" key={role} onClick={()=>toggleInvRole(role)} style={{padding:'4px 10px',borderRadius:6,fontSize:14,fontWeight:600,cursor:'pointer',border:`1px solid ${sel?rm.color:rm.color+'40'}`,background:sel?rm.color+'25':'transparent',color:sel?rm.color:'rgba(255,255,255,0.6)',transition:'all 0.15s'}}>
                        {rm.label||role}
                      </button>
                    );
                  })}
                </div>
                <div style={{...modal.body,fontSize:14,marginTop:8,marginBottom:0}}>Invite link expires in 48 hours. Recipient must accept before expiry.</div>
                <div style={modal.label}>Internal Notes</div>
                <textarea style={modal.textarea} value={inv.notes} onChange={e=>setInv(p=>({...p,notes:e.target.value}))} placeholder="Department, team, access reason..." rows={2}/>
                {invErr&&<div style={modal.err}>{invErr}</div>}
                <div style={modal.btnRow}>
                  <button type="button" style={modal.btnGhost} onClick={()=>{setShowInvite(false);setInvErr('');}}>Cancel</button>
                  <button type="submit" style={modal.btnPrimary} disabled={inviting}>{inviting?'Sending...':'Send Invitation'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Create Staff Modal */}
      {showCreate&&(
        <div style={modal.overlay} onClick={e=>{if(e.target===e.currentTarget&&!creating){setShowCreate(false);setCreateResult(null);setCreateErr('');}}}>
          <div style={modal.box}>
            {createResult?(
              <>
                <div style={{...modal.title,color:'#4ADE80'}}>Staff Account Created</div>
                <div style={{...modal.body,marginBottom:16}}>The account has been created. The staff member will be required to set up MFA on first login.</div>
                <div style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:10,padding:16,marginBottom:16}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                    <div><div style={{fontSize:13,color:'rgba(255,255,255,0.6)',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:3}}>Email</div><div style={{fontSize:13,color:'#fff'}}>{createResult.email}</div></div>
                    <div><div style={{fontSize:13,color:'rgba(255,255,255,0.6)',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:3}}>Roles</div><div style={{fontSize:13,color:'#fff'}}>{(createResult.roles||[]).join(', ')}</div></div>
                    <div><div style={{fontSize:13,color:'rgba(255,255,255,0.6)',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:3}}>Status</div><div style={{fontSize:13,color:'#2D6CFF'}}>{createResult.staff_status}</div></div>
                    {createResult.temp_password&&<div><div style={{fontSize:13,color:'rgba(255,255,255,0.6)',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:3}}>Temp Password</div><div style={{fontSize:14,fontFamily:'monospace',color:'#2D6CFF',background:'rgba(45,108,255,0.08)',padding:'3px 8px',borderRadius:4}}>{createResult.temp_password}</div></div>}
                  </div>
                </div>
                {createResult.temp_password&&<div style={{fontSize:14,color:'rgba(245,158,11,0.8)',marginBottom:16}}>Save the temporary password — it will not be shown again.</div>}
                <div style={modal.btnRow}>
                  <button style={modal.btnPrimary} onClick={()=>{setShowCreate(false);setCreateResult(null);}}>Done</button>
                </div>
              </>
            ):(
              <form onSubmit={handleCreate}>
                <div style={modal.title}>Create Staff Account</div>
                <div style={modal.body}>Create a new staff account. MFA setup will be required on first login.</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  <div>
                    <div style={modal.label}>First Name</div>
                    <input style={modal.input} value={cf.first_name} onChange={e=>setCf(p=>({...p,first_name:e.target.value}))} placeholder="First name"/>
                  </div>
                  <div>
                    <div style={modal.label}>Last Name</div>
                    <input style={modal.input} value={cf.last_name} onChange={e=>setCf(p=>({...p,last_name:e.target.value}))} placeholder="Last name"/>
                  </div>
                </div>
                <div style={modal.label}>Email Address <span style={{color:'#EF4444'}}>*</span></div>
                <input style={modal.input} type="email" value={cf.email} onChange={e=>setCf(p=>({...p,email:e.target.value}))} placeholder="staff@visinvoke.com" required/>
                <div style={modal.label}>Assign Roles <span style={{color:'#EF4444'}}>*</span></div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:4}}>
                  {ALL_ROLES.map(role=>{
                    const rm=ROLE_META[role]||{color:T.textSub,label:role};
                    const sel=cf.roles.includes(role);
                    return(
                      <button type="button" key={role} onClick={()=>toggleRole(role)} style={{padding:'4px 10px',borderRadius:6,fontSize:14,fontWeight:600,cursor:'pointer',border:`1px solid ${sel?rm.color:rm.color+'40'}`,background:sel?rm.color+'25':'transparent',color:sel?rm.color:'rgba(255,255,255,0.6)',transition:'all 0.15s'}}>
                        {rm.label||role}
                      </button>
                    );
                  })}
                </div>
                <div style={modal.label}>Internal Notes</div>
                <textarea style={modal.textarea} value={cf.notes} onChange={e=>setCf(p=>({...p,notes:e.target.value}))} placeholder="Internal memo, department, team..." rows={2}/>
                {createErr&&<div style={modal.err}>{createErr}</div>}
                <div style={modal.btnRow}>
                  <button type="button" style={modal.btnGhost} onClick={()=>{setShowCreate(false);setCreateErr('');}}>Cancel</button>
                  <button type="submit" style={modal.btnPrimary} disabled={creating}>{creating?'Creating...':'Create Staff Account'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Action feedback */}
      {actionMsg&&<div style={{padding:'10px 14px',background:'rgba(74,222,128,0.08)',border:'1px solid rgba(74,222,128,0.2)',borderRadius:8,fontSize:14,color:'#4ADE80',marginBottom:12,display:'flex',alignItems:'center',justifyContent:'space-between'}}>{actionMsg}<button style={{background:'none',border:'none',color:'rgba(255,255,255,0.6)',cursor:'pointer',fontSize:16,padding:0}} onClick={()=>setActionMsg('')}>×</button></div>}
      {actionErr&&<div style={{padding:'10px 14px',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,fontSize:14,color:'#FCA5A5',marginBottom:12,display:'flex',alignItems:'center',justifyContent:'space-between'}}>{actionErr}<button style={{background:'none',border:'none',color:'rgba(255,255,255,0.6)',cursor:'pointer',fontSize:16,padding:0}} onClick={()=>setActionErr('')}>×</button></div>}
      {err&&<div style={{padding:'10px 14px',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,fontSize:14,color:'#FCA5A5',marginBottom:12}}>{err}</div>}

      {/* Controls */}
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <input style={{...s.input,minWidth:220,flex:1}} placeholder="Search name or email..." value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}/>
        <select style={s.select} value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setPage(1);}}>
          <option value="">All statuses</option>
          {['ACTIVE','MFA_REQUIRED','INVITED','SUSPENDED','DISABLED','PENDING'].map(v=><option key={v} value={v}>{v}</option>)}
        </select>
        <select style={s.select} value={roleFilter} onChange={e=>{setRoleFilter(e.target.value);setPage(1);}}>
          <option value="">All roles</option>
          {['OWNER','CO_OWNER','OPERATIONS_ADMIN','SUPPORT','MODERATION','FINANCE','ANALYTICS_VIEWER','TECH_OPERATOR','SECURITY_AUDITOR','READ_ONLY_ADMIN'].map(r=><option key={r} value={r}>{ROLE_META[r]?.label||r}</option>)}
        </select>
        <button style={s.btn('primary')} onClick={()=>{setShowInvite(true);setInvResult(null);setInvErr('');}}>+ Invite Staff</button>
        <button style={s.btn()} onClick={()=>{setShowCreate(true);setCreateResult(null);setCreateErr('');}}>+ Create Staff</button>
        <button style={{...s.btn(),borderColor:showInviteTab?'rgba(45,108,255,0.5)':'',color:showInviteTab?'#2D6CFF':''}} onClick={()=>setShowInviteTab(v=>!v)}>Pending Invites</button>
        <button style={s.btn()} onClick={load}>Refresh</button>
      </div>

      {showInviteTab&&(
        <div style={{marginBottom:16}}>
          <TableCard title="Pending Invitations" count={invites?invites.length:0}>
            {!invites?<Loader/>:invites.length===0?(
              <div style={{...s.empty,color:'rgba(255,255,255,0.5)',padding:'24px 0'}}>No pending invitations</div>
            ):(
              <div style={{overflowX:'auto'}}>
              <table style={{...s.table,minWidth:700}}>
                <thead>
                  <tr>{['Email','Roles','Invited','Expires','Status','Actions'].map(h=><th key={h} style={{...s.th,whiteSpace:'nowrap'}}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {invites.map(inv=>{
                    const expired=new Date(inv.expires_at)<new Date();
                    const status=inv.is_used?'Accepted':expired?'Expired':'Pending';
                    const statusColor={Accepted:'green',Expired:'red',Pending:'amber'}[status]||'grey';
                    const maskedEmail=inv.email?inv.email.replace(/(.{2})(.+)(@.+)/,(m,a,b,c)=>a+'*'.repeat(Math.min(b.length,6))+c):'—';
                    return(
                      <tr key={inv.id}>
                        <td style={{...s.td,fontSize:15}}>{maskedEmail}</td>
                        <td style={s.td}>
                          <div style={{display:'flex',flexWrap:'wrap',gap:3}}>
                            {(inv.roles||[]).map(r=>{const rm=ROLE_META[r]||{color:T.textSub,label:r};return(<span key={r} style={{display:'inline-block',padding:'3px 10px',borderRadius:4,fontSize:13,fontWeight:700,background:rm.color+'18',color:rm.color,border:`1px solid ${rm.color}30`}}>{rm.label||r}</span>);})}
                          </div>
                        </td>
                        <td style={{...s.td,fontSize:14,color:'rgba(255,255,255,0.65)',whiteSpace:'nowrap'}}>{fmtDate(inv.created_at)}</td>
                        <td style={{...s.td,fontSize:14,color:expired?'#FCA5A5':'rgba(255,255,255,0.65)',whiteSpace:'nowrap'}}>{fmtDate(inv.expires_at)}</td>
                        <td style={s.td}><span style={s.badge(statusColor)}>{status}</span></td>
                        <td style={s.td}>
                          <div style={{display:'flex',gap:4}}>
                            {!inv.is_used&&!expired&&(
                              <button style={{...s.btn(),fontSize:13,padding:'3px 8px',opacity:0.5,cursor:'not-allowed'}} title="Resend — coming soon" disabled>Resend</button>
                            )}
                            {!inv.is_used&&(
                              <button style={{...s.btn(),fontSize:13,padding:'3px 8px',color:'#FCA5A5',borderColor:'rgba(239,68,68,0.3)',opacity:0.5,cursor:'not-allowed'}} title="Revoke — coming soon" disabled>Revoke</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </TableCard>
        </div>
      )}

      {!staff?<Loader/>:(
        <TableCard title="Staff Accounts" count={staff.total}>
          <div style={{overflowX:'auto'}}>
          <table style={{...s.table,minWidth:900}}>
            <thead>
              <tr>
                {['Name','Email','Roles','Status','MFA','Last Login','Sessions','Workload','Created','Actions'].map(h=>(
                  <th key={h} style={{...s.th,whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(staff.staff||[]).map(m=>{
                const isActive=m.staff_status==='ACTIVE';
                const isSuspended=m.staff_status==='SUSPENDED';
                const isDisabled=m.staff_status==='DISABLED';
                return(
                  <tr key={m.user_id} style={{transition:'background 0.1s'}}
                    onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.02)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                  >
                    <td style={s.tds}>
                      <div style={{fontWeight:600,fontSize:13,cursor:'pointer',color:T.accent,textDecoration:'underline',textDecorationColor:T.accentBorder}} onClick={()=>setDetailMember(m.user_id)} title="View staff detail">{m.name||<span style={{color:'rgba(255,255,255,0.5)',fontStyle:'italic',textDecoration:'none'}}>No name</span>}</div>
                    </td>
                    <td style={{...s.td,fontSize:14,color:'rgba(255,255,255,0.5)'}}>{m.email}</td>
                    <td style={s.td}>
                      <div style={{display:'flex',flexWrap:'wrap',gap:3}}>
                        {(m.roles||[]).map(r=>{
                          const rmr=ROLE_META[r]||{color:T.textSub,label:r};
                          return(<span key={r} style={{display:'inline-block',padding:'3px 10px',borderRadius:4,fontSize:13,fontWeight:700,background:rmr.color+'18',color:rmr.color,border:`1px solid ${rmr.color}30`,whiteSpace:'nowrap'}}>{rmr.label||r}</span>);
                        })}
                      </div>
                    </td>
                    <td style={s.td}><span style={statusBadge(m.staff_status)}>{m.staff_status||'-'}</span></td>
                    <td style={s.td}>
                      <span style={s.badge(m.mfa_enabled?'green':'red')}>{m.mfa_enabled?'On':'Off'}</span>
                    </td>
                    <td style={{...s.td,fontSize:14,color:'rgba(255,255,255,0.65)',whiteSpace:'nowrap'}}>{fmtAgo(m.last_login)}</td>
                    <td style={s.td}>
                      <span style={{fontWeight:700,fontSize:13,color:m.active_sessions>0?'#3B82F6':'rgba(255,255,255,0.5)'}}>{m.active_sessions}</span>
                    </td>
                    <td style={s.td}>
                      <div style={{display:'flex',flexDirection:'column',gap:2}}>
                        <span style={{fontSize:12,color:m.open_tickets>0?T.amber:T.textFaint,fontWeight:m.open_tickets>0?700:400}} title="Open tickets assigned">
                          {m.open_tickets!=null?`${m.open_tickets} ticket${m.open_tickets!==1?'s':''}`:'-'}
                        </span>
                        <span style={{fontSize:12,color:m.open_threads>0?'#63b3ed':T.textFaint,fontWeight:m.open_threads>0?700:400}} title="Open threads assigned">
                          {m.open_threads!=null?`${m.open_threads} thread${m.open_threads!==1?'s':''}`:'-'}
                        </span>
                      </div>
                    </td>
                    <td style={{...s.td,fontSize:14,color:'rgba(255,255,255,0.6)',whiteSpace:'nowrap'}}>{fmtDate(m.created_at)}</td>
                    <td style={s.td}>
                      <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                        {m.active_sessions>0&&(
                          <button style={{...s.btn(),fontSize:13,padding:'3px 8px'}} onClick={()=>handleForceLogout(m)} title="Revoke all sessions">Logout</button>
                        )}
                        {m.mfa_enabled&&(
                          <button style={{...s.btn(),fontSize:13,padding:'3px 8px'}} onClick={()=>handleMFAReset(m)} title="Reset MFA">MFA Reset</button>
                        )}
                        {isActive&&(
                          <button style={{...s.btn(),fontSize:13,padding:'3px 8px',background:T.amberBg,color:T.amber,borderColor:T.amberBorder}} onClick={()=>handleSuspend(m)}>Suspend</button>
                        )}
                        {(isSuspended||isDisabled)&&(
                          <button style={{...s.btn(),fontSize:13,padding:'3px 8px',color:'#4ADE80',borderColor:'rgba(74,222,128,0.3)'}} onClick={()=>handleRestore(m)}>Restore</button>
                        )}
                        {!isDisabled&&(
                          <button style={{...s.btn(),fontSize:13,padding:'3px 8px',background:T.redBg,color:T.red,borderColor:T.redBorder}} onClick={()=>handleDisable(m)}>Disable</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(staff.staff||[]).length===0&&(
                <tr><td colSpan={10} style={{...s.empty,color:'rgba(255,255,255,0.5)'}}>No staff accounts found</td></tr>
              )}
            </tbody>
          </table>
          </div>
          <Pager page={staff.page} pages={staff.pages} total={staff.total} onPage={setPage}/>
        </TableCard>
      )}
    </>
  );
}

// -- STAFF SESSIONS -----------------------------------------------------------
function SecSessions({api}){
  const[sessions,setSessions]=useState(null);const[err,setErr]=useState(null);
  const[activeOnly,setActiveOnly]=useState('true');const[page,setPage]=useState(1);
  const[actionMsg,setActionMsg]=useState('');const[actionErr,setActionErr]=useState('');
  const[revoking,setRevoking]=useState(null);const[revokeReason,setRevokeReason]=useState('');
  const[mySessionId,setMySessionId]=useState(null);
  const[revokeAllModal,setRevokeAllModal]=useState(false);const[revokeAllReason,setRevokeAllReason]=useState('');

  // Fetch current session ID to mark it in table
  useEffect(()=>{
    fetch(`${api}/v1/staff/sessions/me`,{credentials:'include'})
      .then(r=>r.json()).then(d=>setMySessionId(d.session_id)).catch(()=>{});
  },[api]);

  const load=useCallback(()=>{
    const q=new URLSearchParams({page,per_page:50,active:activeOnly});
    fetch(`${api}/v1/staff/sessions?${q}`,{credentials:'include'})
      .then(r=>r.json()).then(setSessions).catch(()=>setErr('Failed to load sessions'));
  },[api,page,activeOnly]);

  useEffect(()=>{load();},[load]);

  const handleRevoke=(sess)=>{setRevoking(sess);setRevokeReason('');};

  const confirmRevoke=()=>{
    if(!revokeReason.trim())return;
    fetch(`${api}/v1/staff/sessions/${revoking.id}/revoke`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({reason:revokeReason})})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{
        if(ok){setActionMsg('Session revoked.');setRevoking(null);setRevokeReason('');load();}
        else{setActionErr(d.error||'Revoke failed.');}
      }).catch(()=>setActionErr('Network error.'));
  };

  const confirmRevokeAll=()=>{
    if(!revokeAllReason.trim())return;
    fetch(`${api}/v1/staff/sessions/revoke-others`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({reason:revokeAllReason})})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{
        if(ok){setActionMsg(`${d.count||0} session(s) revoked.`);setRevokeAllModal(false);setRevokeAllReason('');load();}
        else{setActionErr(d.error||'Revoke failed.');}
      }).catch(()=>setActionErr('Network error.'));
  };

  const modal={overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9000,display:'flex',alignItems:'center',justifyContent:'center',padding:20},box:{background:'#12161F',border:'1px solid rgba(255,255,255,0.1)',borderRadius:12,padding:28,width:'100%',maxWidth:420},title:{fontFamily:"'Syne',sans-serif",fontSize:19,fontWeight:700,color:'#fff',marginBottom:10},body:{fontSize:15,color:'rgba(255,255,255,0.65)',marginBottom:16},inp:{width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,padding:'12px 16px',fontSize:15,color:'#fff',outline:'none',resize:'vertical',minHeight:70,fontFamily:"'DM Sans',sans-serif"},btnRow:{display:'flex',gap:8,marginTop:16,justifyContent:'flex-end'},btnGhost:{padding:'10px 18px',background:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,fontSize:14,color:'rgba(255,255,255,0.5)',cursor:'pointer'},btnDanger:{padding:'10px 18px',background:'rgba(239,68,68,0.15)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:8,fontSize:14,fontWeight:600,color:'#FCA5A5',cursor:'pointer'}};

  if(err)return<div style={s.error}>! {err}</div>;
  return(
    <>
      {/* Revoke single session modal */}
      {revoking&&(
        <div style={modal.overlay} onClick={e=>{if(e.target===e.currentTarget)setRevoking(null);}}>
          <div style={modal.box}>
            <div style={modal.title}>Revoke Session</div>
            <div style={modal.body}>Revoking session for <strong style={{color:'#fff'}}>{revoking.staff_name||revoking.staff_email}</strong>. This will immediately end their session.</div>
            <textarea style={modal.inp} value={revokeReason} onChange={e=>setRevokeReason(e.target.value)} placeholder="Reason for revoking (required for audit log)..." rows={3} autoFocus/>
            <div style={modal.btnRow}>
              <button style={modal.btnGhost} onClick={()=>setRevoking(null)}>Cancel</button>
              <button style={{...modal.btnDanger,opacity:revokeReason.trim()?1:0.4}} onClick={confirmRevoke} disabled={!revokeReason.trim()}>Revoke Session</button>
            </div>
          </div>
        </div>
      )}
      {/* Revoke all others modal */}
      {revokeAllModal&&(
        <div style={modal.overlay} onClick={e=>{if(e.target===e.currentTarget)setRevokeAllModal(false);}}>
          <div style={modal.box}>
            <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:12}}>
              <div style={{width:32,height:32,borderRadius:8,background:'rgba(239,68,68,0.15)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
              </div>
              <div style={modal.title}>Revoke All Other Sessions</div>
            </div>
            <div style={modal.body}>This will immediately revoke all active staff sessions except your current one. All other staff will be logged out.</div>
            <textarea style={modal.inp} value={revokeAllReason} onChange={e=>setRevokeAllReason(e.target.value)} placeholder="Reason (required for audit log)..." rows={3} autoFocus/>
            <div style={modal.btnRow}>
              <button style={modal.btnGhost} onClick={()=>setRevokeAllModal(false)}>Cancel</button>
              <button style={{...modal.btnDanger,opacity:revokeAllReason.trim()?1:0.4}} onClick={confirmRevokeAll} disabled={!revokeAllReason.trim()}>Revoke All Others</button>
            </div>
          </div>
        </div>
      )}
      {actionMsg&&<div style={{padding:'10px 14px',background:'rgba(74,222,128,0.08)',border:'1px solid rgba(74,222,128,0.2)',borderRadius:8,fontSize:14,color:'#4ADE80',marginBottom:12,display:'flex',alignItems:'center',justifyContent:'space-between'}}>{actionMsg}<button style={{background:'none',border:'none',color:'rgba(255,255,255,0.6)',cursor:'pointer',fontSize:16,padding:0}} onClick={()=>setActionMsg('')}>×</button></div>}
      {actionErr&&<div style={{padding:'10px 14px',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,fontSize:14,color:'#FCA5A5',marginBottom:12,display:'flex',alignItems:'center',justifyContent:'space-between'}}>{actionErr}<button style={{background:'none',border:'none',color:'rgba(255,255,255,0.6)',cursor:'pointer',fontSize:16,padding:0}} onClick={()=>setActionErr('')}>×</button></div>}
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <select style={s.select} value={activeOnly} onChange={e=>{setActiveOnly(e.target.value);setPage(1);}}>
          <option value="true">Active sessions</option><option value="false">All sessions</option>
        </select>
        <button style={s.btn('primary')} onClick={load}>Refresh</button>
        <button style={{...s.btn(),color:'#FCA5A5',borderColor:'rgba(239,68,68,0.3)',marginLeft:'auto'}} onClick={()=>setRevokeAllModal(true)}>Revoke All Others</button>
      </div>
      {!sessions?<Loader/>:(
        <TableCard title="Staff Sessions" count={sessions.total}>
          <div style={{overflowX:'auto'}}>
          <table style={{...s.table,minWidth:900}}>
            <thead><tr>{['Staff','Role','IP','Browser','Device','Login','Last Seen','MFA','Status',''].map(h=><th key={h} style={{...s.th,whiteSpace:'nowrap'}}>{h}</th>)}</tr></thead>
            <tbody>
              {(sessions.sessions||[]).map(sess=>{
                const isCurrent=mySessionId&&sess.id===mySessionId;
                const rm=ROLE_META[sess.active_role]||{color:T.textSub,label:sess.active_role||'-'};
                return(
                  <tr key={sess.id} style={isCurrent?{background:'rgba(45,108,255,0.04)',outline:'1px solid rgba(45,108,255,0.15)'}:{}}>
                    <td style={s.td}>
                      <div style={{fontSize:15,fontWeight:600,display:'flex',alignItems:'center',gap:6}}>
                        {sess.staff_name||'-'}
                        {isCurrent&&<span style={{fontSize:9,fontWeight:700,color:'#2D6CFF',background:'rgba(45,108,255,0.12)',padding:'1px 5px',borderRadius:3,border:'1px solid rgba(45,108,255,0.25)',letterSpacing:'0.05em'}}>YOU</span>}
                      </div>
                      <div style={{fontSize:13,color:T.textSub,display:'flex',alignItems:'center',gap:5,marginTop:2}}>
                        <EntityChip type="user" id={sess.staff_user_id} label={sess.staff_email||sess.staff_user_id?.slice(0,8)}/>
                      </div>
                    </td>
                    <td style={s.td}><span style={{padding:'3px 10px',borderRadius:4,fontSize:13,fontWeight:700,background:rm.color+'18',color:rm.color,border:`1px solid ${rm.color}30`}}>{rm.label}</span></td>
                    <td style={{...s.td,fontSize:14,fontFamily:mono,color:T.textSub}}>{sess.ip_address||'-'}</td>
                    <td style={{...s.td,fontSize:14}}>{sess.browser||'-'}</td>
                    <td style={{...s.td,fontSize:14}}>{sess.device||'-'}</td>
                    <td style={{...s.td,fontSize:14,whiteSpace:'nowrap'}}>{fmtAgo(sess.login_at)}</td>
                    <td style={{...s.td,fontSize:14,whiteSpace:'nowrap'}}>{fmtAgo(sess.last_seen_at)}</td>
                    <td style={s.td}><span style={s.badge(sess.mfa_verified?'green':'red')}>{sess.mfa_verified?'Yes':'No'}</span></td>
                    <td style={s.td}><span style={s.badge(sess.is_active?'green':'grey')}>{sess.is_active?'Active':'Ended'}</span></td>
                    <td style={s.td}>
                      <div style={{display:'flex',gap:4,alignItems:'center'}}>
                        <EntityChip type="session" id={sess.id} label={sess.id?.slice(0,8)}/>
                        {sess.is_active&&!isCurrent&&(
                          <button style={{...s.btn(),fontSize:13,padding:'3px 8px',color:T.red,borderColor:T.redBorder}} onClick={()=>handleRevoke(sess)}>Revoke</button>
                        )}
                        {isCurrent&&sess.is_active&&(
                          <span style={{fontSize:13,color:'rgba(45,108,255,0.5)'}}>Current</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(sessions.sessions||[]).length===0&&<tr><td colSpan={10} style={s.empty}>No sessions found</td></tr>}
            </tbody>
          </table>
          </div>
          <Pager page={sessions.page} pages={sessions.pages} total={sessions.total} onPage={setPage}/>
        </TableCard>
      )}
    </>
  );
}

// -- SECURITY CENTER ----------------------------------------------------------
function SecSecurity({api}){
  const[overview,setOverview]=useState(null);const[events,setEvents]=useState(null);
  const[failed,setFailed]=useState(null);const[err,setErr]=useState(null);
  const[hours,setHours]=useState(24);const[riskFilter,setRiskFilter]=useState('');
  const[tab,setTab]=useState('overview'); // overview | events | failed | actions
  const[actionMsg,setActionMsg]=useState('');const[actionErr,setActionErr]=useState('');
  const[lockdownModal,setLockdownModal]=useState(null);const[lockdownReason,setLockdownReason]=useState('');

  useEffect(()=>{
    fetch(`${api}/v1/staff/security/overview`,{credentials:'include'})
      .then(r=>r.json()).then(setOverview).catch(()=>setErr('Failed to load security overview'));
  },[api]);

  const loadEvents=useCallback(()=>{
    const q=new URLSearchParams({hours,per_page:100});
    if(riskFilter)q.set('risk_level',riskFilter);
    fetch(`${api}/v1/staff/security/events?${q}`,{credentials:'include'})
      .then(r=>r.json()).then(setEvents).catch(()=>{});
  },[api,hours,riskFilter]);

  const loadFailed=useCallback(()=>{
    fetch(`${api}/v1/staff/security/failed-logins?hours=${hours}&per_page=50`,{credentials:'include'})
      .then(r=>r.json()).then(setFailed).catch(()=>{});
  },[api,hours]);

  useEffect(()=>{if(tab==='events')loadEvents();},[tab,loadEvents]);
  useEffect(()=>{if(tab==='failed')loadFailed();},[tab,loadFailed]);

  const doPost=(url,body,ok,fail)=>fetch(`${api}${url}`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)})
    .then(r=>r.json().then(d=>({r,d}))).then(({r,d})=>{if(r.ok){setActionMsg(ok);}else{setActionErr(d.error||fail);}}).catch(()=>setActionErr(fail));

  const handleLockdown=(user)=>{setLockdownModal(user);setLockdownReason('');};
  const confirmLockdown=()=>{
    if(!lockdownReason.trim())return;
    doPost('/v1/staff/security/actions/emergency-lockdown',{user_id:lockdownModal.user_id,reason:lockdownReason},'Account locked down and sessions revoked.','Lockdown failed.');
    setLockdownModal(null);setLockdownReason('');
  };

  const securityModal={overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9000,display:'flex',alignItems:'center',justifyContent:'center',padding:20},box:{background:'#12161F',border:'1px solid rgba(239,68,68,0.2)',borderRadius:12,padding:28,width:'100%',maxWidth:440},title:{fontFamily:"'Syne',sans-serif",fontSize:19,fontWeight:700,color:'#fff',marginBottom:10},body:{fontSize:15,color:'rgba(255,255,255,0.65)',marginBottom:16},inp:{width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,padding:'12px 16px',fontSize:15,color:'#fff',outline:'none',resize:'vertical',minHeight:70,fontFamily:"'DM Sans',sans-serif"},btnRow:{display:'flex',gap:8,marginTop:16,justifyContent:'flex-end'}};

  const tabStyle=(t)=>({padding:'9px 18px',borderRadius:8,fontSize:14,fontWeight:600,cursor:'pointer',border:'none',background:tab===t?'rgba(45,108,255,0.15)':'transparent',color:tab===t?'#2D6CFF':'rgba(255,255,255,0.4)',transition:'all 0.15s'});

  const riskColor=(r)=>r==='CRITICAL'?'red':r==='HIGH'?'amber':'blue';

  if(err)return<div style={s.error}>! {err}</div>;
  return(
    <>
      {/* Emergency lockdown modal */}
      {lockdownModal&&(
        <div style={securityModal.overlay} onClick={e=>{if(e.target===e.currentTarget)setLockdownModal(null);}}>
          <div style={securityModal.box}>
            <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:12}}>
              <div style={{width:32,height:32,borderRadius:8,background:'rgba(239,68,68,0.15)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <div style={securityModal.title}>Emergency Lockdown</div>
            </div>
            <div style={securityModal.body}>Suspend <strong style={{color:'#fff'}}>{lockdownModal.staff_name||lockdownModal.email}</strong> and revoke all their sessions immediately. This is a CRITICAL security action.</div>
            <textarea style={securityModal.inp} value={lockdownReason} onChange={e=>setLockdownReason(e.target.value)} placeholder="Reason for emergency lockdown (required)..." rows={3} autoFocus/>
            <div style={securityModal.btnRow}>
              <button style={{padding:'10px 18px',background:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,fontSize:14,color:'rgba(255,255,255,0.5)',cursor:'pointer'}} onClick={()=>setLockdownModal(null)}>Cancel</button>
              <button style={{padding:'10px 18px',background:'rgba(239,68,68,0.2)',border:'1px solid rgba(239,68,68,0.4)',borderRadius:8,fontSize:14,fontWeight:700,color:'#FCA5A5',cursor:'pointer',opacity:lockdownReason.trim()?1:0.4}} onClick={confirmLockdown} disabled={!lockdownReason.trim()}>Lockdown Account</button>
            </div>
          </div>
        </div>
      )}

      {actionMsg&&<div style={{padding:'10px 14px',background:'rgba(74,222,128,0.08)',border:'1px solid rgba(74,222,128,0.2)',borderRadius:8,fontSize:14,color:'#4ADE80',marginBottom:12,display:'flex',alignItems:'center',justifyContent:'space-between'}}>{actionMsg}<button style={{background:'none',border:'none',color:'rgba(255,255,255,0.6)',cursor:'pointer',fontSize:16,padding:0}} onClick={()=>setActionMsg('')}>×</button></div>}
      {actionErr&&<div style={{padding:'10px 14px',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,fontSize:14,color:'#FCA5A5',marginBottom:12,display:'flex',alignItems:'center',justifyContent:'space-between'}}>{actionErr}<button style={{background:'none',border:'none',color:'rgba(255,255,255,0.6)',cursor:'pointer',fontSize:16,padding:0}} onClick={()=>setActionErr('')}>×</button></div>}

      {/* Summary KPIs */}
      {overview&&(
        <div style={{background:'rgba(255,255,255,0.02)',borderRadius:10,padding:'16px 20px',marginBottom:16,border:'1px solid rgba(255,255,255,0.06)'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:12}}>
            {[
              {label:'Active Sessions',val:overview.summary?.active_sessions,color:'blue'},
              {label:'Failed Logins 24h',val:overview.summary?.failed_pw_24h,color:overview.summary?.failed_pw_24h>10?'red':'amber'},
              {label:'Failed MFA 24h',val:overview.summary?.failed_mfa_24h,color:overview.summary?.failed_mfa_24h>5?'red':'amber'},
              {label:'Total Staff',val:overview.summary?.total_staff,color:'blue'},
              {label:'Active Staff',val:overview.summary?.active_staff,color:'green'},
              {label:'Suspended',val:overview.summary?.suspended_staff,color:overview.summary?.suspended_staff>0?'red':'green'},
            ].map(k=>(
              <div key={k.label} style={{textAlign:'center'}}>
                <div style={{fontFamily:"'Syne',sans-serif",fontSize:36,fontWeight:800,color:k.color==='red'?'#EF4444':k.color==='amber'?'#F59E0B':k.color==='green'?'#4ADE80':'#3B82F6'}}>{fmt(k.val)}</div>
                <div style={{fontSize:13,color:'rgba(255,255,255,0.6)',marginTop:4,letterSpacing:'0.04em'}}>{k.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab navigation */}
      <div style={{display:'flex',gap:4,marginBottom:16,padding:'4px',background:'rgba(255,255,255,0.03)',borderRadius:10,border:'1px solid rgba(255,255,255,0.06)',width:'fit-content'}}>
        {[['overview','Overview'],['events','Security Events'],['failed','Failed Logins'],['actions','Security Actions']].map(([k,l])=>(
          <button key={k} style={tabStyle(k)} onClick={()=>setTab(k)}>{l}</button>
        ))}
      </div>

      {/* Hours + risk filter */}
      {tab!=='actions'&&(
        <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center'}}>
          <select style={s.select} value={hours} onChange={e=>setHours(Number(e.target.value))}>
            {[1,6,12,24,48,72].map(h=><option key={h} value={h}>Last {h}h</option>)}
          </select>
          {tab==='events'&&(
            <select style={s.select} value={riskFilter} onChange={e=>setRiskFilter(e.target.value)}>
              <option value="">All risk levels</option>
              {['LOW','HIGH','CRITICAL'].map(r=><option key={r} value={r}>{r}</option>)}
            </select>
          )}
          <button style={s.btn()} onClick={tab==='events'?loadEvents:loadFailed}>Refresh</button>
        </div>
      )}

      {/* Overview tab */}
      {tab==='overview'&&overview&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <TableCard title="Critical Actions (24h)" count={(overview.critical_actions||[]).length}>
            <table style={s.table}>
              <thead><tr><th style={s.th}>Action</th><th style={s.th}>Staff</th><th style={s.th}>Risk</th><th style={s.th}>When</th></tr></thead>
              <tbody>
                {(overview.critical_actions||[]).map(a=>(<tr key={a.id}><td style={{...s.td,fontSize:14,fontFamily:mono,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.action}</td><td style={{...s.td,fontSize:14,color:T.textSub}}>{a.staff_user_id?.slice(0,8)}</td><td style={s.td}><span style={s.badge(riskColor(a.risk_level))}>{a.risk_level}</span></td><td style={{...s.td,fontSize:14,whiteSpace:'nowrap'}}>{fmtAgo(a.created_at)}</td></tr>))}
                {(overview.critical_actions||[]).length===0&&<tr><td colSpan={4} style={s.empty}>No critical actions in 24h</td></tr>}
              </tbody>
            </table>
          </TableCard>
          <TableCard title="Recent Logins (24h)" count={(overview.recent_logins||[]).length}>
            <table style={s.table}>
              <thead><tr><th style={s.th}>Staff</th><th style={s.th}>Role</th><th style={s.th}>IP</th><th style={s.th}>When</th></tr></thead>
              <tbody>
                {(overview.recent_logins||[]).map(sess=>{const rm=ROLE_META[sess.active_role]||{color:T.textSub,label:sess.active_role||'-'};return(<tr key={sess.id}><td style={{...s.td,fontSize:14}}>{sess.staff_name||sess.staff_email}</td><td style={s.td}><span style={{fontSize:13,fontWeight:700,color:rm.color}}>{rm.label}</span></td><td style={{...s.td,fontSize:14,fontFamily:mono,color:T.textSub}}>{sess.ip_address}</td><td style={{...s.td,fontSize:14,whiteSpace:'nowrap'}}>{fmtAgo(sess.login_at)}</td></tr>);})}
                {(overview.recent_logins||[]).length===0&&<tr><td colSpan={4} style={s.empty}>No recent logins</td></tr>}
              </tbody>
            </table>
          </TableCard>
        </div>
      )}

      {/* Security Events tab */}
      {tab==='events'&&(!events?<Loader/>:(
        <TableCard title="Security Events" count={events.total}>
          <div style={{overflowX:'auto'}}>
          <table style={{...s.table,minWidth:800}}>
            <thead><tr>{['Event','Actor','Target','Risk','IP','When'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {(events.events||[]).map(e=>(
                <tr key={e.id}>
                  <td style={s.td}>
                    <div style={{fontSize:14,fontWeight:600,color:'#fff'}}>{e.event_label||e.action}</div>
                    <div style={{fontSize:13,fontFamily:mono,color:T.textFaint,marginTop:2}}>{e.action}</div>
                  </td>
                  <td style={s.td}>
                    <div style={{fontSize:14}}>{e.actor_name||e.actor_email||e.staff_user_id?.slice(0,8)||'-'}</div>
                    <div style={{fontSize:13,color:T.textSub}}>{e.actor_email}</div>
                  </td>
                  <td style={{...s.td,fontSize:14,color:T.textSub}}>{e.target_type&&e.target_id?`${e.target_type}:${e.target_id?.slice(0,8)}`:'-'}</td>
                  <td style={s.td}><span style={s.badge(riskColor(e.risk_level))}>{e.risk_level||'LOW'}</span></td>
                  <td style={{...s.td,fontSize:14,fontFamily:mono,color:T.textSub}}>{e.ip_address||'-'}</td>
                  <td style={{...s.td,fontSize:14,whiteSpace:'nowrap'}}>{fmtAgo(e.created_at)}</td>
                </tr>
              ))}
              {(events.events||[]).length===0&&<tr><td colSpan={6} style={s.empty}>No security events in this period</td></tr>}
            </tbody>
          </table>
          </div>
        </TableCard>
      ))}

      {/* Failed Logins tab */}
      {tab==='failed'&&(!failed?<Loader/>:(
        <TableCard title="Failed Login Attempts" count={failed.total}>
          <table style={s.table}>
            <thead><tr>{['Email','Type','IP Address','When'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {(failed.failed_logins||[]).map(f=>(<tr key={f.id}><td style={{...s.td,fontSize:15}}>{f.email}</td><td style={s.td}><span style={s.badge(f.attempt_type==='password'?'amber':'red')}>{f.attempt_type}</span></td><td style={{...s.td,fontSize:14,fontFamily:mono,color:T.textSub}}>{f.ip_address||'-'}</td><td style={{...s.td,fontSize:14,whiteSpace:'nowrap'}}>{fmtAgo(f.created_at)}</td></tr>))}
              {(failed.failed_logins||[]).length===0&&<tr><td colSpan={4} style={s.empty}>No failed attempts in this period</td></tr>}
            </tbody>
          </table>
        </TableCard>
      ))}

      {/* Security Actions tab */}
      {tab==='actions'&&(
        <div>
          <div style={{fontSize:14,color:'rgba(255,255,255,0.6)',marginBottom:16,padding:'10px 14px',background:'rgba(239,68,68,0.05)',border:'1px solid rgba(239,68,68,0.1)',borderRadius:8}}>
            Security actions are permanent and audited. All require owner or security.manage permission. Session revocations take effect immediately.
          </div>
          {/* Active staff for lockdown */}
          {overview&&(
            <TableCard title="Active Staff — Emergency Actions" count={overview.summary?.active_staff}>
              <table style={s.table}>
                <thead><tr>{['Staff','Status','Sessions','Action'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {(overview.recent_logins||[]).filter((v,i,a)=>a.findIndex(x=>x.staff_user_id===v.staff_user_id)===i).map(sess=>(
                    <tr key={sess.id}>
                      <td style={s.td}><div style={{fontSize:14,fontWeight:600}}>{sess.staff_name||sess.staff_email}</div><div style={{fontSize:13,color:T.textSub}}>{sess.staff_email}</div></td>
                      <td style={s.td}><span style={s.badge('green')}>Active</span></td>
                      <td style={{...s.td,fontSize:15}}>{sess.is_active?'1+ active':'-'}</td>
                      <td style={s.td}>
                        <div style={{display:'flex',gap:4}}>
                          <button style={{...s.btn(),fontSize:13,padding:'3px 8px',color:'#FCA5A5',borderColor:'rgba(239,68,68,0.3)'}} onClick={()=>handleLockdown(sess)}>Emergency Lockdown</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(overview.recent_logins||[]).length===0&&<tr><td colSpan={4} style={s.empty}>No recent active sessions</td></tr>}
                </tbody>
              </table>
            </TableCard>
          )}
        </div>
      )}
    </>
  );
}

// -- AUDIT LOG ----------------------------------------------------------------
function SecAuditLog({api}){
  const[logs,setLogs]=useState(null);const[err,setErr]=useState(null);
  const[page,setPage]=useState(1);const[risk,setRisk]=useState('');
  const[actionFilter,setActionFilter]=useState('');const[userId,setUserId]=useState('');

  const load=useCallback(()=>{
    const q=new URLSearchParams({page,per_page:50});
    if(risk)q.set('risk_level',risk);
    if(actionFilter)q.set('action',actionFilter);
    if(userId)q.set('staff_user_id',userId);
    fetch(`${api}/v1/staff/audit?${q}`,{credentials:'include'})
      .then(r=>r.json()).then(setLogs).catch(()=>setErr('Failed to load audit log'));
  },[api,page,risk,actionFilter,userId]);

  useEffect(()=>{load();},[load]);

  if(err)return<div style={s.error}>! {err}</div>;
  return(
    <>
      <div style={s.filterRow}>
        <input style={{...s.input,minWidth:160}} placeholder="Filter by action..." value={actionFilter} onChange={e=>{setActionFilter(e.target.value);setPage(1);}}/>
        <input style={{...s.input,minWidth:180}} placeholder="Staff user ID..." value={userId} onChange={e=>{setUserId(e.target.value);setPage(1);}}/>
        <select style={s.select} value={risk} onChange={e=>{setRisk(e.target.value);setPage(1);}}>
          <option value="">All risk levels</option>
          {['LOW','HIGH','CRITICAL'].map(v=><option key={v} value={v}>{v}</option>)}
        </select>
        <button style={s.btn('primary')} onClick={()=>{setPage(1);load();}}>Search</button>
      </div>
      {!logs?<Loader/>:(
        <TableCard title="Staff Audit Log" count={logs.total}>
          <table style={s.table}>
            <thead><tr>{['Action','Staff','Role','Target','Risk','IP','When'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {(logs.logs||[]).map(l=>{
                const rm=ROLE_META[l.active_role]||{color:T.textSub,label:l.active_role||'-'};
                const TARGET_TYPE_MAP={
                  staff_user:'user', staff_session:'session', support_ticket:'ticket',
                  job:'job', provider:'provider', queue:'queue',
                  automation_incident:'incident', recovery_rule:'automation_rule',
                  autonomous_execution:'autonomous_execution',
                  operational_incident:'incident',
                  communication_thread:'communication_thread',
                  communication_message:'communication_message',
                };
                const mappedTargetType=l.target_type?TARGET_TYPE_MAP[l.target_type]:null;
                return(
                  <tr key={l.id}>
                    <td style={{...s.td,fontSize:14,fontFamily:mono,maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.action}</td>
                    <td style={{...s.td,fontSize:14,color:T.textSub}}><EntityChip type="user" id={l.staff_user_id} label={l.staff_user_id?.slice(0,8)}/></td>
                    <td style={s.td}><span style={{fontSize:13,fontWeight:700,color:rm.color}}>{rm.label||'-'}</span></td>
                    <td style={{...s.td,fontSize:14,color:T.textSub}}>
                      {mappedTargetType&&l.target_id
                        ? <EntityChip type={mappedTargetType} id={l.target_id} label={`${l.target_type}:${l.target_id?.slice(0,8)}`}/>
                        : l.target_type&&l.target_id?`${l.target_type}:${l.target_id?.slice(0,8)}`:'-'}
                    </td>
                    <td style={s.td}><span style={s.badge(l.risk_level==='CRITICAL'?'red':l.risk_level==='HIGH'?'amber':'blue')}>{l.risk_level||'LOW'}</span></td>
                    <td style={{...s.td,fontSize:14,fontFamily:mono,color:T.textSub}}>{l.ip_address||'-'}</td>
                    <td style={{...s.td,fontSize:14,whiteSpace:'nowrap'}}>{fmtAgo(l.created_at)}</td>
                  </tr>
                );
              })}
              {(logs.logs||[]).length===0&&<tr><td colSpan={7} style={s.empty}>No audit entries found</td></tr>}
            </tbody>
          </table>
          <Pager page={logs.page} pages={logs.pages} total={logs.total} onPage={setPage}/>
        </TableCard>
      )}
    </>
  );
}

const styles = `
.cc-field::placeholder{color:#B9C5D4;opacity:1;}

  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .ad-root {
    display: flex;
    min-height: 100vh;
    background: #080B12;
    font-family: 'DM Sans', sans-serif;
    color: #fff;
  }

  /* ---- LEFT COLUMN (primary nav) ---- */
  .ad-sidebar {
    width: 210px;
    flex-shrink: 0;
    background: linear-gradient(180deg, #0e1520 0%, #090d15 60%, #070b12 100%);
    border-right: 1px solid rgba(148, 163, 184, 0.12);
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 100;
    transition: transform 0.3s ease;
  }

  .ad-sidebar.mobile-hidden {
    transform: translateX(-100%);
  }
  .ad-sidebar.nav-collapsed {
    transform: translateX(-100%);
  }

  .ad-sidebar-logo {
    padding: 18px 14px 16px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.10);
    background: linear-gradient(180deg, rgba(14,21,36,0.95) 0%, rgba(10,15,26,0.8) 100%);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  .ad-logo-mark {
    width: 30px;
    height: 30px;
    background: linear-gradient(135deg, #2D6CFF, #1D4ED8);
    border-radius: 7px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Syne', sans-serif;
    font-size: 11px;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.5px;
    flex-shrink: 0;
  }

  .ad-logo-text {
    font-family: 'Syne', sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.04em;
    line-height: 1.2;
  }

  .ad-logo-badge {
    font-size: 10px;
    font-weight: 600;
    color: rgba(148, 163, 184, 0.5);
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  /* Primary section nav */
  .ad-primary-nav {
    flex: 1;
    overflow-y: auto;
    padding: 8px 8px;
  }

  .ad-primary-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    height: 42px;
    padding: 0 14px;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    background: none;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    text-align: left;
    font-family: 'DM Sans', sans-serif;
    transition: all 0.15s ease;
    letter-spacing: 0.01em;
    line-height: 1.2;
    overflow: visible;
    margin-bottom: 2px;
    box-sizing: border-box;
  }
  .ad-primary-item:hover { background: rgba(45,108,255,0.18); color: #ffffff; }
  .ad-primary-item.active {
    background: linear-gradient(180deg, #2D6CFF 0%, #1B57F5 100%);
    color: #ffffff;
    font-weight: 500;
  }

  .ad-sidebar-footer {
    padding: 10px 8px 12px;
    border-top: 1px solid rgba(148, 163, 184, 0.10);
    background: linear-gradient(180deg, rgba(8,12,20,0.6) 0%, rgba(6,9,16,0.85) 100%);
    flex-shrink: 0;
  }

  .ad-logout-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 7px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    color: #ffffff;
    background: none;
    border: none;
    width: 100%;
    text-align: left;
    transition: background 0.18s ease, color 0.18s ease;
    font-family: 'DM Sans', sans-serif;
  }
  .ad-logout-btn:hover { background: rgba(239,68,68,0.1); color: #fca5a5; }

  .vi-nav-collapse-btn {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 14px;
    height: 42px;
    border-radius: 7px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0.01em;
    color: #FFFFFF;
    background: linear-gradient(180deg, #1565FF 0%, #0F52E8 100%);
    border: 1px solid rgba(147,197,253,0.4);
    width: 100%;
    text-align: left;
    transition: background 0.15s, box-shadow 0.15s;
    font-family: 'DM Sans', sans-serif;
    margin-bottom: 6px;
    box-shadow: 0 2px 8px rgba(21,101,255,0.3);
    white-space: nowrap;
    overflow: hidden;
    box-sizing: border-box;
    line-height: 1.2;
  }
  .vi-nav-collapse-btn:hover {
    background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
    box-shadow: 0 3px 12px rgba(21,101,255,0.45);
  }

  /* ---- RIGHT COLUMN (contextual nav) ---- */
  .ad-right-col {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 320px;
    z-index: 9080;
    background: linear-gradient(180deg, #0b1220 0%, #090d15 100%);
    border-left: 2px solid rgba(45,108,255,0.45);
    box-shadow: -8px 0 40px rgba(0,0,0,0.55);
    display: flex;
    flex-direction: column;
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.32,0.72,0,1);
    pointer-events: none;
  }
  .ad-right-col.rc-open {
    transform: translateX(0);
    pointer-events: auto;
  }

  .ad-rc-header {
    padding: 14px 14px 12px;
    border-bottom: 1px solid rgba(148,163,184,0.08);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    min-height: 52px;
    background: linear-gradient(180deg, rgba(21,101,255,0.12) 0%, transparent 100%);
  }

  .ad-rc-guidance-btn {
    display: flex;
    align-items: center;
    gap: 7px;
    background: rgba(45,108,255,0.08);
    border: 1px solid rgba(45,108,255,0.25);
    border-radius: 7px;
    padding: 6px 10px;
    cursor: pointer;
    font-size: 11px;
    font-weight: 700;
    color: #2D6CFF;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-family: 'DM Sans', sans-serif;
    width: 100%;
    transition: background 0.15s;
  }
  .ad-rc-guidance-btn:hover { background: rgba(45,108,255,0.16); }
  .ad-rc-guidance-btn.active { background: rgba(45,108,255,0.2); }

  .ad-rc-collapse-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: rgba(148,163,184,0.4);
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    transition: color 0.15s;
  }
  .ad-rc-collapse-btn:hover { color: #2D6CFF; }

  .ad-rc-section-label {
    padding: 12px 14px 8px;
    font-size: 13px;
    font-weight: 800;
    color: #FFFFFF;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-family: 'DM Sans', sans-serif;
  }

  .ad-rc-nav {
    flex: 1;
    overflow-y: auto;
    padding: 8px 10px 8px;
  }

  .ad-rc-item {
    display: flex;
    align-items: center;
    width: 100%;
    height: 40px;
    padding: 0 12px;
    font-size: 14px;
    font-weight: 500;
    color: #ffffff;
    background: linear-gradient(180deg, rgba(45,108,255,0.18), rgba(27,87,245,0.12));
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 9px;
    cursor: pointer;
    text-align: left;
    font-family: 'DM Sans', sans-serif;
    transition: all 0.14s ease;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: 0.01em;
    line-height: 1.2;
    margin-bottom: 4px;
    box-sizing: border-box;
  }
  .ad-rc-item:hover {
    background: linear-gradient(180deg, rgba(45,108,255,0.30), rgba(27,87,245,0.22));
    border-color: rgba(255,255,255,0.22);
  }
  .ad-rc-item.active {
    background: linear-gradient(180deg, #2D6CFF 0%, #1B57F5 100%);
    color: #ffffff;
    font-weight: 600;
    border-color: rgba(255,255,255,0.25);
  }

  .ad-right-col.collapsed .ad-rc-header,
  .ad-right-col.collapsed .ad-rc-section-label,
  .ad-right-col.collapsed .ad-rc-nav { display: none; }
  .ad-right-col.collapsed .ad-rc-collapse-only { display: flex; align-items: center; justify-content: center; padding: 14px 0; }
  .ad-rc-collapse-only { display: none; }

  /* ---- MAIN ---- */
  .ad-main {
    flex: 1;
    margin-left: 210px;
    margin-right: 0;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    transition: margin-left 0.3s ease;
  }
  .ad-main.nav-collapsed { margin-left: 0; }


  /* ---- TOPBAR ---- */
  .ad-topbar {
    border-bottom: 1px solid rgba(255,255,255,0.06);
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;
    padding: 0 20px 0 28px;
    height: 64px;
    background: rgba(8,11,18,0.95);
    backdrop-filter: blur(12px);
    position: sticky;
    top: 0;
    z-index: 50;
  }

  .ad-hamburger {
    display: none;
    flex-shrink: 0;
    background: none;
    border: none;
    color: rgba(255,255,255,0.5);
    cursor: pointer;
    padding: 6px;
    border-radius: 8px;
  }

  .ad-nav-toggle {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.7);
    cursor: pointer;
    padding: 0;
    border-radius: 9px;
    transition: all 0.15s ease;
  }
  .ad-nav-toggle:hover { background: rgba(45,108,255,0.14); border-color: rgba(45,108,255,0.4); color: #fff; }

  .ad-panel-toggle {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 15px;
    background: linear-gradient(180deg, #1565FF 0%, #0F52E8 100%);
    border: 1px solid rgba(147,197,253,0.35);
    color: #FFFFFF;
    cursor: pointer;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    letter-spacing: 0.01em;
    line-height: 1.2;
    box-sizing: border-box;
    white-space: nowrap;
    box-shadow: 0 2px 8px rgba(21,101,255,0.30);
    transition: background 0.15s, box-shadow 0.15s;
  }
  .ad-panel-toggle:hover { background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%); box-shadow: 0 3px 12px rgba(21,101,255,0.45); }
  .ad-panel-toggle.active { background: linear-gradient(180deg, #0F52E8 0%, #0d47c8 100%); border-color: rgba(147,197,253,0.6); }

  /* SCC content-area action row + cluster (dashboard + section drawer tabs) */
  .scc-action-row {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: center;
    column-gap: 12px;
    row-gap: 8px;
    margin-bottom: 16px;
    box-sizing: border-box;
  }
  .scc-action-cluster {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-end;
    align-items: flex-start;
    gap: 8px;
  }
  .scc-action-spacer { flex: 1 1 0; min-width: 0; }

  .vi-hdr-panel-btn {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 0 14px;
    height: 46px;
    border-radius: 7px;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0.01em;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    background: linear-gradient(180deg, #1565FF 0%, #0F52E8 100%);
    border: 1px solid rgba(147,197,253,0.35);
    color: #FFFFFF;
    box-shadow: 0 2px 8px rgba(21,101,255,0.3);
    transition: background 0.15s, box-shadow 0.15s;
    flex-shrink: 0;
    white-space: nowrap;
    pointer-events: all;
    line-height: 1.2;
    box-sizing: border-box;
  }
  .vi-hdr-panel-btn:hover {
    background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
    box-shadow: 0 3px 12px rgba(21,101,255,0.45);
  }
  .vi-hdr-panel-btn.active {
    background: linear-gradient(180deg, #0F52E8 0%, #0d47c8 100%);
    border-color: rgba(147,197,253,0.6);
  }
  @media (max-width: 768px) {
    .vi-hdr-panel-btn { height: 40px; font-size: 14px; padding: 0 12px; gap: 8px; }
  }

  .ad-topbar-title {
    flex-shrink: 0;
    font-family: 'Syne', sans-serif;
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
  }

  .ad-topbar-search {
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .ad-topbar-right {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .ad-role-pill {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    font-family: 'Syne', sans-serif;
    letter-spacing: 0.3px;
    cursor: pointer;
    border: none;
    transition: opacity 0.2s;
    position: relative;
  }

  .ad-role-pill:hover { opacity: 0.85; }

  .ad-role-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .ad-role-switcher {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: #12161F;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 12px;
    padding: 8px;
    min-width: 200px;
    box-shadow: 0 16px 40px rgba(0,0,0,0.5);
    z-index: 200;
    animation: ad-dropdown 0.2s ease both;
  }

  @keyframes ad-dropdown {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .ad-switcher-label {
    font-size: 10px;
    color: rgba(255,255,255,0.55);
    letter-spacing: 1px;
    text-transform: uppercase;
    padding: 4px 10px 8px;
  }

  .ad-switcher-role {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 10px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    color: rgba(255,255,255,0.6);
    transition: background 0.15s;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
  }

  .ad-switcher-role:hover { background: rgba(255,255,255,0.05); color: #fff; }
  .ad-switcher-role.current { color: #fff; }

  .ad-user-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .ad-avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 13px;
    flex-shrink: 0;
  }

  .ad-user-name {
    font-size: 14px;
    font-weight: 500;
    color: rgba(255,255,255,0.7);
    white-space: nowrap;
  }

  /* ---- CONTENT ---- */
  .ad-content {
    flex: 1;
    padding: 32px 28px;
    width: 100%;
  }

  .ad-welcome {
    margin-bottom: 32px;
  }

  .ad-welcome-title {
    font-family: 'Syne', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.5px;
    margin-bottom: 6px;
  }

  .ad-welcome-sub {
    font-size: 14px;
    color: rgba(255,255,255,0.65);
  }

  .ad-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
  }

  .ad-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 16px;
    padding: 24px;
    transition: border-color 0.2s;
  }

  .ad-card:hover { border-color: rgba(255,255,255,0.12); }

  .ad-card-label {
    font-size: 11px;
    font-weight: 500;
    color: rgba(255,255,255,0.6);
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  .ad-card-value {
    font-family: 'Syne', sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 4px;
  }

  .ad-card-sub {
    font-size: 12px;
    color: rgba(255,255,255,0.6);
  }

  .ad-status-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 18px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 12px;
    margin-bottom: 10px;
  }

  .ad-status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .ad-status-dot.green { background: #4ADE80; box-shadow: 0 0 6px rgba(74,222,128,0.4); }
  .ad-status-dot.gold  { background: #2D6CFF; box-shadow: 0 0 6px rgba(45,108,255,0.4); }

  .ad-status-text {
    font-size: 13px;
    color: rgba(255,255,255,0.6);
    flex: 1;
  }

  .ad-status-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
  }

  .ad-overlay {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.6);
    z-index: 90;
  }

  .ad-overlay.visible { display: block; }

  @media (max-width: 1200px) {
    .ad-right-col { width: 300px; }
    .ad-content { padding: 26px 22px; }
  }

  @media (max-width: 960px) {
    .ad-sidebar { width: 200px; }
    .ad-main { margin-left: 200px; }
    .ad-main.nav-collapsed { margin-left: 0; }
    .ad-right-col { width: 280px; }
    .ad-content { padding: 22px 18px; }
    .ad-topbar { gap: 12px; }
  }

  @media (max-width: 768px) {
    .ad-sidebar { transform: translateX(-100%); }
    .ad-sidebar.mobile-open { transform: translateX(0); }
    .ad-sidebar.nav-collapsed { transform: translateX(-100%); }
    .ad-main { margin-left: 0; margin-right: 0; }
    .ad-main.nav-collapsed { margin-left: 0; }
    .ad-right-col { width: 86vw; max-width: 340px; }
    .ad-hamburger { display: flex; }
    .ad-nav-toggle { display: none; }
    .ad-content { padding: 20px 14px; }
    .ad-topbar { padding: 0 12px; height: 56px; gap: 10px; }
    .ad-topbar-title { font-size: 15px; }
    .ad-topbar-search { display: none; }
    .ad-user-name { display: none; }
  }

  /* ---- ENTERPRISE SCROLLBAR SYSTEM ---- */

  /* Chromium: global */
  ::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }
  ::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.6);
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(45, 108, 255, 0.45);
    border-radius: 10px;
    transition: background 0.2s;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(45, 108, 255, 0.65);
    box-shadow: 0 0 6px rgba(45, 108, 255, 0.3);
  }
  ::-webkit-scrollbar-corner {
    background: transparent;
  }

  /* Firefox */
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(45, 108, 255, 0.45) rgba(15, 23, 42, 0.6);
  }

  /* Smooth scrolling for content areas */
  .ad-nav,
  .ad-content,
  .ad-main {
    scroll-behavior: smooth;
  }


  /* Table hover + sticky header */
  table tbody tr:hover td { background: rgba(255,255,255,0.015); }
  table thead th { box-shadow: 0 1px 0 rgba(255,255,255,0.06); }
`;


// All operational sections grouped for navigation.
// requiredPermission: if set, item is hidden unless hasPermission(requiredPermission).
// If no requiredPermission, item is always visible.
const ALL_NAV = [
  {section:'Executive',items:[
    {id:'exec_overview',   label:'Executive Overview',   requiredPermission:'executive.view'},
    {id:'exec_health',     label:'Business Health',      requiredPermission:'executive.view'},
    {id:'exec_signals',    label:'Growth Signals',       requiredPermission:'executive.view'},
    {id:'exec_forecast',   label:'Forecasting',          requiredPermission:'executive.view'},
    {id:'exec_intel',      label:'Intel Foundation',     requiredPermission:'data_intelligence.view'},
  ]},
  {section:'Overview',items:[
    {id:'overview',  label:'Dashboard'},
    {id:'activity',  label:'Live Activity'},
    {id:'health',    label:'System Health',  requiredPermission:'system.health.view'},
  ]},
  {section:'Revenue',items:[
    {id:'plans',     label:'Plans & Revenue',  requiredPermission:'finance.view'},
    {id:'margin',    label:'Cost vs Revenue',   requiredPermission:'analytics.costs'},
    {id:'credits',   label:'Credit Monitor',    requiredPermission:'credits.view'},
    // Unified commercial surface over payment_ledger: every paid product, not just the three
    // families the legacy revenue endpoints covered.
    {id:'commerce_summary', label:'Commercial Summary', requiredPermission:'finance.view'},
    {id:'commerce_sales',   label:'Sales & Transactions', requiredPermission:'finance.view'},
    {id:'commerce_credits', label:'Shop Credit Activity', requiredPermission:'finance.view'},
    {id:'commerce_recon',   label:'Reconciliation',       requiredPermission:'finance.view'},
  ]},
  {section:'Commercial Intelligence',items:[
    {id:'ci_revenue',      label:'Revenue Intelligence',   requiredPermission:'commercial_intelligence.financials'},
    {id:'ci_subscribers',  label:'Subscriber Intelligence', requiredPermission:'commercial_intelligence.view'},
    {id:'ci_conversion',   label:'Conversion Intelligence', requiredPermission:'commercial_intelligence.view'},
    {id:'ci_acquisition',  label:'Acquisition Intelligence',requiredPermission:'commercial_intelligence.financials'},
    {id:'ci_retention',    label:'Retention Intelligence',  requiredPermission:'commercial_intelligence.view'},
    {id:'ci_ecosystem',    label:'Ecosystem Adoption',      requiredPermission:'commercial_intelligence.view'},
    {id:'ci_health',       label:'Product Health',          requiredPermission:'commercial_intelligence.view'},
    {id:'ci_target',       label:'Owner Target Tracker',    requiredPermission:'commercial_intelligence.executive'},
    {id:'ci_cohorts',       label:'Cohort Intelligence',     requiredPermission:'commercial_intelligence.view'},
    {id:'ci_ltv',           label:'Product LTV',             requiredPermission:'commercial_intelligence.financials'},
    {id:'ci_migration',     label:'Cross Product Migration',  requiredPermission:'commercial_intelligence.view'},
  ]},
  {section:'Marketing & Community',items:[
    {id:'mc_channels',    label:'Social Channels',    requiredPermission:'marketing_community.view'},
    {id:'mc_ci',          label:'Channel Intelligence',requiredPermission:'channel_intelligence.view'},
    {id:'mc_calendar',    label:'Content Calendar',   requiredPermission:'marketing_community.view'},
    {id:'mc_activity',    label:'Marketing Activity', requiredPermission:'marketing_community.view'},
    {id:'mc_campaigns',   label:'Campaigns',          requiredPermission:'marketing_community.manage'},
    {id:'mc_impact',      label:'Campaign Impact',    requiredPermission:'marketing_community.view'},
    {id:'mc_traffic',     label:'Traffic Sources',    requiredPermission:'marketing_community.view'},
    {id:'mc_community',   label:'Community Growth',   requiredPermission:'marketing_community.view'},
    {id:'mc_releases',    label:'Release Tracker',    requiredPermission:'marketing_community.view'},
    {id:'mc_email',       label:'Email Campaigns',    requiredPermission:'marketing_community.view'},
    {id:'mc_release_impact',label:'Release Impact',     requiredPermission:'marketing_community.view'},
    {id:'mc_voting',        label:'Community Voting',   requiredPermission:'marketing_community.view'},
  ]},
  {section:'Generator Intelligence',items:[
    {id:'gi_performance',  label:'Generator Performance', requiredPermission:'analytics.view'},
    {id:'gi_engines',      label:'Engine Performance',    requiredPermission:'analytics.view'},
    {id:'gi_trends',       label:'Usage Trends',          requiredPermission:'analytics.view'},
    {id:'gi_revenue',      label:'Generator Revenue',     requiredPermission:'analytics.costs'},
    {id:'gi_attribution',  label:'Generator Attribution', requiredPermission:'analytics.view'},
  ]},
  {section:'Slug Intelligence',items:[
    {id:'si_performance',  label:'Slug Performance',      requiredPermission:'analytics.view'},
    {id:'si_families',     label:'Slug Families',         requiredPermission:'analytics.view'},
    {id:'si_demand',       label:'Community Demand',      requiredPermission:'analytics.view'},
    {id:'si_planning',     label:'Future Planning',       requiredPermission:'analytics.view'},
  ]},
  {section:'Usage',items:[
    {id:'generations',label:'Generations',      requiredPermission:'analytics.view'},
    {id:'generators', label:'Generator Stats',  requiredPermission:'generators.view'},
    {id:'ledger',     label:'Usage Ledger',     requiredPermission:'analytics.view'},
    {id:'costs',      label:'Cost Analytics',   requiredPermission:'analytics.costs'},
  ]},
  {section:'Users & Jobs',items:[
    {id:'users',     label:'Users',             requiredPermission:'user.view'},
    {id:'jobs',      label:'Job Monitor',       requiredPermission:'jobs.view'},
    {id:'tickets',   label:'Support Tickets',   requiredPermission:'ticket.view'},
    {id:'actions',   label:'User Actions',      requiredPermission:'user.view'},
    {id:'rewards',   label:'Reward Moderation', requiredPermission:'rewards.view'},
  ]},
  {section:'Governance',items:[
    {id:'staff',     label:'Staff Accounts',    requiredPermission:'staff.view'},
    {id:'sessions',  label:'Staff Sessions',    requiredPermission:'session.view'},
    {id:'security',  label:'Security Center',   requiredPermission:'security.view'},
    {id:'audit',     label:'Audit Log',         requiredPermission:'audit.view'},
  ]},
  {section:'DCC Admin',items:[
    {id:'dcc_overview', label:'Overview',        requiredPermission:'dcc.view'},
    {id:'dcc_news',     label:'News & Updates',  requiredPermission:'dcc.view'},
  ]},
  {section:'Virtual Shop',items:[
    {id:'virtual_shop', label:'Shop Management', requiredRole:'OWNER'},
  ]},
  {section:'Communications',items:[
    {id:'comms_threads',    label:'Threads',           requiredPermission:'comms.view'},
    {id:'comms_inboxes',    label:'Inboxes',           requiredPermission:'comms.view'},
    {id:'comms_overview',   label:'Overview',         requiredPermission:'comms.view'},
    {id:'comms_queue',      label:'Mail Queue',        requiredPermission:'comms.view'},
    {id:'comms_delivery',   label:'Delivery Monitor',  requiredPermission:'comms.view'},
    {id:'comms_suppression',label:'Suppression List',  requiredPermission:'comms.view'},
    {id:'comms_templates',  label:'Templates',         requiredPermission:'comms.view'},
    {id:'gmail_mailboxes',  label:'Gmail Connector',   requiredPermission:'comms.manage'},
  ]},
  {section:'User Intelligence',items:[
    {id:'intel',label:'User Intelligence',requiredPermission:'intelligence.view'},
  ]},
  {section:'Products',items:[
    {id:'products_overview', label:'Overview',  requiredPermission:'products.view'},
    {id:'products_registry', label:'Registry',  requiredPermission:'products.view'},
  ]},
  {section:'Support',items:[
    {id:'support_queue',    label:'Queue',      requiredPermission:'support.view'},
    {id:'support_assigned', label:'Assigned',   requiredPermission:'support.view'},
    {id:'support_escalated',label:'Escalated',  requiredPermission:'support.view'},
    {id:'support_resolved', label:'Resolved',   requiredPermission:'support.view'},
    {id:'support_analytics',label:'Analytics',  requiredPermission:'support.analytics.view'},
  ]},
  {section:'Platform Ops',items:[
    {id:'platform_overview', label:'Overview',   requiredPermission:'platform.view'},
    {id:'platform_jobs',     label:'Jobs',       requiredPermission:'platform.view'},
    {id:'platform_providers',label:'Providers',  requiredPermission:'platform.view'},
    {id:'platform_failures', label:'Failures',   requiredPermission:'platform.view'},
    {id:'platform_credits',  label:'Credits',    requiredPermission:'platform.view'},
    {id:'platform_queues',   label:'Queues',     requiredPermission:'platform.view'},
  ]},
  {section:'Autonomous Ops',items:[
    {id:'autonomous_overview',  label:'Overview',         requiredPermission:'autonomous.view'},
    {id:'autonomous_rules',     label:'Recovery Rules',   requiredPermission:'autonomous.view'},
    {id:'autonomous_queue',     label:'Queue Recovery',   requiredPermission:'autonomous.view'},
    {id:'autonomous_provider',  label:'Provider Failover',requiredPermission:'autonomous.view'},
    {id:'autonomous_credits',   label:'Credit Protection',requiredPermission:'autonomous.view'},
    {id:'autonomous_security',  label:'Security Response',requiredPermission:'autonomous.view'},
    {id:'autonomous_history',   label:'Execution History',requiredPermission:'autonomous.view'},
  ]},
  {section:'Automation',items:[
    {id:'auto_overview',    label:'Overview',    requiredPermission:'automation.view'},
    {id:'auto_rules',       label:'Rules',       requiredPermission:'automation.rules'},
    {id:'auto_triggers',    label:'Triggers',    requiredPermission:'automation.view'},
    {id:'auto_incidents',   label:'Incidents',   requiredPermission:'automation.incidents'},
    {id:'auto_suggestions', label:'Suggestions', requiredPermission:'automation.view'},
    {id:'auto_history',     label:'History',     requiredPermission:'automation.history'},
  ]},
  {section:'Technicians',items:[
    {id:'tech_overview',      label:'Overview',      requiredPermission:'technicians.view'},
    {id:'tech_applications',  label:'Applications',  requiredPermission:'technicians.view'},
    {id:'tech_submissions',   label:'Technician Submissions', requiredPermission:'technicians.view'},
    {id:'tech_accounts',      label:'Accounts',      requiredPermission:'technicians.view'},
    {id:'tech_verification',  label:'Verification',  requiredPermission:'technicians.view'},
    {id:'tech_capabilities',  label:'Capabilities',  requiredPermission:'technicians.view'},
    {id:'tech_audit',         label:'Audit',         requiredPermission:'technicians.view'},
  ]},
];

// Primary sections shown in the left column — maps to ALL_NAV section labels
const PRIMARY_SECTIONS = [
  { id: 'Executive',              label: 'Executive'        },
  { id: 'Overview',          label: 'Overview'         },
  { id: 'Revenue',           label: 'Revenue'          },
  { id: 'Commercial Intelligence', label: 'Commercial Intel' },
  { id: 'Generator Intelligence',  label: 'Generator Intel'  },
  { id: 'Slug Intelligence',       label: 'Slug Intel'       },
  { id: 'Marketing & Community', label: 'Marketing'        },
  { id: 'Usage',             label: 'Usage'            },
  { id: 'Users & Jobs',      label: 'Users & Jobs'     },
  { id: 'Technicians',       label: 'Technicians'      },
  { id: 'Governance',        label: 'Governance'       },
  { id: 'DCC Admin',         label: 'DCC Admin'        },
  { id: 'Virtual Shop',      label: 'Virtual Shop'     },
  { id: 'Communications',    label: 'Communications'   },
  { id: 'User Intelligence', label: 'User Intel'       },
  { id: 'Products',          label: 'Products'         },
  { id: 'Support',           label: 'Support'          },
  { id: 'Platform Ops',      label: 'Platform Ops'     },
  { id: 'Autonomous Ops',    label: 'Autonomous Ops'   },
  { id: 'Automation',        label: 'Automation'       },
];

// Map each subsection id to its primary section label
const SUBSECTION_TO_PRIMARY = {};
ALL_NAV.forEach(group => {
  group.items.forEach(item => {
    SUBSECTION_TO_PRIMARY[item.id] = group.section;
  });
});

// Persistence helpers
const _LS_PRIMARY  = 'vi_nav_primary';
const _LS_SECTION  = 'vi_nav_section';
const _LS_RC_OPEN  = 'vi_nav_rc_open';
const _lsGet = (k, def) => { try { const v = localStorage.getItem(k); return v !== null ? JSON.parse(v) : def; } catch { return def; } };
const _lsSet = (k, v)   => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };

const VIRTUAL_SHOP_ADMIN_PATH = '/admin/virtual-shop';
const adminSectionFromPath = (pathname) => {
  const raw = pathname.replace('/admin/', '').replace('/admin', '') || 'overview';
  return raw === 'virtual-shop' ? 'virtual_shop' : raw;
};
const adminSectionFromLocation = (location) => {
  const fromPath = adminSectionFromPath(location.pathname);
  if (fromPath !== 'dashboard') return fromPath;
  return typeof location.state?.adminSection === 'string' ? location.state.adminSection : 'overview';
};

// ---------------------------------------------------------------------------
// GlobalCommandBar — enterprise command palette (CMD+K / CTRL+K)
// ---------------------------------------------------------------------------
// Local storage key for recent ops items
const CMD_RECENT_KEY = 'visinvoke_cmd_recent';
const CMD_RECENT_MAX = 15;

function _loadRecent() {
  try { return JSON.parse(localStorage.getItem(CMD_RECENT_KEY) || '[]'); } catch { return []; }
}
function _saveRecent(items) {
  try { localStorage.setItem(CMD_RECENT_KEY, JSON.stringify(items.slice(0, CMD_RECENT_MAX))); } catch {}
}
function _addRecent(item) {
  const prev = _loadRecent().filter(r => r.id !== item.id);
  _saveRecent([item, ...prev]);
}

// Result type config
const CMD_TYPE_CONFIG = {
  user:      { label: 'USER',      color: '#3182ce', icon: 'U' },
  ticket:    { label: 'TICKET',    color: '#805ad5', icon: 'T' },
  thread:    { label: 'THREAD',    color: '#2b6cb0', icon: 'TH' },
  job:       { label: 'JOB',       color: '#2b6cb0', icon: 'J' },
  failure:   { label: 'FAILURE',   color: '#c53030', icon: 'F' },
  generator: { label: 'GEN',       color: '#276749', icon: 'G' },
  session:   { label: 'SESSION',   color: '#553c9a', icon: 'S' },
  staff:     { label: 'STAFF',     color: '#b7791f', icon: 'ST' },
  audit:     { label: 'AUDIT',     color: '#b7791f', icon: 'A' },
  email:     { label: 'EMAIL',     color: '#2b6cb0', icon: 'E' },
  recent:    { label: 'RECENT',    color: '#8b97a8', icon: '↩' },
  nav:       { label: 'NAV',       color: '#8b97a8', icon: '→' },
};

const CMD_SEVERITY_COLOR = {
  critical: '#e53e3e',
  high:     '#c05621',
  medium:   '#b7791f',
  low:      '#276749',
};

// Quick nav items - always available
const CMD_NAV_ITEMS = [
  { type:'nav', id:'overview',         title:'Dashboard Overview',      subtitle:'Platform overview',           section:'overview' },
  { type:'nav', id:'users',            title:'Users',                   subtitle:'Customer accounts',           section:'users' },
  { type:'nav', id:'support_queue',    title:'Support Queue',           subtitle:'Open tickets',                section:'support_queue' },
  { type:'nav', id:'support_analytics',title:'Support Analytics',       subtitle:'Volume, response, per product',section:'support_analytics' },
  { type:'nav', id:'products_overview',title:'Products',                subtitle:'Every product, paid and free',  section:'products_overview' },
  { type:'nav', id:'intel',            title:'User Intelligence',       subtitle:'Unified user timeline',       section:'intel' },
  { type:'nav', id:'platform_overview',title:'Platform Ops Overview',   subtitle:'Generation telemetry',        section:'platform_overview' },
  { type:'nav', id:'platform_jobs',    title:'Jobs Monitor',            subtitle:'Generation jobs',             section:'platform_jobs' },
  { type:'nav', id:'platform_failures',title:'Failure Center',          subtitle:'Operational failures',        section:'platform_failures' },
  { type:'nav', id:'platform_queues',  title:'Queue Monitor',           subtitle:'Queue depth and health',      section:'platform_queues' },
  { type:'nav', id:'comms_overview',   title:'Communications Overview', subtitle:'Email delivery telemetry',    section:'comms_overview' },
  { type:'nav', id:'comms_queue',      title:'Mail Queue',              subtitle:'Mail event log',              section:'comms_queue' },
  { type:'nav', id:'staff',            title:'Staff Accounts',          subtitle:'Staff management',            section:'staff' },
  { type:'nav', id:'audit',            title:'Audit Log',               subtitle:'Staff action history',        section:'audit' },
];

// Single result row
function CmdResultRow({ item, selected, onSelect, onHover }) {
  const tc = CMD_TYPE_CONFIG[item.type] || CMD_TYPE_CONFIG.nav;
  const sevColor = CMD_SEVERITY_COLOR[item.severity] || null;

  return (
    <div
      onClick={() => onSelect(item)}
      onMouseEnter={onHover}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 16px', cursor: 'pointer',
        background: selected ? 'rgba(45,108,255,0.10)' : 'transparent',
        borderLeft: selected ? '2px solid rgba(45,108,255,0.7)' : '2px solid transparent',
        transition: 'background 0.1s',
      }}
    >
      {/* Type badge */}
      <span style={{
        flexShrink: 0, width: 44, textAlign: 'center',
        fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
        color: tc.color, background: tc.color + '18',
        border: `1px solid ${tc.color}40`,
        borderRadius: 4, padding: '2px 4px',
      }}>
        {tc.label}
      </span>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 600,
          color: selected ? '#2D6CFF' : '#e2e8f0',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {item.title}
        </div>
        {item.subtitle && (
          <div style={{
            fontSize: 12, color: 'rgba(255,255,255,0.6)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {item.subtitle}
          </div>
        )}
      </div>

      {/* Status + severity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {item.severity && item.severity !== 'low' && (
          <span style={{
            fontSize: 10, fontWeight: 800, color: sevColor,
            background: sevColor + '20', border: `1px solid ${sevColor}50`,
            borderRadius: 3, padding: '1px 5px', letterSpacing: '0.05em',
          }}>
            {item.severity.toUpperCase()}
          </span>
        )}
        {item.status && item.type !== 'nav' && (
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
            {item.status}
          </span>
        )}
        {item.meta && (
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.meta}
          </span>
        )}
      </div>
    </div>
  );
}

function GlobalCommandBar({ api, onNavigate, hasPermission }) {
  const [open,       setOpen]       = useState(false);
  const [query,      setQuery]      = useState('');
  const [results,    setResults]    = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [cursor,     setCursor]     = useState(0);
  const [recent,     setRecent]     = useState([]);
  const inputRef     = useRef(null);
  const listRef      = useRef(null);
  const debounceRef  = useRef(null);
  const mountedRef   = useRef(true);

  // Load recent on open
  useEffect(() => { if (open) setRecent(_loadRecent()); }, [open]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // CMD+K / CTRL+K global shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
    } else {
      setQuery('');
      setResults(null);
      setCursor(0);
    }
  }, [open]);

  // Debounced search
  const doSearch = useCallback((q) => {
    if (!q || q.length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetch(`${api}/v1/staff/search/global?q=${encodeURIComponent(q)}`, { credentials: 'include' })
        .then(r => r.json())
        .then(d => {
          if (!mountedRef.current) return;
          setResults(d);
          setLoading(false);
          setCursor(0);
        })
        .catch(() => { if (mountedRef.current) { setResults({}); setLoading(false); } });
    }, 220);
  }, [api]);

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    doSearch(q);
  };

  // Build flat list for keyboard nav
  const flatItems = (() => {
    if (!query) {
      // Show recent + nav shortcuts
      const r = recent.map(i => ({ ...i, _isRecent: true }));
      const n = CMD_NAV_ITEMS.filter(n => hasPermission ? true : true); // nav always shown
      return [...r, ...n].slice(0, 20);
    }
    if (!results) return [];
    const cats = ['users','tickets','threads','jobs','staff','failures','generators','emails','sessions','audits'];
    return cats.flatMap(cat => (results[cat] || []).map(item => ({ ...item, _cat: cat })));
  })();

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor(c => Math.min(c + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(c => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatItems[cursor]) handleSelect(flatItems[cursor]);
    }
  };

  // Map entity type → section for direct navigation on search result select
  const ENTITY_SECTION = {
    user:    'users',
    ticket:  'support_queue',
    thread:  'comms_threads',
    job:     'platform_jobs',
    staff:   'staff',
    failure: 'platform_failures',
    session: 'sessions',
    audit:   'audit',
  };

  const handleSelect = (item) => {
    _addRecent(item);
    setOpen(false);
    const section = item.section || ENTITY_SECTION[item.type];
    if (section) {
      onNavigate(section, item);
    }
  };

  // Group flat results by category for display
  const grouped = (() => {
    if (!query) return null;
    if (!results) return null;
    const cats = [
      { key: 'users',      label: 'Users' },
      { key: 'tickets',    label: 'Support Tickets' },
      { key: 'threads',    label: 'Communication Threads' },
      { key: 'jobs',       label: 'Jobs' },
      { key: 'staff',      label: 'Staff' },
      { key: 'failures',   label: 'Failures' },
      { key: 'generators', label: 'Generators' },
      { key: 'emails',     label: 'Email Events' },
      { key: 'sessions',   label: 'Sessions' },
      { key: 'audits',     label: 'Audit Log' },
    ];
    return cats.filter(c => (results[c.key] || []).length > 0);
  })();

  const isEmpty = query && !loading && results && (results.total === 0 || flatItems.length === 0);

  // Keep cursor visible
  useEffect(() => {
    if (!listRef.current) return;
    const rows = listRef.current.querySelectorAll('[data-cmd-row]');
    if (rows[cursor]) rows[cursor].scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  return (
    <>
      {/* Topbar trigger button */}
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.09)',
          borderRadius: 10, padding: '8px 14px',
          color: 'rgba(255,255,255,0.65)', cursor: 'pointer',
          fontSize: 14, fontFamily: "'DM Sans', sans-serif",
          flex: '1 1 0', maxWidth: 480, minWidth: 200,
          transition: 'border-color 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <span style={{ flex: 1, textAlign: 'left' }}>Search or jump to...</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
          color: 'rgba(255,255,255,0.25)',
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 4, padding: '1px 5px',
        }}>
          {navigator.platform?.includes('Mac') ? '⌘K' : 'Ctrl+K'}
        </span>
      </button>

      {/* Command palette overlay */}
      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9000,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            paddingTop: 80,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div style={{
            width: '100%', maxWidth: 640,
            background: '#0f1623',
            border: '1px solid rgba(148,163,184,0.18)',
            borderRadius: 12,
            boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)',
            overflow: 'hidden',
            maxHeight: '70vh',
            display: 'flex', flexDirection: 'column',
          }}>

            {/* Search input */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '14px 18px',
              borderBottom: '1px solid rgba(148,163,184,0.12)',
              flexShrink: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(148,163,184,0.6)" strokeWidth="2" style={{ flexShrink: 0 }}>
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                ref={inputRef}
                value={query}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Search users, tickets, jobs, failures..."
                style={{
                  flex: 1, background: 'none', border: 'none', outline: 'none',
                  color: '#f1f5f9', fontSize: 16,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              />
              {loading && (
                <span style={{ fontSize: 11, color: 'rgba(148,163,184,0.5)', flexShrink: 0 }}>Searching...</span>
              )}
              {query && !loading && results && (
                <span style={{ fontSize: 11, color: 'rgba(148,163,184,0.4)', flexShrink: 0 }}>{results.total || 0} results</span>
              )}
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 5, color: 'rgba(148,163,184,0.5)', cursor: 'pointer', fontSize: 11, padding: '2px 7px', flexShrink: 0 }}>ESC</button>
            </div>

            {/* Results */}
            <div ref={listRef} style={{ overflowY: 'auto', flex: 1 }}>

              {/* Empty state */}
              {isEmpty && (
                <div style={{ padding: '24px 18px', textAlign: 'center', color: 'rgba(148,163,184,0.4)', fontSize: 14 }}>
                  No results for "{query}"
                </div>
              )}

              {/* Loading state */}
              {loading && (
                <div style={{ padding: '16px 18px', color: 'rgba(148,163,184,0.4)', fontSize: 13 }}>
                  Searching across users, tickets, jobs...
                </div>
              )}

              {/* Grouped results when searching */}
              {!loading && query && grouped && flatItems.length > 0 && (() => {
                let idx = 0;
                return grouped.map(cat => {
                  const items = results[cat.key] || [];
                  return (
                    <div key={cat.key}>
                      <div style={{
                        padding: '8px 16px 4px',
                        fontSize: 10, fontWeight: 800, letterSpacing: '0.12em',
                        color: 'rgba(148,163,184,0.4)', textTransform: 'uppercase',
                        borderTop: '1px solid rgba(148,163,184,0.06)',
                      }}>
                        {cat.label}
                      </div>
                      {items.map(item => {
                        const myIdx = idx++;
                        return (
                          <div key={item.id} data-cmd-row>
                            <CmdResultRow
                              item={item}
                              selected={cursor === myIdx}
                              onSelect={handleSelect}
                              onHover={() => setCursor(myIdx)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                });
              })()}

              {/* Recent + Nav when no query */}
              {!query && (
                <>
                  {recent.length > 0 && (
                    <div>
                      <div style={{ padding: '8px 16px 4px', fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', color: 'rgba(148,163,184,0.4)', textTransform: 'uppercase' }}>
                        Recent
                      </div>
                      {recent.slice(0, 5).map((item, i) => (
                        <div key={item.id + i} data-cmd-row>
                          <CmdResultRow
                            item={{ ...item, type: 'recent' }}
                            selected={cursor === i}
                            onSelect={handleSelect}
                            onHover={() => setCursor(i)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <div>
                    <div style={{ padding: '8px 16px 4px', fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', color: 'rgba(148,163,184,0.4)', textTransform: 'uppercase', borderTop: recent.length > 0 ? '1px solid rgba(148,163,184,0.06)' : 'none' }}>
                      Jump to Section
                    </div>
                    {CMD_NAV_ITEMS.map((item, i) => {
                      const myIdx = recent.slice(0, 5).length + i;
                      return (
                        <div key={item.id} data-cmd-row>
                          <CmdResultRow
                            item={item}
                            selected={cursor === myIdx}
                            onSelect={handleSelect}
                            onHover={() => setCursor(myIdx)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '8px 16px', borderTop: '1px solid rgba(148,163,184,0.08)',
              display: 'flex', gap: 16, flexShrink: 0,
            }}>
              {[['↑↓', 'Navigate'], ['↵', 'Open'], ['ESC', 'Close']].map(([key, label]) => (
                <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'rgba(148,163,184,0.35)' }}>
                  <span style={{ background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.15)', borderRadius: 4, padding: '1px 5px', fontFamily: 'monospace', fontSize: 10 }}>{key}</span>
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// COMMUNICATIONS CENTER -- 5 sections
// ============================================================================

// Status pill config for mail events
const MAIL_STATUS_CONFIG = {
  SENT:       {bg:'#f0fff4',color:'#276749',border:'#9ae6b4',dot:'#38a169'},
  FAILED:     {bg:'#fff5f5',color:'#c53030',border:'#feb2b2',dot:'#e53e3e'},
  SUPPRESSED: {bg:'#fffaf0',color:'#b7791f',border:'#fbd38d',dot:'#d69e2e'},
  PENDING:    {bg:'#ebf8ff',color:'#2b6cb0',border:'#bee3f8',dot:'#3182ce'},
  BOUNCED:    {bg:'#fff5f5',color:'#c53030',border:'#feb2b2',dot:'#e53e3e'},
  COMPLAINT:  {bg:'#faf5ff',color:'#553c9a',border:'#d6bcfa',dot:'#805ad5'},
};

function MailStatusPill({status}){
  const k=(status||'').toUpperCase();
  const c=MAIL_STATUS_CONFIG[k]||{bg:T.bg,color:T.textSub,border:T.border,dot:T.textFaint};
  return(
    <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:5,minHeight:32,boxSizing:'border-box',padding:'0 12px',borderRadius:6,fontSize:13,fontWeight:500,whiteSpace:'nowrap',background:c.bg,color:c.color,border:`1px solid ${c.border}`}}>
      <span style={{width:6,height:6,borderRadius:'50%',background:c.dot,flexShrink:0}}/>
      {k}
    </span>
  );
}

function SuppressionTypePill({type}){
  const map={
    hard_bounce:       {bg:'#fff5f5',color:'#c53030',border:'#feb2b2'},
    complaint:         {bg:'#faf5ff',color:'#553c9a',border:'#d6bcfa'},
    manual_block:      {bg:'#fffaf0',color:'#b7791f',border:'#fbd38d'},
    abuse:             {bg:'#fff5f5',color:'#c53030',border:'#feb2b2'},
    invalid_recipient: {bg:'#f0f4ff',color:'#2b6cb0',border:'#bee3f8'},
  };
  const c=map[type]||{bg:T.bg,color:T.textSub,border:T.border};
  return(
    <span style={{display:'inline-flex',padding:'3px 9px',borderRadius:4,fontSize:13,fontWeight:700,...c}}>
      {(type||'unknown').replace(/_/g,' ')}
    </span>
  );
}

// -- COMMS OVERVIEW -----------------------------------------------------------
// ============================================================================
// COMMUNICATION THREAD PANEL — slide-over detail view
// ============================================================================

const THREAD_STATUS_CFG = {
  open:             { color:'#F87171', bg:'rgba(239,68,68,0.18)',   border:'rgba(239,68,68,0.45)',   label:'Open' },
  triaged:          { color:'#FBBF24', bg:'rgba(245,158,11,0.18)',  border:'rgba(245,158,11,0.45)',  label:'Triaged' },
  pending:          { color:'#60A5FA', bg:'rgba(59,130,246,0.18)',  border:'rgba(59,130,246,0.45)',  label:'Pending' },
  escalated:        { color:'#F87171', bg:'rgba(197,48,48,0.22)',   border:'rgba(239,68,68,0.5)',    label:'Escalated' },
  resolved:         { color:'#4ADE80', bg:'rgba(16,185,129,0.18)',  border:'rgba(16,185,129,0.45)',  label:'Resolved' },
  closed:           { color:'#C2CCD8', bg:'rgba(148,163,184,0.16)', border:'rgba(148,163,184,0.4)',  label:'Closed' },
};
const THREAD_PRIORITY_CFG = {
  urgent:   { color:'#F87171', label:'Urgent' },
  high:     { color:'#FBBF24', label:'High' },
  normal:   { color:'#60A5FA', label:'Normal' },
  low:      { color:'#9AA7B8', label:'Low' },
};
const THREAD_VALID_STATUSES = ['open','triaged','pending','escalated','resolved','closed'];

const CC = { text:'#e8edf4', text2:'#c9d3e0', blue:'#6EA8FF' };
function CommsChip({ type, id, label }) {
  const ctx = useEntityInspector();
  const content = (label===''||label==null) ? String(id).slice(0,8) : label;
  if (!ctx) return <span style={{ fontSize:13, color:'#FFFFFF' }}>{content}</span>;
  return (
    <span
      onClick={e => { e.stopPropagation(); ctx.openEntity(type, id); }}
      style={{ display:'inline-flex', alignItems:'center', boxSizing:'border-box', minHeight:32, gap:4, padding:'0 12px', borderRadius:6, fontSize:13, fontWeight:500, lineHeight:1.2, color:'#FFFFFF', background:'rgba(45,108,255,0.14)', border:'1px solid rgba(110,168,255,0.7)', cursor:'pointer' }}
      onMouseEnter={e => e.currentTarget.style.background='rgba(45,108,255,0.28)'}
      onMouseLeave={e => e.currentTarget.style.background='rgba(45,108,255,0.16)'}
      title={`Inspect ${type}: ${id}`}
    >{content}</span>
  );
}
function CommsPill({ label, color, fg, title }) {
  const base = color || '#8b97a8';
  return <span title={title} style={{ display:'inline-flex', alignItems:'center', boxSizing:'border-box', minHeight:32, fontSize:13, fontWeight:500, lineHeight:1, padding:'0 12px', borderRadius:6, whiteSpace:'nowrap', color:fg||base, background:base+'22', border:`1px solid ${base}59` }}>{label}</span>;
}
function ThreadStatusBadge({ status }) {
  const cfg = THREAD_STATUS_CFG[status] || THREAD_STATUS_CFG.open;
  return <CommsPill label={cfg.label} color={cfg.color}/>;
}

function CommunicationThreadPanel({ api, threadId, onClose, onUpdate }) {
  const { hasPermission } = useStaffAuth();
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [err,       setErr]       = useState(null);
  const [tab,       setTab]       = useState('detail'); // detail | messages | assignments | timeline
  const [submitting,setSubmitting]= useState(false);
  const [msg,       setMsg]       = useState('');
  const [actionErr, setActionErr] = useState('');

  // Status form
  const [newStatus,  setNewStatus]  = useState('');
  const [statusNote, setStatusNote] = useState('');
  // Assign form
  const [assignTo,   setAssignTo]   = useState('');
  const [escOwner,   setEscOwner]   = useState('');
  const [assignNote, setAssignNote] = useState('');

  const load = useCallback(() => {
    if (!threadId) return;
    setLoading(true); setErr(null);
    fetch(`${api}/v1/staff/comms/threads/${threadId}`, { credentials:'include' })
      .then(r => r.json())
      .then(d => {
        if (d.error) { setErr(d.error); return; }
        setData(d);
        setNewStatus(d.thread?.status || 'open');
        setAssignTo(d.thread?.assigned_staff_id || '');
        setEscOwner(d.thread?.escalation_owner_id || '');
      })
      .catch(() => setErr('Failed to load thread'))
      .finally(() => setLoading(false));
  }, [api, threadId]);

  useEffect(() => { load(); }, [load]);

  const post = (path, body, successMsg) => {
    setSubmitting(true); setMsg(''); setActionErr('');
    fetch(`${api}/v1/staff/comms/threads/${threadId}/${path}`, {
      method:'POST', credentials:'include',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify(body),
    }).then(r => r.json().then(d => ({ ok:r.ok, d })))
    .then(({ ok, d }) => {
      if (ok) { setMsg(successMsg); load(); if (onUpdate) onUpdate(); }
      else setActionErr(d.error || 'Action failed');
    })
    .catch(() => setActionErr('Network error'))
    .finally(() => setSubmitting(false));
  };

  const thread = data?.thread;
  const messages = data?.messages || [];
  const participants = data?.participants || [];
  const assignments = data?.assignments || [];

  const canAssign = hasPermission('comms.assign');
  const canManage = hasPermission('comms.manage');

  const dm = {
    overlay: { position:'fixed', inset:0, zIndex:8500, background:'rgba(0,0,0,0.45)', display:'flex', justifyContent:'flex-end' },
    panel:   { width:520, maxWidth:'95vw', background:'#12161F', borderLeft:'1px solid rgba(255,255,255,0.09)', display:'flex', flexDirection:'column', height:'100vh' },
    header:  { padding:'18px 22px 14px', borderBottom:'1px solid rgba(255,255,255,0.07)', flexShrink:0 },
    tabBtn:  (active) => ({ display:'inline-flex', alignItems:'center', minHeight:42, padding:'0 14px', fontSize:14, fontWeight:500, lineHeight:1.4, cursor:'pointer', border:'none', background:'none', borderBottom:`2px solid ${active?'#2D6CFF':'transparent'}`, color:active?'#F5F7FB':'#D6DEE9', fontFamily:"'DM Sans',sans-serif", transition:'all 0.12s' }),
    section: { padding:'18px 20px', borderBottom:'1px solid rgba(255,255,255,0.05)' },
    label:   { fontSize:14, fontWeight:600, lineHeight:1.4, color:'#F0F4FA', textTransform:'uppercase', letterSpacing:'0.2px', marginBottom:12, display:'block' },
    kv:      { display:'flex', justifyContent:'space-between', alignItems:'center', gap:16, minHeight:34, padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,0.05)', fontSize:14 },
    kvL:     { color:'#E2E8F0', fontWeight:400, fontSize:14, lineHeight:1.5 },
    kvV:     { color:'#FFFFFF', fontWeight:500, fontSize:14, lineHeight:1.5, textAlign:'right' },
    input:   { width:'100%', boxSizing:'border-box', minHeight:42, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:6, padding:'0 14px', fontSize:14, color:'#FFFFFF', fontFamily:"'DM Sans',sans-serif", outline:'none', marginBottom:10, colorScheme:'dark' },
    textarea:{ width:'100%', boxSizing:'border-box', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:6, padding:'12px 14px', fontSize:14, lineHeight:1.55, color:'#FFFFFF', fontFamily:"'DM Sans',sans-serif", outline:'none', resize:'vertical', minHeight:88, marginBottom:10 },
    btn:     { display:'inline-flex', alignItems:'center', justifyContent:'center', minHeight:40, padding:'0 16px', borderRadius:6, fontSize:14, fontWeight:600, cursor:'pointer', border:'none', background:'linear-gradient(135deg,#2D6CFF,#1D4ED8)', color:'#FFFFFF', fontFamily:"'DM Sans',sans-serif" },
  };

  const DIR_COLOR = { inbound:'#60A5FA', outbound:'#4ADE80', internal:'#F59E0B' };

  return (
    <div style={dm.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={dm.panel}>
        {/* Header */}
        <div style={dm.header}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:18, fontWeight:600, lineHeight:1.35, color:'#F5F7FB', marginBottom:6 }}>
                {thread?.subject || '(no subject)'}
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                {thread && <ThreadStatusBadge status={thread.status}/>}
                {thread?.priority && (
                  <CommsPill label={THREAD_PRIORITY_CFG[thread.priority]?.label||thread.priority} color={THREAD_PRIORITY_CFG[thread.priority]?.color||'#8b97a8'}/>
                )}
                {thread?.channel && <CommsPill label={thread.channel} color="#8b97a8" fg="#e8edf4"/>}
              </div>
            </div>
            <button onClick={onClose} style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:36, height:36, background:'none', border:'none', color:'#DDE5F0', cursor:'pointer', fontSize:20, fontWeight:500, lineHeight:1, padding:0, flexShrink:0 }}>×</button>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display:'flex', borderBottom:'1px solid rgba(255,255,255,0.07)', paddingLeft:6, flexShrink:0 }}>
          {['detail','messages','assignments','timeline'].map(t => (
            <button key={t} style={dm.tabBtn(tab===t)} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase()+t.slice(1)}
              {t==='messages'&&messages.length>0?` (${messages.length})`:''}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', overflowX:'hidden', minHeight:0, paddingBottom:24 }}>
          {loading && <div style={{ padding:'32px 20px', textAlign:'center', fontSize:14, color:'#F0F4FA' }}>Loading...</div>}
          {err && <div style={{ padding:'12px 20px', fontSize:13, color:'#FCA5A5' }}>{err}</div>}
          {msg && <div style={{ padding:'8px 20px', fontSize:12, color:'#4ADE80', background:'rgba(74,222,128,0.06)' }}>{msg}</div>}
          {actionErr && <div style={{ padding:'8px 20px', fontSize:12, color:'#FCA5A5' }}>{actionErr}</div>}

          {!loading && data && (
            <>
              {/* DETAIL TAB */}
              {tab === 'detail' && (
                <>
                  <div style={dm.section}>
                    <span style={dm.label}>Thread Info</span>
                    {[
                      { label:'ID',       val:<code style={{ fontFamily:'monospace', fontSize:13, fontWeight:400, color:'#E2E8F0', overflowWrap:'anywhere' }}>{thread?.id}</code> },
                      { label:'Source',   val:thread?.source||'-' },
                      { label:'Messages', val:thread?.message_count||0 },
                      { label:'Created',  val:fmtDate(thread?.created_at) },
                      { label:'Updated',  val:fmtAgo(thread?.updated_at||thread?.created_at) },
                      { label:'Resolved', val:thread?.resolved_at?fmtDate(thread.resolved_at):'-' },
                    ].map(({ label, val }) => (
                      <div key={label} style={dm.kv}><span style={dm.kvL}>{label}</span><span style={dm.kvV}>{val}</span></div>
                    ))}
                  </div>

                  {/* Linked entities */}
                  <div style={dm.section}>
                    <span style={dm.label}>Linked Entities</span>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                      {data.user?.id && <CommsChip type="user" id={data.user.id} label={data.user.email}/>}
                      {thread?.ticket_id && <CommsChip type="ticket" id={thread.ticket_id} label={thread.ticket_id}/>}
                      {thread?.incident_rule_id && <CommsChip type="incident" id={`inc_${thread.incident_rule_id}_0`} label="Incident"/>}
                      {(!data.user?.id&&!thread?.ticket_id&&!thread?.incident_rule_id) && (
                        <span style={{ fontSize:14, fontWeight:400, color:'#F0F4FA', fontStyle:'italic' }}>No linked entities</span>
                      )}
                    </div>
                  </div>

                  {/* Assignment info */}
                  <div style={dm.section}>
                    <span style={dm.label}>Ownership</span>
                    {[
                      { label:'Assigned To', val:thread?.assigned_staff_id||'Unassigned' },
                      { label:'Escalation',  val:thread?.escalation_owner_id||'-' },
                    ].map(({ label, val }) => (
                      <div key={label} style={dm.kv}><span style={dm.kvL}>{label}</span><span style={dm.kvV}>{val}</span></div>
                    ))}
                  </div>

                  {/* Participants */}
                  {participants.length > 0 && (
                    <div style={dm.section}>
                      <span style={dm.label}>Participants ({participants.length})</span>
                      {participants.map(p => (
                        <div key={p.id} style={{ display:'flex', alignItems:'center', gap:10, minHeight:42, padding:'6px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                          <CommsPill label={p.role} color="#8b97a8" fg="#e8edf4"/>
                          <span style={{ fontSize:14, fontWeight:400, color:'#FFFFFF' }}>{p.participant_email||p.participant_id||p.participant_type}</span>
                          {p.participant_id && p.participant_type==='user' && (
                            <CommsChip type="user" id={p.participant_id} label=""/>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Status action */}
                  {canManage && (
                    <div style={dm.section}>
                      <span style={dm.label}>Change Status</span>
                      <select style={{ ...dm.input }} value={newStatus} onChange={e=>setNewStatus(e.target.value)}>
                        {THREAD_VALID_STATUSES.map(st=><option key={st} value={st} style={OPT_DARK}>{THREAD_STATUS_CFG[st]?.label||st}</option>)}
                      </select>
                      <textarea className="cc-field" style={dm.textarea} placeholder="Note (optional)..." value={statusNote} onChange={e=>setStatusNote(e.target.value)}/>
                      <button style={dm.btn} disabled={submitting} onClick={()=>post('status',{status:newStatus,note:statusNote},`Status changed to ${newStatus}`)}>
                        {submitting?'Saving...':'Update Status'}
                      </button>
                    </div>
                  )}

                  {/* Assign action */}
                  {canAssign && (
                    <div style={dm.section}>
                      <span style={dm.label}>Assign</span>
                      <input className="cc-field" style={dm.input} placeholder="Staff user ID..." value={assignTo} onChange={e=>setAssignTo(e.target.value)}/>
                      <input className="cc-field" style={dm.input} placeholder="Escalation owner ID (optional)..." value={escOwner} onChange={e=>setEscOwner(e.target.value)}/>
                      <textarea className="cc-field" style={dm.textarea} placeholder="Assignment note (optional)..." value={assignNote} onChange={e=>setAssignNote(e.target.value)}/>
                      <button style={dm.btn} disabled={submitting||!assignTo} onClick={()=>post('assign',{assigned_to:assignTo,escalation_owner:escOwner||undefined,notes:assignNote||undefined},'Thread assigned')}>
                        {submitting?'Saving...':'Assign'}
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* MESSAGES TAB */}
              {tab === 'messages' && (
                <div style={{ padding:'18px 20px 24px' }}>
                  {messages.length === 0
                    ? <div style={{ fontSize:14, fontWeight:400, lineHeight:1.6, color:'#F0F4FA', padding:'12px 0' }}>No messages in this thread.</div>
                    : messages.map(m => (
                      <div key={m.id} style={{ marginBottom:12, padding:16, boxSizing:'border-box', height:'auto', minHeight:0, overflow:'visible', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:8, borderLeft:`3px solid ${DIR_COLOR[m.direction]||'#718096'}` }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
                          <span style={{ fontSize:13, fontWeight:600, color:DIR_COLOR[m.direction]||'#E2E8F0', textTransform:'uppercase', letterSpacing:'0.05em' }}>{m.direction}</span>
                          {m.is_internal && <span style={{ fontSize:11, fontWeight:600, color:'#FBBF24', background:'rgba(245,158,11,0.16)', padding:'2px 7px', borderRadius:4, border:'1px solid rgba(245,158,11,0.4)' }}>INTERNAL</span>}
                          <span style={{ fontSize:13, fontWeight:400, color:'#E2E8F0', marginLeft:'auto' }}>{fmtAgo(m.created_at)}</span>
                        </div>
                        {m.subject && <div style={{ fontSize:15, fontWeight:600, lineHeight:1.45, color:'#FFFFFF', marginBottom:8 }}>{m.subject}</div>}
                        <div style={{ display:'flex', gap:12, flexWrap:'wrap', fontSize:13, fontWeight:400, lineHeight:1.5, color:'#E2E8F0', marginBottom:8, overflowWrap:'anywhere', wordBreak:'break-word' }}>
                          {m.sender_email && <span>From: {m.sender_email}</span>}
                          {m.recipient_email && <span>To: {m.recipient_email}</span>}
                        </div>
                        {m.body_text && <div style={{ display:'block', width:'100%', height:'auto', overflow:'visible', fontSize:15, fontWeight:400, color:'#FFFFFF', lineHeight:1.7, whiteSpace:'pre-wrap', overflowWrap:'anywhere', wordBreak:'break-word', marginBottom:12 }}>{m.body_text}</div>}
                        <div style={{ marginTop:8 }}>
                          <CommsChip type="communication_message" id={m.id} label="inspect"/>
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}

              {/* ASSIGNMENTS TAB */}
              {tab === 'assignments' && (
                <div style={{ padding:'18px 20px 24px' }}>
                  <span style={dm.label}>Assignment History</span>
                  {assignments.length === 0
                    ? <div style={{ fontSize:14, fontWeight:400, lineHeight:1.6, color:'#F0F4FA', padding:'8px 0' }}>No assignment history.</div>
                    : assignments.map(a => (
                      <div key={a.id} style={{ padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:13 }}>
                          <span style={{ color:a.status==='active'?'#4ADE80':'#F5F7FB', fontWeight:600, fontSize:13 }}>{a.status?.toUpperCase()}</span>
                          <span style={{ color:'#DDE5F0', fontSize:13, fontWeight:400 }}>{fmtAgo(a.created_at)}</span>
                        </div>
                        <div style={{ fontSize:14, fontWeight:400, lineHeight:1.6, color:'#F0F4FA', marginTop:6 }}>
                          To: {a.assigned_to||'unassigned'} · By: {a.assigned_by?.slice(0,8)}
                        </div>
                        {a.escalation_owner && <div style={{ fontSize:14, fontWeight:400, color:'#FBBF24', marginTop:4 }}>Escalation: {a.escalation_owner}</div>}
                        {a.notes && <div style={{ fontSize:14, fontWeight:400, lineHeight:1.6, color:'#F0F4FA', marginTop:6, fontStyle:'italic' }}>{a.notes}</div>}
                      </div>
                    ))
                  }
                </div>
              )}

              {/* TIMELINE TAB */}
              {tab === 'timeline' && (
                <div style={{ padding:'18px 20px 24px' }}>
                  <span style={dm.label}>Thread Timeline</span>
                  <div style={{ marginTop:6 }}>
                    <CommsChip type="communication_thread" id={threadId} label="Open in Inspector (full timeline)"/>
                  </div>
                  <div style={{ marginTop:12, fontSize:14, fontWeight:400, color:'#F0F4FA', lineHeight:1.6 }}>
                    Full timeline, relationship graph, investigation workflow, and communications intelligence available in the Entity Inspector.
                  </div>
                  {/* Compact message timeline */}
                  {messages.length > 0 && (
                    <div style={{ marginTop:14 }}>
                      <span style={dm.label}>Message Events</span>
                      {messages.slice().reverse().map(m => (
                        <div key={m.id} style={{ display:'flex', gap:10, padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', alignItems:'flex-start' }}>
                          <span style={{ width:8, height:8, borderRadius:'50%', background:DIR_COLOR[m.direction]||'#718096', flexShrink:0, marginTop:4 }}/>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:14, fontWeight:400, lineHeight:1.55, color:'#F5F7FB' }}>{m.direction} message{m.subject?`: ${m.subject}`:''}</div>
                            <div style={{ fontSize:13, fontWeight:400, color:'#DDE5F0' }}>{fmtAgo(m.created_at)}</div>
                          </div>
                          <span style={{ fontSize:13, fontWeight:600, color:m.status==='failed'?'#F87171':m.status==='delivered'?'#4ADE80':'#DDE5F0' }}>{m.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// COMMUNICATIONS CENTER — SecCommunications
// ============================================================================

function SecCommunications({ api }) {
  const { hasPermission } = useStaffAuth();
  const [data,        setData]       = useState(null);
  const [err,         setErr]        = useState(null);
  const [page,        setPage]       = useState(1);
  const [statusFilter,setStatusFilter] = useState('');
  const [channelFilter,setChannelFilter] = useState('');
  const [assignedFilter,setAssignedFilter] = useState('');
  const [search,      setSearch]     = useState('');
  const [selectedThread,setSelectedThread] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulk,    setShowBulk]   = useState(false);
  const [staffList,   setStaffList]  = useState([]);

  useEffect(() => {
    fetch(`${api}/v1/staff/members?per_page=100`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setStaffList(d.staff || d.members || []))
      .catch(() => {});
  }, [api]);

  const load = useCallback(() => {
    setData(null);
    const p = new URLSearchParams({ page, per_page:50 });
    if (statusFilter)   p.set('status',           statusFilter);
    if (channelFilter)  p.set('channel',           channelFilter);
    if (assignedFilter) p.set('assigned_staff_id', assignedFilter);
    fetch(`${api}/v1/staff/comms/threads?${p}`, { credentials:'include' })
      .then(r => r.json())
      .then(setData)
      .catch(() => setErr('Failed to load threads'));
  }, [api, page, statusFilter, channelFilter, assignedFilter]);

  useEffect(() => { load(); }, [load]);

  const threads = ((data&&data.threads)||[]).filter(t =>
    !search || (t.subject||'').toLowerCase().includes(search.toLowerCase()) ||
    (t.user_id||'').includes(search) || (t.ticket_id||'').includes(search)
  );

  const allIds = threads.map(t => t.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(allIds));
  const toggleOne = id => setSelectedIds(prev => { const n = new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });

  const CHANNELS = ['support','billing','sales','security','general'];
  const STATUSES = Object.keys(THREAD_STATUS_CFG);

  return (
    <>
      {selectedThread && (
        <CommunicationThreadPanel
          api={api}
          threadId={selectedThread}
          onClose={() => setSelectedThread(null)}
          onUpdate={load}
        />
      )}
      {showBulk && (
        <BulkActionPanel
          api={api}
          entityType="communication_thread"
          entityIds={[...selectedIds]}
          onClose={() => setShowBulk(false)}
          hasPermission={hasPermission}
        />
      )}

      <div style={s.filterRow}>
        <input className="cc-field" style={{ ...s.input, width:200, minHeight:42, fontSize:14, padding:'0 14px', color:'#FFFFFF' }} placeholder="Subject / user / ticket..." value={search} onChange={e=>setSearch(e.target.value)}/>
        <select style={{...s.select, minHeight:42, fontSize:14}} value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setPage(1);}}>
          <option value="">All Statuses</option>
          {STATUSES.map(st=><option key={st} value={st}>{THREAD_STATUS_CFG[st].label}</option>)}
        </select>
        <select style={{...s.select, minHeight:42, fontSize:14}} value={channelFilter} onChange={e=>{setChannelFilter(e.target.value);setPage(1);}}>
          <option value="">All Channels</option>
          {CHANNELS.map(ch=><option key={ch} value={ch}>{ch.charAt(0).toUpperCase()+ch.slice(1)}</option>)}
        </select>
        <select style={{ ...s.select, width: 180 }} value={assignedFilter} onChange={e=>{setAssignedFilter(e.target.value);setPage(1);}}><option value="">All Staff</option><option value="unassigned">Unassigned</option>{staffList.map(m => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}</select>
        <button style={s.btn('primary')} onClick={()=>{setPage(1);load();}}>Apply</button>
        <button style={s.btn()} onClick={load}>Refresh</button>
      </div>

      {err && <div style={s.error}>! {err}</div>}

      {selectedIds.size > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:T.accentLight, border:`1px solid ${T.accentBorder}`, borderRadius:6, marginBottom:8, fontSize:13 }}>
          <span style={{ color:'#6EA8FF', fontWeight:700 }}>{selectedIds.size} thread{selectedIds.size!==1?'s':''} selected</span>
          {hasPermission('bulk.execute') && <button style={{ ...s.btn('primary'), fontSize:12, padding:'4px 12px' }} onClick={()=>setShowBulk(true)}>Bulk Action</button>}
          <button style={{ ...s.btn(), fontSize:12, padding:'4px 10px' }} onClick={()=>setSelectedIds(new Set())}>Clear</button>
        </div>
      )}

      {!data ? <Loader/> : (
        <TableCard title="Communication Threads" count={data.total}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ ...s.table, minWidth:900 }}>
              <thead><tr>
                <th style={{ ...s.th, width:32, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ cursor:'pointer' }}/></th>
                {['Subject','Status','Priority','Channel','Linked User','Assigned','Messages','Last Activity','Created'].map(h=>(
                  <th key={h} style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {threads.length === 0
                  ? <tr><td colSpan={10} style={{ ...s.td, ...s.empty, color:'#F0F4FA' }}>No threads found.</td></tr>
                  : threads.map(t => {
                    const stCfg = THREAD_STATUS_CFG[t.status] || THREAD_STATUS_CFG.open;
                    const prCfg = THREAD_PRIORITY_CFG[t.priority] || THREAD_PRIORITY_CFG.normal;
                    return (
                      <tr key={t.id}
                        style={{ cursor:'pointer', height:72, background:selectedIds.has(t.id)?'rgba(45,108,255,0.20)':selectedThread===t.id?'rgba(45,108,255,0.20)':'transparent' }}
                        onClick={() => setSelectedThread(t.id===selectedThread?null:t.id)}
                        onMouseEnter={e=>{ if(!selectedIds.has(t.id)&&selectedThread!==t.id) e.currentTarget.style.background=T.borderSoft; }}
                        onMouseLeave={e=>e.currentTarget.style.background=selectedIds.has(t.id)?'rgba(45,108,255,0.20)':selectedThread===t.id?'rgba(45,108,255,0.20)':'transparent'}
                      >
                        <td style={s.td} onClick={e=>e.stopPropagation()}><input type="checkbox" checked={selectedIds.has(t.id)} onChange={()=>toggleOne(t.id)} style={{ cursor:'pointer' }}/></td>
                        <td style={s.tds}>
                          <div style={{ fontSize:15, fontWeight:500, color:'#FFFFFF', maxWidth:260, wordBreak:'break-word', lineHeight:1.4 }}>
                            {t.subject||<span style={{ fontSize:15, color:'#F0F4FA', fontStyle:'italic' }}>(no subject)</span>}
                          </div>
                          <CommsChip type="communication_thread" id={t.id} label={t.id.slice(0,8)}/>
                        </td>
                        <td style={s.td}>
                          <CommsPill label={stCfg.label} color={stCfg.color}/>
                        </td>
                        <td style={s.td}><CommsPill label={prCfg.label} color={prCfg.color}/></td>
                        <td style={s.td}><span style={{ fontSize:14, fontWeight:400, color:'#F5F7FB' }}>{t.channel||'-'}</span></td>
                        <td style={s.td}>
                          {t.user_id
                            ? <CommsChip type="user" id={t.user_id} label={t.user_id.slice(0,8)}/>
                            : <span style={{ fontSize:14, color:'#F5F7FB' }}>-</span>}
                        </td>
                        <td style={s.td}><span style={{ fontSize:14, fontWeight:400, color:t.assigned_staff_id?'#FFFFFF':'#F0F4FA' }}>{t.assigned_staff_id?(staffList.find(m=>m.user_id===t.assigned_staff_id)||{name:t.assigned_staff_id.slice(0,8)}).name||t.assigned_staff_id.slice(0,8):'Unassigned'}</span></td>
                        <td style={s.td}><span style={{ fontSize:14, fontWeight:500, color:t.message_count>0?'#6EA8FF':'#F5F7FB' }}>{t.message_count||0}</span></td>
                        <td style={{ ...s.td, fontSize:14, color:'#E2E8F0', whiteSpace:'nowrap' }}>{fmtAgo(t.last_message_at||t.updated_at||t.created_at)}</td>
                        <td style={{ ...s.td, fontSize:14, color:'#E2E8F0', whiteSpace:'nowrap' }}>{fmtDate(t.created_at)}</td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          </div>
          {data.pages>1 && <Pager page={page} pages={data.pages} total={data.total} onPage={setPage}/>}
        </TableCard>
      )}
    </>
  );
}

function SecCommsOverview({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  useEffect(()=>{
    fetch(`${api}/v1/staff/comms/overview`,{credentials:'include'})
      .then(r=>r.json()).then(setData).catch(()=>setErr('Failed to load communications overview'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;
  return(
    <>
      {/* Primary KPI strip */}
      <div style={{background:'rgba(255,255,255,0.03)',borderRadius:10,padding:'20px 24px',marginBottom:12,border:'1px solid rgba(255,255,255,0.07)'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16}}>
          <RadialKPI value={fmt(data.sent_today)}        label="Sent Today"         color="green"/>
          <RadialKPI value={fmt(data.failed_today)}      label="Failed Today"       color={data.failed_today>0?'red':'green'}/>
          <RadialKPI value={`${data.bounce_rate}%`}      label="Bounce Rate"        color={data.bounce_rate>5?'red':data.bounce_rate>1?'amber':'green'}/>
          <RadialKPI value={fmt(data.suppressed_active)} label="Suppressed Active"  color={data.suppressed_active>0?'amber':'green'}/>
        </div>
      </div>

      {/* Secondary KPI strip */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:12}}>
        {[
          {label:'Invite Emails Today',   val:fmt(data.invite_today),    sub:'staff_invite template',        color:T.chart1},
          {label:'Password Reset Today',  val:fmt(data.reset_today),     sub:'staff_password_reset template', color:T.chart3},
          {label:'Security Emails Today', val:fmt(data.security_today),  sub:'staff_security_alert template', color:T.chart5},
          {label:'Queue Failures (All)',  val:fmt(data.queue_failures),  sub:'All-time MAIL:FAILED events',   color:data.queue_failures>0?T.chart5:T.chart2},
        ].map(({label,val,sub,color})=>(
          <div key={label} style={{...s.panel,padding:'14px 16px',borderTop:`3px solid ${color}`}}>
            <div style={{fontSize:13,fontWeight:700,color:T.textMid,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>{label}</div>
            <div style={{fontSize:34,fontWeight:800,color:T.text,lineHeight:1,letterSpacing:'-0.02em'}}>{val??'-'}</div>
            <div style={{fontSize:13,color:T.textMid,marginTop:6}}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Delivery telemetry row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:12}}>
        {[
          {label:'Delivery Events',   val:fmt(data.delivery_total),   sub:'SES Send + Delivery events', color:T.chart2},
          {label:'Bounce Events',     val:fmt(data.bounce_total),     sub:'Hard + soft bounces',        color:data.bounce_total>0?T.chart5:T.chart2},
          {label:'Complaint Events',  val:fmt(data.complaint_total),  sub:'Spam complaints',            color:data.complaint_total>0?T.chart5:T.chart2},
        ].map(({label,val,sub,color})=>(
          <div key={label} style={{...s.panel,padding:'14px 16px',borderLeft:`3px solid ${color}`}}>
            <div style={{fontSize:13,fontWeight:700,color:T.textMid,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>{label}</div>
            <div title={val==null?'-':String(val)} style={{fontSize:28,fontWeight:600,color:'#FFFFFF',lineHeight:1.15,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{val??'-'}</div>
            <div style={{fontSize:13,color:T.textMid,marginTop:6}}>{sub}</div>
          </div>
        ))}
      </div>
    </>
  );
}


// -- COMMS MAIL QUEUE ---------------------------------------------------------
function SecCommsQueue({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[page,setPage]=useState(1);
  const[status,setStatus]=useState('');
  const[template,setTemplate]=useState('');
  const[hours,setHours]=useState(168);
  const[selected,setSelected]=useState(null);

  const load=useCallback(()=>{
    setData(null);
    const p=new URLSearchParams({page,per_page:50,hours});
    if(status)p.set('status',status);
    if(template)p.set('template',template);
    fetch(`${api}/v1/staff/comms/mail-queue?${p}`,{credentials:'include'})
      .then(r=>r.json()).then(setData).catch(()=>setErr('Failed to load mail queue'));
  },[api,page,status,template,hours]);

  useEffect(()=>{load();},[load]);

  const TEMPLATES=['staff_invite','staff_password_reset','staff_mfa_recovery','staff_security_alert'];

  return(
    <div style={{display:'flex',gap:12}}>
      {/* Main table */}
      <div style={{flex:1,minWidth:0}}>
        <div style={s.filterRow}>
          <select style={{...s.select, minHeight:42, fontSize:14}} value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
            <option value="">All Statuses</option>
            <option value="SENT">SENT</option>
            <option value="FAILED">FAILED</option>
            <option value="SUPPRESSED">SUPPRESSED</option>
          </select>
          <select style={{...s.select, minHeight:42, fontSize:14}} value={template} onChange={e=>{setTemplate(e.target.value);setPage(1);}}>
            <option value="">All Templates</option>
            {TEMPLATES.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          <select style={{...s.select, minHeight:42, fontSize:14}} value={hours} onChange={e=>{setHours(Number(e.target.value));setPage(1);}}>
            {[24,48,168,720].map(h=><option key={h} value={h}>Last {h}h</option>)}
          </select>
          <button style={s.btn('primary')} onClick={()=>load()}>Refresh</button>
        </div>
        {err&&<div style={s.error}>! {err}</div>}
        <TableCard title="Mail Events" count={data&&data.total}>
          {!data?<Loader/>:(
            <>
              <div style={{overflowX:'auto'}}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Status</th>
                      <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Template</th>
                      <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Recipient Hash</th>
                      <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Provider ID</th>
                      <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Actor</th>
                      <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Sent At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.events.length===0?(
                      <tr><td colSpan={6} style={{...s.td,...s.empty, color:'#F0F4FA'}}>No mail events found for this filter.</td></tr>
                    ):data.events.map(e=>(
                      <tr key={e.id} onClick={()=>setSelected(e)}
                        style={{cursor:'pointer',background:selected&&selected.id===e.id?T.accentLight:'transparent'}}
                        onMouseEnter={ev=>{ if(!selected||selected.id!==e.id) ev.currentTarget.style.background=T.borderSoft; }}
                        onMouseLeave={ev=>ev.currentTarget.style.background=selected&&selected.id===e.id?T.accentLight:'transparent'}
                      >
                        <td style={s.td}><MailStatusPill status={e.status}/></td>
                        <td style={s.td}><code style={{fontFamily:'monospace',fontSize:13,color:'#6EA8FF'}}>{e.template||'-'}</code></td>
                        <td style={s.td}><span style={{fontFamily:'monospace',fontSize:12,color:T.textMid}}>{e.recipient_hash?e.recipient_hash.slice(0,16)+'...':'-'}</span></td>
                        <td style={s.td}><span style={{fontFamily:'monospace',fontSize:12,color:T.textMid}}>{e.provider_id?e.provider_id.slice(0,20)+'...':'-'}</span></td>
                        <td style={s.td}>
                          {e.actor_id&&e.actor_id!=='system'
                            ? <CommsChip type="user" id={e.actor_id} label={e.actor_name||e.actor_id.slice(0,8)}/>
                            : <span style={{fontSize:13,color:T.textMid}}>system</span>}
                        </td>
                        <td style={s.td}>
                          <div style={{display:'flex',alignItems:'center',gap:6}}>
                            <span style={{fontSize:13,color:T.textMid}}>{fmtDate(e.created_at)}</span>
                            <CommsChip type="mail_event" id={String(e.id)} label="inspect"/>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.pages>1&&<Pager page={page} pages={data.pages} total={data.total} onPage={setPage}/>}
            </>
          )}
        </TableCard>
      </div>

      {/* Inspector panel */}
      {selected&&(
        <div style={{width:320,flexShrink:0}}>
          <div style={{...s.panel,position:'sticky',top:20}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
              <div style={{fontSize:15,fontWeight:700,color:T.text}}>Event Inspector</div>
              <button onClick={()=>setSelected(null)} style={{background:'none',border:'none',color:T.textMid,cursor:'pointer',fontSize:18,lineHeight:1}}>×</button>
            </div>
            <div style={{marginBottom:10}}><MailStatusPill status={selected.status}/></div>
            {[
              {label:'Template',      val:<code style={{fontFamily:'monospace',fontSize:13,color:'#6EA8FF'}}>{selected.template||'-'}</code>},
              {label:'Provider ID',   val:<span style={{fontFamily:'monospace',fontSize:12,wordBreak:'break-all'}}>{selected.provider_id||'-'}</span>},
              {label:'Recipient Hash',val:<span style={{fontFamily:'monospace',fontSize:11,wordBreak:'break-all',color:T.textMid}}>{selected.recipient_hash||'-'}</span>},
              {label:'Actor',         val:selected.actor_id&&selected.actor_id!=='system'?<CommsChip type="user" id={selected.actor_id} label={selected.actor_name||selected.actor_id.slice(0,8)}/>:(selected.actor_name||'system')},
              {label:'Session',       val:<span style={{fontFamily:'monospace',fontSize:11}}>{selected.session_id||'-'}</span>},
              {label:'Event',         val:<CommsChip type="mail_event" id={String(selected.id)} label="open inspector"/>},
              {label:'Sent At',       val:fmtDate(selected.created_at)},
            ].map(({label,val})=>(
              <div key={label} style={s.kvRow}>
                <span style={s.kvLabel}>{label}</span>
                <span style={{...s.kvValue,maxWidth:180,textAlign:'right',fontSize:13}}>{val}</span>
              </div>
            ))}
            {selected.error&&(
              <div style={{marginTop:12,padding:'10px 12px',background:T.redBg,border:`1px solid ${T.redBorder}`,borderRadius:6,fontSize:13,color:T.red}}>
                <strong>Error:</strong> {selected.error}
              </div>
            )}
            <div style={{marginTop:14,display:'flex',gap:8}}>
              <button style={{...s.btn(),flex:1,justifyContent:'center',opacity:0.5,cursor:'not-allowed'}} disabled>Resend</button>
              <button style={{...s.btn(),flex:1,justifyContent:'center',opacity:0.5,cursor:'not-allowed'}} disabled>Retry</button>
            </div>
            <div style={{marginTop:6,fontSize:12,color:T.textMid,textAlign:'center'}}>Resend / Retry coming in next phase</div>
          </div>
        </div>
      )}
    </div>
  );
}


// -- COMMS DELIVERY MONITOR ---------------------------------------------------
function SecCommsDelivery({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[page,setPage]=useState(1);
  const[eventType,setEventType]=useState('');
  const[hours,setHours]=useState(168);

  const load=useCallback(()=>{
    setData(null);
    const p=new URLSearchParams({page,per_page:50,hours});
    if(eventType)p.set('event_type',eventType);
    fetch(`${api}/v1/staff/comms/delivery-monitor?${p}`,{credentials:'include'})
      .then(r=>r.json()).then(setData).catch(()=>setErr('Failed to load delivery monitor'));
  },[api,page,eventType,hours]);

  useEffect(()=>{load();},[load]);

  return(
    <>
      {/* Suppression summary pills */}
      {data&&data.suppression_summary&&data.suppression_summary.length>0&&(
        <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
          <span style={{fontSize:13,fontWeight:700,color:T.textMid,alignSelf:'center'}}>Suppression breakdown:</span>
          {data.suppression_summary.map(s2=>(
            <div key={s2.type} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 10px',background:T.surface,border:`1px solid ${T.border}`,borderRadius:6}}>
              <SuppressionTypePill type={s2.type}/>
              <span style={{fontSize:13,fontWeight:700,color:T.text}}>{fmt(s2.count)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={s.filterRow}>
        <select style={{...s.select, minHeight:42, fontSize:14}} value={eventType} onChange={e=>{setEventType(e.target.value);setPage(1);}}>
          <option value="">All Event Types</option>
          <option value="Send">Send</option>
          <option value="Delivery">Delivery</option>
          <option value="Bounce">Bounce</option>
          <option value="Complaint">Complaint</option>
        </select>
        <select style={{...s.select, minHeight:42, fontSize:14}} value={hours} onChange={e=>{setHours(Number(e.target.value));setPage(1);}}>
          {[24,48,168,720].map(h=><option key={h} value={h}>Last {h}h</option>)}
        </select>
        <button style={s.btn('primary')} onClick={()=>load()}>Refresh</button>
      </div>
      {err&&<div style={s.error}>! {err}</div>}
      <TableCard title="SES Delivery Events" count={data&&data.total}>
        {!data?<Loader/>:(
          <>
            <div style={{overflowX:'auto'}}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Event Type</th>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Recipient</th>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>SES Message ID</th>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Provider</th>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Config Set</th>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Event Time</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events.length===0?(
                    <tr><td colSpan={6} style={{...s.td,...s.empty, color:'#F0F4FA'}}>No delivery events found for this filter.</td></tr>
                  ):data.events.map(e=>(
                    <tr key={e.id}>
                      <td style={s.td}>
                        <span style={{display:'inline-flex',alignItems:'center',gap:5,padding:'4px 10px',borderRadius:6,fontSize:13,fontWeight:600,
                          background:e.event_type==='Bounce'||e.event_type==='bounce'?T.redBg:e.event_type==='Complaint'||e.event_type==='complaint'?T.purpleBg:e.event_type==='Delivery'||e.event_type==='delivery'?T.greenBg:T.accentLight,
                          color:e.event_type==='Bounce'||e.event_type==='bounce'?'#F87171':e.event_type==='Complaint'||e.event_type==='complaint'?T.purple:e.event_type==='Delivery'||e.event_type==='delivery'?'#4ADE80':'#6EA8FF',
                          border:`1px solid ${e.event_type==='Bounce'||e.event_type==='bounce'?T.redBorder:e.event_type==='Delivery'||e.event_type==='delivery'?T.greenBorder:T.border}`,
                        }}>
                          {e.event_type}
                        </span>
                      </td>
                      <td style={s.td}><span style={{fontSize:13,fontFamily:'monospace'}}>{e.recipient_email}</span></td>
                      <td style={s.td}><span style={{fontFamily:'monospace',fontSize:11,color:T.textMid}}>{e.ses_message_id?e.ses_message_id.slice(0,24)+'...':'-'}</span></td>
                      <td style={s.td}><span style={{fontSize:13}}>{e.provider||'-'}</span></td>
                      <td style={s.td}><span style={{fontSize:12,color:T.textMid}}>{e.configuration_set||'-'}</span></td>
                      <td style={s.td}><span style={{fontSize:13,color:T.textMid}}>{fmtDate(e.event_timestamp||e.created_at)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.pages>1&&<Pager page={page} pages={data.pages} total={data.total} onPage={setPage}/>}
          </>
        )}
      </TableCard>
    </>
  );
}


// -- COMMS SUPPRESSION LIST ---------------------------------------------------
function SecCommsSuppression({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[page,setPage]=useState(1);
  const[supType,setSupType]=useState('');
  const[activeFilter,setActiveFilter]=useState('true');

  const load=useCallback(()=>{
    setData(null);
    const p=new URLSearchParams({page,per_page:50});
    if(supType)p.set('suppression_type',supType);
    if(activeFilter)p.set('active',activeFilter);
    fetch(`${api}/v1/staff/comms/suppression-list?${p}`,{credentials:'include'})
      .then(r=>r.json()).then(setData).catch(()=>setErr('Failed to load suppression list'));
  },[api,page,supType,activeFilter]);

  useEffect(()=>{load();},[load]);

  const SUP_TYPES=['hard_bounce','complaint','manual_block','abuse','invalid_recipient'];

  return(
    <>
      <div style={s.filterRow}>
        <select style={{...s.select, minHeight:42, fontSize:14}} value={supType} onChange={e=>{setSupType(e.target.value);setPage(1);}}>
          <option value="">All Types</option>
          {SUP_TYPES.map(t=><option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
        </select>
        <select style={{...s.select, minHeight:42, fontSize:14}} value={activeFilter} onChange={e=>{setActiveFilter(e.target.value);setPage(1);}}>
          <option value="true">Active Only</option>
          <option value="false">Inactive Only</option>
          <option value="">All</option>
        </select>
        <button style={s.btn('primary')} onClick={()=>load()}>Refresh</button>
      </div>
      {err&&<div style={s.error}>! {err}</div>}
      <TableCard title="Suppression List" count={data&&data.total}>
        {!data?<Loader/>:(
          <>
            <div style={{overflowX:'auto'}}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Email (Masked)</th>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Type</th>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Source</th>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Active</th>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Reason</th>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Added</th>
                    <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Override</th>
                  </tr>
                </thead>
                <tbody>
                  {data.suppressions.length===0?(
                    <tr><td colSpan={7} style={{...s.td,...s.empty, color:'#F0F4FA'}}>No suppression records found.</td></tr>
                  ):data.suppressions.map(s2=>(
                    <tr key={s2.id}>
                      <td style={s.td}><span style={{fontFamily:'monospace',fontSize:13}}>{s2.email}</span></td>
                      <td style={s.td}><SuppressionTypePill type={s2.suppression_type}/></td>
                      <td style={s.td}><span style={{fontSize:13,color:T.textMid}}>{s2.source||'-'}</span></td>
                      <td style={s.td}>
                        <CommsPill label={s2.active?'ACTIVE':'INACTIVE'} color={s2.active?'#F87171':'#4ADE80'}/>
                      </td>
                      <td style={s.td}><span style={{fontSize:12,color:T.textMid,maxWidth:200,display:'block',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s2.reason||'-'}</span></td>
                      <td style={s.td}><span style={{fontSize:13,color:T.textMid}}>{fmtDate(s2.created_at)}</span></td>
                      <td style={s.td}><button style={{...s.btn(),fontSize:12,opacity:0.5,cursor:'not-allowed'}} disabled>Override</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.pages>1&&<Pager page={page} pages={data.pages} total={data.total} onPage={setPage}/>}
          </>
        )}
      </TableCard>
    </>
  );
}


// -- COMMS TEMPLATES ----------------------------------------------------------
function SecCommsTemplates({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[preview,setPreview]=useState(null);

  useEffect(()=>{
    fetch(`${api}/v1/staff/comms/templates`,{credentials:'include'})
      .then(r=>r.json()).then(setData).catch(()=>setErr('Failed to load templates'));
  },[api]);
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const CATEGORY_COLORS={onboarding:T.chart2,auth:T.chart1,security:T.chart5};
  const templates=data.templates||[];

  return(
    <div style={{display:'flex',gap:12}}>
      <div style={{flex:1,minWidth:0}}>
        <TableCard title="Staff Email Templates" count={templates.length}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Template</th>
                <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Category</th>
                <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Subject</th>
                <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Sent Total</th>
                <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Failed</th>
                <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Last Sent</th>
                <th style={{...s.th, color:'#F0F4FA', fontWeight:600, letterSpacing:'0.2px', height:48}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map(tpl=>(
                <tr key={tpl.name} onClick={()=>setPreview(tpl)} style={{cursor:'pointer',background:preview&&preview.name===tpl.name?T.accentLight:'transparent'}}
                  onMouseEnter={ev=>{ if(!preview||preview.name!==tpl.name) ev.currentTarget.style.background=T.borderSoft; }}
                  onMouseLeave={ev=>ev.currentTarget.style.background=preview&&preview.name===tpl.name?T.accentLight:'transparent'}
                >
                  <td style={s.tds}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div style={{width:3,height:28,borderRadius:2,background:CATEGORY_COLORS[tpl.category]||T.textFaint,flexShrink:0}}/>
                      <div>
                        <div style={{fontSize:14,fontWeight:700,color:T.text}}>{tpl.label}</div>
                        <div style={{fontSize:12,fontFamily:'monospace',color:T.textMid}}>{tpl.name}</div>
                      </div>
                    </div>
                  </td>
                  <td style={s.td}>
                    <span style={{display:'inline-flex',alignItems:'center',padding:'4px 11px',borderRadius:6,fontSize:12,fontWeight:600,lineHeight:1.5,
                      background:CATEGORY_COLORS[tpl.category]?CATEGORY_COLORS[tpl.category]+'22':'rgba(255,255,255,0.06)',
                      color:CATEGORY_COLORS[tpl.category]||'#c9d3e0',
                      border:`1px solid ${CATEGORY_COLORS[tpl.category]?CATEGORY_COLORS[tpl.category]+'59':'rgba(255,255,255,0.16)'}`,
                    }}>
                      {tpl.category}
                    </span>
                  </td>
                  <td style={s.td}><span style={{fontSize:13,color:T.textMid}}>{tpl.subject}</span></td>
                  <td style={s.tds}>{fmt(tpl.sent_total)}</td>
                  <td style={s.td}>
                    <span style={{fontSize:13,fontWeight:700,color:tpl.failed_total>0?T.red:T.green}}>{fmt(tpl.failed_total)}</span>
                  </td>
                  <td style={s.td}><span style={{fontSize:13,color:T.textMid}}>{tpl.last_sent?fmtAgo(tpl.last_sent):'-'}</span></td>
                  <td style={s.td}>
                    <button style={{...s.btn(),fontSize:12,opacity:0.5,cursor:'not-allowed'}} disabled>Send Test</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      </div>

      {/* Template detail panel */}
      {preview&&(
        <div style={{width:300,flexShrink:0}}>
          <div style={{...s.panel,position:'sticky',top:20}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
              <div style={{fontSize:15,fontWeight:700}}>{preview.label}</div>
              <button onClick={()=>setPreview(null)} style={{background:'none',border:'none',color:T.textMid,cursor:'pointer',fontSize:18}}>×</button>
            </div>
            {[
              {label:'Internal Name', val:<code style={{fontFamily:'monospace',fontSize:12}}>{preview.name}</code>},
              {label:'Category',      val:preview.category},
              {label:'Token Type',    val:preview.token_type||'none'},
              {label:'Token Expiry',  val:preview.expires_hours?`${preview.expires_hours}h`:'-'},
              {label:'Sent Total',    val:fmt(preview.sent_total)},
              {label:'Failed Total',  val:<span style={{color:preview.failed_total>0?T.red:T.green,fontWeight:700}}>{fmt(preview.failed_total)}</span>},
              {label:'Last Sent',     val:preview.last_sent?fmtDate(preview.last_sent):'-'},
            ].map(({label,val})=>(
              <div key={label} style={s.kvRow}>
                <span style={s.kvLabel}>{label}</span>
                <span style={{...s.kvValue,maxWidth:160,textAlign:'right',fontSize:13}}>{val}</span>
              </div>
            ))}
            <div style={{marginTop:12,padding:'10px 12px',background:T.bg,borderRadius:6,border:`1px solid ${T.border}`}}>
              <div style={{fontSize:12,fontWeight:700,color:T.textMid,marginBottom:4}}>Subject Line</div>
              <div style={{fontSize:13,color:T.text}}>{preview.subject}</div>
            </div>
            <div style={{marginTop:10,padding:'10px 12px',background:T.bg,borderRadius:6,border:`1px solid ${T.border}`}}>
              <div style={{fontSize:12,fontWeight:700,color:T.textMid,marginBottom:4}}>Description</div>
              <div style={{fontSize:13,color:T.text,lineHeight:1.5}}>{preview.description}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// END COMMUNICATIONS CENTER
// ============================================================================


// ============================================================================
// MAILBOX INBOXES -- SecMailboxInbox
// ============================================================================

const INBOX_MAILBOXES = [
  { key: 'support', label: 'Support', email: 'support@visinvoke.com' },
  { key: 'billing', label: 'Billing', email: 'billing@visinvoke.com' },
  { key: 'hello',   label: 'Hello',   email: 'hello@visinvoke.com'   },
  { key: 'admin',   label: 'Admin',   email: 'admin@visinvoke.com'   },
];

function InboxMessageView({ api, message, onClose }) {
  if (!message) return null;

  const sanitizeHtml = (html) => {
    // Strip script tags, event handlers, and dangerous attributes
    return html
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/<iframe[\s\S]*?<\/iframe>/gi, '');
  };

  const body = message.body_html
    ? sanitizeHtml(message.body_html)
    : null;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#ffffff', marginBottom: 6, wordBreak: 'break-word' }}>
              {message.subject || '(no subject)'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 12, color: '#ffffff' }}>
              {message.sender_email && (
                <span><span style={{ color: '#ffffff' }}>From:</span> <span style={{ color: '#ffffff', fontWeight: 600 }}>{message.sender_email}</span></span>
              )}
              {message.recipient_email && (
                <span><span style={{ color: '#ffffff' }}>To:</span> <span style={{ color: '#ffffff' }}>{message.recipient_email}</span></span>
              )}
              <span><span style={{ color: '#ffffff' }}>Date:</span> <span style={{ color: '#ffffff' }}>{fmtDate(message.created_at)}</span></span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <span style={{
              fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4,
              background: message.direction === 'inbound' ? 'rgba(99,179,237,0.2)' : 'rgba(74,222,128,0.2)',
              color: message.direction === 'inbound' ? '#93c5fd' : '#4ADE80',
              border: `1px solid ${message.direction === 'inbound' ? 'rgba(99,179,237,0.4)' : 'rgba(74,222,128,0.4)'}`,
              textTransform: 'uppercase',
            }}>
              {message.direction}
            </span>
            {!message.is_read && (
              <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: 'rgba(45,108,255,0.2)', color: '#e8edf4', border: '1px solid rgba(45,108,255,0.4)', textTransform: 'uppercase' }}>
                Unread
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 18px' }}>
        {body ? (
          <div
            style={{ fontSize: 15, lineHeight: 1.7, color: '#FFFFFF', background: 'rgba(255,255,255,0.04)', borderRadius: 6, padding: '14px 16px', border: '1px solid rgba(255,255,255,0.1)', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: body }}
          />
        ) : message.body_text ? (
          <pre style={{ fontSize: 15, lineHeight: 1.7, color: '#FFFFFF', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', background: 'rgba(255,255,255,0.04)', borderRadius: 6, padding: '14px 16px', border: '1px solid rgba(255,255,255,0.1)', fontFamily: 'inherit', margin: 0 }}>
            {message.body_text}
          </pre>
        ) : (
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', fontStyle: 'italic' }}>No message body.</div>
        )}
      </div>
    </div>
  );
}

function InboxReplyBox({ api, thread, mailbox, onSent }) {
  const [open,         setOpen]         = useState(false);
  const [to,           setTo]           = useState('');
  const [cc,           setCc]           = useState('');
  const [subject,      setSubject]      = useState('');
  const [body,         setBody]         = useState('');
  const [sending,      setSending]      = useState(false);
  const [err,          setErr]          = useState('');
  const [ok,           setOk]           = useState('');
  const [canned,       setCanned]       = useState(null);
  const [cannedCat,    setCannedCat]    = useState('');
  const [cannedSel,    setCannedSel]    = useState(null);
  const [showCanned,   setShowCanned]   = useState(false);

  useEffect(() => {
    if (!showCanned || canned) return;
    fetch(`${api}/v1/staff/support/canned-responses`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (ok) setCanned(d); else setCanned({ categories: [], canned_responses: [] }); })
      .catch(() => setCanned({ categories: [], canned_responses: [] }));
  }, [api, showCanned, canned]);

  const initReply = (replyAll) => {
    setTo(thread.sender_email || '');
    setSubject(`Re: ${(thread.subject || '').replace(/^Re:\s*/i, '')}`);
    setBody('');
    setCc('');
    setErr('');
    setOk('');
    setOpen(true);
  };

  const send = () => {
    if (!to.trim() || !body.trim()) { setErr('To and message body are required.'); return; }
    setSending(true); setErr(''); setOk('');
    fetch(`${api}/v1/staff/gmail/send`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mailbox:   mailbox.key,
        to:        to.trim(),
        subject:   subject.trim(),
        body_text: body.trim(),
        thread_id: thread.id,
        note:      'Staff reply via Mailbox Inbox',
      }),
    })
      .then(r => r.json())
      .then(res => {
        if (res.error) { setErr(res.error); }
        else { setOk('Sent.'); setBody(''); setOpen(false); if (onSent) onSent(); }
      })
      .catch(() => setErr('Send failed.'))
      .finally(() => setSending(false));
  };

  return (
    <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '12px 18px', background: 'rgba(255,255,255,0.02)', flexShrink: 0 }}>
      {!open ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', background: '#2D6CFF', color: '#ffffff', fontFamily: font }} onClick={() => initReply(false)}>Reply</button>
          <button style={{ padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: '#ffffff', fontFamily: font }} onClick={() => { setTo(''); setSubject(`Re: ${thread.subject || ''}`); setBody(''); setCc(''); setErr(''); setOk(''); setOpen(true); }}>Compose</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ flex: 1, fontSize: 13, padding: '7px 12px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: '#ffffff', outline: 'none', fontFamily: font }} placeholder="To" value={to} onChange={e => setTo(e.target.value)} />
            <input style={{ flex: 1, fontSize: 13, padding: '7px 12px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: '#ffffff', outline: 'none', fontFamily: font }} placeholder="CC (optional)" value={cc} onChange={e => setCc(e.target.value)} />
          </div>
          <input style={{ fontSize: 13, padding: '7px 12px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: '#ffffff', outline: 'none', fontFamily: font, width: '100%', boxSizing: 'border-box' }} placeholder="Subject" value={subject} onChange={e => setSubject(e.target.value)} />
          {/* Canned responses */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${showCanned ? 'rgba(45,108,255,0.5)' : 'rgba(255,255,255,0.15)'}`, background: showCanned ? 'rgba(45,108,255,0.12)' : 'rgba(255,255,255,0.06)', color: showCanned ? '#2D6CFF' : 'rgba(255,255,255,0.7)', fontFamily: font }}
              onClick={() => setShowCanned(v => !v)}>
              {showCanned ? 'Hide Templates' : 'Canned Responses'}
            </button>
          </div>
          {showCanned && (
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '10px 12px' }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#c9d3e0', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Category</span>
                {!canned ? (
                  <span style={{ fontSize: 13, color: '#c9d3e0' }}>Loading...</span>
                ) : (canned.categories || []).map(cat => (
                  <button key={cat}
                    onClick={() => { setCannedCat(c => c === cat ? '' : cat); setCannedSel(null); }}
                    style={{ padding: '2px 9px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${cannedCat === cat ? 'rgba(45,108,255,0.5)' : 'rgba(255,255,255,0.15)'}`, background: cannedCat === cat ? 'rgba(45,108,255,0.12)' : 'transparent', color: cannedCat === cat ? '#2D6CFF' : 'rgba(255,255,255,0.6)' }}>
                    {cat.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
              {canned && (() => {
                const filtered = (canned.canned_responses || []).filter(r => !cannedCat || r.category === cannedCat);
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 200, overflowY: 'auto' }}>
                    {filtered.length === 0 && <div style={{ fontSize: 13, color: '#c9d3e0', padding: '4px 0' }}>No templates in this category.</div>}
                    {filtered.map(r => {
                      const isSel = cannedSel && cannedSel.id === r.id;
                      return (
                        <div key={r.id}
                          style={{ border: `1px solid ${isSel ? 'rgba(45,108,255,0.5)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 5, padding: '7px 10px', cursor: 'pointer', background: isSel ? 'rgba(45,108,255,0.08)' : 'transparent' }}
                          onClick={() => setCannedSel(isSel ? null : r)}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: isSel ? '#2D6CFF' : '#fff' }}>{r.title}</span>
                            <span style={{ fontSize: 12, color: '#E2E8F0', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, padding: '2px 7px', whiteSpace: 'nowrap' }}>{r.category.replace(/_/g, ' ')}</span>
                          </div>
                          {isSel && (
                            <>
                              <div style={{ fontSize: 11, color: '#c9d3e0', marginTop: 4, marginBottom: 4, fontStyle: 'italic' }}>Preview:</div>
                              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.55, whiteSpace: 'pre-wrap', maxHeight: 80, overflow: 'hidden', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4, padding: '5px 7px' }}>
                                {r.body.slice(0, 240)}{r.body.length > 240 ? '...' : ''}
                              </div>
                              <button
                                style={{ marginTop: 6, padding: '4px 12px', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: '#2D6CFF', color: '#ffffff', fontFamily: font }}
                                onClick={e => { e.stopPropagation(); setBody(r.body); setShowCanned(false); setCannedSel(null); }}>
                                Insert into Reply
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
          <textarea
            style={{ fontSize: 13, padding: '9px 12px', minHeight: 110, resize: 'vertical', lineHeight: 1.6, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: '#ffffff', outline: 'none', fontFamily: font, width: '100%', boxSizing: 'border-box' }}
            placeholder="Write your reply..."
            value={body}
            onChange={e => setBody(e.target.value)}
          />
          {err && <div style={{ fontSize: 12, color: '#F87171', fontWeight: 600 }}>! {err}</div>}
          {ok  && <div style={{ fontSize: 12, color: '#4ADE80', fontWeight: 600 }}>{ok}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', background: '#2D6CFF', color: '#ffffff', fontFamily: font }} onClick={send} disabled={sending}>
              {sending ? 'Sending...' : 'Send'}
            </button>
            <button style={{ padding: '7px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: '#ffffff', fontFamily: font }} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function InboxThreadReader({ api, thread, mailbox, onClose, onRefresh }) {
  const [messages,   setMessages]   = useState(null);
  const [err,        setErr]        = useState('');
  const [selMsg,     setSelMsg]     = useState(null);
  const [order,      setOrder]      = useState('asc');
  const [markedRead, setMarkedRead] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [staffList,  setStaffList]  = useState(null);
  const [assignTo,   setAssignTo]   = useState('');
  const [priority,   setPriority]   = useState(thread ? (thread.priority || 'normal') : 'normal');
  const [saving,     setSaving]     = useState(false);
  const [saveMsg,    setSaveMsg]    = useState('');

  const loadMessages = useCallback(() => {
    if (!thread) return;
    setMessages(null); setErr('');
    fetch(`${api}/v1/staff/gmail/threads/${thread.id}/messages`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        const msgs = d.messages || [];
        setMessages(msgs);
        setSelMsg(msgs.length ? msgs[msgs.length - 1] : null);
      })
      .catch(() => setErr('Failed to load messages'));
  }, [api, thread]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  useEffect(() => {
    if (!thread || !messages || markedRead) return;
    if (thread.unread_count > 0) {
      fetch(`${api}/v1/staff/gmail/threads/${thread.id}/mark-read`, {
        method: 'POST', credentials: 'include',
      }).then(() => setMarkedRead(true)).catch(() => {});
    }
  }, [api, thread, messages, markedRead]);

  useEffect(() => {
    if (!showAssign || staffList) return;
    fetch(`${api}/v1/staff/members?per_page=100`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setStaffList(d.staff || d.members || []))
      .catch(() => setStaffList([]));
  }, [api, showAssign, staffList]);

  useEffect(() => {
    if (thread) {
      setPriority(thread.priority || 'normal');
      setAssignTo(thread.assigned_staff_id || '');
    }
  }, [thread]);

  const saveAssign = () => {
    setSaving(true); setSaveMsg('');
    fetch(`${api}/v1/staff/comms/threads/${thread.id}/assign`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assigned_to: assignTo || null, notes: '' }),
    })
      .then(r => r.json())
      .then(d => {
        setSaveMsg(d.error ? `Error: ${d.error}` : 'Assigned.');
        if (!d.error && onRefresh) onRefresh();
      })
      .catch(() => setSaveMsg('Failed.'))
      .finally(() => setSaving(false));
  };

  const savePriority = (p) => {
    setPriority(p);
    fetch(`${api}/v1/staff/comms/threads/${thread.id}/priority`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ priority: p }),
    }).catch(() => {});
  };

  if (!thread) return null;

  const sorted = messages
    ? [...messages].sort((a, b) => order === 'asc'
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at))
    : [];

  const PRIORITY_COLORS = { high: '#F87171', normal: '#F59E0B', low: '#ffffff' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#0e1520' }}>
      {/* Thread header bar */}
      <div style={{ padding: '10px 16px', background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button style={{ background: 'none', border: 'none', color: '#ffffff', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }} onClick={onClose} title="Close">×</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff', wordBreak: 'break-word', lineHeight: 1.4 }}>
              {thread.subject || '(no subject)'}
            </div>
            <div style={{ fontSize: 11, color: '#ffffff', marginTop: 2 }}>
              {thread.message_count || 0} message{thread.message_count !== 1 ? 's' : ''}
              &nbsp;&middot;&nbsp;{thread.mailbox || mailbox.email}
              &nbsp;&middot;&nbsp;
              <span style={{ color: PRIORITY_COLORS[priority] || '#F59E0B' }}>
                {priority.charAt(0).toUpperCase() + priority.slice(1)} priority
              </span>
              {thread.assigned_staff_id && (
                <span style={{ color: '#63b3ed' }}>&nbsp;&middot;&nbsp;Assigned</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <select style={{ fontSize: 12, padding: '4px 8px', background: '#1e2a3a', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 5, color: '#ffffff', cursor: 'pointer', fontFamily: font, outline: 'none', colorScheme: 'dark' }} value={priority} onChange={e => savePriority(e.target.value)}>
              <option value="low" style={OPT_DARK}>Low</option>
              <option value="normal" style={OPT_DARK}>Normal</option>
              <option value="high" style={OPT_DARK}>High</option>
            </select>
            <button style={{ ...s.btn(showAssign ? 'primary' : undefined), fontSize: 12, padding: '5px 12px' }} onClick={() => setShowAssign(v => !v)}>
              Assign
            </button>
            <select style={{ fontSize: 12, padding: '4px 8px', background: '#1e2a3a', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 5, color: '#ffffff', cursor: 'pointer', fontFamily: font, outline: 'none', colorScheme: 'dark' }} value={order} onChange={e => setOrder(e.target.value)}>
              <option value="asc" style={OPT_DARK}>Oldest first</option>
              <option value="desc" style={OPT_DARK}>Newest first</option>
            </select>
          </div>
        </div>

        {/* Assign panel */}
        {showAssign && (
          <div style={{ marginTop: 10, padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#ffffff', fontWeight: 600 }}>Assign to:</span>
            <select
              style={{ fontSize: 13, padding: '5px 10px', flex: 1, minWidth: 180, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 5, color: '#ffffff', cursor: 'pointer', fontFamily: font, outline: 'none', colorScheme: 'dark' }}
              value={assignTo}
              onChange={e => setAssignTo(e.target.value)}
            >
              <option value="" style={OPT_DARK}>Unassigned</option>
              {(staffList || []).map(m => (
                <option key={m.user_id} value={m.user_id} style={OPT_DARK}>
                  {m.name || m.email}
                </option>
              ))}
            </select>
            <button style={{ ...s.btn('primary'), fontSize: 13, padding: '5px 14px' }} onClick={saveAssign} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button style={{ ...s.btn(), fontSize: 13, padding: '5px 10px' }} onClick={() => { setShowAssign(false); setSaveMsg(''); }}>
              Cancel
            </button>
            {saveMsg && <span style={{ fontSize: 12, color: saveMsg.startsWith('Error') ? T.red : '#4ADE80', fontWeight: 600 }}>{saveMsg}</span>}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* Left: message list */}
        <div style={{ width: 240, borderRight: '1px solid rgba(255,255,255,0.08)', overflowY: 'auto', flexShrink: 0, background: 'rgba(255,255,255,0.02)' }}>
          {!messages ? (
            <div style={{ padding: 16 }}><Loader /></div>
          ) : err ? (
            <div style={{ padding: 12, fontSize: 12, color: T.red }}>{err}</div>
          ) : sorted.length === 0 ? (
            <div style={{ padding: 16, fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>No messages.</div>
          ) : sorted.map(m => (
            <div
              key={m.id}
              onClick={() => setSelMsg(m)}
              style={{
                padding: '10px 14px',
                borderBottom: `1px solid ${T.borderSoft}`,
                cursor: 'pointer',
                background: selMsg && selMsg.id === m.id ? 'rgba(45,108,255,0.12)' : 'transparent',
                borderLeft: `3px solid ${m.direction === 'inbound' ? '#63b3ed' : '#4ADE80'}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: m.direction === 'inbound' ? '#63b3ed' : '#4ADE80', textTransform: 'uppercase' }}>
                  {m.direction}
                </span>
                {!m.is_read && (
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2D6CFF', display: 'inline-block' }} />
                )}
              </div>
              <div style={{ fontSize: 12, color: '#e8edf5', fontWeight: m.is_read ? 400 : 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.sender_email || '(unknown)'}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>{fmtAgo(m.created_at)}</div>
            </div>
          ))}
        </div>

        {/* Right: message body */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          {selMsg ? (
            <InboxMessageView api={api} message={selMsg} />
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c9d3e0', fontSize: 13 }}>
              Select a message to read
            </div>
          )}
          <InboxReplyBox api={api} thread={thread} mailbox={mailbox} onSent={() => { loadMessages(); if (onRefresh) onRefresh(); }} />
        </div>
      </div>
    </div>
  );
}

function InboxThreadList({ api, mailbox, counts, onSelect, selectedId }) {
  const [data,   setData]   = useState(null);
  const [err,    setErr]    = useState('');
  const [page,   setPage]   = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [draft,  setDraft]  = useState('');

  const load = useCallback(() => {
    setData(null); setErr('');
    const p = new URLSearchParams({ page, per_page: 50 });
    if (status) p.set('status', status);
    if (search) p.set('search', search);
    fetch(`${api}/v1/staff/gmail/mailboxes/${mailbox.key}/threads?${p}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.error) setErr(d.error); else setData(d); })
      .catch(() => setErr('Failed to load threads'));
  }, [api, mailbox.key, page, status, search]);

  useEffect(() => { load(); }, [load]);

  const mb = counts[mailbox.key] || {};
  const STATUSES = ['', 'open', 'triaged', 'pending', 'resolved', 'closed'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 12, padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
        {[
          ['Threads', mb.total_threads || 0, 'rgba(255,255,255,0.75)'],
          ['Open', mb.open_threads || 0, '#63b3ed'],
          ['Unread', mb.unread_threads || 0, '#F59E0B'],
        ].map(([l, v, c]) => (
          <div key={l} style={{ textAlign: 'center', minWidth: 60 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: c, lineHeight: 1 }}>{v}</div>
            <div style={{ fontSize: 11, color: '#ffffff', marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 6, padding: '8px 12px', background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
        <input
          style={{ flex: 1, fontSize: 13, padding: '6px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#fff', outline: 'none', fontFamily: font }}
          placeholder="Search subject..."
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setSearch(draft); setPage(1); } }}
        />
        <select style={{ fontSize: 13, padding: '6px 8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#fff', outline: 'none', cursor: 'pointer', fontFamily: font }} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          {STATUSES.map(st => <option key={st} value={st}>{st || 'All'}</option>)}
        </select>
        <button style={{ padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.8)', fontFamily: font }} onClick={() => { setSearch(draft); setPage(1); }}>Go</button>
        <button style={{ padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.8)', fontFamily: font }} onClick={load}>Refresh</button>
      </div>

      {err && <div style={{ ...s.error, margin: '8px 12px' }}>! {err}</div>}

      {/* Thread list */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {!data ? <div style={{ padding: 16 }}><Loader /></div> : data.threads.length === 0 ? (
          <div style={{ padding: '28px 16px', textAlign: 'center', color: '#c9d3e0', fontSize: 13 }}>No threads in this mailbox.</div>
        ) : data.threads.map(t => {
          const isSelected = t.id === selectedId;
          const hasUnread = t.unread_count > 0;
          return (
            <div
              key={t.id}
              onClick={() => onSelect(t)}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = isSelected ? 'rgba(45,108,255,0.12)' : 'transparent'; }}
              style={{
                padding: '10px 14px',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                cursor: 'pointer',
                background: isSelected ? 'rgba(45,108,255,0.12)' : 'transparent',
                borderLeft: `3px solid ${isSelected ? '#2D6CFF' : hasUnread ? '#F59E0B' : 'transparent'}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
                <div style={{ fontSize: 13, fontWeight: hasUnread ? 700 : 500, color: '#ffffff', wordBreak: 'break-word', flex: 1, lineHeight: 1.4 }}>
                  {t.subject || '(no subject)'}
                </div>
                {hasUnread && (
                  <span style={{ fontSize: 11, fontWeight: 700, background: '#F59E0B', color: '#000', borderRadius: 10, padding: '1px 6px', flexShrink: 0, marginTop: 1 }}>
                    {t.unread_count}
                  </span>
                )}
              </div>
              <div style={{ marginTop: 4, fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>
                {fmtAgo(t.last_message_at || t.created_at)}
                &nbsp;&middot;&nbsp;
                <span style={{ color: t.priority === 'high' ? '#F87171' : t.priority === 'low' ? '#fff' : '#F59E0B' }}>
                  {t.priority === 'high' ? 'High' : t.priority === 'low' ? 'Low' : 'Normal'}
                </span>
                &nbsp;&middot;&nbsp;
                <span style={{ color: t.assigned_staff_id ? '#63b3ed' : '#fff' }}>
                  {t.assigned_staff_id ? 'Assigned' : 'Unassigned'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {data && data.pages > 1 && (
        <div style={{ display: 'flex', gap: 8, padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: 12, color: '#c9d3e0', flexShrink: 0 }}>
          <button style={{ ...s.btn(), fontSize: 12, padding: '4px 10px' }} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
          <span style={{ lineHeight: '28px' }}>{page} / {data.pages}</span>
          <button style={{ ...s.btn(), fontSize: 12, padding: '4px 10px' }} disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}

function SecMailboxInbox({ api }) {
  const [activeTab,     setActiveTab]     = useState('support');
  const [counts,        setCounts]        = useState({});
  const [selectedThread, setSelectedThread] = useState(null);
  const [countsErr,     setCountsErr]     = useState('');

  const loadCounts = useCallback(() => {
    fetch(`${api}/v1/staff/gmail/inbox-counts`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.counts) setCounts(d.counts); })
      .catch(() => setCountsErr('Failed to load counts'));
  }, [api]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const activeMailbox = INBOX_MAILBOXES.find(m => m.key === activeTab);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)', minHeight: 600 }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '2px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)', flexShrink: 0 }}>
        {INBOX_MAILBOXES.map(mb => {
          const c = counts[mb.key] || {};
          const isActive = mb.key === activeTab;
          const unread = c.unread_threads || 0;
          return (
            <button
              key={mb.key}
              onClick={() => { setActiveTab(mb.key); setSelectedThread(null); }}
              style={{
                padding: '10px 20px', background: 'none', border: 'none', borderBottom: `2px solid ${isActive ? '#2D6CFF' : 'transparent'}`,
                marginBottom: -2, cursor: 'pointer', fontFamily: font, fontSize: 14,
                fontWeight: isActive ? 700 : 500, color: isActive ? '#2D6CFF' : 'rgba(255,255,255,0.6)',
                display: 'flex', alignItems: 'center', gap: 7,
              }}
            >
              {mb.label}
              {unread > 0 && (
                <span style={{ fontSize: 11, fontWeight: 700, background: '#F59E0B', color: '#000', borderRadius: 10, padding: '1px 6px', lineHeight: 1.4 }}>
                  {unread}
                </span>
              )}
            </button>
          );
        })}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', paddingRight: 14 }}>
          <button style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.8)', fontFamily: font }} onClick={() => { loadCounts(); }}>Refresh</button>
        </div>
      </div>

      {countsErr && <div style={{ ...s.error, margin: '8px 12px' }}>! {countsErr}</div>}

      {/* Split pane */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* Thread list */}
        <div style={{ width: 300, borderRight: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <InboxThreadList
            api={api}
            mailbox={activeMailbox}
            counts={counts}
            selectedId={selectedThread ? selectedThread.id : null}
            onSelect={t => setSelectedThread(t)}
          />
        </div>

        {/* Reader pane */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {selectedThread ? (
            <InboxThreadReader
              api={api}
              thread={selectedThread}
              mailbox={activeMailbox}
              onClose={() => setSelectedThread(null)}
              onRefresh={loadCounts}
            />
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', gap: 8 }}>
              <div style={{ fontSize: 32 }}>_</div>
              <div style={{ fontSize: 14 }}>Select a thread to read</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{activeMailbox.email}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// GMAIL CONNECTOR -- SecGmailMailboxes
// ============================================================================

const GMAIL_STATUS_CFG = {
  connected:    { color:'#4ADE80', bg:'#052e16', border:'#166534', label:'Connected' },
  disconnected: { color:'#9CA3AF', bg:'#1f2937', border:'#374151', label:'Disconnected' },
  error:        { color:'#F87171', bg:'#2d1a1a', border:'#7f1d1d', label:'Error' },
};

function SecGmailMailboxes({ api }) {
  const { hasPermission } = useStaffAuth();
  const [data,    setData]    = useState(null);
  const [err,     setErr]     = useState(null);
  const [busy,    setBusy]    = useState({});
  const [msg,     setMsg]     = useState(null);
  const [syncLog, setSyncLog] = useState(null);

  const load = useCallback(() => {
    setData(null);
    fetch(`${api}/v1/staff/gmail/mailboxes`, { credentials: 'include' })
      .then(r => r.json())
      .then(setData)
      .catch(() => setErr('Failed to load Gmail mailboxes'));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const loadSyncLog = () => {
    fetch(`${api}/v1/staff/gmail/sync-log?per_page=20`, { credentials: 'include' })
      .then(r => r.json())
      .then(setSyncLog)
      .catch(() => {});
  };

  const action = (mailbox, endpoint, label) => {
    setBusy(b => ({ ...b, [mailbox + endpoint]: true }));
    setMsg(null);
    fetch(`${api}/v1/staff/gmail/mailboxes/${mailbox}/${endpoint}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: `${label} via admin UI` }),
    })
      .then(r => r.json())
      .then(res => {
        setMsg(res.error ? { type: 'error', text: res.error } : { type: 'ok', text: `${label} complete` });
        load();
      })
      .catch(() => setMsg({ type: 'error', text: `${label} failed` }))
      .finally(() => setBusy(b => ({ ...b, [mailbox + endpoint]: false })));
  };

  const renew = () => {
    setBusy(b => ({ ...b, renew: true }));
    fetch(`${api}/v1/staff/gmail/watch/renew`, { method: 'POST', credentials: 'include' })
      .then(r => r.json())
      .then(res => {
        setMsg({ type: 'ok', text: `Watch renewal: ${JSON.stringify(res.results)}` });
        load();
      })
      .catch(() => setMsg({ type: 'error', text: 'Renewal failed' }))
      .finally(() => setBusy(b => ({ ...b, renew: false })));
  };

  const mailboxes = (data && data.mailboxes) || [];
  const configured = mailboxes.some(m => m.configured);

  return (
    <>
      {!configured && (
        <div style={{ padding: '12px 16px', background: '#2d1a1a', border: '1px solid #7f1d1d', borderRadius: 6, marginBottom: 12, fontSize: 13, color: '#F87171' }}>
          GOOGLE_SA_SECRET_NAME is not configured. Set this environment variable in EB to enable the Gmail connector.
        </div>
      )}

      {msg && (
        <div style={{ padding: '10px 14px', borderRadius: 6, marginBottom: 10, fontSize: 13,
          background: msg.type === 'ok' ? '#052e16' : '#2d1a1a',
          border: `1px solid ${msg.type === 'ok' ? '#166534' : '#7f1d1d'}`,
          color: msg.type === 'ok' ? '#4ADE80' : '#F87171' }}>
          {msg.text}
          <button onClick={() => setMsg(null)} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 13 }}>×</button>
        </div>
      )}

      {err && <div style={s.error}>! {err}</div>}

      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
        <button style={s.btn()} onClick={load}>Refresh</button>
        {hasPermission('comms.manage') && (
          <button style={s.btn()} onClick={renew} disabled={busy.renew}>
            {busy.renew ? 'Renewing...' : 'Renew All Watches'}
          </button>
        )}
        <button style={s.btn()} onClick={() => { loadSyncLog(); }}>View Sync Log</button>
      </div>

      {!data ? <Loader /> : (
        <TableCard title="Gmail Mailboxes" count={mailboxes.length}>
          <table style={{ ...s.table, minWidth: 700 }}>
            <thead><tr>
              {['Mailbox', 'Status', 'History ID', 'Watch Expiry', 'Last Sync', 'Actions'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {mailboxes.map(m => {
                const stCfg = GMAIL_STATUS_CFG[m.status] || GMAIL_STATUS_CFG.disconnected;
                const isConnected = m.status === 'connected';
                return (
                  <tr key={m.mailbox}>
                    <td style={s.tds}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{m.email_address}</div>
                      <div style={{ fontSize: 12, color: T.textFaint }}>{m.mailbox}</div>
                    </td>
                    <td style={s.td}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                        background: stCfg.bg, color: stCfg.color, border: `1px solid ${stCfg.border}` }}>
                        {stCfg.label}
                      </span>
                    </td>
                    <td style={s.td}>
                      <code style={{ fontFamily: 'monospace', fontSize: 12, color: T.textSub }}>
                        {m.history_id || '-'}
                      </code>
                    </td>
                    <td style={{ ...s.td, fontSize: 12, color: m.watch_expiry ? T.textMid : T.textFaint }}>
                      {m.watch_expiry ? fmtDate(m.watch_expiry) : 'No watch'}
                    </td>
                    <td style={{ ...s.td, fontSize: 12, color: T.textSub }}>
                      {m.last_sync_at ? fmtAgo(m.last_sync_at) : '-'}
                    </td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {hasPermission('comms.manage') && !isConnected && (
                          <button style={{ ...s.btn('primary'), fontSize: 12, padding: '4px 12px' }}
                            disabled={busy[m.mailbox + 'connect'] || !configured}
                            onClick={() => action(m.mailbox, 'connect', 'Connect')}>
                            {busy[m.mailbox + 'connect'] ? '...' : 'Connect'}
                          </button>
                        )}
                        {hasPermission('comms.manage') && isConnected && (
                          <>
                            <button style={{ ...s.btn(), fontSize: 12, padding: '4px 10px' }}
                              disabled={busy[m.mailbox + 'sync']}
                              onClick={() => action(m.mailbox, 'sync', 'Sync')}>
                              {busy[m.mailbox + 'sync'] ? '...' : 'Sync'}
                            </button>
                            <button style={{ ...s.btn(), fontSize: 12, padding: '4px 10px', color: T.accent }}
                              disabled={busy[m.mailbox + 'backfill']}
                              onClick={() => action(m.mailbox, 'backfill', 'Backfill')}>
                              {busy[m.mailbox + 'backfill'] ? '...' : 'Backfill'}
                            </button>
                            <button style={{ ...s.btn(), fontSize: 12, padding: '4px 10px', color: T.red }}
                              disabled={busy[m.mailbox + 'disconnect']}
                              onClick={() => action(m.mailbox, 'disconnect', 'Disconnect')}>
                              {busy[m.mailbox + 'disconnect'] ? '...' : 'Disconnect'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableCard>
      )}

      {syncLog && (
        <div style={{ marginTop: 16 }}>
          <TableCard title="Recent Sync Log" count={syncLog.total}>
            <table style={s.table}>
              <thead><tr>
                {['Mailbox', 'Operation', 'Synced', 'Created', 'Appended', 'Status', 'Time'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(syncLog.logs || []).map(row => (
                  <tr key={row.id}>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.accent }}>{row.mailbox}</span></td>
                    <td style={s.td}><code style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.operation}</code></td>
                    <td style={s.td}><span style={{ fontSize: 13, fontWeight: 700 }}>{row.messages_synced}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.green }}>{row.threads_created}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.chart2 }}>{row.threads_appended}</span></td>
                    <td style={s.td}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: row.status === 'ok' ? T.green : T.red }}>
                        {row.status}
                      </span>
                      {row.error && <div style={{ fontSize: 11, color: T.red, marginTop: 2 }}>{row.error}</div>}
                    </td>
                    <td style={{ ...s.td, fontSize: 12, color: T.textSub }}>{fmtAgo(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        </div>
      )}
    </>
  );
}

// ============================================================================
// END GMAIL CONNECTOR
// ============================================================================







// ============================================================================
// UNIVERSAL ENTITY INSPECTOR + BULK ACTION SYSTEM
// ============================================================================

const ENTITY_TYPE_CONFIG = {
  user:                 { label:'User',               color:'#3182ce', section:'users' },
  job:                  { label:'Job',                color:'#553c9a', section:'platform_jobs' },
  ticket:               { label:'Ticket',             color:'#805ad5', section:'support_queue' },
  audit:                { label:'Audit',              color:'#b7791f', section:'audit' },
  automation_rule:      { label:'Rule',               color:'#276749', section:'auto_rules' },
  autonomous_execution: { label:'Execution',          color:'#c05621', section:'autonomous_history' },
  session:              { label:'Session',            color:'#553c9a', section:'sessions' },
  mail_event:           { label:'Mail',               color:'#2b6cb0', section:'comms_queue' },
  generator:            { label:'Generator',          color:'#276749', section:'generators' },
  provider:             { label:'Provider',           color:'#805ad5', section:'platform_providers' },
  incident:             { label:'Incident',           color:'#c53030', section:'auto_incidents' },
  queue:                { label:'Queue',              color:'#b7791f', section:'platform_queues' },
  communication_thread: { label:'Thread',             color:'#0694a2', section:'comms_queue' },
  communication_message:{ label:'Message',            color:'#047481', section:'comms_queue' },
};

const RISK_CONFIG = {
  critical: { color:T.red,     bg:T.redBg,      border:T.redBorder },
  high:     { color:'#c05621', bg:'#fffaf0',     border:'#fbd38d' },
  medium:   { color:T.amber,   bg:T.amberBg,    border:T.amberBorder },
  low:      { color:T.green,   bg:T.greenBg,    border:T.greenBorder },
};

// ── Inspector context (slide-over panel state) ────────────────────────────────
// Singleton context via a module-level ref pattern - keeps inspector state
// out of the main StaffDashboard render tree so it doesn't trigger full rerenders.
// Used as: const { openEntity, closeEntity } = useEntityInspector()
const EntityInspectorContext = React.createContext(null);

// Live polling interval — configurable constant
// ============================================================================
// INVESTIGATION SESSION MEMORY
// ============================================================================

const ISM_KEY         = 'visinvoke_investigation_session';
const ISM_NOTES_KEY   = 'visinvoke_investigation_notes';
const ISM_MAX_HISTORY = 20;

// Pure localStorage helpers — no React state
const InvestigationSessionState = {
  load() {
    try {
      const raw = localStorage.getItem(ISM_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  },

  save(session) {
    try { localStorage.setItem(ISM_KEY, JSON.stringify(session)); } catch {}
  },

  clear() {
    try { localStorage.removeItem(ISM_KEY); } catch {}
  },

  addEntity(type, id, label) {
    const s = InvestigationSessionState.load() || { history: [], active: null, notes: {}, savedAt: null };
    const entry = { type, id, label: label || id, ts: Date.now() };
    // Deduplicate by type+id, keep newest
    s.history = [entry, ...s.history.filter(h => !(h.type === type && h.id === id))].slice(0, ISM_MAX_HISTORY);
    s.active  = entry;
    s.savedAt = Date.now();
    InvestigationSessionState.save(s);
  },

  setActive(entry) {
    const s = InvestigationSessionState.load() || { history: [], active: null, notes: {}, savedAt: null };
    s.active  = entry;
    s.savedAt = Date.now();
    InvestigationSessionState.save(s);
  },

  clearActive() {
    const s = InvestigationSessionState.load();
    if (!s) return;
    s.active  = null;
    s.savedAt = Date.now();
    InvestigationSessionState.save(s);
  },

  getNotes(type, id) {
    try {
      const raw = localStorage.getItem(ISM_NOTES_KEY);
      const all = raw ? JSON.parse(raw) : {};
      return all[`${type}:${id}`] || '';
    } catch { return ''; }
  },

  setNotes(type, id, text) {
    try {
      const raw = localStorage.getItem(ISM_NOTES_KEY);
      const all = raw ? JSON.parse(raw) : {};
      all[`${type}:${id}`] = text;
      localStorage.setItem(ISM_NOTES_KEY, JSON.stringify(all));
    } catch {}
  },
};

// InvestigationHistoryPanel — recent entities inside the inspector
function InvestigationHistoryPanel() {
  const { openEntity } = useEntityInspector();
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const s = InvestigationSessionState.load();
    if (s && s.history) setHistory(s.history.slice(0, 8));
  }, []);

  if (history.length === 0) return null;

  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'#8899aa', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:7 }}>
        Recent Investigation
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
        {history.map((h, i) => {
          const tc = ENTITY_TYPE_CONFIG[h.type] || { label:h.type, color:T.textSub };
          return (
            <div
              key={`${h.type}:${h.id}:${i}`}
              onClick={() => openEntity(h.type, h.id)}
              style={{
                display:'flex', alignItems:'center', gap:8, padding:'5px 8px',
                background:T.bg, border:`1px solid ${T.borderSoft}`,
                borderLeft:`3px solid ${tc.color}`, borderRadius:4,
                cursor:'pointer', transition:'background 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background=tc.color+'10'}
              onMouseLeave={e => e.currentTarget.style.background=T.bg}
            >
              <span style={{ fontSize:9, fontWeight:800, color:tc.color, flexShrink:0 }}>{tc.label}</span>
              <span style={{ fontSize:11, color:T.textMid, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'monospace' }}>
                {h.label && h.label !== h.id ? h.label : (typeof h.id === 'string' ? h.id.slice(0,20) : h.id)}
              </span>
              <span style={{ fontSize:10, color:T.textFaint, flexShrink:0, whiteSpace:'nowrap' }}>{fmtAgo(new Date(h.ts).toISOString())}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// InvestigationNotesPanel — local operator notes for the current entity
function InvestigationNotesPanel({ entityType, entityId }) {
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (entityType && entityId) {
      setNotes(InvestigationSessionState.getNotes(entityType, entityId));
      setSaved(false);
    }
  }, [entityType, entityId]);

  const handleChange = (val) => {
    setNotes(val);
    setSaved(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      InvestigationSessionState.setNotes(entityType, entityId, val);
      setSaved(true);
      timerRef.current = setTimeout(() => setSaved(false), 2000);
    }, 800);
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:5 }}>
        <span style={{ fontSize:11, fontWeight:700, color:'#8899aa', textTransform:'uppercase', letterSpacing:'0.08em', flex:1 }}>
          Investigation Notes
        </span>
        {saved && <span style={{ fontSize:10, color:T.green }}>Saved</span>}
        <span style={{ fontSize:10, color:'#445566' }}>Local only</span>
      </div>
      <textarea
        style={{
          width:'100%', boxSizing:'border-box',
          background:'#0a1019', border:'1px solid rgba(255,255,255,0.08)',
          borderRadius:4, color:'#cbd5e0', fontSize:12, fontFamily:'inherit',
          padding:'7px 9px', resize:'vertical', minHeight:72, outline:'none',
          lineHeight:1.55,
        }}
        placeholder="Add investigation notes here... (stored locally, not visible to users or in audit logs)"
        value={notes}
        onChange={e => handleChange(e.target.value)}
      />
    </div>
  );
}

// InvestigationResumeCard — shown in content area when active investigation detected
function InvestigationResumeCard({ onResume, onDismiss }) {
  const [session, setSession] = useState(null);

  useEffect(() => {
    const s = InvestigationSessionState.load();
    if (s && s.active && s.active.type && s.active.id) {
      setSession(s);
    }
  }, []);

  if (!session || !session.active) return null;

  const { active } = session;
  const tc = ENTITY_TYPE_CONFIG[active.type] || { label:active.type, color:T.textSub };
  const age = active.ts ? Math.floor((Date.now() - active.ts) / 1000) : null;
  const ageStr = age !== null ? (age < 3600 ? `${Math.floor(age/60)}m ago` : `${Math.floor(age/3600)}h ago`) : '';

  return (
    <div style={{
      display:'flex', alignItems:'center', gap:12,
      padding:'10px 14px', marginBottom:14,
      background:T.bg, border:`1px solid ${T.border}`,
      borderLeft:`3px solid ${tc.color}`, borderRadius:6,
      flexWrap:'wrap',
    }}>
      <span style={{ fontSize:9, fontWeight:800, color:tc.color, background:tc.color+'18', border:`1px solid ${tc.color}30`, borderRadius:3, padding:'1px 6px', flexShrink:0 }}>
        {tc.label}
      </span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:600, color:T.text }}>Resume Investigation</div>
        <div style={{ fontSize:11, color:T.textSub, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {active.label && active.label !== active.id ? active.label : (typeof active.id === 'string' ? active.id.slice(0,32) : active.id)}
          {ageStr && <span style={{ color:T.textFaint, marginLeft:6 }}>{ageStr}</span>}
        </div>
      </div>
      <button
        style={{ ...s.btn('primary'), fontSize:12, padding:'4px 12px', flexShrink:0 }}
        onClick={() => { onResume(active.type, active.id); setSession(null); }}
      >
        Resume
      </button>
      <button
        style={{ ...s.btn(), fontSize:12, padding:'4px 10px', flexShrink:0 }}
        onClick={() => { InvestigationSessionState.clearActive(); setSession(null); onDismiss && onDismiss(); }}
      >
        Dismiss
      </button>
    </div>
  );
}

// InvestigationBreadcrumbs — shows current investigation path from traversal stack
function InvestigationBreadcrumbs({ stack }) {
  if (!stack || stack.length <= 1) return null;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:4, flexWrap:'wrap', marginBottom:6 }}>
      {stack.map((s2, i) => {
        const tc = ENTITY_TYPE_CONFIG[s2.type] || { label:s2.type, color:T.textSub };
        return (
          <React.Fragment key={i}>
            {i > 0 && <span style={{ fontSize:10, color:'#445566' }}>-&gt;</span>}
            <span style={{ fontSize:10, fontWeight:700, color:tc.color }}>
              {tc.label}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ============================================================================
// END INVESTIGATION SESSION MEMORY
// ============================================================================

const LIVE_POLL_INTERVAL = 30000; // 30 seconds

// Freshness thresholds in ms
const FRESHNESS_AGING = 60000;   // 60s
const FRESHNESS_STALE = 180000;  // 180s

// Detect meaningful changes between two entity payloads
function detectEntityChanges(prev, next) {
  if (!prev || !next) return false;
  if (prev.risk !== next.risk) return true;
  if ((prev.timeline || []).length !== (next.timeline || []).length) return true;
  const pe = prev.entity || {};
  const ne = next.entity || {};
  if (pe.status !== ne.status) return true;
  if (pe.credits !== ne.credits) return true;
  if (pe.tier !== ne.tier) return true;
  const pl = prev.links || {};
  const nl = next.links || {};
  if (JSON.stringify(Object.keys(pl).map(k => Array.isArray(pl[k]) ? pl[k].length : pl[k]))
    !== JSON.stringify(Object.keys(nl).map(k => Array.isArray(nl[k]) ? nl[k].length : nl[k]))) return true;
  return false;
}

function EntityInspectorProvider({ children, api }) {
  const [stack,     setStack]     = useState([]);   // traversal stack: [{type, id}]
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [err,       setErr]       = useState(null);
  // Live state
  const [lastRefresh,  setLastRefresh]  = useState(null);   // timestamp ms
  const [liveStatus,   setLiveStatus]   = useState('live'); // live | aging | stale
  const [hasChanges,   setHasChanges]   = useState(false);  // "Updated Just Now" flash
  const prevDataRef    = useRef(null);
  const changesTimerRef= useRef(null);
  const pollTimerRef   = useRef(null);

  // Hard load — clears data, sets loading, used on open/navigate
  const load = useCallback((type, id) => {
    setLoading(true); setErr(null); setData(null);
    prevDataRef.current = null;
    fetch(`${api}/v1/staff/entity/${type}/${encodeURIComponent(id)}`, { credentials:'include' })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) {
          setData(d);
          prevDataRef.current = d;
          setLastRefresh(Date.now());
          setLiveStatus('live');
          setHasChanges(false);
        } else {
          setErr(d.error||'Failed');
        }
      })
      .catch(() => setErr('Network error'))
      .finally(() => setLoading(false));
  }, [api]);

  // Soft refresh — does NOT clear data, preserves all UI state
  const softRefresh = useCallback((type, id) => {
    fetch(`${api}/v1/staff/entity/${type}/${encodeURIComponent(id)}`, { credentials:'include' })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) {
          const changed = detectEntityChanges(prevDataRef.current, d);
          prevDataRef.current = d;
          setData(d);
          setLastRefresh(Date.now());
          setLiveStatus('live');
          if (changed) {
            setHasChanges(true);
            if (changesTimerRef.current) clearTimeout(changesTimerRef.current);
            changesTimerRef.current = setTimeout(() => setHasChanges(false), 8000);
          }
        }
        // On failure: retain last data, let freshness tracking handle stale display
      })
      .catch(() => {});
  }, [api]);

  // Freshness ticker — runs every 15s, updates liveStatus based on lastRefresh age
  useEffect(() => {
    if (!lastRefresh) return;
    const tick = setInterval(() => {
      const age = Date.now() - lastRefresh;
      if (age >= FRESHNESS_STALE) setLiveStatus('stale');
      else if (age >= FRESHNESS_AGING) setLiveStatus('aging');
      else setLiveStatus('live');
    }, 15000);
    return () => clearInterval(tick);
  }, [lastRefresh]);

  // Polling engine — polls ONLY the top-most active entity
  // Starts when inspector opens, stops when closed, resets when entity changes
  const current = stack[stack.length - 1] || null;

  useEffect(() => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    if (!current) return;
    pollTimerRef.current = setInterval(() => {
      softRefresh(current.type, current.id);
    }, LIVE_POLL_INTERVAL);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [current, softRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current)   clearInterval(pollTimerRef.current);
      if (changesTimerRef.current) clearTimeout(changesTimerRef.current);
    };
  }, []);

  const openEntity = useCallback((type, id, label) => {
    setStack(prev => [...prev, { type, id }]);
    load(type, id);
    InvestigationSessionState.addEntity(type, id, label || id);
  }, [load]);

  const goBack = useCallback(() => {
    setStack(prev => {
      const next = prev.slice(0, -1);
      if (next.length > 0) {
        const last = next[next.length - 1];
        load(last.type, last.id);
      } else {
        setData(null);
        setLastRefresh(null);
        setLiveStatus('live');
        setHasChanges(false);
      }
      return next;
    });
  }, [load]);

  const closeEntity = useCallback(() => {
    setStack([]);
    setData(null);
    setErr(null);
    setLastRefresh(null);
    setLiveStatus('live');
    setHasChanges(false);
    prevDataRef.current = null;
    if (pollTimerRef.current)   clearInterval(pollTimerRef.current);
    if (changesTimerRef.current) clearTimeout(changesTimerRef.current);
    InvestigationSessionState.clearActive();
  }, []);

  // ESC key
  useEffect(() => {
    if (stack.length === 0) return;
    const h = (e) => { if (e.key === 'Escape') closeEntity(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [stack.length, closeEntity]);

  return (
    <EntityInspectorContext.Provider value={{ openEntity, closeEntity, goBack, stack, current, data, loading, err, liveStatus, hasChanges, lastRefresh, api, softRefresh }}>
      {children}
    </EntityInspectorContext.Provider>
  );
}

function useEntityInspector() {
  return React.useContext(EntityInspectorContext);
}

// ── Live status badge ─────────────────────────────────────────────────────────
function LiveStatusBadge({ status }) {
  const cfg = {
    live:   { label:'LIVE',   color:'#38a169', bg:'rgba(56,161,105,0.1)',  border:'rgba(56,161,105,0.25)'  },
    aging:  { label:'AGING',  color:'#b7791f', bg:'rgba(183,121,31,0.1)', border:'rgba(183,121,31,0.25)' },
    stale:  { label:'STALE',  color:'#c53030', bg:'rgba(197,48,48,0.1)',  border:'rgba(197,48,48,0.25)'  },
  }[status] || { label:'LIVE', color:'#38a169', bg:'rgba(56,161,105,0.1)', border:'rgba(56,161,105,0.25)' };
  return (
    <span style={{
      fontSize:12, fontWeight:600, letterSpacing:'0.06em', display:'inline-flex', alignItems:'center',
      color:cfg.color, background:cfg.bg, border:`1px solid ${cfg.border}`,
      borderRadius:5, padding:'3px 8px', textTransform:'uppercase', flexShrink:0,
    }}>
      {cfg.label}
    </span>
  );
}

// ── Clickable entity chip ─────────────────────────────────────────────────────
function EntityChip({ type, id, label, severity }) {
  const ctx = useEntityInspector();
  const tc = ENTITY_TYPE_CONFIG[type] || { label:type, color:T.textSub };
  if (!ctx) return <span style={{ fontSize:12, color:T.textSub }}>{label||id}</span>;
  return (
    <span
      onClick={e => { e.stopPropagation(); ctx.openEntity(type, id); }}
      style={{
        display:'inline-flex', alignItems:'center', gap:4,
        padding:'2px 8px', borderRadius:4, fontSize:12, fontWeight:600,
        color:tc.color, background:tc.color+'18',
        border:`1px solid ${tc.color}35`,
        cursor:'pointer', transition:'all 0.1s',
        textDecoration:'none',
      }}
      onMouseEnter={e => e.currentTarget.style.background = tc.color+'30'}
      onMouseLeave={e => e.currentTarget.style.background = tc.color+'18'}
      title={`Inspect ${type}: ${id}`}
    >
      <span style={{ fontSize:9, fontWeight:800, opacity:0.7 }}>{tc.label}</span>
      {label || (typeof id === 'string' ? id.slice(0,16)+(id.length>16?'...':'') : id)}
    </span>
  );
}

// ── Inspector timeline row ────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars


// ============================================================================
// RELATIONSHIP GRAPH SYSTEM
// ============================================================================

// Severity visual config for relationship graph - matches existing operational severity patterns
const REL_SEVERITY = {
  critical: { color:T.red,     bg:T.redBg,      border:T.redBorder,   glow:'rgba(197,48,48,0.18)' },
  high:     { color:'#c05621', bg:'#fffaf0',     border:'#fbd38d',     glow:'rgba(192,86,33,0.15)' },
  moderate: { color:T.amber,   bg:T.amberBg,    border:T.amberBorder, glow:'rgba(183,121,31,0.12)' },
  low:      { color:T.green,   bg:T.greenBg,    border:T.greenBorder, glow:'rgba(39,103,73,0.10)' },
};

// Category visual config - gold/dark operational palette
const REL_CATEGORY_CONFIG = {
  user:        { label:'User',        color:'#3182ce', icon:'U' },
  provider:    { label:'Provider',    color:'#805ad5', icon:'P' },
  queue:       { label:'Queue',       color:'#b7791f', icon:'Q' },
  incident:    { label:'Incident',    color:'#c53030', icon:'!' },
  automation:  { label:'Automation',  color:'#276749', icon:'A' },
  mail:        { label:'Mail',        color:'#2b6cb0', icon:'M' },
  support:     { label:'Support',     color:'#553c9a', icon:'S' },
  security:    { label:'Security',    color:'#c53030', icon:'X' },
  credits:     { label:'Credits',     color:'#276749', icon:'C' },
  generations: { label:'Generations', color:'#b7791f', icon:'G' },
};

// Map backend link keys to relationship categories + metadata
const REL_KEY_MAP = {
  // user
  recent_jobs:            { category:'generations', type:'Recent Jobs',         entityType:'job',       severity:'low' },
  failed_jobs:            { category:'generations', type:'Failed Jobs',         entityType:'job',       severity:'high' },
  user_failed_jobs:       { category:'generations', type:'User Failed Jobs',    entityType:'job',       severity:'high' },
  total_jobs:             { category:'generations', type:'Total Jobs',          entityType:null,        severity:'low' },
  // tickets
  open_tickets:           { category:'support',     type:'Open Tickets',        entityType:'ticket',    severity:'moderate' },
  recent_tickets:         { category:'support',     type:'Recent Tickets',      entityType:'ticket',    severity:'low' },
  // mail
  mail_events:            { category:'mail',        type:'Mail Events',         entityType:'mail_event',severity:'low' },
  mail_failures:          { category:'mail',        type:'Mail Failures',       entityType:'mail_event',severity:'high' },
  mail_failed:            { category:'mail',        type:'Mail Failures',       entityType:'mail_event',severity:'high' },
  // sessions
  recent_sessions:        { category:'security',    type:'Recent Sessions',     entityType:'session',   severity:'low' },
  risky_sessions:         { category:'security',    type:'Risky Sessions',      entityType:'session',   severity:'high' },
  // automation
  automation_recoveries:  { category:'automation',  type:'Automation Recoveries',entityType:'automation_rule',severity:'moderate' },
  autonomous_executions:  { category:'automation',  type:'Autonomous Executions',entityType:'autonomous_execution',severity:'moderate' },
  // incidents
  related_incidents:      { category:'incident',    type:'Related Incidents',   entityType:'incident',  severity:'high' },
  active_incidents:       { category:'incident',    type:'Active Incidents',    entityType:'incident',  severity:'critical' },
  // credits
  credit_events:          { category:'credits',     type:'Credit Events',       entityType:null,        severity:'low' },
  credit_refunds:         { category:'credits',     type:'Credit Refunds',      entityType:null,        severity:'moderate' },
  // provider
  provider_failures:      { category:'provider',    type:'Provider Failures',   entityType:'provider',  severity:'high' },
  // queue
  queue_links:            { category:'queue',       type:'Queues',              entityType:'queue',     severity:'low' },
  // user links from job/ticket
  linked_user:            { category:'user',        type:'Linked User',         entityType:'user',      severity:'low' },
  user_id:                { category:'user',        type:'User',                entityType:'user',      severity:'low' },
  // suppression
  suppression_events:     { category:'mail',        type:'Suppression Events',  entityType:'mail_event',severity:'critical' },
  // audit
  audit_trail:            { category:'security',    type:'Audit Trail',         entityType:'audit',     severity:'low' },
};

// Normalize backend links object into grouped relationship categories
function normalizeRelationships(links) {
  const groups = {};
  if (!links || typeof links !== 'object') return groups;

  Object.entries(links).forEach(([key, value]) => {
    const map = REL_KEY_MAP[key];
    if (!map) return;

    // Scalars (counts, booleans) - skip non-traversable scalars with no entityType
    const isArray = Array.isArray(value);
    const count = isArray ? value.length : (typeof value === 'number' ? value : 0);
    if (count === 0 && !isArray) return;
    if (isArray && value.length === 0) return;

    const cat = map.category;
    if (!groups[cat]) {
      groups[cat] = { category: cat, items: [] };
    }
    groups[cat].items.push({
      key,
      type: map.type,
      entityType: map.entityType,
      severity: map.severity,
      values: isArray ? value : [],
      count: count,
    });
  });

  return groups;
}

// Derive aggregate severity for a category group
function groupSeverity(items) {
  const rank = { critical:4, high:3, moderate:2, low:1 };
  let max = 1;
  items.forEach(item => {
    const r = rank[item.severity] || 1;
    if (r > max) max = r;
  });
  const map = { 4:'critical', 3:'high', 2:'moderate', 1:'low' };
  return map[max];
}

// RelationshipStrengthBadge — shows relationship severity as a compact pill
function RelationshipStrengthBadge({ severity }) {
  const cfg = REL_SEVERITY[severity] || REL_SEVERITY.low;
  return (
    <span style={{
      fontSize:10, fontWeight:800, letterSpacing:'0.06em',
      color:cfg.color, background:cfg.bg, border:`1px solid ${cfg.border}`,
      borderRadius:3, padding:'1px 5px', textTransform:'uppercase', flexShrink:0,
    }}>
      {severity}
    </span>
  );
}

// RelationshipCard — single relationship entry inside a category section
function RelationshipCard({ item, catColor }) {
  const [expanded, setExpanded] = useState(false);
  const sev = REL_SEVERITY[item.severity] || REL_SEVERITY.low;
  const hasItems = item.values && item.values.length > 0;
  const displayCount = item.count;

  return (
    <div style={{
      background:'#0f1824',
      border:`1px solid ${item.severity === 'critical' ? sev.border : 'rgba(255,255,255,0.07)'}`,
      borderLeft:`3px solid ${sev.color}`,
      borderRadius:5,
      marginBottom:4,
      overflow:'hidden',
      boxShadow: item.severity === 'critical' ? `0 0 8px ${sev.glow}` : 'none',
      transition:'box-shadow 0.15s',
    }}>
      {/* Card header */}
      <div
        style={{
          display:'flex', alignItems:'center', gap:8, padding:'7px 10px',
          cursor: hasItems ? 'pointer' : 'default',
        }}
        onClick={() => hasItems && setExpanded(v => !v)}
      >
        <span style={{ fontSize:12, fontWeight:700, color:'#cbd5e0', flex:1 }}>{item.type}</span>
        <span style={{
          fontSize:12, fontWeight:800, color:sev.color,
          background:sev.color+'18', border:`1px solid ${sev.color}30`,
          borderRadius:3, padding:'1px 7px', minWidth:24, textAlign:'center',
        }}>
          {displayCount}
        </span>
        <RelationshipStrengthBadge severity={item.severity}/>
        {hasItems && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2"
            style={{ transform:expanded?'rotate(180deg)':'none', transition:'transform 0.15s', flexShrink:0 }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        )}
      </div>

      {/* Expanded entity chips */}
      {expanded && hasItems && (
        <div style={{
          padding:'6px 10px 8px 10px',
          borderTop:'1px solid rgba(255,255,255,0.06)',
          background:'rgba(0,0,0,0.2)',
        }}>
          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
            {item.values.slice(0, 8).map((v, i) => {
              const itemId   = v.id || v.ticket_id || v.job_id || v.slug || String(i);
              const itemLabel = v.email || v.subject || v.name || v.slug || v.type || (typeof itemId === 'string' ? itemId.slice(0,16) : itemId);
              const etype = item.entityType;
              if (!etype || !itemId) {
                return (
                  <span key={i} style={{
                    fontSize:11, color:'#8899aa', background:'rgba(255,255,255,0.05)',
                    border:'1px solid rgba(255,255,255,0.08)', borderRadius:3, padding:'2px 7px',
                  }}>
                    {itemLabel}
                  </span>
                );
              }
              return <EntityChip key={itemId} type={etype} id={itemId} label={itemLabel}/>;
            })}
            {item.values.length > 8 && (
              <span style={{ fontSize:11, color:'#556677', padding:'2px 6px' }}>+{item.values.length-8} more</span>
            )}
          </div>
          {/* Last activity */}
          {item.values[0] && (item.values[0].created_at || item.values[0].updated_at || item.values[0].ts) && (
            <div style={{ fontSize:10, color:'#556677', marginTop:5 }}>
              Last activity: {fmtAgo(item.values[0].created_at || item.values[0].updated_at || item.values[0].ts)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// RelationshipSection — collapsible category group
function RelationshipSection({ category, group }) {
  const [open, setOpen] = useState(true);
  const cfg = REL_CATEGORY_CONFIG[category] || { label:category, color:T.textSub, icon:'?' };
  const sev = groupSeverity(group.items);
  const sevCfg = REL_SEVERITY[sev] || REL_SEVERITY.low;
  const totalCount = group.items.reduce((a, b) => a + b.count, 0);

  return (
    <div style={{ marginBottom:8 }}>
      {/* Section header */}
      <div
        style={{
          display:'flex', alignItems:'center', gap:7, padding:'6px 8px',
          cursor:'pointer', borderRadius:5, userSelect:'none',
          background:'rgba(255,255,255,0.03)',
          border:'1px solid rgba(255,255,255,0.06)',
          marginBottom: open ? 6 : 0,
        }}
        onClick={() => setOpen(v => !v)}
      >
        <span style={{
          width:18, height:18, borderRadius:3, flexShrink:0,
          background:cfg.color+'22', border:`1px solid ${cfg.color}40`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:9, fontWeight:800, color:cfg.color,
        }}>
          {cfg.icon}
        </span>
        <span style={{ fontSize:12, fontWeight:700, color:'#e2e8f0', flex:1, letterSpacing:'0.03em' }}>
          {cfg.label}
        </span>
        {totalCount > 0 && (
          <span style={{
            fontSize:11, fontWeight:700, color:sevCfg.color,
            background:sevCfg.color+'15', border:`1px solid ${sevCfg.color}30`,
            borderRadius:3, padding:'0px 6px',
          }}>
            {totalCount}
          </span>
        )}
        {(sev === 'critical' || sev === 'high') && <RelationshipStrengthBadge severity={sev}/>}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2"
          style={{ transform:open?'rotate(180deg)':'none', transition:'transform 0.15s', flexShrink:0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {/* Cards */}
      {open && (
        <div style={{ paddingLeft:6 }}>
          {group.items.map((item, i) => (
            <RelationshipCard key={item.key||i} item={item} catColor={cfg.color}/>
          ))}
        </div>
      )}
    </div>
  );
}

// EntityRelationshipGraph — replaces the flat links rendering inside EntityInspectorPanel
function EntityRelationshipGraph({ links }) {
  const groups = normalizeRelationships(links);
  const categoryOrder = ['incident','security','provider','mail','support','automation','queue','user','credits','generations'];
  const orderedEntries = categoryOrder
    .filter(cat => groups[cat])
    .map(cat => [cat, groups[cat]])
    .concat(
      Object.entries(groups).filter(([cat]) => !categoryOrder.includes(cat))
    );

  if (orderedEntries.length === 0) {
    return (
      <div style={{ fontSize:12, color:'#556677', padding:'8px 0', textAlign:'center' }}>
        No relationships detected
      </div>
    );
  }

  return (
    <div>
      {orderedEntries.map(([cat, group]) => (
        <RelationshipSection key={cat} category={cat} group={group}/>
      ))}
    </div>
  );
}

// ============================================================================
// END RELATIONSHIP GRAPH SYSTEM
// ============================================================================

// ============================================================================
// TIMELINE CORRELATION ENGINE
// ============================================================================

// Maps event types/metadata to correlation groups
const TIMELINE_GROUP_ORDER = ['CAUSE','FAILURE','AUTOMATION_RESPONSE','STAFF_RESPONSE','RECOVERY','FINAL_STATE','OTHER'];

const TIMELINE_GROUP_CONFIG = {
  CAUSE:              { label:'Cause',              color:'#b7791f', bg:'rgba(183,121,31,0.08)',  border:'rgba(183,121,31,0.2)',  icon:'C' },
  FAILURE:            { label:'Failure',            color:'#c53030', bg:'rgba(197,48,48,0.08)',   border:'rgba(197,48,48,0.2)',   icon:'F' },
  AUTOMATION_RESPONSE:{ label:'Automation Response',color:'#276749', bg:'rgba(39,103,73,0.08)',   border:'rgba(39,103,73,0.2)',   icon:'A' },
  STAFF_RESPONSE:     { label:'Staff Response',     color:'#3182ce', bg:'rgba(49,130,206,0.08)',  border:'rgba(49,130,206,0.2)',  icon:'S' },
  RECOVERY:           { label:'Recovery',           color:'#553c9a', bg:'rgba(85,60,154,0.08)',   border:'rgba(85,60,154,0.2)',   icon:'R' },
  FINAL_STATE:        { label:'Final State',        color:'#2b6cb0', bg:'rgba(43,108,176,0.08)',  border:'rgba(43,108,176,0.2)',  icon:'Z' },
  OTHER:              { label:'Other Events',       color:'#556677', bg:'rgba(85,102,119,0.06)',  border:'rgba(85,102,119,0.15)', icon:'O' },
};

// Classify a single event into a correlation group
// Uses event type, severity, and any group/chain hint in metadata
function classifyTimelineEvent(ev) {
  // Explicit group hint takes priority if backend provides it
  if (ev.group && TIMELINE_GROUP_CONFIG[ev.group]) return ev.group;
  if (ev.chain && TIMELINE_GROUP_CONFIG[ev.chain]) return ev.chain;

  const type = ev.type || '';
  const sev  = ev.severity || 'low';
  const title = (ev.title || '').toLowerCase();

  // CAUSE — events that indicate the root precondition
  if (type === 'failed_login') return 'CAUSE';
  if (type === 'suppression') return 'CAUSE';
  if (title.includes('rate limit') || title.includes('quota')) return 'CAUSE';
  if (title.includes('credit') && title.includes('insufficient')) return 'CAUSE';
  if (title.includes('provider') && title.includes('down')) return 'CAUSE';

  // FAILURE — job/generation/mail failures
  if (type === 'job_failed' || title.includes('job failed') || title.includes('generation failed')) return 'FAILURE';
  if (type === 'mail_event' && (title.includes('bounce') || title.includes('failed') || title.includes('reject'))) return 'FAILURE';
  if (type === 'delivery_event' && (title.includes('bounce') || title.includes('complaint'))) return 'FAILURE';
  if (sev === 'critical' && (title.includes('fail') || title.includes('error'))) return 'FAILURE';

  // AUTOMATION_RESPONSE — autonomous/automation engine events
  if (type === 'automation_trigger' || type === 'automation_action' || type === 'autonomous_execution') return 'AUTOMATION_RESPONSE';
  if (title.includes('auto') && (title.includes('requeue') || title.includes('recovery') || title.includes('failover'))) return 'AUTOMATION_RESPONSE';
  if (title.includes('rule triggered') || title.includes('autonomous')) return 'AUTOMATION_RESPONSE';

  // STAFF_RESPONSE — staff actions, notes, replies, assignments
  if (type === 'admin_action' || type === 'staff_note' || type === 'staff_reply') return 'STAFF_RESPONSE';
  if (title.includes('staff') || title.includes('assigned') || title.includes('escalat')) return 'STAFF_RESPONSE';
  if (title.includes('grant') || (title.includes('credit') && title.includes('added'))) return 'STAFF_RESPONSE';

  // RECOVERY — requeue, retry, resolution, cleared states
  if (title.includes('requeue') || title.includes('retry') || title.includes('recovered')) return 'RECOVERY';
  if (title.includes('suppression cleared') || title.includes('unblock')) return 'RECOVERY';
  if (type === 'support_ticket' && title.includes('resolv')) return 'RECOVERY';

  // FINAL_STATE — resolved, closed, completed, concluded
  if (title.includes('resolved') || title.includes('closed') || title.includes('completed')) return 'FINAL_STATE';
  if (title.includes('session end') || title.includes('session expired')) return 'FINAL_STATE';
  if (type === 'staff_session' && title.includes('end')) return 'FINAL_STATE';

  return 'OTHER';
}

// Group and sort a flat timeline array into correlation chains
function correlateTimeline(timeline) {
  if (!timeline || timeline.length === 0) return {};
  const groups = {};
  timeline.forEach(ev => {
    const g = classifyTimelineEvent(ev);
    if (!groups[g]) groups[g] = [];
    groups[g].push(ev);
  });
  // Within each group sort chronologically ascending
  Object.values(groups).forEach(evs => evs.sort((a, b) => new Date(a.ts||0) - new Date(b.ts||0)));
  return groups;
}

// Aggregate severity for a group of events
function groupTimelineSeverity(events) {
  const rank = { critical:4, high:3, medium:2, moderate:2, low:1 };
  let max = 0;
  events.forEach(ev => { const r = rank[ev.severity]||0; if (r > max) max = r; });
  if (max >= 4) return 'critical';
  if (max >= 3) return 'high';
  if (max >= 2) return 'moderate';
  return 'low';
}

// TimelineSeverityBadge
function TimelineSeverityBadge({ severity }) {
  const cfg = {
    critical:{ color:T.red,     bg:T.redBg,      border:T.redBorder   },
    high:    { color:'#c05621', bg:'#fffaf0',     border:'#fbd38d'     },
    moderate:{ color:T.amber,   bg:T.amberBg,    border:T.amberBorder },
    low:     { color:T.green,   bg:T.greenBg,    border:T.greenBorder },
  }[severity] || { color:T.green, bg:T.greenBg, border:T.greenBorder };
  return (
    <span style={{
      fontSize:9, fontWeight:800, letterSpacing:'0.06em',
      color:cfg.color, background:cfg.bg, border:`1px solid ${cfg.border}`,
      borderRadius:3, padding:'1px 5px', textTransform:'uppercase', flexShrink:0,
    }}>
      {severity}
    </span>
  );
}

// TimelineChainCard — single correlated event card
function TimelineChainCard({ ev, groupColor }) {
  const [expanded, setExpanded] = useState(false);
  const sev = ev.severity || 'low';
  const sevColors = {
    critical:T.red, high:'#c05621', medium:T.amber, moderate:T.amber, low:T.accent,
  };
  const dotColor = sevColors[sev] || T.accent;

  return (
    <div style={{
      marginBottom:3,
      borderLeft:`2px solid ${sev === 'critical' ? T.red : sev === 'high' ? '#c05621' : groupColor+'60'}`,
      paddingLeft:8,
    }}>
      <div
        style={{ display:'flex', alignItems:'flex-start', gap:7, cursor:'pointer', padding:'5px 0' }}
        onClick={() => setExpanded(v => !v)}
      >
        <span style={{ width:6, height:6, borderRadius:'50%', background:dotColor, flexShrink:0, marginTop:4 }}/>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
            <span style={{ fontSize:13, fontWeight:600, color:T.text }}>{ev.title}</span>
            {(sev === 'critical' || sev === 'high') && <TimelineSeverityBadge severity={sev}/>}
          </div>
          {ev.detail && (
            <div style={{ fontSize:11, color:T.textSub, marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:340 }}>
              {ev.detail}
            </div>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          {ev.entity_type && ev.entity_id && (
            <EntityChip type={ev.entity_type} id={ev.entity_id} label={ev.entity_type}/>
          )}
          <span style={{ fontSize:10, color:T.textFaint, whiteSpace:'nowrap' }}>{ev.ts ? fmtAgo(ev.ts) : ''}</span>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={T.textFaint} strokeWidth="2"
            style={{ transform:expanded?'rotate(180deg)':'none', transition:'transform 0.12s', flexShrink:0 }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </div>

      {expanded && (
        <div style={{ padding:'6px 0 6px 13px', borderTop:`1px solid ${T.borderSoft}` }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2px 16px', marginBottom:6 }}>
            {[
              ['Type',      ev.type],
              ['Severity',  ev.severity],
              ['Timestamp', fmtDate(ev.ts)],
              ['Actor',     ev.actor],
            ].filter(([,v]) => v).map(([k,v]) => (
              <div key={k} style={s.kvRow}>
                <span style={s.kvLabel}>{k}</span>
                <span style={{ ...s.kvValue, fontSize:11 }}>{v}</span>
              </div>
            ))}
          </div>
          {ev.meta && Object.keys(ev.meta).length > 0 && (
            <div style={{ background:T.bg, border:`1px solid ${T.borderSoft}`, borderRadius:4, padding:'5px 8px' }}>
              <div style={{ fontSize:10, fontWeight:700, color:T.textFaint, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:3 }}>Metadata</div>
              <pre style={{ margin:0, fontSize:11, color:T.textMid, fontFamily:'monospace', whiteSpace:'pre-wrap', wordBreak:'break-all', lineHeight:1.5 }}>
                {JSON.stringify(ev.meta, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// TimelineGroupHeader — collapsible section header for a correlation group
function TimelineGroupHeader({ groupKey, events, open, onToggle }) {
  const cfg = TIMELINE_GROUP_CONFIG[groupKey] || TIMELINE_GROUP_CONFIG.OTHER;
  const sev = groupTimelineSeverity(events);
  const isCritical = sev === 'critical';
  const isHigh = sev === 'high';

  return (
    <div
      style={{
        display:'flex', alignItems:'center', gap:7, padding:'6px 8px',
        cursor:'pointer', borderRadius:5, userSelect:'none', marginBottom: open ? 5 : 0,
        background: isCritical ? 'rgba(197,48,48,0.07)' : isHigh ? 'rgba(192,86,33,0.06)' : cfg.bg,
        border:`1px solid ${isCritical ? T.redBorder : isHigh ? '#fbd38d' : cfg.border}`,
      }}
      onClick={onToggle}
    >
      <span style={{
        width:18, height:18, borderRadius:3, flexShrink:0,
        background:cfg.color+'22', border:`1px solid ${cfg.color}40`,
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:9, fontWeight:800, color:cfg.color,
      }}>
        {cfg.icon}
      </span>
      <span style={{ fontSize:12, fontWeight:700, color:'#e2e8f0', flex:1, letterSpacing:'0.02em' }}>
        {cfg.label}
      </span>
      <span style={{
        fontSize:11, fontWeight:700, color:cfg.color,
        background:cfg.color+'15', border:`1px solid ${cfg.color}30`,
        borderRadius:3, padding:'0 6px', minWidth:20, textAlign:'center',
      }}>
        {events.length}
      </span>
      {(isCritical || isHigh) && <TimelineSeverityBadge severity={sev}/>}
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2"
        style={{ transform:open?'rotate(180deg)':'none', transition:'transform 0.13s', flexShrink:0 }}>
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </div>
  );
}

// TimelineGroup — one correlated group with its chain cards
function TimelineGroup({ groupKey, events }) {
  const [open, setOpen] = useState(groupKey !== 'OTHER');
  const cfg = TIMELINE_GROUP_CONFIG[groupKey] || TIMELINE_GROUP_CONFIG.OTHER;

  return (
    <div style={{ marginBottom:7 }}>
      <TimelineGroupHeader groupKey={groupKey} events={events} open={open} onToggle={() => setOpen(v => !v)}/>
      {open && (
        <div style={{ paddingLeft:6, paddingTop:2 }}>
          {events.map((ev, i) => (
            <TimelineChainCard key={ev.id||ev.ts||i} ev={ev} groupColor={cfg.color}/>
          ))}
        </div>
      )}
    </div>
  );
}

// TimelineCorrelationEngine — replaces flat timeline rendering
function TimelineCorrelationEngine({ timeline }) {
  const groups = correlateTimeline(timeline);
  const totalGroups = Object.keys(groups).length;

  if (!timeline || timeline.length === 0) {
    return (
      <div style={{ fontSize:12, color:'#556677', padding:'8px 0', textAlign:'center' }}>
        No timeline events
      </div>
    );
  }

  return (
    <div>
      {TIMELINE_GROUP_ORDER.filter(g => groups[g]).map(g => (
        <TimelineGroup key={g} groupKey={g} events={groups[g]}/>
      ))}
      {totalGroups === 0 && timeline.map((ev, i) => (
        <TimelineChainCard key={ev.id||ev.ts||i} ev={ev} groupColor={T.accent}/>
      ))}
    </div>
  );
}

// ============================================================================
// END TIMELINE CORRELATION ENGINE
// ============================================================================

// ============================================================================
// CONTEXTUAL INLINE ACTION FRAMEWORK
// ============================================================================

// Action definitions per entity type
const ENTITY_ACTIONS = {
  user: [
    { action:'grant_credits',       label:'Grant Credits',       severity:'low',      permission:'user.view',    needsAmount:true  },
    { action:'clear_suppression',   label:'Clear Suppression',   severity:'moderate', permission:'comms.view'                     },
    { action:'invalidate_sessions', label:'Invalidate Sessions', severity:'high',     permission:'session.view'                   },
    { action:'unlock_account',      label:'Unlock Account',      severity:'moderate', permission:'user.view'                      },
    { action:'lock_account',        label:'Lock Account',        severity:'critical', permission:'user.view'                      },
  ],
  job: [
    { action:'requeue',             label:'Requeue Job',         severity:'moderate', permission:'platform.jobs' },
    { action:'refund_credits',      label:'Refund Credits',      severity:'moderate', permission:'platform.jobs' },
    { action:'acknowledge_failure', label:'Acknowledge',         severity:'low',      permission:'platform.jobs' },
    { action:'mark_resolved',       label:'Mark Resolved',       severity:'moderate', permission:'platform.jobs' },
  ],
  incident: [
    { action:'acknowledge_incident',label:'Acknowledge',         severity:'low',      permission:'automation.incidents' },
    { action:'escalate_incident',   label:'Escalate',            severity:'high',     permission:'automation.incidents' },
    { action:'mark_resolved',       label:'Mark Resolved',       severity:'moderate', permission:'automation.incidents' },
    { action:'trigger_recovery',    label:'Trigger Recovery',    severity:'high',     permission:'autonomous.execute'   },
  ],
  queue: [
    { action:'pause_queue',         label:'Pause Queue',         severity:'high',     permission:'platform.queues' },
    { action:'resume_queue',        label:'Resume Queue',        severity:'moderate', permission:'platform.queues' },
    { action:'drain_queue',         label:'Drain Queue',         severity:'critical', permission:'platform.queues' },
    { action:'reroute_provider',    label:'Reroute Provider',    severity:'high',     permission:'platform.queues' },
  ],
};

const ACTION_SEV_COLOR = {
  low:      { color:T.green,   bg:T.greenBg,    border:T.greenBorder  },
  moderate: { color:T.amber,   bg:T.amberBg,    border:T.amberBorder  },
  high:     { color:'#c05621', bg:'#fffaf0',     border:'#fbd38d'      },
  critical: { color:T.red,     bg:T.redBg,      border:T.redBorder    },
};

// EntityActionButton — single action trigger
function EntityActionButton({ actionDef, onClick, disabled }) {
  const sc = ACTION_SEV_COLOR[actionDef.severity] || ACTION_SEV_COLOR.low;
  return (
    <button
      disabled={disabled}
      onClick={() => onClick(actionDef)}
      style={{
        padding:'5px 10px', borderRadius:4, fontSize:12, fontWeight:600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border:`1px solid ${sc.border}`,
        background: disabled ? T.bg : sc.bg,
        color: disabled ? T.textFaint : sc.color,
        opacity: disabled ? 0.5 : 1,
        fontFamily:'inherit', transition:'all 0.1s',
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = sc.color+'25'; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = sc.bg; }}
    >
      {actionDef.label}
    </button>
  );
}

// ActionConfirmationModal — confirmation + note entry before execution
function ActionConfirmationModal({ actionDef, entityType, entityId, api, onClose, onSuccess }) {
  const [note,       setNote]       = useState('');
  const [amount,     setAmount]     = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [err,        setErr]        = useState(null);
  const sc = ACTION_SEV_COLOR[actionDef.severity] || ACTION_SEV_COLOR.low;

  const execute = () => {
    if (!note.trim()) { setErr('Operator note is required.'); return; }
    setSubmitting(true); setErr(null);
    const body = { entity_type: entityType, entity_id: entityId, action: actionDef.action, note: note.trim() };
    if (actionDef.needsAmount) body.amount = amount;
    fetch(`${api}/v1/staff/entity/action`, {
      method:'POST', credentials:'include',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify(body),
    }).then(r => r.json().then(d => ({ ok:r.ok, d })))
    .then(({ ok, d }) => {
      if (ok && d.success) {
        onSuccess(d);
      } else {
        setErr(d.error || 'Action failed');
        setSubmitting(false);
      }
    }).catch(() => { setErr('Network error'); setSubmitting(false); });
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:9600, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:T.surface, border:`1px solid ${sc.border}`, borderTop:`3px solid ${sc.color}`, borderRadius:8, padding:'20px 22px', width:420, boxShadow:'0 8px 32px rgba(0,0,0,0.5)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
          <span style={{ fontSize:14, fontWeight:700, color:T.text, flex:1 }}>{actionDef.label}</span>
          <span style={{ fontSize:10, fontWeight:800, color:sc.color, background:sc.bg, border:`1px solid ${sc.border}`, borderRadius:3, padding:'1px 6px', textTransform:'uppercase' }}>
            {actionDef.severity}
          </span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:T.textFaint, cursor:'pointer', fontSize:18, lineHeight:1 }}>×</button>
        </div>

        <div style={{ fontSize:13, color:T.textSub, marginBottom:12, padding:'8px 10px', background:T.bg, borderRadius:4, border:`1px solid ${T.border}` }}>
          Entity: <strong style={{ color:T.text }}>{entityType}</strong> / <code style={{ fontFamily:'monospace', fontSize:11 }}>{typeof entityId === 'string' ? entityId.slice(0,24) : entityId}</code>
        </div>

        {actionDef.needsAmount && (
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:12, fontWeight:600, color:T.textSub, marginBottom:4 }}>Credit Amount</div>
            <input
              type="number" min="1" max="10000"
              style={{ ...s.input, width:'100%', boxSizing:'border-box' }}
              value={amount}
              onChange={e => setAmount(Math.max(1, Math.min(10000, Number(e.target.value))))}
            />
          </div>
        )}

        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:12, fontWeight:600, color:T.textSub, marginBottom:4 }}>Operator Note <span style={{ color:T.red }}>*</span></div>
          <textarea
            style={{ ...s.input, width:'100%', boxSizing:'border-box', minHeight:72, resize:'vertical', fontFamily:'inherit', fontSize:13 }}
            placeholder="Reason for this action (required)..."
            value={note}
            onChange={e => setNote(e.target.value)}
            disabled={submitting}
            autoFocus
          />
        </div>

        {err && <div style={{ ...s.error, marginBottom:10 }}>! {err}</div>}

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button style={s.btn()} onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            style={{ ...s.btn('primary'), background: sc.color, borderColor: sc.color }}
            onClick={execute}
            disabled={submitting || !note.trim()}
          >
            {submitting ? 'Executing...' : `Confirm ${actionDef.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ActionExecutionResult — inline success/failure result display
function ActionExecutionResult({ result, onDismiss }) {
  if (!result) return null;
  const isOk = result.success;
  return (
    <div style={{
      padding:'9px 12px', borderRadius:5, marginBottom:10,
      background: isOk ? T.greenBg : T.redBg,
      border:`1px solid ${isOk ? T.greenBorder : T.redBorder}`,
      display:'flex', alignItems:'flex-start', gap:8,
    }}>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:13, fontWeight:700, color: isOk ? T.green : T.red }}>
          {isOk ? 'Action Executed' : 'Action Failed'}
        </div>
        <div style={{ fontSize:12, color: isOk ? T.green : T.red, marginTop:2 }}>{result.detail || result.error}</div>
        {result.rollback_capable && <div style={{ fontSize:11, color:T.textSub, marginTop:3 }}>Rollback capable</div>}
        {result.actor && <div style={{ fontSize:11, color:T.textFaint, marginTop:2 }}>by {result.actor}</div>}
      </div>
      <button onClick={onDismiss} style={{ background:'none', border:'none', color:T.textFaint, cursor:'pointer', fontSize:16, lineHeight:1, flexShrink:0 }}>×</button>
    </div>
  );
}

// EntityActionPanel — renders contextual actions for the active entity
function EntityActionPanel({ entityType, entityId, api, hasPermission, onActionSuccess }) {
  const [modal,   setModal]   = useState(null);
  const [result,  setResult]  = useState(null);

  const available = (ENTITY_ACTIONS[entityType] || []).filter(a => !a.permission || hasPermission(a.permission));
  if (available.length === 0) return null;

  const handleSuccess = (res) => {
    setModal(null);
    setResult(res);
    if (onActionSuccess) onActionSuccess();
  };

  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'#8899aa', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:7 }}>
        Operational Actions
      </div>

      <ActionExecutionResult result={result} onDismiss={() => setResult(null)}/>

      <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
        {available.map(a => (
          <EntityActionButton key={a.action} actionDef={a} onClick={() => setModal(a)} disabled={!!modal}/>
        ))}
      </div>

      {modal && (
        <ActionConfirmationModal
          actionDef={modal}
          entityType={entityType}
          entityId={entityId}
          api={api}
          onClose={() => setModal(null)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}

// ============================================================================
// END CONTEXTUAL INLINE ACTION FRAMEWORK
// ============================================================================

// ============================================================================
// OPERATIONAL RISK INTELLIGENCE SYSTEM
// ============================================================================

// RiskConfidenceBadge — displays confidence level 0-100
function RiskConfidenceBadge({ confidence }) {
  const c = confidence || 0;
  const color = c >= 70 ? T.green : c >= 40 ? T.amber : T.textSub;
  return (
    <span style={{
      fontSize:10, fontWeight:700, color, background:color+'18',
      border:`1px solid ${color}30`, borderRadius:3,
      padding:'1px 6px', letterSpacing:'0.05em',
    }}>
      {c}% confidence
    </span>
  );
}

// RiskScoreMeter — horizontal score bar 0-100
function RiskScoreMeter({ score, severity }) {
  const s2 = Math.min(Math.max(score || 0, 0), 100);
  const barColor = severity === 'critical' ? T.red : severity === 'high' ? '#c05621' : severity === 'moderate' ? T.amber : T.green;
  return (
    <div style={{ marginBottom:8 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:3 }}>
        <span style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em' }}>Risk Score</span>
        <span style={{ fontSize:12, fontWeight:800, color:barColor }}>{s2}/100</span>
      </div>
      <div style={{ height:5, background:T.borderSoft, borderRadius:3, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${s2}%`, background:barColor, borderRadius:3, transition:'width 0.3s' }}/>
      </div>
    </div>
  );
}

// RiskFactorRow — single contributing risk factor
function RiskFactorRow({ factor }) {
  const sevColor = {
    critical:T.red, high:'#c05621', moderate:T.amber, low:T.green,
  }[factor.severity] || T.textSub;
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:7,
      padding:'5px 8px', marginBottom:2,
      background:T.bg, border:`1px solid ${T.borderSoft}`,
      borderLeft:`3px solid ${sevColor}`, borderRadius:4,
    }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:sevColor, flexShrink:0 }}/>
      <span style={{ fontSize:12, color:T.textMid, flex:1 }}>{factor.label}</span>
      {factor.weight > 0 && (
        <span style={{ fontSize:10, color:sevColor, fontWeight:700 }}>+{factor.weight}</span>
      )}
    </div>
  );
}

// RiskBreakdownPanel — full risk intelligence panel
function RiskBreakdownPanel({ data }) {
  const [expanded, setExpanded] = useState(true);
  const rd = data && data.risk_detail;
  if (!rd) return null;

  const sev = rd.severity || data.risk || 'low';
  const rc = RISK_CONFIG[sev] || RISK_CONFIG.low;
  const factors = rd.factors || [];
  const actions = rd.recommended_actions || [];

  return (
    <div style={{ marginBottom:14 }}>
      {/* Panel header */}
      <div
        style={{
          display:'flex', alignItems:'center', gap:7, marginBottom: expanded ? 8 : 0,
          cursor:'pointer', userSelect:'none',
        }}
        onClick={() => setExpanded(v => !v)}
      >
        <div style={{ fontSize:11, fontWeight:700, color:'#8899aa', textTransform:'uppercase', letterSpacing:'0.08em', flex:1 }}>
          Risk Intelligence
        </div>
        <span style={{
          fontSize:10, fontWeight:800, color:rc.color,
          background:rc.bg, border:`1px solid ${rc.border}`,
          borderRadius:3, padding:'1px 6px', textTransform:'uppercase',
        }}>
          {sev}
        </span>
        <RiskConfidenceBadge confidence={rd.confidence}/>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2"
          style={{ transform:expanded?'rotate(180deg)':'none', transition:'transform 0.13s', flexShrink:0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {expanded && (
        <div style={{
          background:T.bg, border:`1px solid ${T.border}`,
          borderLeft:`3px solid ${rc.color}`, borderRadius:5, padding:'10px 12px',
        }}>
          {/* Score meter */}
          <RiskScoreMeter score={rd.score} severity={sev}/>

          {/* Contributing factors */}
          {factors.length > 0 && (
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5 }}>
                Contributing Factors ({factors.length})
              </div>
              {factors.map((f, i) => <RiskFactorRow key={i} factor={f}/>)}
            </div>
          )}

          {/* Recommended actions */}
          {actions.length > 0 && (
            <div>
              <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5 }}>
                Recommended Actions
              </div>
              {actions.map((a, i) => (
                <div key={i} style={{
                  display:'flex', alignItems:'flex-start', gap:6,
                  padding:'4px 0', borderBottom:`1px solid ${T.borderSoft}`,
                  fontSize:12, color:T.textMid,
                }}>
                  <span style={{ fontSize:10, fontWeight:800, color:T.accent, flexShrink:0, marginTop:1 }}>{i+1}.</span>
                  {a}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// END OPERATIONAL RISK INTELLIGENCE SYSTEM
// ============================================================================

// ============================================================================
// INCIDENT CORRELATION SYSTEM
// ============================================================================

// IncidentMetricStrip — top summary bar of operational counts
function IncidentMetricStrip({ links, entity }) {
  const sev = entity.severity || 'low';
  const sevColor = {critical:T.red, high:'#c05621', medium:T.amber, low:T.green, unknown:'#c05621'}[sev] || T.amber;

  const metrics = [
    { label:'Failed Jobs',    val: links.failed_job_count || 0,      color: (links.failed_job_count||0) > 0 ? T.red : T.green },
    { label:'Stuck Jobs',     val: links.stuck_job_count  || 0,      color: (links.stuck_job_count||0)  > 0 ? T.red : T.green },
    { label:'Affected Users', val: (links.affected_users||[]).length, color: (links.affected_users||[]).length > 0 ? T.amber : T.green },
    { label:'Credits at Risk',val: links.credit_impact    || 0,      color: (links.credit_impact||0)    > 0 ? T.amber : T.green },
    { label:'Automation Acts',val: (links.automation_actions||[]).length, color: T.accent },
    { label:'Staff Actions',  val: (links.staff_actions||[]).length,  color: T.accent },
    { label:'Mail Failures',  val: links.mail_failures    || 0,      color: (links.mail_failures||0)    > 0 ? T.amber : T.green },
    { label:'Security Events',val: links.security_events  || 0,      color: (links.security_events||0)  > 0 ? T.red : T.green },
  ];

  return (
    <div style={{ marginBottom:12 }}>
      {/* Incident severity banner */}
      <div style={{
        padding:'8px 12px', borderRadius:5, marginBottom:8,
        background: sev === 'critical' ? T.redBg : sev === 'high' ? '#fffaf0' : T.amberBg,
        border:`1px solid ${sev === 'critical' ? T.redBorder : sev === 'high' ? '#fbd38d' : T.amberBorder}`,
        display:'flex', alignItems:'center', gap:8,
      }}>
        <span style={{ width:7, height:7, borderRadius:'50%', background:sevColor, flexShrink:0 }}/>
        <span style={{ fontSize:12, fontWeight:700, color:sevColor, flex:1 }}>
          {entity.message || entity.type || 'Active Incident'}
        </span>
        <span style={{ fontSize:10, fontWeight:800, color:sevColor, background:sevColor+'18', border:`1px solid ${sevColor}30`, borderRadius:3, padding:'1px 6px', textTransform:'uppercase' }}>
          {sev}
        </span>
      </div>

      {/* Metric grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:5 }}>
        {metrics.map(m => (
          <div key={m.label} style={{
            background:T.bg, border:`1px solid ${T.border}`,
            borderTop:`2px solid ${m.color}`, borderRadius:4,
            padding:'7px 8px', textAlign:'center',
          }}>
            <div style={{ fontSize:18, fontWeight:800, color:m.color, lineHeight:1 }}>{m.val}</div>
            <div style={{ fontSize:10, color:T.textFaint, marginTop:3, lineHeight:1.2 }}>{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// IncidentEntityGroup — a labeled group of traversable entity chips
function IncidentEntityGroup({ title, entities, entityType, emptyLabel, color }) {
  const groupColor = color || T.accent;
  if (!entities || entities.length === 0) return null;

  return (
    <div style={{ marginBottom:10 }}>
      <div style={{
        display:'flex', alignItems:'center', gap:6, marginBottom:5,
        fontSize:11, fontWeight:700, color:'#8899aa',
        textTransform:'uppercase', letterSpacing:'0.07em',
      }}>
        <span style={{ width:3, height:14, borderRadius:2, background:groupColor, flexShrink:0 }}/>
        {title}
        <span style={{
          fontSize:10, color:groupColor, background:groupColor+'15',
          border:`1px solid ${groupColor}30`, borderRadius:3, padding:'0 5px',
        }}>
          {entities.length}
        </span>
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
        {entities.slice(0, 10).map((e, i) => {
          const eid   = e.id || e.ticket_id || e.slug || String(i);
          const elabel = e.email || e.subject || e.slug || e.action || e.name || eid.slice(0,14);
          return <EntityChip key={eid} type={entityType} id={eid} label={elabel}/>;
        })}
        {entities.length > 10 && (
          <span style={{ fontSize:11, color:T.textFaint, padding:'2px 6px' }}>+{entities.length-10} more</span>
        )}
      </div>
    </div>
  );
}

// IncidentImpactCard — a single impact domain (providers, credits, mail, security)
function IncidentImpactCard({ title, value, detail, color, icon }) {
  const cardColor = color || T.textSub;
  return (
    <div style={{
      background:T.bg, border:`1px solid ${T.border}`,
      borderLeft:`3px solid ${cardColor}`, borderRadius:5,
      padding:'8px 10px', marginBottom:6,
      display:'flex', alignItems:'center', gap:10,
    }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:11, fontWeight:700, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>{title}</div>
        <div style={{ fontSize:20, fontWeight:800, color:cardColor, lineHeight:1 }}>{value}</div>
        {detail && <div style={{ fontSize:11, color:T.textFaint, marginTop:2 }}>{detail}</div>}
      </div>
    </div>
  );
}

// IncidentCorrelationPanel — renders only for incident entity_type
function IncidentCorrelationPanel({ data }) {
  if (!data || data.entity_type !== 'incident') return null;
  const entity = data.entity || {};
  const links  = data.links  || {};

  const hasProviders = (links.affected_providers || []).length > 0;
  const hasUsers     = (links.affected_users     || []).length > 0;
  const hasJobs      = (links.affected_jobs      || []).length > 0;
  const hasAuto      = (links.automation_actions || []).length > 0;
  const hasStaff     = (links.staff_actions      || []).length > 0;

  return (
    <div style={{ marginBottom:16 }}>
      {/* Section label */}
      <div style={{
        fontSize:11, fontWeight:700, color:'#8899aa',
        textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8,
        display:'flex', alignItems:'center', gap:6,
      }}>
        <span>Incident Investigation</span>
        <span style={{
          fontSize:10, color:'#2D6CFF', background:'rgba(45,108,255,0.1)',
          border:'1px solid rgba(45,108,255,0.2)', borderRadius:3, padding:'0 5px',
        }}>RCA</span>
      </div>

      {/* Metric strip */}
      <IncidentMetricStrip links={links} entity={entity}/>

      {/* Impact cards row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:10 }}>
        <IncidentImpactCard
          title="Credit Impact"
          value={links.credit_impact || 0}
          detail={`${links.credit_impact_jobs || 0} jobs with deducted credits`}
          color={(links.credit_impact||0) > 0 ? T.amber : T.green}
        />
        <IncidentImpactCard
          title="Mail Failures"
          value={links.mail_failures || 0}
          detail="In 2h window"
          color={(links.mail_failures||0) > 0 ? T.amber : T.green}
        />
        <IncidentImpactCard
          title="Recovery Attempts"
          value={links.recovery_attempts || 0}
          detail="Autonomous recovery actions"
          color={T.accent}
        />
        <IncidentImpactCard
          title="Security Events"
          value={links.security_events || 0}
          detail="Failed logins in 2h window"
          color={(links.security_events||0) > 3 ? T.red : T.green}
        />
      </div>

      {/* Provider impact */}
      {hasProviders && (
        <div style={{ marginBottom:10 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#8899aa', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5, display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ width:3, height:14, borderRadius:2, background:'#805ad5', flexShrink:0 }}/>
            Affected Providers
          </div>
          {(links.affected_providers || []).map((p, i) => (
            <div key={i} style={{
              display:'flex', justifyContent:'space-between', alignItems:'center',
              padding:'5px 8px', background:T.bg, border:`1px solid ${T.border}`,
              borderLeft:`3px solid #805ad5`, borderRadius:4, marginBottom:3,
            }}>
              <span style={{ fontSize:12, fontWeight:700, color:T.text, fontFamily:'monospace' }}>{p.name}</span>
              <span style={{ fontSize:11, fontWeight:700, color:T.red }}>{p.fail_count} failures</span>
            </div>
          ))}
        </div>
      )}

      {/* Affected entities */}
      {hasUsers && <IncidentEntityGroup title="Affected Users" entities={links.affected_users} entityType="user" color={T.accent}/>}
      {hasJobs  && <IncidentEntityGroup title="Affected Jobs"  entities={links.affected_jobs}  entityType="job"  color={T.red}/>}
      {hasAuto  && <IncidentEntityGroup title="Automation Actions" entities={links.automation_actions.map(a => ({...a, id:a.id, name:a.action}))} entityType="autonomous_execution" color={T.green}/>}
      {hasStaff && <IncidentEntityGroup title="Staff Interventions" entities={links.staff_actions.map(a => ({...a, id:a.id, name:a.action}))} entityType="audit" color={T.accent}/>}
    </div>
  );
}

// ============================================================================
// END INCIDENT CORRELATION SYSTEM
// ============================================================================

// ============================================================================
// UNIFIED COMMUNICATIONS PLATFORM
// ============================================================================

// Communication event type config
const COMM_TYPE_CONFIG = {
  EMAIL_SENT:           { label:'Sent',           color:T.green,   icon:'S' },
  EMAIL_DELIVERED:      { label:'Delivered',      color:T.green,   icon:'D' },
  EMAIL_FAILED:         { label:'Failed',         color:T.red,     icon:'F' },
  EMAIL_SUPPRESSED:     { label:'Suppressed',     color:T.red,     icon:'X' },
  STAFF_REPLY:          { label:'Staff Reply',    color:T.accent,  icon:'R' },
  AUTOMATED_REPLY:      { label:'Auto Reply',     color:T.accent,  icon:'A' },
  TICKET_RESPONSE:      { label:'Ticket Reply',   color:'#553c9a', icon:'T' },
  ESCALATION_NOTICE:    { label:'Escalation',     color:'#c05621', icon:'!' },
  SYSTEM_NOTIFICATION:  { label:'System',         color:T.textSub, icon:'N' },
};

// Communication health config
const COMM_HEALTH_CONFIG = {
  HEALTHY:  { color:T.green,   bg:T.greenBg,    border:T.greenBorder   },
  DEGRADED: { color:T.amber,   bg:T.amberBg,    border:T.amberBorder   },
  FAILING:  { color:'#c05621', bg:'#fffaf0',    border:'#fbd38d'       },
  CRITICAL: { color:T.red,     bg:T.redBg,      border:T.redBorder     },
};

// CommunicationStatusBadge
function CommunicationStatusBadge({ status }) {
  const cfg = COMM_TYPE_CONFIG[status] || COMM_TYPE_CONFIG.SYSTEM_NOTIFICATION;
  return (
    <span style={{
      fontSize:10, fontWeight:800, letterSpacing:'0.05em', textTransform:'uppercase',
      color:cfg.color, background:cfg.color+'18', border:`1px solid ${cfg.color}30`,
      borderRadius:3, padding:'1px 6px', flexShrink:0,
    }}>
      {cfg.label}
    </span>
  );
}

// CommunicationEventCard — single expandable communication event
function CommunicationEventCard({ ev }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = COMM_TYPE_CONFIG[ev.type] || COMM_TYPE_CONFIG.SYSTEM_NOTIFICATION;
  const isFailed = ev.type === 'EMAIL_FAILED' || ev.type === 'EMAIL_SUPPRESSED';

  return (
    <div style={{
      marginBottom:3,
      borderLeft:`3px solid ${cfg.color}`,
      background:isFailed ? `${cfg.color}06` : 'transparent',
      borderRadius:'0 4px 4px 0',
    }}>
      <div
        style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 9px', cursor:'pointer' }}
        onClick={() => setExpanded(v => !v)}
      >
        <span style={{
          width:16, height:16, borderRadius:'50%', flexShrink:0,
          background:cfg.color+'20', border:`1px solid ${cfg.color}40`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:8, fontWeight:800, color:cfg.color,
        }}>
          {cfg.icon}
        </span>
        <div style={{ flex:1, minWidth:0 }}>
          <CommunicationStatusBadge status={ev.type}/>
          {ev.meta && ev.meta.template && (
            <span style={{ fontSize:10, color:T.textSub, marginLeft:6 }}>{ev.meta.template}</span>
          )}
        </div>
        {ev.entity_type && ev.entity_id && (
          <EntityChip type={ev.entity_type} id={ev.entity_id} label={ev.entity_type}/>
        )}
        <span style={{ fontSize:10, color:T.textFaint, whiteSpace:'nowrap', flexShrink:0 }}>
          {ev.ts ? fmtAgo(ev.ts) : ''}
        </span>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={T.textFaint} strokeWidth="2"
          style={{ transform:expanded?'rotate(180deg)':'none', transition:'transform 0.12s', flexShrink:0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>
      {expanded && (
        <div style={{ padding:'4px 9px 7px 33px', borderTop:`1px solid ${T.borderSoft}` }}>
          {[
            ['Time',      fmtDate(ev.ts)],
            ['Status',    ev.status],
            ['Source',    ev.source],
            ['Recipient', ev.recipient],
          ].filter(([,v]) => v).map(([k, v]) => (
            <div key={k} style={s.kvRow}>
              <span style={s.kvLabel}>{k}</span>
              <span style={{ ...s.kvValue, fontSize:11 }}>{v}</span>
            </div>
          ))}
          {ev.meta && Object.keys(ev.meta).filter(k => ev.meta[k] && k !== 'action').length > 0 && (
            <div style={{ marginTop:4, background:T.bg, border:`1px solid ${T.borderSoft}`, borderRadius:3, padding:'5px 7px', fontSize:11, fontFamily:'monospace', color:T.textMid, whiteSpace:'pre-wrap' }}>
              {JSON.stringify(ev.meta, null, 2)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// CommunicationTimeline — scrollable list of communication events
function CommunicationTimeline({ events }) {
  if (!events || events.length === 0) {
    return <div style={{ fontSize:12, color:T.textFaint, padding:'6px 0' }}>No communication events in last 30 days.</div>;
  }
  return (
    <div style={{ maxHeight:260, overflowY:'auto' }}>
      {events.map((ev, i) => <CommunicationEventCard key={ev.entity_id || i} ev={ev}/>)}
    </div>
  );
}

// CommunicationEntityGroup — compact group of linked communication entities
function CommunicationEntityGroup({ title, items, entityType, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom:7 }}>
      <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>{title}</div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
        {items.slice(0,6).map((item, i) => {
          const id = item.entity_id || item.id || String(i);
          const label = item.status || item.type || id.slice(0,12);
          return <EntityChip key={id} type={entityType} id={id} label={label}/>;
        })}
      </div>
    </div>
  );
}

// CommunicationSummaryPanel — full communications intelligence section
function CommunicationSummaryPanel({ comms }) {
  const [open, setOpen] = useState(false);
  if (!comms) return null;

  const hc = COMM_HEALTH_CONFIG[comms.health] || COMM_HEALTH_CONFIG.HEALTHY;
  const failedEvents = (comms.events || []).filter(e => e.type === 'EMAIL_FAILED' || e.type === 'EMAIL_SUPPRESSED');

  return (
    <div style={{ marginBottom:14 }}>
      {/* Section header */}
      <div
        style={{ display:'flex', alignItems:'center', gap:7, marginBottom: open ? 8 : 0, cursor:'pointer', userSelect:'none' }}
        onClick={() => setOpen(v => !v)}
      >
        <div style={{ fontSize:11, fontWeight:700, color:'#8899aa', textTransform:'uppercase', letterSpacing:'0.08em', flex:1 }}>
          Communications
        </div>
        {/* Health badge */}
        <span style={{
          fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.05em',
          color:hc.color, background:hc.bg, border:`1px solid ${hc.border}`,
          borderRadius:3, padding:'1px 7px',
        }}>
          {comms.health}
        </span>
        {comms.suppressed && (
          <span style={{ fontSize:10, fontWeight:800, color:T.red, background:T.redBg, border:`1px solid ${T.redBorder}`, borderRadius:3, padding:'1px 6px' }}>
            SUPPRESSED
          </span>
        )}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2"
          style={{ transform:open?'rotate(180deg)':'none', transition:'transform 0.13s', flexShrink:0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {open && (
        <div style={{
          background:T.bg, border:`1px solid ${T.border}`,
          borderLeft:`3px solid ${hc.color}`, borderRadius:5, padding:'10px 12px',
        }}>
          {/* Summary metrics */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:5, marginBottom:10 }}>
            {[
              { label:'Sent',       val:comms.total_sent,          color:T.green },
              { label:'Failed',     val:comms.total_failed,        color:comms.total_failed>0?T.red:T.green },
              { label:'Suppressed', val:comms.total_suppressed,    color:comms.total_suppressed>0?T.red:T.green },
              { label:'Staff Reply',val:comms.total_staff_replies, color:T.accent },
            ].map(m => (
              <div key={m.label} style={{
                background:T.surface, border:`1px solid ${T.border}`, borderRadius:4, padding:'5px 6px', textAlign:'center',
              }}>
                <div style={{ fontSize:15, fontWeight:800, color:m.color, lineHeight:1 }}>{m.val || 0}</div>
                <div style={{ fontSize:9, color:T.textFaint, marginTop:2 }}>{m.label}</div>
              </div>
            ))}
          </div>

          {/* Last communication */}
          {comms.last_communication && (
            <div style={{ fontSize:11, color:T.textSub, marginBottom:8 }}>
              Last communication: <strong style={{ color:T.text }}>{fmtAgo(comms.last_communication)}</strong>
            </div>
          )}

          {/* Suppression warning */}
          {comms.suppressed && (
            <div style={{ padding:'5px 8px', background:T.redBg, border:`1px solid ${T.redBorder}`, borderRadius:4, fontSize:11, fontWeight:700, color:T.red, marginBottom:8 }}>
              Email suppressed: {comms.suppression_type || 'unknown'} — replies will not reach this user.
            </div>
          )}

          {/* Failed events group */}
          {failedEvents.length > 0 && (
            <CommunicationEntityGroup title="Failed Events" items={failedEvents} entityType="mail_event" color={T.red}/>
          )}

          {/* Full timeline */}
          <div style={{ marginTop:6 }}>
            <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:5 }}>
              Event History (30 days)
            </div>
            <CommunicationTimeline events={comms.events}/>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// END UNIFIED COMMUNICATIONS PLATFORM
// ============================================================================

// ── Main Inspector slide-over panel ──────────────────────────────────────────
function EntityInspectorPanel() {
  const { stack, current, data, loading, err, closeEntity, goBack, liveStatus, hasChanges, lastRefresh, api, softRefresh } = useEntityInspector();
  const { hasPermission } = useStaffAuth();

  const handleActionSuccess = React.useCallback(() => {
    if (current && softRefresh) softRefresh(current.type, current.id);
  }, [current, softRefresh]);

  if (stack.length === 0) return null;

  const tc  = current ? (ENTITY_TYPE_CONFIG[current.type] || { label:current.type, color:T.accent }) : { label:'', color:T.accent };
  const rc  = data ? (RISK_CONFIG[data.risk] || RISK_CONFIG.low) : null;
  const entity = data?.entity || {};
  const links  = data?.links  || {};
  const timeline = data?.timeline || [];

  const renderEntityCore = () => {
    if (!data) return null;
    const { entity_type } = data;

    if (entity_type === 'user') return (
      <>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
          {data.suppressed && <span style={{ ...s.badge('red'), fontSize:11 }}>SUPPRESSED</span>}
          {entity.tier && <span style={{ ...s.badge('blue'), fontSize:11 }}>{entity.tier}</span>}
          {entity.email_verified && <span style={{ ...s.badge('green'), fontSize:11 }}>Verified</span>}
        </div>
        {[['Email', entity.email], ['Name', `${entity.first_name||''} ${entity.last_name||''}`.trim()||'-'], ['Credits', fmt(entity.credits)], ['Joined', fmtDate(entity.created_at)]].map(([k,v]) => (
          <div key={k} style={s.kvRow}><span style={s.kvLabel}>{k}</span><span style={{ ...s.kvValue, fontSize:12 }}>{v}</span></div>
        ))}
        {data.subscription && (
          <div style={{ marginTop:8, padding:'8px', background:T.bg, border:`1px solid ${T.border}`, borderRadius:5 }}>
            <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:'uppercase', marginBottom:4 }}>Subscription</div>
            {[['Plan', data.subscription.plan?.name||data.subscription.plan_id], ['Status', data.subscription.status]].map(([k,v]) => (
              <div key={k} style={s.kvRow}><span style={s.kvLabel}>{k}</span><span style={{ ...s.kvValue, fontSize:12 }}>{v}</span></div>
            ))}
          </div>
        )}
        <div style={{ marginTop:8, display:'flex', gap:6, flexWrap:'wrap' }}>
          <div style={{ ...s.panel, padding:'8px 10px', flex:1, textAlign:'center' }}>
            <div style={{ fontSize:18, fontWeight:800, color:links.failed_jobs>0?T.red:T.text }}>{fmt(links.total_jobs)}</div>
            <div style={{ fontSize:10, color:T.textFaint }}>Jobs</div>
          </div>
          <div style={{ ...s.panel, padding:'8px 10px', flex:1, textAlign:'center' }}>
            <div style={{ fontSize:18, fontWeight:800, color:links.open_tickets>0?T.amber:T.text }}>{fmt(links.open_tickets)}</div>
            <div style={{ fontSize:10, color:T.textFaint }}>Open Tickets</div>
          </div>
          <div style={{ ...s.panel, padding:'8px 10px', flex:1, textAlign:'center' }}>
            <div style={{ fontSize:18, fontWeight:800, color:links.mail_failed>0?T.red:T.text }}>{fmt(links.mail_failed)}</div>
            <div style={{ fontSize:10, color:T.textFaint }}>Mail Failures</div>
          </div>
        </div>
        {data.communications && <CommunicationSummaryPanel comms={data.communications}/>}
      </>
    );

    if (entity_type === 'job') return (
      <>
        <div style={{ marginBottom:8 }}><JobStatusPill status={entity.status}/></div>
        {[
          ['User', entity.user_email ? <EntityChip type="user" id={entity.user_id} label={entity.user_email}/> : '-'],
          ['Provider', <code style={{ fontFamily:'monospace', fontSize:11 }}>{entity.provider||'-'}</code>],
          ['Generator', entity.generator_slug],
          ['Attempts', <span style={{ fontWeight:700, color:entity.attempts>2?T.red:T.text }}>{entity.attempts}</span>],
          ['Duration', entity.duration_ms ? `${(entity.duration_ms/1000).toFixed(1)}s` : '-'],
          ['Credits', entity.credit_deducted ? <span style={{ color:T.amber }}>Deducted</span> : 'No'],
          ['Created', fmtDate(entity.created_at)],
        ].map(([k,v]) => <div key={k} style={s.kvRow}><span style={s.kvLabel}>{k}</span><span style={{ ...s.kvValue, fontSize:12 }}>{v}</span></div>)}
        {entity.error_code && (
          <div style={{ marginTop:8, padding:'8px', background:T.redBg, border:`1px solid ${T.redBorder}`, borderRadius:5, fontSize:12, color:T.red }}>
            <strong>{entity.error_code}</strong>
            {entity.error_detail && <div style={{ marginTop:4, fontFamily:'monospace', fontSize:10, wordBreak:'break-all' }}>{entity.error_detail.slice(0,200)}</div>}
          </div>
        )}
      </>
    );

    if (entity_type === 'ticket') return (
      <>
        <div style={{ marginBottom:8 }}><SupportStatusPill status={entity.status}/></div>
        {[
          ['User', entity.user_email ? <EntityChip type="user" id={entity.user_id} label={entity.user_email}/> : '-'],
          ['Subject', entity.subject],
          ['Priority', entity.severity],
          ['Assignee', entity.assignee||'Unassigned'],
          ['Updated', fmtDate(entity.updated_at)],
        ].map(([k,v]) => <div key={k} style={s.kvRow}><span style={s.kvLabel}>{k}</span><span style={{ ...s.kvValue, fontSize:12 }}>{v}</span></div>)}
        {links.user_failed_jobs?.length > 0 && (
          <div style={{ marginTop:8 }}>
            <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:'uppercase', marginBottom:4 }}>User Failed Jobs</div>
            {links.user_failed_jobs.map(j => <div key={j.id} style={{ marginBottom:2 }}><EntityChip type="job" id={j.id} label={`${j.error||j.provider||'failed'}`}/></div>)}
          </div>
        )}
        {data.communications && <CommunicationSummaryPanel comms={data.communications}/>}
      </>
    );

    // Generic fallback for other entity types
    if (entity_type === 'mail_event') return (
      <>
        {[
          ['Action',    entity.action],
          ['Status',    entity.status],
          ['Template',  entity.template],
          ['Recipient', entity.recipient],
          ['Created',   fmtDate(entity.created_at)],
        ].filter(([,v]) => v).map(([k,v]) => (
          <div key={k} style={s.kvRow}><span style={s.kvLabel}>{k}</span><span style={{ ...s.kvValue, fontSize:12 }}>{v}</span></div>
        ))}
        {entity.user_id && (
          <div style={{ marginTop:6 }}>
            <EntityChip type="user" id={entity.user_id} label={entity.recipient || entity.user_id}/>
          </div>
        )}
      </>
    );

    if (entity_type === 'communication_thread') return (
      <>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
          {entity.status && <span style={{ ...s.badge(entity.status==='escalated'?'red':entity.status==='resolved'?'green':'blue'), fontSize:11 }}>{entity.status?.toUpperCase()}</span>}
          {entity.priority && <span style={{ ...s.badge('grey'), fontSize:11 }}>{entity.priority}</span>}
          {entity.channel && <span style={{ fontSize:11, padding:'2px 7px', borderRadius:4, background:'rgba(6,148,162,0.1)', color:'#0694a2', border:'1px solid rgba(6,148,162,0.25)' }}>{entity.channel}</span>}
        </div>
        {[
          ['Subject',  entity.subject || '(no subject)'],
          ['Source',   entity.source],
          ['Messages', entity.message_count],
          ['Assigned', data.links?.assigned_to?.name || 'Unassigned'],
          ['Created',  fmtDate(entity.created_at)],
          ['Updated',  fmtDate(entity.updated_at)],
        ].filter(([,v]) => v !== null && v !== undefined).map(([k,v]) => (
          <div key={k} style={s.kvRow}><span style={s.kvLabel}>{k}</span><span style={{ ...s.kvValue, fontSize:12 }}>{v}</span></div>
        ))}
        {entity.user_id && (
          <div style={{ marginTop:6 }}>
            <EntityChip type="user" id={entity.user_id} label="Linked User"/>
          </div>
        )}
        {entity.ticket_id && (
          <div style={{ marginTop:4 }}>
            <EntityChip type="ticket" id={entity.ticket_id} label={entity.ticket_id}/>
          </div>
        )}
      </>
    );

    if (entity_type === 'communication_message') return (
      <>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
          {entity.direction && <span style={{ fontSize:11, padding:'2px 7px', borderRadius:4, background:'rgba(6,148,162,0.1)', color:'#0694a2', border:'1px solid rgba(6,148,162,0.25)' }}>{entity.direction?.toUpperCase()}</span>}
          {entity.status && <span style={{ ...s.badge(entity.status==='failed'||entity.status==='suppressed'?'red':'green'), fontSize:11 }}>{entity.status?.toUpperCase()}</span>}
          {entity.is_internal && <span style={{ ...s.badge('amber'), fontSize:11 }}>INTERNAL</span>}
        </div>
        {[
          ['Subject',   entity.subject || '(no subject)'],
          ['From',      entity.sender_email || entity.sender_id],
          ['To',        entity.recipient_email],
          ['Provider',  entity.provider],
          ['Sent',      fmtDate(entity.created_at)],
        ].filter(([,v]) => v).map(([k,v]) => (
          <div key={k} style={s.kvRow}><span style={s.kvLabel}>{k}</span><span style={{ ...s.kvValue, fontSize:12 }}>{v}</span></div>
        ))}
        {entity.thread_id && (
          <div style={{ marginTop:6 }}>
            <EntityChip type="communication_thread" id={entity.thread_id} label="View Thread"/>
          </div>
        )}
        {entity.sender_id && entity.sender_type === 'staff' && (
          <div style={{ marginTop:4 }}>
            <EntityChip type="session" id={entity.sender_id} label="Sender"/>
          </div>
        )}
        {data.attachments && data.attachments.length > 0 && (
          <div style={{ marginTop:8 }}>
            <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:'uppercase', marginBottom:4 }}>Attachments ({data.attachments.length})</div>
            {data.attachments.map(a => (
              <div key={a.id} style={{ fontSize:11, color:T.textMid, padding:'3px 0', borderBottom:`1px solid ${T.borderSoft}` }}>
                {a.filename} {a.file_size ? `(${(a.file_size/1024).toFixed(0)}KB)` : ''} <span style={{ color:T.textFaint }}>{a.mime_type}</span>
              </div>
            ))}
          </div>
        )}
      </>
    );

    return (
      <pre style={{ fontSize:11, color:T.textMid, fontFamily:'monospace', whiteSpace:'pre-wrap', wordBreak:'break-all', maxHeight:200, overflowY:'auto' }}>
        {JSON.stringify(entity, null, 2)}
      </pre>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div onClick={closeEntity} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:8000 }}/>

      {/* Slide-over panel */}
      <div style={{
        position:'fixed', top:0, right:0, bottom:0, width:420,
        background:T.surface, borderLeft:`1px solid ${T.border}`,
        zIndex:8001, display:'flex', flexDirection:'column',
        boxShadow:'-8px 0 32px rgba(0,0,0,0.4)',
        overflowY:'auto',
      }}>
        {/* Header */}
        <div style={{ padding:'16px 18px', borderBottom:`1px solid ${T.border}`, flexShrink:0, background:T.bg }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            {stack.length > 1 && (
              <button onClick={goBack} style={{ background:'none', border:'none', color:T.accent, cursor:'pointer', fontSize:13, fontWeight:700, padding:0 }}>← Back</button>
            )}
            <span style={{ fontSize:10, fontWeight:800, color:tc.color, background:tc.color+'20', border:`1px solid ${tc.color}40`, borderRadius:4, padding:'2px 6px', letterSpacing:'0.07em' }}>{tc.label.toUpperCase()}</span>
            {rc && (
              <span style={{ fontSize:10, fontWeight:800, color:rc.color, background:rc.bg, border:`1px solid ${rc.border}`, borderRadius:4, padding:'2px 6px' }}>
                {data.risk?.toUpperCase()}
              </span>
            )}
            <LiveStatusBadge status={liveStatus}/>
            {hasChanges && (
              <span style={{ fontSize:9, fontWeight:700, color:'#2D6CFF', background:'rgba(45,108,255,0.1)', border:'1px solid rgba(45,108,255,0.2)', borderRadius:3, padding:'1px 6px', letterSpacing:'0.05em' }}>
                UPDATED
              </span>
            )}
            <button onClick={closeEntity} style={{ marginLeft:'auto', background:'none', border:'none', color:T.textFaint, cursor:'pointer', fontSize:20, lineHeight:1 }}>×</button>
          </div>
          <div style={{ fontSize:14, fontWeight:700, color:T.text, fontFamily:'monospace', wordBreak:'break-all' }}>
            {current?.id}
          </div>
          {stack.length > 1 && (
            <div style={{ marginTop:4 }}>
              <InvestigationBreadcrumbs stack={stack}/>
            </div>
          )}
          {lastRefresh && (
            <div style={{ marginTop:4, fontSize:10, color:'#556677' }}>
              Refreshed {fmtAgo(new Date(lastRefresh).toISOString())} · polling every {LIVE_POLL_INTERVAL/1000}s
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'16px 18px' }}>
          {loading && <Loader/>}
          {err && <div style={s.error}>! {err}</div>}
          {!loading && !err && data && (
            <>
              {/* Entity core */}
              <div style={{ marginBottom:16 }}>{renderEntityCore()}</div>

              {/* Risk Intelligence Panel -- renders when risk_detail present */}
              {data.risk_detail && <RiskBreakdownPanel data={data}/>}

              {/* Contextual Inline Actions */}
              {current && api && (
                <EntityActionPanel
                  entityType={current.type}
                  entityId={current.id}
                  api={api}
                  hasPermission={hasPermission}
                  onActionSuccess={handleActionSuccess}
                />
              )}

              {/* Relationship Graph -- replaces flat linked entity chips */}
              {Object.keys(links).length > 0 && (
                <div style={{ marginBottom:16 }}>
                  <div style={{
                    fontSize:11, fontWeight:700, color:'#8899aa',
                    textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8,
                    display:'flex', alignItems:'center', gap:6,
                  }}>
                    <span>Operational Relationships</span>
                    <span style={{
                      fontSize:10, color:'#2D6CFF', background:'rgba(45,108,255,0.1)',
                      border:'1px solid rgba(45,108,255,0.2)', borderRadius:3, padding:'0 5px',
                    }}>
                      {Object.keys(normalizeRelationships(links)).length} categories
                    </span>
                  </div>
                  <EntityRelationshipGraph links={links}/>
                </div>
              )}

              {/* Incident Correlation Panel -- renders only for incident entity_type */}
              {data.entity_type === 'incident' && <IncidentCorrelationPanel data={data}/>}

              {/* Incident Lifecycle Panel -- persistent status/owner/resolution for incidents */}
              {data.entity_type === 'incident' && data.entity && data.entity.rule_id && (
                <IncidentLifecyclePanel
                  api={api}
                  ruleId={data.entity.rule_id}
                  incidentTitle={data.entity.message || data.entity.type}
                  incidentSeverity={data.entity.severity}
                  incidentCategory={data.entity.type}
                  onUpdate={() => { if (current && softRefresh) softRefresh(current.type, current.id); }}
                />
              )}

              {/* Communications Panel -- renders for user, ticket, incident, mail_event */}
              {data.communications && <CommunicationSummaryPanel comms={data.communications}/>}

              {/* Investigation Notes -- local operator notes for current entity */}
              {current && <InvestigationNotesPanel entityType={current.type} entityId={current.id}/>}

              {/* Timeline Correlation Engine -- replaces flat chronological timeline */}
              {timeline.length > 0 && (
                <div>
                  <div style={{
                    fontSize:11, fontWeight:700, color:'#8899aa',
                    textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8,
                    display:'flex', alignItems:'center', gap:6,
                  }}>
                    <span>Incident Timeline</span>
                    <span style={{
                      fontSize:10, color:'#2D6CFF', background:'rgba(45,108,255,0.1)',
                      border:'1px solid rgba(45,108,255,0.2)', borderRadius:3, padding:'0 5px',
                    }}>
                      {timeline.length} events
                    </span>
                  </div>
                  <TimelineCorrelationEngine timeline={timeline}/>
                </div>
              )}

              {/* Investigation History -- recent entity investigations */}
              <InvestigationHistoryPanel/>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ============================================================================
// INCIDENT LIFECYCLE PANEL
// ============================================================================

const ILC_STATUS_CONFIG = {
  OPEN:          { color:'#EF4444', bg:'rgba(239,68,68,0.1)',   border:'rgba(239,68,68,0.25)',   label:'Open' },
  INVESTIGATING: { color:'#F59E0B', bg:'rgba(245,158,11,0.1)',  border:'rgba(245,158,11,0.25)',  label:'Investigating' },
  MITIGATING:    { color:'#3B82F6', bg:'rgba(59,130,246,0.1)',  border:'rgba(59,130,246,0.25)',  label:'Mitigating' },
  MONITORING:    { color:'#8B5CF6', bg:'rgba(139,92,246,0.1)',  border:'rgba(139,92,246,0.25)',  label:'Monitoring' },
  RESOLVED:      { color:'#10B981', bg:'rgba(16,185,129,0.1)',  border:'rgba(16,185,129,0.25)',  label:'Resolved' },
  CLOSED:        { color:'#6B7280', bg:'rgba(107,114,128,0.1)', border:'rgba(107,114,128,0.25)', label:'Closed' },
};

function IncidentStatusBadge({ status }) {
  const cfg = ILC_STATUS_CONFIG[status] || ILC_STATUS_CONFIG.OPEN;
  return (
    <span style={{ fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:20, background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.border}`, letterSpacing:'0.04em' }}>
      {cfg.label}
    </span>
  );
}

function IncidentLifecyclePanel({ api, ruleId, incidentTitle, incidentSeverity, incidentCategory, onUpdate }) {
  const { hasPermission } = useStaffAuth();
  const [lifecycle, setLifecycle] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [err,       setErr]       = useState(null);
  const [tab,       setTab]       = useState('status'); // status | assign | resolve | postmortem
  const [submitting,setSubmitting]= useState(false);
  const [msg,       setMsg]       = useState('');

  // Form state
  const [newStatus,  setNewStatus]  = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [ownerId,    setOwnerId]    = useState('');
  const [escOwnerId, setEscOwnerId] = useState('');
  const [team,       setTeam]       = useState('');
  const [assignNote, setAssignNote] = useState('');
  const [resolution, setResolution] = useState({ summary:'', root_cause:'', customer_impact:'', operational_impact:'', preventative_actions:'' });
  const [postmortem, setPostmortem] = useState({ notes:'', lessons:'', followup:'', verified:false });

  const load = useCallback(() => {
    if (!ruleId) { setLoading(false); return; }
    setLoading(true); setErr(null);
    fetch(`${api}/v1/staff/incidents/${encodeURIComponent(ruleId)}`, { credentials:'include' })
      .then(r => r.json())
      .then(d => {
        setLifecycle(d.incident || null);
        if (d.incident) {
          setNewStatus(d.incident.status || 'OPEN');
          setOwnerId(d.incident.owner_id || '');
          setEscOwnerId(d.incident.escalation_owner_id || '');
          setTeam(d.incident.assigned_team || '');
          setResolution({
            summary:              d.incident.resolution_summary || '',
            root_cause:           d.incident.root_cause || '',
            customer_impact:      d.incident.customer_impact || '',
            operational_impact:   d.incident.operational_impact || '',
            preventative_actions: d.incident.preventative_actions || '',
          });
          setPostmortem({
            notes:    d.incident.postmortem_notes || '',
            lessons:  d.incident.lessons_learned || '',
            followup: d.incident.followup_actions || '',
            verified: d.incident.postmortem_verified || false,
          });
        }
      })
      .catch(() => setErr('Failed to load lifecycle'))
      .finally(() => setLoading(false));
  }, [api, ruleId]);

  useEffect(() => { load(); }, [load]);

  const post = (path, body, successMsg) => {
    setSubmitting(true); setMsg(''); setErr(null);
    fetch(`${api}/v1/staff/incidents/${encodeURIComponent(ruleId)}/${path}`, {
      method:'POST', credentials:'include',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify(body),
    }).then(r => r.json().then(d => ({ ok:r.ok, d })))
    .then(({ ok, d }) => {
      if (ok) { setMsg(successMsg); setLifecycle(d.incident); if (onUpdate) onUpdate(d.incident); }
      else setErr(d.error || 'Action failed');
    })
    .catch(() => setErr('Network error'))
    .finally(() => setSubmitting(false));
  };

  const dm = {
    section: { marginBottom:16 },
    label:   { fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.6)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6, display:'block' },
    input:   { width:'100%', boxSizing:'border-box', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:6, padding:'9px 12px', fontSize:13, color:'#fff', fontFamily:"'DM Sans',sans-serif", outline:'none' },
    textarea:{ width:'100%', boxSizing:'border-box', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:6, padding:'9px 12px', fontSize:13, color:'#fff', fontFamily:"'DM Sans',sans-serif", outline:'none', resize:'vertical', minHeight:60 },
    btn:     { padding:'8px 16px', borderRadius:6, fontSize:13, fontWeight:600, cursor:'pointer', border:'none', background:'linear-gradient(135deg,#2D6CFF,#1D4ED8)', color:'#FFFFFF', fontFamily:"'DM Sans',sans-serif" },
    btnGhost:{ padding:'8px 14px', borderRadius:6, fontSize:13, fontWeight:500, cursor:'pointer', border:'1px solid rgba(255,255,255,0.12)', background:'transparent', color:'rgba(255,255,255,0.5)', fontFamily:"'DM Sans',sans-serif" },
    tabBtn:  (active) => ({ padding:'7px 14px', fontSize:12, fontWeight:active?700:500, cursor:'pointer', border:'none', background:'none', borderBottom:`2px solid ${active?'#2D6CFF':'transparent'}`, color:active?'#2D6CFF':'rgba(255,255,255,0.55)', transition:'all 0.12s' }),
    kv:      { display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'6px 0', borderBottom:'1px solid rgba(255,255,255,0.05)', fontSize:12 },
    kvL:     { color:'rgba(255,255,255,0.65)', flexShrink:0, width:110 },
    kvV:     { color:'rgba(255,255,255,0.8)', fontWeight:600, textAlign:'right', wordBreak:'break-word' },
  };

  const STATUSES = ['OPEN','INVESTIGATING','MITIGATING','MONITORING','RESOLVED','CLOSED'];
  const canUpdate    = hasPermission('incident.update');
  const canAssign    = hasPermission('incident.assign');
  const canResolve   = hasPermission('incident.resolve');
  const canPostmortem= hasPermission('incident.postmortem');

  return (
    <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:8, overflow:'hidden', marginBottom:14 }}>
      {/* Panel header */}
      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', borderBottom:'1px solid rgba(255,255,255,0.07)', background:'rgba(255,255,255,0.03)' }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2D6CFF" strokeWidth="2.2">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <span style={{ fontSize:11, fontWeight:700, color:'#8899aa', textTransform:'uppercase', letterSpacing:'0.08em', flex:1 }}>
          Incident Lifecycle
        </span>
        {lifecycle && <IncidentStatusBadge status={lifecycle.status}/>}
        {!lifecycle && !loading && (
          <span style={{ fontSize:10, color:'rgba(255,255,255,0.5)' }}>Not tracked</span>
        )}
      </div>

      {loading && <div style={{ padding:'12px 14px', fontSize:12, color:'rgba(255,255,255,0.6)' }}>Loading lifecycle...</div>}
      {err && <div style={{ padding:'8px 14px', fontSize:12, color:'#FCA5A5' }}>{err}</div>}
      {msg && <div style={{ padding:'8px 14px', fontSize:12, color:'#4ADE80', background:'rgba(74,222,128,0.06)' }}>{msg}</div>}

      {!loading && (
        <>
          {/* Current state summary (always visible) */}
          {lifecycle && (
            <div style={{ padding:'10px 14px', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
              <div style={dm.kv}><span style={dm.kvL}>Status</span><span style={dm.kvV}><IncidentStatusBadge status={lifecycle.status}/></span></div>
              <div style={dm.kv}><span style={dm.kvL}>Severity</span><span style={dm.kvV}>{lifecycle.severity||incidentSeverity||'-'}</span></div>
              {lifecycle.owner_name&&<div style={dm.kv}><span style={dm.kvL}>Owner</span><span style={dm.kvV}>{lifecycle.owner_name}</span></div>}
              {lifecycle.escalation_owner_name&&<div style={dm.kv}><span style={dm.kvL}>Escalation</span><span style={dm.kvV}>{lifecycle.escalation_owner_name}</span></div>}
              {lifecycle.assigned_team&&<div style={dm.kv}><span style={dm.kvL}>Team</span><span style={dm.kvV}>{lifecycle.assigned_team}</span></div>}
              {lifecycle.resolved_at&&<div style={dm.kv}><span style={dm.kvL}>Resolved</span><span style={dm.kvV}>{fmtDate(lifecycle.resolved_at)}</span></div>}
              {lifecycle.closed_at&&<div style={dm.kv}><span style={dm.kvL}>Closed</span><span style={dm.kvV}>{fmtDate(lifecycle.closed_at)}</span></div>}
              {lifecycle.resolution_summary&&(
                <div style={{ marginTop:8, padding:'8px 10px', background:'rgba(16,185,129,0.06)', border:'1px solid rgba(16,185,129,0.15)', borderRadius:5 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#10B981', marginBottom:3, textTransform:'uppercase', letterSpacing:'0.06em' }}>Resolution</div>
                  <div style={{ fontSize:12, color:'rgba(255,255,255,0.6)', lineHeight:1.5 }}>{lifecycle.resolution_summary}</div>
                </div>
              )}
              {lifecycle.postmortem_notes&&(
                <div style={{ marginTop:8, padding:'8px 10px', background:'rgba(139,92,246,0.06)', border:'1px solid rgba(139,92,246,0.15)', borderRadius:5 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#8B5CF6', marginBottom:3, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                    Postmortem {lifecycle.postmortem_verified?'✓':'(Draft)'}
                  </div>
                  <div style={{ fontSize:12, color:'rgba(255,255,255,0.6)', lineHeight:1.5 }}>{lifecycle.postmortem_notes.slice(0,200)}{lifecycle.postmortem_notes.length>200?'...':''}</div>
                </div>
              )}
            </div>
          )}

          {/* Action tab bar */}
          {(canUpdate || canAssign || canResolve || canPostmortem) && (
            <>
              <div style={{ display:'flex', borderBottom:'1px solid rgba(255,255,255,0.07)', paddingLeft:4, background:'rgba(255,255,255,0.02)' }}>
                {canUpdate    && <button style={dm.tabBtn(tab==='status')}    onClick={()=>setTab('status')}>Status</button>}
                {canAssign    && <button style={dm.tabBtn(tab==='assign')}    onClick={()=>setTab('assign')}>Assign</button>}
                {canResolve   && <button style={dm.tabBtn(tab==='resolve')}   onClick={()=>setTab('resolve')}>Resolve</button>}
                {canPostmortem&& <button style={dm.tabBtn(tab==='postmortem')}onClick={()=>setTab('postmortem')}>Postmortem</button>}
              </div>

              <div style={{ padding:'12px 14px' }}>
                {/* STATUS TAB */}
                {tab === 'status' && canUpdate && (
                  <div style={dm.section}>
                    <label style={dm.label}>New Status</label>
                    <select style={{ ...dm.input, marginBottom:8 }} value={newStatus} onChange={e=>setNewStatus(e.target.value)}>
                      {STATUSES.map(st => <option key={st} value={st}>{ILC_STATUS_CONFIG[st]?.label||st}</option>)}
                    </select>
                    <label style={dm.label}>Note {newStatus==='RESOLVED'||newStatus==='CLOSED'?'(required)':'(optional)'}</label>
                    <textarea style={{ ...dm.textarea, marginBottom:8 }} placeholder="Reason for status change..." value={statusNote} onChange={e=>setStatusNote(e.target.value)}/>
                    <button style={dm.btn} disabled={submitting} onClick={() => post('status', { status:newStatus, note:statusNote }, `Status changed to ${newStatus}`)}>
                      {submitting ? 'Saving...' : 'Update Status'}
                    </button>
                  </div>
                )}

                {/* ASSIGN TAB */}
                {tab === 'assign' && canAssign && (
                  <div style={dm.section}>
                    <label style={dm.label}>Owner (Staff User ID)</label>
                    <input style={{ ...dm.input, marginBottom:8 }} placeholder="Staff user ID..." value={ownerId} onChange={e=>setOwnerId(e.target.value)}/>
                    <label style={dm.label}>Escalation Owner (optional)</label>
                    <input style={{ ...dm.input, marginBottom:8 }} placeholder="Staff user ID..." value={escOwnerId} onChange={e=>setEscOwnerId(e.target.value)}/>
                    <label style={dm.label}>Assigned Team (optional)</label>
                    <input style={{ ...dm.input, marginBottom:8 }} placeholder="Team name..." value={team} onChange={e=>setTeam(e.target.value)}/>
                    <label style={dm.label}>Note (optional)</label>
                    <textarea style={{ ...dm.textarea, marginBottom:8 }} placeholder="Assignment note..." value={assignNote} onChange={e=>setAssignNote(e.target.value)}/>
                    <button style={dm.btn} disabled={submitting||!ownerId} onClick={() => post('assign', { owner_id:ownerId, escalation_owner_id:escOwnerId||undefined, assigned_team:team||undefined, note:assignNote }, 'Ownership assigned')}>
                      {submitting ? 'Saving...' : 'Assign'}
                    </button>
                  </div>
                )}

                {/* RESOLVE TAB */}
                {tab === 'resolve' && canResolve && (
                  <div style={dm.section}>
                    <label style={dm.label}>Resolution Summary <span style={{ color:'#EF4444' }}>*</span></label>
                    <textarea style={{ ...dm.textarea, marginBottom:8 }} placeholder="What was done to resolve this incident..." value={resolution.summary} onChange={e=>setResolution(p=>({...p,summary:e.target.value}))}/>
                    <label style={dm.label}>Root Cause</label>
                    <textarea style={{ ...dm.textarea, marginBottom:8 }} placeholder="What caused this incident..." value={resolution.root_cause} onChange={e=>setResolution(p=>({...p,root_cause:e.target.value}))}/>
                    <label style={dm.label}>Customer Impact</label>
                    <textarea style={{ ...dm.textarea, marginBottom:8 }} placeholder="How customers were affected..." value={resolution.customer_impact} onChange={e=>setResolution(p=>({...p,customer_impact:e.target.value}))}/>
                    <label style={dm.label}>Operational Impact</label>
                    <textarea style={{ ...dm.textarea, marginBottom:8 }} placeholder="Platform impact..." value={resolution.operational_impact} onChange={e=>setResolution(p=>({...p,operational_impact:e.target.value}))}/>
                    <label style={dm.label}>Preventative Actions</label>
                    <textarea style={{ ...dm.textarea, marginBottom:8 }} placeholder="What will prevent recurrence..." value={resolution.preventative_actions} onChange={e=>setResolution(p=>({...p,preventative_actions:e.target.value}))}/>
                    <button style={dm.btn} disabled={submitting||!resolution.summary} onClick={() => post('resolve', { resolution_summary:resolution.summary, root_cause:resolution.root_cause, customer_impact:resolution.customer_impact, operational_impact:resolution.operational_impact, preventative_actions:resolution.preventative_actions }, 'Incident resolved')}>
                      {submitting ? 'Saving...' : 'Mark Resolved'}
                    </button>
                  </div>
                )}

                {/* POSTMORTEM TAB */}
                {tab === 'postmortem' && canPostmortem && (
                  <div style={dm.section}>
                    <label style={dm.label}>Postmortem Notes <span style={{ color:'#EF4444' }}>*</span></label>
                    <textarea style={{ ...dm.textarea, marginBottom:8 }} placeholder="Detailed postmortem analysis..." value={postmortem.notes} onChange={e=>setPostmortem(p=>({...p,notes:e.target.value}))}/>
                    <label style={dm.label}>Lessons Learned</label>
                    <textarea style={{ ...dm.textarea, marginBottom:8 }} placeholder="What did we learn..." value={postmortem.lessons} onChange={e=>setPostmortem(p=>({...p,lessons:e.target.value}))}/>
                    <label style={dm.label}>Follow-Up Actions</label>
                    <textarea style={{ ...dm.textarea, marginBottom:8 }} placeholder="Action items to prevent recurrence..." value={postmortem.followup} onChange={e=>setPostmortem(p=>({...p,followup:e.target.value}))}/>
                    <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'rgba(255,255,255,0.5)', cursor:'pointer', marginBottom:12 }}>
                      <input type="checkbox" checked={postmortem.verified} onChange={e=>setPostmortem(p=>({...p,verified:e.target.checked}))} style={{ cursor:'pointer' }}/>
                      Mark postmortem as verified (auto-closes resolved incident)
                    </label>
                    <button style={dm.btn} disabled={submitting||!postmortem.notes} onClick={() => post('postmortem', { postmortem_notes:postmortem.notes, lessons_learned:postmortem.lessons, followup_actions:postmortem.followup, verified:postmortem.verified }, 'Postmortem updated')}>
                      {submitting ? 'Saving...' : 'Save Postmortem'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Not yet tracked — first interaction button */}
          {!lifecycle && !loading && (canUpdate || canAssign) && (
            <div style={{ padding:'10px 14px' }}>
              <button style={{ ...dm.btn, fontSize:12 }} onClick={() => post('status', { status:'OPEN', note:'Lifecycle tracking initiated' }, 'Incident tracking started')}>
                Start Tracking This Incident
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// BULK GOVERNANCE SIMULATION SYSTEM
// ============================================================================

// Blast radius visual config
const BLAST_CONFIG = {
  CRITICAL: { color:T.red,     bg:T.redBg,      border:T.redBorder   },
  HIGH:     { color:'#c05621', bg:'#fffaf0',     border:'#fbd38d'     },
  MODERATE: { color:T.amber,   bg:T.amberBg,    border:T.amberBorder },
  LOW:      { color:T.accent,  bg:T.accentLight,border:T.accentBorder},
  NONE:     { color:T.green,   bg:T.greenBg,    border:T.greenBorder },
};

// BlastRadiusBadge
function BlastRadiusBadge({ level }) {
  const cfg = BLAST_CONFIG[level] || BLAST_CONFIG.NONE;
  return (
    <span style={{
      fontSize:10, fontWeight:800, letterSpacing:'0.06em', textTransform:'uppercase',
      color:cfg.color, background:cfg.bg, border:`1px solid ${cfg.border}`,
      borderRadius:3, padding:'2px 7px',
    }}>
      Blast: {level}
    </span>
  );
}

// ExecutionRiskBadge
function ExecutionRiskBadge({ level }) {
  const cfg = BLAST_CONFIG[level] || BLAST_CONFIG.NONE;
  return (
    <span style={{
      fontSize:10, fontWeight:800, letterSpacing:'0.06em', textTransform:'uppercase',
      color:cfg.color, background:cfg.bg, border:`1px solid ${cfg.border}`,
      borderRadius:3, padding:'2px 7px',
    }}>
      Risk: {level}
    </span>
  );
}

// SimulationMetricGrid — compact grid of impact counts
function SimulationMetricGrid({ sim }) {
  const metrics = [
    { label:'Affected',    val:sim.affected_count,           color:T.text },
    { label:'Users',       val:sim.affected_user_count||0,   color:sim.affected_user_count>0?T.amber:T.text },
    { label:'Jobs',        val:sim.affected_job_count||0,    color:sim.affected_job_count>0?T.accent:T.text },
    { label:'Tickets',     val:sim.affected_ticket_count||0, color:sim.affected_ticket_count>0?T.accent:T.text },
    { label:'Incidents',   val:sim.affected_incident_count||0, color:sim.affected_incident_count>0?T.red:T.text },
    { label:'Credits',     val:sim.estimated_credit_impact||0, color:sim.estimated_credit_impact>0?T.amber:T.text },
  ];
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:5, marginBottom:10 }}>
      {metrics.map(m => (
        <div key={m.label} style={{
          background:T.bg, border:`1px solid ${T.border}`, borderRadius:4,
          padding:'6px 8px', textAlign:'center',
        }}>
          <div style={{ fontSize:16, fontWeight:800, color:m.color, lineHeight:1 }}>{m.val}</div>
          <div style={{ fontSize:10, color:T.textFaint, marginTop:2 }}>{m.label}</div>
        </div>
      ))}
    </div>
  );
}

// SimulationWarningList — collapsible warning list
function SimulationWarningList({ warnings }) {
  const [open, setOpen] = useState(true);
  if (!warnings || warnings.length === 0) return null;
  return (
    <div style={{ marginBottom:10 }}>
      <div
        style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', marginBottom: open ? 5 : 0 }}
        onClick={() => setOpen(v => !v)}
      >
        <span style={{ fontSize:11, fontWeight:700, color:T.amber, textTransform:'uppercase', letterSpacing:'0.06em', flex:1 }}>
          Warnings ({warnings.length})
        </span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.textFaint} strokeWidth="2"
          style={{ transform:open?'rotate(180deg)':'none', transition:'transform 0.12s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>
      {open && warnings.map((w, i) => (
        <div key={i} style={{
          display:'flex', gap:7, padding:'5px 8px', marginBottom:3,
          background:T.amberBg, border:`1px solid ${T.amberBorder}`,
          borderLeft:`3px solid ${T.amber}`, borderRadius:4,
          fontSize:12, color:T.amber,
        }}>
          <span style={{ fontWeight:800, flexShrink:0 }}>!</span>
          {w}
        </div>
      ))}
    </div>
  );
}

// BulkActionSimulationViewer — renders simulation results after dry-run
function BulkActionSimulationViewer({ result, onConfirmExecute, onReset }) {
  if (!result || !result.simulation) return null;
  const sim = result.simulation;
  const rollback = sim.rollback_available;

  return (
    <div style={{
      marginBottom:12, padding:'12px 14px',
      background:T.bg, border:`1px solid ${T.border}`,
      borderLeft:`3px solid ${T.amber}`, borderRadius:6,
    }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:10, flexWrap:'wrap' }}>
        <span style={{ fontSize:13, fontWeight:700, color:T.text, flex:1 }}>Simulation Result</span>
        <BlastRadiusBadge level={sim.blast_radius}/>
        <ExecutionRiskBadge level={sim.execution_risk}/>
        {rollback
          ? <span style={{ fontSize:10, fontWeight:700, color:T.green, background:T.greenBg, border:`1px solid ${T.greenBorder}`, borderRadius:3, padding:'2px 6px' }}>ROLLBACK OK</span>
          : <span style={{ fontSize:10, fontWeight:700, color:T.red, background:T.redBg, border:`1px solid ${T.redBorder}`, borderRadius:3, padding:'2px 6px' }}>NO ROLLBACK</span>
        }
      </div>

      {/* Metrics */}
      <SimulationMetricGrid sim={sim}/>

      {/* Warnings */}
      <SimulationWarningList warnings={sim.warnings}/>

      {/* Errors from dry run */}
      {result.errors && result.errors.length > 0 && (
        <div style={{ marginBottom:8, fontSize:12, color:T.red }}>
          {result.errors.length} error(s): {result.errors.slice(0,2).join('; ')}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:4 }}>
        <button style={s.btn()} onClick={onReset}>Reset</button>
        <button
          style={{
            ...s.btn('primary'),
            background: sim.execution_risk === 'CRITICAL' ? T.red : sim.execution_risk === 'HIGH' ? '#c05621' : T.accent,
          }}
          onClick={onConfirmExecute}
        >
          Proceed with Execution
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// END BULK GOVERNANCE SIMULATION SYSTEM
// ============================================================================

// ── Bulk Action Panel ─────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function BulkActionPanel({ api, entityType, entityIds, onClose, hasPermission }) {
  const [action, setAction]         = useState('');
  const [note, setNote]             = useState('');
  const [result, setResult]         = useState(null);
  const [simResult, setSimResult]   = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr]               = useState(null);

  const ACTIONS_FOR_TYPE = {
    job:    ['requeue', 'acknowledge', 'refund_credits'],
    ticket: ['escalate', 'acknowledge'],
    user:   ['acknowledge'],
  };
  const available = ACTIONS_FOR_TYPE[entityType] || ['acknowledge'];

  const execute = (forceLive) => {
    if (!action) return;
    const isLive = !!forceLive;
    if (isLive && !note.trim()) { setErr('Note required for live execution.'); return; }
    setSubmitting(true); setResult(null); setErr(null);
    if (!forceLive) setSimResult(null);
    fetch(`${api}/v1/staff/entity/bulk`, {
      method:'POST', credentials:'include',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify({ action, entity_type:entityType, entity_ids:entityIds, note, dry_run: !isLive }),
    }).then(r => r.json().then(d => ({ ok:r.ok, d })))
    .then(({ ok, d }) => {
      if (ok) {
        if (!isLive && d.simulation) {
          setSimResult(d);
        } else {
          setResult(d);
          setSimResult(null);
        }
      } else {
        setErr(d.error||'Failed');
      }
    })
    .catch(() => setErr('Network error'))
    .finally(() => setSubmitting(false));
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:9500, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:'22px', width:500, boxShadow:'0 8px 32px rgba(0,0,0,0.5)', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:4 }}>Bulk Action</div>
        <div style={{ fontSize:13, color:T.textSub, marginBottom:14 }}>{entityIds.length} {entityType}(s) selected</div>

        <select style={{ ...s.select, width:'100%', marginBottom:12 }} value={action} onChange={e => { setAction(e.target.value); setSimResult(null); setResult(null); }}>
          <option value="">Select action...</option>
          {available.map(a => <option key={a} value={a}>{a.replace(/_/g,' ')}</option>)}
        </select>

        <textarea style={{ ...s.input, width:'100%', boxSizing:'border-box', minHeight:52, resize:'vertical', fontFamily:'inherit', fontSize:13, marginBottom:10 }}
          placeholder="Operator note (required for execution after simulation)..."
          value={note} onChange={e => setNote(e.target.value)}/>

        {err && <div style={{ ...s.error, marginBottom:8 }}>! {err}</div>}

        {/* Simulation viewer — shown after dry-run */}
        {simResult && (
          <BulkActionSimulationViewer
            result={simResult}
            onConfirmExecute={() => execute(true)}
            onReset={() => { setSimResult(null); setResult(null); }}
          />
        )}

        {/* Live execution result */}
        {result && !result.dry_run && (
          <div style={{ padding:'10px 12px', background:T.greenBg, border:`1px solid ${T.greenBorder}`, borderRadius:5, fontSize:13, marginBottom:10 }}>
            <div style={{ fontWeight:700, color:T.green }}>
              Executed: {result.affected}/{result.total_requested} affected
            </div>
            {result.errors && result.errors.length > 0 && (
              <div style={{ fontSize:12, color:T.amber, marginTop:4 }}>{result.errors.slice(0,3).join('; ')}</div>
            )}
          </div>
        )}

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button style={s.btn()} onClick={onClose}>Close</button>
          {!simResult && !result && (
            <button style={s.btn('primary')}
              onClick={() => execute(false)}
              disabled={!action || submitting}>
              {submitting ? 'Simulating...' : 'Simulate'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// END UNIVERSAL ENTITY INSPECTOR + BULK ACTION SYSTEM
// ============================================================================

// ============================================================================
// AUTONOMOUS OPERATIONS LAYER
// ============================================================================

const AUTON_SEVERITY = {
  critical:{ color:T.red,     bg:T.redBg,      border:T.redBorder,   dot:'#e53e3e' },
  high:    { color:'#c05621', bg:'#fffaf0',     border:'#fbd38d',     dot:'#c05621' },
  medium:  { color:T.amber,   bg:T.amberBg,    border:T.amberBorder, dot:'#d69e2e' },
  low:     { color:T.accent,  bg:T.accentLight,border:T.accentBorder,dot:'#3182ce' },
  nominal: { color:T.green,   bg:T.greenBg,    border:T.greenBorder, dot:'#38a169' },
};

const AUTON_CAT_COLOR = {
  queue:    '#3182ce',
  credits:  '#d69e2e',
  security: '#c53030',
  provider: '#805ad5',
  support:  '#553c9a',
};

function AutonomousKPI({ label, val, color, sub }) {
  const bc = color==='red'?T.red:color==='green'?T.green:color==='amber'?T.amber:color==='purple'?T.purple:T.accent;
  return (
    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderTop:`3px solid ${bc}`, borderRadius:6, padding:'10px 12px', flex:1, minWidth:100 }}>
      <div style={{ fontSize:11, fontWeight:700, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:5 }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:800, color:T.text, lineHeight:1 }}>{val ?? '-'}</div>
      {sub && <div style={{ fontSize:11, color:T.textFaint, marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function AutonomousSeverityPill({ severity }) {
  const k = (severity||'low').toLowerCase();
  const c = AUTON_SEVERITY[k] || AUTON_SEVERITY.low;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', gap:5, minHeight:32, boxSizing:'border-box', padding:'0 12px', borderRadius:6, fontSize:13, fontWeight:500, whiteSpace:'nowrap', background:c.bg, color:c.color, border:`1px solid ${c.border}` }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:c.dot, flexShrink:0 }}/>
      {k.toUpperCase()}
    </span>
  );
}

function ExecuteModal({ rule, onConfirm, onCancel }) {
  const [dryRun, setDryRun] = useState(true);
  const [note, setNote] = useState('');
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:'22px', width:460, boxShadow:'0 8px 32px rgba(0,0,0,0.5)' }}>
        <div style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:6 }}>Execute Recovery Rule</div>
        <div style={{ fontSize:13, color:T.textSub, marginBottom:14, lineHeight:1.6 }}>
          <strong>{rule.name}</strong><br/>
          {rule.description}
        </div>
        <div style={{ padding:'10px 12px', background:T.amberBg, border:`1px solid ${T.amberBorder}`, borderRadius:6, fontSize:13, color:T.amber, marginBottom:14 }}>
          Safety: {rule.safety_note}
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:14, color:T.text, marginBottom:12, cursor:'pointer' }}>
          <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} style={{ cursor:'pointer' }}/>
          Dry-run mode (preview only, no mutations)
        </label>
        <textarea style={{ ...s.input, width:'100%', boxSizing:'border-box', minHeight:56, resize:'vertical', fontFamily:'inherit', fontSize:13, marginBottom:12 }}
          placeholder="Operator note (required for live execution)..." value={note} onChange={e => setNote(e.target.value)}/>
        {!dryRun && !note.trim() && (
          <div style={{ fontSize:12, color:T.red, marginBottom:8 }}>Operator note required for live execution.</div>
        )}
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button style={s.btn()} onClick={onCancel}>Cancel</button>
          <button
            style={{ ...s.btn(dryRun ? 'primary' : 'danger'), opacity:(!dryRun && !note.trim()) ? 0.5 : 1 }}
            disabled={!dryRun && !note.trim()}
            onClick={() => onConfirm({ dryRun, note })}>
            {dryRun ? 'Simulate' : 'Execute Live'}
          </button>
        </div>
      </div>
    </div>
  );
}

// -- AUTONOMOUS OVERVIEW -------------------------------------------------------
function SecAutonomousOverview({ api }) {
  const [data, setData] = useState(null); const [err, setErr] = useState(null);
  const load = useCallback(() => {
    setData(null);
    fetch(`${api}/v1/staff/autonomous/overview`, { credentials:'include' })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => { if (ok) setData(d); else setErr(d.error||'Failed'); })
      .catch(() => setErr('Network error'));
  }, [api]);
  useEffect(() => { load(); }, [load]);
  if (err) return <div style={s.error}>! {err}</div>;
  if (!data) return <Loader/>;
  const sc = AUTON_SEVERITY[data.severity?.toLowerCase()] || AUTON_SEVERITY.nominal;
  return (
    <>
      <div style={{ padding:'10px 16px', background:sc.bg, border:`1px solid ${sc.border}`, borderRadius:7, marginBottom:12, display:'flex', alignItems:'center', gap:12 }}>
        <span style={{ width:10, height:10, borderRadius:'50%', background:sc.dot, flexShrink:0 }}/>
        <span style={{ fontSize:14, fontWeight:800, color:sc.color, letterSpacing:'0.1em' }}>{data.severity||'NOMINAL'}</span>
        <span style={{ fontSize:13, color:sc.color, opacity:0.85 }}>
          {data.severity==='NOMINAL'
            ? `All ${data.active_rules} recovery rules nominal. No triggers active.`
            : `${data.triggered_now} rule${data.triggered_now!==1?'s':''} triggered — bounded execution available.`}
        </span>
        <div style={{ marginLeft:'auto', fontSize:12, color:sc.color, opacity:0.6 }}>{new Date().toISOString().slice(0,19).replace('T',' ')} UTC</div>
      </div>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:10 }}>
        <AutonomousKPI label="Actions 24h"        val={data.actions_24h}              color="blue"/>
        <AutonomousKPI label="Queue Recoveries"   val={data.queue_recoveries_24h}     color={data.queue_recoveries_24h>0?'amber':'green'}/>
        <AutonomousKPI label="Credit Refunds"     val={data.credit_refunds_24h}       color={data.credit_refunds_24h>0?'purple':'green'}/>
        <AutonomousKPI label="Security Responses" val={data.security_responses_24h}   color={data.security_responses_24h>0?'red':'green'}/>
        <AutonomousKPI label="Failed Executions"  val={data.failed_executions_24h}    color={data.failed_executions_24h>0?'red':'green'}/>
        <AutonomousKPI label="Success Rate"       val={`${data.recovery_success_rate}%`} color={data.recovery_success_rate<90?'red':'green'}/>
      </div>

      {data.triggered_rules && data.triggered_rules.length > 0 && (
        <TableCard title="Active Recovery Triggers" count={data.triggered_rules.length}>
          <table style={s.table}>
            <thead><tr>{['Rule','Category','Severity','Trigger Detail','Rate OK','Action'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.triggered_rules.map((r, i) => (
                <tr key={r.id||i} style={{ background: r.severity==='critical'?T.redBg:'transparent' }}>
                  <td style={s.tds}><span style={{ fontSize:13, fontWeight:700 }}>{r.name}</span></td>
                  <td style={s.td}><span style={{ fontSize:11, fontWeight:700, color:AUTON_CAT_COLOR[r.category]||T.textSub }}>{r.category}</span></td>
                  <td style={s.td}><AutonomousSeverityPill severity={r.severity}/></td>
                  <td style={s.td}><span style={{ fontSize:12, color:T.textSub }}>{r.trigger_detail}</span></td>
                  <td style={s.td}><span style={{ fontSize:12, fontWeight:700, color:r.rate_ok?T.green:T.red }}>{r.rate_ok?'YES':'RATE LIMITED'}</span></td>
                  <td style={s.td}><span style={{ fontSize:11, fontStyle:'italic', color:T.textSub }}>{r.action}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      )}
      {(!data.triggered_rules || data.triggered_rules.length === 0) && (
        <div style={{ ...s.panel, ...s.empty }}>No recovery triggers active. All systems within thresholds.</div>
      )}
      <div style={{ textAlign:'right', marginTop:8 }}><button style={s.btn()} onClick={load}>Refresh</button></div>
    </>
  );
}

// -- AUTONOMOUS RULES ----------------------------------------------------------
function SecAutonomousRules({ api }) {
  const { hasPermission } = useStaffAuth();
  const [data, setData] = useState(null); const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [simResult, setSimResult] = useState(null);
  const [execResult, setExecResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setData(null);
    fetch(`${api}/v1/staff/autonomous/rules`, { credentials:'include' })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => { if (ok) setData(d); else setErr(d.error||'Failed'); })
      .catch(() => setErr('Network error'));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const handleExecute = (rule, opts) => {
    setSubmitting(true); setExecResult(null); setSimResult(null);
    const endpoint = opts.dryRun ? '/v1/staff/autonomous/simulate' : '/v1/staff/autonomous/execute';
    const body = opts.dryRun ? { rule_id: rule.id } : { rule_id: rule.id, dry_run: false, note: opts.note };
    fetch(`${api}${endpoint}`, { method:'POST', credentials:'include', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => { if (opts.dryRun) setSimResult(d); else setExecResult(d); load(); })
      .catch(() => setExecResult({ success:false, error:'Network error' }))
      .finally(() => setSubmitting(false));
  };

  const canExecute = hasPermission('autonomous.execute');

  return (
    <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
      {modal && <ExecuteModal rule={modal} onCancel={() => setModal(null)} onConfirm={opts => { const r=modal; setModal(null); handleExecute(r, opts); }}/>}
      <div style={{ flex:1, minWidth:0 }}>
        {simResult && (
          <div style={{ ...s.panel, marginBottom:10, padding:'12px 16px', borderLeft:`3px solid ${T.accent}` }}>
            <div style={{ fontSize:13, fontWeight:700, color:T.accent, marginBottom:6 }}>Simulation Result — {simResult.rule_name}</div>
            <div style={{ fontSize:13, color:T.textMid }}>Would trigger: <strong>{simResult.would_trigger?'YES':'NO'}</strong></div>
            <div style={{ fontSize:13, color:T.textMid }}>Would affect: <strong>{simResult.would_affect}</strong> records</div>
            <div style={{ fontSize:13, color:T.textMid }}>Rate OK: <strong>{simResult.rate_ok?'YES':'RATE LIMITED'}</strong></div>
            <div style={{ fontSize:13, color:T.textSub, marginTop:4 }}>{simResult.detail}</div>
          </div>
        )}
        {execResult && (
          <div style={{ padding:'10px 14px', background:execResult.success?T.greenBg:T.redBg, border:`1px solid ${execResult.success?T.greenBorder:T.redBorder}`, borderRadius:6, marginBottom:10, fontSize:13, color:execResult.success?T.green:T.red }}>
            {execResult.success ? `Executed: ${execResult.detail} (${execResult.affected} affected)` : `Failed: ${execResult.error}`}
          </div>
        )}
        {err && <div style={s.error}>! {err}</div>}
        {!data ? <Loader/> : (
          <TableCard title="Recovery Rules" count={data.total}>
            <table style={{ ...s.table, minWidth:900 }}>
              <thead><tr>{['Rule','Category','Sev','Status','Trigger','Triggered Now','Rate','Sim','Execute'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {(data.rules||[]).map(r => (
                  <tr key={r.id}
                    style={{ cursor:'pointer', background: r.triggered?(r.severity==='critical'?T.redBg:T.amberBg):'transparent' }}
                    onClick={() => setSelected(selected?.id===r.id?null:r)}
                    onMouseEnter={e => { if(selected?.id !== r.id) e.currentTarget.style.background = r.triggered ? (r.severity==='critical'?T.redBg:T.amberBg) : T.borderSoft; }}
                    onMouseLeave={e => e.currentTarget.style.background = r.triggered?(r.severity==='critical'?T.redBg:T.amberBg):'transparent'}>
                    <td style={s.tds}>
                      <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{r.name}</div>
                      {r.dry_run && <span style={{ fontSize:10, color:T.accent, fontWeight:700 }}>DRY-RUN MODE</span>}
                    </td>
                    <td style={s.td}><span style={{ fontSize:11, fontWeight:700, color:AUTON_CAT_COLOR[r.category]||T.textSub }}>{r.category}</span></td>
                    <td style={s.td}><AutonomousSeverityPill severity={r.severity}/></td>
                    <td style={s.td}><span style={{ fontSize:12, fontWeight:700, color:r.enabled?T.green:T.textFaint }}>{r.enabled?'ACTIVE':'OFF'}</span></td>
                    <td style={s.td}><span style={{ fontSize:11, color:T.textFaint, fontStyle:'italic' }}>{r.trigger}</span></td>
                    <td style={s.td}>{r.triggered?<span style={{ fontSize:12, fontWeight:700, color:r.severity==='critical'?T.red:T.amber }}>YES — {r.trigger_detail}</span>:<span style={{ fontSize:12, color:T.textFaint }}>No</span>}</td>
                    <td style={s.td}><span style={{ fontSize:12, color:r.rate_ok?T.green:T.red }}>{r.executions_1h}/{r.max_executions_per_hour}/hr</span></td>
                    <td style={s.td}>
                      <button style={{ ...s.btn(), fontSize:11, padding:'2px 7px' }}
                        onClick={e => { e.stopPropagation(); handleExecute(r, { dryRun:true }); }}
                        disabled={submitting}>Sim</button>
                    </td>
                    <td style={s.td}>
                      {canExecute ? (
                        <button style={{ ...s.btn('primary'), fontSize:11, padding:'2px 7px' }}
                          onClick={e => { e.stopPropagation(); setModal(r); }}
                          disabled={submitting}>Execute</button>
                      ) : <span style={{ fontSize:11, color:T.textFaint }}>No perm</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        )}
        <div style={{ textAlign:'right', marginTop:6 }}><button style={s.btn()} onClick={load}>Refresh</button></div>
      </div>

      {selected && (
        <div style={{ width:300, flexShrink:0 }}>
          <div style={{ ...s.panel, position:'sticky', top:20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
              <div style={{ fontSize:13, fontWeight:700, color:T.text, flex:1 }}>Rule Detail</div>
              <button onClick={() => setSelected(null)} style={{ background:'none', border:'none', color:T.textFaint, cursor:'pointer', fontSize:18 }}>×</button>
            </div>
            {selected.triggered && <div style={{ padding:'7px 10px', background:T.redBg, border:`1px solid ${T.redBorder}`, borderRadius:5, fontSize:13, fontWeight:700, color:T.red, marginBottom:8 }}>TRIGGERED: {selected.trigger_detail}</div>}
            {[
              { label:'ID',        val:<code style={{ fontFamily:'monospace', fontSize:10 }}>{selected.id}</code> },
              { label:'Category',  val:selected.category },
              { label:'Action',    val:<code style={{ fontFamily:'monospace', fontSize:10 }}>{selected.action}</code> },
              { label:'Max/hr',    val:selected.max_executions_per_hour },
              { label:'Cooldown',  val:`${selected.cooldown_minutes}m` },
              { label:'Escalation at', val:`${selected.escalation_threshold} triggers` },
              { label:'Rollback',  val:selected.rollback_capable?'Yes':'No' },
            ].map(({ label, val }) => (
              <div key={label} style={s.kvRow}>
                <span style={s.kvLabel}>{label}</span>
                <span style={{ ...s.kvValue, fontSize:12 }}>{val}</span>
              </div>
            ))}
            <div style={{ marginTop:8, padding:'8px', background:T.bg, border:`1px solid ${T.border}`, borderRadius:5 }}>
              <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:4 }}>Safety</div>
              <div style={{ fontSize:12, color:T.textMid, lineHeight:1.5 }}>{selected.safety_note}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -- AUTONOMOUS SUBSYSTEM (shared for Queue/Provider/Credits/Security) ---------
function SecAutonomousSubsystem({ api, category, title }) {
  const { hasPermission } = useStaffAuth();
  const [rules, setRules] = useState(null); const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null);
  const [result, setResult] = useState(null);
  const [simResult, setSimResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setRules(null);
    fetch(`${api}/v1/staff/autonomous/rules`, { credentials:'include' })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) setRules((d.rules||[]).filter(r => r.category === category));
        else setErr(d.error||'Failed');
      })
      .catch(() => setErr('Network error'));
  }, [api, category]);
  useEffect(() => { load(); }, [load]);

  const doAction = (rule, opts) => {
    setSubmitting(true); setResult(null); setSimResult(null);
    const endpoint = opts.dryRun ? '/v1/staff/autonomous/simulate' : '/v1/staff/autonomous/execute';
    const body = opts.dryRun ? { rule_id: rule.id } : { rule_id: rule.id, dry_run: false, note: opts.note };
    fetch(`${api}${endpoint}`, { method:'POST', credentials:'include', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => { if (opts.dryRun) setSimResult(d); else setResult(d); load(); })
      .catch(() => setResult({ success:false, error:'Network error' }))
      .finally(() => setSubmitting(false));
  };

  const canExecute = hasPermission('autonomous.execute');

  const catInfo = {
    queue:    { icon:'⚙', desc:'Detects and recovers stalled queues and stuck jobs. Bounded to 50 jobs per execution.' },
    provider: { icon:'⚡', desc:'Monitors provider health. Creates incidents and audit records. No routing changes without operator action.' },
    credits:  { icon:'◈', desc:'Detects and refunds credits from failed jobs. Bounded per-batch. Rollback capable.' },
    security: { icon:'⛨', desc:'Responds to failed login spikes. Soft-locks only. Operator confirmation required for hard actions.' },
  };
  const info = catInfo[category] || { icon:'◦', desc:'' };

  return (
    <>
      {modal && <ExecuteModal rule={modal} onCancel={() => setModal(null)} onConfirm={opts => { const r=modal; setModal(null); doAction(r, opts); }}/>}
      <div style={{ ...s.panel, padding:'12px 16px', marginBottom:12, borderLeft:`3px solid ${AUTON_CAT_COLOR[category]||T.accent}` }}>
        <div style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:4 }}>{info.icon} {title}</div>
        <div style={{ fontSize:13, color:T.textSub, lineHeight:1.6 }}>{info.desc}</div>
      </div>

      {simResult && (
        <div style={{ ...s.panel, marginBottom:10, padding:'12px 16px', borderLeft:`3px solid ${T.accent}` }}>
          <div style={{ fontSize:13, fontWeight:700, color:T.accent, marginBottom:6 }}>Simulation — {simResult.rule_name}</div>
          <div style={{ fontSize:13, color:T.textMid }}>Would trigger: <strong>{simResult.would_trigger?'YES':'NO'}</strong> &nbsp; Would affect: <strong>{simResult.would_affect}</strong> records</div>
          <div style={{ fontSize:13, color:T.textSub, marginTop:4 }}>{simResult.detail}</div>
        </div>
      )}
      {result && (
        <div style={{ padding:'9px 14px', background:result.success?T.greenBg:T.redBg, border:`1px solid ${result.success?T.greenBorder:T.redBorder}`, borderRadius:6, marginBottom:10, fontSize:13, color:result.success?T.green:T.red }}>
          {result.success ? `Done: ${result.detail} (${result.affected} affected)` : `Failed: ${result.error}`}
        </div>
      )}
      {err && <div style={s.error}>! {err}</div>}
      {!rules ? <Loader/> : rules.length === 0 ? (
        <div style={{ ...s.panel, ...s.empty }}>No rules defined for this subsystem.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {rules.map(r => (
            <div key={r.id} style={{ ...s.panel, padding:'14px 16px', borderLeft:`3px solid ${r.triggered?(r.severity==='critical'?T.red:T.amber):T.borderSoft}` }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                    <span style={{ fontSize:14, fontWeight:700, color:T.text }}>{r.name}</span>
                    <AutonomousSeverityPill severity={r.severity}/>
                    {r.triggered && <span style={{ fontSize:12, fontWeight:700, color:r.severity==='critical'?T.red:T.amber }}>TRIGGERED: {r.trigger_detail}</span>}
                    {r.dry_run && <span style={{ fontSize:10, fontWeight:700, color:T.accent, background:T.accentLight, border:`1px solid ${T.accentBorder}`, borderRadius:3, padding:'1px 5px' }}>DRY-RUN MODE</span>}
                  </div>
                  <div style={{ fontSize:13, color:T.textSub, marginBottom:8 }}>{r.description}</div>
                  <div style={{ display:'flex', gap:16, flexWrap:'wrap', fontSize:12, color:T.textFaint }}>
                    <span>Max {r.max_executions_per_hour}/hr</span>
                    <span>Cooldown {r.cooldown_minutes}m</span>
                    <span>Executions this hour: <strong style={{ color:r.rate_ok?T.green:T.red }}>{r.executions_1h}</strong></span>
                    {r.rollback_capable && <span style={{ color:T.green }}>Rollback capable</span>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                  <button style={{ ...s.btn(), fontSize:12, padding:'5px 10px' }}
                    onClick={() => doAction(r, { dryRun:true })}
                    disabled={submitting}>Simulate</button>
                  {canExecute && (
                    <button style={{ ...s.btn('primary'), fontSize:12, padding:'5px 10px' }}
                      onClick={() => setModal(r)}
                      disabled={submitting || !r.rate_ok}>
                      {!r.rate_ok ? 'Rate Limited' : 'Execute'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ textAlign:'right', marginTop:8 }}><button style={s.btn()} onClick={load}>Refresh</button></div>
    </>
  );
}

// -- AUTONOMOUS HISTORY --------------------------------------------------------
function SecAutonomousHistory({ api }) {
  const [data, setData] = useState(null); const [err, setErr] = useState(null);
  const [page, setPage] = useState(1);
  const [hours, setHours] = useState(168);
  const [category, setCategory] = useState('');
  const [successFilter, setSuccessFilter] = useState('');

  const load = useCallback(() => {
    setData(null);
    const p = new URLSearchParams({ page, per_page:50, hours });
    if (category) p.set('category', category);
    if (successFilter) p.set('success', successFilter);
    fetch(`${api}/v1/staff/autonomous/history?${p}`, { credentials:'include' })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => { if (ok) setData(d); else setErr(d.error||'Failed'); })
      .catch(() => setErr('Network error'));
  }, [api, page, hours, category, successFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div style={s.filterRow}>
        <select style={s.select} value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}>
          <option value="">All Categories</option>
          {['queue','credits','security','provider','support'].map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={s.select} value={successFilter} onChange={e => { setSuccessFilter(e.target.value); setPage(1); }}>
          <option value="">All Results</option>
          <option value="true">Success only</option>
          <option value="false">Failures only</option>
        </select>
        <select style={s.select} value={hours} onChange={e => { setHours(Number(e.target.value)); setPage(1); }}>
          {[24,48,168,720].map(h => <option key={h} value={h}>Last {h}h</option>)}
        </select>
        <button style={s.btn('primary')} onClick={() => { setPage(1); load(); }}>Apply</button>
        <button style={s.btn()} onClick={load}>Refresh</button>
      </div>
      {err && <div style={s.error}>! {err}</div>}
      {!data ? <Loader/> : (
        <TableCard title="Execution History" count={data.total}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ ...s.table, minWidth:900 }}>
              <thead><tr>
                {['Action','Rule','Category','Dry Run','Result','Affected','Rollback','Actor','Time'].map(h => <th key={h} style={s.th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {(data.entries||[]).length === 0
                  ? <tr><td colSpan={9} style={{ ...s.td, ...s.empty }}>No execution history in this window.</td></tr>
                  : (data.entries||[]).map((e, i) => (
                    <tr key={e.id||i} style={{ background: e.success===false ? T.redBg : 'transparent' }}>
                      <td style={s.td}><code style={{ fontFamily:'monospace', fontSize:11, color:T.accent }}>{e.action}</code></td>
                      <td style={s.tds}><span style={{ fontSize:13 }}>{e.rule_name||'-'}</span></td>
                      <td style={s.td}><span style={{ fontSize:11, fontWeight:700, color:AUTON_CAT_COLOR[e.category]||T.textSub }}>{e.category||'-'}</span></td>
                      <td style={s.td}><span style={{ fontSize:12, color:e.dry_run?T.accent:T.textFaint }}>{e.dry_run?'SIM':'LIVE'}</span></td>
                      <td style={s.td}>
                        <span style={{ fontSize:12, fontWeight:700, color:e.success?T.green:T.red }}>
                          {e.success==null?'-':e.success?'OK':'FAIL'}
                        </span>
                      </td>
                      <td style={s.tds}>{e.affected??'-'}</td>
                      <td style={s.td}><span style={{ fontSize:11, fontFamily:'monospace', color:e.rollback_id?T.green:T.textFaint }}>{e.rollback_id?'Yes':'-'}</span></td>
                      <td style={s.td}><span style={{ fontSize:13 }}>{e.actor||'system'}</span></td>
                      <td style={{ ...s.td, fontSize:11, color:T.textFaint, whiteSpace:'nowrap' }}>{fmtAgo(e.created_at)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {data.pages > 1 && <Pager page={page} pages={data.pages} total={data.total} onPage={setPage}/>}
        </TableCard>
      )}
    </>
  );
}

// ============================================================================
// END AUTONOMOUS OPERATIONS LAYER
// ============================================================================

// ============================================================================
// AUTOMATION FOUNDATION
// ============================================================================

const AUTO_SEVERITY_CONFIG = {
  critical: { color: T.red,     bg: T.redBg,      border: T.redBorder,    dot: '#e53e3e' },
  high:     { color: '#c05621', bg: '#fffaf0',     border: '#fbd38d',      dot: '#c05621' },
  medium:   { color: T.amber,   bg: T.amberBg,     border: T.amberBorder,  dot: '#d69e2e' },
  low:      { color: T.accent,  bg: T.accentLight, border: T.accentBorder, dot: '#3182ce' },
  nominal:  { color: T.green,   bg: T.greenBg,     border: T.greenBorder,  dot: '#38a169' },
};

const AUTO_CAT_COLORS = {
  provider:       '#3182ce',
  platform_health:'#553c9a',
  queue:          '#b7791f',
  communications: '#276749',
  support:        '#805ad5',
  security:       '#c53030',
  credits:        '#d69e2e',
  billing:        '#d69e2e',
  moderation:     '#c05621',
};

function AutoSeverityPill({ severity }) {
  const k = (severity || 'low').toLowerCase();
  const c = AUTO_SEVERITY_CONFIG[k] || AUTO_SEVERITY_CONFIG.low;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', gap:5, minHeight:32, boxSizing:'border-box', padding:'0 12px', borderRadius:6, fontSize:13, fontWeight:500, whiteSpace:'nowrap', background:c.bg, color:c.color, border:`1px solid ${c.border}` }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:c.dot, flexShrink:0 }}/>
      {k.toUpperCase()}
    </span>
  );
}

function AutoKPI({ label, val, color, sub }) {
  const borderColor = color==='red'?T.red : color==='green'?T.green : color==='amber'?T.amber : color==='purple'?T.purple : T.accent;
  return (
    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderTop:`3px solid ${borderColor}`, borderRadius:6, padding:'10px 12px', flex:1, minWidth:100 }}>
      <div style={{ fontSize:11, fontWeight:700, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:5 }}>{label}</div>
      <div style={{ fontSize:26, fontWeight:800, color:T.text, lineHeight:1 }}>{val ?? '-'}</div>
      {sub && <div style={{ fontSize:11, color:T.textFaint, marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function AutoActionModal({ title, body, onConfirm, onCancel }) {
  const [note, setNote] = useState('');
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:'22px', width:420, boxShadow:'0 8px 32px rgba(0,0,0,0.4)' }}>
        <div style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:8 }}>{title}</div>
        <div style={{ fontSize:13, color:T.textSub, marginBottom:12, lineHeight:1.6 }}>{body}</div>
        <textarea style={{ ...s.input, width:'100%', boxSizing:'border-box', minHeight:56, resize:'vertical', fontFamily:'inherit', fontSize:13, marginBottom:12 }}
          placeholder="Operator note (optional)..." value={note} onChange={e => setNote(e.target.value)}/>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button style={s.btn()} onClick={onCancel}>Cancel</button>
          <button style={s.btn('primary')} onClick={() => onConfirm(note)}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// -- AUTOMATION OVERVIEW ------------------------------------------------------
function SecAutomationOverview({ api }) {
  const [data, setData] = useState(null); const [err, setErr] = useState(null);
  const load = useCallback(() => {
    setData(null);
    fetch(`${api}/v1/staff/automation/overview`, { credentials:'include' })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => { if (ok) setData(d); else setErr(d.error||'Failed'); })
      .catch(() => setErr('Network error'));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div style={s.error}>! {err}</div>;
  if (!data) return <Loader/>;

  const sevCfg = AUTO_SEVERITY_CONFIG[data.severity?.toLowerCase()] || AUTO_SEVERITY_CONFIG.nominal;

  return (
    <>
      {/* Severity strip */}
      <div style={{ padding:'10px 16px', background:sevCfg.bg, border:`1px solid ${sevCfg.border}`, borderRadius:7, marginBottom:12, display:'flex', alignItems:'center', gap:12 }}>
        <span style={{ width:10, height:10, borderRadius:'50%', background:sevCfg.dot, flexShrink:0 }}/>
        <span style={{ fontSize:14, fontWeight:800, color:sevCfg.color, letterSpacing:'0.1em' }}>{data.severity || 'NOMINAL'}</span>
        <span style={{ fontSize:13, color:sevCfg.color, opacity:0.85 }}>
          {data.severity === 'NOMINAL'
            ? `All rules nominal. ${data.active_rules} rules active, none triggered.`
            : `${data.triggered_rules_now} rule${data.triggered_rules_now !== 1 ? 's' : ''} triggered — ${data.active_incidents} active incident${data.active_incidents !== 1 ? 's' : ''}.`}
        </span>
        <div style={{ marginLeft:'auto', fontSize:12, color:sevCfg.color, opacity:0.6 }}>{new Date().toISOString().slice(0,19).replace('T',' ')} UTC</div>
      </div>

      {/* KPI strip */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:10 }}>
        <AutoKPI label="Active Rules"        val={data.active_rules}         color="blue"   sub={`${data.total_rules} total`}/>
        <AutoKPI label="Triggered Now"       val={data.triggered_rules_now}  color={data.triggered_rules_now > 0 ? 'red' : 'green'}/>
        <AutoKPI label="Active Incidents"    val={data.active_incidents}     color={data.active_incidents > 0 ? 'red' : 'green'}/>
        <AutoKPI label="Critical"            val={data.critical_count}       color={data.critical_count > 0 ? 'red' : 'green'}/>
        <AutoKPI label="High"                val={data.high_severity_count}  color={data.high_severity_count > 0 ? 'amber' : 'green'}/>
        <AutoKPI label="Suggestions"         val={data.active_suggestions}   color="purple"/>
      </div>

      {/* Metrics row */}
      {data.metrics && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
          <AutoKPI label="Stuck Jobs"       val={fmt(data.metrics.stuck)}            color={data.metrics.stuck > 0 ? 'red' : 'green'}/>
          <AutoKPI label="Queue Depth"      val={fmt((data.metrics.queued||0)+(data.metrics.processing||0))} color="blue"/>
          <AutoKPI label="Success Rate"     val={`${data.metrics.success_rate||0}%`} color={(data.metrics.success_rate||100)<95?'red':'green'}/>
          <AutoKPI label="Mail Failed 24h"  val={fmt(data.metrics.mail_failed_today)} color={data.metrics.mail_failed_today>0?'amber':'green'}/>
          <AutoKPI label="Escalated Tix"   val={fmt(data.metrics.escalated_tickets)} color={data.metrics.escalated_tickets>0?'amber':'green'}/>
          <AutoKPI label="Failed Logins 1h" val={fmt(data.metrics.failed_logins_1h)} color={data.metrics.failed_logins_1h>10?'red':'green'}/>
        </div>
      )}

      {/* Top triggered rules */}
      {data.top_triggers && data.top_triggers.length > 0 && (
        <TableCard title="Currently Triggered Rules" count={data.top_triggers.length}>
          <table style={s.table}>
            <thead><tr>
              {['Rule','Category','Severity','Detail','Action'].map(h => <th key={h} style={s.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {data.top_triggers.map((t, i) => (
                <tr key={t.rule_id + i} style={{ background: t.severity === 'critical' ? T.redBg : 'transparent' }}>
                  <td style={s.tds}><span style={{ fontSize:13, fontWeight:700, color:T.text }}>{t.rule_name}</span></td>
                  <td style={s.td}><span style={{ fontSize:11, fontWeight:700, color:AUTO_CAT_COLORS[t.category]||T.textSub }}>{t.category}</span></td>
                  <td style={s.td}><AutoSeverityPill severity={t.severity}/></td>
                  <td style={s.td}><span style={{ fontSize:12, color:T.textSub }}>{t.detail}</span></td>
                  <td style={{ ...s.td, maxWidth:220 }}><span style={{ fontSize:12, color:T.textMid, fontStyle:'italic' }}>{t.suggested_action}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      )}
      {(!data.top_triggers || data.top_triggers.length === 0) && (
        <div style={{ ...s.panel, ...s.empty }}>No rules currently triggered. All systems nominal.</div>
      )}
      <div style={{ textAlign:'right', marginTop:8 }}>
        <button style={s.btn()} onClick={load}>Refresh</button>
      </div>
    </>
  );
}

// -- AUTOMATION RULES ---------------------------------------------------------
function SecAutomationRules({ api }) {
  const [data, setData] = useState(null); const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => {
    setData(null);
    fetch(`${api}/v1/staff/automation/rules`, { credentials:'include' })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => { if (ok) setData(d); else setErr(d.error||'Failed'); })
      .catch(() => setErr('Network error'));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
      <div style={{ flex:1, minWidth:0 }}>
        {err && <div style={s.error}>! {err}</div>}
        {!data ? <Loader/> : (
          <TableCard title="Rule Registry" count={data.total}>
            <table style={s.table}>
              <thead><tr>
                {['Rule','Category','Severity','Status','Triggered Now','Last Triggered','Action'].map(h => <th key={h} style={s.th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {(data.rules || []).map(r => (
                  <tr key={r.id}
                    style={{ cursor:'pointer', background: selected?.id === r.id ? T.accentLight : r.currently_triggered ? (r.severity === 'critical' ? T.redBg : T.amberBg) : 'transparent' }}
                    onClick={() => setSelected(selected?.id === r.id ? null : r)}
                    onMouseEnter={e => { if(selected?.id !== r.id) e.currentTarget.style.background = r.currently_triggered ? (r.severity==='critical'?T.redBg:T.amberBg) : T.borderSoft; }}
                    onMouseLeave={e => e.currentTarget.style.background = selected?.id === r.id ? T.accentLight : r.currently_triggered ? (r.severity==='critical'?T.redBg:T.amberBg) : 'transparent'}
                  >
                    <td style={s.tds}>
                      <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{r.name}</div>
                      <div style={{ fontSize:11, color:T.textFaint, fontFamily:'monospace' }}>{r.id}</div>
                    </td>
                    <td style={s.td}><span style={{ fontSize:11, fontWeight:700, color:AUTO_CAT_COLORS[r.category]||T.textSub }}>{r.category?.replace(/_/g,' ')}</span></td>
                    <td style={s.td}><AutoSeverityPill severity={r.severity}/></td>
                    <td style={s.td}>
                      <span style={{ fontSize:12, fontWeight:700, color: r.enabled ? T.green : T.textFaint }}>
                        {r.enabled ? 'ACTIVE' : 'DISABLED'}
                      </span>
                    </td>
                    <td style={s.td}>
                      {r.currently_triggered
                        ? <span style={{ fontSize:12, fontWeight:700, color: r.severity==='critical' ? T.red : T.amber }}>YES — {r.current_detail}</span>
                        : <span style={{ fontSize:12, color:T.textFaint }}>No</span>}
                    </td>
                    <td style={{ ...s.td, fontSize:12, color:T.textFaint }}>{r.last_triggered ? fmtAgo(r.last_triggered) : 'Never'}</td>
                    <td style={s.td}>
                      <button style={{ ...s.btn(), fontSize:12, padding:'3px 8px' }} onClick={e => { e.stopPropagation(); setSelected(r); }}>Detail</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        )}
        <div style={{ textAlign:'right', marginTop:6 }}><button style={s.btn()} onClick={load}>Refresh</button></div>
      </div>

      {/* Rule detail panel */}
      {selected && (
        <div style={{ width:320, flexShrink:0 }}>
          <div style={{ ...s.panel, position:'sticky', top:20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
              <div style={{ fontSize:14, fontWeight:700, color:T.text, flex:1 }}>Rule Detail</div>
              <button onClick={() => setSelected(null)} style={{ background:'none', border:'none', color:T.textFaint, cursor:'pointer', fontSize:18 }}>×</button>
            </div>
            {selected.currently_triggered && (
              <div style={{ padding:'8px 10px', background:T.redBg, border:`1px solid ${T.redBorder}`, borderRadius:5, fontSize:13, fontWeight:700, color:T.red, marginBottom:10 }}>
                TRIGGERED: {selected.current_detail}
              </div>
            )}
            {[
              { label:'ID',        val:<code style={{ fontFamily:'monospace', fontSize:11 }}>{selected.id}</code> },
              { label:'Category',  val:selected.category?.replace(/_/g,' ') },
              { label:'Severity',  val:<AutoSeverityPill severity={selected.severity}/> },
              { label:'Status',    val:<span style={{ color:selected.enabled?T.green:T.textFaint, fontWeight:700 }}>{selected.enabled?'ACTIVE':'DISABLED'}</span> },
              { label:'Trigger',   val:selected.trigger_type },
              { label:'Cooldown',  val:`${selected.cooldown_minutes || 0}m` },
            ].map(({ label, val }) => (
              <div key={label} style={s.kvRow}>
                <span style={s.kvLabel}>{label}</span>
                <span style={{ ...s.kvValue, fontSize:13 }}>{val}</span>
              </div>
            ))}
            <div style={{ marginTop:10, padding:'10px', background:T.bg, border:`1px solid ${T.border}`, borderRadius:5 }}>
              <div style={{ fontSize:11, fontWeight:700, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5 }}>Description</div>
              <div style={{ fontSize:13, color:T.textMid, lineHeight:1.6 }}>{selected.description}</div>
            </div>
            <div style={{ marginTop:8, padding:'10px', background:T.bg, border:`1px solid ${T.border}`, borderRadius:5 }}>
              <div style={{ fontSize:11, fontWeight:700, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5 }}>Conditions</div>
              <pre style={{ margin:0, fontSize:12, color:T.textMid, fontFamily:'monospace', whiteSpace:'pre-wrap', lineHeight:1.5 }}>
                {JSON.stringify(selected.conditions, null, 2)}
              </pre>
            </div>
            <div style={{ marginTop:8, padding:'10px', background:T.amberBg, border:`1px solid ${T.amberBorder}`, borderRadius:5 }}>
              <div style={{ fontSize:11, fontWeight:700, color:T.amber, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5 }}>Suggested Action</div>
              <div style={{ fontSize:13, color:T.textMid, lineHeight:1.5 }}>{selected.suggested_action}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -- AUTOMATION TRIGGERS ------------------------------------------------------
function SecAutomationTriggers({ api }) {
  const [data, setData] = useState(null); const [err, setErr] = useState(null);
  const [hours, setHours] = useState(24);
  const load = useCallback(() => {
    setData(null);
    fetch(`${api}/v1/staff/automation/triggers?hours=${hours}`, { credentials:'include' })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => { if (ok) setData(d); else setErr(d.error||'Failed'); })
      .catch(() => setErr('Network error'));
  }, [api, hours]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div style={s.filterRow}>
        <select style={s.select} value={hours} onChange={e => setHours(Number(e.target.value))}>
          {[6,24,48,168].map(h => <option key={h} value={h}>Last {h}h</option>)}
        </select>
        <button style={s.btn('primary')} onClick={load}>Refresh</button>
      </div>
      {err && <div style={s.error}>! {err}</div>}
      {!data ? <Loader/> : (
        <>
          {/* Current triggers */}
          <TableCard title="Currently Active Triggers" count={data.current_triggers?.length}>
            {(data.current_triggers || []).length === 0
              ? <div style={{ ...s.td, ...s.empty }}>No rules currently triggered.</div>
              : <table style={s.table}>
                  <thead><tr>{['Rule','Category','Severity','Detail','Triggered At'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {data.current_triggers.map((t, i) => (
                      <tr key={t.rule_id + i} style={{ background: t.severity === 'critical' ? T.redBg : 'transparent' }}>
                        <td style={s.tds}><span style={{ fontSize:13, fontWeight:700 }}>{t.rule_name}</span></td>
                        <td style={s.td}><span style={{ fontSize:11, fontWeight:700, color:AUTO_CAT_COLORS[t.category]||T.textSub }}>{t.category?.replace(/_/g,' ')}</span></td>
                        <td style={s.td}><AutoSeverityPill severity={t.severity}/></td>
                        <td style={s.td}><span style={{ fontSize:12, color:T.textSub }}>{t.detail}</span></td>
                        <td style={{ ...s.td, fontSize:12, color:T.textFaint }}>{fmtDate(t.fired_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
          </TableCard>

          {/* History triggers */}
          <TableCard title="Trigger History (Audit Log)" count={data.history_triggers?.length}>
            {(data.history_triggers || []).length === 0
              ? <div style={{ ...s.td, ...s.empty }}>No automation history in this window.</div>
              : <table style={s.table}>
                  <thead><tr>{['Action','Category','Severity','Detail','Time'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {data.history_triggers.map((t, i) => (
                      <tr key={i}>
                        <td style={s.tds}><span style={{ fontSize:13 }}>{t.rule_name}</span></td>
                        <td style={s.td}><span style={{ fontSize:11, fontWeight:700, color:AUTO_CAT_COLORS[t.category]||T.textSub }}>{t.category?.replace(/_/g,' ')}</span></td>
                        <td style={s.td}><AutoSeverityPill severity={t.severity}/></td>
                        <td style={{ ...s.td, maxWidth:240 }}><span style={{ fontSize:12, color:T.textSub, display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.detail}</span></td>
                        <td style={{ ...s.td, fontSize:12, color:T.textFaint, whiteSpace:'nowrap' }}>{fmtAgo(t.fired_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
          </TableCard>
        </>
      )}
    </>
  );
}

// -- AUTOMATION INCIDENTS ------------------------------------------------------
function SecAutomationIncidents({ api }) {
  const [data, setData] = useState(null); const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [actionMsg, setActionMsg] = useState(''); const [actionErr, setActionErr] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulk, setShowBulk] = useState(false);
  const { hasPermission } = useStaffAuth();

  const load = useCallback(() => {
    setData(null);
    fetch(`${api}/v1/staff/automation/incidents`, { credentials:'include' })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => { if (ok) setData(d); else setErr(d.error||'Failed'); })
      .catch(() => setErr('Network error'));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const acknowledge = (incident, note) => {
    fetch(`${api}/v1/staff/automation/incidents/${incident.id}/acknowledge`, {
      method:'POST', credentials:'include',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify({ note }),
    }).then(r => r.json().then(d => ({ ok:r.ok, d })))
    .then(({ ok, d }) => {
      if (ok) { setActionMsg('Incident acknowledged.'); load(); }
      else setActionErr(d.error||'Failed');
    }).catch(() => setActionErr('Network error'));
  };

  return (
    <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
      {showBulk&&<BulkActionPanel api={api} entityType="incident" entityIds={[...selectedIds]} onClose={()=>setShowBulk(false)} hasPermission={hasPermission}/>}
      {modal && (
        <AutoActionModal
          title="Acknowledge Incident"
          body={`Acknowledge incident: ${modal.title}? This will be audit logged.`}
          onCancel={() => setModal(null)}
          onConfirm={(note) => { setModal(null); acknowledge(modal, note); }}
        />
      )}
      <div style={{ flex:1, minWidth:0 }}>
        {actionMsg && <div style={{ padding:'8px 12px', background:T.greenBg, border:`1px solid ${T.greenBorder}`, borderRadius:5, fontSize:13, color:T.green, marginBottom:8 }}>{actionMsg}</div>}
        {actionErr && <div style={{ ...s.error, marginBottom:8 }}>! {actionErr}</div>}
        {err && <div style={s.error}>! {err}</div>}
        {!data ? <Loader/> : (
          <>
            <div style={{ display:'flex', gap:8, marginBottom:10 }}>
              <AutoKPI label="Total Incidents" val={data.total}    color={data.total>0?'red':'green'}/>
              <AutoKPI label="Critical"        val={data.critical} color={data.critical>0?'red':'green'}/>
              <AutoKPI label="High"            val={data.high}     color={data.high>0?'amber':'green'}/>
            </div>
            {selectedIds.size>0&&(
              <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:T.accentLight,border:`1px solid ${T.accentBorder}`,borderRadius:6,marginBottom:8,fontSize:13}}>
                <span style={{color:T.accent,fontWeight:700}}>{selectedIds.size} incident{selectedIds.size!==1?'s':''} selected</span>
                {hasPermission('bulk.execute')&&<button style={{...s.btn('primary'),fontSize:12,padding:'4px 12px'}} onClick={()=>setShowBulk(true)}>Bulk Action</button>}
                <button style={{...s.btn(),fontSize:12,padding:'4px 10px'}} onClick={()=>setSelectedIds(new Set())}>Clear</button>
              </div>
            )}
            <TableCard title="Active Incidents" count={data.total}>
              {(data.incidents || []).length === 0
                ? <div style={{ ...s.td, ...s.empty }}>No active incidents. All systems nominal.</div>
                : <table style={s.table}>
                    <thead><tr>
                      <th style={{...s.th,width:32}}><input type="checkbox" checked={(data.incidents||[]).length>0&&(data.incidents||[]).every(i=>selectedIds.has(i.id))} onChange={()=>{const ids=(data.incidents||[]).map(i=>i.id);const all=ids.every(id=>selectedIds.has(id));setSelectedIds(all?new Set():new Set(ids));}} style={{cursor:'pointer'}}/></th>
                      {['Severity','Category','Incident','Affected Systems','Detected','Action'].map(h => <th key={h} style={s.th}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {data.incidents.map((inc, i) => (
                        <tr key={inc.id || i}
                          style={{ cursor:'pointer', background: selectedIds.has(inc.id)?T.accentLight:selected?.id === inc.id ? T.accentLight : inc.severity==='critical' ? T.redBg : 'transparent' }}
                          onClick={() => setSelected(selected?.id === inc.id ? null : inc)}
                          onMouseEnter={e => { if(!selectedIds.has(inc.id)&&selected?.id!==inc.id) e.currentTarget.style.background = inc.severity==='critical' ? T.redBg : T.borderSoft; }}
                          onMouseLeave={e => e.currentTarget.style.background = selectedIds.has(inc.id)?T.accentLight:selected?.id===inc.id?T.accentLight:inc.severity==='critical'?T.redBg:'transparent'}>
                          <td style={s.td} onClick={e=>{e.stopPropagation();const n=new Set(selectedIds);n.has(inc.id)?n.delete(inc.id):n.add(inc.id);setSelectedIds(n);}}><input type="checkbox" checked={selectedIds.has(inc.id)} onChange={()=>{}} style={{cursor:'pointer'}}/></td>
                          <td style={s.td}><AutoSeverityPill severity={inc.severity}/></td>
                          <td style={s.td}><span style={{ fontSize:11, fontWeight:700, color:AUTO_CAT_COLORS[inc.category]||T.textSub }}>{inc.category?.replace(/_/g,' ')}</span></td>
                          <td style={s.tds}>
                            <div style={{ fontSize:13, fontWeight:700, color:T.text, display:'flex', alignItems:'center', gap:6 }}>
                              <EntityChip type="incident" id={inc.id} label={inc.title.slice(0,20)}/>
                            </div>
                            <div style={{ fontSize:12, color:T.textSub }}>{inc.detail}</div>
                          </td>
                          <td style={s.td}><span style={{ fontSize:12, color:T.textFaint }}>{(inc.affected_systems||[]).join(', ')}</span></td>
                          <td style={{ ...s.td, fontSize:12, color:T.textFaint, whiteSpace:'nowrap' }}>{fmtAgo(inc.detected_at)}</td>
                          <td style={s.td}>
                            <button style={{ ...s.btn(), fontSize:12, padding:'3px 8px' }}
                              onClick={e => { e.stopPropagation(); setModal(inc); }}>
                              Acknowledge
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>}
            </TableCard>
          </>
        )}
        <div style={{ textAlign:'right', marginTop:6 }}><button style={s.btn()} onClick={load}>Refresh</button></div>
      </div>

      {/* Incident detail panel */}
      {selected && (
        <div style={{ width:340, flexShrink:0 }}>
          <div style={{ ...s.panel, position:'sticky', top:20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
              <div style={{ fontSize:13, fontWeight:700, color:T.text, flex:1 }}>Incident Detail</div>
              <button onClick={() => setSelected(null)} style={{ background:'none', border:'none', color:T.textFaint, cursor:'pointer', fontSize:18 }}>×</button>
            </div>
            <AutoSeverityPill severity={selected.severity}/>
            {[
              { label:'Title',    val:selected.title },
              { label:'Category', val:selected.category?.replace(/_/g,' ') },
              { label:'Detail',   val:selected.detail },
              { label:'Detected by', val:selected.detected_by },
              { label:'Detected', val:fmtDate(selected.detected_at) },
              { label:'Affected', val:(selected.affected_systems||[]).join(', ') },
            ].map(({ label, val }) => (
              <div key={label} style={{ ...s.kvRow, marginTop:6 }}>
                <span style={s.kvLabel}>{label}</span>
                <span style={{ ...s.kvValue, fontSize:13, maxWidth:180, textAlign:'right' }}>{val}</span>
              </div>
            ))}
            <div style={{ marginTop:10, padding:'10px', background:T.amberBg, border:`1px solid ${T.amberBorder}`, borderRadius:5 }}>
              <div style={{ fontSize:11, fontWeight:700, color:T.amber, marginBottom:4 }}>Suggested Response</div>
              <div style={{ fontSize:12, color:T.textMid, lineHeight:1.5 }}>{selected.suggested_response}</div>
            </div>
            <button style={{ ...s.btn('primary'), marginTop:10, width:'100%', justifyContent:'center' }}
              onClick={() => setModal(selected)}>
              Acknowledge
            </button>
            {/* Lifecycle panel — extract rule_id from incident id */}
            {(() => {
              const parts = (selected.id||'').split('_');
              const ruleId = parts.length >= 3 && parts[0] === 'inc' ? parts.slice(1,-1).join('_') : (selected.rule_id||'');
              return ruleId ? (
                <div style={{ marginTop:12 }}>
                  <IncidentLifecyclePanel
                    api={api}
                    ruleId={ruleId}
                    incidentTitle={selected.title}
                    incidentSeverity={selected.severity}
                    incidentCategory={selected.category}
                    onUpdate={load}
                  />
                </div>
              ) : null;
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// -- AUTOMATION SUGGESTIONS ---------------------------------------------------
function SecAutomationSuggestions({ api }) {
  const [data, setData] = useState(null); const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());
  const [actionMsg, setActionMsg] = useState('');

  const load = useCallback(() => {
    setData(null);
    fetch(`${api}/v1/staff/automation/suggestions`, { credentials:'include' })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => { if (ok) setData(d); else setErr(d.error||'Failed'); })
      .catch(() => setErr('Network error'));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const dismiss = (sug, note) => {
    fetch(`${api}/v1/staff/automation/suggestions/${sug.id}/dismiss`, {
      method:'POST', credentials:'include',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify({ note }),
    }).then(r => r.json().then(d => ({ ok:r.ok, d })))
    .then(({ ok, d }) => {
      if (ok) {
        setDismissed(prev => new Set([...prev, sug.id]));
        setActionMsg('Suggestion dismissed and logged.');
      }
    }).catch(() => {});
  };

  const visible = (data?.suggestions || []).filter(s => !dismissed.has(s.id));

  return (
    <>
      {modal && (
        <AutoActionModal
          title="Dismiss Suggestion"
          body={`Dismiss: ${modal.title}? This will be audit logged.`}
          onCancel={() => setModal(null)}
          onConfirm={(note) => { setModal(null); dismiss(modal, note); }}
        />
      )}
      {actionMsg && <div style={{ padding:'8px 12px', background:T.greenBg, border:`1px solid ${T.greenBorder}`, borderRadius:5, fontSize:13, color:T.green, marginBottom:8 }}>{actionMsg}</div>}
      {err && <div style={s.error}>! {err}</div>}
      {!data ? <Loader/> : visible.length === 0 ? (
        <div style={{ ...s.panel, ...s.empty }}>No active suggestions. All systems nominal.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {visible.map(sug => {
            const sc = AUTO_SEVERITY_CONFIG[sug.severity] || AUTO_SEVERITY_CONFIG.low;
            return (
              <div key={sug.id} style={{ ...s.panel, padding:'14px 16px', borderLeft:`4px solid ${sc.dot}` }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                      <AutoSeverityPill severity={sug.severity}/>
                      <span style={{ fontSize:11, fontWeight:700, color:AUTO_CAT_COLORS[sug.category]||T.textSub }}>{sug.category?.replace(/_/g,' ')}</span>
                      <span style={{ fontSize:11, color:T.textFaint }}>Confidence: {sug.confidence?.toUpperCase()}</span>
                      <span style={{ fontSize:11, color:T.textFaint }}>{fmtAgo(sug.generated_at)}</span>
                    </div>
                    <div style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:4 }}>{sug.title}</div>
                    <div style={{ fontSize:13, color:T.textSub, marginBottom:8, lineHeight:1.6 }}>{sug.reasoning}</div>
                    <div style={{ padding:'8px 10px', background:T.amberBg, border:`1px solid ${T.amberBorder}`, borderRadius:5 }}>
                      <span style={{ fontSize:12, fontWeight:700, color:T.amber }}>Recommended: </span>
                      <span style={{ fontSize:12, color:T.textMid }}>{sug.action}</span>
                    </div>
                  </div>
                  <button style={{ ...s.btn(), fontSize:12, padding:'4px 10px', flexShrink:0 }}
                    onClick={() => setModal(sug)}>Dismiss</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ textAlign:'right', marginTop:8 }}><button style={s.btn()} onClick={load}>Refresh</button></div>
    </>
  );
}

// -- AUTOMATION HISTORY -------------------------------------------------------
function SecAutomationHistory({ api }) {
  const [data, setData] = useState(null); const [err, setErr] = useState(null);
  const [page, setPage] = useState(1);
  const [hours, setHours] = useState(168);
  const [sevFilter, setSevFilter] = useState('');

  const load = useCallback(() => {
    setData(null);
    const p = new URLSearchParams({ page, per_page:50, hours });
    if (sevFilter) p.set('severity', sevFilter);
    fetch(`${api}/v1/staff/automation/history?${p}`, { credentials:'include' })
      .then(r => r.json().then(d => ({ ok:r.ok, d })))
      .then(({ ok, d }) => { if (ok) setData(d); else setErr(d.error||'Failed'); })
      .catch(() => setErr('Network error'));
  }, [api, page, hours, sevFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div style={s.filterRow}>
        <select style={s.select} value={sevFilter} onChange={e => { setSevFilter(e.target.value); setPage(1); }}>
          <option value="">All Severities</option>
          {['critical','high','medium','low'].map(sv => <option key={sv} value={sv}>{sv}</option>)}
        </select>
        <select style={s.select} value={hours} onChange={e => { setHours(Number(e.target.value)); setPage(1); }}>
          {[24,48,168,720].map(h => <option key={h} value={h}>Last {h}h</option>)}
        </select>
        <button style={s.btn('primary')} onClick={() => { setPage(1); load(); }}>Apply</button>
        <button style={s.btn()} onClick={load}>Refresh</button>
      </div>
      {err && <div style={s.error}>! {err}</div>}
      {!data ? <Loader/> : (
        <TableCard title="Automation History" count={data.total}>
          <table style={s.table}>
            <thead><tr>
              {['Action','Severity','Rule','Detail','Actor','Time'].map(h => <th key={h} style={s.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {(data.entries || []).length === 0
                ? <tr><td colSpan={6} style={{ ...s.td, ...s.empty }}>No automation history in this window.</td></tr>
                : (data.entries || []).map((e, i) => (
                  <tr key={e.id || i}>
                    <td style={s.tds}><code style={{ fontFamily:'monospace', fontSize:12, color:T.accent }}>{e.action}</code></td>
                    <td style={s.td}><AutoSeverityPill severity={e.severity || 'low'}/></td>
                    <td style={s.td}><span style={{ fontSize:12, color:T.textSub }}>{e.rule_name || '-'}</span></td>
                    <td style={{ ...s.td, maxWidth:240 }}><span style={{ fontSize:12, color:T.textSub, display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.detail || '-'}</span></td>
                    <td style={s.td}><span style={{ fontSize:13 }}>{e.actor || '-'}</span></td>
                    <td style={{ ...s.td, fontSize:12, color:T.textFaint, whiteSpace:'nowrap' }}>{fmtAgo(e.created_at)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          {data.pages > 1 && <Pager page={page} pages={data.pages} total={data.total} onPage={setPage}/>}
        </TableCard>
      )}
    </>
  );
}

// ============================================================================
// END AUTOMATION FOUNDATION
// ============================================================================

// ============================================================================
// PLATFORM OPERATIONS CENTER
// ============================================================================

const JOB_STATUS_CONFIG = {
  queued:     {bg:'#ebf8ff',color:'#2b6cb0',border:'#bee3f8',dot:'#3182ce'},
  processing: {bg:'#faf5ff',color:'#553c9a',border:'#d6bcfa',dot:'#805ad5'},
  completed:  {bg:'#f0fff4',color:'#276749',border:'#9ae6b4',dot:'#38a169'},
  failed:     {bg:'#fff5f5',color:'#c53030',border:'#feb2b2',dot:'#e53e3e'},
  stuck:      {bg:'#fffaf0',color:'#b7791f',border:'#fbd38d',dot:'#d69e2e'},
  retrying:   {bg:'#faf5ff',color:'#553c9a',border:'#d6bcfa',dot:'#805ad5'},
  cancelled:  {bg:T.bg,    color:T.textSub, border:T.border, dot:T.textFaint},
};

const FAILURE_SEVERITY_CONFIG = {
  critical:{color:T.red,    bg:T.redBg,      border:T.redBorder},
  high:    {color:'#c05621',bg:'#fffaf0',     border:'#fbd38d'},
  medium:  {color:T.amber,  bg:T.amberBg,    border:T.amberBorder},
  low:     {color:T.accent, bg:T.accentLight, border:T.accentBorder},
};

const PROVIDER_STATUS = {
  operational: {color:'#276749',bg:'#f0fff4',border:'#9ae6b4',dot:'#38a169'},
  degraded:    {color:'#b7791f',bg:'#fffaf0',border:'#fbd38d',dot:'#d69e2e'},
  outage:      {color:'#c53030',bg:'#fff5f5',border:'#feb2b2',dot:'#e53e3e'},
  maintenance: {color:'#553c9a',bg:'#faf5ff',border:'#d6bcfa',dot:'#805ad5'},
};

function JobStatusPill({status}){
  const k=(status||'queued').toLowerCase();
  const c=JOB_STATUS_CONFIG[k]||JOB_STATUS_CONFIG.queued;
  return(
    <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:5,minHeight:32,boxSizing:'border-box',padding:'0 12px',borderRadius:6,fontSize:13,fontWeight:500,whiteSpace:'nowrap',background:c.bg,color:c.color,border:`1px solid ${c.border}`}}>
      <span style={{width:5,height:5,borderRadius:'50%',background:c.dot,flexShrink:0}}/>
      {k.toUpperCase()}
    </span>
  );
}

function ProviderStatusPill({status}){
  const k=(status||'operational').toLowerCase();
  const c=PROVIDER_STATUS[k]||PROVIDER_STATUS.operational;
  return(
    <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:5,minHeight:32,boxSizing:'border-box',padding:'0 12px',borderRadius:6,fontSize:13,fontWeight:500,whiteSpace:'nowrap',background:c.bg,color:c.color,border:`1px solid ${c.border}`}}>
      <span style={{width:5,height:5,borderRadius:'50%',background:c.dot,flexShrink:0}}/>
      {k.toUpperCase()}
    </span>
  );
}

function PlatformKPI({label,val,color,sub,onClick}){
  const borderColor=color==='red'?T.red:color==='green'?T.green:color==='amber'?T.amber:color==='purple'?T.purple:T.accent;
  return(
    <div onClick={onClick} style={{background:T.surface,border:`1px solid ${T.border}`,borderTop:`3px solid ${borderColor}`,borderRadius:6,padding:'10px 12px',flex:1,minWidth:100,cursor:onClick?'pointer':'default'}}>
      <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:5}}>{label}</div>
      <div style={{fontSize:28,fontWeight:800,color:T.text,lineHeight:1,letterSpacing:'-0.02em'}}>{val??'-'}</div>
      {sub&&<div style={{fontSize:11,color:T.textFaint,marginTop:4}}>{sub}</div>}
    </div>
  );
}

// Confirm action modal
function ConfirmModal({title,body,onConfirm,onCancel,confirmLabel,danger}){
  const[note,setNote]=useState('');
  return(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:'24px',width:440,maxWidth:'90vw',boxShadow:'0 8px 32px rgba(0,0,0,0.4)'}}>
        <div style={{fontSize:16,fontWeight:700,color:danger?T.red:T.text,marginBottom:8}}>{title}</div>
        <div style={{fontSize:14,color:T.textSub,marginBottom:14,lineHeight:1.6}}>{body}</div>
        <textarea style={{...s.input,width:'100%',boxSizing:'border-box',minHeight:60,resize:'vertical',fontFamily:'inherit',fontSize:13,marginBottom:12}}
          placeholder="Operator note (optional)..." value={note} onChange={e=>setNote(e.target.value)}/>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button style={s.btn()} onClick={onCancel}>Cancel</button>
          <button style={{...s.btn(danger?'danger':'primary')}} onClick={()=>onConfirm(note)}>{confirmLabel||'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}

// Job detail inspector panel
function JobDetailPanel({api,jobId,onClose,hasPermission}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[confirm,setConfirm]=useState(null);
  const[actionMsg,setActionMsg]=useState('');const[actionErr,setActionErr]=useState('');
  const[submitting,setSubmitting]=useState(false);

  const load=useCallback(()=>{
    setData(null);
    fetch(`${api}/v1/staff/platform/jobs/${jobId}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api,jobId]);
  useEffect(()=>{load();},[load]);

  const doAction=(url,body,msg)=>{
    setSubmitting(true);setActionMsg('');setActionErr('');
    fetch(`${api}${url}`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok){setActionMsg(d.message||msg);load();}else setActionErr(d.error||'Action failed');})
      .catch(()=>setActionErr('Network error'))
      .finally(()=>setSubmitting(false));
  };

  if(!data&&!err)return(
    <div style={{width:420,flexShrink:0,position:'sticky',top:20}}>
      <div style={{...s.panel,padding:'14px 16px'}}><Loader/></div>
    </div>
  );

  const j=data&&data.job;
  const canControl=hasPermission('platform.control');
  const canRetry=hasPermission('platform.retry');

  return(
    <>
      {confirm&&(
        <ConfirmModal
          title={confirm.title}
          body={confirm.body}
          danger={confirm.danger}
          confirmLabel={confirm.label}
          onCancel={()=>setConfirm(null)}
          onConfirm={(note)=>{
            setConfirm(null);
            doAction(confirm.url,{...confirm.body_extra,note},confirm.label);
          }}
        />
      )}
      <div style={{width:380,flexShrink:0}}>
        <div style={{...s.panel,position:'sticky',top:20,maxHeight:'calc(100vh - 120px)',overflowY:'auto'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
            <div style={{fontSize:14,fontWeight:700,color:T.text,flex:1}}>Job Inspector</div>
            <button onClick={onClose} style={{background:'none',border:'none',color:T.textFaint,cursor:'pointer',fontSize:18,lineHeight:1}}>×</button>
          </div>
          {err&&<div style={s.error}>! {err}</div>}
          {actionMsg&&<div style={{padding:'7px 10px',background:T.greenBg,border:`1px solid ${T.greenBorder}`,borderRadius:5,fontSize:13,color:T.green,marginBottom:8}}>{actionMsg}</div>}
          {actionErr&&<div style={{...s.error,marginBottom:8}}>! {actionErr}</div>}
          {j&&(
            <>
              <div style={{marginBottom:10}}><JobStatusPill status={j.status}/></div>
              {[
                {label:'Job ID',    val:<code style={{fontFamily:'monospace',fontSize:10,wordBreak:'break-all'}}>{j.job_id}</code>},
                {label:'User',      val:<span style={{fontFamily:'monospace',fontSize:11}}>{j.user_email||j.user_id}</span>},
                {label:'Generator', val:j.generator_slug},
                {label:'Domain',    val:j.domain},
                {label:'Provider',  val:<code style={{fontFamily:'monospace',fontSize:12}}>{j.provider||'-'}</code>},
                {label:'Model',     val:<code style={{fontFamily:'monospace',fontSize:11}}>{j.model||'-'}</code>},
                {label:'Attempts',  val:<span style={{fontWeight:700,color:j.attempts>2?T.red:j.attempts>1?T.amber:T.text}}>{j.attempts}</span>},
                {label:'Images',    val:j.image_count},
                {label:'Credits',   val:j.credit_deducted?<span style={{color:T.amber}}>Deducted</span>:'Not deducted'},
                {label:'Duration',  val:j.duration_ms?`${(j.duration_ms/1000).toFixed(1)}s`:'-'},
                {label:'Queue Delay',val:j.queue_delay_ms?`${j.queue_delay_ms}ms`:'-'},
                {label:'Worker',    val:<code style={{fontFamily:'monospace',fontSize:11}}>{j.worker_id||'-'}</code>},
                {label:'Created',   val:fmtDate(j.created_at)},
                {label:'Started',   val:fmtDate(j.started_at)},
                {label:'Completed', val:fmtDate(j.completed_at)},
              ].map(({label,val})=>(
                <div key={label} style={s.kvRow}>
                  <span style={s.kvLabel}>{label}</span>
                  <span style={{...s.kvValue,fontSize:13,maxWidth:200,textAlign:'right'}}>{val}</span>
                </div>
              ))}
              {j.error_code&&(
                <div style={{marginTop:10,padding:'8px 10px',background:T.redBg,border:`1px solid ${T.redBorder}`,borderRadius:5}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.red,marginBottom:3}}>Error: {j.error_code}</div>
                  {j.error_detail&&<div style={{fontSize:12,color:T.red,fontFamily:'monospace',wordBreak:'break-all'}}>{j.error_detail.slice(0,400)}</div>}
                </div>
              )}
              {/* Operator actions */}
              <div style={{marginTop:12,display:'flex',gap:6,flexWrap:'wrap'}}>
                <button style={{...s.btn(),fontSize:12,padding:'4px 10px'}}
                  onClick={()=>doAction(`/v1/staff/platform/jobs/${j.job_id}/acknowledge`,{},'Acknowledged')}
                  disabled={submitting}>
                  Acknowledge
                </button>
                {canRetry&&['failed','cancelled','stuck'].includes(j.status)&&(
                  <button style={{...s.btn('primary'),fontSize:12,padding:'4px 10px'}}
                    onClick={()=>setConfirm({title:'Requeue Job',body:`Requeue job ${j.job_id.slice(0,16)}? It will re-enter the generation queue.`,label:'Requeue',url:`/v1/staff/platform/jobs/${j.job_id}/requeue`,body_extra:{}})}
                    disabled={submitting}>
                    Requeue
                  </button>
                )}
                {canControl&&['queued','processing','stuck'].includes(j.status)&&(
                  <button style={{...s.btn(),fontSize:12,padding:'4px 10px',color:T.red,borderColor:T.redBorder}}
                    onClick={()=>setConfirm({title:'Cancel Job',body:`Cancel job ${j.job_id.slice(0,16)}? This will stop processing.`,label:'Cancel',danger:true,url:`/v1/staff/platform/jobs/${j.job_id}/cancel`,body_extra:{}})}
                    disabled={submitting}>
                    Cancel
                  </button>
                )}
              </div>
              {/* Audit trail */}
              {data.audit_trail&&data.audit_trail.length>0&&(
                <div style={{marginTop:14}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:6}}>Audit Trail</div>
                  {data.audit_trail.map((a,i)=>(
                    <div key={i} style={{padding:'6px 0',borderBottom:`1px solid ${T.borderSoft}`,fontSize:12}}>
                      <div style={{fontWeight:600,color:T.textMid}}>{a.action}</div>
                      <div style={{color:T.textFaint,fontSize:11}}>{fmtDate(a.created_at)} &middot; {a.note?.slice(0,80)}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// -- PLATFORM OVERVIEW --------------------------------------------------------
function SecPlatformOverview({api, liveData, feedPaused, onToggleFeed}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const load=useCallback(()=>{
    setData(null);
    fetch(`${api}/v1/staff/platform/overview`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api]);
  useEffect(()=>{load();},[load]);

  // Merge live snapshot over static data - smooth live updates
  const live=liveData&&liveData.platform;
  const queued     = live ? live.queued     : data&&data.queued_count;
  const processing = live ? live.processing : data&&data.processing_count;
  const stuck      = live ? live.stuck      : data&&data.stuck_count;
  const failedToday= live ? live.failed_today : data&&data.failed_today;
  const successRate= live ? live.success_rate : data&&data.success_rate;
  const provFailRate= live ? live.prov_fail_rate : data&&data.provider_failure_rate;
  const queueDepth = Math.max(0, (queued||0) + (processing||0) - (stuck||0));

  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  // System severity

  // System severity using live values
  const severity=(stuck||0)>5||(provFailRate||0)>15?'CRITICAL'
    :(stuck||0)>0||(provFailRate||0)>5||(successRate||100)<90?'WARNING'
    :(successRate||100)<98?'DEGRADED':'HEALTHY';
  const sevColor={HEALTHY:T.green,DEGRADED:T.amber,WARNING:'#c05621',CRITICAL:T.red}[severity];
  const sevBg={HEALTHY:T.greenBg,DEGRADED:T.amberBg,WARNING:'#fffaf0',CRITICAL:T.redBg}[severity];
  const sevBorder={HEALTHY:T.greenBorder,DEGRADED:T.amberBorder,WARNING:'#fbd38d',CRITICAL:T.redBorder}[severity];

  return(
    <>
      {/* System severity strip */}
      <div style={{padding:'10px 16px',background:sevBg,border:`1px solid ${sevBorder}`,borderRadius:7,marginBottom:12,display:'flex',alignItems:'center',gap:12}}>
        <span style={{width:10,height:10,borderRadius:'50%',background:sevColor,flexShrink:0}}/>
        <span style={{fontSize:14,fontWeight:800,color:sevColor,letterSpacing:'0.1em'}}>{severity}</span>
        <span style={{fontSize:13,color:sevColor,opacity:0.85}}>
          {severity==='HEALTHY'?'All systems operational.'
            :severity==='DEGRADED'?`Success rate ${successRate||0}% — monitor closely.`
            :severity==='WARNING'?`${stuck||0} stuck jobs | Provider fail rate ${provFailRate||0}%`
            :`CRITICAL: ${stuck||0} stuck jobs | ${failedToday||0} failures | Provider fail ${provFailRate||0}%`}
        </span>
        <div style={{marginLeft:'auto',fontSize:12,color:sevColor,opacity:0.7}}>{new Date().toISOString().slice(0,19).replace('T',' ')} UTC</div>
      </div>

      {/* Primary KPI row - live values */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:10}}>
        <PlatformKPI label="Active Jobs"       val={fmt((queued||0)+(processing||0))} color={(queueDepth||0)>20?'amber':'green'} sub="Queued + Processing"/>
        <PlatformKPI label="Queue Depth"       val={fmt(queueDepth)}     color={(queueDepth||0)>30?'red':(queueDepth||0)>10?'amber':'green'}/>
        <PlatformKPI label="Failed 24h"        val={fmt(failedToday)}    color={(failedToday||0)>50?'red':(failedToday||0)>10?'amber':'green'}/>
        <PlatformKPI label="Success Rate"      val={`${successRate||0}%`} color={(successRate||100)<90?'red':(successRate||100)<98?'amber':'green'}/>
        <PlatformKPI label="Avg Latency"       val={data.avg_latency_ms?`${data.avg_latency_ms}ms`:'-'} color="blue"/>
        <PlatformKPI label="Stuck Jobs"        val={fmt(stuck)}          color={(stuck||0)>0?'red':'green'}/>
        <PlatformKPI label="Active Providers"  val={fmt(data.active_providers)} color="blue"/>
        <PlatformKPI label="Provider Fail%"    val={`${provFailRate||0}%`} color={(provFailRate||0)>5?'red':'green'} sub="Today"/>
      </div>

      {/* Secondary KPI row */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
        <PlatformKPI label="Credits Burned 24h"  val={fmt(data.credits_consumed_today)} color="purple"/>
        <PlatformKPI label="Retry Rate"          val={`${data.retry_rate}%`}            color={data.retry_rate>10?'amber':'green'} sub="Attempts > 1"/>
        <PlatformKPI label="Animation Today"     val={fmt(data.anim_today)}             color="blue" sub={`${data.anim_failed||0} failed`}/>
        <PlatformKPI label="Jobs Today"          val={fmt(data.jobs_today)}             color="blue"/>
        <PlatformKPI label="Completed Today"     val={fmt(data.completed_today)}        color="green"/>
      </div>

      {/* Live job stream */}
      <LiveJobStream jobs={liveData&&liveData.jobs} paused={feedPaused} onTogglePause={onToggleFeed}/>

      {/* Provider breakdown */}
      {data.providers&&data.providers.length>0&&(
        <TableCard title="Provider Status" count={data.providers.length}>
          <table style={s.table}>
            <thead><tr>
              {['Provider','Status','Total','Failed','Fail%'].map(h=><th key={h} style={s.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {data.providers.map(p=>{
                const failPct=p.total>0?Math.round(p.failed/p.total*100):0;
                const status=failPct>15?'outage':failPct>5?'degraded':'operational';
                return(
                  <tr key={p.provider}>
                    <td style={s.tds}><code style={{fontFamily:'monospace',fontSize:13}}>{p.provider}</code></td>
                    <td style={s.td}><ProviderStatusPill status={status}/></td>
                    <td style={s.tds}>{fmt(p.total)}</td>
                    <td style={s.td}><span style={{color:p.failed>0?T.red:T.green,fontWeight:700}}>{fmt(p.failed)}</span></td>
                    <td style={s.td}><span style={{fontWeight:700,color:failPct>5?T.red:T.green}}>{failPct}%</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableCard>
      )}
      <div style={{textAlign:'right',marginTop:8}}>
        <button style={s.btn()} onClick={load}>Refresh</button>
      </div>
    </>
  );
}

// -- PLATFORM JOBS ------------------------------------------------------------
function SecPlatformJobs({api}){
  const{hasPermission}=useStaffAuth();
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[page,setPage]=useState(1);
  const[status,setStatus]=useState('');
  const[provider,setProvider]=useState('');
  const[hours,setHours]=useState(24);
  const[stuck,setStuck]=useState(false);
  const[search,setSearch]=useState('');
  const[selectedJob,setSelectedJob]=useState(null);
  const[selectedIds,setSelectedIds]=useState(new Set());
  const[showBulk,setShowBulk]=useState(false);

  const load=useCallback(()=>{
    setData(null);
    const p=new URLSearchParams({page,per_page:50,hours});
    if(status)p.set('status',status);
    if(provider)p.set('provider',provider);
    if(stuck)p.set('stuck','true');
    fetch(`${api}/v1/staff/platform/jobs?${p}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api,page,status,provider,hours,stuck]);
  useEffect(()=>{load();},[load]);

  const jobs=((data&&data.jobs)||[]).filter(j=>!search||j.job_id.includes(search)||j.user_id?.includes(search)||j.generator_slug?.includes(search));
  const allIds=jobs.map(j=>j.job_id);
  const allSelected=allIds.length>0&&allIds.every(id=>selectedIds.has(id));
  const toggleAll=()=>setSelectedIds(allSelected?new Set():new Set(allIds));
  const toggleOne=(id)=>setSelectedIds(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n;});

  return(
    <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
      {showBulk&&<BulkActionPanel api={api} entityType="job" entityIds={[...selectedIds]} onClose={()=>setShowBulk(false)} hasPermission={hasPermission}/>}
      <div style={{flex:1,minWidth:0}}>
        <div style={{...s.filterRow,flexWrap:'wrap'}}>
          <input style={{...s.input,width:180}} placeholder="Job ID / User / Engine..." value={search} onChange={e=>setSearch(e.target.value)}/>
          <select style={s.select} value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
            <option value="">All Statuses</option>
            {['queued','processing','completed','failed','stuck','cancelled'].map(v=><option key={v} value={v}>{v}</option>)}
          </select>
          <input style={{...s.input,width:110}} placeholder="Provider..." value={provider} onChange={e=>{setProvider(e.target.value);setPage(1);}}/>
          <select style={s.select} value={hours} onChange={e=>{setHours(Number(e.target.value));setPage(1);}}>
            {[1,6,24,48,168].map(h=><option key={h} value={h}>Last {h}h</option>)}
          </select>
          <label style={{display:'flex',alignItems:'center',gap:5,fontSize:13,color:T.textSub,cursor:'pointer'}}>
            <input type="checkbox" checked={stuck} onChange={e=>{setStuck(e.target.checked);setPage(1);}} style={{cursor:'pointer'}}/>
            Stuck only
          </label>
          <button style={s.btn('primary')} onClick={()=>{setPage(1);load();}}>Apply</button>
          <button style={s.btn()} onClick={load}>Refresh</button>
        </div>
        {/* Bulk toolbar */}
        {selectedIds.size>0&&(
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:T.accentLight,border:`1px solid ${T.accentBorder}`,borderRadius:6,marginBottom:8,fontSize:13}}>
            <span style={{color:T.accent,fontWeight:700}}>{selectedIds.size} job{selectedIds.size!==1?'s':''} selected</span>
            {hasPermission('bulk.execute')&&<button style={{...s.btn('primary'),fontSize:12,padding:'4px 12px'}} onClick={()=>setShowBulk(true)}>Bulk Action</button>}
            <button style={{...s.btn(),fontSize:12,padding:'4px 10px'}} onClick={()=>setSelectedIds(new Set())}>Clear</button>
          </div>
        )}
        {err&&<div style={s.error}>! {err}</div>}
        {!data?<Loader/>:(
          <TableCard title="Jobs" count={data.total}>
            <div style={{overflowX:'auto'}}>
              <table style={{...s.table,minWidth:960}}>
                <thead><tr>
                  <th style={{...s.th,width:32}}><input type="checkbox" checked={allSelected} onChange={toggleAll} style={{cursor:'pointer'}}/></th>
                  {['Job','User','Engine','Provider','Status','Queue','Runtime','Images','Created','Error'].map(h=><th key={h} style={s.th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {jobs.length===0?<tr><td colSpan={11} style={{...s.td,...s.empty}}>No jobs.</td></tr>
                    :jobs.map(j=>(
                    <tr key={j.job_id}
                      style={{cursor:'pointer',background:selectedIds.has(j.job_id)?T.accentLight:selectedJob===j.job_id?T.accentLight:'transparent'}}
                      onClick={()=>setSelectedJob(j.job_id===selectedJob?null:j.job_id)}
                      onMouseEnter={e=>{ if(!selectedIds.has(j.job_id)&&selectedJob!==j.job_id) e.currentTarget.style.background=T.borderSoft; }}
                      onMouseLeave={e=>e.currentTarget.style.background=selectedIds.has(j.job_id)?T.accentLight:selectedJob===j.job_id?T.accentLight:'transparent'}
                    >
                      <td style={s.td} onClick={e=>e.stopPropagation()}><input type="checkbox" checked={selectedIds.has(j.job_id)} onChange={()=>toggleOne(j.job_id)} style={{cursor:'pointer'}}/></td>
                      <td style={s.td}><EntityChip type="job" id={j.job_id} label={j.job_id.slice(0,10)}/></td>
                      <td style={s.td}><EntityChip type="user" id={j.user_id} label={(j.user_id||'').slice(0,8)}/></td>
                      <td style={s.td}><span style={{fontSize:11,color:T.textMid}}>{(j.generator_slug||'-').split('-').slice(0,2).join('-')}</span></td>
                      <td style={s.td}><code style={{fontFamily:'monospace',fontSize:12}}>{j.provider||'-'}</code></td>
                      <td style={s.td}><JobStatusPill status={j.status}/></td>
                      <td style={s.td}><span style={{fontSize:12,color:T.textSub}}>{j.queue_delay_ms?`${j.queue_delay_ms}ms`:'-'}</span></td>
                      <td style={s.td}><span style={{fontSize:12,color:T.textSub}}>{j.duration_ms?`${(j.duration_ms/1000).toFixed(1)}s`:'-'}</span></td>
                      <td style={s.td}><span style={{fontSize:12,color:T.textFaint}}>{j.image_count||1}</span></td>
                      <td style={{...s.td,fontSize:11,color:T.textFaint,whiteSpace:'nowrap'}}>{fmtAgo(j.created_at)}</td>
                      <td style={s.td}><code style={{fontFamily:'monospace',fontSize:10,color:T.red}}>{j.error_code||''}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.pages>1&&<Pager page={page} pages={data.pages} total={data.total} onPage={setPage}/>}
          </TableCard>
        )}
      </div>
      {selectedJob&&(
        <JobDetailPanel
          api={api}
          jobId={selectedJob}
          onClose={()=>setSelectedJob(null)}
          hasPermission={hasPermission}
        />
      )}
    </div>
  );
}

// -- PLATFORM PROVIDERS -------------------------------------------------------
function SecPlatformProviders({api}){
  const{hasPermission}=useStaffAuth();
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[hours,setHours]=useState(24);
  const[confirm,setConfirm]=useState(null);
  const[actionMsg,setActionMsg]=useState('');const[actionErr,setActionErr]=useState('');

  const load=useCallback(()=>{
    setData(null);
    fetch(`${api}/v1/staff/platform/providers?hours=${hours}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api,hours]);
  useEffect(()=>{load();},[load]);

  const doControl=(provider,action,note)=>{
    fetch(`${api}/v1/staff/platform/providers/control`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({provider,action,note})})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setActionMsg(d.message);else setActionErr(d.error||'Failed');})
      .catch(()=>setActionErr('Network error'));
  };

  const canControl=hasPermission('platform.control');

  return(
    <>
      {confirm&&<ConfirmModal title={confirm.title} body={confirm.body} danger={confirm.danger} confirmLabel={confirm.label} onCancel={()=>setConfirm(null)} onConfirm={(note)=>{setConfirm(null);doControl(confirm.provider,confirm.action,note);}}/>}
      {actionMsg&&<div style={{padding:'8px 12px',background:T.greenBg,border:`1px solid ${T.greenBorder}`,borderRadius:6,fontSize:13,color:T.green,marginBottom:8}}>{actionMsg}</div>}
      {actionErr&&<div style={{...s.error,marginBottom:8}}>! {actionErr}</div>}
      <div style={s.filterRow}>
        <select style={s.select} value={hours} onChange={e=>setHours(Number(e.target.value))}>
          {[6,24,48,168].map(h=><option key={h} value={h}>Last {h}h</option>)}
        </select>
        <button style={s.btn('primary')} onClick={load}>Refresh</button>
      </div>
      {err&&<div style={s.error}>! {err}</div>}
      {!data?<Loader/>:(
        <>
          <TableCard title="Provider Telemetry" count={data.providers&&data.providers.length}>
            <table style={{...s.table,minWidth:900}}>
              <thead><tr>
                {['Provider','Status','Total','Success','Failed','Fail%','Avg Latency','Timeouts','Cost USD','Last Fail','Controls'].map(h=><th key={h} style={s.th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {(data.providers||[]).length===0?<tr><td colSpan={11} style={{...s.td,...s.empty}}>No provider data.</td></tr>
                  :(data.providers||[]).map(p=>{
                  const failPct=p.total_jobs>0?Math.round(p.failed_jobs/p.total_jobs*100):0;
                  const status=failPct>15?'outage':failPct>5?'degraded':'operational';
                  return(
                    <tr key={p.provider}>
                      <td style={s.tds}><code style={{fontFamily:'monospace',fontSize:13}}>{p.provider}</code></td>
                      <td style={s.td}><ProviderStatusPill status={status}/></td>
                      <td style={s.tds}>{fmt(p.total_jobs)}</td>
                      <td style={s.td}><span style={{color:T.green,fontWeight:700}}>{fmt(p.completed_jobs)}</span></td>
                      <td style={s.td}><span style={{color:p.failed_jobs>0?T.red:T.green,fontWeight:700}}>{fmt(p.failed_jobs)}</span></td>
                      <td style={s.td}><span style={{fontWeight:700,color:failPct>5?T.red:T.green}}>{failPct}%</span></td>
                      <td style={s.td}><span style={{fontSize:12,color:T.textSub}}>{p.avg_latency_ms?`${p.avg_latency_ms}ms`:'-'}</span></td>
                      <td style={s.td}><span style={{fontWeight:700,color:p.timeout_count>0?T.red:T.green}}>{p.timeout_count}</span></td>
                      <td style={s.td}><span style={{fontSize:12,color:T.textSub}}>${p.cost_usd??'0.0000'}</span></td>
                      <td style={{...s.td,fontSize:11,color:p.last_failure?T.red:T.textFaint}}>{p.last_failure?fmtAgo(p.last_failure):'-'}</td>
                      <td style={s.td}>
                        {canControl?(
                          <div style={{display:'flex',gap:4}}>
                            <button style={{...s.btn(),fontSize:11,padding:'2px 7px'}} onClick={()=>setConfirm({title:`Maintenance Mode`,body:`Set ${p.provider} to maintenance mode? New jobs will not route to this provider until cleared.`,label:'Set Maintenance',provider:p.provider,action:'maintenance'})}>Maint</button>
                            <button style={{...s.btn(),fontSize:11,padding:'2px 7px',color:T.red,borderColor:T.redBorder}} onClick={()=>setConfirm({title:`Disable Provider`,body:`Disable ${p.provider}? This decision will be audit logged and requires a pipeline deploy to take effect.`,label:'Disable',danger:true,provider:p.provider,action:'disable'})}>Disable</button>
                          </div>
                        ):<span style={{fontSize:11,color:T.textFaint}}>No permission</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableCard>
          <div style={{marginTop:8,fontSize:12,color:T.textFaint}}>Provider control decisions are audit logged. Routing changes require pipeline config deploy.</div>
        </>
      )}
    </>
  );
}

// -- PLATFORM FAILURES --------------------------------------------------------
function SecPlatformFailures({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[page,setPage]=useState(1);
  const[hours,setHours]=useState(24);
  const[category,setCategory]=useState('');
  const[severity,setSeverity]=useState('');
  const[selectedFailure,setSelectedFailure]=useState(null);
  const{hasPermission}=useStaffAuth();

  const load=useCallback(()=>{
    setData(null);
    const p=new URLSearchParams({page,per_page:50,hours});
    if(category)p.set('category',category);
    fetch(`${api}/v1/staff/platform/failures?${p}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api,page,hours,category]);
  useEffect(()=>{load();},[load]);

  const failures=((data&&data.failures)||[]).filter(f=>!severity||f.severity===severity);

  return(
    <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
      <div style={{flex:1,minWidth:0}}>
        {data&&data.summary&&(
          <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap'}}>
            {Object.entries(data.summary).map(([cat,count])=>{
              const c=cat==='stuck'?FAILURE_SEVERITY_CONFIG.critical:cat==='generation'?FAILURE_SEVERITY_CONFIG.high:FAILURE_SEVERITY_CONFIG.medium;
              return(
                <div key={cat} style={{padding:'5px 10px',borderRadius:5,background:c.bg,border:`1px solid ${c.border}`,fontSize:12,fontWeight:700,color:c.color,cursor:'pointer'}}
                  onClick={()=>{setCategory(cat===category?'':cat);setPage(1);}}>
                  {cat}: {fmt(count)}
                </div>
              );
            })}
          </div>
        )}
        <div style={s.filterRow}>
          <select style={s.select} value={category} onChange={e=>{setCategory(e.target.value);setPage(1);}}>
            <option value="">All Types</option>
            {['generation','stuck','reconciliation','animation'].map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select style={s.select} value={severity} onChange={e=>setSeverity(e.target.value)}>
            <option value="">All Severities</option>
            {['critical','high','medium','low'].map(sv=><option key={sv} value={sv}>{sv}</option>)}
          </select>
          <select style={s.select} value={hours} onChange={e=>{setHours(Number(e.target.value));setPage(1);}}>
            {[6,24,48,168].map(h=><option key={h} value={h}>Last {h}h</option>)}
          </select>
          <button style={s.btn('primary')} onClick={()=>{setPage(1);load();}}>Apply</button>
          <button style={s.btn()} onClick={load}>Refresh</button>
        </div>
        {err&&<div style={s.error}>! {err}</div>}
        {!data?<Loader/>:(
          <TableCard title="Failure Center" count={data.total}>
            <div style={{overflowX:'auto'}}>
              <table style={{...s.table,minWidth:900}}>
                <thead><tr>
                  {['Sev','Type','Provider','Domain','Error','Detail','Att','User','Time','Action'].map(h=><th key={h} style={s.th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {failures.length===0?<tr><td colSpan={10} style={{...s.td,...s.empty}}>No failures.</td></tr>
                    :failures.map((f,i)=>{
                    const sc=FAILURE_SEVERITY_CONFIG[f.severity]||FAILURE_SEVERITY_CONFIG.low;
                    const isSelected=selectedFailure&&selectedFailure.id===f.id;
                    return(
                      <tr key={f.id||i} style={{background:f.severity==='critical'?T.redBg:isSelected?T.accentLight:'transparent',cursor:'pointer'}}
                        onClick={()=>setSelectedFailure(isSelected?null:f)}>
                        <td style={s.td}><span style={{fontSize:11,fontWeight:800,color:sc.color,background:sc.bg,border:`1px solid ${sc.border}`,borderRadius:3,padding:'2px 6px'}}>{(f.severity||'').toUpperCase()}</span></td>
                        <td style={s.td}><span style={s.badge('blue')}>{f.category}</span></td>
                        <td style={s.td}><code style={{fontFamily:'monospace',fontSize:11}}>{f.provider||'-'}</code></td>
                        <td style={s.td}><span style={{fontSize:11,color:T.textSub}}>{f.domain||'-'}</span></td>
                        <td style={s.td}><code style={{fontFamily:'monospace',fontSize:10,color:T.red}}>{f.error_code||'-'}</code></td>
                        <td style={{...s.td,maxWidth:160}}><span style={{fontSize:11,color:T.textSub,display:'block',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.detail||'-'}</span></td>
                        <td style={s.td}><span style={{fontSize:12,fontWeight:700,color:f.attempts>2?T.red:T.text}}>{f.attempts??'-'}</span></td>
                        <td style={s.td}><EntityChip type="user" id={f.user_id} label={(f.user_id||'').slice(0,8)}/></td>
                        <td style={{...s.td,fontSize:11,color:T.textFaint,whiteSpace:'nowrap'}}>{fmtAgo(f.created_at)}</td>
                        <td style={s.td}>
                          {f.category==='generation'&&hasPermission('platform.jobs')&&(
                            <EntityChip type="job" id={f.id} label="inspect"/>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {data.pages>1&&<Pager page={page} pages={data.pages} total={data.total} onPage={setPage}/>}
          </TableCard>
        )}
      </div>

      {/* Failure detail inspector */}
      {selectedFailure&&(
        <div style={{width:320,flexShrink:0}}>
          <div style={{...s.panel,position:'sticky',top:20}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
              <div style={{fontSize:14,fontWeight:700,color:T.text,flex:1}}>Failure Detail</div>
              <button onClick={()=>setSelectedFailure(null)} style={{background:'none',border:'none',color:T.textFaint,cursor:'pointer',fontSize:18}}>×</button>
            </div>
            {[
              {label:'Type',     val:<span style={s.badge('blue')}>{selectedFailure.category}</span>},
              {label:'Severity', val:selectedFailure.severity?.toUpperCase()},
              {label:'Provider', val:<code style={{fontFamily:'monospace',fontSize:12}}>{selectedFailure.provider||'-'}</code>},
              {label:'Domain',   val:selectedFailure.domain||'-'},
              {label:'Error',    val:<code style={{fontFamily:'monospace',fontSize:11,color:T.red}}>{selectedFailure.error_code||'-'}</code>},
              {label:'Attempts', val:selectedFailure.attempts??'-'},
              {label:'Time',     val:fmtDate(selectedFailure.created_at)},
            ].map(({label,val})=>(
              <div key={label} style={s.kvRow}>
                <span style={s.kvLabel}>{label}</span>
                <span style={{...s.kvValue,fontSize:13}}>{val}</span>
              </div>
            ))}
            {selectedFailure.detail&&(
              <div style={{marginTop:10,padding:'8px 10px',background:T.redBg,border:`1px solid ${T.redBorder}`,borderRadius:5,fontSize:12,color:T.red,fontFamily:'monospace',wordBreak:'break-all'}}>
                {selectedFailure.detail}
              </div>
            )}
            {selectedFailure.category==='generation'&&hasPermission('platform.jobs')&&(
              <div style={{marginTop:10}}>
                <EntityChip type="job" id={selectedFailure.id} label="Open in Inspector"/>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// -- PLATFORM CREDITS ---------------------------------------------------------
function SecPlatformCredits({api}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[hours,setHours]=useState(24);
  const load=useCallback(()=>{
    setData(null);
    fetch(`${api}/v1/staff/platform/credits?hours=${hours}&days=14`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api,hours]);
  useEffect(()=>{load();},[load]);

  return(
    <>
      <div style={s.filterRow}>
        <select style={s.select} value={hours} onChange={e=>setHours(Number(e.target.value))}>
          {[6,24,48,168].map(h=><option key={h} value={h}>Last {h}h</option>)}
        </select>
        <button style={s.btn('primary')} onClick={load}>Refresh</button>
      </div>
      {err&&<div style={s.error}>! {err}</div>}
      {!data?<Loader/>:(
        <>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
            <PlatformKPI label="Consumed" val={fmt(data.credits_consumed)} color="blue" sub={`Last ${hours}h`}/>
            <PlatformKPI label="At Risk (Failed+Deducted)" val={fmt(data.credits_at_risk)} color={data.credits_at_risk>0?'red':'green'} sub="Refund review needed"/>
            <PlatformKPI label="Stuck+Deducted" val={fmt(data.credits_stuck)} color={data.credits_stuck>0?'amber':'green'} sub="In stuck jobs"/>
          </div>

          {data.daily_trend&&data.daily_trend.length>0&&(
            <div style={{...s.panel,marginBottom:12,height:180}}>
              <div style={{fontSize:13,fontWeight:700,color:T.textSub,marginBottom:8}}>14-Day Credit Consumption</div>
              <ResponsiveContainer width="100%" height={130}>
                <BarChart data={data.daily_trend} margin={{top:0,right:0,bottom:0,left:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false}/>
                  <XAxis dataKey="day" tick={{fontSize:11,fill:T.textFaint}} axisLine={false} tickLine={false} tickFormatter={v=>v.slice(5)}/>
                  <YAxis tick={{fontSize:11,fill:T.textFaint}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<TT/>}/>
                  <Bar dataKey="credits" name="Credits" fill={T.chart1} radius={[2,2,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <TableCard title="Top Generators by Credits">
              <table style={s.table}>
                <thead><tr><th style={s.th}>Generator</th><th style={s.th}>Credits</th><th style={s.th}>Uses</th></tr></thead>
                <tbody>
                  {(data.top_generators||[]).length===0?<tr><td colSpan={3} style={{...s.td,...s.empty}}>No data.</td></tr>
                    :(data.top_generators||[]).map((r,i)=>(
                    <tr key={i}>
                      <td style={s.td}><span style={{fontSize:13}}>{r.slug}</span></td>
                      <td style={s.tds}>{fmt(r.total_credits)}</td>
                      <td style={s.td}>{fmt(r.uses)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableCard>
            <TableCard title="Credits by Source">
              <table style={s.table}>
                <thead><tr><th style={s.th}>Source</th><th style={s.th}>Credits</th></tr></thead>
                <tbody>
                  {(data.by_source||[]).length===0?<tr><td colSpan={2} style={{...s.td,...s.empty}}>No data.</td></tr>
                    :(data.by_source||[]).map((r,i)=>(
                    <tr key={i}>
                      <td style={s.td}><span style={s.badge('blue')}>{r.source||'unknown'}</span></td>
                      <td style={s.tds}>{fmt(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableCard>
          </div>
        </>
      )}
    </>
  );
}

// -- PLATFORM QUEUES ----------------------------------------------------------
function SecPlatformQueues({api}){
  const{hasPermission}=useStaffAuth();
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[confirm,setConfirm]=useState(null);
  const[actionMsg,setActionMsg]=useState('');const[actionErr,setActionErr]=useState('');

  const load=useCallback(()=>{
    setData(null);
    fetch(`${api}/v1/staff/platform/queues`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api]);
  useEffect(()=>{load();},[load]);

  const doQueueControl=(queue,action,note)=>{
    fetch(`${api}/v1/staff/platform/queues/control`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({queue,action,note})})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok){setActionMsg(d.message);load();}else setActionErr(d.error||'Failed');})
      .catch(()=>setActionErr('Network error'));
  };

  const canControl=hasPermission('platform.control');
  const ig=(data&&data.image_generation)||{};
  const an=(data&&data.animation)||{};

  return(
    <>
      {confirm&&<ConfirmModal title={confirm.title} body={confirm.body} danger={confirm.danger} confirmLabel={confirm.label} onCancel={()=>setConfirm(null)} onConfirm={(note)=>{setConfirm(null);doQueueControl(confirm.queue,confirm.action,note);}}/>}
      {actionMsg&&<div style={{padding:'8px 12px',background:T.greenBg,border:`1px solid ${T.greenBorder}`,borderRadius:6,fontSize:13,color:T.green,marginBottom:8}}>{actionMsg}</div>}
      {actionErr&&<div style={{...s.error,marginBottom:8}}>! {actionErr}</div>}
      <div style={{textAlign:'right',marginBottom:8}}>
        <button style={s.btn()} onClick={load}>Refresh</button>
      </div>
      {err&&<div style={s.error}>! {err}</div>}
      {!data?<Loader/>:(
        <>
          <div style={{marginBottom:8,fontSize:13,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em'}}>Image Generation Queue</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
            <PlatformKPI label="Queued"      val={fmt(ig.queued)}      color={ig.queued>50?'amber':'green'}/>
            <PlatformKPI label="Processing"  val={fmt(ig.processing)}  color={ig.processing>20?'amber':'green'}/>
            <PlatformKPI label="Active"      val={fmt(ig.active)}      color="blue"/>
            <PlatformKPI label="Stuck"       val={fmt(ig.stuck)}       color={ig.stuck>0?'red':'green'}/>
            <PlatformKPI label="Avg Delay"   val={ig.avg_queue_delay_ms?`${ig.avg_queue_delay_ms}ms`:'-'} color="blue"/>
            <PlatformKPI label="Oldest"      val={ig.oldest_queued_age_minutes!=null?`${ig.oldest_queued_age_minutes}m`:'-'} color={ig.oldest_queued_age_minutes>30?'red':ig.oldest_queued_age_minutes>10?'amber':'green'}/>
          </div>

          {ig.oldest_queued_id&&(
            <div style={{...s.panel,padding:'10px 14px',marginBottom:12,borderLeft:`3px solid ${T.amberBorder}`}}>
              <div style={{fontSize:13,fontWeight:700,color:T.amber,marginBottom:3}}>Oldest Queued Job</div>
              <div style={{fontSize:13,color:T.textMid}}><code style={{fontFamily:'monospace'}}>{ig.oldest_queued_id}</code> &middot; {ig.oldest_queued_age_minutes}m ago &middot; {fmtDate(ig.oldest_queued_at)}</div>
            </div>
          )}

          {canControl&&(
            <div style={{...s.panel,padding:'12px 14px',marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:700,color:T.textSub,marginBottom:10}}>Queue Controls</div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                <button style={{...s.btn(),fontSize:12}} onClick={()=>setConfirm({title:'Clear Stuck Jobs',body:'Mark all stuck generation jobs as failed? This will free credit locks on stuck jobs.',label:'Clear Stuck',danger:true,queue:'generation',action:'clear_stuck'})}>Clear Stuck (Gen)</button>
                <button style={{...s.btn(),fontSize:12}} onClick={()=>setConfirm({title:'Clear Stuck Animation Jobs',body:'Mark all stuck animation jobs as failed?',label:'Clear Stuck',danger:true,queue:'animation',action:'clear_stuck'})}>Clear Stuck (Anim)</button>
                <button style={{...s.btn(),fontSize:12,opacity:0.55,cursor:'not-allowed'}} disabled title="Requires pipeline deploy">Pause Queue</button>
                <button style={{...s.btn(),fontSize:12,opacity:0.55,cursor:'not-allowed'}} disabled title="Requires pipeline deploy">Resume Queue</button>
              </div>
              <div style={{fontSize:11,color:T.textFaint,marginTop:6}}>Pause/Resume require pipeline config deploy. Clear Stuck takes immediate effect.</div>
            </div>
          )}

          {ig.by_domain&&ig.by_domain.length>0&&(
            <TableCard title="Active Queue by Domain">
              <table style={s.table}>
                <thead><tr><th style={s.th}>Domain</th><th style={s.th}>Count</th></tr></thead>
                <tbody>
                  {ig.by_domain.map(d=>(
                    <tr key={d.domain}>
                      <td style={s.td}><span style={s.badge('blue')}>{d.domain}</span></td>
                      <td style={s.tds}>{fmt(d.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableCard>
          )}

          <div style={{marginTop:14,marginBottom:6,fontSize:13,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em'}}>Animation Queue</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <PlatformKPI label="Queued"     val={fmt(an.queued)}     color={an.queued>10?'amber':'green'}/>
            <PlatformKPI label="Processing" val={fmt(an.processing)} color="blue"/>
            <PlatformKPI label="Active"     val={fmt(an.active)}     color="blue"/>
          </div>
          <div style={{fontSize:11,color:T.textFaint,marginTop:8}}>Stuck threshold: {data.stuck_threshold_minutes} minutes</div>
        </>
      )}
    </>
  );
}

// ============================================================================
// END PLATFORM OPERATIONS CENTER

// ============================================================================


// ============================================================================
// LIVE OPERATIONS INFRASTRUCTURE
// ============================================================================

/**
 * useLiveOps - SSE-based live operational telemetry hook.
 * Connects to /v1/staff/live/stream, handles reconnect with backoff,
 * heartbeat detection, cleanup on unmount. Subscription-based dispatch.
 *
 * Returns: { liveData, connected, incident }
 *   liveData.platform  - platform_snapshot payload
 *   liveData.incident  - incident_state payload
 *   liveData.jobs      - job_activity payload (recent events array)
 *   liveData.support   - support_snapshot payload
 *   liveData.mail      - mail_snapshot payload
 *   connected          - boolean
 *   incident           - shorthand for liveData.incident
 */
function useLiveOps(api) {
  const [liveData, setLiveData] = useState({});
  const [connected, setConnected] = useState(false);
  const esRef      = useRef(null);
  const retryRef   = useRef(0);
  const hbRef      = useRef(null);
  const mountedRef = useRef(true);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    const es = new EventSource(`${api}/v1/staff/live/stream`, { withCredentials: true });
    esRef.current = es;

    const resetHb = () => {
      if (hbRef.current) clearTimeout(hbRef.current);
      // If no message in 30s, consider stale
      hbRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        setConnected(false);
        es.close();
        // Reconnect with backoff
        const delay = Math.min(1000 * Math.pow(2, retryRef.current), 30000);
        retryRef.current = Math.min(retryRef.current + 1, 6);
        setTimeout(connect, delay);
      }, 30000);
    };

    es.onopen = () => {
      if (!mountedRef.current) return;
      setConnected(true);
      retryRef.current = 0;
      resetHb();
    };

    es.onerror = () => {
      if (!mountedRef.current) return;
      setConnected(false);
      es.close();
      if (hbRef.current) clearTimeout(hbRef.current);
      const delay = Math.min(1000 * Math.pow(2, retryRef.current), 30000);
      retryRef.current = Math.min(retryRef.current + 1, 6);
      setTimeout(connect, delay);
    };

    const dispatch = (key) => (ev) => {
      if (!mountedRef.current) return;
      resetHb();
      try {
        const data = JSON.parse(ev.data);
        setLiveData(prev => ({ ...prev, [key]: data }));
      } catch (_) {}
    };

    es.addEventListener('heartbeat',         (ev) => { resetHb(); });
    es.addEventListener('platform_snapshot', dispatch('platform'));
    es.addEventListener('incident_state',    dispatch('incident'));
    es.addEventListener('job_activity',      dispatch('jobs'));
    es.addEventListener('support_snapshot',  dispatch('support'));
    es.addEventListener('mail_snapshot',     dispatch('mail'));
    es.addEventListener('error',             (ev) => { /* server error event, not connection error */ });
  }, [api]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      if (hbRef.current) clearTimeout(hbRef.current);
    };
  }, [connect]);

  return { liveData, connected, incident: liveData.incident };
}

/**
 * LiveDot - subtle connection state indicator
 */
function LiveDot({ connected }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontWeight: 600,
      color: connected ? T.green : T.textFaint,
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: connected ? T.green : T.textFaint,
        opacity: connected ? 1 : 0.5,
      }}/>
      {connected ? 'LIVE' : 'CONNECTING'}
    </span>
  );
}

/**
 * IncidentStrip - global operational incident banner.
 * Only shown during WARNING/DEGRADED/CRITICAL states.
 * Dismissed per-session via local state.
 */
function IncidentStrip({ incident, onNavigate }) {
  const [dismissed, setDismissed] = useState(null);

  if (!incident || incident.severity === 'HEALTHY') return null;
  if (dismissed === incident.severity + (incident.incidents||[]).map(i=>i.type).join(',')) return null;

  const COLOR = {
    CRITICAL: { bg: T.redBg,   border: T.redBorder,   text: T.red,       dot: T.red },
    WARNING:  { bg: '#fffaf0', border: '#fbd38d',      text: '#c05621',   dot: '#c05621' },
    DEGRADED: { bg: T.amberBg, border: T.amberBorder,  text: T.amber,     dot: T.amber },
  };
  const c = COLOR[incident.severity] || COLOR.DEGRADED;

  return (
    <div style={{
      background: c.bg, borderBottom: `1px solid ${c.border}`,
      padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 12,
      flexShrink: 0,
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot, flexShrink: 0 }}/>
      <span style={{ fontSize: 13, fontWeight: 800, color: c.text, letterSpacing: '0.08em' }}>
        {incident.severity}
      </span>
      <div style={{ display: 'flex', gap: 12, flex: 1, flexWrap: 'wrap' }}>
        {(incident.incidents || []).map((inc, i) => (
          <span key={i} style={{ fontSize: 13, color: c.text, cursor: inc.link ? 'pointer' : 'default' }}
            onClick={() => inc.link && onNavigate && onNavigate(inc.link)}>
            {inc.msg}{inc.link ? ' →' : ''}
          </span>
        ))}
        {(!incident.incidents || incident.incidents.length === 0) && (
          <span style={{ fontSize: 13, color: c.text }}>Platform operational state degraded — monitoring.</span>
        )}
      </div>
      <button
        onClick={() => setDismissed(incident.severity + (incident.incidents||[]).map(i=>i.type).join(','))}
        style={{ background: 'none', border: 'none', color: c.text, cursor: 'pointer', fontSize: 16, lineHeight: 1, opacity: 0.7, flexShrink: 0 }}>
        ×
      </button>
    </div>
  );
}

/**
 * LiveJobStream - compact live job event feed for Platform Overview.
 */
function LiveJobStream({ jobs, paused, onTogglePause }) {
  const listRef = useRef(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (paused || !jobs || !jobs.events) return;
    setEvents(prev => {
      const combined = [...(jobs.events || []), ...prev].slice(0, 60);
      // Deduplicate by job_id+event
      const seen = new Set();
      return combined.filter(e => {
        const key = e.job_id + e.event + e.ts;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    });
  }, [jobs, paused]);

  const EVENT_COLOR = {
    job_failed:    T.red,
    job_completed: T.green,
    job_started:   T.accent,
    job_created:   T.textSub,
  };

  return (
    <div style={{ ...s.panel, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.textSub, flex: 1 }}>Live Job Activity</div>
        <button style={{ ...s.btn(), fontSize: 12, padding: '3px 8px' }} onClick={onTogglePause}>
          {paused ? '▶ Resume' : '⏸ Pause'}
        </button>
        {paused && <span style={{ fontSize: 11, color: T.textFaint }}>Feed paused</span>}
      </div>
      <div ref={listRef} style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {events.length === 0 ? (
          <div style={{ fontSize: 13, color: T.textFaint, padding: '8px 0' }}>Waiting for events...</div>
        ) : events.map((ev, i) => (
          <div key={ev.job_id + ev.ts + i} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '4px 8px',
            borderRadius: 4, background: i === 0 && !paused ? T.bg : 'transparent',
            transition: 'background 0.3s',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: EVENT_COLOR[ev.event] || T.textFaint, flexShrink: 0 }}/>
            <code style={{ fontFamily: 'monospace', fontSize: 11, color: T.textFaint, width: 100, flexShrink: 0 }}>{ev.job_id}...</code>
            <span style={{ fontSize: 12, fontWeight: 600, color: EVENT_COLOR[ev.event] || T.textMid, width: 110, flexShrink: 0 }}>{ev.event}</span>
            <code style={{ fontFamily: 'monospace', fontSize: 11, color: T.textSub, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.provider || ''} {ev.domain || ''}</code>
            {ev.error && <code style={{ fontFamily: 'monospace', fontSize: 10, color: T.red }}>{ev.error}</code>}
            <span style={{ fontSize: 11, color: T.textFaint, flexShrink: 0, whiteSpace: 'nowrap' }}>{ev.ts ? fmtAgo(ev.ts) : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// END LIVE OPERATIONS INFRASTRUCTURE
// ============================================================================

// ============================================================================
// SUPPORT OPERATIONS CENTER
// ============================================================================

const SUPPORT_STATUS_CONFIG = {
  OPEN:      {bg:'#ebf8ff',color:'#2b6cb0',border:'#bee3f8',dot:'#3182ce'},
  TRIAGED:   {bg:'#faf5ff',color:'#553c9a',border:'#d6bcfa',dot:'#805ad5'},
  PENDING:   {bg:'#fffaf0',color:'#b7791f',border:'#fbd38d',dot:'#d69e2e'},
  ESCALATED: {bg:'#fff5f5',color:'#c53030',border:'#feb2b2',dot:'#e53e3e'},
  RESOLVED:  {bg:'#f0fff4',color:'#276749',border:'#9ae6b4',dot:'#38a169'},
  CLOSED:    {bg:T.bg,    color:T.textSub, border:T.border, dot:T.textFaint},
};
const SUPPORT_SEVERITY_CONFIG = {
  LOW:      {color:T.accent,   bg:T.accentLight, border:T.accentBorder},
  MEDIUM:   {color:T.amber,    bg:T.amberBg,     border:T.amberBorder},
  HIGH:     {color:'#c05621',  bg:'#fffaf0',     border:'#fbd38d'},
  CRITICAL: {color:T.red,      bg:T.redBg,       border:T.redBorder},
  NORMAL:   {color:T.textSub,  bg:T.bg,          border:T.border},
};

function SupportStatusPill({status}){
  const k=(status||'OPEN').toUpperCase();
  const c=SUPPORT_STATUS_CONFIG[k]||SUPPORT_STATUS_CONFIG.OPEN;
  return(
    <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:5,minHeight:32,boxSizing:'border-box',padding:'0 12px',borderRadius:6,fontSize:13,fontWeight:500,whiteSpace:'nowrap',background:c.bg,color:c.color,border:`1px solid ${c.border}`}}>
      <span style={{width:6,height:6,borderRadius:'50%',background:c.dot,flexShrink:0}}/>
      {k}
    </span>
  );
}

function SupportSeverityPill({severity}){
  const k=(severity||'NORMAL').toUpperCase();
  const c=SUPPORT_SEVERITY_CONFIG[k]||SUPPORT_SEVERITY_CONFIG.NORMAL;
  return(
    <span style={{display:'inline-flex',padding:'3px 9px',borderRadius:4,fontSize:13,fontWeight:700,background:c.bg,color:c.color,border:`1px solid ${c.border}`}}>
      {k}
    </span>
  );
}

// Open time in human readable format
function openTime(created_at){
  if(!created_at)return'-';
  const mins=Math.floor((Date.now()-new Date(created_at))/60000);
  if(mins<60)return`${mins}m`;
  if(mins<1440)return`${Math.floor(mins/60)}h`;
  return`${Math.floor(mins/1440)}d`;
}

// ── Ticket Detail View ────────────────────────────────────────────────────────

// ---------------------------------------------------------------------------
// SUPPORT OPERATIONS
// ---------------------------------------------------------------------------
// Added to the ONE unified Admin, not beside it: these components call the
// /v1/staff/support/* routes registered on the same `staff` blueprint as the
// support queue that already existed.
//
// Every control below is gated on the SAME permission its backend route
// enforces, so the interface can never offer an operator an authority the API
// will refuse. A support operator can classify, claim, escalate and resolve.
// They cannot refund, grant entitlements, adjust credits, change subscription
// state or touch staff accounts - those permissions are not granted to the
// SUPPORT role and none of them are reachable from here.
// ---------------------------------------------------------------------------

const SUPPORT_STATUS_TONE = {
  NEW:'#2D6CFF', OPEN:'#2D6CFF', WAITING_ON_CUSTOMER:'#D69E2E',
  WAITING_INTERNAL:'#D69E2E', ESCALATED:'#E53E3E', RESOLVED:'#38A169', CLOSED:'#718096',
};
const SUPPORT_PRIORITY_TONE = { NORMAL:'#718096', HIGH:'#D69E2E', URGENT:'#E53E3E' };

function SupportChip({label,tone,title}){
  return(
    <span title={title||undefined} style={{
      display:'inline-block',padding:'2px 9px',borderRadius:999,fontSize:11,fontWeight:700,
      letterSpacing:'0.04em',whiteSpace:'nowrap',
      color:tone,background:`${tone}1F`,border:`1px solid ${tone}66`,
    }}>{label}</span>
  );
}

// Minutes are how the backend measures; humans read hours and days.
function supportDuration(minutes){
  if(minutes===null||minutes===undefined)return '-';
  const m=Number(minutes);
  if(!isFinite(m))return '-';
  if(m<60)return `${Math.round(m)}m`;
  if(m<60*24)return `${(m/60).toFixed(1)}h`;
  return `${(m/1440).toFixed(1)}d`;
}

// A rate that could not be calculated must READ as unavailable, never as zero.
function supportRate(value,suffix){
  return (value===null||value===undefined)
    ? <span style={{color:T.textFaint}}>not available</span>
    : <>{value}{suffix||''}</>;
}

// ---------------------------------------------------------------------------
// The operations strip on a case: classification, lifecycle, escalation.
// ---------------------------------------------------------------------------
function SupportCaseOperations({api,ticketId,ticket,onChanged,hasPermission}){
  const[vocab,setVocab]=useState(null);
  const[product,setProduct]=useState('');
  const[reason,setReason]=useState('');
  const[escReason,setEscReason]=useState('');
  const[escPriority,setEscPriority]=useState('');
  const[showEscalate,setShowEscalate]=useState(false);
  const[busy,setBusy]=useState('');
  const[msg,setMsg]=useState('');
  const[err,setErr]=useState('');

  const canClassify=hasPermission('support.status');
  const canEscalate=hasPermission('support.escalate');
  const canResolve=hasPermission('support.resolve');
  const canClaim=hasPermission('support.assign');

  useEffect(()=>{
    if(!hasPermission('support.view'))return;
    fetch(`${api}/v1/staff/support/vocabulary`,{credentials:'include'})
      .then(r=>r.ok?r.json():null).then(d=>{if(d)setVocab(d);}).catch(()=>{});
  },[api,hasPermission]);

  useEffect(()=>{
    setProduct((ticket&&ticket.product)||'');
    setReason((ticket&&ticket.reason)||'');
  },[ticket]);

  const call=(path,body,label)=>{
    setBusy(label);setMsg('');setErr('');
    fetch(`${api}${path}`,{method:'POST',credentials:'include',
      headers:{'Content-Type':'application/json'},body:JSON.stringify(body||{})})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{
        if(ok){setMsg(`${label} applied.`);if(onChanged)onChanged();}
        else setErr(d.message||d.error||`${label} failed.`);
      })
      .catch(()=>setErr('Network error.'))
      .finally(()=>setBusy(''));
  };

  const status=(ticket&&ticket.status?String(ticket.status):'OPEN').toUpperCase();
  const priority=(ticket&&ticket.priority?String(ticket.priority):'NORMAL').toUpperCase();
  const statusTone=SUPPORT_STATUS_TONE[status]||T.textSub;
  const priorityTone=SUPPORT_PRIORITY_TONE[priority]||T.textSub;

  return(
    <div style={{...s.panel,marginBottom:10,padding:'14px 16px'}}>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12,flexWrap:'wrap'}}>
        <div style={{fontSize:14,fontWeight:700,color:T.textSub}}>Case Operations</div>
        <SupportChip label={status.replace(/_/g,' ')} tone={statusTone}/>
        <SupportChip label={priority} tone={priorityTone}/>
        {ticket&&ticket.escalated_at&&<SupportChip label="ESCALATED" tone="#E53E3E"/>}
        {ticket&&!ticket.first_response_at&&
          <SupportChip label="AWAITING FIRST REPLY" tone="#D69E2E"
            title="No reply has been sent to this customer yet."/>}
        <div style={{flex:1}}/>
        {canClaim&&!(ticket&&ticket.assignee_name)&&(
          <button style={s.btn()} disabled={!!busy}
            onClick={()=>call(`/v1/staff/support/ticket/${ticketId}/claim`,{},'Claim')}>
            {busy==='Claim'?'Claiming...':'Claim Case'}
          </button>
        )}
        {canResolve&&(
          <button style={s.btn('primary')} disabled={!!busy}
            onClick={()=>call(`/v1/staff/support/ticket/${ticketId}/resolve`,{},'Resolve')}>
            {busy==='Resolve'?'Resolving...':'Resolve'}
          </button>
        )}
        {canEscalate&&(
          <button style={{...s.btn(),borderColor:'#E53E3E66',color:'#E53E3E'}}
            onClick={()=>setShowEscalate(v=>!v)}>
            {showEscalate?'Cancel Escalation':'Escalate'}
          </button>
        )}
      </div>

      {msg&&<div style={{padding:'8px 12px',background:T.greenBg,border:`1px solid ${T.greenBorder}`,borderRadius:6,fontSize:13,fontWeight:600,color:T.green,marginBottom:10}}>{msg}</div>}
      {err&&<div style={{...s.error,marginBottom:10}}>! {err}</div>}

      {/* Classification. The product list is DERIVED from the commercial registry, so a new
          VISINVOKE product becomes selectable the moment it is declared. */}
      {canClassify&&(
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          <span style={{fontSize:12,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em'}}>Attribution</span>
          <select style={s.select} value={product} onChange={e=>setProduct(e.target.value)}>
            <option value="">Product...</option>
            {((vocab&&vocab.products)||[]).map(p=>
              <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <select style={s.select} value={reason} onChange={e=>setReason(e.target.value)}>
            <option value="">Reason...</option>
            {((vocab&&vocab.reasons)||[]).map(r=>
              <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          <button style={s.btn('primary')} disabled={!!busy||(!product&&!reason)}
            onClick={()=>call(`/v1/staff/support/ticket/${ticketId}/classify`,
              {product:product||undefined,reason:reason||undefined},'Classification')}>
            {busy==='Classification'?'Saving...':'Save Attribution'}
          </button>
          <span style={{fontSize:12,color:T.textFaint}}>
            Attribution is what makes support volume traceable to a product.
          </span>
        </div>
      )}

      {/* Escalation. An operator can raise a financial or security concern WITHOUT holding the
          authority the escalation is about - that is the whole point of the separation. */}
      {canEscalate&&showEscalate&&(
        <div style={{marginTop:12,padding:'12px 14px',background:T.redBg,border:`1px solid ${T.redBorder}`,borderRadius:7}}>
          <div style={{fontSize:13,fontWeight:700,color:'#E53E3E',marginBottom:8}}>Escalate to privileged staff</div>
          <textarea style={{...s.input,width:'100%',boxSizing:'border-box',minHeight:64,resize:'vertical',fontFamily:'inherit',fontSize:14}}
            placeholder="Why does this need higher authority? (e.g. paid but not granted, suspected duplicate charge)"
            value={escReason} onChange={e=>setEscReason(e.target.value)}/>
          <div style={{display:'flex',gap:8,marginTop:8,alignItems:'center',flexWrap:'wrap'}}>
            <select style={s.select} value={escPriority} onChange={e=>setEscPriority(e.target.value)}>
              <option value="">Priority (derived)</option>
              {((vocab&&vocab.priorities)||['NORMAL','HIGH','URGENT']).map(p=>
                <option key={p} value={p}>{p}</option>)}
            </select>
            <button style={{...s.btn('primary'),background:'#E53E3E',borderColor:'#E53E3E'}}
              disabled={!!busy||escReason.trim().length<5}
              onClick={()=>{
                call(`/v1/staff/support/ticket/${ticketId}/escalate`,
                  {reason:escReason.trim(),priority:escPriority||undefined},'Escalation');
                setEscReason('');setShowEscalate(false);
              }}>
              {busy==='Escalation'?'Escalating...':'Escalate Case'}
            </button>
            <span style={{fontSize:12,color:T.textFaint}}>
              Escalating does not give you the authority being escalated to.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The support-SAFE account panel.
// ---------------------------------------------------------------------------
// The backend never reads a password hash, token, MFA secret, recovery code or
// provider secret into this payload, so none of them can be rendered here.
function SupportAccountPanel({api,ticketId,hasPermission}){
  const[data,setData]=useState(null);
  const[err,setErr]=useState('');
  const allowed=hasPermission('support.view_account_summary');

  useEffect(()=>{
    if(!allowed)return;
    fetch(`${api}/v1/staff/support/ticket/${ticketId}/account`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Unavailable');})
      .catch(()=>setErr('Network error'));
  },[api,ticketId,allowed]);

  if(!allowed)return null;
  if(err)return<IntelPanel title="Account Context"><div style={{fontSize:13,color:T.textFaint}}>{err}</div></IntelPanel>;
  if(!data)return<IntelPanel title="Account Context"><div style={{fontSize:13,color:T.textFaint}}>Loading...</div></IntelPanel>;
  if(!data.available)return<IntelPanel title="Account Context"><div style={{fontSize:13,color:T.textFaint}}>No linked account.</div></IntelPanel>;

  const id=data.identity||{};
  const signals=data.commercial_signals||{};
  const ents=data.entitlements||{};
  const scc=ents.service_command_centre||{};
  const history=data.support_history||{};

  return(
    <>
      {signals.has_warning&&(
        <div style={{padding:'9px 12px',background:T.amberBg,border:`1px solid ${T.amberBorder}`,borderRadius:7,marginBottom:10}}>
          <div style={{fontSize:12,fontWeight:700,color:T.amber,marginBottom:4}}>COMMERCIAL SIGNAL</div>
          {(signals.paid_not_granted||[]).length>0&&(
            <div style={{fontSize:13,color:T.textMid,lineHeight:1.5}}>
              {(signals.paid_not_granted||[]).length} payment(s) recorded without entitlement granted.
            </div>
          )}
          {(signals.open_reconciliation||[]).length>0&&(
            <div style={{fontSize:13,color:T.textMid,lineHeight:1.5}}>
              {(signals.open_reconciliation||[]).length} open reconciliation record(s).
            </div>
          )}
          <div style={{fontSize:12,color:T.textFaint,marginTop:5}}>Escalate - do not attempt to correct this from support.</div>
        </div>
      )}

      <IntelPanel title="Account Context">
        <IntelKV label="Tier" val={id.tier||'-'}/>
        <IntelKV label="Status" val={id.account_status||(id.is_active?'active':'inactive')}
          color={id.is_suspended?T.red:undefined}/>
        <IntelKV label="Country" val={id.country||'-'}/>
        <IntelKV label="Shop Credit" val={data.shop_credit_balance===null||data.shop_credit_balance===undefined?'-':fmt(data.shop_credit_balance)}/>
        <IntelKV label="Combat System Pro" val={ents.combat_system_pro?'Yes':'No'}/>
        <IntelKV label="SCC" val={scc.active?(scc.status||'active'):'No'}/>
      </IntelPanel>

      {(data.subscriptions||[]).length>0&&(
        <IntelPanel title="Subscriptions">
          {(data.subscriptions||[]).map((sub,i)=>(
            <div key={i} style={{padding:'5px 0',borderBottom:`1px solid ${T.borderSoft}`,fontSize:13}}>
              <div style={{fontWeight:600,color:T.text}}>{sub.plan||'Plan'}</div>
              <div style={{fontSize:12,color:T.textFaint}}>
                {sub.status}{sub.cancel_at_period_end?' - cancels at period end':''}
              </div>
            </div>
          ))}
        </IntelPanel>
      )}

      {(data.recent_purchases||[]).length>0&&(
        <IntelPanel title="Recent Purchases">
          {(data.recent_purchases||[]).slice(0,6).map((p,i)=>(
            <div key={i} style={{padding:'5px 0',borderBottom:`1px solid ${T.borderSoft}`,fontSize:13}}>
              <div style={{fontWeight:600,color:T.text}}>{String(p.purchase_type||'-').replace(/_/g,' ')}</div>
              <div style={{fontSize:12,color:p.entitlement_granted?T.textFaint:T.amber}}>
                {((p.amount_minor||0)/100).toFixed(2)} {p.currency}
                {p.is_refund?' - refunded':p.entitlement_granted?'':' - NOT GRANTED'}
              </div>
            </div>
          ))}
        </IntelPanel>
      )}

      <IntelPanel title="Support History">
        <IntelKV label="Total Cases" val={fmt(history.total_cases)}/>
        <IntelKV label="Open" val={fmt(history.open_cases)} color={history.open_cases>1?T.amber:undefined}/>
        <IntelKV label="Escalated" val={fmt(history.escalated_cases)} color={history.escalated_cases>0?T.red:undefined}/>
      </IntelPanel>
    </>
  );
}

// ---------------------------------------------------------------------------
// The read-only commercial / refund-policy panel.
// ---------------------------------------------------------------------------
// The worldwide commerce-policy resolver is the authority. Support can SEE the
// customer's actual position so they explain it correctly; executing a refund
// requires finance.refunds and goes through Stripe, not through here.
function SupportCommercialPanel({api,ticketId,hasPermission}){
  const[data,setData]=useState(null);
  const[err,setErr]=useState('');
  const allowed=hasPermission('support.view_commercial_summary');

  useEffect(()=>{
    if(!allowed)return;
    fetch(`${api}/v1/staff/support/ticket/${ticketId}/commercial`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Unavailable');})
      .catch(()=>setErr('Network error'));
  },[api,ticketId,allowed]);

  if(!allowed)return null;
  if(err)return<IntelPanel title="Refund Policy"><div style={{fontSize:13,color:T.textFaint}}>{err}</div></IntelPanel>;
  if(!data)return<IntelPanel title="Refund Policy"><div style={{fontSize:13,color:T.textFaint}}>Loading...</div></IntelPanel>;
  if(!data.available)return null;

  const purchases=data.purchases||[];
  return(
    <IntelPanel title="Refund Policy (read only)">
      {purchases.length===0&&<div style={{fontSize:13,color:T.textFaint}}>No purchases recorded.</div>}
      {purchases.slice(0,6).map((p,i)=>{
        const pol=p.policy||{};
        const review=pol.legal_review_required;
        return(
          <div key={i} style={{padding:'7px 0',borderBottom:`1px solid ${T.borderSoft}`,fontSize:13}}>
            <div style={{fontWeight:600,color:T.text}}>{String(p.purchase_type||'-').replace(/_/g,' ')}</div>
            {!pol.available?(
              <div style={{fontSize:12,color:T.textFaint}}>
                No policy snapshot - this purchase predates policy capture. Escalate rather than guess.
              </div>
            ):(
              <>
                <div style={{fontSize:12,color:T.textFaint}}>
                  {pol.country||'?'}{pol.subdivision?`/${pol.subdivision}`:''} - {pol.supply_form||'-'}
                </div>
                <div style={{fontSize:12,color:T.textMid,marginTop:2}}>
                  Voluntary: {String(pol.voluntary_refund_eligibility||'-').replace(/_/g,' ')}
                </div>
                <div style={{fontSize:12,color:T.textMid}}>
                  Statutory: {String(pol.statutory_status||'-').replace(/_/g,' ')}
                </div>
                {review&&(
                  <div style={{fontSize:12,fontWeight:700,color:T.amber,marginTop:3}}>
                    LEGAL REVIEW REQUIRED - do not state a position. Escalate.
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
      <div style={{fontSize:11,color:T.textFaint,marginTop:8,lineHeight:1.5}}>
        Read only. Refunds require finance.refunds and are executed through Stripe.
      </div>
    </IntelPanel>
  );
}

// ---------------------------------------------------------------------------
// SecSupportAnalytics
// ---------------------------------------------------------------------------
// The question this answers is the owner's: how much support does VISINVOKE
// actually need, and WHICH PRODUCT is causing it - so volume can be driven down
// by fixing the product rather than absorbed by hiring.
//
// Medians are the headline, not means: one case answered over a weekend must
// not make support look slow. A figure whose denominator is unavailable reads
// "not available", never zero.
function SecSupportAnalytics({api}){
  const[period,setPeriod]=useState('month');
  const[data,setData]=useState(null);
  const[err,setErr]=useState(null);

  useEffect(()=>{
    setData(null);setErr(null);
    fetch(`${api}/v1/staff/support/analytics?period=${period}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api,period]);

  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const vol=data.volume||{};
  const resp=data.response||{};
  const fr=resp.first_response||{};
  const res=resp.resolution||{};
  const rate=data.contact_rate||{};
  const friction=data.friction||{};
  const products=(data.by_product||{}).products||[];
  const reasons=(data.by_reason||{}).reasons||[];
  const operators=(data.operators||{}).operators||[];
  const oldest=data.oldest_open||[];
  const series=(data.volume_series||{}).series||[];
  const peak=series.reduce((m,d)=>Math.max(m,d.cases),0)||1;

  return(
    <>
      <div style={{display:'flex',gap:8,marginBottom:14,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{fontSize:12,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em'}}>Period</span>
        {['today','week','month','quarter','all'].map(p=>(
          <button key={p} onClick={()=>setPeriod(p)}
            style={{padding:'5px 14px',borderRadius:999,fontSize:13,fontWeight:600,cursor:'pointer',
              border:`1px solid ${period===p?T.accent:T.border}`,
              background:period===p?T.accentLight:T.surface,
              color:period===p?T.accent:T.textSub}}>{p}</button>
        ))}
        <div style={{flex:1}}/>
        <span style={{fontSize:12,color:T.textFaint}}>Business days: {data.timezone}</span>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))',gap:10,marginBottom:14}}>
        <RadialKPI label="New Today"          value={fmt(vol.new_today)} color="blue"/>
        <RadialKPI label="New This Week"      value={fmt(vol.new_this_week)} color="blue"/>
        <RadialKPI label="Open"               value={fmt(vol.open)} color={vol.open>0?'amber':'green'}/>
        <RadialKPI label="Escalated"          value={fmt(vol.escalated)} color={vol.escalated>0?'red':'green'}/>
        <RadialKPI label="Median First Reply" value={supportDuration(fr.median_minutes)} color="purple"/>
        <RadialKPI label="Median Resolution"  value={supportDuration(res.median_minutes)} color="purple"/>
      </div>

      <div style={{display:'flex',gap:12,alignItems:'flex-start',flexWrap:'wrap'}}>
        <div style={{flex:'1 1 420px',minWidth:0}}>

          {/* THE question: which product causes the support. */}
          <TableCard title="Support Demand By Product" count={products.length}>
            <table style={s.table}>
              <thead><tr>
                <th style={s.th}>Product</th><th style={s.th}>Cases</th>
                <th style={s.th}>Escalated</th><th style={s.th}>Escalation %</th>
                <th style={s.th}>Median Resolution</th>
              </tr></thead>
              <tbody>
                {products.length===0&&<tr><td style={s.td} colSpan={5}><div style={s.empty}>No cases in this period.</div></td></tr>}
                {products.map(p=>(
                  <tr key={p.product}>
                    <td style={s.td}>
                      <div style={{fontWeight:600,color:T.text}}>{p.label}</div>
                      <div style={{fontSize:12,color:T.textFaint}}>{p.family}</div>
                    </td>
                    <td style={s.td}>{fmt(p.cases)}</td>
                    <td style={s.td}>{fmt(p.escalated)}</td>
                    <td style={{...s.td,color:p.escalation_rate_pct>20?T.red:p.escalation_rate_pct>10?T.amber:T.textMid}}>
                      {p.escalation_rate_pct}%
                    </td>
                    <td style={s.td}>{supportDuration(p.median_resolution_minutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>

          <TableCard title="Why Customers Contact Support" count={reasons.length}>
            <table style={s.table}>
              <thead><tr><th style={s.th}>Reason</th><th style={s.th}>Cases</th><th style={s.th}>Share</th></tr></thead>
              <tbody>
                {reasons.length===0&&<tr><td style={s.td} colSpan={3}><div style={s.empty}>No cases in this period.</div></td></tr>}
                {reasons.map(r=>{
                  const total=reasons.reduce((sum,x)=>sum+x.cases,0)||1;
                  const pct=Math.round(r.cases*100/total);
                  return(
                    <tr key={r.reason}>
                      <td style={s.td}>{r.label}</td>
                      <td style={s.td}>{fmt(r.cases)}</td>
                      <td style={s.td}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <div style={{flex:1,height:6,background:T.borderSoft,borderRadius:3,overflow:'hidden',maxWidth:120}}>
                            <div style={{width:`${pct}%`,height:'100%',background:T.accent}}/>
                          </div>
                          <span style={{fontSize:12,color:T.textFaint}}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableCard>

          <TableCard title="Oldest Open Cases" count={oldest.length}>
            <table style={s.table}>
              <thead><tr>
                <th style={s.th}>Case</th><th style={s.th}>Status</th>
                <th style={s.th}>Product</th><th style={s.th}>Age</th><th style={s.th}>First Reply</th>
              </tr></thead>
              <tbody>
                {oldest.length===0&&<tr><td style={s.td} colSpan={5}><div style={s.empty}>Nothing open.</div></td></tr>}
                {oldest.map(c=>(
                  <tr key={c.ticket_id}>
                    <td style={s.td}>
                      <div style={{fontWeight:600,color:T.text}}>{c.subject||'(no subject)'}</div>
                      <div style={{fontSize:11,fontFamily:'monospace',color:T.textFaint}}>{c.ticket_id}</div>
                    </td>
                    <td style={s.td}><SupportChip label={String(c.status).replace(/_/g,' ')} tone={SUPPORT_STATUS_TONE[c.status]||T.textSub}/></td>
                    <td style={s.td}>{c.product}</td>
                    <td style={{...s.td,color:c.age_hours>72?T.red:c.age_hours>24?T.amber:T.textMid}}>
                      {c.age_hours===null?'-':`${c.age_hours}h`}
                    </td>
                    <td style={{...s.td,color:c.awaiting_first_response?T.amber:T.textMid}}>
                      {c.awaiting_first_response?'Awaiting':'Sent'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        </div>

        <div style={{width:290,flexShrink:0}}>
          {/* How much support VISINVOKE needs, relative to the actual population. */}
          <IntelPanel title="Contact Rate">
            <IntelKV label="Active Users" val={rate.active_users===null||rate.active_users===undefined?'-':fmt(rate.active_users)}/>
            <IntelKV label="Cases" val={fmt(rate.cases)}/>
            <IntelKV label="Per 1,000 Users" val={supportRate(rate.cases_per_1000_users)}/>
            <IntelKV label="% Contacting" val={supportRate(rate.pct_users_contacting,'%')}/>
            <IntelKV label="Repeat Contact" val={supportRate(rate.repeat_contact_rate_pct,'%')}/>
            {!rate.denominator_available&&(
              <div style={{fontSize:12,color:T.textFaint,marginTop:6,lineHeight:1.5}}>
                The user population could not be read, so no rate is reported. A fabricated rate would be worse than none.
              </div>
            )}
          </IntelPanel>

          <IntelPanel title="Response Times">
            <IntelKV label="Median First Reply" val={supportDuration(fr.median_minutes)}/>
            <IntelKV label="Mean First Reply" val={supportDuration(fr.mean_minutes)}/>
            <IntelKV label="Sample" val={fmt(fr.sample)}/>
            <IntelKV label="Awaiting First Reply" val={fmt(fr.awaiting_first_response)}
              color={fr.awaiting_first_response>0?T.amber:undefined}/>
            <IntelKV label="Median Resolution" val={supportDuration(res.median_minutes)}/>
            <IntelKV label="Resolved Sample" val={fmt(res.sample)}/>
          </IntelPanel>

          {/* Friction is the product-improvement signal, not a staff scorecard. */}
          <IntelPanel title="Product Friction">
            <IntelKV label="Reopened Rate" val={supportRate(friction.reopened_rate_pct,'%')}
              color={friction.reopened_rate_pct>10?T.amber:undefined}/>
            <IntelKV label="Escalation Rate" val={supportRate(friction.escalation_rate_pct,'%')}
              color={friction.escalation_rate_pct>15?T.red:undefined}/>
            {(friction.help_topics_most_sent||[]).length>0&&(
              <>
                <div style={{fontSize:12,fontWeight:700,color:T.textSub,marginTop:8,marginBottom:4}}>
                  Help Topics Most Sent
                </div>
                {(friction.help_topics_most_sent||[]).slice(0,5).map((h,i)=>(
                  <div key={i} style={{fontSize:12,color:T.textMid,padding:'2px 0'}}>
                    {h.topic} - {fmt(h.count)}
                  </div>
                ))}
                <div style={{fontSize:11,color:T.textFaint,marginTop:5,lineHeight:1.5}}>
                  Customers contacting support despite the answer existing points at where the answer is hard to find.
                </div>
              </>
            )}
          </IntelPanel>

          <IntelPanel title="Operator Workload">
            {operators.length===0&&<div style={{fontSize:13,color:T.textFaint}}>No assigned cases.</div>}
            {operators.map(o=>(
              <div key={o.operator_id} style={{padding:'5px 0',borderBottom:`1px solid ${T.borderSoft}`,fontSize:13}}>
                <div style={{fontWeight:600,color:T.text,fontSize:12,fontFamily:'monospace',wordBreak:'break-all'}}>{o.operator_id}</div>
                <div style={{fontSize:12,color:T.textFaint}}>
                  {fmt(o.assigned)} assigned - {fmt(o.open)} open - {fmt(o.resolved)} resolved
                </div>
              </div>
            ))}
            <div style={{fontSize:11,color:T.textFaint,marginTop:6,lineHeight:1.5}}>
              Workload, for balancing. Not a performance score.
            </div>
          </IntelPanel>

          {series.length>0&&(
            <IntelPanel title="Case Volume (30 days)">
              <div style={{display:'flex',alignItems:'flex-end',gap:2,height:60,marginTop:4}}>
                {series.map(d=>(
                  <div key={d.date} title={`${d.date}: ${d.cases}`}
                    style={{flex:1,height:`${Math.max(2,(d.cases/peak)*100)}%`,
                      background:d.cases>0?T.accent:T.borderSoft,borderRadius:'2px 2px 0 0'}}/>
                ))}
              </div>
              <div style={{fontSize:11,color:T.textFaint,marginTop:6}}>Peak {peak} in a business day.</div>
            </IntelPanel>
          )}
        </div>
      </div>

      <div style={{marginTop:12,fontSize:12,color:T.textFaint,lineHeight:1.6,maxWidth:820}}>
        {data.note}
      </div>
    </>
  );
}


// ---------------------------------------------------------------------------
// The policy-aware saved-reply library.
// ---------------------------------------------------------------------------
// Distinct from the older canned-responses picker, which is unchanged and still
// present. A reply flagged `uses_policy_resolver` takes its refund/cancellation
// wording from the customer's RESOLVED jurisdiction at render time, so a stored
// reply can never state a policy that is wrong for the person reading it.
//
// When that resolution fails the server says so, and this panel will not let the
// reply be inserted as if it were complete: sending a refund answer with the
// policy paragraph silently missing is its own kind of wrong answer.
function SupportSavedReplies({api,ticketId,reason,product,onInsert}){
  const[list,setList]=useState(null);
  const[open,setOpen]=useState(false);
  const[rendered,setRendered]=useState(null);
  const[busy,setBusy]=useState(false);
  const[err,setErr]=useState('');

  useEffect(()=>{
    if(!open||list)return;
    const p=new URLSearchParams();
    if(reason)p.set('reason',reason);
    if(product)p.set('product',product);
    fetch(`${api}/v1/staff/support/saved-replies?${p}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setList(d.saved_replies||[]);else setErr(d.error||'Unavailable');})
      .catch(()=>setErr('Network error'));
  },[api,open,list,reason,product]);

  const render=(slug)=>{
    setBusy(true);setErr('');setRendered(null);
    fetch(`${api}/v1/staff/support/saved-replies/${slug}/render`,{
      method:'POST',credentials:'include',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({ticket_id:ticketId}),
    }).then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setRendered(d);else setErr(d.error||'Render failed');})
      .catch(()=>setErr('Network error'))
      .finally(()=>setBusy(false));
  };

  return(
    <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,padding:'10px 12px',marginBottom:10}}>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:12,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em'}}>
          Saved Replies
        </span>
        <span style={{fontSize:11,color:T.textFaint}}>policy resolved per customer</span>
        <div style={{flex:1}}/>
        <button style={{...s.btn(),fontSize:12,padding:'4px 10px'}} onClick={()=>setOpen(v=>!v)}>
          {open?'Hide':'Browse'}
        </button>
      </div>

      {open&&(
        <div style={{marginTop:10}}>
          {err&&<div style={{...s.error,marginBottom:8}}>! {err}</div>}
          {!list&&!err&&<div style={{fontSize:13,color:T.textFaint}}>Loading...</div>}
          {list&&list.length===0&&(
            <div style={{fontSize:13,color:T.textFaint}}>
              No saved replies yet. Curating the library needs support.saved_replies.manage.
            </div>
          )}
          <div style={{display:'flex',flexDirection:'column',gap:4,maxHeight:200,overflowY:'auto'}}>
            {(list||[]).map(r=>(
              <div key={r.slug} style={{border:`1px solid ${T.borderSoft}`,borderRadius:5,padding:'7px 9px',display:'flex',alignItems:'center',gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.text}}>{r.title}</div>
                  <div style={{fontSize:11,color:T.textFaint}}>
                    {r.reason||'ANY'}{r.uses_policy_resolver?' - policy resolved':''}
                  </div>
                </div>
                <button style={{...s.btn(),fontSize:12,padding:'3px 10px'}} disabled={busy}
                  onClick={()=>render(r.slug)}>Preview</button>
              </div>
            ))}
          </div>

          {rendered&&(
            <div style={{marginTop:10,border:`1px solid ${rendered.ready_to_send?T.borderSoft:T.amberBorder}`,
              background:rendered.ready_to_send?T.surface:T.amberBg,borderRadius:5,padding:'9px 11px'}}>
              {!rendered.ready_to_send&&(
                <div style={{fontSize:12,fontWeight:700,color:T.amber,marginBottom:6,lineHeight:1.5}}>
                  {rendered.warning}
                </div>
              )}
              <div style={{fontSize:13,color:T.textMid,lineHeight:1.6,whiteSpace:'pre-wrap',maxHeight:180,overflowY:'auto'}}>
                {rendered.rendered}
                {rendered.policy_text?`\n\n${rendered.policy_text}`:''}
              </div>
              {(rendered.unresolved_placeholders||[]).length>0&&(
                <div style={{fontSize:11,color:T.textFaint,marginTop:6}}>
                  Unresolved: {(rendered.unresolved_placeholders||[]).join(', ')} - these stay visible rather than becoming blanks.
                </div>
              )}
              <div style={{display:'flex',gap:8,marginTop:8,alignItems:'center'}}>
                <button style={s.btn('primary')} disabled={!rendered.ready_to_send}
                  onClick={()=>{
                    onInsert(rendered.rendered+(rendered.policy_text?`\n\n${rendered.policy_text}`:''));
                    setRendered(null);setOpen(false);
                  }}>
                  Insert into Reply
                </button>
                {!rendered.ready_to_send&&(
                  <span style={{fontSize:12,color:T.amber}}>
                    Blocked: the policy paragraph is missing.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// UNIVERSAL PRODUCTS
// ---------------------------------------------------------------------------
// Every VISINVOKE product in one place - paid, free, internal-currency,
// pre-launch and retired. A free utility is a first-class product here: it has
// identity, access, usage and support, and NO commercial pathway, which is
// correct rather than missing.
//
// The two rules this UI exists to keep:
//   * a free product shows FREE / N/A for revenue, never a misleading A$0;
//   * usage that could not be read shows UNAVAILABLE, never "never used" -
//     those are different facts and only one of them is about the customer.
// ---------------------------------------------------------------------------

const LAUNCH_TONE = {
  LIVE_FREE:'#38A169', LIVE_PAID:'#2D6CFF', LIVE_INTERNAL_CURRENCY:'#805AD5',
  PRELAUNCH:'#D69E2E', RETIRED:'#718096',
};
const LAUNCH_LABEL = {
  LIVE_FREE:'LIVE · FREE', LIVE_PAID:'LIVE · PAID',
  LIVE_INTERNAL_CURRENCY:'LIVE · CREDITS', PRELAUNCH:'PRE-LAUNCH', RETIRED:'RETIRED',
};
const ACCESS_TONE = {
  FREE_PRODUCT:'#38A169', LIFETIME_PURCHASE:'#2D6CFF', ACTIVE_SUBSCRIPTION:'#2D6CFF',
  CREDIT_BALANCE:'#805AD5', SHOP_CREDIT_OWNERSHIP:'#805AD5', SCC_ENTITLEMENT:'#0EA5E9',
  ADMIN_GRANTED:'#D69E2E', PRELAUNCH_DENIED:'#D69E2E', EXPIRED:'#E53E3E',
  RETIRED_HISTORICAL:'#718096', NO_ACCESS:'#718096',
};

function ProductChip({label,tone,title}){
  const c = tone || T.textSub;
  return(
    <span title={title||undefined} style={{
      display:'inline-block',padding:'2px 9px',borderRadius:999,fontSize:11,fontWeight:700,
      letterSpacing:'0.04em',whiteSpace:'nowrap',
      color:c,background:`${c}1F`,border:`1px solid ${c}66`,
    }}>{label}</span>
  );
}

// A figure that could not be read is NOT zero. Rendering them the same way is how an owner ends up
// believing nobody uses something that simply could not be counted.
function usageCell(usage){
  if(!usage) return <span style={{color:T.textFaint}}>-</span>;
  if(usage.available===false){
    return <span title={usage.reason||''} style={{color:T.amber,fontWeight:600}}>UNAVAILABLE</span>;
  }
  // An enquiry-driven managed service is delivered off-platform, so the platform can see that the
  // customer ENGAGED but never that the service ran. Rendering that as a use count would tell the
  // owner somebody is protected when nothing has been installed.
  if(usage.delivered_use_provable===false){
    const e=usage.engagement||{};
    return e.ever_engaged
      ? <span style={{color:'#805AD5',fontWeight:600}}
              title="Engaged (enquiry). Delivery happens off-platform, so use cannot be proven.">
          ENGAGED{e.engagement_count>1?` ×${fmt(e.engagement_count)}`:''}
        </span>
      : <span style={{color:T.textFaint}} title="No enquiry recorded. Use cannot be proven either way.">
          No engagement
        </span>;
  }
  if(!usage.ever_used) return <span style={{color:T.textFaint}}>Never used</span>;
  return <span>{fmt(usage.meaningful_use_count)}</span>;
}

function whenCell(value){
  return value ? <span title={value}>{fmtDate(value)}</span>
               : <span style={{color:T.textFaint}}>-</span>;
}

// FREE / N/A, never A$0. A zero revenue figure for a free utility is a fact about nothing.
function revenueCell(commercial){
  if(!commercial) return <span style={{color:T.textFaint}}>-</span>;
  if(!commercial.applicable){
    return <span title={commercial.reason||''} style={{color:T.textFaint,fontWeight:600}}>N/A</span>;
  }
  if(commercial.internal_currency){
    return(
      <span style={{color:'#805AD5'}} title="Internal currency - not cash revenue">
        {fmt(commercial.internal_currency_spent)} credits
      </span>
    );
  }
  const amounts=commercial.amount_minor_by_currency||{};
  const parts=Object.keys(amounts).map(c=>`${(amounts[c]/100).toFixed(2)} ${c}`);
  return <span>{parts.length?parts.join(' · '):'0.00'}</span>;
}

// ---------------------------------------------------------------------------
// SecProductsOverview
// ---------------------------------------------------------------------------
function SecProductsOverview({api}){
  const[data,setData]=useState(null);
  const[err,setErr]=useState(null);
  const[page,setPage]=useState(1);
  const[selected,setSelected]=useState(null);
  const[filter,setFilter]=useState('');

  useEffect(()=>{
    setData(null);setErr(null);
    fetch(`${api}/v1/staff/products/overview?page=${page}&page_size=50`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api,page]);

  if(selected)return<ProductDetail api={api} productKey={selected} onBack={()=>setSelected(null)}/>;
  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const totals=data.totals_by_launch_status||{};
  const rows=(data.products||[]).filter(p=>!filter||p.launch_status===filter);

  return(
    <>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:10,marginBottom:14}}>
        <RadialKPI label="Products"        value={fmt(data.total)} color="blue"/>
        <RadialKPI label="Live · Free"     value={fmt(totals.LIVE_FREE)} color="green"/>
        <RadialKPI label="Live · Paid"     value={fmt(totals.LIVE_PAID)} color="blue"/>
        <RadialKPI label="Live · Credits"  value={fmt(totals.LIVE_INTERNAL_CURRENCY)} color="purple"/>
        <RadialKPI label="Pre-launch"      value={fmt(totals.PRELAUNCH)} color="amber"/>
        <RadialKPI label="Retired"         value={fmt(totals.RETIRED)} color="green"/>
      </div>

      <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{fontSize:12,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em'}}>Launch state</span>
        {['','LIVE_FREE','LIVE_PAID','LIVE_INTERNAL_CURRENCY','PRELAUNCH','RETIRED'].map(v=>(
          <button key={v||'all'} onClick={()=>setFilter(v)}
            style={{padding:'5px 14px',borderRadius:999,fontSize:13,fontWeight:600,cursor:'pointer',
              border:`1px solid ${filter===v?T.accent:T.border}`,
              background:filter===v?T.accentLight:T.surface,
              color:filter===v?T.accent:T.textSub}}>
            {v?(LAUNCH_LABEL[v]||v):'All'}
          </button>
        ))}
      </div>

      <TableCard title="VISINVOKE Products" count={rows.length}>
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Product</th>
            <th style={s.th}>State</th>
            <th style={s.th}>Access</th>
            <th style={s.th}>Users With Access</th>
            <th style={s.th}>Users Who Used It</th>
            <th style={s.th}>Last Activity</th>
            <th style={s.th}>Revenue</th>
            <th style={s.th}>Support</th>
          </tr></thead>
          <tbody>
            {rows.length===0&&<tr><td style={s.td} colSpan={8}><div style={s.empty}>No products in this state.</div></td></tr>}
            {rows.map(p=>{
              const usage=p.usage||{};
              const holders=p.access_holders||{};
              const support=p.support||{};
              return(
                <tr key={p.product_key} style={{cursor:'pointer'}} onClick={()=>setSelected(p.product_key)}>
                  <td style={s.td}>
                    <div style={{fontWeight:600,color:T.text}}>{p.display_name}</div>
                    <div style={{fontSize:12,color:T.textFaint}}>{p.family} · {p.product_key}</div>
                  </td>
                  <td style={s.td}>
                    <ProductChip label={LAUNCH_LABEL[p.launch_status]||p.launch_status}
                      tone={LAUNCH_TONE[p.launch_status]}/>
                  </td>
                  <td style={s.td}>
                    <ProductChip label={String(p.access_authority||'-').replace(/_/g,' ')}
                      tone={ACCESS_TONE[p.access_authority]}/>
                  </td>
                  <td style={s.td}>
                    {holders.count===null||holders.count===undefined
                      ? <span style={{color:T.textFaint}} title={holders.basis||''}>
                          {p.launch_status==='PRELAUNCH'?'NOT AVAILABLE YET':'-'}
                        </span>
                      : <span title={holders.basis||''}>{fmt(holders.count)}</span>}
                  </td>
                  <td style={s.td}>
                    {usage.available===false
                      ? <span style={{color:T.amber,fontWeight:600}} title={usage.reason||''}>UNAVAILABLE</span>
                      : usage.delivered_use_provable===false
                        ? <span style={{color:'#805AD5',fontWeight:600}} title={usage.note||''}>
                            {fmt((usage.engagement||{}).users_engaged)} ENGAGED
                          </span>
                        : usage.users_ever_used===null||usage.users_ever_used===undefined
                          ? <span style={{color:T.textFaint}} title={usage.note||''}>see detail</span>
                          : fmt(usage.users_ever_used)}
                  </td>
                  <td style={s.td}>{whenCell(usage.last_observed_at)}</td>
                  <td style={s.td}>{revenueCell(p.commercial)}</td>
                  <td style={s.td}>
                    {fmt(support.cases)}
                    {support.escalated>0&&<span style={{color:T.red,fontSize:12}}> · {fmt(support.escalated)} esc</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>
      {data.total>data.page_size&&(
        <Pager page={data.page} pages={Math.ceil(data.total/data.page_size)} total={data.total}
          onPage={setPage}/>
      )}
      <div style={{fontSize:12,color:T.textFaint,marginTop:10,lineHeight:1.6,maxWidth:860}}>
        A free product shows N/A for revenue because it has no commercial pathway - that is correct,
        not missing. Usage that could not be read shows UNAVAILABLE, which is not the same as never
        used.
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// ProductDetail
// ---------------------------------------------------------------------------
function ProductDetail({api,productKey,onBack}){
  const[data,setData]=useState(null);
  const[err,setErr]=useState(null);

  useEffect(()=>{
    setData(null);setErr(null);
    fetch(`${api}/v1/staff/products/${productKey}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api,productKey]);

  if(err)return<><button style={s.btn()} onClick={onBack}>&lt;- Back</button><div style={{...s.error,marginTop:12}}>! {err}</div></>;
  if(!data)return<Loader/>;

  const id=data.identity||{};
  const usage=data.usage||{};
  const holders=data.access_holders||{};
  const commercial=data.commercial||{};
  const support=data.support||{};

  return(
    <>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,flexWrap:'wrap'}}>
        <button style={s.btn()} onClick={onBack}>&lt;- Back to Products</button>
        <div style={{flex:1}}/>
        <ProductChip label={LAUNCH_LABEL[id.launch_status]||id.launch_status} tone={LAUNCH_TONE[id.launch_status]}/>
        <ProductChip label={String(id.access_authority||data.access_authority||'-').replace(/_/g,' ')}
          tone={ACCESS_TONE[data.access_authority]}/>
      </div>

      <div style={{...s.panel,marginBottom:12}}>
        <div style={{fontSize:19,fontWeight:700,color:T.text}}>{id.display_name}</div>
        <div style={{fontSize:13,color:T.textSub,marginTop:4}}>
          {id.family} · {id.product_key}{id.model?` · ${id.model}`:''}
        </div>
        {id.launch_override_reason&&(
          <div style={{marginTop:8,padding:'8px 11px',background:T.amberBg,border:`1px solid ${T.amberBorder}`,borderRadius:6,fontSize:13,color:T.textMid,lineHeight:1.55}}>
            Launch state set deliberately: {id.launch_override_reason}
          </div>
        )}
        {(id.historical_aliases||[]).length>0&&(
          <div style={{fontSize:12,color:T.textFaint,marginTop:8}}>
            Also known as: {(id.historical_aliases||[]).join(', ')} — historical records using these
            names still resolve here.
          </div>
        )}
      </div>

      <div style={{display:'flex',gap:12,alignItems:'flex-start',flexWrap:'wrap'}}>
        <div style={{flex:'1 1 380px',minWidth:0}}>
          <IntelPanel title="Access">
            <IntelKV label="How access is decided" val={String(data.access_authority||'-').replace(/_/g,' ')}/>
            <IntelKV label="Basis" val={holders.basis||'-'}/>
            <IntelKV label="Accounts with access"
              val={holders.count===null||holders.count===undefined
                ? (id.launch_status==='PRELAUNCH'?'NOT AVAILABLE YET':'-')
                : fmt(holders.count)}/>
            <IntelKV label="Entitlement authority"
              val={<span style={{fontSize:12,lineHeight:1.5}}>{id.entitlement_authority||'-'}</span>}/>
          </IntelPanel>

          <IntelPanel title="Usage">
            {usage.available===false?(
              <div style={{fontSize:13,color:T.amber,lineHeight:1.55}}>
                USAGE UNAVAILABLE — {usage.reason||'no reason given'}. This is not the same as never used.
              </div>
            ):(
              <>
                <IntelKV label="Proven by" val={usage.authority||'-'}/>
                <IntelKV label="Users ever used"
                  val={usage.users_ever_used===null||usage.users_ever_used===undefined
                    ? 'not derived' : fmt(usage.users_ever_used)}/>
                {usage.active&&Object.keys(usage.active).map(k=>(
                  <IntelKV key={k} label={`Active ${k}`} val={fmt(usage.active[k])}/>
                ))}
                <IntelKV label="Meaningful operations" val={fmt(usage.meaningful_operations)}/>
                <IntelKV label="First observed" val={usage.first_observed_at?fmtDate(usage.first_observed_at):'-'}/>
                <IntelKV label="Last observed" val={usage.last_observed_at?fmtDate(usage.last_observed_at):'-'}/>
                {usage.note&&<div style={{fontSize:12,color:T.textFaint,marginTop:6,lineHeight:1.5}}>{usage.note}</div>}
              </>
            )}
          </IntelPanel>
        </div>

        <div style={{width:320,flexShrink:0}}>
          <IntelPanel title="Commercial">
            {!commercial.applicable?(
              <div style={{fontSize:13,color:T.textFaint,lineHeight:1.55}}>
                <strong style={{color:T.textSub}}>NOT APPLICABLE</strong> — {commercial.reason}
              </div>
            ):(
              <>
                <IntelKV label="Transactions" val={fmt(commercial.transactions)}/>
                {commercial.internal_currency?(
                  <>
                    <IntelKV label="Credits spent" val={fmt(commercial.internal_currency_spent)}/>
                    <div style={{fontSize:12,color:'#805AD5',marginTop:6,lineHeight:1.5}}>{commercial.note}</div>
                  </>
                ):Object.keys(commercial.amount_minor_by_currency||{}).map(c=>(
                  <IntelKV key={c} label={`Revenue ${c}`}
                    val={((commercial.amount_minor_by_currency[c]||0)/100).toFixed(2)}/>
                ))}
              </>
            )}
          </IntelPanel>

          <IntelPanel title="Support">
            <IntelKV label="Cases" val={fmt(support.cases)}/>
            <IntelKV label="Escalated" val={fmt(support.escalated)} color={support.escalated>0?T.red:undefined}/>
            <IntelKV label="Escalation rate" val={`${support.escalation_rate_pct||0}%`}/>
            {(data.support_reasons||[]).length>0&&(
              <>
                <div style={{fontSize:12,fontWeight:700,color:T.textSub,marginTop:8,marginBottom:4}}>Top reasons</div>
                {(data.support_reasons||[]).slice(0,5).map(r=>(
                  <div key={r.reason} style={{fontSize:12,color:T.textMid,padding:'2px 0'}}>
                    {r.label} — {fmt(r.cases)}
                  </div>
                ))}
              </>
            )}
          </IntelPanel>

          <IntelPanel title="Derived From">
            {(id.sources||[]).map((src,i)=>(
              <div key={i} style={{fontSize:11,color:T.textFaint,padding:'2px 0',wordBreak:'break-all'}}>{src}</div>
            ))}
          </IntelPanel>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// AccountProductsPanel
// ---------------------------------------------------------------------------
// Products / Access / Usage for ONE account, free products included. This is the
// panel that answers "does this user have File Converter and have they used it"
// without making the owner open five screens.
function AccountProductsPanel({api,userId}){
  const[data,setData]=useState(null);
  const[err,setErr]=useState(null);
  const[showAll,setShowAll]=useState(false);

  useEffect(()=>{
    if(!userId)return;
    fetch(`${api}/v1/staff/products/account/${userId}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Unavailable');})
      .catch(()=>setErr('Network error'));
  },[api,userId]);

  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const summary=data.summary||{};
  const rows=(data.products||[]).filter(r=>showAll||r.available||(r.usage||{}).ever_used);

  return(
    <>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))',gap:10,marginBottom:12}}>
        <RadialKPI label="Products Available" value={fmt(summary.products_available)} color="blue"/>
        <RadialKPI label="Paid Products Owned" value={fmt(summary.paid_products_owned)} color="blue"/>
        <RadialKPI label="Free Products Used" value={fmt(summary.free_products_used)} color="green"/>
        <RadialKPI label="Engaged, Not Delivered" value={fmt(summary.products_engaged_not_delivered)}
          color="purple"/>
        <RadialKPI label="Shop Credits"
          value={summary.shop_credit_balance===null||summary.shop_credit_balance===undefined?'-':fmt(summary.shop_credit_balance)}
          color="purple"/>
        <RadialKPI label="Open Support Cases" value={fmt(summary.open_support_cases)}
          color={summary.open_support_cases>0?'amber':'green'}/>
        <RadialKPI label="Last Activity"
          value={summary.last_platform_activity?fmtDate(summary.last_platform_activity):'Never'}
          color="green"/>
      </div>

      <TableCard title="Products / Access / Usage" count={rows.length} action={
        <button style={{...s.btn(),fontSize:12,padding:'4px 10px'}} onClick={()=>setShowAll(v=>!v)}>
          {showAll?'Only relevant':'Show every product'}
        </button>
      }>
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Product</th><th style={s.th}>Access</th><th style={s.th}>Why</th>
            <th style={s.th}>Uses</th><th style={s.th}>First</th><th style={s.th}>Last</th>
            <th style={s.th}>Purchase</th><th style={s.th}>Support</th>
          </tr></thead>
          <tbody>
            {rows.length===0&&<tr><td style={s.td} colSpan={8}><div style={s.empty}>No products.</div></td></tr>}
            {rows.map(r=>{
              const commercial=r.commercial||{};
              const support=r.support||{};
              return(
                <tr key={r.product_key}>
                  <td style={s.td}>
                    <div style={{fontWeight:600,color:T.text}}>{r.display_name}</div>
                    <div style={{fontSize:12,color:T.textFaint}}>
                      {r.family} · {LAUNCH_LABEL[r.launch_status]||r.launch_status}
                    </div>
                  </td>
                  <td style={s.td}>
                    {r.available===null
                      ? <span style={{color:T.amber,fontWeight:600}}>UNKNOWN</span>
                      : r.available
                        ? <span style={{color:T.green,fontWeight:700}}>Yes</span>
                        : <span style={{color:T.textFaint}}>No</span>}
                  </td>
                  <td style={s.td}>
                    <ProductChip label={String(r.access_reason||'-').replace(/_/g,' ')}
                      tone={ACCESS_TONE[r.access_reason]} title={r.access_detail||''}/>
                  </td>
                  <td style={s.td}>{usageCell(r.usage)}</td>
                  <td style={s.td}>{whenCell((r.usage||{}).first_used_at)}</td>
                  <td style={s.td}>{whenCell((r.usage||{}).last_used_at)}</td>
                  <td style={s.td}>
                    {!commercial.applicable
                      ? <span style={{color:T.textFaint,fontWeight:600}} title={commercial.reason||''}>FREE / N/A</span>
                      : <>
                          {fmt(commercial.transactions)}
                          {commercial.paid_not_granted>0&&
                            <span style={{color:T.red,fontSize:12}}> · {fmt(commercial.paid_not_granted)} NOT GRANTED</span>}
                        </>}
                  </td>
                  <td style={s.td}>
                    {fmt(support.total_cases)}
                    {support.open_cases>0&&<span style={{color:T.amber,fontSize:12}}> · {fmt(support.open_cases)} open</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>
      <div style={{fontSize:12,color:T.textFaint,marginTop:8,lineHeight:1.6,maxWidth:860}}>{data.note}</div>
    </>
  );
}

// ---------------------------------------------------------------------------
// SecProductRegistry
// ---------------------------------------------------------------------------
// The canonical identity itself: one row per product, every alias, and the
// authority each entry came from. This is what makes "derived, not maintained"
// something the owner can check rather than something they have to believe.
function SecProductRegistry({api}){
  const[data,setData]=useState(null);
  const[err,setErr]=useState(null);

  useEffect(()=>{
    fetch(`${api}/v1/staff/products/registry`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api]);

  if(err)return<div style={s.error}>! {err}</div>;
  if(!data)return<Loader/>;

  const aliases=data.aliases||{};
  const aliasKeys=Object.keys(aliases);

  return(
    <>
      <TableCard title="Canonical Product Identity" count={(data.products||[]).length}>
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Key</th><th style={s.th}>Product</th><th style={s.th}>State</th>
            <th style={s.th}>Purchase Types</th><th style={s.th}>Usage Source</th>
            <th style={s.th}>Derived From</th>
          </tr></thead>
          <tbody>
            {(data.products||[]).map(p=>(
              <tr key={p.product_key}>
                <td style={{...s.td,fontFamily:'monospace',fontSize:12}}>{p.product_key}</td>
                <td style={s.td}>
                  <div style={{fontWeight:600,color:T.text}}>{p.display_name}</div>
                  <div style={{fontSize:12,color:T.textFaint}}>{p.family}</div>
                </td>
                <td style={s.td}><ProductChip label={LAUNCH_LABEL[p.launch_status]||p.launch_status} tone={LAUNCH_TONE[p.launch_status]}/></td>
                <td style={{...s.td,fontSize:12}}>
                  {(p.purchase_types||[]).length?(p.purchase_types||[]).join(', ')
                    :<span style={{color:T.textFaint}}>none — not a commercial product</span>}
                </td>
                <td style={{...s.td,fontSize:12}}>
                  {p.usage_source}{p.usage_authority?` · ${p.usage_authority}`:''}
                </td>
                <td style={{...s.td,fontSize:11,color:T.textFaint}}>
                  {(p.sources||[]).join(' · ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>

      <TableCard title="Historical Aliases" count={aliasKeys.length}>
        <table style={s.table}>
          <thead><tr><th style={s.th}>Historical identifier</th><th style={s.th}>Resolves to</th></tr></thead>
          <tbody>
            {aliasKeys.length===0&&<tr><td style={s.td} colSpan={2}><div style={s.empty}>No aliases.</div></td></tr>}
            {aliasKeys.map(a=>(
              <tr key={a}>
                <td style={{...s.td,fontFamily:'monospace',fontSize:12}}>{a}</td>
                <td style={{...s.td,fontFamily:'monospace',fontSize:12}}>{aliases[a]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>

      <div style={{fontSize:12,color:T.textFaint,marginTop:10,lineHeight:1.6,maxWidth:860}}>
        {data.note} A historical purchase or support case recorded under an old identifier still
        resolves to its canonical product — the evidence is never rewritten.
      </div>
    </>
  );
}

function SupportTicketDetail({api,ticketId,onBack,hasPermission}){
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[intel,setIntel]=useState(null);
  const[noteText,setNoteText]=useState('');
  const[submitting,setSubmitting]=useState(false);
  const[actionMsg,setActionMsg]=useState('');const[actionErr,setActionErr]=useState('');
  const[newStatus,setNewStatus]=useState('');
  const[newSeverity,setNewSeverity]=useState('');
  const[replyBody,setReplyBody]=useState('');
  const[replyPending,setReplyPending]=useState(false);
  const[replying,setReplying]=useState(false);
  const[replyMsg,setReplyMsg]=useState('');
  const[replyErr,setReplyErr]=useState('');
  const[canned,setCanned]=useState(null);
  const[cannedCat,setCannedCat]=useState('');
  const[cannedSelected,setCannedSelected]=useState(null);
  const[showCanned,setShowCanned]=useState(false);

  const load=useCallback(()=>{
    setData(null);setErr(null);
    fetch(`${api}/v1/staff/support/ticket/${ticketId}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api,ticketId]);

  useEffect(()=>{load();},[load]);

  // Load intelligence for right panel once ticket data arrives
  useEffect(()=>{
    if(!data||!data.ticket||!data.ticket.user_id||!hasPermission('intelligence.view'))return;
    fetch(`${api}/v1/staff/intelligence/${data.ticket.user_id}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setIntel(d);})
      .catch(()=>{});
  },[api,data,hasPermission]);

  // Lazy load canned responses when reply panel first opened
  useEffect(()=>{
    if(!showCanned||canned)return;
    fetch(`${api}/v1/staff/support/canned-responses`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setCanned(d);})
      .catch(()=>{});
  },[api,showCanned,canned]);

  const doPost=(url,body,successMsg)=>{
    setSubmitting(true);setActionMsg('');setActionErr('');
    fetch(`${api}${url}`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{
        if(ok){setActionMsg(successMsg);load();}
        else setActionErr(d.error||'Action failed');
      }).catch(()=>setActionErr('Network error'))
      .finally(()=>setSubmitting(false));
  };

  const handleNote=()=>{
    if(!noteText.trim())return;
    doPost(`/v1/staff/support/ticket/${ticketId}/note`,{note:noteText.trim()},'Note added.');
    setNoteText('');
  };

  const handleStatus=()=>{
    if(!newStatus&&!newSeverity)return;
    doPost(`/v1/staff/support/ticket/${ticketId}/status`,{status:newStatus||undefined,severity:newSeverity||undefined},'Ticket updated.');
    setNewStatus('');setNewSeverity('');
  };

  if(err)return<><button style={s.btn()} onClick={onBack}>&lt;- Back</button><div style={{...s.error,marginTop:12}}>! {err}</div></>;
  if(!data)return<Loader/>;

  const t=data.ticket;
  const thread=data.thread||[];
  const notes=data.notes||[];

  const canNote=hasPermission('support.note');
  const canStatus=hasPermission('support.status');
  const canReply=hasPermission('support.reply');

  const handleReply=()=>{
    if(!replyBody.trim())return;
    setReplying(true);setReplyMsg('');setReplyErr('');
    fetch(`${api}/v1/staff/support/ticket/${ticketId}/reply`,{
      method:'POST',credentials:'include',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({body:replyBody.trim(),set_pending:replyPending}),
    }).then(r=>r.json().then(d=>({ok:r.ok,d,status:r.status})))
    .then(({ok,d,status})=>{
      if(ok){
        setReplyMsg('Reply sent successfully.');
        setReplyBody('');
        setReplyPending(false);
        // Append sent reply into thread without full reload
        setData(prev=>prev?({
          ...prev,
          thread:[...prev.thread,{
            id:d.message_id,
            sender_type:'staff_reply',
            sender_id:d.actor_name,
            message:replyBody.trim(),
            created_at:d.created_at||new Date().toISOString(),
          }],
          ticket:{...prev.ticket,status:d.status||prev.ticket.status},
        }):prev);
      } else {
        setReplyErr(d.error||(status===422?'Recipient email is suppressed.':'Reply failed.'));
      }
    }).catch(()=>setReplyErr('Network error.'))
    .finally(()=>setReplying(false));
  };

  return(
    <>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
        <button style={s.btn()} onClick={onBack}>&lt;- Back to Queue</button>
        <div style={{flex:1}}/>
        <SupportStatusPill status={t.status}/>
        <SupportSeverityPill severity={t.severity}/>
      </div>

      {actionMsg&&<div style={{padding:'9px 14px',background:T.greenBg,border:`1px solid ${T.greenBorder}`,borderRadius:7,color:T.green,fontSize:14,fontWeight:600,marginBottom:10}}>{actionMsg}</div>}
      {actionErr&&<div style={{...s.error,marginBottom:10}}>! {actionErr}</div>}

      <SupportCaseOperations api={api} ticketId={ticketId} ticket={t} onChanged={load} hasPermission={hasPermission}/>

      <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>

        {/* LEFT: Thread + Notes + Actions */}
        <div style={{flex:1,minWidth:0}}>

          {/* Ticket header */}
          <div style={{...s.panel,marginBottom:10,padding:'14px 16px'}}>
            <div style={{fontSize:17,fontWeight:700,color:T.text,marginBottom:4}}>{t.subject||'(no subject)'}</div>
            <div style={{display:'flex',gap:12,fontSize:13,color:T.textSub,flexWrap:'wrap'}}>
              <span>#{t.ticket_id}</span>
              <span>{t.type}</span>
              <span>{t.user_email}</span>
              <span>Opened {openTime(t.created_at)} ago</span>
              {t.assignee_name&&<span>Assigned: {t.assignee_name}</span>}
            </div>
          </div>

          {/* Original message */}
          <div style={{...s.panel,marginBottom:10,padding:'14px 16px',borderLeft:`3px solid ${T.accentBorder}`}}>
            <div style={{fontSize:12,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:8}}>User Message</div>
            <div style={{fontSize:14,color:T.text,lineHeight:1.65,whiteSpace:'pre-wrap'}}>{t.message}</div>
          </div>

          {/* Thread messages - user messages and staff replies */}
          {thread.length>0&&(
            <TableCard title="Thread" count={thread.length}>
              <div style={{padding:'8px 0'}}>
                {thread.map((m,i)=>{
                  const isUser=m.sender_type==='user';
                  const isReply=m.sender_type==='staff_reply';
                  const dotColor=isUser?T.chart2:isReply?T.accent:T.textFaint;
                  const label=isUser?'User':isReply?'Staff Reply':'Staff';
                  const labelColor=isUser?T.chart2:isReply?T.accent:T.textSub;
                  const bg=isReply?T.accentLight:'transparent';
                  return(
                    <div key={m.id||i} style={{padding:'10px 16px',borderBottom:`1px solid ${T.borderSoft}`,display:'flex',gap:10,background:bg}}>
                      <div style={{width:8,height:8,borderRadius:'50%',background:dotColor,flexShrink:0,marginTop:5}}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',gap:10,marginBottom:4,alignItems:'center'}}>
                          <span style={{fontSize:13,fontWeight:700,color:labelColor}}>{label}</span>
                          {isReply&&<span style={{fontSize:11,fontWeight:700,color:T.accent,background:T.accentLight,border:`1px solid ${T.accentBorder}`,borderRadius:3,padding:'1px 5px'}}>EMAIL SENT</span>}
                          <span style={{fontSize:12,color:T.textFaint}}>{fmtDate(m.created_at)}</span>
                        </div>
                        <div style={{fontSize:14,color:T.textMid,lineHeight:1.6,whiteSpace:'pre-wrap'}}>{m.message}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </TableCard>
          )}

          {/* Internal notes */}
          <TableCard title="Internal Staff Notes" count={notes.length}>
            <div style={{padding:'8px 0'}}>
              {notes.length===0&&<div style={{...s.empty,padding:'20px 0'}}>No internal notes yet.</div>}
              {notes.map((n,i)=>(
                <div key={n.id||i} style={{padding:'10px 16px',borderBottom:`1px solid ${T.borderSoft}`,background:T.amberBg,display:'flex',gap:10}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:T.amber,flexShrink:0,marginTop:5}}/>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',gap:10,marginBottom:4}}>
                      <span style={{fontSize:13,fontWeight:700,color:T.amber}}>{n.actor_name||n.sender_id||'Staff'}</span>
                      <span style={{fontSize:12,color:T.textFaint}}>{fmtDate(n.created_at)}</span>
                      <span style={{fontSize:11,fontWeight:700,color:T.amber,background:T.amberBg,border:`1px solid ${T.amberBorder}`,borderRadius:3,padding:'1px 5px'}}>INTERNAL</span>
                    </div>
                    <div style={{fontSize:14,color:T.textMid,lineHeight:1.6,whiteSpace:'pre-wrap'}}>{n.message}</div>
                  </div>
                </div>
              ))}
            </div>
            {/* Add note */}
            {canNote&&(
              <div style={{padding:'12px 16px',borderTop:`1px solid ${T.border}`}}>
                <div style={{fontSize:13,fontWeight:700,color:T.textSub,marginBottom:6}}>Add Internal Note</div>
                <textarea style={{...s.input,width:'100%',boxSizing:'border-box',minHeight:72,resize:'vertical',fontFamily:'inherit',fontSize:14}}
                  placeholder="Staff-only internal note..." value={noteText} onChange={e=>setNoteText(e.target.value)}/>
                <div style={{marginTop:8,display:'flex',justifyContent:'flex-end'}}>
                  <button style={s.btn('primary')} onClick={handleNote} disabled={submitting||!noteText.trim()}>{submitting?'Saving...':'Add Note'}</button>
                </div>
              </div>
            )}
          </TableCard>

          {/* Status / Severity update */}
          {canStatus&&(
            <div style={{...s.panel,marginTop:10,padding:'14px 16px'}}>
              <div style={{fontSize:14,fontWeight:700,color:T.textSub,marginBottom:10}}>Update Ticket</div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                <select style={s.select} value={newStatus} onChange={e=>setNewStatus(e.target.value)}>
                  <option value="">Status (unchanged)</option>
                  {['open','triaged','pending','escalated','resolved','closed'].map(st=><option key={st} value={st}>{st}</option>)}
                </select>
                <select style={s.select} value={newSeverity} onChange={e=>setNewSeverity(e.target.value)}>
                  <option value="">Severity (unchanged)</option>
                  {['low','medium','high','critical'].map(sv=><option key={sv} value={sv}>{sv}</option>)}
                </select>
                <button style={s.btn('primary')} onClick={handleStatus} disabled={submitting||(!newStatus&&!newSeverity)}>Update</button>
              </div>
            </div>
          )}

          {/* Staff Reply panel */}
          {canReply&&(
            <div style={{...s.panel,marginTop:10,padding:'14px 16px',borderLeft:`3px solid ${T.accent}`}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                <div style={{fontSize:14,fontWeight:700,color:T.accent}}>Send Reply to Customer</div>
                <span style={{fontSize:11,fontWeight:700,color:T.accent,background:T.accentLight,border:`1px solid ${T.accentBorder}`,borderRadius:3,padding:'1px 6px'}}>EMAIL</span>
                <div style={{marginLeft:'auto'}}>
                  <button style={{...s.btn(),fontSize:12,padding:'4px 10px',color:showCanned?T.accent:T.textSub,borderColor:showCanned?T.accentBorder:T.border}}
                    onClick={()=>setShowCanned(v=>!v)}>
                    {showCanned?'Hide Templates':'Canned Responses'}
                  </button>
                </div>
              </div>

              {/* Canned response selector */}
              {showCanned&&(
                <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,padding:'10px 12px',marginBottom:10}}>
                  <div style={{display:'flex',gap:8,marginBottom:8,alignItems:'center',flexWrap:'wrap'}}>
                    <span style={{fontSize:12,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em'}}>Category</span>
                    {!canned?(
                      <span style={{fontSize:12,color:T.textFaint}}>Loading...</span>
                    ):(canned.categories||[]).map(cat=>(
                      <button key={cat} onClick={()=>{setCannedCat(c=>c===cat?'':cat);setCannedSelected(null);}}
                        style={{padding:'3px 10px',borderRadius:12,fontSize:12,fontWeight:600,cursor:'pointer',border:`1px solid ${cannedCat===cat?T.accent:T.border}`,background:cannedCat===cat?T.accentLight:'transparent',color:cannedCat===cat?T.accent:T.textSub}}>
                        {cat.replace(/_/g,' ')}
                      </button>
                    ))}
                  </div>
                  {canned&&(()=>{
                    const filtered=(canned.canned_responses||[]).filter(r=>!cannedCat||r.category===cannedCat);
                    return(
                      <div style={{display:'flex',flexDirection:'column',gap:4,maxHeight:220,overflowY:'auto'}}>
                        {filtered.length===0&&<div style={{fontSize:13,color:T.textFaint,padding:'8px 0'}}>No templates in this category.</div>}
                        {filtered.map(r=>{
                          const isSelected=cannedSelected&&cannedSelected.id===r.id;
                          return(
                            <div key={r.id} style={{border:`1px solid ${isSelected?T.accent:T.borderSoft}`,borderRadius:5,padding:'8px 10px',cursor:'pointer',background:isSelected?T.accentLight:'transparent'}}
                              onClick={()=>setCannedSelected(isSelected?null:r)}>
                              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
                                <span style={{fontSize:13,fontWeight:700,color:isSelected?T.accent:T.text}}>{r.title}</span>
                                <span style={{fontSize:11,color:T.textFaint,background:T.bg,border:`1px solid ${T.border}`,borderRadius:3,padding:'1px 6px',whiteSpace:'nowrap'}}>{r.category.replace(/_/g,' ')}</span>
                              </div>
                              {isSelected&&(
                                <>
                                  <div style={{fontSize:12,color:T.textSub,marginTop:4,marginBottom:6,fontStyle:'italic'}}>Preview:</div>
                                  <div style={{fontSize:12,color:T.textMid,lineHeight:1.55,whiteSpace:'pre-wrap',maxHeight:100,overflow:'hidden',background:T.surface,border:`1px solid ${T.borderSoft}`,borderRadius:4,padding:'6px 8px'}}>
                                    {r.body.slice(0,280)}{r.body.length>280?'...':''}
                                  </div>
                                  <div style={{display:'flex',gap:6,marginTop:6,alignItems:'center'}}>
                                    <button style={{...s.btn('primary'),fontSize:12,padding:'4px 12px'}}
                                      onClick={e=>{
                                        e.stopPropagation();
                                        setReplyBody(r.body);
                                        if(r.recommended_status==='pending')setReplyPending(true);
                                        setShowCanned(false);
                                        setCannedSelected(null);
                                      }}>
                                      Insert into Reply
                                    </button>
                                    <span style={{fontSize:11,color:T.textFaint}}>Recommended status: {r.recommended_status}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}

              <SupportSavedReplies api={api} ticketId={ticketId} reason={t.reason} product={t.product}
                onInsert={body=>setReplyBody(body)}/>

              <div style={{fontSize:13,color:T.textSub,marginBottom:8}}>
                Sending to: <span style={{fontWeight:700,color:T.text}}>{t.email}</span>
                &nbsp;&middot;&nbsp;Re: [{t.ticket_id}] {t.subject||'Support Request'}
              </div>
              {replyMsg&&<div style={{padding:'8px 12px',background:T.greenBg,border:`1px solid ${T.greenBorder}`,borderRadius:6,fontSize:13,fontWeight:600,color:T.green,marginBottom:8}}>{replyMsg}</div>}
              {replyErr&&<div style={{...s.error,marginBottom:8}}>! {replyErr}</div>}
              <textarea
                style={{...s.input,width:'100%',boxSizing:'border-box',minHeight:96,resize:'vertical',fontFamily:'inherit',fontSize:14}}
                placeholder="Write your reply to the customer here..."
                value={replyBody}
                onChange={e=>setReplyBody(e.target.value)}
                disabled={replying}
              />
              <div style={{display:'flex',alignItems:'center',gap:10,marginTop:8,justifyContent:'space-between',flexWrap:'wrap'}}>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,color:T.textSub,cursor:'pointer'}}>
                  <input type="checkbox" checked={replyPending} onChange={e=>setReplyPending(e.target.checked)} style={{cursor:'pointer'}}/>
                  Set status to Pending after send
                </label>
                <button style={{...s.btn('primary'),minWidth:120,justifyContent:'center'}}
                  onClick={handleReply}
                  disabled={replying||!replyBody.trim()}>
                  {replying?'Sending...':'Send Reply'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: User intelligence summary */}
        <div style={{width:260,flexShrink:0}}>
          <IntelPanel title="User Account">
            <IntelKV label="Email" val={<span style={{fontSize:12,wordBreak:'break-all'}}>{t.user_email}</span>}/>
            <IntelKV label="Name" val={t.user_name||'-'}/>
            <IntelKV label="User ID" val={<span style={{fontSize:11,fontFamily:'monospace',wordBreak:'break-all'}}>{t.user_id}</span>}/>
          </IntelPanel>

          <SupportAccountPanel api={api} ticketId={ticketId} hasPermission={hasPermission}/>
          <SupportCommercialPanel api={api} ticketId={ticketId} hasPermission={hasPermission}/>

          {intel?(
            <>
              <IntelPanel title="Account State">
                <IntelKV label="Tier" val={(intel.user||{}).tier||'-'}/>
                <IntelKV label="Credits" val={fmt((intel.user||{}).credits)}/>
                <IntelKV label="Suppressed" val={intel.suppressed?'YES':'No'} color={intel.suppressed?T.red:T.green}/>
                <IntelKV label="Open Tickets" val={fmt(intel.open_tickets)} color={intel.open_tickets>1?T.amber:undefined}/>
                <IntelKV label="Mail Failures" val={fmt(intel.mail_failed)} color={intel.mail_failed>0?T.red:undefined}/>
                <IntelKV label="Failed Jobs" val={fmt(intel.failed_jobs)} color={intel.failed_jobs>0?T.amber:undefined}/>
              </IntelPanel>

              {intel.suppressed&&(
                <div style={{padding:'8px 12px',background:T.redBg,border:`1px solid ${T.redBorder}`,borderRadius:6,marginBottom:10,fontSize:13,fontWeight:700,color:T.red}}>
                  Email Suppressed: {intel.suppression_type||'unknown'}
                </div>
              )}

              <IntelPanel title="Security Indicators">
                {(()=>{
                  const failedLogins=(intel.timeline||[]).filter(e=>e.type==='failed_login');
                  const critEvents=(intel.timeline||[]).filter(e=>e.severity==='critical');
                  return(
                    <>
                      <IntelKV label="Failed Logins" val={fmt(failedLogins.length)} color={failedLogins.length>3?T.red:failedLogins.length>0?T.amber:undefined}/>
                      <IntelKV label="Critical Events" val={fmt(critEvents.length)} color={critEvents.length>0?T.red:undefined}/>
                      <IntelKV label="Staff Sessions" val={fmt(intel.active_staff_sessions)}/>
                    </>
                  );
                })()}
              </IntelPanel>

              <IntelPanel title="Recent Mail">
                {(()=>{
                  const mailEvs=(intel.timeline||[]).filter(e=>e.type==='mail_event'||e.type==='delivery_event').slice(0,4);
                  return mailEvs.length===0
                    ?<div style={{fontSize:13,color:T.textFaint}}>No mail events</div>
                    :mailEvs.map((e,i)=>(
                      <div key={i} style={{padding:'5px 0',borderBottom:`1px solid ${T.borderSoft}`,fontSize:13}}>
                        <div style={{fontWeight:600,color:T.text}}>{e.title}</div>
                        <div style={{fontSize:12,color:T.textFaint}}>{fmtAgo(e.ts)}</div>
                      </div>
                    ));
                })()}
              </IntelPanel>
            </>
          ):(
            <IntelPanel title="Intelligence">
              <div style={{fontSize:13,color:T.textFaint}}>{hasPermission('intelligence.view')?'Loading...':'No permission'}</div>
            </IntelPanel>
          )}
        </div>
      </div>
    </>
  );
}


// ── Support Queue ─────────────────────────────────────────────────────────────
// ---------------------------------------------------------------------------
// SecTechnicianApplications
// Admin review of technician access requests (Feature 1, review side).
// Wired to the ALREADY-LIVE (v234) staff-gated /v1/technicians routes:
//   GET  /v1/technicians                      -- list (status/country/search)
//   GET  /v1/technicians/<id>                 -- detail (row is reused here)
//   POST /v1/technicians/<id>/status          -- lifecycle transition
// Section visibility is gated by technicians.view (SECTION_PERMISSIONS). Status
// changes require technicians.review OR technicians.manage; the backend /status
// route enforces the exact per-target permission and allowed transitions, so
// this panel surfaces its 400/403 responses rather than second-guessing them.
// Technicians never receive staff permissions; this is staff-side only.
// ---------------------------------------------------------------------------
const TECH_APP_STATUSES = [
  'pending', 'under_review', 'more_info_required', 'approved', 'active',
  'paused', 'suspended', 'cancelled', 'rejected', 'banned', 'expired_review',
];
const TECH_APP_STATUS_COLOR = {
  pending: 'grey', under_review: 'purple', more_info_required: 'amber',
  approved: 'blue', active: 'green', paused: 'amber', suspended: 'red',
  cancelled: 'grey', rejected: 'red', banned: 'red', expired_review: 'grey',
};

// ---------------------------------------------------------------------------
// Technician admin management: Overview + master Accounts database view.
// Both consume the already-live /v1/technicians routes (list/detail/status,
// plus PATCH /<id>/capabilities which is live in v234). Reuses TECH_APP_STATUSES
// and TECH_APP_STATUS_COLOR defined just below. Section visibility is gated by
// technicians.view; status changes need technicians.review OR technicians.manage;
// capability edits need technicians.manage. effective_capabilities is backend
// derived and shown read-only. Technicians never receive staff permissions.
// ---------------------------------------------------------------------------
const TECH_CAP_FLAGS = [
  { key: 'hub_access',                label: 'Hub Access' },
  { key: 'utilities_access',          label: 'Utilities Access' },
  { key: 'software_directory_access', label: 'Software Directory' },
  { key: 'tool_launcher_access',      label: 'Tool Launcher' },
  { key: 'job_hub_access',            label: 'Job Hub' },
  { key: 'technician_pro_visible',    label: 'Technician Pro Visible' },
  { key: 'technician_pro_eligible',   label: 'Technician Pro Eligible' },
  { key: 'advanced_downloads_access', label: 'Advanced Downloads' },
  { key: 'beta_tools_access',         label: 'Beta Tools' },
  { key: 'support_priority',          label: 'Support Priority' },
];
const TECH_VERIF_STATUSES = ['unverified', 'in_review', 'verified', 'failed'];

// ---------------------------------------------------------------------------
// SecTechnicianManagement
// Single command-centre workspace that consolidates the four technician admin
// panels behind visible tabs (Overview / Applications / Accounts / Messages).
// Each tab renders the existing panel unchanged, so all permission gating stays
// intact: section visible with technicians.view, status changes need
// technicians.review OR technicians.manage, capability edits need
// technicians.manage. No backend or route changes.
// ---------------------------------------------------------------------------
const TECH_MGMT_TABS = [
  { id: 'overview',     label: 'Overview' },
  { id: 'applications', label: 'Applications' },
  { id: 'accounts',     label: 'Accounts' },
  { id: 'messages',     label: 'Messages' },
];

function SecTechnicianManagement(props) {
  const [tab, setTab] = useState('overview');

  const tabBtn = (active) => ({
    padding: '9px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
    fontFamily: font, transition: 'all 0.15s',
    border: '1px solid ' + (active ? 'rgba(45,108,255,0.5)' : T.border),
    background: active ? 'rgba(45,108,255,0.16)' : T.surface,
    color: active ? '#2D6CFF' : T.textMid,
  });

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, color: '#fff' }}>Technician Management</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginTop: 4, maxWidth: 720, lineHeight: 1.5 }}>
          Review technician applications, manage technician accounts, control access, and handle technician messages.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {TECH_MGMT_TABS.map(t => (
          <button key={t.id} style={tabBtn(tab === t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview'     && <SecTechnicianOverview {...props} />}
      {tab === 'applications' && <SecTechnicianApplications {...props} />}
      {tab === 'accounts'     && <SecTechnicianAccounts {...props} />}
      {tab === 'messages'     && <SecTechnicianMessages {...props} />}
    </div>
  );
}

function SecTechnicianOverview({ api }) {
  const [rows,  setRows]  = useState(null);
  const [total, setTotal] = useState(0);
  const [err,   setErr]   = useState(null);

  const load = useCallback(() => {
    setRows(null); setErr(null);
    fetch(`${api}/v1/technicians?per_page=200`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) { setRows(d.technicians || []); setTotal(d.total || (d.technicians || []).length); }
        else setErr(d.error || 'Failed to load technician overview');
      })
      .catch(() => setErr('Network error'));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const list = rows || [];
  const by = (st) => list.filter(t => t.status === st).length;
  const proActive   = list.filter(t => t.technician_pro && t.technician_pro.active).length;
  const proEligible = list.filter(t => t.capabilities && t.capabilities.technician_pro_eligible === true).length;

  const cards = [
    { label: 'Total Accounts',          val: rows ? total : null, color: T.accent },
    { label: 'Pending',                 val: by('pending'),            color: T.textSub },
    { label: 'Under Review',            val: by('under_review'),       color: T.purple },
    { label: 'More Info Required',      val: by('more_info_required'), color: T.amber },
    { label: 'Approved',                val: by('approved'),           color: T.accent },
    { label: 'Active',                  val: by('active'),             color: T.green },
    { label: 'Paused',                  val: by('paused'),             color: T.amber },
    { label: 'Suspended',               val: by('suspended'),          color: T.red },
    { label: 'Rejected',                val: by('rejected'),           color: T.red },
    { label: 'Banned',                  val: by('banned'),             color: T.red },
    { label: 'Technician Pro Active',   val: proActive,                color: T.green },
    { label: 'Technician Pro Eligible', val: proEligible,              color: T.accent },
  ];

  return (
    <>
      <div style={{ ...s.filterRow, marginBottom: 12 }}>
        <button style={s.btn()} onClick={load}>Refresh</button>
        {total > 200 && <span style={{ fontSize: 12, color: T.textSub }}>Per-status counts derived from the first 200 accounts.</span>}
      </div>
      {err && <div style={s.error}>! {err}</div>}
      {!rows && !err && <div style={s.loading}>Loading technician overview...</div>}
      {rows && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          {cards.map(c => (
            <div key={c.label} style={{ ...s.panel, padding: '12px 16px', borderTop: `3px solid ${c.color}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{c.label}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.text, lineHeight: 1 }}>{c.val == null ? '-' : fmt(c.val)}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function SecTechnicianAccounts({ api, mode }) {
  const _focus = mode === 'verification' || mode === 'capabilities' ? mode : null;
  const _focusLabel = mode === 'verification' ? 'Verification' : mode === 'capabilities' ? 'Capabilities' : 'Accounts';
  const { hasPermission } = useStaffAuth();
  const canReview = hasPermission('technicians.review') || hasPermission('technicians.manage');
  const canManage = hasPermission('technicians.manage');

  const [rows,  setRows]  = useState(null);
  const [total, setTotal] = useState(0);
  const [err,   setErr]   = useState(null);

  const [fSearch,  setFSearch]  = useState('');
  const [fStatus,  setFStatus]  = useState('');
  const [fCountry, setFCountry] = useState('');
  const [fType,    setFType]    = useState('');
  const [fVerif,   setFVerif]   = useState('');
  const [fPro,     setFPro]     = useState('');

  const [selected,     setSelected]     = useState(null);
  const [newStatus,    setNewStatus]    = useState('');
  const [reason,       setReason]       = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusErr,    setStatusErr]    = useState('');
  const [statusMsg,    setStatusMsg]    = useState('');

  const [caps,      setCaps]      = useState({});
  const [savingCaps, setSavingCaps] = useState(false);
  const [capErr,    setCapErr]    = useState('');
  const [capMsg,    setCapMsg]    = useState('');

  const load = useCallback(() => {
    setRows(null); setErr(null);
    fetch(`${api}/v1/technicians?per_page=200`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) { setRows(d.technicians || []); setTotal(d.total || (d.technicians || []).length); }
        else setErr(d.error || 'Failed to load technicians');
      })
      .catch(() => setErr('Network error'));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (t) => {
    setSelected(t);
    setNewStatus(''); setReason(''); setStatusErr(''); setStatusMsg('');
    setCaps(t.capabilities ? { ...t.capabilities } : {});
    setCapErr(''); setCapMsg('');
  };

  const applyStatus = () => {
    if (!canReview || !selected || !newStatus) return;
    setSavingStatus(true); setStatusErr(''); setStatusMsg('');
    fetch(`${api}/v1/technicians/${selected.technician_account_id}/status`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus, reason: reason }),
    })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) { setStatusMsg('Status updated.'); setNewStatus(''); setReason(''); setSelected(d.technician); setCaps(d.technician.capabilities ? { ...d.technician.capabilities } : {}); load(); }
        else setStatusErr(d.error || 'Update failed');
      })
      .catch(() => setStatusErr('Network error'))
      .finally(() => setSavingStatus(false));
  };

  const toggleCap = (k) => { if (!canManage) return; setCaps(c => ({ ...c, [k]: !c[k] })); setCapMsg(''); setCapErr(''); };

  const saveCaps = () => {
    if (!canManage || !selected) return;
    const stored = selected.capabilities || {};
    const changed = {};
    TECH_CAP_FLAGS.forEach(({ key }) => { const nv = !!caps[key]; if (nv !== !!stored[key]) changed[key] = nv; });
    if (Object.keys(changed).length === 0) { setCapMsg('No capability changes to save.'); return; }
    setSavingCaps(true); setCapErr(''); setCapMsg('');
    fetch(`${api}/v1/technicians/${selected.technician_account_id}/capabilities`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ capabilities: changed, reason: 'admin capability update' }),
    })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) { setCapMsg('Capabilities saved.'); setSelected(d.technician); setCaps(d.technician.capabilities ? { ...d.technician.capabilities } : {}); load(); }
        else setCapErr(d.error || 'Save failed');
      })
      .catch(() => setCapErr('Network error'))
      .finally(() => setSavingCaps(false));
  };

  // ---- Detail view ----
  if (selected) {
    const t = selected;
    const kv = (label, value) => (
      <div style={s.kvRow}><span style={s.kvLabel}>{label}</span><span style={s.kvValue}>{(value === 0 || value) ? value : '-'}</span></div>
    );
    const eff = t.effective_capabilities || {};
    const capOn  = { display: 'inline-flex', padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 700, background: T.greenBg, color: T.green, border: `1px solid ${T.greenBorder}` };
    const capOff = { display: 'inline-flex', padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 700, background: T.bg, color: T.textSub, border: `1px solid ${T.border}` };

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <button style={s.btn()} onClick={() => { setSelected(null); load(); }}>&lt;- Back to accounts</button>
          <span style={s.badge(TECH_APP_STATUS_COLOR[t.status] || 'grey')}>{(t.status || '').replace(/_/g, ' ')}</span>
        </div>

        <div style={{ ...s.panel, marginBottom: 12 }}>
          <div style={s.panelTitle}>{t.business_name || t.display_name || 'Technician account'}</div>
          <div style={s.panelSub}>Account {t.technician_account_id} | user {t.user_id}</div>
        </div>

        <div style={{ ...s.grid(2, 12), display: _focus ? 'none' : undefined }}>
          <div style={s.panel}>
            <div style={s.panelTitle}>Identity</div>
            {kv('Display Name', t.display_name)}
            {kv('Legal Name', t.legal_name)}
            {kv('Contact Email', t.contact_email)}
            {kv('Contact Phone', t.contact_phone)}
          </div>
          <div style={s.panel}>
            <div style={s.panelTitle}>Business</div>
            {kv('Business Name', t.business_name)}
            {kv('Trading Name', t.trading_name)}
            {kv('Business Structure', t.business_structure)}
            {kv('Technician Type', t.technician_type)}
            {kv('Country', t.country)}
            {kv('Region / State', t.region_state)}
            {kv('City / Suburb', t.city_suburb)}
            {kv('Website', t.website)}
          </div>
        </div>

        <div style={{ ...s.grid(2, 12), marginTop: 12, display: _focus === 'capabilities' ? 'none' : undefined }}>
          <div style={s.panel}>
            <div style={s.panelTitle}>Registration / Verification</div>
            {kv('ABN', t.abn)}
            {kv('ACN', t.acn)}
            {kv('GST Registered', t.gst_registered === true ? 'Yes' : t.gst_registered === false ? 'No' : '-')}
            {kv('Business Registration Number', t.business_registration_number)}
            {kv('Company Number', t.company_number)}
            {kv('Tax ID', t.tax_id)}
            {kv('VAT Number', t.vat_number)}
            {kv('EIN', t.ein)}
            {kv('Registration Scheme', t.registration_scheme)}
            {kv('Verification Status', t.verification_status)}
            {kv('Review Outcome', t.review_outcome)}
            {kv('Last Reviewed', fmtDate(t.last_reviewed_at))}
          </div>
          <div style={s.panel}>
            <div style={s.panelTitle}>Application</div>
            {kv('Created', fmtDate(t.created_at))}
            {kv('Updated', fmtDate(t.updated_at))}
            <div style={{ marginTop: 10, fontSize: 13, color: T.textSub, marginBottom: 4 }}>Application message</div>
            <div style={{ fontSize: 14, color: T.text, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{t.application_message || '(none provided)'}</div>
          </div>
        </div>

        <div style={{ ...s.panel, marginTop: 12, display: _focus ? 'none' : undefined }}>
          <div style={s.panelTitle}>Technician Pro</div>
          {kv('Active', t.technician_pro && t.technician_pro.active ? 'Yes' : 'No')}
          {kv('Unlocked', t.technician_pro && t.technician_pro.unlocked ? 'Yes' : 'No')}
          {kv('Status', t.technician_pro ? t.technician_pro.status : null)}
          {kv('Period End', t.technician_pro ? fmtDate(t.technician_pro.period_end) : '-')}
        </div>

        <div style={{ ...s.panel, marginTop: 12, display: _focus === 'verification' ? 'none' : undefined }}>
          <div style={s.panelTitle}>Capabilities</div>
          <div style={s.panelSub}>
            {canManage
              ? 'Toggle stored capabilities and save. Effective access is backend-derived (status must be active, Pro-gated flags need active Pro) and shown read-only.'
              : 'Stored and effective capabilities (read-only). Editing requires technicians.manage.'}
          </div>
          {capErr && <div style={s.error}>! {capErr}</div>}
          {capMsg && <div style={{ ...s.badge('green'), marginBottom: 10 }}>{capMsg}</div>}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...s.table, minWidth: 520 }}>
              <thead><tr>{['Capability', 'Stored', 'Effective'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {TECH_CAP_FLAGS.map(f => (
                  <tr key={f.key}>
                    <td style={s.td}>{f.label}</td>
                    <td style={s.td}>
                      {canManage ? (
                        <button onClick={() => toggleCap(f.key)} style={caps[f.key] ? capOn : capOff}>{caps[f.key] ? 'On' : 'Off'}</button>
                      ) : (
                        <span style={caps[f.key] ? capOn : capOff}>{caps[f.key] ? 'On' : 'Off'}</span>
                      )}
                    </td>
                    <td style={s.td}><span style={eff[f.key] ? capOn : capOff}>{eff[f.key] ? 'On' : 'Off'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canManage && (
            <div style={{ marginTop: 12 }}>
              <button style={{ ...s.btn('primary'), opacity: savingCaps ? 0.6 : 1 }} onClick={saveCaps} disabled={savingCaps}>
                {savingCaps ? 'Saving...' : 'Save Capabilities'}
              </button>
            </div>
          )}
        </div>

        {(t.internal_notes || t.rejection_reason || t.suspension_reason || t.cancellation_reason) && (
          <div style={{ ...s.panel, marginTop: 12 }}>
            <div style={s.panelTitle}>Internal / Admin (read-only)</div>
            {t.internal_notes    && kv('Internal Notes', t.internal_notes)}
            {t.rejection_reason  && kv('Rejection Reason', t.rejection_reason)}
            {t.suspension_reason && kv('Suspension Reason', t.suspension_reason)}
            {t.cancellation_reason && kv('Cancellation Reason', t.cancellation_reason)}
          </div>
        )}

        <div style={{ ...s.panel, marginTop: 12 }}>
          <div style={s.panelTitle}>Status</div>
          <div style={s.panelSub}>
            {canReview ? 'Set a new status. The backend enforces which transitions your role may perform and which are allowed.' : 'View-only. Status changes require technicians.review or technicians.manage.'}
          </div>
          {statusErr && <div style={s.error}>! {statusErr}</div>}
          {statusMsg && <div style={{ ...s.badge('green'), marginBottom: 10 }}>{statusMsg}</div>}
          {canReview && (
            <>
              <div style={{ ...s.grid(2, 12) }}>
                <div>
                  <div style={{ fontSize: 13, color: T.textSub, marginBottom: 4 }}>New Status</div>
                  <select style={{ ...s.select, width: '100%' }} value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                    <option value="">Select...</option>
                    {TECH_APP_STATUSES.filter(st => st !== t.status).map(st => <option key={st} value={st}>{st.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: T.textSub, marginBottom: 4 }}>Reason / note (optional)</div>
                  <input style={{ ...s.input, width: '100%' }} value={reason} onChange={e => setReason(e.target.value)} placeholder="Recorded with the transition" />
                </div>
              </div>
              <div style={{ marginTop: 14 }}>
                <button style={{ ...s.btn('primary'), opacity: (savingStatus || !newStatus) ? 0.6 : 1 }} onClick={applyStatus} disabled={savingStatus || !newStatus}>
                  {savingStatus ? 'Saving...' : 'Apply Status Change'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ---- List view (master database) ----
  const list = rows || [];
  const countries = Array.from(new Set(list.map(t => t.country).filter(Boolean))).sort();
  const types     = Array.from(new Set(list.map(t => t.technician_type).filter(Boolean))).sort();

  const filtered = list.filter(t => {
    if (fStatus  && t.status !== fStatus) return false;
    if (fCountry && (t.country || '') !== fCountry) return false;
    if (fType    && (t.technician_type || '') !== fType) return false;
    if (fVerif   && (t.verification_status || '') !== fVerif) return false;
    if (fPro) {
      const active   = t.technician_pro && t.technician_pro.active;
      const eligible = t.capabilities && t.capabilities.technician_pro_eligible === true;
      if (fPro === 'active'   && !active) return false;
      if (fPro === 'eligible' && !eligible) return false;
      if (fPro === 'none'     && (active || eligible)) return false;
    }
    if (fSearch) {
      const q = fSearch.toLowerCase();
      const hay = [t.display_name, t.legal_name, t.contact_email, t.business_name, t.trading_name].map(x => (x || '').toLowerCase()).join(' ');
      if (hay.indexOf(q) === -1) return false;
    }
    return true;
  });

  return (
    <>
      <div style={{ ...s.filterRow, marginBottom: 12 }}>
        <input style={{ ...s.input, width: 220 }} placeholder="Search name, email, business, trading..." value={fSearch} onChange={e => setFSearch(e.target.value)} />
        <select style={s.select} value={fStatus} onChange={e => setFStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {TECH_APP_STATUSES.map(st => <option key={st} value={st}>{st.replace(/_/g, ' ')}</option>)}
        </select>
        <select style={s.select} value={fCountry} onChange={e => setFCountry(e.target.value)}>
          <option value="">All Countries</option>
          {countries.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={s.select} value={fType} onChange={e => setFType(e.target.value)}>
          <option value="">All Types</option>
          {types.map(ty => <option key={ty} value={ty}>{ty}</option>)}
        </select>
        <select style={s.select} value={fVerif} onChange={e => setFVerif(e.target.value)}>
          <option value="">All Verification</option>
          {TECH_VERIF_STATUSES.map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
        </select>
        <select style={s.select} value={fPro} onChange={e => setFPro(e.target.value)}>
          <option value="">All Pro States</option>
          <option value="active">Pro active</option>
          <option value="eligible">Pro eligible</option>
          <option value="none">No Pro</option>
        </select>
        <button style={s.btn()} onClick={load}>Refresh</button>
      </div>

      {err && <div style={s.error}>! {err}</div>}
      {!rows && !err && <div style={s.loading}>Loading technician accounts...</div>}

      {rows && (
        <div style={s.tableCard}>
          <div style={s.tableHeader}>
            <div style={s.tableTitle}>
              {_focusLabel === 'Accounts' ? 'Technician Accounts' : 'Technician ' + _focusLabel}<span style={s.tableBadge}>{fmt(filtered.length)}{filtered.length !== total ? ' of ' + fmt(total) : ''}</span>
            </div>
            {total > 200 && <span style={{ fontSize: 12, color: T.textSub }}>Showing first 200 loaded.</span>}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...s.table, minWidth: 1000 }}>
              <thead>
                <tr>{['Name', 'Email', 'Business', 'Trading', 'Country', 'Type', 'Status', 'Verification', 'Pro', 'Created', 'Updated'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={11} style={{ ...s.td, ...s.empty }}>No technician accounts for this filter.</td></tr>
                ) : filtered.map(t => (
                  <tr key={t.technician_account_id} style={{ cursor: 'pointer' }}
                    onClick={() => openDetail(t)}
                    onMouseEnter={e => e.currentTarget.style.background = T.borderSoft}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={s.tds}>{t.display_name || t.legal_name || '-'}</td>
                    <td style={s.td}>{t.contact_email || '-'}</td>
                    <td style={s.td}>{t.business_name || '-'}</td>
                    <td style={s.td}>{t.trading_name || '-'}</td>
                    <td style={s.td}>{t.country || '-'}</td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub }}>{t.technician_type || '-'}</span></td>
                    <td style={s.td}><span style={s.badge(TECH_APP_STATUS_COLOR[t.status] || 'grey')}>{(t.status || '').replace(/_/g, ' ')}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub }}>{t.verification_status || '-'}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub }}>{t.technician_pro && t.technician_pro.active ? 'active' : (t.technician_pro && t.technician_pro.status ? t.technician_pro.status : '-')}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub, whiteSpace: 'nowrap' }}>{fmtDate(t.created_at)}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub, whiteSpace: 'nowrap' }}>{fmtDate(t.updated_at)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function SecTechnicianAudit({ api }) {
  const [rows, setRows] = useState(null);
  const [err,  setErr]  = useState(null);

  const load = useCallback(() => {
    setRows(null); setErr(null);
    fetch(`${api}/v1/technicians?per_page=200`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) setRows(d.technicians || []);
        else setErr(d.error || 'Failed to load technician audit');
      })
      .catch(() => setErr('Network error'));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const list = (rows || []).slice().sort((a, b) => {
    const ax = a.last_reviewed_at || a.updated_at || '';
    const bx = b.last_reviewed_at || b.updated_at || '';
    return String(bx).localeCompare(String(ax));
  });

  return (
    <>
      <div style={{ ...s.filterRow, marginBottom: 12 }}>
        <button style={s.btn()} onClick={load}>Refresh</button>
        <span style={{ fontSize: 12, color: T.textSub }}>
          Read-only review history derived from backend-provided fields. No status changes are made here.
        </span>
      </div>
      {err && <div style={s.error}>! {err}</div>}
      {!rows && !err && <div style={s.loading}>Loading technician audit...</div>}
      {rows && (
        <div style={s.tableCard}>
          <div style={s.tableHeader}>
            <div style={s.tableTitle}>Technician Review Audit<span style={s.tableBadge}>{fmt(list.length)}</span></div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...s.table, minWidth: 940 }}>
              <thead>
                <tr>{['Name', 'Status', 'Verification', 'Review Outcome', 'Last Reviewed', 'Created', 'Updated'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr><td colSpan={7} style={{ ...s.td, ...s.empty }}>No technician accounts to audit.</td></tr>
                ) : list.map(t => (
                  <tr key={t.technician_account_id}>
                    <td style={s.tds}>{t.display_name || t.legal_name || t.business_name || '-'}</td>
                    <td style={s.td}><span style={s.badge(TECH_APP_STATUS_COLOR[t.status] || 'grey')}>{(t.status || '').replace(/_/g, ' ') || '-'}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub }}>{t.verification_status || '-'}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub }}>{t.review_outcome || '-'}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub, whiteSpace: 'nowrap' }}>{fmtDate(t.last_reviewed_at)}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub, whiteSpace: 'nowrap' }}>{fmtDate(t.created_at)}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub, whiteSpace: 'nowrap' }}>{fmtDate(t.updated_at)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function SecTechnicianSubmissions({ api, onSendToActivation }) {
  const { hasPermission } = useStaffAuth();
  const canReview = hasPermission('technicians.review');

  const [rows,  setRows]  = useState(null);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page,  setPage]  = useState(1);
  const [err,   setErr]   = useState(null);

  const [fSearch,  setFSearch]  = useState('');
  const [fStatus,  setFStatus]  = useState('');
  const [fCountry, setFCountry] = useState('');

  const [selected,   setSelected]   = useState(null);
  const [detailErr,  setDetailErr]  = useState('');

  // Status + review-notes editing (technicians.review)
  const [newStatus,    setNewStatus]    = useState('');
  const [reviewNotes,  setReviewNotes]  = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusErr,    setStatusErr]    = useState('');
  const [statusMsg,    setStatusMsg]    = useState('');

  // User matching (reuses the existing /v1/admin/users search)
  const [userQuery,    setUserQuery]    = useState('');
  const [userResults,  setUserResults]  = useState(null);
  const [userSearchErr, setUserSearchErr] = useState('');
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [linking,      setLinking]      = useState(false);
  const [linkErr,      setLinkErr]      = useState('');
  const [linkMsg,      setLinkMsg]      = useState('');

  // Non-activation statuses staff may set. 'activated' is intentionally omitted
  // (the backend refuses that transition; it is owned by ACTIVATE_TECHNICIAN).
  const SUBMISSION_STATUSES = ['new','under_review','awaiting_user_match','ready_for_activation','rejected','closed'];
  const SENDABLE_STATES = ['new','under_review','awaiting_user_match','ready_for_activation'];

  const load = useCallback(() => {
    setRows(null); setErr(null);
    const p = new URLSearchParams({ page: page, per_page: 50 });
    if (fStatus)  p.set('status',  fStatus);
    if (fCountry) p.set('country', fCountry);
    if (fSearch)  p.set('search',  fSearch);
    fetch(`${api}/v1/technicians/submissions?${p}`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) { setRows(d.submissions || []); setTotal(d.total || 0); setPages(d.pages || 1); }
        else setErr(d.error || 'Failed to load submissions');
      })
      .catch(() => setErr('Network error'));
  }, [api, page, fStatus, fCountry, fSearch]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (id) => {
    setSelected(null); setDetailErr('');
    setNewStatus(''); setReviewNotes(''); setStatusErr(''); setStatusMsg('');
    setUserQuery(''); setUserResults(null); setUserSearchErr(''); setLinkErr(''); setLinkMsg('');
    fetch(`${api}/v1/technicians/submissions/${id}`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) { setSelected(d.submission); setReviewNotes(d.submission.review_notes || ''); }
        else setDetailErr(d.error || 'Failed to load submission');
      })
      .catch(() => setDetailErr('Network error'));
  };

  const applyStatus = () => {
    if (!canReview || !selected || !newStatus) return;
    setSavingStatus(true); setStatusErr(''); setStatusMsg('');
    fetch(`${api}/v1/technicians/submissions/${selected.id}`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus, review_notes: reviewNotes }),
    })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) { setStatusMsg('Submission updated.'); setSelected(d.submission); setNewStatus(''); setReviewNotes(d.submission.review_notes || ''); load(); }
        else setStatusErr(d.error || 'Update failed');
      })
      .catch(() => setStatusErr('Network error'))
      .finally(() => setSavingStatus(false));
  };

  const searchUsers = () => {
    if (!canReview) return;
    const q = userQuery.trim();
    if (!q) { setUserResults(null); return; }
    setSearchingUsers(true); setUserSearchErr(''); setUserResults(null);
    const p = new URLSearchParams({ page: 1, per_page: 20, search: q });
    fetch(`${api}/v1/admin/users?${p}`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) setUserResults(d.users || []);
        else setUserSearchErr(d.error || 'User search failed');
      })
      .catch(() => setUserSearchErr('Network error'))
      .finally(() => setSearchingUsers(false));
  };

  const linkUser = (userId) => {
    if (!canReview || !selected || !userId) return;
    setLinking(true); setLinkErr(''); setLinkMsg('');
    fetch(`${api}/v1/technicians/submissions/${selected.id}/link-user`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) { setLinkMsg('User linked.'); setSelected(d.submission); setUserResults(null); setUserQuery(''); load(); }
        else setLinkErr(d.error || 'Link failed');
      })
      .catch(() => setLinkErr('Network error'))
      .finally(() => setLinking(false));
  };

  const buildPrefill = (sub) => {
    // Compatible activation fields only, plus the submission id. Application
    // details are folded into a readable note the activation form seeds into its
    // own note field (staff can edit before submitting).
    const parts = [];
    if (sub.business_name)       parts.push('Business: ' + sub.business_name);
    if (sub.years_experience)    parts.push('Experience: ' + sub.years_experience);
    if (sub.services_offered)    parts.push('Services: ' + sub.services_offered);
    if (sub.website)             parts.push('Website: ' + sub.website);
    if (sub.application_message)  parts.push('Message: ' + sub.application_message);
    const note = parts.length ? ('From technician submission ' + sub.id + '. ' + parts.join(' | ')) : '';
    return {
      id:                  sub.id,
      user_id:             sub.selected_user_id || '',
      technician_type:     sub.technician_type || '',
      business_name:       sub.business_name || '',
      trading_name:        sub.trading_name || '',
      country:             sub.country || '',
      verification_status: 'in_review',
      note:                note,
    };
  };

  const canSend = (sub) => (
    canReview && !!sub && !!sub.selected_user_id &&
    SENDABLE_STATES.indexOf(sub.status) !== -1 &&
    ['activated','rejected','closed'].indexOf(sub.status) === -1
  );

  const sendToActivation = (sub) => {
    if (!canSend(sub) || !onSendToActivation) return;
    onSendToActivation(sub.selected_user_id, buildPrefill(sub));
  };

  const chipColor = (st) => (
    st === 'activated' ? 'green' :
    st === 'rejected' || st === 'closed' ? 'red' :
    st === 'ready_for_activation' ? 'blue' : 'amber'
  );

  const fullName = (sub) => `${sub.first_name || ''} ${sub.last_name || ''}`.trim() || '-';

  return (
    <>
      <div style={s.filterRow}>
        <input style={{ ...s.input, width: 200 }} placeholder="Search name, business, email..." value={fSearch} onChange={e => { setFSearch(e.target.value); setPage(1); }} />
        <select style={s.select} value={fStatus} onChange={e => { setFStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>{SUBMISSION_STATUSES.concat(['activated']).map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
        </select>
        <input style={{ ...s.input, width: 150 }} placeholder="Country" value={fCountry} onChange={e => { setFCountry(e.target.value); setPage(1); }} />
        <button style={s.btn('primary')} onClick={() => { setPage(1); load(); }}>Search</button>
        <button style={s.btn()} onClick={load}>Refresh</button>
      </div>

      {err && <div style={s.error}>! {err}</div>}
      {!rows && !err && <Loader />}

      {rows && (
        <TableCard title="Technician Submissions" count={total}>
          <table style={s.table}>
            <thead><tr>{['Applicant', 'Business', 'Email', 'Type', 'Location', 'Status', 'Submitted', ''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map(sub => (
                <tr key={sub.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(sub.id)} onMouseEnter={e => e.currentTarget.style.background = T.borderSoft} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={s.td}><div style={{ fontWeight: 600, fontSize: 15, color: T.text }}>{fullName(sub)}</div></td>
                  <td style={{ ...s.td, fontSize: 14, color: T.textSub }}>{sub.business_name || '-'}</td>
                  <td style={{ ...s.td, fontSize: 14, color: T.textSub }}>{sub.contact_email || '-'}</td>
                  <td style={{ ...s.td, fontSize: 14, color: T.textSub }}>{sub.technician_type || '-'}</td>
                  <td style={{ ...s.td, fontSize: 14, color: T.textSub }}>{[sub.city_suburb, sub.region_state, sub.country].filter(Boolean).join(', ') || '-'}</td>
                  <td style={s.td}><span style={s.badge(chipColor(sub.status))}>{(sub.status || '').replace(/_/g, ' ')}</span></td>
                  <td style={{ ...s.td, fontSize: 14, whiteSpace: 'nowrap' }}>{fmtDate(sub.created_at)}</td>
                  <td style={s.td}><button style={{ ...s.btn(), fontSize: 13, padding: '4px 9px' }} onClick={e => { e.stopPropagation(); openDetail(sub.id); }}>Open</button></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} style={s.empty}>No submissions found</td></tr>}
            </tbody>
          </table>
          <Pager page={page} pages={pages} total={total} onPage={setPage} />
        </TableCard>
      )}

      {detailErr && <div style={{ ...s.error, marginTop: 12 }}>! {detailErr}</div>}

      {selected && (
        <div style={{ ...s.panel, marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={s.panelTitle}>Submission Detail</div>
            <button style={s.btn()} onClick={() => setSelected(null)}>Close</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            {[
              ['First name', selected.first_name], ['Last name', selected.last_name],
              ['Contact email', selected.contact_email], ['Contact phone', selected.contact_phone],
              ['Business name', selected.business_name], ['Trading name', selected.trading_name],
              ['Website', selected.website], ['Technician type', selected.technician_type],
              ['Country', selected.country], ['State/Region', selected.region_state],
              ['Suburb/City', selected.city_suburb], ['Years experience', selected.years_experience],
              ['Stated account email', selected.stated_account_email],
              ['Legitimate technician', selected.is_legitimate_technician ? 'Yes' : 'No'],
              ['Consent to contact', selected.consent_contact ? 'Yes' : 'No'],
              ['Status', (selected.status || '').replace(/_/g, ' ')],
              ['Selected user ID', selected.selected_user_id],
              ['Linked technician account', selected.linked_technician_account_id],
              ['Reviewer', selected.reviewed_by], ['Reviewed at', fmtDate(selected.reviewed_at)],
              ['Created at', fmtDate(selected.created_at)],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', flexDirection: 'column', padding: '4px 0', borderBottom: `1px solid ${T.borderSoft}` }}>
                <span style={{ fontSize: 12, color: T.textMuted || T.textSub, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{k}</span>
                <span style={{ fontSize: 14, color: T.text, wordBreak: 'break-word' }}>{(v === null || v === undefined || v === '') ? '-' : String(v)}</span>
              </div>
            ))}
          </div>

          {selected.services_offered && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: T.textMuted || T.textSub, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Services offered</div>
              <div style={{ fontSize: 14, color: T.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{selected.services_offered}</div>
            </div>
          )}
          {selected.application_message && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: T.textMuted || T.textSub, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Application message</div>
              <div style={{ fontSize: 14, color: T.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{selected.application_message}</div>
            </div>
          )}

          {!canReview && (
            <div style={{ fontSize: 13, color: T.textSub, marginBottom: 8 }}>You have view-only access to technician submissions.</div>
          )}

          {canReview && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ ...s.panel }}>
                <div style={{ ...s.panelTitle, marginBottom: 8 }}>Review</div>
                {statusErr && <div style={{ ...s.error, marginBottom: 8 }}>! {statusErr}</div>}
                {statusMsg && <div style={{ padding: '8px 11px', borderRadius: 6, background: T.greenBg, border: `1px solid ${T.greenBorder}`, color: T.green, fontSize: 14, marginBottom: 8 }}>{statusMsg}</div>}
                <div style={{ fontSize: 13, fontWeight: 600, color: T.textSub, marginBottom: 4 }}>Status</div>
                <select style={{ ...s.select, width: '100%', boxSizing: 'border-box', marginBottom: 8 }} value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                  <option value="">Keep current ({(selected.status || '').replace(/_/g, ' ')})</option>
                  {SUBMISSION_STATUSES.map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
                </select>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.textSub, marginBottom: 4 }}>Review notes</div>
                <textarea style={{ ...s.input, width: '100%', boxSizing: 'border-box', minHeight: 64, resize: 'vertical', fontFamily: font }} placeholder="Internal review notes..." value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} />
                <button style={{ ...s.btn('primary'), justifyContent: 'center', padding: '8px 0', marginTop: 8, width: '100%' }} onClick={applyStatus} disabled={savingStatus || !newStatus}>{savingStatus ? 'Saving...' : 'Save Review'}</button>
                <div style={{ fontSize: 12, color: T.textSub, marginTop: 6 }}>Activation is not set here. Use Send to Activation once a user is linked.</div>
              </div>

              <div style={{ ...s.panel }}>
                <div style={{ ...s.panelTitle, marginBottom: 8 }}>Match VISINVOKE User</div>
                <div style={{ fontSize: 13, color: T.textSub, marginBottom: 6 }}>Submitted email: <code style={{ fontFamily: mono, fontSize: 12 }}>{selected.contact_email || '-'}</code></div>
                {selected.stated_account_email && <div style={{ fontSize: 13, color: T.textSub, marginBottom: 6 }}>Stated account: <code style={{ fontFamily: mono, fontSize: 12 }}>{selected.stated_account_email}</code></div>}
                {selected.selected_user_id
                  ? <div style={{ padding: '8px 11px', borderRadius: 6, background: T.greenBg, border: `1px solid ${T.greenBorder}`, color: T.green, fontSize: 13, marginBottom: 8 }}>Linked user: <code style={{ fontFamily: mono, fontSize: 12 }}>{selected.selected_user_id}</code></div>
                  : <div style={{ fontSize: 13, color: T.amber || T.textSub, marginBottom: 8 }}>No user linked yet. Search and confirm the correct user before activation.</div>}
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  <input style={{ ...s.input, flex: 1 }} placeholder="Search users by email or name..." value={userQuery} onChange={e => setUserQuery(e.target.value)} />
                  <button style={s.btn('primary')} onClick={searchUsers} disabled={searchingUsers || !userQuery.trim()}>{searchingUsers ? '...' : 'Search'}</button>
                </div>
                {userSearchErr && <div style={{ ...s.error, marginBottom: 8 }}>! {userSearchErr}</div>}
                {linkErr && <div style={{ ...s.error, marginBottom: 8 }}>! {linkErr}</div>}
                {linkMsg && <div style={{ padding: '8px 11px', borderRadius: 6, background: T.greenBg, border: `1px solid ${T.greenBorder}`, color: T.green, fontSize: 13, marginBottom: 8 }}>{linkMsg}</div>}
                {userResults && userResults.length === 0 && <div style={{ fontSize: 13, color: T.textSub }}>No matching users found.</div>}
                {userResults && userResults.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                    {userResults.map(u => (
                      <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 8px', border: `1px solid ${T.borderSoft}`, borderRadius: 6 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                          <div style={{ fontSize: 12, color: T.textSub }}>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || '-'}</div>
                        </div>
                        <button style={{ ...s.btn('primary'), fontSize: 13, padding: '4px 9px' }} onClick={() => linkUser(u.id)} disabled={linking}>{linking ? '...' : 'Link'}</button>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  style={{ ...s.btn('primary'), justifyContent: 'center', padding: '8px 0', marginTop: 12, width: '100%', background: canSend(selected) ? '#553c9a' : undefined }}
                  onClick={() => sendToActivation(selected)}
                  disabled={!canSend(selected)}
                  title={!selected.selected_user_id ? 'Link a user first' : ''}
                >Send to Activation</button>
                {!selected.selected_user_id && <div style={{ fontSize: 12, color: T.textSub, marginTop: 6 }}>A linked user is required before activation.</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function SecTechnicianApplications({ api }) {
  const { hasPermission } = useStaffAuth();
  const canReview = hasPermission('technicians.review') || hasPermission('technicians.manage');

  const [data, setData] = useState(null);
  const [err,  setErr]  = useState(null);
  const [page, setPage] = useState(1);
  // Default to the PENDING work queue (oldest-first, see sortedTechs) so staff
  // open Technician Applications straight onto the review backlog.
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchFilter, setSearchFilter] = useState('');

  const [selected,   setSelected]   = useState(null);
  const [newStatus,  setNewStatus]  = useState('');
  const [reason,     setReason]     = useState('');
  const [saving,     setSaving]     = useState(false);
  const [actionErr,  setActionErr]  = useState('');
  const [actionMsg,  setActionMsg]  = useState('');

  const load = useCallback(() => {
    setData(null); setErr(null);
    const p = new URLSearchParams({ page, per_page: 50 });
    if (statusFilter) p.set('status', statusFilter);
    if (searchFilter) p.set('search', searchFilter);
    fetch(`${api}/v1/technicians?${p}`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (ok) setData(d); else setErr(d.error || 'Failed to load applications'); })
      .catch(() => setErr('Network error'));
  }, [api, page, statusFilter, searchFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (t) => {
    setSelected(t); setNewStatus(''); setReason(''); setActionErr(''); setActionMsg('');
  };

  const applyStatus = () => {
    if (!canReview || !selected || !newStatus) return;
    setSaving(true); setActionErr(''); setActionMsg('');
    fetch(`${api}/v1/technicians/${selected.technician_account_id}/status`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus, reason: reason }),
    })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) { setSelected(d.technician); setActionMsg('Status updated.'); setNewStatus(''); setReason(''); load(); }
        else setActionErr(d.error || 'Update failed');
      })
      .catch(() => setActionErr('Network error'))
      .finally(() => setSaving(false));
  };

  // Explicit one-click review decisions. APPROVE transitions to 'active' — the
  // REAL authoritative Technician entitlement (the backend auto-provisions the
  // default capability set on 'active'), so approval completes the entitlement
  // step in one action with no second system to touch. MORE INFO / DECLINE map
  // to more_info_required / rejected. All go through the same status endpoint,
  // which enforces role + allowed transitions and writes history + audit.
  const decide = (targetStatus, confirmMsg) => {
    if (!canReview || !selected || saving) return;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setSaving(true); setActionErr(''); setActionMsg('');
    fetch(`${api}/v1/technicians/${selected.technician_account_id}/status`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: targetStatus, reason: reason }),
    })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) {
          setSelected(d.technician);
          setActionMsg(targetStatus === 'active' ? 'Approved — Technician access activated.'
            : targetStatus === 'more_info_required' ? 'Marked: more information required.'
            : targetStatus === 'rejected' ? 'Application declined.' : 'Status updated.');
          setNewStatus(''); setReason(''); load();
        } else setActionErr(d.error || 'Update failed');
      })
      .catch(() => setActionErr('Network error'))
      .finally(() => setSaving(false));
  };

  // ---- Detail view ----
  if (selected) {
    const t = selected;
    const kv = (label, value) => (
      <div style={s.kvRow}><span style={s.kvLabel}>{label}</span><span style={s.kvValue}>{value || '-'}</span></div>
    );
    const d = t.application_detail || {};
    const lnk = (label, url) => url ? (
      <div style={s.kvRow}><span style={s.kvLabel}>{label}</span>
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ ...s.kvValue, color: T.accent, textDecoration: 'underline', wordBreak: 'break-all' }}>{url}</a></div>
    ) : null;
    const arr = (v) => (Array.isArray(v) && v.length) ? v.join(', ') : (v || '-');
    const hasDetail = d && Object.keys(d).length > 0;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <button style={s.btn()} onClick={() => { setSelected(null); load(); }}>&lt;- Back to applications</button>
          <span style={s.badge(TECH_APP_STATUS_COLOR[t.status] || 'grey')}>{(t.status || '').replace(/_/g, ' ')}</span>
        </div>

        <div style={{ ...s.panel, marginBottom: 12 }}>
          <div style={s.panelTitle}>{t.business_name || t.display_name || 'Technician application'}</div>
          <div style={s.panelSub}>Submitted {fmtDate(t.created_at)}{t.last_reviewed_at ? ' | last reviewed ' + fmtDate(t.last_reviewed_at) : ''}</div>
          {kv('Technician Account', t.technician_account_id)}
          {kv('User ID', t.user_id)}
          {kv('Display Name', t.display_name)}
          {kv('Legal Name', t.legal_name)}
          {kv('Trading Name', t.trading_name)}
          {kv('Business Structure', t.business_structure)}
          {kv('Technician Type', t.technician_type)}
          {kv('Country', t.country)}
          {kv('Region / State', t.region_state)}
          {kv('City / Suburb', t.city_suburb)}
          {kv('Contact Email', t.contact_email)}
          {kv('Contact Phone', t.contact_phone)}
          {kv('Website', t.website)}
        </div>

        <div style={{ ...s.panel, marginBottom: 12 }}>
          <div style={s.panelTitle}>Business Verification</div>
          {kv('Verification Status', t.verification_status)}
          {kv('ABN', t.abn)}
          {kv('ACN', t.acn)}
          {kv('GST Registered', t.gst_registered === true ? 'Yes' : t.gst_registered === false ? 'No' : '-')}
          {kv('Business Registration Number', t.business_registration_number)}
          {kv('Tax ID', t.tax_id)}
          {kv('VAT Number', t.vat_number)}
          {kv('EIN', t.ein)}
          {kv('Registration Scheme', t.registration_scheme)}
          {t.verification_notes && (
            <div style={{ marginTop: 10, fontSize: 14, color: T.textMid, whiteSpace: 'pre-wrap' }}>{t.verification_notes}</div>
          )}
        </div>

        <div style={{ ...s.panel, marginBottom: 12 }}>
          <div style={s.panelTitle}>Application Message</div>
          <div style={{ fontSize: 14, color: T.text, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {t.application_message || '(none provided)'}
          </div>
        </div>

        {hasDetail && (
          <div style={{ ...s.panel, marginBottom: 12 }}>
            <div style={s.panelTitle}>Verification Detail</div>

            <div style={{ ...s.kvLabel, marginTop: 6, marginBottom: 4, color: T.accent }}>Applicant</div>
            {kv('Full name', d.applicant_full_name)}
            {kv('Position / role', d.applicant_position)}
            {kv('Relationship to business', d.applicant_relationship)}
            {kv('Employer (if employee)', d.employer_name)}
            {kv('Preferred contact email', d.preferred_contact_email)}

            <div style={{ ...s.kvLabel, marginTop: 12, marginBottom: 4, color: T.accent }}>Business & operating history</div>
            {kv('Year commenced', d.year_commenced)}
            {kv('Years operating', d.years_operating)}
            {kv('Total staff', d.staff_count)}
            {kv('Technical staff', d.technical_staff_count)}

            <div style={{ ...s.kvLabel, marginTop: 12, marginBottom: 4, color: T.accent }}>Technical services</div>
            {kv('Services', arr(d.services))}
            {kv('Other services', d.services_other)}

            <div style={{ ...s.kvLabel, marginTop: 12, marginBottom: 4, color: T.accent }}>Public verification links</div>
            {lnk('Website', t.website)}
            {lnk('Google Business', d.google_business_url)}
            {lnk('LinkedIn / business', d.linkedin_url)}
            {lnk('Social', d.social_url)}
            {lnk('Marketplace / service', d.marketplace_url)}
            {lnk('Other listing', d.other_listing_url)}

            <div style={{ ...s.kvLabel, marginTop: 12, marginBottom: 4, color: T.accent }}>Technical experience & activity</div>
            {kv('Personal years in field', d.personal_years_experience)}
            {kv('Specialisation', d.specialisation)}
            {kv('Customers / jobs per month', d.customers_per_month)}
            {kv('Customer type', d.customer_type)}
            {kv('Service model', d.service_model)}
            {kv('Performs paid technical work', d.performs_paid_work === true ? 'Yes' : '-')}

            <div style={{ ...s.kvLabel, marginTop: 12, marginBottom: 4, color: T.accent }}>Intended use</div>
            {kv('Uses', arr(d.reason_uses))}
            {d.reason_text && (
              <div style={{ marginTop: 6, fontSize: 14, color: T.text, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{d.reason_text}</div>
            )}

            {d.additional_info && (
              <>
                <div style={{ ...s.kvLabel, marginTop: 12, marginBottom: 4, color: T.accent }}>Additional information</div>
                <div style={{ fontSize: 14, color: T.text, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{d.additional_info}</div>
              </>
            )}

            <div style={{ ...s.kvLabel, marginTop: 12, marginBottom: 4, color: T.accent }}>Declarations</div>
            {kv('Information accurate', d.decl_accurate ? 'Confirmed' : '-')}
            {kv('Genuine technical worker', d.decl_genuine ? 'Confirmed' : '-')}
            {kv('Understands manual review', d.decl_manual ? 'Confirmed' : '-')}
            {kv('No approval guarantee', d.decl_no_guarantee ? 'Confirmed' : '-')}
            {kv('Revocable access', d.decl_revocable ? 'Confirmed' : '-')}
          </div>
        )}

        <div style={s.panel}>
          <div style={s.panelTitle}>Review</div>
          <div style={s.panelSub}>
            {canReview
              ? 'Set a new status. The backend enforces which transitions your role may perform.'
              : 'You have view-only access to technician applications.'}
          </div>
          {actionErr && <div style={s.error}>! {actionErr}</div>}
          {actionMsg && <div style={{ ...s.badge('green'), marginBottom: 10 }}>{actionMsg}</div>}

          {canReview && (
            <>
              <div style={{ ...s.grid(2, 12) }}>
                <div>
                  <div style={{ fontSize: 13, color: T.textSub, marginBottom: 4 }}>New Status</div>
                  <select style={{ ...s.select, width: '100%' }} value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                    <option value="">Select...</option>
                    {TECH_APP_STATUSES.filter(st => st !== t.status).map(st => (
                      <option key={st} value={st}>{st.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: T.textSub, marginBottom: 4 }}>Reason / note (optional)</div>
                  <input style={{ ...s.input, width: '100%' }} value={reason} onChange={e => setReason(e.target.value)} placeholder="Recorded with the transition" />
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
                <button style={{ ...s.btn('primary'), background: '#16a34a', borderColor: '#16a34a', opacity: saving ? 0.6 : 1 }}
                  onClick={() => decide('active', 'Approve this application and ACTIVATE Technician access for this account? This grants the real Technician entitlement.')}
                  disabled={saving}>
                  Approve Technician Access
                </button>
                <button style={{ ...s.btn(), borderColor: '#d97706', color: '#d97706', opacity: saving ? 0.6 : 1 }}
                  onClick={() => decide('more_info_required')}
                  disabled={saving}>
                  More Information Required
                </button>
                <button style={{ ...s.btn(), borderColor: '#dc2626', color: '#dc2626', opacity: saving ? 0.6 : 1 }}
                  onClick={() => decide('rejected', 'Decline this technician application? This does not affect their normal VISINVOKE account.')}
                  disabled={saving}>
                  Decline Application
                </button>
              </div>
              <div style={{ fontSize: 12.5, color: T.textSub, marginTop: 8 }}>
                Approve activates the authoritative Technician entitlement immediately (default capabilities auto-provisioned). Add an optional reason above before declining or requesting more information.
              </div>
              <div style={{ marginTop: 14, borderTop: `1px solid ${T.borderSoft}`, paddingTop: 12 }}>
                <div style={{ fontSize: 12.5, color: T.textSub, marginBottom: 6 }}>Advanced: set any allowed status</div>
                <button style={{ ...s.btn('primary'), opacity: (saving || !newStatus) ? 0.6 : 1 }} onClick={applyStatus} disabled={saving || !newStatus}>
                  {saving ? 'Saving...' : 'Apply Status Change'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ---- List view ----
  // Oldest-first work queue: staff process the longest-waiting applications first.
  const sortedTechs = (data && Array.isArray(data.technicians))
    ? [...data.technicians].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
    : [];
  return (
    <>
      <div style={{ ...s.filterRow, marginBottom: 12 }}>
        <input style={{ ...s.input, width: 220 }} placeholder="Search business, name, email..." value={searchFilter}
          onChange={e => { setSearchFilter(e.target.value); setPage(1); }}
          onKeyDown={e => { if (e.key === 'Enter') load(); }} />
        <select style={s.select} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          {TECH_APP_STATUSES.map(st => <option key={st} value={st}>{st.replace(/_/g, ' ')}</option>)}
        </select>
        <button style={s.btn('primary')} onClick={() => { setPage(1); load(); }}>Apply</button>
        <button style={s.btn()} onClick={load}>Refresh</button>
      </div>

      {err && <div style={s.error}>! {err}</div>}
      {!data && <div style={s.loading}>Loading technician applications...</div>}

      {data && (
        <div style={s.tableCard}>
          <div style={s.tableHeader}>
            <div style={s.tableTitle}>Technician Applications<span style={s.tableBadge}>{fmt(data.total)}</span></div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...s.table, minWidth: 860 }}>
              <thead>
                <tr>{['Business', 'Display Name', 'Email', 'Type', 'Country', 'Status', 'Submitted'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {sortedTechs.length === 0 ? (
                  <tr><td colSpan={7} style={{ ...s.td, ...s.empty }}>No technician applications for this filter.</td></tr>
                ) : sortedTechs.map(t => (
                  <tr key={t.technician_account_id} style={{ cursor: 'pointer' }}
                    onClick={() => openDetail(t)}
                    onMouseEnter={e => e.currentTarget.style.background = T.borderSoft}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={s.tds}>{t.business_name || '(no business name)'}</td>
                    <td style={s.td}>{t.display_name || '-'}</td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub }}>{t.contact_email || '-'}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub }}>{t.technician_type || '-'}</span></td>
                    <td style={s.td}>{t.country || '-'}</td>
                    <td style={s.td}><span style={s.badge(TECH_APP_STATUS_COLOR[t.status] || 'grey')}>{(t.status || '').replace(/_/g, ' ')}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub, whiteSpace: 'nowrap' }}>{fmtDate(t.created_at)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={s.pagination}>
            <button style={s.btn()} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>&lt;- Prev</button>
            <span>Page {page}{data.pages ? ' of ' + data.pages : ''}</span>
            <button style={s.btn()} onClick={() => setPage(p => (data.pages && p >= data.pages ? p : p + 1))} disabled={data.pages ? page >= data.pages : false}>Next -&gt;</button>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// SecTechnicianMessages
// Admin handling for technician account-support messages (Feature 2).
// Consumes the staff-gated /v1/technicians/messages routes with the shared
// staff cookie (credentials:'include'), matching the native fetch idiom.
// Section visibility is gated by technicians.view (SECTION_PERMISSIONS). Update
// controls require technicians.review OR technicians.manage; close requires
// technicians.manage. Technicians never receive staff permissions; this is
// staff-side visibility only.
// ---------------------------------------------------------------------------
const TECH_MSG_TYPE_LABELS = {
  application_question:           'Application question',
  verification_issue:             'Verification issue',
  technician_access_problem:      'Technician access problem',
  technician_utilities_issue:     'Technician utilities issue',
  technician_pro_question:        'Technician Pro question',
  billing_subscription_question:  'Billing / subscription',
  bug_report:                     'Bug report',
  feature_request:                'Feature request',
  other:                          'Other',
};
const TECH_MSG_STATUS_COLOR = {
  new: 'blue', in_review: 'amber', waiting_on_technician: 'purple', responded: 'green', closed: 'grey',
};
const TECH_MSG_URGENCY_COLOR = {
  low: 'grey', normal: 'blue', high: 'amber', urgent: 'red',
};
const TECH_MSG_STATUSES = ['new', 'in_review', 'waiting_on_technician', 'responded', 'closed'];

function SecTechnicianMessages({ api }) {
  const { hasPermission } = useStaffAuth();
  const canUpdate = hasPermission('technicians.review') || hasPermission('technicians.manage');
  const canClose  = hasPermission('technicians.manage');

  const [data, setData] = useState(null);
  const [err,  setErr]  = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter,  setStatusFilter]  = useState('');
  const [typeFilter,    setTypeFilter]     = useState('');
  const [urgencyFilter, setUrgencyFilter]  = useState('');

  const [selected,     setSelected]     = useState(null);
  const [editStatus,   setEditStatus]   = useState('');
  const [editAssigned, setEditAssigned] = useState('');
  const [editResponse, setEditResponse] = useState('');
  const [saving,       setSaving]       = useState(false);
  const [actionErr,    setActionErr]    = useState('');
  const [actionMsg,    setActionMsg]    = useState('');

  const load = useCallback(() => {
    setData(null); setErr(null);
    const p = new URLSearchParams({ page, per_page: 50 });
    if (statusFilter)  p.set('status', statusFilter);
    if (typeFilter)    p.set('message_type', typeFilter);
    if (urgencyFilter) p.set('urgency', urgencyFilter);
    fetch(`${api}/v1/technicians/messages?${p}`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (ok) setData(d); else setErr(d.error || 'Failed to load messages'); })
      .catch(() => setErr('Network error'));
  }, [api, page, statusFilter, typeFilter, urgencyFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (m) => {
    setSelected(m);
    setEditStatus(m.status || '');
    setEditAssigned(m.assigned_to || '');
    setEditResponse(m.admin_response || '');
    setActionErr(''); setActionMsg('');
  };

  const applyUpdate = () => {
    if (!canUpdate || !selected) return;
    setSaving(true); setActionErr(''); setActionMsg('');
    fetch(`${api}/v1/technicians/messages/${selected.technician_message_id}`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: editStatus, assigned_to: editAssigned, admin_response: editResponse }),
    })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) { setSelected(d.message); setActionMsg('Saved.'); load(); }
        else setActionErr(d.error || 'Update failed');
      })
      .catch(() => setActionErr('Network error'))
      .finally(() => setSaving(false));
  };

  const closeMessage = () => {
    if (!canClose || !selected) return;
    setSaving(true); setActionErr(''); setActionMsg('');
    fetch(`${api}/v1/technicians/messages/${selected.technician_message_id}/close`, {
      method: 'POST', credentials: 'include',
    })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) { setSelected(d.message); setEditStatus(d.message.status); setActionMsg('Message closed.'); load(); }
        else setActionErr(d.error || 'Close failed');
      })
      .catch(() => setActionErr('Network error'))
      .finally(() => setSaving(false));
  };

  // ---- Detail view ----
  if (selected) {
    const m = selected;
    const typeLabel = TECH_MSG_TYPE_LABELS[m.message_type] || m.message_type;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <button style={s.btn()} onClick={() => { setSelected(null); load(); }}>&lt;- Back to messages</button>
          <span style={s.badge(TECH_MSG_STATUS_COLOR[m.status] || 'grey')}>{(m.status || '').replace(/_/g, ' ')}</span>
          <span style={s.badge(TECH_MSG_URGENCY_COLOR[m.urgency] || 'grey')}>{m.urgency}</span>
        </div>

        <div style={{ ...s.panel, marginBottom: 12 }}>
          <div style={s.panelTitle}>{m.subject || typeLabel}</div>
          <div style={s.panelSub}>{typeLabel} &middot; received {fmtDate(m.created_at)}</div>
          <div style={{ ...s.kvRow }}><span style={s.kvLabel}>Message ID</span><span style={{ ...s.kvValue, fontFamily: 'monospace', fontSize: 13 }}>{m.technician_message_id}</span></div>
          <div style={s.kvRow}><span style={s.kvLabel}>User ID</span><span style={{ ...s.kvValue, fontFamily: 'monospace', fontSize: 13 }}>{m.user_id}</span></div>
          <div style={s.kvRow}><span style={s.kvLabel}>Technician Account</span><span style={{ ...s.kvValue, fontFamily: 'monospace', fontSize: 13 }}>{m.technician_account_id || 'None (applicant)'}</span></div>
          {m.reference && <div style={s.kvRow}><span style={s.kvLabel}>Reference</span><span style={s.kvValue}>{m.reference}</span></div>}
          <div style={s.kvRow}><span style={s.kvLabel}>Last Updated</span><span style={s.kvValue}>{fmtDate(m.updated_at)}</span></div>
          {m.closed_at && <div style={s.kvRow}><span style={s.kvLabel}>Closed</span><span style={s.kvValue}>{fmtDate(m.closed_at)}</span></div>}
          <div style={{ marginTop: 12, fontSize: 14, color: T.text, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{m.message}</div>
        </div>

        <div style={s.panel}>
          <div style={s.panelTitle}>Admin Handling</div>
          <div style={s.panelSub}>
            {canUpdate ? 'Update status, assignment, and the response shown to the technician.' : 'You have view-only access to technician messages.'}
          </div>

          {actionErr && <div style={s.error}>! {actionErr}</div>}
          {actionMsg && <div style={{ ...s.badge('green'), marginBottom: 10 }}>{actionMsg}</div>}

          <div style={{ ...s.grid(2, 12) }}>
            <div>
              <div style={{ fontSize: 13, color: T.textSub, marginBottom: 4 }}>Status</div>
              <select style={{ ...s.select, width: '100%' }} value={editStatus} disabled={!canUpdate}
                onChange={e => setEditStatus(e.target.value)}>
                {TECH_MSG_STATUSES.map(st => <option key={st} value={st}>{st.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 13, color: T.textSub, marginBottom: 4 }}>Assigned To (staff user id)</div>
              <input style={{ ...s.input, width: '100%' }} value={editAssigned} disabled={!canUpdate}
                onChange={e => setEditAssigned(e.target.value)} placeholder="Unassigned" />
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, color: T.textSub, marginBottom: 4 }}>Response to technician</div>
            <textarea style={{ ...s.input, width: '100%', minHeight: 120, resize: 'vertical' }} value={editResponse} disabled={!canUpdate}
              onChange={e => setEditResponse(e.target.value)} placeholder="This text is shown to the technician." />
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            {canUpdate && (
              <button style={{ ...s.btn('primary'), opacity: saving ? 0.6 : 1 }} onClick={applyUpdate} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
            {canClose && m.status !== 'closed' && (
              <button style={{ ...s.btn(), opacity: saving ? 0.6 : 1 }} onClick={closeMessage} disabled={saving}>
                Close Message
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- List view ----
  return (
    <>
      <div style={{ ...s.filterRow, marginBottom: 12 }}>
        <select style={s.select} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          {TECH_MSG_STATUSES.map(st => <option key={st} value={st}>{st.replace(/_/g, ' ')}</option>)}
        </select>
        <select style={s.select} value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="">All Types</option>
          {Object.keys(TECH_MSG_TYPE_LABELS).map(t => <option key={t} value={t}>{TECH_MSG_TYPE_LABELS[t]}</option>)}
        </select>
        <select style={s.select} value={urgencyFilter} onChange={e => { setUrgencyFilter(e.target.value); setPage(1); }}>
          <option value="">All Urgencies</option>
          {['low', 'normal', 'high', 'urgent'].map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <button style={s.btn('primary')} onClick={() => { setPage(1); load(); }}>Apply</button>
        <button style={s.btn()} onClick={load}>Refresh</button>
      </div>

      {err && <div style={s.error}>! {err}</div>}
      {!data && <div style={s.loading}>Loading technician messages...</div>}

      {data && (
        <div style={s.tableCard}>
          <div style={s.tableHeader}>
            <div style={s.tableTitle}>Technician Messages<span style={s.tableBadge}>{fmt(data.total)}</span></div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...s.table, minWidth: 820 }}>
              <thead>
                <tr>{['Subject', 'Type', 'Urgency', 'Status', 'User', 'Received'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.messages.length === 0 ? (
                  <tr><td colSpan={6} style={{ ...s.td, ...s.empty }}>No technician messages for this filter.</td></tr>
                ) : data.messages.map(m => (
                  <tr key={m.technician_message_id} style={{ cursor: 'pointer' }}
                    onClick={() => openDetail(m)}
                    onMouseEnter={e => e.currentTarget.style.background = T.borderSoft}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={s.tds}>
                      <div style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.subject || TECH_MSG_TYPE_LABELS[m.message_type] || m.message_type}
                      </div>
                    </td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub }}>{TECH_MSG_TYPE_LABELS[m.message_type] || m.message_type}</span></td>
                    <td style={s.td}><span style={s.badge(TECH_MSG_URGENCY_COLOR[m.urgency] || 'grey')}>{m.urgency}</span></td>
                    <td style={s.td}><span style={s.badge(TECH_MSG_STATUS_COLOR[m.status] || 'grey')}>{(m.status || '').replace(/_/g, ' ')}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub, fontFamily: 'monospace' }}>{(m.user_id || '').slice(0, 12)}</span></td>
                    <td style={s.td}><span style={{ fontSize: 13, color: T.textSub, whiteSpace: 'nowrap' }}>{fmtDate(m.created_at)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={s.pagination}>
            <button style={s.btn()} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>&lt;- Prev</button>
            <span>Page {page}{data.pages ? ' of ' + data.pages : ''}</span>
            <button style={s.btn()} onClick={() => setPage(p => (data.pages && p >= data.pages ? p : p + 1))} disabled={data.pages ? page >= data.pages : false}>Next -&gt;</button>
          </div>
        </div>
      )}
    </>
  );
}


function SecSupport({api,defaultFilter}){
  const{hasPermission}=useStaffAuth();
  const[data,setData]=useState(null);const[err,setErr]=useState(null);
  const[page,setPage]=useState(1);
  const[statusFilter,setStatusFilter]=useState(
    defaultFilter==='resolved'?'resolved':defaultFilter==='escalated'?'escalated':''
  );
  const[severityFilter,setSeverityFilter]=useState('');
  const[searchFilter,setSearchFilter]=useState('');
  const[unresolved,setUnresolved]=useState(defaultFilter==='unresolved');
  const[assignedFilter,setAssignedFilter]=useState(defaultFilter==='assigned'?'me':'');
  const[selectedTicket,setSelectedTicket]=useState(null);
  const[selectedIds,setSelectedIds]=useState(new Set());
  const[showBulk,setShowBulk]=useState(false);

  const{staffUser}=useStaffAuth();

  const load=useCallback(()=>{
    setData(null);
    const p=new URLSearchParams({page,per_page:50});
    if(statusFilter)p.set('status',statusFilter);
    if(severityFilter)p.set('severity',severityFilter);
    if(searchFilter)p.set('search',searchFilter);
    if(unresolved)p.set('unresolved','true');
    if(assignedFilter==='me'&&staffUser)p.set('assigned_to',staffUser.id);
    else if(assignedFilter&&assignedFilter!=='me')p.set('assigned_to',assignedFilter);
    fetch(`${api}/v1/staff/support/issues?${p}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed');})
      .catch(()=>setErr('Network error'));
  },[api,page,statusFilter,severityFilter,searchFilter,unresolved,assignedFilter,staffUser]);

  useEffect(()=>{load();},[load]);

  if(selectedTicket)return(
    <SupportTicketDetail
      api={api}
      ticketId={selectedTicket}
      onBack={()=>{setSelectedTicket(null);load();}}
      hasPermission={hasPermission}
    />
  );

  const summary=(data&&data.summary)||{};
  const allTicketIds=((data&&data.tickets)||[]).map(t=>t.ticket_id);
  const allSelected=allTicketIds.length>0&&allTicketIds.every(id=>selectedIds.has(id));
  const toggleAll=()=>setSelectedIds(allSelected?new Set():new Set(allTicketIds));
  const toggleOne=(id)=>setSelectedIds(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n;});

  return(
    <>
      {showBulk&&<BulkActionPanel api={api} entityType="ticket" entityIds={[...selectedIds]} onClose={()=>setShowBulk(false)} hasPermission={hasPermission}/>}
      {/* Summary KPI strip */}
      {data&&(
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:12}}>
          {[
            {label:'Open',      val:summary.open,      color:T.chart1},
            {label:'Triaged',   val:summary.triaged,   color:T.chart4},
            {label:'Pending',   val:summary.pending,   color:T.amber},
            {label:'Escalated', val:summary.escalated, color:summary.escalated>0?T.red:T.chart2},
          ].map(({label,val,color})=>(
            <div key={label} style={{...s.panel,padding:'10px 14px',borderTop:`3px solid ${color}`}}>
              <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>{label}</div>
              <div style={{fontSize:28,fontWeight:800,color:T.text,lineHeight:1}}>{fmt(val)||0}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{...s.filterRow,marginBottom:12,flexWrap:'wrap'}}>
        <input style={{...s.input,width:200}} placeholder="Search ticket, email..." value={searchFilter}
          onChange={e=>{setSearchFilter(e.target.value);setPage(1);}}
          onKeyDown={e=>{if(e.key==='Enter')load();}}/>
        <select style={s.select} value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setPage(1);}}>
          <option value="">All Statuses</option>
          {['open','triaged','pending','escalated','resolved','closed'].map(st=><option key={st} value={st}>{st}</option>)}
        </select>
        <select style={s.select} value={severityFilter} onChange={e=>{setSeverityFilter(e.target.value);setPage(1);}}>
          <option value="">All Severities</option>
          {['low','medium','high','critical'].map(sv=><option key={sv} value={sv}>{sv}</option>)}
        </select>
        <select style={s.select} value={assignedFilter} onChange={e=>{setAssignedFilter(e.target.value);setPage(1);}}>
          <option value="">All Assignees</option>
          <option value="me">Assigned to Me</option>
        </select>
        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:14,color:T.textSub,cursor:'pointer'}}>
          <input type="checkbox" checked={unresolved} onChange={e=>{setUnresolved(e.target.checked);setPage(1);}} style={{cursor:'pointer'}}/>
          Unresolved only
        </label>
        <button style={s.btn('primary')} onClick={()=>{setPage(1);load();}}>Apply</button>
        <button style={s.btn()} onClick={load}>Refresh</button>
      </div>

      {err&&<div style={s.error}>! {err}</div>}
      {!data&&<Loader/>}
      {data&&(
        <>
        {selectedIds.size>0&&(
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:T.accentLight,border:`1px solid ${T.accentBorder}`,borderRadius:6,marginBottom:8,fontSize:13}}>
            <span style={{color:T.accent,fontWeight:700}}>{selectedIds.size} ticket{selectedIds.size!==1?'s':''} selected</span>
            {hasPermission('bulk.execute')&&<button style={{...s.btn('primary'),fontSize:12,padding:'4px 12px'}} onClick={()=>setShowBulk(true)}>Bulk Action</button>}
            <button style={{...s.btn(),fontSize:12,padding:'4px 10px'}} onClick={()=>setSelectedIds(new Set())}>Clear</button>
          </div>
        )}
        <TableCard title="Support Queue" count={data.total}>
          <div style={{overflowX:'auto'}}>
            <table style={{...s.table,minWidth:900}}>
              <thead>
                <tr>
                  <th style={{...s.th,width:32}}><input type="checkbox" checked={allSelected} onChange={toggleAll} style={{cursor:'pointer'}}/></th>
                  {['Ticket ID','User','Subject','Severity','Status','Assigned','Notes','Open','Updated'].map(h=>(
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.tickets.length===0?(
                  <tr><td colSpan={10} style={{...s.td,...s.empty}}>No tickets found for this filter.</td></tr>
                ):data.tickets.map(t=>{
                  const isEscalated=t.status==='ESCALATED'||t.status==='escalated';
                  return(
                    <tr key={t.ticket_id} style={{cursor:'pointer',background:isEscalated?T.redBg:selectedIds.has(t.ticket_id)?T.accentLight:'transparent'}}
                      onClick={()=>setSelectedTicket(t.ticket_id)}
                      onMouseEnter={e=>{ if(!selectedIds.has(t.ticket_id)) e.currentTarget.style.background=isEscalated?T.redBg:T.borderSoft; }}
                      onMouseLeave={e=>e.currentTarget.style.background=isEscalated?T.redBg:selectedIds.has(t.ticket_id)?T.accentLight:'transparent'}
                    >
                      <td style={s.td} onClick={e=>e.stopPropagation()}><input type="checkbox" checked={selectedIds.has(t.ticket_id)} onChange={()=>toggleOne(t.ticket_id)} style={{cursor:'pointer'}}/></td>
                      <td style={s.tds}><EntityChip type="ticket" id={t.ticket_id} label={t.ticket_id}/></td>
                      <td style={s.td}>
                        <EntityChip type="user" id={t.user_id} label={t.user_name||t.user_email}/>
                        {t.user_name&&<div style={{fontSize:12,color:T.textFaint,marginTop:2}}>{t.user_email}</div>}
                      </td>
                      <td style={s.td}><div style={{fontSize:14,color:T.text,maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.subject||'(no subject)'}</div><div style={{fontSize:12,color:T.textFaint}}>{t.type}</div></td>
                      <td style={s.td}><SupportSeverityPill severity={t.severity}/></td>
                      <td style={s.td}><SupportStatusPill status={t.status}/></td>
                      <td style={s.td}><span style={{fontSize:13,color:t.assignee_name?T.text:T.textFaint}}>{t.assignee_name||'Unassigned'}</span></td>
                      <td style={s.td}><span style={{fontSize:13,fontWeight:600,color:t.note_count>0?T.accent:T.textFaint}}>{t.note_count||0}</span></td>
                      <td style={s.td}><span style={{fontSize:13,color:T.textSub,whiteSpace:'nowrap'}}>{openTime(t.created_at)}</span></td>
                      <td style={s.td}><span style={{fontSize:13,color:T.textSub,whiteSpace:'nowrap'}}>{fmtAgo(t.updated_at||t.created_at)}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data.pages>1&&<Pager page={page} pages={data.pages} total={data.total} onPage={setPage}/>}
        </TableCard>
        </>
      )}
    </>
  );
}

// ============================================================================
// END SUPPORT OPERATIONS CENTER
// ============================================================================

// ============================================================================
// USER INTELLIGENCE CENTER
// ============================================================================

// Severity -> visual config
const INTEL_SEVERITY = {
  low:      {color:T.accent,     bg:T.accentLight,  border:T.accentBorder,  dot:'#3182ce'},
  medium:   {color:T.amber,      bg:T.amberBg,      border:T.amberBorder,   dot:'#d69e2e'},
  high:     {color:'#c05621',    bg:'#fffaf0',       border:'#fbd38d',       dot:'#c05621'},
  critical: {color:T.red,        bg:T.redBg,         border:T.redBorder,     dot:'#e53e3e'},
};

// Event type -> filter category mapping
const INTEL_TYPE_FILTER = {
  admin_action:  'admin',
  mail_event:    'email',
  delivery_event:'email',
  token_event:   'email',
  suppression:   'email',
  staff_session: 'sessions',
  failed_login:  'security',
  support_ticket:'support',
};

// SVG icons for each event type
function IntelIcon({type,size=16}){
  const paths = {
    action:     <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    mail:       <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
    token:      <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    delivery:   <><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></>,
    ticket:     <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>,
    suppression:<><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></>,
    session:    <><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>,
    login:      <><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></>,
    note:       <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></>,
  };
  const iconKey = {
    admin_action:'action', mail_event:'mail', delivery_event:'delivery',
    token_event:'token', suppression:'suppression', staff_session:'session',
    failed_login:'login', support_ticket:'ticket', staff_note:'note',
  }[type] || 'action';
  return(
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {paths[iconKey]}
    </svg>
  );
}

// Single timeline event row
function IntelEventRow({ev}){
  const[expanded,setExpanded]=useState(false);
  const sev=INTEL_SEVERITY[ev.severity]||INTEL_SEVERITY.low;
  return(
    <div style={{marginBottom:2,background:T.surface,borderRadius:'0 6px 6px 0',overflow:'hidden',border:`1px solid ${T.borderSoft}`,borderLeft:`3px solid ${sev.dot}`}}>
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'9px 14px',cursor:'pointer'}}
        onClick={()=>setExpanded(v=>!v)}>
        <span style={{color:sev.color,flexShrink:0,display:'flex',alignItems:'center'}}>
          <IntelIcon type={ev.type}/>
        </span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            <span style={{fontSize:14,fontWeight:700,color:T.text}}>{ev.title}</span>
            {ev.severity==='critical'&&<span style={{fontSize:11,fontWeight:800,color:sev.color,background:sev.bg,border:`1px solid ${sev.border}`,borderRadius:3,padding:'1px 5px',letterSpacing:'0.05em'}}>CRITICAL</span>}
            {ev.severity==='high'&&<span style={{fontSize:11,fontWeight:700,color:sev.color,background:sev.bg,border:`1px solid ${sev.border}`,borderRadius:3,padding:'1px 5px',letterSpacing:'0.05em'}}>HIGH</span>}
          </div>
          {ev.detail&&<div style={{fontSize:13,color:T.textSub,marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:500}}>{ev.detail}</div>}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:12,flexShrink:0}}>
          {ev.actor&&<span style={{fontSize:12,color:T.textFaint,fontFamily:'monospace'}}>{ev.actor.length>20?ev.actor.slice(0,16)+'...':ev.actor}</span>}
          <span style={{fontSize:12,color:T.textFaint,whiteSpace:'nowrap'}}>{fmtAgo(ev.ts)}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.textFaint} strokeWidth="2" style={{transform:expanded?'rotate(180deg)':'none',transition:'transform 0.15s'}}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </div>
      {expanded&&(
        <div style={{padding:'0 14px 10px 14px',borderTop:`1px solid ${T.borderSoft}`}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px 24px',marginBottom:8,paddingTop:8}}>
            <div style={s.kvRow}><span style={s.kvLabel}>Type</span><span style={{...s.kvValue,fontSize:13}}>{ev.type}</span></div>
            <div style={s.kvRow}><span style={s.kvLabel}>Severity</span><span style={{...s.kvValue,fontSize:13,color:sev.color}}>{ev.severity}</span></div>
            <div style={s.kvRow}><span style={s.kvLabel}>Timestamp</span><span style={{...s.kvValue,fontSize:13}}>{fmtDate(ev.ts)}</span></div>
            {ev.actor&&<div style={s.kvRow}><span style={s.kvLabel}>Actor</span><span style={{...s.kvValue,fontSize:13,fontFamily:'monospace'}}>{ev.actor}</span></div>}
          </div>
          {ev.meta&&Object.keys(ev.meta).length>0&&(
            <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,padding:'8px 10px'}}>
              <div style={{fontSize:11,fontWeight:700,color:T.textFaint,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:5}}>Metadata</div>
              <pre style={{margin:0,fontSize:12,color:T.textMid,fontFamily:'monospace',whiteSpace:'pre-wrap',wordBreak:'break-all',lineHeight:1.6}}>
                {JSON.stringify(ev.meta,null,2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Right-panel context card
function IntelPanel({title,children,mb=12}){
  return(
    <div style={{...s.panel,marginBottom:mb,padding:'12px 14px'}}>
      <div style={{fontSize:13,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:10}}>{title}</div>
      {children}
    </div>
  );
}

function IntelKV({label,val,color}){
  return(
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:`1px solid ${T.borderSoft}`,fontSize:13}}>
      <span style={{color:T.textSub}}>{label}</span>
      <span style={{fontWeight:700,color:color||T.text}}>{val||'-'}</span>
    </div>
  );
}

// Main section
function SecUserIntelligence({api,initialUserId}){
  const[userId,setUserId]=useState(initialUserId||'');
  const[input,setInput]=useState(initialUserId||'');
  const[data,setData]=useState(null);
  const[loading,setLoading]=useState(false);
  const[err,setErr]=useState(null);
  const[filter,setFilter]=useState('all');
  const[search,setSearch]=useState('');

  const load=useCallback((uid)=>{
    if(!uid)return;
    setLoading(true);setData(null);setErr(null);
    fetch(`${api}/v1/staff/intelligence/${uid}`,{credentials:'include'})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{if(ok)setData(d);else setErr(d.error||'Failed to load intelligence');})
      .catch(()=>setErr('Network error'))
      .finally(()=>setLoading(false));
  },[api]);

  useEffect(()=>{
    if(initialUserId){setUserId(initialUserId);setInput(initialUserId);load(initialUserId);}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[initialUserId]);

  const handleSearch=()=>{
    const uid=input.trim();
    if(!uid)return;
    setUserId(uid);
    load(uid);
  };

  // Filter + text-search timeline
  const timeline=(data&&data.timeline)||[];
  const filtered=timeline.filter(ev=>{
    const catMatch=filter==='all'||INTEL_TYPE_FILTER[ev.type]===filter;
    if(!catMatch)return false;
    if(!search)return true;
    const q=search.toLowerCase();
    return (ev.title||'').toLowerCase().includes(q)||(ev.detail||'').toLowerCase().includes(q)||(ev.actor||'').toLowerCase().includes(q);
  });

  const FILTERS=[
    {id:'all',     label:'All'},
    {id:'security',label:'Security'},
    {id:'email',   label:'Email'},
    {id:'sessions',label:'Sessions'},
    {id:'support', label:'Support'},
    {id:'admin',   label:'Admin'},
  ];

  const critCount=filtered.filter(e=>e.severity==='critical').length;
  const highCount=filtered.filter(e=>e.severity==='high').length;

  if(!data&&!loading&&!err){
    return(
      <div style={{...s.panel,padding:'32px 24px',textAlign:'center',maxWidth:480,margin:'40px auto'}}>
        <div style={{fontSize:17,fontWeight:700,color:T.text,marginBottom:8}}>User Intelligence Console</div>
        <div style={{fontSize:14,color:T.textSub,marginBottom:20}}>Enter a user ID to load their unified intelligence timeline.</div>
        <div style={{display:'flex',gap:8}}>
          <input style={{...s.input,flex:1}} placeholder="User UUID..." value={input} onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{if(e.key==='Enter')handleSearch();}}/>
          <button style={s.btn('primary')} onClick={handleSearch}>Load</button>
        </div>
        {err&&<div style={{...s.error,marginTop:12}}>! {err}</div>}
      </div>
    );
  }

  return(
    <>
      {/* Search bar always visible once loaded */}
      <div style={{...s.filterRow,marginBottom:12}}>
        <input style={{...s.input,width:300}} placeholder="User UUID..." value={input} onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter')handleSearch();}}/>
        <button style={s.btn('primary')} onClick={handleSearch}>Load</button>
        {data&&<button style={s.btn()} onClick={()=>load(userId)}>Refresh</button>}
      </div>

      {err&&<div style={s.error}>! {err}</div>}
      {loading&&<Loader/>}

      {data&&(()=>{
        const u=data.user||{};
        const sub=data.subscription;
        return(
          <>
            {/* Overview KPI strip */}
            <div style={{background:'rgba(255,255,255,0.03)',borderRadius:10,padding:'16px 20px',marginBottom:12,border:'1px solid rgba(255,255,255,0.07)'}}>
              <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:12,flexWrap:'wrap'}}>
                <div style={{width:38,height:38,borderRadius:'50%',background:T.accentLight,border:`1px solid ${T.accentBorder}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:17,fontWeight:800,color:T.accent,flexShrink:0}}>
                  {(u.first_name?.[0]||u.email?.[0]||'?').toUpperCase()}
                </div>
                <div>
                  <div style={{fontSize:17,fontWeight:700,color:T.text}}>{`${u.first_name||''} ${u.last_name||''}`.trim()||'Unknown'}</div>
                  <div style={{fontSize:13,color:T.textSub}}>{u.email}</div>
                </div>
                <div style={{marginLeft:'auto',display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                  {data.suppressed&&<span style={{...s.badge('red'),fontSize:12}}>SUPPRESSED</span>}
                  {u.email_verified&&<span style={{...s.badge('green'),fontSize:12}}>Verified</span>}
                  {u.is_admin&&<span style={{...s.badge('purple'),fontSize:12}}>Admin</span>}
                  <SBadge status={u.tier||'trial'}/>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(8,1fr)',gap:10}}>
                {[
                  {label:'Tier',              val:u.tier||'-',                color:T.chart1},
                  {label:'Credits',           val:fmt(u.credits),             color:T.chart2},
                  {label:'Total Jobs',        val:fmt(data.total_jobs),       color:T.chart3},
                  {label:'Failed Jobs',       val:fmt(data.failed_jobs),      color:data.failed_jobs>0?T.red:T.chart2},
                  {label:'Open Tickets',      val:fmt(data.open_tickets),     color:data.open_tickets>0?T.amber:T.chart2},
                  {label:'Mail Failures',     val:fmt(data.mail_failed),      color:data.mail_failed>0?T.red:T.chart2},
                  {label:'Staff Sessions',    val:fmt(data.active_staff_sessions), color:T.chart4},
                  {label:'Suppression',       val:data.suppressed?(data.suppression_type||'Yes'):'None', color:data.suppressed?T.red:T.green},
                ].map(({label,val,color})=>(
                  <div key={label} style={{background:T.surface,borderRadius:6,padding:'10px 12px',border:'1px solid rgba(255,255,255,0.07)',borderTop:`3px solid ${color}`}}>
                    <div style={{fontSize:11,fontWeight:700,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>{label}</div>
                    <div style={{fontSize:20,fontWeight:800,color:T.text,lineHeight:1}}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Severity alert strip */}
            {(critCount>0||highCount>0)&&(
              <div style={{display:'flex',gap:8,marginBottom:10}}>
                {critCount>0&&<div style={{padding:'6px 12px',borderRadius:5,background:T.redBg,border:`1px solid ${T.redBorder}`,fontSize:13,fontWeight:700,color:T.red}}>{critCount} CRITICAL event{critCount>1?'s':''}</div>}
                {highCount>0&&<div style={{padding:'6px 12px',borderRadius:5,background:'#fffaf0',border:'1px solid #fbd38d',fontSize:13,fontWeight:700,color:'#c05621'}}>{highCount} HIGH event{highCount>1?'s':''}</div>}
              </div>
            )}

            {/* Main layout: timeline + right panels */}
            <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>

              {/* LEFT: Timeline */}
              <div style={{flex:1,minWidth:0}}>
                {/* Filter chips + search */}
                <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>
                  {FILTERS.map(f=>(
                    <button key={f.id} onClick={()=>setFilter(f.id)}
                      style={{padding:'5px 12px',borderRadius:20,fontSize:13,fontWeight:600,cursor:'pointer',border:`1px solid ${filter===f.id?T.accent:T.border}`,background:filter===f.id?T.accent:'transparent',color:filter===f.id?'#fff':T.textSub,transition:'all 0.1s'}}>
                      {f.label}
                    </button>
                  ))}
                  <input style={{...s.input,fontSize:13,padding:'5px 10px',flex:1,minWidth:140}} placeholder="Filter events..." value={search} onChange={e=>setSearch(e.target.value)}/>
                  <span style={{fontSize:13,color:T.textFaint,whiteSpace:'nowrap'}}>{filtered.length} events</span>
                </div>

                {/* Timeline feed */}
                <div style={{display:'flex',flexDirection:'column',gap:3}}>
                  {filtered.length===0?(
                    <div style={{...s.panel,...s.empty}}>No events match this filter.</div>
                  ):filtered.map((ev,i)=>(
                    <IntelEventRow key={`${ev.type}-${ev.ts}-${i}`} ev={ev}/>
                  ))}
                </div>
              </div>

              {/* RIGHT: Context panels */}
              <div style={{width:260,flexShrink:0}}>

                <IntelPanel title="Account">
                  <IntelKV label="Email" val={<span style={{fontSize:12,wordBreak:'break-all'}}>{u.email}</span>}/>
                  <IntelKV label="Name" val={`${u.first_name||''} ${u.last_name||''}`.trim()||'-'}/>
                  <IntelKV label="Country" val={u.country||'-'}/>
                  <IntelKV label="Joined" val={fmtDate(u.created_at)}/>
                  <IntelKV label="Verified" val={u.email_verified?'Yes':'No'} color={u.email_verified?T.green:T.amber}/>
                  <IntelKV label="User ID" val={<span style={{fontSize:11,fontFamily:'monospace',wordBreak:'break-all'}}>{u.id}</span>}/>
                </IntelPanel>

                <IntelPanel title="Subscription">
                  {sub?(
                    <>
                      <IntelKV label="Plan" val={sub.plan?.name||sub.plan_id||'-'}/>
                      <IntelKV label="Status" val={sub.status} color={sub.status==='active'?T.green:T.amber}/>
                      <IntelKV label="Period End" val={fmtDate(sub.current_period_end)}/>
                      <IntelKV label="Used" val={fmt(sub.specs_used_this_period)}/>
                    </>
                  ):<div style={{fontSize:13,color:T.textFaint,padding:'4px 0'}}>No active subscription</div>}
                </IntelPanel>

                <IntelPanel title="Credits">
                  <IntelKV label="Balance" val={fmt(u.credits)} color={T.chart2}/>
                  <IntelKV label="Topup" val={fmt(u.topup_credits)}/>
                </IntelPanel>

                <IntelPanel title="Email Status">
                  <IntelKV label="Suppressed" val={data.suppressed?'YES':'No'} color={data.suppressed?T.red:T.green}/>
                  {data.suppressed&&<IntelKV label="Reason" val={data.suppression_type||'-'} color={T.red}/>}
                  <IntelKV label="Mail Failures" val={fmt(data.mail_failed)} color={data.mail_failed>0?T.red:T.green}/>
                </IntelPanel>

                <IntelPanel title="Timeline Summary">
                  {[
                    {label:'Total Events', val:data.timeline_count},
                    {label:'Critical',     val:data.timeline.filter(e=>e.severity==='critical').length, color:T.red},
                    {label:'High',         val:data.timeline.filter(e=>e.severity==='high').length,     color:'#c05621'},
                    {label:'Admin Actions',val:data.timeline.filter(e=>e.type==='admin_action').length},
                    {label:'Mail Events',  val:data.timeline.filter(e=>e.type==='mail_event').length},
                    {label:'Tickets',      val:data.timeline.filter(e=>e.type==='support_ticket').length},
                    {label:'Failed Logins',val:data.timeline.filter(e=>e.type==='failed_login').length, color:data.timeline.filter(e=>e.type==='failed_login').length>0?T.amber:undefined},
                  ].map(({label,val,color})=>(
                    <IntelKV key={label} label={label} val={fmt(val)} color={color}/>
                  ))}
                </IntelPanel>

              </div>
            </div>
          </>
        );
      })()}
    </>
  );
}

// ============================================================================
// END USER INTELLIGENCE CENTER
// ============================================================================

// ============================================================================
// OPERATIONAL GUIDANCE SYSTEM
// ============================================================================

// Centralized guidance configuration — easy to extend, no architectural changes needed

// ── Cross-System Intelligence Rules ─────────────────────────────────────────
// Deterministic relationship rules between operational domains.
// Each rule defines: when you're in section X, show related signals from Y.
const CROSS_SYSTEM_RULES = {
  // Revenue signals
  plans: [
    { signal: 'Revenue declining?', related: ['User Intelligence', 'Support Queue', 'Communications'], sections: ['intel', 'support_queue', 'comms_threads'], reason: 'Churn correlates with support escalations and communication gaps.' },
    { signal: 'Churn exceeding upgrades?', related: ['User Intelligence', 'Support Escalations'], sections: ['intel', 'support_escalated'], reason: 'Churned users often have unresolved support history.' },
  ],
  margin: [
    { signal: 'Margin compressing?', related: ['Platform Providers', 'Cost Analytics', 'Credit Monitor'], sections: ['platform_providers', 'costs', 'credits'], reason: 'Margin compression originates from provider cost or credit anomalies.' },
  ],
  credits: [
    { signal: 'Credit usage spiking?', related: ['Platform Jobs', 'Usage Ledger', 'Platform Credits'], sections: ['platform_jobs', 'ledger', 'platform_credits'], reason: 'Credit spikes correlate with generation volume or anomalous consumption.' },
  ],
  // Usage signals
  generations: [
    { signal: 'Volume dropping?', related: ['Platform Providers', 'Platform Failures', 'Automation Incidents'], sections: ['platform_providers', 'platform_failures', 'auto_incidents'], reason: 'Generation drops are typically caused by provider degradation or automation failures.' },
  ],
  generators: [
    { signal: 'Generator fail rate elevated?', related: ['Platform Failures', 'Platform Providers'], sections: ['platform_failures', 'platform_providers'], reason: 'Generator failures usually trace to provider errors or spec issues.' },
  ],
  // Support signals
  support_queue: [
    { signal: 'Ticket volume rising?', related: ['Platform Overview', 'Staff Workload', 'User Intelligence'], sections: ['platform_overview', 'staff', 'intel'], reason: 'Support spikes often correlate with platform failures or user dissatisfaction.' },
    { signal: 'Escalations growing?', related: ['Support Escalated Queue', 'Staff Accounts', 'User Intelligence'], sections: ['support_escalated', 'staff', 'intel'], reason: 'Escalations indicate resolution failures or workload imbalance.' },
  ],
  support_escalated: [
    { signal: 'Escalations correlate with platform issues?', related: ['Platform Overview', 'Platform Failures'], sections: ['platform_overview', 'platform_failures'], reason: 'Escalations often cluster during platform degradation events.' },
  ],
  // Platform signals
  platform_overview: [
    { signal: 'Failures rising?', related: ['Platform Providers', 'Automation Incidents', 'Autonomous Recovery'], sections: ['platform_providers', 'auto_incidents', 'autonomous_overview'], reason: 'Platform failures cascade from provider degradation or automation failures.' },
    { signal: 'Queue depth growing?', related: ['Platform Queues', 'Platform Providers', 'Autonomous Queue Recovery'], sections: ['platform_queues', 'platform_providers', 'autonomous_queue'], reason: 'Queue saturation originates from provider or worker issues.' },
  ],
  platform_failures: [
    { signal: 'Credits deducted on failed jobs?', related: ['Credit Monitor', 'User Actions'], sections: ['credits', 'actions'], reason: 'Failed jobs with credit deductions require refund before requeue.' },
    { signal: 'Failures triggering support tickets?', related: ['Support Queue', 'User Intelligence'], sections: ['support_queue', 'intel'], reason: 'Users experiencing failures often raise support tickets.' },
  ],
  platform_providers: [
    { signal: 'Provider degraded?', related: ['Autonomous Provider Failover', 'Platform Failures', 'Platform Queues'], sections: ['autonomous_provider', 'platform_failures', 'platform_queues'], reason: 'Provider degradation triggers failures and queue saturation.' },
  ],
  // Communications signals
  comms_overview: [
    { signal: 'Bounce rate rising?', related: ['Suppression List', 'Delivery Monitor', 'Mail Queue'], sections: ['comms_suppression', 'comms_delivery', 'comms_queue'], reason: 'Bounce spikes require immediate suppression list management.' },
  ],
  // Security signals
  security: [
    { signal: 'Suspicious login patterns?', related: ['User Intelligence', 'Staff Sessions', 'Audit Log'], sections: ['intel', 'sessions', 'audit'], reason: 'Security incidents require cross-reference with user timeline and staff actions.' },
    { signal: 'Possible compromise?', related: ['Autonomous Security Response', 'User Actions'], sections: ['autonomous_security', 'actions'], reason: 'Compromise indicators warrant immediate autonomous security response.' },
  ],
  // Staff signals
  staff: [
    { signal: 'Workload imbalanced?', related: ['Support Queue', 'Communication Threads'], sections: ['support_queue', 'comms_threads'], reason: 'Staff workload imbalance directly impacts response times in support and communications.' },
  ],
  // Automation signals
  auto_incidents: [
    { signal: 'Incidents correlate with platform failures?', related: ['Platform Overview', 'Platform Providers', 'Autonomous Recovery'], sections: ['platform_overview', 'platform_providers', 'autonomous_overview'], reason: 'Automation incidents are typically downstream of platform degradation.' },
  ],
};

const GUIDANCE_CONTENT = {
  // ── SECTION GUIDANCE ────────────────────────────────────────────────────────
  sections: {
    overview: {
      title: 'Dashboard Overview',
      purpose: 'Platform-wide operational health at a glance. Use this to spot trends and anomalies before drilling into specifics.',
      common_tasks: ['Check active user counts', 'Monitor generation volume', 'Review cost vs revenue', 'Identify anomalies early'],
      workflow: ['Review KPIs', 'Check for anomalies in charts', 'Drill into specific sections if needed'],
      tips: ['High cost with low generation volume signals provider inefficiency.', 'Active user drop with stable generation suggests automation.'],
    
      recommendations: ['If active users decline while generation volume holds: check User Intelligence for retention signals, review Support queue for dissatisfaction.',
          'If cost rises faster than generation volume: review Platform Providers for inefficiency, check Cost Analytics for provider breakdown.',
          'If generation volume drops suddenly: check Platform Overview for provider failures, review Automation incidents.'],
      suggested_actions: ['Review Platform Overview',
          'Open User Intelligence',
          'Check Support Queue',
          'Review Automation Incidents'],
    },
    users: {
      title: 'User Management',
      purpose: 'Investigate and manage customer accounts. Starting point for customer-facing operational issues.',
      common_tasks: ['Search for a user by email', 'Review account risk', 'Grant credits', 'Clear mail suppression', 'Open User Intelligence'],
      workflow: ['Search for user', 'Open User Inspector', 'Review Risk Intelligence', 'Review Relationships', 'Check Timeline', 'Execute action if needed'],
      tips: ['Always check suppression status before taking action on mail failures.', 'Use Intel tab for a full timeline before granting credits.'],
      field_explanations: {
        'Risk Score': 'Composite score 0-100 derived from failed jobs, mail failures, escalations, and suppression state.',
        'Credits at Risk': 'Failed jobs where credits were already deducted but not refunded.',
        'Suppressed': 'Email delivery to this user is blocked. Check suppression type before replying to tickets.',
      },
    
      recommendations: ['If a user has high risk score: open User Intelligence before taking action — context is critical.',
          'If suppression flag is set: clear suppression before replying to any tickets for this user.',
          'If user has many failed jobs with credits deducted: apply credit refund via User Actions.'],
      suggested_actions: ['Open User Intelligence',
          'Navigate to User Actions',
          'Check Support Queue for this user'],
    },
    jobs: {
      title: 'Job Monitor',
      purpose: 'Monitor the generation pipeline. Use for identifying stuck jobs, provider failures, and retry storms.',
      common_tasks: ['Filter by failed status', 'Identify stuck jobs', 'Check provider distribution', 'Requeue failed jobs'],
      workflow: ['Filter by status', 'Identify failed or stuck clusters', 'Open Job Inspector', 'Review error code', 'Requeue or refund as appropriate'],
      tips: ['Stuck jobs are queued/processing jobs past the processing window — requeue them.', 'Multiple failures on one provider signal provider degradation.'],
    
      recommendations: ['If many failed jobs share an error code: likely a provider issue — check Platform Providers before requeuing.',
          'If stuck jobs are present: requeue immediately — they block credit refunds and user retries.',
          'If failed jobs have credits deducted: refund before requeuing to avoid double-charging.'],
      suggested_actions: ['Check Platform Providers',
          'Open Platform Failures',
          'Review Credit Monitor'],
    },
    platform_overview: {
      title: 'Platform Operations',
      purpose: 'Live operational telemetry. The operational nerve center for generation pipeline health.',
      common_tasks: ['Monitor live job stream', 'Check provider health', 'Review failure rates', 'Monitor queue depths'],
      workflow: ['Check KPI strip', 'Review live job feed', 'Inspect any red providers', 'Review failure center if failure rate elevated'],
      tips: ['Live feed updates every 8 seconds via SSE.', 'Queue depth above normal with low throughput signals worker saturation.'],
    
      recommendations: ['If success rate drops below 95%: navigate to Platform Failures immediately — do not wait for automation.',
          'If queue depth is growing: check Platform Queues and consider triggering Autonomous Queue Recovery.',
          'If provider failure rate exceeds 5%: navigate to Platform Providers and assess failover.'],
      suggested_actions: ['Open Platform Failures',
          'Check Platform Providers',
          'Trigger Autonomous Recovery if needed'],
    },
    platform_failures: {
      title: 'Failure Center',
      purpose: 'Centralized view of all generation and system failures. Use for root cause investigation.',
      common_tasks: ['Filter by error code', 'Identify failure clusters', 'Open job inspector', 'Trigger bulk requeue'],
      workflow: ['Filter failures by time window', 'Group by error code', 'Identify most common failure type', 'Inspect representative job', 'Execute remediation'],
      tips: ['Provider timeout errors cluster together — check provider health first.', 'Credit-deducted failures should always be refunded.'],
    
      recommendations: ['If failures cluster around one error code: that error code is the root cause — investigate provider documentation.',
          'If credits were deducted on failed jobs: process bulk refund before requeuing.',
          'If failure rate is rising: check Platform Providers before requeuing — requeuing to a degraded provider repeats the failure.'],
      suggested_actions: ['Check Platform Providers first',
          'Bulk refund credits',
          'Trigger Autonomous Recovery'],
    },
    platform_queues: {
      title: 'Queue Monitor',
      purpose: 'Read-only queue depth and processing time visibility.',
      common_tasks: ['Check queue depths', 'Identify stuck queues', 'Monitor wait times'],
      workflow: ['Review all queue depths', 'Flag any queue above normal depth', 'Check corresponding provider health', 'Escalate to autonomous recovery if stuck'],
      tips: ['Queue depth spikes without corresponding job volume suggest stuck workers.'],
    
      recommendations: ['If queue depth is critical: navigate to Autonomous Queue Recovery to simulate and execute drain.',
          'If multiple queues are saturated simultaneously: check Platform Providers — provider degradation causes queue backup.'],
      suggested_actions: ['Open Autonomous Queue Recovery',
          'Check Platform Providers',
          'Review Platform Jobs for stuck jobs'],
    },
    platform_providers: {
      title: 'Provider Telemetry',
      purpose: 'Per-provider operational performance metrics.',
      common_tasks: ['Compare provider success rates', 'Identify degraded providers', 'Monitor latency trends'],
      workflow: ['Review success rate per provider', 'Flag any below threshold', 'Open provider inspector', 'Trigger autonomous failover if appropriate'],
      tips: ['Success rate below 85% on a primary provider warrants failover consideration.'],
    
      recommendations: ['If a provider success rate drops below 85%: trigger failover from Autonomous Provider Failover.',
          'If multiple providers degrade simultaneously: check for upstream API outage before taking action.'],
      suggested_actions: ['Open Autonomous Provider Failover',
          'Check Platform Failures',
          'Review Platform Jobs'],
    },
    support_queue: {
      title: 'Support Queue',
      purpose: 'Manage and respond to customer support tickets.',
      common_tasks: ['Review open tickets', 'Assign tickets', 'Reply to customers', 'Escalate critical issues'],
      workflow: ['Filter by severity', 'Review unassigned tickets first', 'Open ticket detail', 'Check user intelligence panel', 'Reply or escalate'],
      tips: ['Check user suppression status before sending a reply — suppressed emails will bounce.', 'Use canned responses to speed up common replies.'],
    
      recommendations: ['If unassigned ticket count is growing: review Staff Workload in Staff Accounts immediately.',
          'If escalated tickets appear in queue: prioritise these above all others.',
          'If generation-related complaints cluster: check Platform Overview for provider failures as root cause.'],
      suggested_actions: ['Review Staff Workload',
          'Open Escalated Queue',
          'Check Platform Overview'],
    },
    support_escalated: {
      title: 'Escalated Tickets',
      purpose: 'High-priority tickets requiring immediate attention.',
      common_tasks: ['Review all escalated tickets', 'Assign to senior staff', 'Resolve or de-escalate'],
      workflow: ['Review severity', 'Open ticket', 'Review user intelligence', 'Reply or resolve', 'Update status'],
      tips: ['Escalated tickets should be responded to within 1 business day.'],
    
      recommendations: ['If escalated count is rising rapidly: possible systemic issue — check Platform Overview for correlation.',
          'If all escalations relate to the same product area: escalate to OPERATIONS_ADMIN for platform investigation.'],
      suggested_actions: ['Check Platform Overview',
          'Review User Intelligence for affected users',
          'Notify OPERATIONS_ADMIN if systemic'],
    },
    intel: {
      title: 'User Intelligence Console',
      purpose: 'Full unified operational timeline for any user. Use for deep investigations before taking action.',
      common_tasks: ['Load user by UUID', 'Review full event timeline', 'Filter by security events', 'Check mail delivery history'],
      workflow: ['Enter user UUID', 'Review KPI strip for risk indicators', 'Filter timeline by category', 'Look for correlated events', 'Return to user inspector to act'],
      tips: ['Use the timeline filter to isolate security events during investigations.', 'Correlated failed logins + mail failures may indicate account compromise.'],
    
      recommendations: ['If timeline shows correlated security + mail failures: possible account compromise — escalate to Security Center.',
          'If user has open tickets and failed jobs: address jobs first (credit refund) before responding to ticket.'],
      suggested_actions: ['Navigate to User Actions',
          'Open Support Queue for this user',
          'Check Security Center if compromise suspected'],
    },
    auto_incidents: {
      title: 'Incident Detection',
      purpose: 'Active operational incidents detected by the automation rule engine.',
      common_tasks: ['Review active incidents', 'Open incident inspector', 'Acknowledge or escalate', 'Trigger recovery'],
      workflow: ['Review severity', 'Open incident inspector', 'Review RCA panel', 'Check affected users and jobs', 'Execute recovery action or acknowledge'],
      tips: ['Always review provider impact before triggering recovery actions.', 'Acknowledge incidents after investigation to track operational history.'],
    
      recommendations: ['If critical incidents are present: execute autonomous recovery immediately — do not wait.',
          'If multiple incidents across different categories: likely a platform-wide issue — check Platform Overview.'],
      suggested_actions: ['Open Autonomous Operations',
          'Check Platform Overview',
          'Review Platform Providers'],
    },
    auto_overview: {
      title: 'Automation Overview',
      purpose: 'Deterministic rule evaluation state and operational severity.',
      common_tasks: ['Check triggered rules', 'Review suggestions', 'Navigate to incidents'],
      workflow: ['Review overall severity', 'Check triggered rules count', 'Open incidents for active issues', 'Review suggestions for proactive fixes'],
      tips: ['Triggered rules do not automatically execute — they require manual or autonomous action.'],
    
      recommendations: ['If triggered rule count is rising: navigate to Automation Rules to see which conditions are firing.',
          'If suggestions are accumulating without action: review Automation Suggestions — aged suggestions indicate missed operational signals.'],
      suggested_actions: ['Open Automation Rules',
          'Review Automation Suggestions',
          'Check Automation Incidents'],
    },
    autonomous_overview: {
      title: 'Autonomous Operations',
      purpose: 'Bounded autonomous recovery execution status and control.',
      common_tasks: ['Review execution history', 'Simulate recovery rules', 'Execute bounded recovery', 'Monitor recovery outcomes'],
      workflow: ['Review active triggered rules', 'Simulate before executing', 'Execute with operator note', 'Monitor outcome in execution history'],
      tips: ['Always simulate before executing live autonomous actions.', 'All executions are audited with actor attribution.'],
    
      recommendations: ['If multiple subsystems show triggered rules: execute the highest-severity subsystem first.',
          'Always simulate before executing live — dry-run mode is the default for this reason.'],
      suggested_actions: ['Review triggered rules',
          'Open Execution History',
          'Simulate before executing'],
    },
    comms_overview: {
      title: 'Communications Overview',
      purpose: 'Email delivery health and operational communications metrics.',
      common_tasks: ['Monitor delivery rates', 'Check bounce rates', 'Review suppression list', 'Monitor mail queue'],
      workflow: ['Review delivery KPIs', 'Check bounce/complaint rates', 'Investigate suppression list if rates elevated', 'Review mail queue for stuck messages'],
      tips: ['Complaint rate above 0.1% may trigger SES sending limits.'],
    
      recommendations: ['If bounce rate exceeds 2%: immediately review Suppression List and Delivery Monitor for patterns.',
          'If complaint rate approaches 0.1%: pause non-critical email sends and review affected templates.',
          'If failed delivery count rises: check Mail Queue for stuck messages and Suppression List for new blocks.'],
      suggested_actions: ['Open Suppression List',
          'Check Delivery Monitor',
          'Review Mail Queue'],
    },
    audit: {
      title: 'Audit Log',
      purpose: 'Complete staff action history. Use for compliance, investigation, and accountability.',
      common_tasks: ['Search by action type', 'Review high-risk actions', 'Trace staff activity'],
      workflow: ['Filter by action or staff member', 'Review risk level', 'Open audit inspector for detail'],
      tips: ['HIGH risk actions include bulk operations and account modifications.'],
    
      recommendations: ['If HIGH or CRITICAL actions appear from unexpected staff members: cross-reference with Staff Sessions.',
          'If bulk actions appear in the log: verify they were authorised — cross-reference with incident timeline.'],
      suggested_actions: ['Cross-reference with Staff Sessions',
          'Review Security Center',
          'Check Staff Accounts'],
    },
    security: {
      title: 'Security Center',
      purpose: 'Failed logins, MFA events, and suspicious activity monitoring.',
      common_tasks: ['Review failed login clusters', 'Check MFA bypass attempts', 'Identify suspicious IP patterns'],
      workflow: ['Review recent failed logins', 'Cluster by IP or email', 'Cross-reference with user intelligence', 'Lock account if compromise suspected'],
      tips: ['Multiple failed logins from different IPs for the same email suggests credential stuffing.'],
    
      recommendations: [
        'If failed login cluster from multiple IPs: possible credential stuffing — lock targeted accounts via User Actions.',
        'If MFA bypass attempts appear: alert OWNER role immediately and review Staff Sessions.',
        'If suspicious activity detected: cross-reference with Audit Log for correlating staff actions.',
      ],
      suggested_actions: ['Review User Intelligence for targeted accounts', 'Open Staff Sessions', 'Check Audit Log'],
    },

    // ── MARKETING & COMMUNITY ────────────────────────────────────────────────
    mc_ci: {
      title: 'Channel Intelligence',
      purpose: 'Unified channel performance intelligence — correlate social channels with traffic, conversions, revenue, and retention.',
      workflow: ['Review Channel Overview for top-line KPIs', 'Open Channel Performance for per-channel breakdown', 'Check Attribution for first/last touch revenue', 'Review Comparison to rank channels', 'Check Health scores for declining channels'],
      common_tasks: ['Identify highest revenue channel', 'Compare conversion rates across channels', 'Check LTV by channel', 'Review audience quality per channel', 'Identify declining channels'],
      tips: ['All metrics are sourced from existing attribution, payment, and campaign data — no separate tracking required.', 'Unavailable shows where data source is not yet connected.', 'Health scores exclude unavailable components from the average.'],
      recommendations: [
        'If a channel shows Critical health: review its engagement and traffic metrics — may need content strategy adjustment.',
        'If attribution shows high Unknown: UTM tagging is incomplete — review Marketing Activity for missing UTM fields.',
        'If Reddit or Discord show high LTV despite low volume: increase content frequency on those channels.',
      ],
      suggested_actions: ['Review Traffic Sources', 'Check Campaign Impact', 'Open Social Channels to update metrics'],
      field_explanations: {
        'Revenue Per Subscriber': 'Total revenue attributed to channel divided by follower/subscriber count.',
        'Audience Quality Score': 'Composite of conversion rate, LTV, retention, and refund rate for users from this channel.',
        'First Touch': 'Channel that brought the user to VISINVOKE for the first time.',
        'Assisted Conversion': 'Channel that appeared in the user journey but was not first or last touch.',
      },
    },
    // ── EXECUTIVE ─────────────────────────────────────────────────────────────
    exec_intel: {
      title: 'Intelligence Foundation',
      purpose: 'Status of all Phase B data collection systems. Shows which tables exist and are collecting data.',
      workflow: ['Review system status table', 'Capture daily snapshot', 'Backfill cohorts from existing users', 'Review data quality score', 'Log marketing spend and operational costs'],
      common_tasks: ['Capture daily snapshot', 'Check data quality score', 'Backfill cohort records', 'Review which systems need setup'],
      tips: ['Tables are created automatically on first access — refresh after initial visit.', 'Capture snapshots daily to enable forecasting confidence to increase over time.', 'Backfill cohorts once from existing users — re-run is safe (uses UPSERT).'],
      recommendations: ['If data quality score is below 80%: review the failing checks and address setup items.', 'Capture a snapshot now and schedule daily captures to build forecasting history.'],
      suggested_actions: ['Capture daily snapshot', 'Backfill cohorts', 'Log marketing spend', 'Log operational costs'],
    },
    exec_overview: {
      title: 'Executive Overview',
      purpose: 'Complete business health — revenue, subscribers, platform, owner target.',
      workflow: ['Review revenue KPIs', 'Check subscriber signals', 'Review owner target', 'Check health score', 'Navigate to problem areas'],
      common_tasks: ['Check revenue 30D', 'Review subscriber net growth', 'Check owner target %', 'Identify top channel and generator'],
      tips: ['Aggregates from multiple sources — some KPIs unavailable until tables are populated.'],
      recommendations: ['If owner target below 85%: review Cost vs Revenue and Acquisition Intelligence.', 'If subscriber net growth is negative: review Retention Intelligence.'],
      suggested_actions: ['Open Business Health', 'Open Growth Signals', 'Check Revenue Intelligence'],
    },
    exec_health: {
      title: 'Business Health',
      purpose: 'Weighted deterministic health score across all major business systems.',
      workflow: ['Review overall score', 'Identify lowest-scoring components', 'Navigate to weak intelligence page', 'Monitor score improvement'],
      common_tasks: ['Check which component is dragging score', 'Navigate to weak area'],
      tips: ['Components with no data excluded from average. Score improves as data sources populate.'],
      recommendations: ['If Retention score low: open Retention Intelligence.', 'If Platform score low: check Platform Overview immediately.'],
      suggested_actions: ['Open Retention Intelligence', 'Check Conversion Intelligence', 'Review Platform Overview'],
    },
    exec_signals: {
      title: 'Growth Signals',
      purpose: 'Fast operational signal view — subscriber, generator, channel, and campaign momentum.',
      workflow: ['Scan all signal rows', 'Identify red signals', 'Navigate to relevant detail page', 'Take action on red signals'],
      common_tasks: ['Check subscriber growth direction', 'Identify unused generators', 'Review channel attribution'],
      tips: ['Red = immediate action. Amber = monitor. Green = positive momentum.'],
      recommendations: ['If subscriber signal is red: open Retention Intelligence.', 'If channel attribution Unknown: configure UTM tagging.'],
      suggested_actions: ['Open Generator Intelligence', 'Review Marketing Activity for UTM', 'Check Retention Intelligence'],
    },
    exec_forecast: {
      title: 'Forecasting',
      purpose: 'Linear projections from current snapshot. LOW confidence until historical snapshots accumulate.',
      workflow: ['Review confidence level', 'Check assumptions', 'Review 30D and 90D projections', 'Compare projected profit to A$6,000 target'],
      common_tasks: ['Check projected subscribers', 'Review projected revenue', 'Compare projected profit to target'],
      tips: ['LOW confidence = single data point only. Projections are directional. Confidence requires historical snapshots.'],
      recommendations: ['If projected 90D profit below A$6,000: review Acquisition and Retention Intelligence.'],
      suggested_actions: ['Open Owner Target Tracker', 'Check Revenue Intelligence'],
    },
    gi_performance: {
      title: 'Generator Performance',
      purpose: 'Usage, failure rates, and health per generator.',
      workflow: ['Review top generators', 'Filter by fail rate', 'Identify high-fail generators', 'Check unused generators'],
      common_tasks: ['Identify generators with high fail rate', 'Find unused generators', 'Check generator health'],
      tips: ['Fail rate above 20% warrants investigation — cross-reference with Platform Failures.'],
      recommendations: ['If fail rate high: check Platform Failures filtered to that generator.', 'If generators unused: review engine discoverability.'],
      suggested_actions: ['Open Platform Failures', 'Check Engine Performance'],
      field_explanations: {'Fail Rate': 'Percentage of attempts that failed.', 'Status': 'Healthy <10%, Watch 10-29%, High Fail ≥30%.'},
    },
    gi_engines: {
      title: 'Engine Performance',
      purpose: 'Usage by engine category and domain.',
      workflow: ['Review category chart', 'Check domain split', 'Identify dominant engines'],
      common_tasks: ['Check which category dominates', 'Review D&D vs Professional vs Battlemaps'],
      tips: ['D&D typically dominates — monitor Professional and Battlemaps growth.'],
      suggested_actions: ['Open Generator Performance', 'Review Usage Trends'],
    },
    gi_trends: {
      title: 'Usage Trends',
      purpose: 'Period comparison: 7D vs 30D vs 90D generation volume.',
      workflow: ['Review total uses across periods', 'Check 7D pace vs 30D actual', 'Identify accelerating categories'],
      common_tasks: ['Compare 7D vs 30D volume', 'Identify trending categories'],
      tips: ['7D pace × 4.3 approximates monthly run rate. Full time-series requires dedicated endpoint.'],
      suggested_actions: ['Open Engine Performance', 'Check Platform Overview for correlation'],
    },
    gi_revenue: {
      title: 'Generator Revenue',
      purpose: 'Usage as revenue proxy. Full revenue attribution requires payment_ledger join.',
      workflow: ['Review usage by generator', 'Note revenue attribution unavailable until payment_ledger.payment_type_detail populated'],
      common_tasks: ['Check which generators drive most volume', 'Plan payment_ledger integration'],
      tips: ['High usage is a revenue proxy but not a direct measurement.'],
      recommendations: ['To enable revenue attribution: populate payment_ledger.payment_type_detail with generator identifiers.'],
      suggested_actions: ['Open Generator Performance', 'Check Revenue Intelligence'],
    },
    mc_release_impact: {
      title: 'Release Impact',
      purpose: 'Registration and revenue outcomes after each product release.',
      workflow: ['Select impact window', 'Review releases with measured outcomes', 'Add actual impact notes in Release Tracker'],
      common_tasks: ['Check registrations after a release', 'Review revenue in the impact window', 'Add actual impact notes'],
      tips: ['Impact data only available for activities with published_date and UTM campaign set.'],
      recommendations: ['If attribution shows Unknown: configure UTM campaign tags on all marketing activities linked to releases.'],
      suggested_actions: ['Open Release Tracker', 'Configure UTM tags in Marketing Activity'],
    },
    si_performance: { title: 'Slug Performance', purpose: 'Requires slug_usage_log table.', workflow: ['Setup required'], common_tasks: ['Create slug_usage_log table'], tips: ['Slug tracking table needed.'], suggested_actions: ['Create slug_usage_log table'] },
    si_families:    { title: 'Slug Families',    purpose: 'Requires slug_families taxonomy table.', workflow: ['Setup required'], common_tasks: ['Create slug taxonomy'], tips: ['Taxonomy table required.'], suggested_actions: ['Create slug_families table'] },
    si_demand:      { title: 'Community Demand', purpose: 'Requires community_votes table.', workflow: ['Setup required'], common_tasks: ['Create voting system'], tips: ['Voting table required.'], suggested_actions: ['Build community_votes table'] },
    si_planning:    { title: 'Future Planning',  purpose: 'Requires slug + community data.', workflow: ['Setup required'], common_tasks: ['Enable Slug Performance and Community Demand first'], tips: ['Derived from slug and community data.'], suggested_actions: ['Enable Slug Performance first'] },
    mc_voting:      { title: 'Community Voting', purpose: 'Requires community_votes table and vote capture endpoint.', workflow: ['Setup required'], common_tasks: ['Create voting system'], tips: ['Vote table and endpoint needed.'], suggested_actions: ['Create community_votes table'] },
    ci_cohorts:     { title: 'Cohort Intelligence', purpose: 'Requires cohort retention table or subscription snapshots.', workflow: ['Setup required'], common_tasks: ['Create subscription snapshot table'], tips: ['Monthly cohort snapshots needed.'], suggested_actions: ['Create subscription snapshot table'] },
    ci_ltv:         { title: 'Product LTV', purpose: 'Requires payment_ledger.payment_type_detail populated per product.', workflow: ['Setup required'], common_tasks: ['Populate payment_type_detail'], tips: ['Product attribution in payment_ledger needed.'], suggested_actions: ['Populate payment_ledger.payment_type_detail'] },
    ci_migration:   { title: 'Cross Product Migration', purpose: 'Requires user_product_access table with journey timestamps.', workflow: ['Setup required'], common_tasks: ['Create user_product_access table'], tips: ['Journey timestamps required.'], suggested_actions: ['Create user_product_access table'] },

    mc_channels: {
      title: 'Social Channels',
      purpose: 'Track all official VISINVOKE social channels, follower counts, engagement, and revenue attribution.',
      workflow: ['Review channel follower counts', 'Check registrations and revenue attributed', 'Identify best-performing channels', 'Update manual metrics for channels without API'],
      common_tasks: ['Add a new channel', 'Update manual follower counts', 'Review channel conversion rates', 'Compare platform performance'],
      tips: ['Channels without API connection require manual metric updates — set a weekly update schedule.', 'Revenue 30D on a channel only populates if UTM tracking is configured for that channel.'],
      recommendations: ['If a channel has zero registrations but high followers: review UTM setup for that channel.'],
      suggested_actions: ['Review Traffic Sources for channel attribution', 'Open Content Calendar', 'Check Marketing Activity'],
    },
    mc_calendar: {
      title: 'Content Calendar',
      purpose: 'View all planned and published content in a single calendar. Cross-section view of all marketing activities.',
      workflow: ['Review upcoming scheduled content', 'Check for gaps in publishing schedule', 'Verify published content has UTM tags', 'Link activities to campaigns'],
      common_tasks: ['View scheduled posts', 'Check content gaps', 'Navigate to activity to add UTM tags', 'Review published content this week'],
      tips: ['Calendar is read-only — create and edit activities in Marketing Activity.', 'Gaps in the calendar of more than 7 days may reduce organic reach.'],
      recommendations: ['If no content is scheduled for the next 7 days: log activities in Marketing Activity immediately.'],
      suggested_actions: ['Open Marketing Activity to add content', 'Review Campaigns for active campaign schedule'],
    },
    mc_activity: {
      title: 'Marketing Activity',
      purpose: 'Log and manage every marketing action. The operational record of all VISINVOKE marketing execution.',
      workflow: ['Log activity immediately when published', 'Add UTM tags at time of logging', 'Link to a campaign if applicable', 'Mark status as Published when live', 'Review impact after window in Campaign Impact'],
      common_tasks: ['Log a new TikTok post', 'Log a Reddit post', 'Log a paid ad', 'Update status to Published', 'Add spend to a logged activity'],
      tips: ['Always add UTM tags when logging — retroactive attribution is impossible without them.', 'Log spend at time of creation so Campaign Impact can calculate CAC and ROAS.'],
      recommendations: ['If activities are untagged with UTM: attribution will show Unknown in Campaign Impact.'],
      suggested_actions: ['Review Campaign Impact after publishing', 'Check Traffic Sources for attribution data'],
    },
    mc_campaigns: {
      title: 'Campaigns',
      purpose: 'Group marketing activities into campaigns. Track budget, spend, and goals per campaign.',
      workflow: ['Create campaign before launching activities', 'Set UTM campaign slug before any activities are created', 'Log all activities under the campaign', 'Track spend vs budget', 'Review impact in Campaign Impact after completion'],
      common_tasks: ['Create a new campaign', 'Track budget vs spend', 'Review active campaign status', 'Complete or pause a campaign'],
      tips: ['Set the UTM Campaign slug before creating any activities — it must match the UTM tags in all activities.', 'Budget tracking shows spend as activities are logged with spend values.'],
      recommendations: ['If spend exceeds budget on an active campaign: pause and review in Campaign Impact before continuing.'],
      suggested_actions: ['Open Campaign Impact', 'Review Marketing Activity for campaign activities'],
    },
    mc_impact: {
      title: 'Campaign Impact',
      purpose: 'Measure what happened after each published activity. Deterministic attribution — no fake data.',
      workflow: ['Select impact window', 'Review registrations and revenue per activity', 'Check attribution type', 'Identify high-performing activities', 'Optimise future activities toward best performers'],
      common_tasks: ['Check 7-day impact of recent posts', 'Compare ROAS across channels', 'Identify best attribution source', 'Filter by Direct Attribution only'],
      tips: ['Direct Attribution requires matching UTM campaign data — activities without UTM tags show Unknown.', 'Possible Influence means the conversion happened in the window but no direct UTM match exists.', 'Visitor data is unavailable without external analytics integration — only Stripe outcomes are shown.'],
      recommendations: ['If most attribution shows Unknown: set up UTM tagging in all future activities.', 'If ROAS is below 1.0 on a paid channel: pause that channel spend.'],
      suggested_actions: ['Review Traffic Sources for overall attribution', 'Check Acquisition Intelligence for CAC trend'],
      field_explanations: {
        'Direct Attribution': 'UTM data from this activity matches the conversion event.',
        'Possible Influence': 'Conversion occurred in the impact window but no direct UTM match found.',
        'Unknown': 'No UTM data — cannot attribute reliably.',
      },
    },
    mc_traffic: {
      title: 'Traffic Sources',
      purpose: 'Where are registered users and paying customers coming from? UTM-based attribution from existing user data.',
      workflow: ['Select time window', 'Review registrations by source', 'Check paying user conversion by source', 'Identify highest-revenue sources', 'Cross-reference with Acquisition Intelligence'],
      common_tasks: ['Check which source drives most registrations', 'Compare paying user conversion by source', 'Review revenue share by source'],
      tips: ['Visitor data requires external analytics integration and shows Unavailable until configured.', 'Source attribution is based on UTM data captured at registration — requires UTM tagging in all marketing activities.'],
      recommendations: ['If direct source dominates: UTM tracking is not set up — implement immediately.', 'If organic outperforms paid sources: review Acquisition Intelligence to reduce paid CAC.'],
      suggested_actions: ['Check Acquisition Intelligence', 'Review Marketing Activity for UTM completeness'],
    },
    mc_community: {
      title: 'Community Growth',
      purpose: 'Track VISINVOKE community size across all platforms. Identify engagement trends.',
      workflow: ['Review total community size', 'Check growth trends per channel', 'Identify highest-engagement channels', 'Update manual counts weekly for channels without API'],
      common_tasks: ['Check total follower count', 'Compare engagement by platform', 'Review community growth trend', 'Update manual channel metrics'],
      tips: ['Manual channels require weekly metric updates — set a calendar reminder.', 'Engagement rate (engagement / followers) is more meaningful than raw follower count.'],
      recommendations: ['If community growth is stagnant: review Content Calendar for publishing frequency.'],
      suggested_actions: ['Open Social Channels to update metrics', 'Review Content Calendar for publishing schedule'],
    },
    mc_releases: {
      title: 'Release Tracker',
      purpose: 'Track all VISINVOKE product releases, updates, and community events. Operational release record.',
      workflow: ['Log release before scheduled date', 'Update status to In Progress when work begins', 'Mark Released with actual date when live', 'Record actual impact after 7-30 days', 'Link to campaign if marketing is planned'],
      common_tasks: ['Log a new release', 'Update release status', 'Record actual release date', 'Add actual impact after release'],
      tips: ['Log releases before they go live so you can cross-reference with Campaign Impact.', 'Delayed releases should be updated immediately — stale Planned status creates confusion.'],
      recommendations: ['If releases are frequently delayed: review planning timeline and communicate earlier.'],
      suggested_actions: ['Link releases to Campaigns for coordinated tracking', 'Check Campaign Impact after each release'],
    },
    mc_email: {
      title: 'Email Campaigns',
      purpose: 'Track email marketing campaigns, engagement metrics, and revenue attribution.',
      workflow: ['Log campaign before sending', 'Update recipient count and status after send', 'Enter open/click metrics after 24-48 hours if available', 'Record registrations and revenue attributed'],
      common_tasks: ['Log a new email campaign', 'Update open and click rates', 'Record revenue attributed to campaign', 'Review unsubscribe rate'],
      tips: ['Open and click metrics require email provider API integration — log manually if unavailable.', 'Unsubscribe rate above 2% per campaign warrants a review of audience targeting.'],
      recommendations: ['If unsubscribe rate is high: review audience segmentation and email frequency.', 'If revenue attributed is zero: review UTM setup for email links.'],
      suggested_actions: ['Check Traffic Sources for email source attribution', 'Review Suppression List in Communications'],
    },

    // ── COMMERCIAL INTELLIGENCE ─────────────────────────────────────────────
    ci_revenue: {
      title: 'Revenue Intelligence',
      purpose: 'Executive revenue performance across all VISINVOKE products.',
      workflow: ['Review top KPI strip for period performance', 'Check revenue by product chart', 'Identify declining products', 'Drill into product for timeline'],
      common_tasks: ['Compare 30D vs 90D revenue', 'Review ARPU and ARPPU', 'Check refund rate', 'Review product revenue share'],
      tips: ['ARPPU excludes free users — use it to track monetisation depth.', 'Annual Run Rate is a forward projection — validate against trend.'],
      recommendations: ['If refunds exceed 5% of revenue: review Product Health for the affected product.', 'If ARPU drops while subscriber count holds: users are downgrading — check Subscriber Intelligence.'],
      suggested_actions: ['Open Subscriber Intelligence', 'Check Retention Intelligence', 'Review Product Health'],
    },
    ci_subscribers: {
      title: 'Subscriber Intelligence',
      purpose: 'Subscription health by product and tier. Track growth, churn, and trial conversion.',
      workflow: ['Review active subscriber count', 'Check new vs cancelled ratio', 'Review trial-to-paid trend', 'Identify products with declining subscribers'],
      common_tasks: ['Check net subscriber growth', 'Review trial expiry rate', 'Monitor reactivations', 'Compare upgrades vs downgrades'],
      tips: ['Reactivations indicate successful win-back — monitor the trigger event.', 'Downgrade count rising faster than upgrade count is an early revenue risk signal.'],
      recommendations: ['If cancelled subscribers exceed new subscribers: flag retention risk — review Retention Intelligence.', 'If trial expiry rate is high: review Conversion Intelligence for funnel leakage.'],
      suggested_actions: ['Open Retention Intelligence', 'Check Conversion Intelligence', 'Review Revenue Intelligence'],
    },
    ci_conversion: {
      title: 'Conversion Intelligence',
      purpose: 'Track user journey conversion from registration through to payment and renewal.',
      workflow: ['Review funnel KPI strip', 'Identify the lowest conversion stage', 'Check conversion by product', 'Compare this period to prior'],
      common_tasks: ['Check trial-to-paid rate', 'Review registration-to-paid rate', 'Identify products with low conversion', 'Check renewal rate'],
      tips: ['Trial-to-paid below 15% typically indicates onboarding friction or expectation mismatch.', 'Visitor metrics require external analytics integration — unavailable shows correctly if source not configured.'],
      recommendations: ['If trial-to-paid drops more than 20%: flag conversion risk — review product onboarding and trial length.', 'If renewal rate drops: cross-reference with Retention Intelligence.'],
      suggested_actions: ['Review Retention Intelligence', 'Check Acquisition Intelligence', 'Open Product Health'],
    },
    ci_acquisition: {
      title: 'Acquisition Intelligence',
      purpose: 'Track whether advertising spend is producing paying customers and at what cost.',
      workflow: ['Review total ad spend vs paying users from ads', 'Check CAC by source', 'Identify best and worst performing sources', 'Review ROAS by source'],
      common_tasks: ['Compare CAC across channels', 'Check cost per paying user', 'Review ROAS for each ad source', 'Identify organic vs paid split'],
      tips: ['CAC above LTV is unsustainable — cross-reference with Retention Intelligence LTV.', 'Organic traffic has zero CAC — monitor its share as paid scales.'],
      recommendations: ['If CAC increases more than 25%: flag acquisition efficiency risk — review source breakdown.', 'If ROAS drops below 1.0: ad spend is losing money — pause that source.'],
      suggested_actions: ['Check Retention Intelligence for LTV', 'Review Revenue Intelligence', 'Open Conversion Intelligence'],
      field_explanations: {
        'CAC': 'Customer Acquisition Cost — ad spend divided by new paying users from paid sources.',
        'ROAS': 'Return On Ad Spend — revenue from source divided by ad spend for that source.',
        'Cost Per Trial': 'Ad spend divided by trials from paid sources.',
      },
    },
    ci_retention: {
      title: 'Retention Intelligence',
      purpose: 'Show whether users keep paying. Track churn, renewal, LTV, and reactivation.',
      workflow: ['Review churn rate trend', 'Check renewal rate by product', 'Calculate LTV vs CAC ratio', 'Identify products with highest churn'],
      common_tasks: ['Check churn rate by product', 'Review average subscription length', 'Monitor LTV:CAC ratio', 'Track reactivation rate'],
      tips: ['LTV:CAC ratio below 3:1 is a business model risk — acquisition is too expensive relative to lifetime value.', 'Churn spiking for a specific product warrants a Product Health review.'],
      recommendations: ['If churn rate increases more than 15%: flag retention risk — review cancellation reasons and support tickets.', 'If LTV:CAC drops below 3.0: review Acquisition Intelligence for CAC reduction opportunities.'],
      suggested_actions: ['Check Acquisition Intelligence for CAC', 'Review Support Queue for cancellation-related tickets', 'Open Product Health'],
      field_explanations: {
        'LTV': 'Lifetime Value — ARPPU multiplied by average subscription length in months.',
        'LTV:CAC': 'Ratio of lifetime value to customer acquisition cost. Above 3.0 is healthy.',
        'Churn Rate': 'Cancelled subscribers in period divided by active subscribers at period start.',
        'Saved Revenue': 'Monthly value retained from reactivation or prevented cancellation events.',
      },
    },
    ci_ecosystem: {
      title: 'Ecosystem Adoption',
      purpose: 'Show whether VISINVOKE is becoming a multi-product ecosystem.',
      workflow: ['Check cross-product adoption rate', 'Review ownership depth distribution', 'Identify most common product combinations', 'Track multi-product revenue share'],
      common_tasks: ['Check average products per paying user', 'Review 2+ product adoption rate', 'Identify best product pairings', 'Monitor multi-product revenue share'],
      tips: ['Multi-product users typically have 2-3x higher LTV than single-product users.', 'Product combination data reveals natural bundling opportunities.'],
      recommendations: ['If cross-product adoption is growing: flag ecosystem growth opportunity — this is a positive signal.', 'If single-product users dominate: review product discovery and cross-sell touchpoints.'],
      suggested_actions: ['Review Revenue Intelligence for product breakdown', 'Check Product Health for each product', 'Open Subscriber Intelligence'],
    },
    ci_health: {
      title: 'Product Health',
      purpose: 'Deterministic health scores for each VISINVOKE product. No AI scoring — rule-based only.',
      workflow: ['Review health table for red or declining products', 'Open worst-scoring product', 'Identify primary risk component', 'Follow recommended action for that component'],
      common_tasks: ['Identify critical or declining products', 'Check which component is dragging the score', 'Review recommended actions', 'Cross-reference with specific intelligence pages'],
      tips: ['Health scores exclude unavailable data from the average — a low score from 3 components is more reliable than a high score from 7.', 'Primary Risk identifies the single worst-performing component driving the recommended action.'],
      recommendations: ['If any product is Critical: immediate investigation required — check the primary risk component.', 'If multiple products are Declining simultaneously: likely a platform-wide issue — check Platform Overview.'],
      suggested_actions: ['Navigate to the relevant intelligence page for declining products', 'Check Platform Overview if multiple products decline together', 'Review Support Queue for product-specific complaints'],
      field_explanations: {
        'Overall Score': 'Average of all available component scores (0-100). Unavailable components are excluded.',
        'Primary Risk': 'The lowest-scoring component driving the recommended action.',
        'Excellent': '85-100', 'Strong': '70-84', 'Stable': '50-69', 'Declining': '30-49', 'Critical': '0-29',
      },
    },
    ci_target: {
      title: 'Owner Target Tracker',
      purpose: 'Track progress toward A$6,000 monthly VISINVOKE operating profit target.',
      workflow: ['Review current operating profit vs target', 'Check progress percentage', 'Review cost breakdown', 'Check projected target date'],
      common_tasks: ['Check monthly operating profit', 'Review cost categories', 'Monitor remaining gap', 'Check profit trend'],
      tips: ['Operating profit excludes MSP and Technogeek — VISINVOKE only.', 'Progress below 85% triggers amber status — review cost reduction opportunities.'],
      recommendations: ['If progress is below 85%: review cost breakdown for reduction opportunities, check Revenue Intelligence for growth levers.', 'If infrastructure costs are rising faster than revenue: review Platform Providers for cost optimisation.'],
      suggested_actions: ['Open Revenue Intelligence', 'Check Acquisition Intelligence for CAC efficiency', 'Review Cost vs Revenue'],
      field_explanations: {
        'Monthly Operating Profit': 'Revenue minus infrastructure, ad spend, payment fees, and platform costs. VISINVOKE only.',
        'Annual Run Rate': 'Current monthly operating profit multiplied by 12.',
        'Progress': 'Current operating profit divided by A$6,000 target.',
      },
    },

    // ── OVERVIEW ────────────────────────────────────────────────────────────
    activity: {
      title: 'Live Activity Feed',
      purpose: 'Real-time generation job stream across all providers. Use to monitor pipeline health in the moment.',
      common_tasks: ['Watch for failure bursts', 'Identify stuck jobs in the feed', 'Monitor provider distribution', 'Pause feed to inspect a specific job'],
      workflow: ['Open feed', 'Observe job flow by provider', 'Identify failure clusters', 'Pause and inspect if anomaly detected', 'Navigate to Platform Jobs for detail'],
      tips: ['Pausing the feed lets you inspect individual jobs without losing context.', 'Provider imbalance in the feed may indicate a failover is active.'],
    
      recommendations: ['If failure bursts appear in the live feed: navigate to Platform Failures to identify error code clusters.',
          'If provider imbalance is visible: a failover may be active — check Platform Providers.'],
      suggested_actions: ['Navigate to Platform Failures',
          'Check Platform Providers',
          'Review Queue Monitor'],
    },
    health: {
      title: 'System Health',
      purpose: 'Aggregated job health metrics. Use for shift handover, incident post-mortems, and health trend tracking.',
      common_tasks: ['Review error rate trend', 'Check reconciliation health', 'Compare latency to baseline', 'Identify failure code clusters'],
      workflow: ['Select time window', 'Review error rate KPI', 'Check error code table', 'Drill into specific error codes', 'Navigate to Failure Center if rate elevated'],
      tips: ['Reconciliation errors indicate jobs that completed on the provider but failed to update internally.', 'Avg latency above 8 seconds warrants provider investigation.'],
      field_explanations: {
        'Error Rate': 'Percentage of jobs that failed in the selected window.',
        'Reconciliation': 'Jobs that succeeded at the provider but failed to update in the VISINVOKE database.',
        'Avg Latency': 'Mean provider response time across all completed jobs in the window.',
      },
    
      recommendations: ['If error rate is elevated: navigate to Failure Center to identify error code breakdown.',
          'If reconciliation failures are high: check Platform Jobs for stuck processing jobs.',
          'If avg latency is high: review Platform Providers for degraded providers.'],
      suggested_actions: ['Open Failure Center',
          'Check Platform Providers',
          'Review Platform Jobs'],
    },

    // ── REVENUE ─────────────────────────────────────────────────────────────
    plans: {
      title: 'Plans & Revenue',
      purpose: 'Subscription revenue health. MRR, churn, upgrades, and active subscription overview.',
      common_tasks: ['Check MRR trend', 'Review churn count', 'Compare upgrades vs downgrades', 'Review active subscription list'],
      workflow: ['Review MRR and ARPU KPIs', 'Check churn vs upgrade ratio', 'Review active subscriptions table', 'Identify expired or at-risk accounts'],
      tips: ['Churn exceeding upgrades in a period is a leading indicator of revenue decline.', 'ARPU dropping while subscriber count holds indicates plan downgrades.'],
      recommendations: ['If churn > upgrades: review User Intelligence for recently churned users, check Support escalations for dissatisfaction signals.'],
      field_explanations: {
        'MRR': 'Monthly Recurring Revenue — sum of all active subscription charges.',
        'ARPU': 'Average Revenue Per User — MRR divided by active subscriber count.',
        'Churned': 'Subscriptions cancelled in the current period.',
        'Upgrades': 'Subscriptions upgraded to a higher tier in the current period.',
      },
    
      suggested_actions: ['Review User Intelligence for churned users',
          'Check Support Escalations',
          'Review Cost vs Revenue'],
    },
    margin: {
      title: 'Cost vs Revenue',
      purpose: 'Gross margin visibility. Provider cost against subscription and topup revenue.',
      common_tasks: ['Check gross margin %', 'Compare provider cost trend', 'Identify cost spikes', 'Review profitability by day'],
      workflow: ['Review margin KPI', 'Check provider cost vs revenue', 'Identify periods with compressed margin', 'Drill into provider cost if margin drops'],
      tips: ['Margin below 60% warrants immediate provider cost investigation.', 'Topup revenue has lower margin than subscription revenue — monitor mix.'],
      recommendations: ['If margin drops below threshold: review Platform Providers for cost anomalies, check generation volume vs cost ratio.'],
      field_explanations: {
        'Gross Margin': 'Revenue minus provider cost, as a percentage.',
        'Provider Cost': 'Total fal.ai provider charges in the selected period.',
        'Profitable': 'Whether revenue exceeded provider cost in the selected period.',
      },
    
      suggested_actions: ['Review Cost Analytics',
          'Check Platform Providers',
          'Review Credit Monitor'],
    },
    credits: {
      title: 'Credit Monitor',
      purpose: 'Credit consumption and topup revenue tracking. Use to identify credit anomalies and heavy users.',
      common_tasks: ['Check credits consumed today', 'Identify top credit consumers', 'Review topup revenue', 'Monitor credit grant activity'],
      workflow: ['Review consumption KPIs', 'Check top user list', 'Identify abnormal consumption patterns', 'Drill into users with suspicious credit usage'],
      tips: ['Unusually high credit consumption from a single user may indicate automated abuse.', 'Credit grants should always be logged with a reason in the audit trail.'],
      recommendations: ['If credits consumed spike unexpectedly: check Platform Jobs for generation volume, review top consumers for abuse signals.'],
    
      suggested_actions: ['Open Platform Credits', 'Review Usage Ledger', 'Check User Intelligence for top consumers'],
    },

    // ── USAGE ───────────────────────────────────────────────────────────────
    generations: {
      title: 'Generations Analytics',
      purpose: 'Generation volume, image counts, and domain breakdown. Use for usage trend analysis.',
      common_tasks: ['Review generation volume trend', 'Check image count by domain', 'Monitor quality tier distribution', 'Compare multi-image usage'],
      workflow: ['Select time window', 'Review total generation KPIs', 'Check domain breakdown', 'Look for unusual volume drops or spikes', 'Drill into Platform Jobs if anomaly found'],
      tips: ['Volume drop with stable user count suggests engagement or generation quality issues.', 'D&D domain typically represents 60%+ of generation volume.'],
      recommendations: ['If generation volume drops significantly: check Platform Providers for failures, review automation health, check Support queue for complaints.'],
    
      suggested_actions: ['Check Platform Providers',
          'Review Generator Stats',
          'Open Support Queue'],
    },
    generators: {
      title: 'Generator Stats',
      purpose: 'Per-generator usage and failure rates. Identify underperforming or over-used generators.',
      common_tasks: ['Sort by fail rate', 'Identify generators with elevated failure', 'Review total usage distribution', 'Cross-reference with Platform Failures'],
      workflow: ['Review fail rate column', 'Flag generators above 20% fail rate', 'Open Job Inspector for that generator', 'Check provider routing for that spec', 'Escalate if spec-level issue'],
      tips: ['High fail rate on a specific generator with normal platform health suggests a spec or prompt issue.', 'Unused generators may have stale configuration.'],
      field_explanations: {
        'Fail Rate': 'Percentage of generation attempts that failed for this generator.',
        'Total Uses': 'All-time generation count for this generator.',
      },
    
      recommendations: ['If a generator shows elevated fail rate: review Platform Failures filtered to that generator, check provider routing.',
          'If multiple generators fail simultaneously: likely a provider issue — check Platform Providers.'],
      suggested_actions: ['Open Platform Failures',
          'Check Platform Providers',
          'Review Platform Jobs'],
    },
    ledger: {
      title: 'Usage Ledger',
      purpose: 'Per-user generation history. Use for billing verification, abuse investigation, and support context.',
      common_tasks: ['Search for a specific user', 'Review generation history', 'Verify credit consumption', 'Identify abnormal usage patterns'],
      workflow: ['Search by user ID or email', 'Review generation count and credit spend', 'Look for usage pattern anomalies', 'Cross-reference with User Intelligence for context'],
      tips: ['High generation count with high fail rate may indicate a problematic generator or spec.', 'Usage ledger is the source of truth for billing disputes.'],
    
      recommendations: ['If a user shows abnormal generation volume: open User Intelligence for full context before taking action.',
          'If credit consumption does not match generation count: cross-reference with Credit Monitor for anomalies.'],
      suggested_actions: ['Open User Intelligence',
          'Review Credit Monitor',
          'Check Platform Jobs'],
    },
    costs: {
      title: 'Cost Analytics',
      purpose: 'Granular provider cost breakdown. Use for cost attribution and margin investigation.',
      common_tasks: ['Review cost by provider', 'Check cost per generation', 'Identify cost spikes', 'Compare provider cost efficiency'],
      workflow: ['Select time window', 'Review cost breakdown by provider', 'Identify outlier providers', 'Compare cost per generation against baseline', 'Flag for finance review if anomalous'],
      tips: ['Cost per generation rising without volume increase signals provider price changes or quality tier shifts.'],
      field_explanations: {
        'Cost per Generation': 'Average provider cost per completed generation in the selected window.',
        'Provider Cost': 'Total charges from a specific AI provider.',
      },
    
      recommendations: ['If cost per generation is rising: check if quality tier distribution has shifted toward Ultra.',
          'If one provider shows disproportionate cost: review Platform Providers and consider load balancing.'],
      suggested_actions: ['Review Platform Providers',
          'Check Cost vs Revenue',
          'Open Platform Credits'],
    },

    // ── USERS & JOBS ─────────────────────────────────────────────────────────
    tickets: {
      title: 'Support Tickets',
      purpose: 'Full support ticket management. Assign, reply, escalate, and resolve customer issues.',
      common_tasks: ['Review open tickets', 'Assign unassigned tickets', 'Reply to customers', 'Escalate critical issues', 'Use canned responses for common queries'],
      workflow: ['Filter by status and severity', 'Open unassigned tickets first', 'Load user intelligence for context', 'Reply or escalate', 'Update status on resolution'],
      tips: ['Check mail suppression before replying — bounced replies are invisible to customers.', 'Escalate tickets unresolved for more than 2 business days.'],
    
      recommendations: ['If ticket volume is rising: check if generation failures are the cause — review Platform Overview.',
          'If many tickets are unassigned: review Staff Workload in Staff Accounts and redistribute.',
          'If escalated tickets are growing: navigate to Support Escalated Queue immediately.'],
      suggested_actions: ['Open Support Escalated Queue',
          'Review Staff Workload',
          'Check Platform Overview'],
    },
    actions: {
      title: 'User Actions',
      purpose: 'Administrative action console. Apply account actions, grants, restrictions, and bans.',
      common_tasks: ['Grant credits for failed jobs', 'Apply restriction for abuse', 'Clear account warnings', 'Restore suspended accounts'],
      workflow: ['Enter user ID', 'Select action type', 'Review action consequences', 'Add reason note', 'Execute action', 'Verify in audit log'],
      tips: ['Every action is permanently audit-logged. Always add a meaningful reason.', 'Review User Intelligence before applying enforcement actions.', 'FULL_BAN is irreversible without owner override — use SOFT_BAN first.'],
      field_explanations: {
        'GRANT_CREDITS': 'Add credits to a user balance. Use for compensation after failed jobs.',
        'FULL_BAN': 'Permanent account ban. Cannot be reversed without owner action.',
        'SOFT_BAN': 'Prevents login while preserving data. Reversible.',
        'RESTRICT': 'Limits generation access without blocking login.',
        'CLEAR_WARNING': 'Removes an active warning flag from the account.',
      },
    
      recommendations: ['Always review User Intelligence before applying enforcement actions — context prevents mistakes.',
          'If applying FULL_BAN: verify with OWNER role before executing — this action is irreversible without owner intervention.'],
      suggested_actions: ['Open User Intelligence first',
          'Review Audit Log after action',
          'Check Support Queue for context'],
    },
    rewards: {
      title: 'Reward Moderation',
      purpose: 'Review and moderate user reward submissions and referral events.',
      common_tasks: ['Review pending submissions', 'Approve or reject reward claims', 'Review referral events', 'Grant reward credits'],
      workflow: ['Filter by pending status', 'Open submission for review', 'Check submission context', 'Approve or reject with note', 'Grant credits if approved'],
      tips: ['Check the referral event timeline for suspicious patterns before approving bulk rewards.'],
    
      recommendations: ['If reward submissions are unusually high: check referral event patterns for coordinated abuse before approving.'],
      suggested_actions: ['Review User Intelligence for submitters',
          'Check Audit Log for credit grants'],
    },

    // ── GOVERNANCE ──────────────────────────────────────────────────────────
    staff: {
      title: 'Staff Accounts',
      purpose: 'Manage staff members, roles, MFA, and account health. Monitor workload distribution.',
      common_tasks: ['Review workload column for imbalance', 'Check MFA compliance', 'Invite new staff', 'Review active sessions', 'Disable departed staff immediately'],
      workflow: ['Review workload column first', 'Flag overloaded staff members', 'Check MFA status — all staff must have MFA enabled', 'Disable or suspend departed staff', 'Review session count for anomalies'],
      tips: ['Staff with 0 active sessions and recent login may have stale sessions elsewhere.', 'Disable staff accounts immediately on departure — do not just revoke sessions.', 'Workload column shows open tickets and threads — use for assignment balancing.'],
      recommendations: ['If workload is heavily imbalanced: review Support Queue and Comms Threads for reassignment opportunities.'],
    
      suggested_actions: ['Review Support Queue assignment',
          'Check Comms Thread assignment',
          'Review Staff Sessions for anomalies'],
    },
    sessions: {
      title: 'Staff Sessions',
      purpose: 'Active and historical staff session management. Use for security review and session revocation.',
      common_tasks: ['Review active sessions', 'Revoke suspicious sessions', 'Identify sessions from unusual IPs', 'Revoke all sessions for a staff member'],
      workflow: ['Filter by status', 'Identify sessions from unexpected IPs or locations', 'Cross-reference with audit log', 'Revoke suspicious sessions with reason', 'Report to security if compromise suspected'],
      tips: ['Multiple concurrent sessions from different IPs for the same staff member is a security indicator.', 'Revoking sessions forces immediate re-authentication.'],
      field_explanations: {
        'MFA Verified': 'Whether this session was established with MFA authentication.',
        'Last Active': 'Most recent API activity for this session.',
        'IP Address': 'Source IP for the session. Flag unexpected geolocations.',
      },
    
      recommendations: ['If sessions from unexpected IPs appear: cross-reference with Audit Log to verify the actions taken.',
          'If a staff member has unusually many active sessions: review in Security Center for compromise indicators.'],
      suggested_actions: ['Cross-reference with Audit Log',
          'Review Security Center',
          'Check Security Events for that staff member'],
    },

    // ── COMMUNICATIONS ───────────────────────────────────────────────────────
    comms_threads: {
      title: 'Communication Threads',
      purpose: 'All inbound and outbound communication threads across all channels. Central comms management.',
      common_tasks: ['Review unassigned threads', 'Assign threads to staff', 'Set priority', 'Open thread detail', 'Reply to customer'],
      workflow: ['Filter by unassigned and open status', 'Assign high-priority threads first', 'Open thread detail for context', 'Reply or escalate', 'Update status on resolution'],
      tips: ['Unassigned threads should be assigned within 1 business day.', 'Filter by mailbox to focus on a specific channel.'],
    
      recommendations: ['If unassigned thread count is growing: review Staff Workload in Staff Accounts for assignment capacity.',
          'If threads from a specific user are escalating: open User Intelligence for full context.'],
      suggested_actions: ['Review Staff Workload',
          'Open User Intelligence',
          'Check Support Queue for related tickets'],
    },
    comms_inboxes: {
      title: 'Mailbox Inboxes',
      purpose: 'Direct Gmail mailbox viewer. Read, reply, and manage inbound emails across all four VISINVOKE mailboxes.',
      common_tasks: ['Check support inbox for new emails', 'Reply to customer emails', 'Mark threads as read', 'Assign threads to staff', 'Use canned responses for common queries'],
      workflow: ['Select mailbox tab', 'Filter by unread', 'Open thread', 'Read full email history', 'Reply with canned response or custom', 'Mark as read and assign'],
      tips: ['Check all four mailboxes daily — support@, billing@, hello@, and admin@.', 'Canned Responses button in the reply area provides pre-written templates.'],
    
      recommendations: ['If unread count is growing across multiple mailboxes: check Staff Workload — may need redistribution.',
          'If delivery failures appear in replies: check Mail Queue and Suppression List for that recipient.'],
      suggested_actions: ['Review Staff Workload',
          'Check Mail Queue',
          'Open Suppression List'],
    },
    comms_queue: {
      title: 'Mail Queue',
      purpose: 'Outbound email event log. Track delivery status of all system-generated emails.',
      common_tasks: ['Check for failed deliveries', 'Review delivery status by template', 'Identify suppression-related failures', 'Filter by template type'],
      workflow: ['Filter by FAILED status', 'Identify failure patterns', 'Check recipient hash against suppression list', 'Clear suppression if appropriate', 'Retry delivery if needed'],
      tips: ['SUPPRESSED status means the recipient is on the suppression list — clear suppression before retrying.', 'BOUNCED failures may trigger further suppression automatically.'],
      field_explanations: {
        'SENT': 'Email was accepted by the provider for delivery.',
        'FAILED': 'Email failed at the provider level.',
        'SUPPRESSED': 'Email was blocked because the recipient is on the suppression list.',
        'BOUNCED': 'Email was rejected by the recipient mail server.',
      },
    
      recommendations: ['If FAILED events cluster by template: that template may have a deliverability issue — review Template content.',
          'If SUPPRESSED events are high: review Suppression List for recent additions and clear if appropriate.'],
      suggested_actions: ['Open Suppression List',
          'Check Delivery Monitor',
          'Review Email Templates'],
    },
    comms_delivery: {
      title: 'Delivery Monitor',
      purpose: 'SES delivery event tracking. Monitor bounces, complaints, and delivery health.',
      common_tasks: ['Check bounce rate', 'Review complaint events', 'Identify delivery failures by domain', 'Monitor SES health'],
      workflow: ['Review delivery event log', 'Filter by bounce or complaint type', 'Check recipient domain patterns', 'Add to suppression if appropriate', 'Monitor SES sending reputation'],
      tips: ['AWS SES complaint rate above 0.1% can trigger account review.', 'Hard bounces should be immediately added to the suppression list.'],
      field_explanations: {
        'Bounce': 'Email was permanently rejected by the recipient server. Add to suppression.',
        'Complaint': 'Recipient marked email as spam. Add to suppression.',
        'Delivery': 'Email was successfully delivered to the recipient inbox.',
      },
    
      recommendations: ['If hard bounce rate is rising: add bounced addresses to Suppression List immediately.',
          'If complaint events appear: suppress the complainant and review the template that triggered the complaint.'],
      suggested_actions: ['Open Suppression List',
          'Review Mail Queue',
          'Check Email Templates'],
    },
    comms_suppression: {
      title: 'Suppression List',
      purpose: 'Blocked email addresses. Manage suppressed recipients to protect sending reputation.',
      common_tasks: ['Search for a specific email', 'Review suppression reason', 'Remove from suppression after resolution', 'Add manual blocks'],
      workflow: ['Search for email address', 'Review suppression type and date', 'Assess if suppression is still valid', 'Remove if customer issue resolved and bounce resolved', 'Document reason in audit log'],
      tips: ['Never remove a hard bounce suppression without confirming the bounce is resolved.', 'Manual blocks are appropriate for known bad actors.', 'Removing suppression for a complaint may repeat the spam report.'],
      field_explanations: {
        'hard_bounce': 'Permanent bounce — address does not exist or rejected permanently.',
        'complaint': 'Recipient reported email as spam.',
        'manual_block': 'Manually added by staff.',
        'abuse': 'Flagged as an abusive address.',
      },
    
      recommendations: ['If suppression list is growing rapidly: investigate the source — check Mail Queue for delivery failure patterns.',
          'Before removing a suppression: verify the underlying bounce or complaint is resolved.'],
      suggested_actions: ['Check Delivery Monitor',
          'Review Mail Queue',
          'Cross-reference with Support Tickets'],
    },
    comms_templates: {
      title: 'Email Templates',
      purpose: 'View and preview all system email templates. Reference for support and QA.',
      common_tasks: ['Preview a template', 'Review template content for accuracy', 'Check canned response availability', 'Cross-reference with mail queue events'],
      workflow: ['Browse template list', 'Select a template to preview', 'Review subject and body content', 'Flag inaccurate content for developer update'],
      tips: ['Templates cannot be edited from the dashboard — changes require a backend deploy.', 'Use canned responses in Support and Inboxes for customisable reply templates.'],
    
      recommendations: ['If a template shows high failure correlation in Mail Queue: flag for developer review — template update requires a backend deploy.'],
      suggested_actions: ['Cross-reference with Mail Queue',
          'Check Delivery Monitor'],
    },
    gmail_mailboxes: {
      title: 'Gmail Connector',
      purpose: 'Gmail API connection management for all four VISINVOKE mailboxes.',
      common_tasks: ['Check mailbox connection status', 'Renew watch subscriptions', 'Backfill historical messages', 'Disconnect and reconnect if sync issues'],
      workflow: ['Review connection status for all mailboxes', 'Check watch expiry dates', 'Renew any expiring watches', 'Backfill if messages are missing', 'Disconnect and reconnect if sync is broken'],
      tips: ['Gmail watch subscriptions expire every 7 days and must be renewed manually.', 'Backfill runs once per mailbox — re-run if new messages are missing from the inbox viewer.'],
      field_explanations: {
        'Watch Active': 'Gmail push notification subscription is active for this mailbox.',
        'Last Synced': 'Most recent successful sync event.',
        'Backfill': 'Import historical messages from Gmail into the VISINVOKE database.',
      },
    
      recommendations: ['If a mailbox shows disconnected status: reconnect immediately — inbound emails are not being received.',
          'If watch expiry is within 24 hours: renew now — expiry causes a gap in inbound email processing.'],
      suggested_actions: ['Renew watch subscriptions',
          'Run backfill if messages are missing',
          'Check Comms Inboxes after reconnection'],
    },

    // ── SUPPORT ──────────────────────────────────────────────────────────────
    support_assigned: {
      title: 'Assigned Tickets',
      purpose: 'Tickets assigned to you or a specific staff member. Use for personal workload management.',
      common_tasks: ['Review your open assigned tickets', 'Prioritise by severity', 'Reply to outstanding tickets', 'Resolve completed tickets'],
      workflow: ['Filter to your assignments', 'Sort by severity', 'Open highest severity first', 'Reply or resolve', 'Update status'],
      tips: ['Assigned tickets unactioned for 24h should be reassigned or escalated.'],
    
      recommendations: ['If assigned tickets are ageing without response: reassign or escalate — do not let them stale.'],
      suggested_actions: ['Escalate aged tickets',
          'Review Staff Workload for redistribution'],
    },
    support_resolved: {
      title: 'Resolved Tickets',
      purpose: 'Completed ticket history. Use for reference, quality review, and pattern analysis.',
      common_tasks: ['Search for a resolved ticket', 'Review resolution history', 'Check resolution time', 'Reopen if customer responds'],
      workflow: ['Search by customer email or ticket ID', 'Review full conversation', 'Check resolution action', 'Reopen if follow-up needed'],
      tips: ['Resolved tickets can be reopened if a customer responds. Check your resolved queue regularly.'],
    
      recommendations: ['If resolved tickets are being reopened frequently: review the resolution quality — canned responses may need updating.'],
      suggested_actions: ['Review Email Templates for response quality',
          'Check Support Queue for re-opened tickets'],
    },

    // ── PLATFORM OPS ─────────────────────────────────────────────────────────
    platform_jobs: {
      title: 'Jobs Monitor',
      purpose: 'Full generation job table with filtering, inspection, and bulk action capability.',
      common_tasks: ['Filter by failed status', 'Identify stuck jobs', 'Bulk requeue failed jobs', 'Refund credits on failed jobs', 'Open job inspector'],
      workflow: ['Filter by status and time window', 'Identify stuck jobs first', 'Select stuck or failed jobs', 'Bulk requeue', 'Refund credits if deducted', 'Monitor re-processing'],
      tips: ['Stuck jobs older than 10 minutes should be requeued.', 'Always refund credits before requeuing to avoid double-charging.', 'Bulk requeue of more than 50 jobs should be simulated first.'],
      recommendations: ['If large failure cluster found: check Platform Providers first before requeuing — requeuing to a degraded provider will fail again.'],
    
      suggested_actions: ['Bulk requeue stuck jobs',
          'Refund credits on failed jobs',
          'Check Platform Providers before requeuing'],
    },
    platform_credits: {
      title: 'Credit Anomalies',
      purpose: 'Unusual credit consumption events and anomaly detection log.',
      common_tasks: ['Review anomaly events', 'Identify suspicious consumption spikes', 'Cross-reference with user accounts', 'Escalate if abuse suspected'],
      workflow: ['Review anomaly log', 'Filter by severity', 'Open user inspector for flagged accounts', 'Check usage ledger for pattern', 'Apply restriction if abuse confirmed'],
      tips: ['Credit anomalies do not auto-restrict users — manual review and action required.', 'Cross-reference with the Usage Ledger for full consumption context.'],
    
      recommendations: ['If anomaly severity is critical: open User Intelligence for the flagged user immediately.',
          'Cross-reference with Usage Ledger to understand the full consumption pattern before acting.'],
      suggested_actions: ['Open User Intelligence for flagged user',
          'Check Usage Ledger',
          'Apply restriction via User Actions if confirmed'],
    },

    // ── AUTONOMOUS OPS ───────────────────────────────────────────────────────
    autonomous_rules: {
      title: 'Recovery Rules',
      purpose: 'Bounded autonomous recovery rule library. Simulate and execute recovery actions with full audit trail.',
      common_tasks: ['Review triggered rules', 'Simulate before executing', 'Execute with operator note', 'Review execution history'],
      workflow: ['Review rules list', 'Identify triggered rules', 'Open triggered rule', 'Simulate execution first', 'Review simulation output', 'Execute live with operator note', 'Monitor in Execution History'],
      tips: ['Always simulate before executing — simulation shows exact impact without mutations.', 'Dry-run mode is the default — explicitly switch to live execution.', 'All executions are permanently audit-logged.'],
      field_explanations: {
        'Triggered': 'Recovery rule condition is currently met and is eligible for execution.',
        'Dry Run': 'Execution mode that previews actions without making any changes.',
        'Rollback Capable': 'Whether the rule action can be reversed after execution.',
      },
    
      recommended_actions: ['Simulate before executing',
          'Add operator note to all live executions',
          'Check Execution History after running'],
      suggested_actions: ['Simulate recovery first',
          'Review Execution History',
          'Check Platform Overview after recovery'],
    
      recommendations: [
        'If a rule is triggered: always simulate first — dry-run shows exact impact without mutations.',
        'If multiple rules are triggered simultaneously: execute highest severity first.',
      ],
    },
    autonomous_queue: {
      title: 'Queue Recovery',
      purpose: 'Autonomous queue health monitoring and recovery rules specifically for queue saturation events.',
      common_tasks: ['Check queue saturation rules', 'Simulate queue drain', 'Execute queue recovery', 'Monitor queue depth after recovery'],
      workflow: ['Review queue saturation indicators', 'Check which rules are triggered', 'Simulate recovery action', 'Execute if simulation is safe', 'Monitor queue depth KPI in Platform Ops'],
      tips: ['Queue recovery actions drain stuck jobs — verify provider health first to avoid re-saturation.'],
    
      recommendations: ['Before executing queue drain: verify provider health — draining to a degraded provider re-saturates the queue.'],
      suggested_actions: ['Check Platform Providers first',
          'Review Platform Queues',
          'Monitor Platform Overview after execution'],
    },
    autonomous_provider: {
      title: 'Provider Failover',
      purpose: 'Autonomous provider failover rules. Redirect generation traffic away from degraded providers.',
      common_tasks: ['Check degraded provider rules', 'Simulate failover action', 'Execute failover', 'Monitor provider health post-failover'],
      workflow: ['Identify degraded provider', 'Check failover rule trigger state', 'Simulate failover', 'Execute with operator note', 'Monitor Platform Providers for recovery'],
      tips: ['Failover redirects new jobs — existing queued jobs are not automatically moved.', 'Monitor provider recovery to reverse failover when provider stabilises.'],
    
      recommendations: ['After failover: monitor Platform Providers to detect when the degraded provider recovers — reverse failover when stable.'],
      suggested_actions: ['Monitor Platform Providers post-failover',
          'Check Platform Jobs for routing',
          'Review Execution History'],
    },
    autonomous_credits: {
      title: 'Credit Protection',
      purpose: 'Autonomous credit anomaly detection and protection rules. Prevent runaway credit consumption.',
      common_tasks: ['Review credit anomaly rules', 'Check triggered protection rules', 'Simulate credit protection action', 'Execute if confirmed'],
      workflow: ['Review triggered rules', 'Cross-reference with Credit Anomalies section', 'Simulate restriction or lock action', 'Execute if pattern is confirmed abuse', 'Monitor Credit Monitor for normalisation'],
      tips: ['Credit protection rules apply account restrictions — review carefully before executing live.'],
    
      recommendations: ['Before executing credit protection: verify the anomaly is genuine — cross-reference with Credit Monitor and Usage Ledger.'],
      suggested_actions: ['Check Credit Monitor',
          'Review Usage Ledger',
          'Open User Intelligence for flagged user'],
    },
    autonomous_security: {
      title: 'Security Response',
      purpose: 'Autonomous security response rules. Lock accounts and revoke sessions on detected security events.',
      common_tasks: ['Review triggered security rules', 'Simulate lockdown action', 'Execute security response', 'Monitor Security Center post-action'],
      workflow: ['Review triggered rules', 'Open Security Center to verify the underlying events', 'Simulate lockdown', 'Execute if confirmed security incident', 'Add operator note with investigation reference'],
      tips: ['Security response actions are high-impact — always verify in Security Center before executing.', 'All security lockdowns revoke all sessions immediately.'],
    
      recommendations: ['Security lockdown actions revoke all sessions — ensure the correct user is targeted before executing live.',
          'Always verify in Security Center first — do not execute security response based on automation alone.'],
      suggested_actions: ['Verify in Security Center first',
          'Check Staff Sessions',
          'Review Audit Log after execution'],
    },
    autonomous_history: {
      title: 'Execution History',
      purpose: 'Complete audit trail of all autonomous recovery executions. Use for post-mortem and compliance review.',
      common_tasks: ['Review recent executions', 'Check execution outcomes', 'Review dry-run vs live ratio', 'Identify repeated rule executions'],
      workflow: ['Filter by time window', 'Review execution outcomes', 'Flag failed executions for investigation', 'Check repeated rule triggers for runaway conditions'],
      tips: ['Repeated executions of the same rule may indicate the root cause was not resolved.', 'All executions include the operator note — use these for post-mortem documentation.'],
    
      recommendations: ['If the same rule executes repeatedly: the root cause was not resolved — investigate the underlying condition.'],
      suggested_actions: ['Cross-reference with Automation History',
          'Review Platform Overview',
          'Check Automation Rules'],
    },

    // ── AUTOMATION ───────────────────────────────────────────────────────────
    auto_rules: {
      title: 'Automation Rules',
      purpose: 'Deterministic rule definitions and current trigger state. The rule engine evaluates continuously.',
      common_tasks: ['Review currently triggered rules', 'Check rule conditions', 'Navigate to incidents for active triggers', 'Review rule history'],
      workflow: ['Filter for triggered rules', 'Open triggered rule', 'Review trigger detail and condition', 'Navigate to Incidents to take action', 'Return to rules to confirm resolution'],
      tips: ['Triggered rules do not auto-execute — they surface operational conditions for human action.', 'Multiple triggered rules in the same category indicate systemic issues.'],
      field_explanations: {
        'Triggered': 'Rule condition is currently met based on live platform data.',
        'Category': 'Operational domain of the rule — platform, security, credits, support.',
        'Severity': 'Impact level if the rule condition is unaddressed.',
      },
    
      recommendations: ['If a rule is triggering repeatedly: the underlying condition is not being resolved — investigate the root cause.',
          'If multiple rules in the same category trigger: indicates a systemic issue in that operational domain.'],
      suggested_actions: ['Navigate to Automation Incidents',
          'Review Automation Triggers',
          'Check Automation History'],
    },
    auto_triggers: {
      title: 'Automation Triggers',
      purpose: 'Individual trigger event log. Each entry represents a specific rule condition evaluation.',
      common_tasks: ['Review recent trigger events', 'Identify high-frequency triggers', 'Cross-reference with incidents', 'Check trigger timing patterns'],
      workflow: ['Review trigger event log', 'Identify which rules are firing most frequently', 'Check timing — frequent triggers may indicate instability', 'Navigate to Incidents for remediation'],
      tips: ['High-frequency triggers on the same rule may indicate a threshold needs adjustment or a persistent platform issue.'],
    
      recommendations: ['If trigger frequency is accelerating: escalate to OPERATIONS_ADMIN — rapid trigger accumulation signals instability.'],
      suggested_actions: ['Open Automation Incidents',
          'Review Automation Rules',
          'Check Autonomous Overview'],
    },
    auto_suggestions: {
      title: 'Automation Suggestions',
      purpose: 'Proactive operational recommendations generated by the rule engine. Review and act or dismiss.',
      common_tasks: ['Review pending suggestions', 'Act on high-severity suggestions', 'Dismiss false positives', 'Navigate to suggested sections'],
      workflow: ['Review all pending suggestions', 'Prioritise by severity', 'Open suggested section or entity', 'Take action', 'Dismiss handled suggestions'],
      tips: ['Dismiss suggestions you have already addressed — this keeps the queue focused.', 'Suggestions are generated deterministically from rule evaluations, not AI.'],
    
      recommendations: ['If high-severity suggestions are accumulating: these represent missed operational signals — act immediately.',
          'Dismiss suggestions only after verifying the underlying condition has been resolved.'],
      suggested_actions: ['Act on high-severity suggestions first',
          'Navigate to suggested sections',
          'Dismiss after resolution'],
    },
    auto_history: {
      title: 'Automation History',
      purpose: 'Historical log of all automation events, trigger firings, and suggestion generation.',
      common_tasks: ['Review automation event history', 'Check which rules fired in a window', 'Use for post-mortem analysis', 'Identify rule trigger frequency trends'],
      workflow: ['Select time window', 'Review event log', 'Filter by event type', 'Identify patterns and frequency', 'Use for incident post-mortem documentation'],
      tips: ['Automation history is immutable — all events are permanently recorded for compliance.'],
    
      recommendations: ['If history shows repeated executions for the same rule: root cause investigation required — automation is compensating for an unresolved issue.'],
      suggested_actions: ['Cross-reference with Automation Rules',
          'Review Platform Overview',
          'Check Autonomous History'],
    },
  },

  // ── ENTITY GUIDANCE ─────────────────────────────────────────────────────────
  entities: {
    user: {
      title: 'User Entity',
      purpose: 'Inspect a customer account. Review risk, relationships, and history before taking action.',
      workflow: ['Review risk score and factors', 'Check recommended actions', 'Review relationship categories', 'Inspect timeline for incident context', 'Execute action if needed'],
      field_explanations: {
        'Risk Score': 'Composite score 0-100. Higher = more operational attention needed.',
        'Confidence': 'How many risk factors contributed. Higher confidence = more reliable risk assessment.',
        'Suppressed': 'Mail delivery blocked. Clear suppression before replying to tickets.',
        'Credits at Risk': 'Failed jobs with credits already deducted — consider refund.',
        'Mail Failures': 'Failed delivery events. High count may indicate suppression or bounce issues.',
      },
      next_steps: {
        critical: ['Clear suppression immediately', 'Review escalated tickets', 'Check failed job cluster', 'Consider account lock if compromised'],
        high: ['Review failed jobs', 'Check open tickets', 'Review mail delivery history', 'Grant credits if jobs failed with deduction'],
        moderate: ['Review recent timeline', 'Check open ticket status'],
        low: ['No immediate action required', 'Continue standard monitoring'],
      },
    },
    job: {
      title: 'Job Entity',
      purpose: 'Inspect a single generation job. Use to understand failure cause and determine remediation.',
      workflow: ['Check job status and error code', 'Review attempt count', 'Check credit deduction status', 'Requeue or refund as appropriate'],
      field_explanations: {
        'Stuck': 'Job has been in queued/processing state past the processing window. Requeue immediately.',
        'Attempts': 'Number of retry attempts. More than 3 suggests provider or spec issue.',
        'Credit Deducted': 'Credits were charged but job failed. Refund required.',
        'Error Code': 'Provider-specific error. Use to identify root cause.',
        'Duration': 'Provider processing time. High latency may indicate provider degradation.',
      },
      next_steps: {
        critical: ['Requeue stuck job immediately', 'Check provider health', 'Refund credits if deducted'],
        high: ['Review error code', 'Check provider telemetry', 'Requeue if provider recovered', 'Refund credits'],
        moderate: ['Acknowledge failure', 'Monitor for recurrence'],
        low: ['No action required'],
      },
    },
    ticket: {
      title: 'Support Ticket',
      purpose: 'Inspect a customer support ticket. Review full context before responding.',
      workflow: ['Review ticket severity and status', 'Check user intelligence panel', 'Review failed jobs', 'Check suppression status', 'Reply or escalate'],
      field_explanations: {
        'Severity': 'Ticket priority set by customer or staff. Critical requires immediate response.',
        'Escalated': 'Ticket has been escalated to senior staff. Requires immediate attention.',
        'User Failed Jobs': 'Recent failed generation jobs for this user — may be related to the ticket.',
        'Suppression': 'If suppressed, email replies will bounce. Clear suppression first.',
      },
      next_steps: {
        critical: ['Respond within 1 hour', 'Check suppression before replying', 'Review related failed jobs', 'Consider credit compensation'],
        high: ['Respond same day', 'Review user intelligence', 'Check failed jobs'],
        moderate: ['Respond within 1 business day', 'Review ticket context'],
        low: ['Standard response timeline'],
      },
    },
    incident: {
      title: 'Operational Incident',
      purpose: 'Root cause analysis hub for active incidents. Full operational context in one view.',
      workflow: ['Review severity and incident type', 'Check RCA metrics strip', 'Review affected users and jobs', 'Check provider impact', 'Review automation actions taken', 'Execute recovery or acknowledge'],
      field_explanations: {
        'Affected Users': 'Users impacted by failed jobs in the incident window.',
        'Failed Jobs': 'Generation jobs that failed during the incident.',
        'Stuck Jobs': 'Jobs in queued/processing state past the processing window — requires immediate requeue.',
        'Recovery Attempts': 'Autonomous recovery actions already executed.',
        'Credit Impact': 'Credits deducted on failed jobs — may require bulk refund.',
        'Provider Impact': 'Provider-specific failure counts during the incident window.',
      },
      next_steps: {
        critical: ['Requeue stuck jobs immediately', 'Check and failover degraded providers', 'Process credit refunds', 'Acknowledge incident to track response', 'Notify affected users if mail not suppressed'],
        high: ['Review provider health', 'Check automation recovery status', 'Process credit refunds', 'Acknowledge incident'],
        moderate: ['Monitor recovery progress', 'Acknowledge after review'],
        low: ['Acknowledge and continue monitoring'],
      },
    },
    queue: {
      title: 'Queue Entity',
      purpose: 'Inspect queue operational state. Monitor depth and processing health.',
      workflow: ['Review queue depth', 'Check stuck job count', 'Compare against normal baseline', 'Pause or drain if needed', 'Reroute provider if source of issue'],
      field_explanations: {
        'Queue Depth': 'Current number of jobs waiting. Spikes indicate saturation or stuck workers.',
        'Processing Rate': 'Jobs being processed per minute. Drop signals worker issues.',
        'Stuck Jobs': 'Jobs past the processing window. Requeue or drain to resolve.',
      },
      next_steps: {
        critical: ['Pause queue to prevent further accumulation', 'Drain stuck jobs', 'Reroute to healthy provider'],
        high: ['Monitor depth trend', 'Check provider health', 'Consider requeue of oldest stuck jobs'],
        moderate: ['Continue monitoring', 'Flag for review'],
        low: ['No action required'],
      },
    },
    provider: {
      title: 'Provider Entity',
      purpose: 'Inspect a generation provider. Review success rates, latency, and operational state.',
      workflow: ['Review success rate', 'Check latency trends', 'Compare against threshold', 'Trigger failover if degraded'],
      field_explanations: {
        'Success Rate': 'Percentage of jobs completed successfully. Below 85% warrants investigation.',
        'Latency': 'Average provider response time. High latency may precede failures.',
        'Failure Rate': 'Percentage of jobs that failed. Cluster with other providers to isolate root cause.',
      },
      next_steps: {
        critical: ['Trigger provider failover immediately', 'Reroute queues to healthy provider', 'Monitor recovery'],
        high: ['Check provider status page', 'Reduce traffic to degraded provider', 'Monitor closely'],
        moderate: ['Monitor latency trend', 'Prepare failover if degrades further'],
        low: ['No action required'],
      },
    },
    automation_rule: {
      title: 'Automation Rule',
      purpose: 'Inspect a deterministic automation rule. Review trigger state and execution history.',
      workflow: ['Check if rule is currently triggered', 'Review trigger condition and detail', 'Check rate limit status', 'Simulate before executing', 'Execute if conditions warrant'],
      field_explanations: {
        'Triggered': 'Rule condition is currently met. Does not auto-execute — requires manual or autonomous action.',
        'Rate OK': 'Rule has not hit its hourly execution limit. If rate limited, investigate runaway condition.',
        'Cooldown': 'Minimum time between executions. Prevents over-correction.',
        'Rollback Capable': 'Whether the action can be reversed if needed.',
      },
      next_steps: {
        high: ['Simulate rule before executing', 'Review trigger detail for accuracy', 'Execute with operator note'],
        low: ['No action required — rule not triggered'],
      },
    },
    generator: {
      title: 'Generator Entity',
      purpose: 'Inspect a content generator. Review usage and failure patterns.',
      workflow: ['Review fail rate', 'Compare against normal baseline', 'Check for provider-specific failures', 'Investigate spec if rate high'],
      field_explanations: {
        'Fail Rate': 'Percentage of generation attempts that failed. Above 20% warrants investigation.',
        'Total Uses': 'Lifetime generation count for this generator.',
        'Failed Uses': 'Lifetime failed generation count.',
      },
      next_steps: {
        high: ['Review recent failed jobs for this generator', 'Check provider routing', 'Review generator spec for issues'],
        low: ['No action required'],
      },
    },
  },

  // ── FIELD EXPLANATIONS (global) ──────────────────────────────────────────
  global_fields: {
    'CAUSE': 'Root events that triggered or preceded the incident or failure.',
    'FAILURE': 'Direct failure events — jobs, mail, or system failures.',
    'AUTOMATION_RESPONSE': 'Actions taken automatically by the autonomous recovery system.',
    'STAFF_RESPONSE': 'Manual actions taken by staff members.',
    'RECOVERY': 'Requeue, retry, or resolution events.',
    'FINAL_STATE': 'Terminal state events — resolved, closed, or completed.',
    'Live Refresh': 'Inspector data refreshes automatically every 30 seconds. AGING means 60s+ since last refresh. STALE means 180s+.',
    'Relationship Graph': 'Grouped view of all entities connected to this entity by operational relationship.',
    'Risk Intelligence': 'Deterministic risk assessment based on observed operational data. Not AI-generated.',
  },
};

// ── Guidance context ──────────────────────────────────────────────────────────
const GuidanceContext = React.createContext(null);

// Role capability map — static constant, outside component to avoid memo deps
const ROLE_CAPABILITIES = {
  OWNER:             ['all'],
  CO_OWNER:          ['all'],
  OPERATIONS_ADMIN:  ['platform', 'automation', 'providers', 'support', 'credits', 'security', 'users'],
  SUPPORT:           ['support', 'users', 'communications'],
  MODERATION:        ['users', 'rewards'],
  FINANCE:           ['revenue', 'credits', 'costs'],
  ANALYTICS_VIEWER:  ['analytics'],
  TECH_OPERATOR:     ['platform', 'providers', 'automation'],
  SECURITY_AUDITOR:  ['security', 'audit', 'sessions'],
  DELIVERABILITY_ADMIN: ['communications'],
  CONTENT_MANAGER:   ['users', 'generators'],
  GENERATOR_QA:      ['generators', 'jobs'],
};

function OperationalGuidanceProvider({ children, section, inspectorData, inspectorCurrent, activeRole, hasPermission }) {
  const [open, setOpen] = useState(false);

  // Determine active guidance context
  const activeContext = React.useMemo(() => {
    // If inspector is open, show entity-specific guidance
    if (inspectorCurrent && inspectorData) {
      const entityType = inspectorData.entity_type || inspectorCurrent.type;
      const entityGuide = GUIDANCE_CONTENT.entities[entityType];
      if (entityGuide) {
        const risk = (inspectorData.risk_detail && inspectorData.risk_detail.severity) || inspectorData.risk || 'low';
        return { mode:'entity', entityType, guide:entityGuide, risk, activeRole };
      }
    }
    // Otherwise show section guidance
    const sectionGuide = GUIDANCE_CONTENT.sections[section];
    // Cross-system rules for this section
    const crossRules = CROSS_SYSTEM_RULES[section] || [];
    // Role capabilities
    const caps = ROLE_CAPABILITIES[activeRole] || [];
    const canAll = caps.includes('all');
    return {
      mode: 'section',
      section,
      guide: sectionGuide || null,
      crossRules,
      activeRole,
      canAll,
      // AI enhancement context — structured data for future AI layer
      aiContext: {
        section,
        activeRole,
        entityType: null,
        crossRules: crossRules.map(r => r.signal),
        recommendations: sectionGuide ? (sectionGuide.recommendations || []) : [],
      },
    };
  }, [section, inspectorData, inspectorCurrent, activeRole]);

  return (
    <GuidanceContext.Provider value={{ open, setOpen, activeContext }}>
      {children}
    </GuidanceContext.Provider>
  );
}

function useGuidance() {
  return React.useContext(GuidanceContext);
}

// ── GuidanceRCButton — right column trigger (full label) ─────────────────────
function GuidanceRCButton() {
  const ctx = useGuidance();
  if (!ctx) return null;
  const { open, setOpen } = ctx;
  return (
    <button
      className={`ad-rc-guidance-btn${open ? ' active' : ''}`}
      onClick={() => setOpen(v => !v)}
      title="VISINVOKE Guidance"
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      Guidance
    </button>
  );
}

// ── GuidanceTip ───────────────────────────────────────────────────────────────
function GuidanceTip({ text }) {
  return (
    <div style={{
      display:'flex', gap:7, padding:'6px 9px', marginBottom:4,
      background:'rgba(45,108,255,0.07)', border:'1px solid rgba(45,108,255,0.18)',
      borderRadius:4, fontSize:12, color:'#cbd5e0', lineHeight:1.5,
    }}>
      <span style={{ color:'#2D6CFF', flexShrink:0, fontSize:11, fontWeight:800, marginTop:1 }}>TIP</span>
      {text}
    </div>
  );
}

// ── GuidanceSection ───────────────────────────────────────────────────────────
function GuidanceSection({ title, children, defaultOpen=true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom:10 }}>
      <div
        style={{
          display:'flex', alignItems:'center', gap:7, padding:'5px 0',
          cursor:'pointer', userSelect:'none',
          borderBottom:'1px solid rgba(255,255,255,0.06)', marginBottom: open ? 7 : 0,
        }}
        onClick={() => setOpen(v => !v)}
      >
        <span style={{ fontSize:11, fontWeight:700, color:'#8899aa', textTransform:'uppercase', letterSpacing:'0.07em', flex:1 }}>{title}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2"
          style={{ transform:open?'rotate(180deg)':'none', transition:'transform 0.13s', flexShrink:0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>
      {open && <div>{children}</div>}
    </div>
  );
}

// ── GuidanceCard ──────────────────────────────────────────────────────────────
function GuidanceCard({ label, value }) {
  return (
    <div style={{
      display:'flex', alignItems:'flex-start', gap:8, padding:'5px 0',
      borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:12,
    }}>
      <span style={{ color:'#2D6CFF', flexShrink:0, marginTop:1, fontSize:11 }}>+</span>
      <div style={{ flex:1 }}>
        {label && <div style={{ fontWeight:700, color:'#e2e8f0', marginBottom:1 }}>{label}</div>}
        <div style={{ color:'#8899aa', lineHeight:1.5 }}>{value}</div>
      </div>
    </div>
  );
}

// ── GuidanceWorkflow ──────────────────────────────────────────────────────────
function GuidanceWorkflow({ steps }) {
  return (
    <div>
      {steps.map((step, i) => (
        <div key={i} style={{ display:'flex', gap:9, alignItems:'flex-start', marginBottom:5 }}>
          <span style={{
            width:18, height:18, borderRadius:'50%', flexShrink:0,
            background:'rgba(45,108,255,0.15)', border:'1px solid rgba(45,108,255,0.3)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:10, fontWeight:800, color:'#2D6CFF',
          }}>
            {i+1}
          </span>
          <span style={{ fontSize:12, color:'#cbd5e0', lineHeight:1.5, paddingTop:1 }}>{step}</span>
        </div>
      ))}
    </div>
  );
}

// ── GuidanceNextSteps ─────────────────────────────────────────────────────────
function GuidanceNextSteps({ nextSteps, risk }) {
  const sev = risk || 'low';
  const steps = nextSteps[sev] || nextSteps.low || [];
  const sevColor = {critical:'#e53e3e', high:'#c05621', moderate:'#b7791f', low:'#38a169'}[sev] || '#38a169';
  if (steps.length === 0) return null;
  return (
    <div>
      <div style={{ fontSize:10, fontWeight:700, color:sevColor, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6, display:'flex', alignItems:'center', gap:5 }}>
        <span style={{ width:6, height:6, borderRadius:'50%', background:sevColor, flexShrink:0 }}/>
        Recommended for {sev} risk
      </div>
      {steps.map((step, i) => (
        <div key={i} style={{ display:'flex', gap:7, alignItems:'flex-start', marginBottom:4 }}>
          <span style={{ color:sevColor, flexShrink:0, fontSize:13, lineHeight:1 }}>-</span>
          <span style={{ fontSize:12, color:'#cbd5e0', lineHeight:1.5 }}>{step}</span>
        </div>
      ))}
    </div>
  );
}

// ── OperationalGuidancePanel — main slide-over panel ─────────────────────────
function OperationalGuidancePanel() {
  const ctx = useGuidance();
  if (!ctx) return null;
  const { open, setOpen, activeContext } = ctx;
  if (!open) return null;

  const { mode, guide, entityType, risk, crossRules } = activeContext;

  const renderContent = () => {
    if (!guide) {
      return (
        <div style={{ padding:'20px 0', textAlign:'center', fontSize:12, color:'#556677' }}>
          No guidance available for this section yet.
        </div>
      );
    }

    return (
      <>
        {/* Purpose */}
        <div style={{
          padding:'10px 12px', marginBottom:12,
          background:'rgba(45,108,255,0.06)', border:'1px solid rgba(45,108,255,0.15)',
          borderLeft:'3px solid #2D6CFF', borderRadius:4,
          fontSize:13, color:'#cbd5e0', lineHeight:1.6,
        }}>
          {guide.purpose}
        </div>

        {/* Workflow */}
        {guide.workflow && guide.workflow.length > 0 && (
          <GuidanceSection title="Recommended Workflow">
            <GuidanceWorkflow steps={guide.workflow}/>
          </GuidanceSection>
        )}

        {/* Next Steps (entity mode with risk) */}
        {mode === 'entity' && guide.next_steps && risk && (
          <GuidanceSection title="Next Steps">
            <GuidanceNextSteps nextSteps={guide.next_steps} risk={risk}/>
          </GuidanceSection>
        )}

        {/* Common Tasks (section mode) */}
        {mode === 'section' && guide.common_tasks && guide.common_tasks.length > 0 && (
          <GuidanceSection title="Common Tasks">
            {guide.common_tasks.map((t, i) => (
              <div key={i} style={{ display:'flex', gap:7, marginBottom:4 }}>
                <span style={{ color:'#2D6CFF', flexShrink:0 }}>+</span>
                <span style={{ fontSize:12, color:'#cbd5e0' }}>{t}</span>
              </div>
            ))}
          </GuidanceSection>
        )}

        {/* Recommendations (section mode - deterministic) */}
        {mode === 'section' && guide.recommendations && guide.recommendations.length > 0 && (
          <GuidanceSection title="Operational Recommendations">
            {guide.recommendations.map((r, i) => (
              <div key={i} style={{ display:'flex', gap:7, padding:'5px 7px', marginBottom:4, background:'rgba(197,48,48,0.06)', border:'1px solid rgba(197,48,48,0.15)', borderLeft:'2px solid #c53030', borderRadius:4 }}>
                <span style={{ color:'#fc8181', flexShrink:0, fontSize:11, fontWeight:800, marginTop:1 }}>!</span>
                <span style={{ fontSize:12, color:'#cbd5e0', lineHeight:1.5 }}>{r}</span>
              </div>
            ))}
          </GuidanceSection>
        )}

        {/* Cross-System Intelligence */}
        {mode === 'section' && crossRules && crossRules.length > 0 && (
          <GuidanceSection title="Cross-System Signals" defaultOpen={true}>
            {crossRules.map((rule, i) => (
              <div key={i} style={{ marginBottom:8, padding:'7px 9px', background:'rgba(49,130,206,0.06)', border:'1px solid rgba(49,130,206,0.15)', borderLeft:'2px solid #3182ce', borderRadius:4 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#63b3ed', marginBottom:3 }}>{rule.signal}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.55)', marginBottom:4 }}>{rule.reason}</div>
                <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                  {(rule.related || []).map((r, j) => (
                    <span key={j} style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:3, background:'rgba(49,130,206,0.12)', border:'1px solid rgba(49,130,206,0.25)', color:'#90cdf4' }}>{r}</span>
                  ))}
                </div>
              </div>
            ))}
          </GuidanceSection>
        )}

        {/* Suggested Actions */}
        {mode === 'section' && guide.suggested_actions && guide.suggested_actions.length > 0 && (
          <GuidanceSection title="Suggested Actions">
            {guide.suggested_actions.map((a, i) => (
              <div key={i} style={{ display:'flex', gap:7, alignItems:'center', padding:'4px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ color:'#2D6CFF', fontSize:11, flexShrink:0 }}>→</span>
                <span style={{ fontSize:12, color:'#e2e8f0' }}>{a}</span>
              </div>
            ))}
          </GuidanceSection>
        )}

        {/* Field explanations */}
        {guide.field_explanations && Object.keys(guide.field_explanations).length > 0 && (
          <GuidanceSection title="Field Reference" defaultOpen={false}>
            {Object.entries(guide.field_explanations).map(([k, v]) => (
              <GuidanceCard key={k} label={k} value={v}/>
            ))}
          </GuidanceSection>
        )}

        {/* Global field explanations */}
        <GuidanceSection title="System Reference" defaultOpen={false}>
          {Object.entries(GUIDANCE_CONTENT.global_fields).map(([k, v]) => (
            <GuidanceCard key={k} label={k} value={v}/>
          ))}
        </GuidanceSection>

        {/* Tips (section mode) */}
        {mode === 'section' && guide.tips && guide.tips.length > 0 && (
          <GuidanceSection title="Operational Tips" defaultOpen={false}>
            {guide.tips.map((tip, i) => <GuidanceTip key={i} text={tip}/>)}
          </GuidanceSection>
        )}
      </>
    );
  };

  const panelTitle = guide ? guide.title : (mode === 'entity' ? `${entityType} Guide` : 'Operational Guide');

  return (
    <>
      {/* Backdrop */}
      <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:7900 }}/>

      {/* Slide-over panel */}
      <div style={{
        position:'fixed', top:0, right:0, bottom:0, width:380,
        background:'#0f1824', borderLeft:'1px solid rgba(255,255,255,0.08)',
        zIndex:7901, display:'flex', flexDirection:'column',
        boxShadow:'-8px 0 32px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{
          padding:'16px 18px', borderBottom:'1px solid rgba(255,255,255,0.07)',
          flexShrink:0, background:'#0a1019',
          display:'flex', alignItems:'center', gap:10,
        }}>
          <span style={{
            fontSize:10, fontWeight:800, color:'#2D6CFF',
            background:'rgba(45,108,255,0.12)', border:'1px solid rgba(45,108,255,0.25)',
            borderRadius:4, padding:'2px 7px', letterSpacing:'0.07em', flexShrink:0,
          }}>
            {mode === 'entity' ? entityType.toUpperCase() : 'GUIDE'}
          </span>
          <div style={{ fontSize:14, fontWeight:700, color:'#e2e8f0', flex:1 }}>{panelTitle}</div>
          <button onClick={() => setOpen(false)} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.6)', cursor:'pointer', fontSize:20, lineHeight:1 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'16px 18px' }}>
          {renderContent()}
        </div>

        {/* Footer */}
        <div style={{
          padding:'10px 18px', borderTop:'1px solid rgba(255,255,255,0.06)',
          fontSize:11, color:'#445566', flexShrink:0,
        }}>
          Guidance updates automatically based on current view and inspector state.
        </div>
      </div>
    </>
  );
}

// ============================================================================
// END OPERATIONAL GUIDANCE SYSTEM
// ============================================================================

// InvestigationResumeBridge — accesses openEntity from EntityInspectorContext to wire resume card
function InvestigationResumeBridge() {
  const { openEntity } = useEntityInspector();
  return (
    <InvestigationResumeCard
      onResume={(type, id) => openEntity(type, id)}
      onDismiss={() => {}}
    />
  );
}

// GuidanceBridge — reads inspector context inside EntityInspectorProvider and feeds OperationalGuidanceProvider
function GuidanceBridge({ section, children }) {
  const { data: inspectorData, current: inspectorCurrent } = useEntityInspector();
  const { activeRole, hasPermission } = useStaffAuth();
  return (
    <OperationalGuidanceProvider section={section} inspectorData={inspectorData} inspectorCurrent={inspectorCurrent} activeRole={activeRole} hasPermission={hasPermission}>
      {children}
    </OperationalGuidanceProvider>
  );
}

// ===========================================================================
// DCC ADMIN — Overview + News & Updates management (native Staff Admin style).
// Talks to /v1/staff/dcc/* (require_staff + dcc.view / dcc.manage). All fetches
// send credentials (staff JWT cookie). Nothing here is customer-facing.
// ===========================================================================
const DCC_AUDIENCES = [
  ['all','All DCC Users'], ['technician','Technician Users'], ['combat_pro','Combat PRO Users'],
  ['generators','Generator Users'], ['scc','Service Command Centre Users'],
  ['security','Security Users'], ['rpg_worlds','RPG Worlds Users'],
];
const DCC_PRIORITIES = [['normal','Normal'], ['important','Important'], ['critical','Critical']];
const DCC_STATUSES = ['draft','scheduled','published','archived'];

function dccFmtDate(iso){
  if(!iso) return '—';
  try{ const d=new Date(iso); if(isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined,{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}); }
  catch(e){ return '—'; }
}
function dccToLocalInput(iso){
  try{ const d=new Date(iso); if(isNaN(d.getTime())) return '';
    const p=n=>String(n).padStart(2,'0');
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; }
  catch(e){ return ''; }
}
function dccMini(color){
  return {minHeight:30,padding:'0 10px',borderRadius:6,fontSize:13,fontWeight:600,cursor:'pointer',
    border:`1px solid ${color}`,background:'transparent',color:color,fontFamily:font};
}
function dccEmptyForm(){
  return {title:'',summary:'',body:'',audience:'all',priority:'normal',publishMode:'draft',
    publish_at:'',expiry_at:'',cta_label:'',cta_target:''};
}

function SecDccOverview({api}){
  const [data,setData]=useState(null);
  const [err,setErr]=useState(null);
  useEffect(()=>{
    let alive=true;
    fetch(`${api}/v1/staff/dcc/overview`,{credentials:'include'})
      .then(r=>r.json())
      .then(d=>{ if(!alive) return; if(d && d.counts){ setData(d); setErr(null); } else { setErr((d&&d.error)||'Failed to load'); } })
      .catch(()=>{ if(alive) setErr('Failed to load DCC overview'); });
    return ()=>{alive=false;};
  },[api]);
  if(err) return <div style={s.content}><div style={s.error}>{err}</div></div>;
  if(!data) return <div style={s.content}><Loader/></div>;
  const c=data.counts||{};
  const Stat=({label,value,color})=>(
    <div style={s.panel}><div style={{fontSize:13,color:T.textSub,marginBottom:6}}>{label}</div>
      <div style={{fontSize:28,fontWeight:800,color:color||T.text}}>{value}</div></div>
  );
  return (
    <div style={s.content}>
      <div style={s.grid(4)}>
        <Stat label="Published" value={c.published||0} color={T.green}/>
        <Stat label="Scheduled" value={c.scheduled||0} color={T.amber}/>
        <Stat label="Drafts" value={c.draft||0} color={T.textMid}/>
        <Stat label="Live now" value={c.active||0} color={T.accent}/>
      </div>
      <div style={{...s.panel,marginBottom:12}}>
        <div style={s.panelTitle}>DCC client</div>
        <div style={{fontSize:13,color:T.textSub,marginTop:4}}>Enforced minimum client version:{' '}
          <b style={{color:T.text}}>{data.dccMinClientVersion||'—'}</b></div>
      </div>
      <TableCard title="Currently live" count={(data.active||[]).length}>
        <table style={s.table}><thead><tr>
          <th style={s.th}>Title</th><th style={s.th}>Audience</th><th style={s.th}>Priority</th><th style={s.th}>Published</th>
        </tr></thead><tbody>
          {(data.active||[]).map(n=>(
            <tr key={n.id}><td style={s.tds}>{n.title}</td><td style={s.td}>{n.audienceLabel||n.audience}</td>
              <td style={s.td}><span style={s.badge(n.priority==='critical'?'red':n.priority==='important'?'amber':'blue')}>{n.priority}</span></td>
              <td style={s.td}>{dccFmtDate(n.publishAt)}</td></tr>
          ))}
          {(!data.active||!data.active.length)&&<tr><td style={s.td} colSpan={4}><div style={s.empty}>No live announcements</div></td></tr>}
        </tbody></table>
      </TableCard>
      <TableCard title="Scheduled" count={(data.scheduled||[]).length}>
        <table style={s.table}><thead><tr>
          <th style={s.th}>Title</th><th style={s.th}>Audience</th><th style={s.th}>Publishes</th>
        </tr></thead><tbody>
          {(data.scheduled||[]).map(n=>(
            <tr key={n.id}><td style={s.tds}>{n.title}</td><td style={s.td}>{n.audienceLabel||n.audience}</td>
              <td style={s.td}>{dccFmtDate(n.publishAt)}</td></tr>
          ))}
          {(!data.scheduled||!data.scheduled.length)&&<tr><td style={s.td} colSpan={3}><div style={s.empty}>Nothing scheduled</div></td></tr>}
        </tbody></table>
      </TableCard>
    </div>
  );
}

function DccNewsModal({editing,form,setForm,onSubmit,onClose,saving,formErr}){
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const box={position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:9000,display:'flex',
    alignItems:'flex-start',justifyContent:'center',overflowY:'auto',padding:'40px 16px'};
  const card={background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:24,width:'100%',
    maxWidth:640,boxShadow:'0 20px 60px rgba(0,0,0,0.5)'};
  const label={fontSize:13,fontWeight:600,color:T.textMid,margin:'12px 0 5px'};
  const full={...s.input,width:'100%',minHeight:42};
  const area={...s.input,width:'100%',minHeight:110,padding:'10px 14px',resize:'vertical',lineHeight:1.5};
  const wantSchedule = form.publishMode==='schedule' || (editing && editing.status==='scheduled');
  return (
    <div style={box} onClick={onClose}>
      <form style={card} onClick={e=>e.stopPropagation()} onSubmit={onSubmit}>
        <div style={{fontSize:18,fontWeight:700,color:T.text,marginBottom:4}}>{editing?'Edit announcement':'New announcement'}</div>
        <div style={{fontSize:13,color:T.textSub,marginBottom:8}}>
          {editing?`Status: ${editing.status}. Use the list actions to publish / archive.`:'Choose Draft, Publish now, or Schedule below.'}</div>
        {formErr&&<div style={s.error}>{formErr}</div>}
        <div style={label}>Title *</div>
        <input style={full} value={form.title} onChange={e=>set('title',e.target.value)} maxLength={255}/>
        <div style={label}>Short summary</div>
        <input style={full} value={form.summary} onChange={e=>set('summary',e.target.value)} maxLength={500}/>
        <div style={label}>Full message *</div>
        <textarea style={area} value={form.body} onChange={e=>set('body',e.target.value)}/>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div><div style={label}>Audience</div>
            <select style={{...s.select,width:'100%'}} value={form.audience} onChange={e=>set('audience',e.target.value)}>
              {DCC_AUDIENCES.map(([v,l])=><option key={v} value={v} style={OPT_DARK}>{l}</option>)}
            </select></div>
          <div><div style={label}>Priority</div>
            <select style={{...s.select,width:'100%'}} value={form.priority} onChange={e=>set('priority',e.target.value)}>
              {DCC_PRIORITIES.map(([v,l])=><option key={v} value={v} style={OPT_DARK}>{l}</option>)}
            </select></div>
        </div>
        {!editing&&<div>
          <div style={label}>Publishing</div>
          <select style={{...s.select,width:'100%'}} value={form.publishMode} onChange={e=>set('publishMode',e.target.value)}>
            <option value="draft" style={OPT_DARK}>Save as draft</option>
            <option value="publish" style={OPT_DARK}>Publish now</option>
            <option value="schedule" style={OPT_DARK}>Schedule</option>
          </select>
        </div>}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div><div style={label}>{wantSchedule?'Publish at *':'Publish at (optional)'}</div>
            <input type="datetime-local" style={full} value={form.publish_at} onChange={e=>set('publish_at',e.target.value)}/></div>
          <div><div style={label}>Expiry (optional)</div>
            <input type="datetime-local" style={full} value={form.expiry_at} onChange={e=>set('expiry_at',e.target.value)}/></div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div><div style={label}>CTA label (optional)</div>
            <input style={full} value={form.cta_label} onChange={e=>set('cta_label',e.target.value)} maxLength={64}/></div>
          <div><div style={label}>CTA target (/path or https://*.visinvoke.com)</div>
            <input style={full} value={form.cta_target} onChange={e=>set('cta_target',e.target.value)} placeholder="/dashboard/..."/></div>
        </div>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:18}}>
          <button type="button" style={s.btn()} onClick={onClose}>Cancel</button>
          <button type="submit" style={s.btn('primary')} disabled={saving}>{saving?'Saving…':(editing?'Save changes':'Create')}</button>
        </div>
      </form>
    </div>
  );
}

function SecDccNews({api}){
  const [rows,setRows]=useState(null);
  const [err,setErr]=useState(null);
  const [statusFilter,setStatusFilter]=useState('');
  const [audienceFilter,setAudienceFilter]=useState('');
  const [search,setSearch]=useState('');
  const [modalOpen,setModalOpen]=useState(false);
  const [editing,setEditing]=useState(null);
  const [form,setForm]=useState(dccEmptyForm());
  const [saving,setSaving]=useState(false);
  const [formErr,setFormErr]=useState(null);
  const [busyId,setBusyId]=useState(null);

  const load=useCallback(()=>{
    const q=new URLSearchParams();
    if(statusFilter)q.set('status',statusFilter);
    if(audienceFilter)q.set('audience',audienceFilter);
    if(search)q.set('search',search);
    fetch(`${api}/v1/staff/dcc/news?${q.toString()}`,{credentials:'include'})
      .then(r=>r.json())
      .then(d=>{ if(d && Array.isArray(d.items)){ setRows(d.items); setErr(null); } else { setErr((d&&d.error)||'Failed to load'); } })
      .catch(()=>setErr('Failed to load news'));
  },[api,statusFilter,audienceFilter,search]);
  useEffect(()=>{load();},[load]);

  const openCreate=()=>{ setEditing(null); setForm(dccEmptyForm()); setFormErr(null); setModalOpen(true); };
  const openEdit=(n)=>{
    setEditing(n);
    setForm({ title:n.title||'', summary:n.summary||'', body:n.body||'', audience:n.audience||'all', priority:n.priority||'normal',
      publishMode: n.status==='scheduled'?'schedule':(n.status==='published'?'publish':'draft'),
      publish_at: n.publishAt?dccToLocalInput(n.publishAt):'', expiry_at: n.expiryAt?dccToLocalInput(n.expiryAt):'',
      cta_label:n.ctaLabel||'', cta_target:n.ctaTarget||'' });
    setFormErr(null); setModalOpen(true);
  };

  const submit=async(e)=>{
    e.preventDefault(); setSaving(true); setFormErr(null);
    const payload={ title:form.title, summary:form.summary, body:form.body, audience:form.audience, priority:form.priority,
      cta_label:form.cta_label, cta_target:form.cta_target,
      publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : null,
      expiry_at:  form.expiry_at  ? new Date(form.expiry_at).toISOString()  : null };
    if(!editing){ payload.status = form.publishMode==='publish'?'published':form.publishMode==='schedule'?'scheduled':'draft'; }
    try{
      const url = editing ? `${api}/v1/staff/dcc/news/${editing.id}` : `${api}/v1/staff/dcc/news`;
      const r = await fetch(url,{method:editing?'PUT':'POST',credentials:'include',
        headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
      const d = await r.json().catch(()=>({}));
      if(r.ok){ setModalOpen(false); load(); } else { setFormErr(d.error||'Save failed'); }
    }catch(x){ setFormErr('Save failed'); }
    setSaving(false);
  };

  const action=async(n,verb)=>{
    setBusyId(n.id);
    try{
      const r = verb==='delete'
        ? await fetch(`${api}/v1/staff/dcc/news/${n.id}`,{method:'DELETE',credentials:'include'})
        : await fetch(`${api}/v1/staff/dcc/news/${n.id}/${verb}`,{method:'POST',credentials:'include',
            headers:{'Content-Type':'application/json'},body:JSON.stringify({})});
      const d = await r.json().catch(()=>({}));
      if(r.ok){ setErr(null); load(); } else { setErr(d.error||`${verb} failed`); }
    }catch(x){ setErr(`${verb} failed`); }
    setBusyId(null);
  };
  const del=(n)=>{ if(window.confirm(`Delete "${n.title}"? This cannot be undone.`)) action(n,'delete'); };

  return (
    <div style={s.content}>
      {err&&<div style={s.error}>{err}</div>}
      <div style={s.filterRow}>
        <input style={{...s.input,minWidth:220}} placeholder="Search title / summary" value={search} onChange={e=>setSearch(e.target.value)}/>
        <select style={s.select} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}>
          <option value="" style={OPT_DARK}>All statuses</option>
          {DCC_STATUSES.map(st=><option key={st} value={st} style={OPT_DARK}>{st}</option>)}
        </select>
        <select style={s.select} value={audienceFilter} onChange={e=>setAudienceFilter(e.target.value)}>
          <option value="" style={OPT_DARK}>All audiences</option>
          {DCC_AUDIENCES.map(([v,l])=><option key={v} value={v} style={OPT_DARK}>{l}</option>)}
        </select>
        <button style={{...s.btn('primary'),marginLeft:'auto'}} onClick={openCreate}>+ New announcement</button>
      </div>
      <TableCard title="News & Updates" count={rows?rows.length:undefined}>
        {!rows?<Loader/>:(
        <table style={s.table}><thead><tr>
          <th style={s.th}>Title</th><th style={s.th}>Status</th><th style={s.th}>Audience</th><th style={s.th}>Priority</th>
          <th style={s.th}>Publish</th><th style={s.th}>Expiry</th><th style={s.th}>Updated</th><th style={s.th}>Actions</th>
        </tr></thead><tbody>
          {rows.map(n=>(
            <tr key={n.id}>
              <td style={s.tds}>{n.title}</td>
              <td style={s.td}><span style={s.badge(n.status==='published'?'green':n.status==='scheduled'?'amber':n.status==='archived'?'red':'blue')}>{n.status}</span></td>
              <td style={s.td}>{n.audienceLabel||n.audience}</td>
              <td style={s.td}><span style={s.badge(n.priority==='critical'?'red':n.priority==='important'?'amber':'blue')}>{n.priority}</span></td>
              <td style={s.td}>{dccFmtDate(n.publishAt)}</td>
              <td style={s.td}>{n.expiryAt?dccFmtDate(n.expiryAt):'—'}</td>
              <td style={s.td}>{dccFmtDate(n.updatedAt)}</td>
              <td style={s.td}>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  <button style={dccMini(T.accent)} disabled={busyId===n.id} onClick={()=>openEdit(n)}>Edit</button>
                  {n.status!=='published'&&<button style={dccMini(T.green)} disabled={busyId===n.id} onClick={()=>action(n,'publish')}>Publish</button>}
                  {n.status!=='archived'&&<button style={dccMini(T.amber)} disabled={busyId===n.id} onClick={()=>action(n,'archive')}>Archive</button>}
                  <button style={dccMini(T.red)} disabled={busyId===n.id} onClick={()=>del(n)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
          {rows.length===0&&<tr><td style={s.td} colSpan={8}><div style={s.empty}>No announcements yet</div></td></tr>}
        </tbody></table>
        )}
      </TableCard>
      {modalOpen&&<DccNewsModal editing={editing} form={form} setForm={setForm} onSubmit={submit} onClose={()=>setModalOpen(false)} saving={saving} formErr={formErr}/>}
    </div>
  );
}

export default function StaffDashboard() {
  const { staffUser, activeRole, staffLogout, switchRole, loading, hasPermission } = useStaffAuth();

  // DCC desktop sign-in bridge: once the staff session is established (MFA verified), resume the DCC
  // /v1/desktop/authorize handoff so the desktop session captures verified staff authority. No-op for
  // ordinary browser staff logins (nothing was persisted).
  useEffect(() => {
    if (!staffUser) return;
    const back = consumeDesktopRedirect();
    if (back) window.location.assign(back);
  }, [staffUser]);

  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);
  const [sidebarOpen,      setSidebarOpen]      = useState(false);
  const [loggingOut,       setLoggingOut]        = useState(false);
  const [actionUserId,     setActionUserId]      = useState('');
  const [actionTechnician, setActionTechnician]  = useState(null);
  const [intelUserId,      setIntelUserId]       = useState('');
  const [feedPaused,       setFeedPaused]        = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Live operations telemetry
  const { liveData, connected, incident } = useLiveOps(API);

  // Derive active section from URL path
  const _pathSection = adminSectionFromLocation(location);
  // ALL_NAV is module-level constant — _allIds never changes, no need to recompute
  const _allIds = React.useMemo(() => ALL_NAV.flatMap(g => g.items.map(i => i.id)), []);
  const [section, setSection] = useState(() => {
    const persisted = _lsGet(_LS_SECTION, null);
    const fromPath = _allIds.includes(_pathSection) ? _pathSection : null;
    return fromPath || persisted || 'overview';
  });

  // Active primary section — derived from current subsection, persisted
  const [primarySection, setPrimarySection] = useState(() => {
    const persisted = _lsGet(_LS_PRIMARY, null);
    return SUBSECTION_TO_PRIMARY[section] || persisted || 'Overview';
  });

  // Right column open/collapsed (SCC-style overlay pull-out)
  const [rcOpen, setRcOpen] = useState(() => _lsGet(_LS_RC_OPEN, false));
  // Left primary-nav collapse (in-memory only; not persisted)
  const [navOpen, setNavOpen] = useState(true);

  // Compute permission-filtered nav.
  const visibleNav = ALL_NAV.map(group => ({

    ...group,
    items: group.items.filter(item =>
      (!item.requiredPermission || loading || hasPermission(item.requiredPermission))
      && (!item.requiredRole || loading || activeRole === item.requiredRole)
    ),
  })).filter(group => group.items.length > 0);

  // Flat list of all visible section IDs — memoised to avoid infinite useEffect loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const _visibleIds = React.useMemo(() => visibleNav.flatMap(g => g.items.map(i => i.id)), [activeRole, loading]);

  // Sync section when path changes (e.g. browser back/forward)
  useEffect(() => {
    const ps = adminSectionFromLocation(location);

    if (_allIds.includes(ps) && ps !== section) {
      setSection(ps);
      const nextPrimary = SUBSECTION_TO_PRIMARY[ps];
      if (nextPrimary) {
        setPrimarySection(nextPrimary);
        _lsSet(_LS_PRIMARY, nextPrimary);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state]);

  // Fallback: if current section becomes hidden after permissions load,
  // redirect to the first visible permitted section (or overview).
  useEffect(() => {
    if (!loading && staffUser && _visibleIds.length > 0 && !_visibleIds.includes(section)) {
      setSection(_visibleIds[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRole, loading, staffUser]);

  useEffect(() => {
    if (!loading && !staffUser) {
      navigate('/admin/login', { replace: true });
    }
  }, [staffUser, loading, navigate]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [section]);

  const handleLogout = async () => {
    setLoggingOut(true);
    if (staffUser) await staffLogout();
    navigate('/admin/login', { replace: true });
  };

  const handleRoleSwitch = async (role) => {
    setShowRoleSwitcher(false);
    if (role !== activeRole) await switchRole(role);
  };

  const goSection = (id) => {
    setSection(id);
    _lsSet(_LS_SECTION, id);
    const ps = SUBSECTION_TO_PRIMARY[id];
    if (ps) { setPrimarySection(ps); _lsSet(_LS_PRIMARY, ps); }
    if (id !== 'intel') setIntelUserId('');
    if (id === 'virtual_shop' && location.pathname !== VIRTUAL_SHOP_ADMIN_PATH) {
      navigate(VIRTUAL_SHOP_ADMIN_PATH);
    } else if (id !== 'virtual_shop' && location.pathname === VIRTUAL_SHOP_ADMIN_PATH) {
      navigate('/admin/dashboard', { state: { adminSection: id } });
    }
  };
  const goAction = (userId) => { setActionUserId(userId); setActionTechnician(null); goSection('actions'); };
  const goActionTech = (userId, techObj) => { setActionUserId(userId); setActionTechnician(techObj); goSection('actions'); };
  const goIntel  = (userId) => { setIntelUserId(userId);  goSection('intel'); };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#080B12', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 36, height: 36, border: '3px solid rgba(45,108,255,0.15)', borderTopColor: '#2D6CFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!staffUser) return null;

  const roleMeta  = ROLE_META[activeRole] || { color: '#6B7280', bg: 'rgba(107,114,128,0.12)', label: activeRole || 'Admin' };
  const roles     = staffUser ? (staffUser.roles || []) : [];
  const name      = staffUser.name || staffUser.email || 'Admin';
  const initials  = name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
  const pageTitle = (PM[section] && PM[section][0])
    || (()=>{ for (const g of ALL_NAV) for (const it of (g.items||[])) if (it && it.id===section) return it.label; return null; })()
    || (section ? section.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) : 'Dashboard');
  const overlayLabel = primarySection || 'Sections';

  // Section -> required permission. Sections not listed are always accessible.
  const SECTION_PERMISSIONS = {
    health:      'system.health.view',
    plans:       'finance.view',
    margin:      'analytics.costs',
    credits:     'credits.view',
    commerce_summary: 'finance.view',
    commerce_sales:   'finance.view',
    commerce_credits: 'finance.view',
    commerce_recon:   'finance.view',
    generations: 'analytics.view',
    generators:  'generators.view',
    ledger:      'analytics.view',
    costs:       'analytics.costs',
    users:       'user.view',
    jobs:        'jobs.view',
    tickets:     'ticket.view',
    actions:     'user.view',
    rewards:     'rewards.view',
    staff:       'staff.view',
    sessions:    'session.view',
    security:    'security.view',
    audit:       'audit.view',
    dcc_overview: 'dcc.view',
    dcc_news:     'dcc.view',
    comms_overview:    'comms.view',
    comms_threads:     'comms.view',
    comms_inboxes:     'comms.view',
    comms_queue:       'comms.view',
    comms_delivery:    'comms.view',
    comms_suppression: 'comms.view',
    comms_templates:   'comms.view',
    gmail_mailboxes:   'comms.manage',
    mc_ci:         'channel_intelligence.view',
    exec_intel:       'data_intelligence.view',
    exec_overview:    'executive.view',
    exec_health:      'executive.view',
    exec_signals:     'executive.view',
    exec_forecast:    'executive.view',
    gi_attribution:   'analytics.view',
    gi_performance:   'analytics.view',
    gi_engines:       'analytics.view',
    gi_trends:        'analytics.view',
    gi_revenue:       'analytics.costs',
    si_performance:   'analytics.view',
    si_families:      'analytics.view',
    si_demand:        'analytics.view',
    si_planning:      'analytics.view',
    mc_release_impact:'marketing_community.view',
    mc_voting:        'marketing_community.view',
    ci_cohorts:       'commercial_intelligence.view',
    ci_ltv:           'commercial_intelligence.financials',
    ci_migration:     'commercial_intelligence.view',
    mc_channels:   'marketing_community.view',
    mc_calendar:   'marketing_community.view',
    mc_activity:   'marketing_community.view',
    mc_campaigns:  'marketing_community.manage',
    mc_impact:     'marketing_community.view',
    mc_traffic:    'marketing_community.view',
    mc_community:  'marketing_community.view',
    mc_releases:   'marketing_community.view',
    mc_email:      'marketing_community.view',
    ci_revenue:      'commercial_intelligence.financials',
    ci_subscribers:  'commercial_intelligence.view',
    ci_conversion:   'commercial_intelligence.view',
    ci_acquisition:  'commercial_intelligence.financials',
    ci_retention:    'commercial_intelligence.view',
    ci_ecosystem:    'commercial_intelligence.view',
    ci_health:       'commercial_intelligence.view',
    ci_target:       'commercial_intelligence.executive',
    intel:             'intelligence.view',
    tech_overview:      'technicians.view',
    tech_applications:  'technicians.view',
    tech_accounts:      'technicians.view',
    tech_verification:  'technicians.view',
    tech_capabilities:  'technicians.view',
    tech_audit:         'technicians.view',
    support_queue:     'support.view',
    support_assigned:  'support.view',
    support_escalated: 'support.view',
    support_resolved:  'support.view',
    support_analytics: 'support.analytics.view',
    products_overview: 'products.view',
    products_registry: 'products.view',
    platform_overview: 'platform.view',
    platform_jobs:     'platform.view',
    platform_providers:'platform.view',
    platform_failures: 'platform.view',
    platform_credits:  'platform.view',
    platform_queues:   'platform.view',
    autonomous_overview:  'autonomous.view',
    autonomous_rules:     'autonomous.view',
    autonomous_queue:     'autonomous.view',
    autonomous_provider:  'autonomous.view',
    autonomous_credits:   'autonomous.view',
    autonomous_security:  'autonomous.view',
    autonomous_history:   'autonomous.view',
    auto_overview:     'automation.view',
    auto_rules:        'automation.rules',
    auto_triggers:     'automation.view',
    auto_incidents:    'automation.incidents',
    auto_suggestions:  'automation.view',
    auto_history:      'automation.history',
  };

  // Centralized gate: if permission required and missing, render unauthorized card.
  // Never renders blank or throws — always returns a valid element.
  const gated = (sectionId, element) => {
    const perm = SECTION_PERMISSIONS[sectionId];
    const deniedByRole = sectionId === 'virtual_shop' && activeRole !== 'OWNER';
    if (!deniedByRole && (!perm || hasPermission(perm))) return element;
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: 320, padding: 40,
        color: '#6B7280', textAlign: 'center',
      }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2D6CFF" strokeWidth="1.5" style={{ marginBottom: 16, opacity: 0.6 }}>
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#9CA3AF', marginBottom: 6 }}>Access Restricted</div>
        <div style={{ fontSize: 12, color: '#6B7280', maxWidth: 280, lineHeight: 1.5 }}>
          Your current role does not have permission to view this section.
        </div>
      </div>
    );
  };

  const renderSection = () => {
    const R = { api: API, liveData, feedPaused, onToggleFeed: () => setFeedPaused(v => !v) };
    switch (section) {
      case 'overview':    return <SecOverview {...R} />;
      case 'activity':    return <SecActivity {...R} />;
      case 'health':      return gated('health',      <SecHealth {...R} />);
      case 'plans':       return gated('plans',       <SecPlans {...R} />);
      case 'margin':      return gated('margin',      <SecMargin {...R} />);
      case 'credits':     return gated('credits',     <SecCredits {...R} />);
      case 'commerce_summary': return gated('commerce_summary', <SecCommerceSummary {...R} />);
      case 'commerce_sales':   return gated('commerce_sales',   <SecCommerceTransactions {...R} />);
      case 'commerce_credits': return gated('commerce_credits', <SecCommerceCredits {...R} />);
      case 'commerce_recon':   return gated('commerce_recon',   <SecCommerceReconciliation {...R} />);
      case 'generations': return gated('generations', <SecGenerations {...R} />);
      case 'generators':  return gated('generators',  <SecGenerators {...R} />);
      case 'ledger':      return gated('ledger',      <SecLedger {...R} />);
      case 'costs':       return gated('costs',       <SecCosts {...R} />);
      case 'users':       return gated('users',       <SecUsers {...R} onAction={goAction} onIntel={goIntel} />);
      case 'jobs':        return gated('jobs',        <SecJobs {...R} />);
      case 'tickets':     return gated('support_queue', <SecSupport {...R} defaultFilter="unresolved" />);
      case 'tech_overview':     return gated('tech_overview',     <SecTechnicianOverview {...R} />);
      case 'tech_applications': return gated('tech_applications', <SecTechnicianApplications {...R} />);
      case 'tech_submissions':  return gated('tech_submissions',  <SecTechnicianSubmissions {...R} onSendToActivation={goActionTech} />);
      case 'tech_accounts':     return gated('tech_accounts',     <SecTechnicianAccounts {...R} mode="accounts" />);
      case 'tech_verification': return gated('tech_verification', <SecTechnicianAccounts {...R} mode="verification" />);
      case 'tech_capabilities': return gated('tech_capabilities', <SecTechnicianAccounts {...R} mode="capabilities" />);
      case 'tech_audit':        return gated('tech_audit',        <SecTechnicianAudit {...R} />);
      case 'actions':     return gated('actions',     <SecActions {...R} prefillUserId={actionUserId} prefillTechnician={actionTechnician} />);
      case 'rewards':     return gated('rewards',     <SecRewards {...R} />);
      case 'staff':       return gated('staff',       <SecStaff {...R} />);
      case 'sessions':    return gated('sessions',    <SecSessions {...R} />);
      case 'security':    return gated('security',    <SecSecurity {...R} />);
      case 'audit':       return gated('audit',       <SecAuditLog {...R} />);
      case 'comms_threads':     return gated('comms_threads',     <SecCommunications {...R} />);
      case 'comms_inboxes':     return gated('comms_inboxes',     <SecMailboxInbox {...R} />);
      case 'comms_overview':    return gated('comms_overview',    <SecCommsOverview {...R} />);
      case 'comms_queue':       return gated('comms_queue',       <SecCommsQueue {...R} />);
      case 'comms_delivery':    return gated('comms_delivery',    <SecCommsDelivery {...R} />);
      case 'comms_suppression': return gated('comms_suppression', <SecCommsSuppression {...R} />);
      case 'comms_templates':   return gated('comms_templates',   <SecCommsTemplates {...R} />);
      case 'gmail_mailboxes':   return gated('gmail_mailboxes',   <SecGmailMailboxes {...R} />);
      case 'exec_intel':      return gated('exec_intel',      <SecIntelFoundation {...R} />);
      case 'exec_overview':    return gated('exec_overview',    <SecExecOverview {...R} />);
      case 'exec_health':      return gated('exec_health',      <SecExecHealth {...R} />);
      case 'exec_signals':     return gated('exec_signals',     <SecExecSignals {...R} />);
      case 'exec_forecast':    return gated('exec_forecast',    <SecExecForecast {...R} />);
      case 'gi_performance':   return gated('gi_performance',   <SecGIPerformance {...R} />);
      case 'gi_engines':       return gated('gi_engines',       <SecGIEngines {...R} />);
      case 'gi_trends':        return gated('gi_trends',        <SecGITrends {...R} />);
      case 'gi_revenue':       return gated('gi_revenue',       <SecGIRevenue {...R} />);
      case 'gi_attribution': return gated('gi_attribution', <SecGIAttribution {...R} />);
      case 'si_performance':   return gated('si_performance',   <SecSIPerformance {...R} />);
      case 'si_families':      return gated('si_families',      <SecSIFamilies {...R} />);
      case 'si_demand':        return gated('si_demand',        <SecSIDemand {...R} />);
      case 'si_planning':      return gated('si_planning',      <SecSIPlanning {...R} />);
      case 'mc_release_impact':return gated('mc_release_impact',<SecMCReleaseImpact {...R} />);
      case 'mc_voting':        return gated('mc_voting',        <SecMCVoting {...R} />);
      case 'ci_cohorts':       return gated('ci_cohorts',       <SecCICohorts {...R} />);
      case 'ci_ltv':           return gated('ci_ltv',           <SecCIProductLTV {...R} />);
      case 'ci_migration':     return gated('ci_migration',     <SecCIMigration {...R} />);
      case 'mc_ci':       return gated('mc_ci',       <SecMCChannelIntel {...R} />);
      case 'mc_channels':  return gated('mc_channels',  <SecMCChannels {...R} />);
      case 'mc_calendar':  return gated('mc_calendar',  <SecMCCalendar {...R} />);
      case 'mc_activity':  return gated('mc_activity',  <SecMCActivity {...R} />);
      case 'mc_campaigns': return gated('mc_campaigns', <SecMCCampaigns {...R} />);
      case 'mc_impact':    return gated('mc_impact',    <SecMCImpact {...R} />);
      case 'mc_traffic':   return gated('mc_traffic',   <SecMCTraffic {...R} />);
      case 'mc_community': return gated('mc_community', <SecMCCommunity {...R} />);
      case 'mc_releases':  return gated('mc_releases',  <SecMCReleases {...R} />);
      case 'mc_email':     return gated('mc_email',     <SecMCEmail {...R} />);
      case 'ci_revenue':     return gated('ci_revenue',     <SecCIRevenue {...R} />);
      case 'ci_subscribers': return gated('ci_subscribers', <SecCISubscribers {...R} />);
      case 'ci_conversion':  return gated('ci_conversion',  <SecCIConversion {...R} />);
      case 'ci_acquisition': return gated('ci_acquisition', <SecCIAcquisition {...R} />);
      case 'ci_retention':   return gated('ci_retention',   <SecCIRetention {...R} />);
      case 'ci_ecosystem':   return gated('ci_ecosystem',   <SecCIEcosystem {...R} />);
      case 'ci_health':      return gated('ci_health',      <SecCIHealth {...R} />);
      case 'ci_target':      return gated('ci_target',      <SecCITarget {...R} />);
      case 'intel':             return gated('intel',             <SecUserIntelligence {...R} initialUserId={intelUserId} />);
      case 'support_queue':     return gated('support_queue',     <SecSupport {...R} defaultFilter="unresolved" />);
      case 'support_assigned':  return gated('support_assigned',  <SecSupport {...R} defaultFilter="assigned" />);
      case 'support_escalated': return gated('support_escalated', <SecSupport {...R} defaultFilter="escalated" />);
      case 'support_resolved':  return gated('support_resolved',  <SecSupport {...R} defaultFilter="resolved" />);
      case 'support_analytics': return gated('support_analytics', <SecSupportAnalytics {...R} />);
      case 'products_overview': return gated('products_overview', <SecProductsOverview {...R} />);
      case 'products_registry': return gated('products_registry', <SecProductRegistry {...R} />);
      case 'platform_overview': return gated('platform_overview', <SecPlatformOverview {...R} />);
      case 'platform_jobs':     return gated('platform_jobs',     <SecPlatformJobs {...R} />);
      case 'platform_providers':return gated('platform_providers',<SecPlatformProviders {...R} />);
      case 'platform_failures': return gated('platform_failures', <SecPlatformFailures {...R} />);
      case 'platform_credits':  return gated('platform_credits',  <SecPlatformCredits {...R} />);
      case 'platform_queues':   return gated('platform_queues',   <SecPlatformQueues {...R} />);
      case 'autonomous_overview':  return gated('autonomous_overview',  <SecAutonomousOverview {...R} />);
      case 'autonomous_rules':     return gated('autonomous_rules',     <SecAutonomousRules {...R} />);
      case 'autonomous_queue':     return gated('autonomous_queue',     <SecAutonomousSubsystem {...R} category="queue" title="Queue Recovery" />);
      case 'autonomous_provider':  return gated('autonomous_provider',  <SecAutonomousSubsystem {...R} category="provider" title="Provider Failover" />);
      case 'autonomous_credits':   return gated('autonomous_credits',   <SecAutonomousSubsystem {...R} category="credits" title="Credit Protection" />);
      case 'autonomous_security':  return gated('autonomous_security',  <SecAutonomousSubsystem {...R} category="security" title="Security Response" />);
      case 'autonomous_history':   return gated('autonomous_history',   <SecAutonomousHistory {...R} />);
      case 'auto_overview':     return gated('auto_overview',     <SecAutomationOverview {...R} />);
      case 'auto_rules':        return gated('auto_rules',        <SecAutomationRules {...R} />);
      case 'auto_triggers':     return gated('auto_triggers',     <SecAutomationTriggers {...R} />);
      case 'auto_incidents':    return gated('auto_incidents',    <SecAutomationIncidents {...R} />);
      case 'auto_suggestions':  return gated('auto_suggestions',  <SecAutomationSuggestions {...R} />);
      case 'auto_history':      return gated('auto_history',      <SecAutomationHistory {...R} />);
      case 'dcc_overview':      return gated('dcc_overview',      <SecDccOverview {...R} />);
      case 'dcc_news':          return gated('dcc_news',          <SecDccNews {...R} />);
      case 'virtual_shop':      return gated('virtual_shop',      <VirtualShopOwnerAdmin api={VIRTUAL_SHOP_ADMIN_API} />);
      default:            return <SecOverview {...R} />;
    }
  };

  return (
    <EntityInspectorProvider api={API}>
      <GuidanceBridge section={section}>
      <>
      <style>{styles}</style>
      <div className="ad-root">
        <div className={`ad-overlay${sidebarOpen ? ' visible' : ''}`} onClick={() => setSidebarOpen(false)} />

        {/* LEFT COLUMN — primary sections only */}
        <aside className={`ad-sidebar${sidebarOpen ? ' mobile-open' : ''}${navOpen ? '' : ' nav-collapsed'}`}>
          <div className="ad-sidebar-logo">
            <div className="ad-logo-mark">VI</div>
            <div>
              <div className="ad-logo-text">VISINVOKE</div>
              <div className="ad-logo-badge">Admin</div>
            </div>
          </div>

          <nav className="ad-primary-nav">
            {PRIMARY_SECTIONS.filter(ps =>
              visibleNav.some(g => g.section === ps.id)
            ).map(ps => (
              <button
                key={ps.id}
                className={`ad-primary-item${primarySection === ps.id ? ' active' : ''}`}
                onClick={() => {
                  _lsSet(_LS_PRIMARY, ps.id);
                  setPrimarySection(ps.id);
                  // Navigate to first visible subsection of this primary
                  const group = visibleNav.find(g => g.section === ps.id);
                  if (group && group.items.length > 0) goSection(group.items[0].id);
                }}
              >
                {ps.label}
              </button>
            ))}
          </nav>

          <div className="ad-sidebar-footer">
            <button className="vi-nav-collapse-btn" onClick={() => setNavOpen(false)} title="Collapse navigation">
              Collapse Navigation
            </button>
            <button className="ad-logout-btn" onClick={handleLogout} disabled={loggingOut}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              {loggingOut ? 'Signing out...' : 'Sign Out'}
            </button>
          </div>
        </aside>

        {/* MAIN area */}
        <div className={`ad-main${navOpen ? '' : ' nav-collapsed'}`}>

          {/* Topbar — single row: title | search | pills */}
          <header className="ad-topbar">
            <button className="ad-hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="6"  x2="21" y2="6"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>

            <div className="ad-topbar-title">{pageTitle}</div>

            <div className="ad-topbar-search">
              <GlobalCommandBar
                api={API}
                hasPermission={hasPermission}
                onNavigate={(sectionId, item) => {
                  goSection(sectionId);
                  navigate(sectionId === 'virtual_shop' ? VIRTUAL_SHOP_ADMIN_PATH : `/admin/${sectionId}`);
                  if (item && item.id && item.type === 'user') {
                    setActionUserId(item.id);
                  }
                }}
              />
            </div>

            <div className="ad-topbar-right">

              <LiveDot connected={connected} />

              {staffUser && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 4, background: 'rgba(74,222,128,0.1)', color: '#4ADE80', border: '1px solid rgba(74,222,128,0.2)', letterSpacing: '0.05em' }}>
                  MFA
                </span>
              )}

              {/* Role pill + switcher */}
              <div style={{ position: 'relative' }}>
                <button
                  className="ad-role-pill"
                  style={{ background: roleMeta.bg || roleMeta.color + '18', color: roleMeta.color, border: `1px solid ${roleMeta.color}30` }}
                  onClick={() => setShowRoleSwitcher(!showRoleSwitcher)}
                >
                  <div className="ad-role-dot" style={{ background: roleMeta.color }} />
                  {roleMeta.label}
                  {roles.length > 1 && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  )}
                </button>

                {showRoleSwitcher && roles.length > 1 && (
                  <div className="ad-role-switcher">
                    <div className="ad-switcher-label">Switch Role</div>
                    {roles.map(role => {
                      const m = ROLE_META[role] || { color: '#6B7280', label: role };
                      return (
                        <button
                          key={role}
                          className={`ad-switcher-role${role === activeRole ? ' current' : ''}`}
                          onClick={() => handleRoleSwitch(role)}
                        >
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                          {m.label}
                          {role === activeRole && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2D6CFF" strokeWidth="2.5" style={{ marginLeft: 'auto' }}>
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="ad-user-info">
                <div className="ad-avatar" style={{ background: roleMeta.bg || roleMeta.color + '18', color: roleMeta.color }}>
                  {initials}
                </div>
                <div className="ad-user-name">{name}</div>
              </div>
            </div>
          </header>

          {/* Live incident strip */}
          <IncidentStrip incident={incident} onNavigate={(id) => { goSection(id); navigate(id === 'virtual_shop' ? VIRTUAL_SHOP_ADMIN_PATH : `/admin/${id}`); }} />

          {/* Operational content */}
          <div className="ad-content" style={{ padding: '16px 28px 28px', position: 'relative' }}>
            {/* Reopen-nav tab (top-left) when the left navigation is collapsed */}
            {!navOpen && (
              <button
                className="vi-hdr-panel-btn"
                style={{ position: 'absolute', top: 0, left: 0, borderRadius: '0 0 7px 0', zIndex: 10 }}
                onClick={() => setNavOpen(true)}
                title="Open navigation"
              >
                {'\u2261'} Open Navigation
              </button>
            )}
            {/* Action row: VISINVOKE Dashboard + section drawer toggle (SCC cluster) */}
            <div className="scc-action-row">
              <div className="scc-action-spacer" aria-hidden="true" />
              <div className="scc-action-cluster">
                <button
                  className="vi-hdr-panel-btn"
                  style={{ borderRadius: '0 0 7px 7px', flexShrink: 0, whiteSpace: 'nowrap' }}
                  onClick={() => navigate('/dashboard')}
                  title="Return to VISINVOKE Dashboard"
                >
                  {'\u2190'} VISINVOKE Dashboard
                </button>
                <button
                  className={`vi-hdr-panel-btn${rcOpen ? ' active' : ''}`}
                  style={{ borderRadius: '0 0 0 7px', flexShrink: 0, whiteSpace: 'nowrap' }}
                  onClick={() => { setRcOpen(v => { const next = !v; _lsSet(_LS_RC_OPEN, next); return next; }); }}
                  title={rcOpen ? `Close ${overlayLabel} panel` : `Open ${overlayLabel} panel`}
                >
                  {rcOpen ? `Close ${overlayLabel}` : `Open ${overlayLabel}`}
                </button>
              </div>
            </div>
            <InvestigationResumeBridge/>
            {renderSection()}
          </div>
        </div>

        {/* RIGHT COLUMN — contextual nav + guidance */}
        <aside className={`ad-right-col${rcOpen ? ' rc-open' : ''}`}>
          {/* Collapsed state: just the expand button */}
          <div className="ad-rc-collapse-only">
            <button
              className="ad-rc-collapse-btn"
              title="Expand navigation"
              onClick={() => { setRcOpen(true); _lsSet(_LS_RC_OPEN, true); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
          </div>

          {/* Expanded state */}
          <div className="ad-rc-header">
            <GuidanceRCButton />
            <button
              className="ad-rc-collapse-btn"
              title="Collapse navigation"
              onClick={() => { setRcOpen(false); _lsSet(_LS_RC_OPEN, false); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>

          <div className="ad-rc-section-label">{primarySection}</div>
          <nav className="ad-rc-nav">
            {(visibleNav.find(g => g.section === primarySection) || { items: [] }).items.map(item => (
              <button
                key={item.id}
                className={`ad-rc-item${section === item.id ? ' active' : ''}`}
                onClick={() => { goSection(item.id); setRcOpen(false); _lsSet(_LS_RC_OPEN, false); }}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
      <EntityInspectorPanel />
      <OperationalGuidancePanel />
      </>
      </GuidanceBridge>
    </EntityInspectorProvider>
  );
}
